import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { db, auth, storage } from './firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  CheckSquare as CheckSquareIcon,
  CalendarClock as CalendarClockIcon,
  Megaphone as MegaphoneIcon,
  FolderOpen as FolderOpenIcon,
  Users as UsersIcon,
  ClipboardList as ClipboardListIcon,
  CalendarRange as CalendarRangeIcon,
  Presentation as PresentationIcon,
  Trophy as TrophyIcon,
  Package as PackageIcon,
  Archive as ArchiveIcon,
  Award as AwardIcon,
  FileText as FileTextIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Repeat as RepeatIcon,
  CreditCard as CreditCardIcon,
  // Parent registration flow status screens only (see the icon note there).
  // The rest of the app's emoji is deliberately untouched.
  Hourglass as HourglassIcon,
  CircleCheck as CircleCheckIcon,
  UserCheck as UserCheckIcon,
  ShoppingCart as ShoppingCartIcon,
  SearchX as SearchXIcon,
  // Players action row only (Batch 2 carve-out). The other ~259 UI emoji
  // are Batch 4; the ~30 in notification/email/SMS strings stay emoji.
  UserPlus as UserPlusIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  // GameScheduler games toolbar only. DownloadIcon above is shared with the
  // Players row rather than imported twice.
  Plus as PlusIcon,
  Zap as ZapIcon,
  CalendarPlus as CalendarPlusIcon,
  FileDown as FileDownIcon,
  // Published-to-calendar state, on both the per-game toggle and its caption.
  // Deliberately NOT CalendarX: that button is a reversible visibility flag,
  // not a delete, and an X would imply destruction. CalendarPlus above marks
  // the unpublished state on the same button.
  CalendarCheck as CalendarCheckIcon,
  Trash2 as Trash2Icon,
  // Batch 4, first pass. Megaphone/Trophy/Archive/CheckSquare above are the
  // sidebar nav icons and are REFERENCED here, not re-imported: a section
  // header that names a nav destination now carries that destination's own
  // icon, so the header and the sidebar item cannot drift apart.
  Smartphone as SmartphoneIcon,
  ArchiveRestore as ArchiveRestoreIcon,
  // Task metadata rows (task detail + Archive card). Sized to the text they
  // sit in rather than the 17 used on buttons -- see the note at each site.
  MapPin as MapPinIcon,
  User as UserIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  // Task detail action buttons. The claim button's raised-hand emoji stays by
  // decision (see CLAUDE.md) and "Mark as Complete" leads with a typographic
  // check rather than an emoji, so neither takes an icon here.
  HelpCircle as HelpCircleIcon,
  Pencil as PencilIcon,
  // Superadmin redesign (Phase 1: shell + Dashboard). These are scoped to the
  // /superadmin screens and deliberately do NOT change any league-app icon --
  // the slugs come straight from the design handoff's asset list. Users,
  // Megaphone, Download, Plus, ClipboardList, Clock, Pencil, Trash2 and
  // FileText are already imported above and are REFERENCED by the superadmin
  // chrome rather than imported twice.
  ChartNoAxesColumn as ChartNoAxesColumnIcon,
  UserRound as UserRoundIcon,
  Tag as TagIcon,
  LogOut as LogOutIcon,
  TrendingUp as TrendingUpIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
  ArrowRight as ArrowRightIcon,
  RefreshCw as RefreshCwIcon,
  Menu as MenuIcon,
  X as XIcon,
  // Phase 2 (Leagues). Eye is the emulate action -- "View & Edit as League" --
  // and is the only new glyph the phase needs. Calendar, Pencil, Trash2,
  // ChevronRight and Check are already imported above and are REFERENCED by
  // the Leagues surface rather than re-imported. The search field deliberately
  // takes no magnifier icon: a placeholder already says what it does, and an
  // icon inside the control would need absolute positioning over an input
  // whose height changes at the 640px iOS-zoom breakpoint.
  Eye as EyeIcon
} from 'lucide-react';
import {
  collection, onSnapshot, setDoc, updateDoc, deleteDoc, writeBatch, addDoc,
  doc, getDoc, query, where, orderBy, documentId, limit, getDocs, arrayUnion, arrayRemove, increment, runTransaction
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, sendPasswordResetEmail,
  EmailAuthProvider, reauthenticateWithCredential, updatePassword
} from 'firebase/auth';

// ─── PLATFORM ADMIN PASSWORD (change this to something only you know) ───────
const PLATFORM_PASSWORD = "GroundsPal2024!";

// Mirrors the server-side ENABLE_EVENT_REQUEST_SMS kill switch in
// api/send-sms.js -- that message type isn't yet registered on the Twilio
// A2P 10DLC campaign, so sending it would fail (403) even though the pref
// is saved. Keeps the "Event requests awaiting your approval" SMS toggle
// hidden in Settings until the Twilio sample is submitted and approved.
// Flip to true here (and set ENABLE_EVENT_REQUEST_SMS=true in Vercel) once
// that happens -- the underlying adminSmsPrefEventRequests preference is
// still saved as true by default in the meantime, so nothing needs to be
// re-opted-in when this flips.
const EVENT_REQUEST_SMS_LIVE = false;

const LOGO_URL = "data:image/png;base64,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";
const RISE_LOGO_URL = "data:image/png;base64,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";
const C = {
  green: "#1A7A35", greenMid: "#34C759", greenLt: "#E8F9ED", greenBd: "#A3E6B8",
  amber: "#E65100", amberLt: "#FFF3E0", amberBd: "#FFCC02",
  red: "#E24B4A", redLt: "#FCEBEB", redBd: "#E24B4A", redDark: "#501313",
  // Red as a FILL behind white text, and nothing else. White on C.red measures
  // 3.93:1 and fails WCAG AA (4.5:1 for normal text); white on this measures
  // 5.62:1 and passes. Deliberately a separate token rather than a redefinition
  // of C.red: red is also used as a border and as text, where the existing value
  // is fine, and C.red/C.redBd are currently the SAME hex -- redefining C.red
  // would silently split that pair and shift borders that today match their
  // neighbours. Use this ONLY where white text sits on a red fill. Tints
  // (C.redLt + C.redDark) and the unread indicator dots stay on their own tokens.
  redFill: "#C62828",
  blue: "#1565C0", blueLt: "#E3F2FD", blueBd: "#90CAF9",
  purple: "#6A1B9A", purpleLt: "#F3E5F5", purpleBd: "#CE93D8",
  text: "#1C1C1E", muted: "#8E8E93", border: "#E5E5EA",
  white: "#FFFFFF", surface: "#F2F2F7", grayLt: "#F2F2F7", grayBd: "#D1D1D6",
  pageBg: "#F5F5F5", headerBg: "#FFFFFF", headerBorder: "#34C759",
  cardBg: "#FFFFFF", navBg: "#FFFFFF", navBorder: "#E8E8E8",
  headerText: "#0D0D0D", headerSub: "#34C759",
  btnBg: "#F0F0F0", btnBorder: "#E0E0E0", btnText: "#444444"
};

const R = { card: 0, control: 2, circle: "50%" };

const T = {
  display: "'Barlow Condensed', 'Arial Narrow', sans-serif",
  body: "'Roboto', Arial, sans-serif"
};

const PRIORITY = {
  high: { label: "High", bg: "#FCEBEB", text: "#E24B4A" },
  medium: { label: "Medium", bg: "#FFF8E1", text: "#E65100" },
  low: { label: "Low", bg: "#E8F9ED", text: "#34C759" }
};

const STATUS = {
  open: { label: "Open", color: C.blue, bg: C.blueLt },
  "in-progress": { label: "In Progress", color: C.amber, bg: C.amberLt },
  complete: { label: "Complete", color: C.green, bg: C.greenLt }
};

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" }
];

function emptyAvailability() {
  const a = {};
  DAYS.forEach(d => { a[d.key] = { enabled: false, start: "17:30", end: "21:30" }; });
  return a;
}

function toggleLocation(loc, currentList, onChange) {
  const has = currentList.includes(loc);
  onChange(has ? currentList.filter(l => l !== loc) : [...currentList, loc]);
}

/// Commits a list of batch operations (each a function receiving a writeBatch
// and calling .set/.delete/.update on it) in chunks of <=500, Firestore's
// per-batch operation cap. Used to replace sequential per-doc awaited writes
// with a small number of atomic batch commits.
async function commitBatchedOps(db, ops) {
  for (let i = 0; i < ops.length; i += 500) {
    const batch = writeBatch(db);
    ops.slice(i, i + 500).forEach(applyOp => applyOp(batch));
    await batch.commit();
  }
}

function NewDivisionForm({ lid, seasonId, leagueLocations, hideScheduleConfig, onDone }) {
  const [newDivision, setNewDivision] = useState({ name: "", sport: "Baseball", eligibleLocations: [], maxGamesPerDay: "", maxGamesPerWeek: "", gameDurationHours: "", gameDurationMins: "", breakBetweenGamesMinutes: "", availability: emptyAvailability(), teamNames: [""] });

  const createDivision = async () => {
    if (!newDivision.name.trim() || !lid || !seasonId) return;
    const id = "div_" + Date.now();
    const hrs = newDivision.gameDurationHours === "" ? 0 : Number(newDivision.gameDurationHours);
    const mins = newDivision.gameDurationMins === "" ? 0 : Number(newDivision.gameDurationMins);
    const totalDurationMinutes = (newDivision.gameDurationHours === "" && newDivision.gameDurationMins === "") ? null : (hrs * 60 + mins);
    await setDoc(doc(db, "leagues", lid, "divisions", id), {
      id,
      seasonId,
      name: newDivision.name.trim(),
      sport: newDivision.sport,
      eligibleLocations: newDivision.eligibleLocations,
      maxGamesPerDay: newDivision.maxGamesPerDay === "" ? null : Number(newDivision.maxGamesPerDay),
      maxGamesPerWeek: newDivision.maxGamesPerWeek === "" ? null : Number(newDivision.maxGamesPerWeek),
      gameDurationMinutes: totalDurationMinutes,
      breakBetweenGamesMinutes: newDivision.breakBetweenGamesMinutes === "" ? null : Number(newDivision.breakBetweenGamesMinutes),
      availability: newDivision.availability,
      createdAt: new Date().toISOString()
    });
    const teamNamesToCreate = newDivision.teamNames.map(n => n.trim()).filter(Boolean);
    for (let i = 0; i < teamNamesToCreate.length; i++) {
      const teamId = "team_" + Date.now() + "_" + i;
      await setDoc(doc(db, "leagues", lid, "teams", teamId), {
        id: teamId, name: teamNamesToCreate[i], divisionId: id, createdAt: new Date().toISOString()
      });
    }
    setNewDivision({ name: "", sport: "Baseball", eligibleLocations: [], maxGamesPerDay: "", maxGamesPerWeek: "", gameDurationHours: "", gameDurationMins: "", breakBetweenGamesMinutes: "", availability: emptyAvailability(), teamNames: [""] });
    if (onDone) onDone();
  };

  const namedLocations = (leagueLocations || []).filter(Boolean);

  return (
    <div style={{ background: C.white, border: "2px solid " + C.greenBd, borderRadius: R.card, padding: "1rem", marginBottom: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input
          placeholder="Division name, e.g. 10U"
          value={newDivision.name}
          onChange={e => setNewDivision(Object.assign({}, newDivision, { name: e.target.value }))}
          style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
        />
        <select
          value={newDivision.sport}
          onChange={e => setNewDivision(Object.assign({}, newDivision, { sport: e.target.value }))}
          style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }}
        >
          <option>Baseball</option>
          <option>Softball</option>
          <option>Soccer</option>
          <option>Basketball</option>
          <option>Football</option>
          <option>Volleyball</option>
          <option>Other</option>
        </select>
        {!hideScheduleConfig && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
              MAX GAMES/DAY (optional)
            </label>
            <input type="number" min="1" placeholder="No limit"
              value={newDivision.maxGamesPerDay}
              onChange={e => setNewDivision(Object.assign({}, newDivision, { maxGamesPerDay: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
              MAX GAMES/WEEK (optional)
            </label>
            <input type="number" min="1" placeholder="No limit"
              value={newDivision.maxGamesPerWeek}
              onChange={e => setNewDivision(Object.assign({}, newDivision, { maxGamesPerWeek: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
            />
          </div>
        </div>
        )}
        {!hideScheduleConfig && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: -4 }}>
          Per team. Used by the auto-scheduler (once built) to avoid overloading any team's week.
        </div>
        )}
        {!hideScheduleConfig && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
              GAME LENGTH — HOURS
            </label>
            <input type="number" min="0" placeholder="e.g. 1"
              value={newDivision.gameDurationHours}
              onChange={e => setNewDivision(Object.assign({}, newDivision, { gameDurationHours: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
              GAME LENGTH — MINUTES
            </label>
            <input type="number" min="0" max="59" placeholder="e.g. 45"
              value={newDivision.gameDurationMins}
              onChange={e => setNewDivision(Object.assign({}, newDivision, { gameDurationMins: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
              BREAK (minutes)
            </label>
            <input type="number" min="0" placeholder="e.g. 15"
              value={newDivision.breakBetweenGamesMinutes}
              onChange={e => setNewDivision(Object.assign({}, newDivision, { breakBetweenGamesMinutes: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
            />
          </div>
        </div>
        )}
        <div style={{ fontSize: 11, color: C.muted, marginTop: -4 }}>
          Everything below is optional — leave it blank and set it up later from this division's panel if you're not ready yet.
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
            ELIGIBLE LOCATIONS
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {namedLocations.length === 0 && (
              <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
                No locations set yet — add them under ⚙️ Settings → Locations first.
              </div>
            )}
            {namedLocations.map(loc => {
              const on = newDivision.eligibleLocations.includes(loc);
              return (
                <button key={loc} type="button"
                  onClick={() => toggleLocation(loc, newDivision.eligibleLocations, v => setNewDivision(Object.assign({}, newDivision, { eligibleLocations: v })))}
                  style={{ padding: "5px 12px", borderRadius: R.control, border: "1px solid " + (on ? C.green : C.border), background: on ? C.greenLt : C.white, color: on ? C.green : C.muted, fontSize: 12, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                  {loc}
                </button>
              );
            })}
          </div>
        </div>
        {!hideScheduleConfig && (
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
            WEEKLY AVAILABILITY
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {DAYS.map(day => {
              const dayConfig = newDivision.availability[day.key] || { enabled: false, start: "17:30", end: "21:30" };
              return (
                <div key={day.key} style={{ display: "flex", alignItems: "center", gap: 8, background: C.grayLt, borderRadius: R.control, padding: "7px 10px", flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 100, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!dayConfig.enabled}
                      onChange={e => setNewDivision(Object.assign({}, newDivision, { availability: { ...newDivision.availability, [day.key]: { ...dayConfig, enabled: e.target.checked } } }))} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: dayConfig.enabled ? C.text : C.muted }}>{day.label}</span>
                  </label>
                  {dayConfig.enabled && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="time" value={dayConfig.start}
                        onChange={e => setNewDivision(Object.assign({}, newDivision, { availability: { ...newDivision.availability, [day.key]: { ...dayConfig, start: e.target.value } } }))}
                        style={{ padding: "5px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                      <span style={{ fontSize: 11, color: C.muted }}>to</span>
                      <input type="time" value={dayConfig.end}
                        onChange={e => setNewDivision(Object.assign({}, newDivision, { availability: { ...newDivision.availability, [day.key]: { ...dayConfig, end: e.target.value } } }))}
                        style={{ padding: "5px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
            TEAMS
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {newDivision.teamNames.map((name, i) => (
              <div key={i} style={{ display: "flex", gap: 6 }}>
                <input placeholder={"Team " + (i + 1) + " name, e.g. Ironbirds"} value={name}
                  onChange={e => { const u = [...newDivision.teamNames]; u[i] = e.target.value; setNewDivision(Object.assign({}, newDivision, { teamNames: u })); }}
                  style={{ flex: 1, padding: "8px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                {newDivision.teamNames.length > 1 && (
                  <button onClick={() => { const u = newDivision.teamNames.filter((_, j) => j !== i); setNewDivision(Object.assign({}, newDivision, { teamNames: u })); }}
                    style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, width: 32, cursor: "pointer", fontSize: 14, color: C.redDark }}>
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
          <button onClick={() => setNewDivision(Object.assign({}, newDivision, { teamNames: [...newDivision.teamNames, ""] }))}
            style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "7px 12px", cursor: "pointer", fontSize: 12, color: C.green, fontWeight: 700, fontFamily: "inherit", marginTop: 6 }}>
            + Add another team
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button onClick={createDivision}
            style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Create division
          </button>
          <button onClick={onDone}
            style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SchedulerSetup({ lid, leagueLocations, seasonId, hideBlackoutDates, hideScheduleConfig }) {
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDivision, setShowNewDivision] = useState(false);
  const [editingDivisionId, setEditingDivisionId] = useState(null);
  const [expandedDivisionId, setExpandedDivisionId] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [newConflictDate, setNewConflictDate] = useState("");
  const [newAssistantCoach, setNewAssistantCoach] = useState("");
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [newBlackoutDate, setNewBlackoutDate] = useState("");
  const [newBlackoutEndDate, setNewBlackoutEndDate] = useState("");
  const [newBlackoutLabel, setNewBlackoutLabel] = useState("");
  const [showBlackoutSection, setShowBlackoutSection] = useState(false);

  useEffect(() => {
    if (!lid || !seasonId) { setDivisions([]); setLoading(false); return; }
    const unsubDivisions = onSnapshot(query(collection(db, "leagues", lid, "divisions"), where("seasonId", "==", seasonId)), snap => {
      setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubTeams = onSnapshot(collection(db, "leagues", lid, "teams"), snap => {
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubBlackouts = onSnapshot(collection(db, "leagues", lid, "blackoutDates"), snap => {
      setBlackoutDates(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    });
    return () => { unsubDivisions(); unsubTeams(); unsubBlackouts(); };
  }, [lid, seasonId]);

  const addBlackoutDate = async () => {
    if (!newBlackoutDate || !lid) return;
    const endDate = newBlackoutEndDate && newBlackoutEndDate >= newBlackoutDate ? newBlackoutEndDate : newBlackoutDate;
    const dates = [];
    for (let d = new Date(newBlackoutDate + "T12:00:00"); d <= new Date(endDate + "T12:00:00"); d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
    for (const dateStr of dates) {
      if (blackoutDates.some(b => b.date === dateStr)) continue;
      const id = "bo_" + Date.now() + "_" + dateStr;
      await setDoc(doc(db, "leagues", lid, "blackoutDates", id), {
        id, date: dateStr, label: newBlackoutLabel.trim() || "", createdAt: new Date().toISOString()
      });
    }
    setNewBlackoutDate("");
    setNewBlackoutEndDate("");
    setNewBlackoutLabel("");
  };

  const removeBlackoutDate = async (id) => {
    await deleteDoc(doc(db, "leagues", lid, "blackoutDates", id));
  };

  const updateDivisionLimits = async (divisionId, field, value) => {
    await updateDoc(doc(db, "leagues", lid, "divisions", divisionId), { [field]: value === "" ? null : Number(value) });
  };

  const updateDivisionAvailability = async (divisionId, dayKey, field, value) => {
    await updateDoc(doc(db, "leagues", lid, "divisions", divisionId), { ["availability." + dayKey + "." + field]: value });
  };

  const updateDivisionDuration = async (divisionId) => {
    const hEl = document.getElementById("gdh_" + divisionId);
    const mEl = document.getElementById("gdm_" + divisionId);
    const hVal = hEl && hEl.value !== "" ? Number(hEl.value) : 0;
    const mVal = mEl && mEl.value !== "" ? Number(mEl.value) : 0;
    const bothBlank = (!hEl || hEl.value === "") && (!mEl || mEl.value === "");
    await updateDoc(doc(db, "leagues", lid, "divisions", divisionId), { gameDurationMinutes: bothBlank ? null : (hVal * 60 + mVal) });
  };

  const updateDivisionLocations = async (divisionId, eligibleLocations) => {
    await updateDoc(doc(db, "leagues", lid, "divisions", divisionId), { eligibleLocations });
  };

  const renameDivision = async (divisionId, name) => {
    if (!name.trim()) { setEditingDivisionId(null); return; }
    await updateDoc(doc(db, "leagues", lid, "divisions", divisionId), { name: name.trim() });
    setEditingDivisionId(null);
  };

  const deleteDivision = async (divisionId) => {
    const teamsInDivision = teams.filter(t => t.divisionId === divisionId);
    if (teamsInDivision.length > 0) {
      if (!window.confirm(
        "This division has " + teamsInDivision.length + " team(s). Deleting it will also delete those teams. Continue?"
      )) return;
      for (const t of teamsInDivision) await deleteDoc(doc(db, "leagues", lid, "teams", t.id));
    } else {
      if (!window.confirm("Delete this division?")) return;
    }
    await deleteDoc(doc(db, "leagues", lid, "divisions", divisionId));
  };

  const addTeam = async (divisionId) => {
    if (!newTeamName.trim() || !lid) return;
    const id = "team_" + Date.now();
    await setDoc(doc(db, "leagues", lid, "teams", id), {
      id,
      name: newTeamName.trim(),
      divisionId,
      createdAt: new Date().toISOString()
    });
    setNewTeamName("");
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm("Remove this team?")) return;
    await deleteDoc(doc(db, "leagues", lid, "teams", teamId));
  };

  const renameTeam = async (teamId, name) => {
    if (!name.trim()) return;
    await updateDoc(doc(db, "leagues", lid, "teams", teamId), { name: name.trim() });
  };

  const toggleTeamConflictDay = async (team, dayKey) => {
    const current = team.conflictDays || [];
    const next = current.includes(dayKey) ? current.filter(d => d !== dayKey) : [...current, dayKey];
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { conflictDays: next });
  };

  const addTeamConflictDate = async (team, dateStr) => {
    if (!dateStr) return;
    const current = team.conflictDates || [];
    if (current.includes(dateStr)) return;
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { conflictDates: [...current, dateStr].sort() });
  };

  const removeTeamConflictDate = async (team, dateStr) => {
    const current = team.conflictDates || [];
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { conflictDates: current.filter(d => d !== dateStr) });
  };

  const updateTeamHeadCoach = async (team, name) => {
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { headCoach: name.trim() });
  };

  const addAssistantCoach = async (team, name) => {
    if (!name.trim()) return;
    const current = team.assistantCoaches || [];
    if (current.includes(name.trim())) return;
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { assistantCoaches: [...current, name.trim()] });
  };

  const removeAssistantCoach = async (team, name) => {
    const current = team.assistantCoaches || [];
    await updateDoc(doc(db, "leagues", lid, "teams", team.id), { assistantCoaches: current.filter(n => n !== name) });
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 14 }}>
        Loading divisions...
      </div>
    );
  }

  const namedLocations = (leagueLocations || []).filter(Boolean);

  return (
    <div style={{ padding: "1rem" }}>
      {!hideBlackoutDates && (
      <div style={{ background: C.white, border: "1px solid " + C.redBd, borderRadius: R.card, padding: "1rem", marginBottom: 20 }}>
        <button onClick={() => setShowBlackoutSection(v => !v)}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0, textAlign: "left" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>League blackout dates{blackoutDates.length > 0 ? " (" + blackoutDates.length + ")" : ""}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              Dates the auto-scheduler will never schedule any game on, across every division
            </div>
          </div>
          <span style={{ fontSize: 12, color: C.redDark, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>{showBlackoutSection ? "▲ Hide" : "▼ Show"}</span>
        </button>
        {showBlackoutSection && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Holidays, league-wide breaks, etc. Separate from location closures and per-team conflicts.
            </div>
            {blackoutDates.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                {blackoutDates.map(b => (
                  <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, background: C.redLt, borderRadius: R.control, padding: "7px 10px", border: "1px solid " + C.redBd }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.redDark }}>{b.date}</span>
                    {b.label && <span style={{ fontSize: 12, color: C.redDark }}>— {b.label}</span>}
                    <span style={{ flex: 1 }} />
                    <button onClick={() => removeBlackoutDate(b.id)}
                      style={{ background: "none", border: "none", color: C.redDark, cursor: "pointer", fontSize: 14 }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Date (or start of range)</label>
                <input type="date" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Through (optional, for a range)</label>
                <input type="date" value={newBlackoutEndDate} min={newBlackoutDate} onChange={e => setNewBlackoutEndDate(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Label (optional)</label>
                <input type="text" placeholder="e.g. Winter Break" value={newBlackoutLabel} onChange={e => setNewBlackoutLabel(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
              </div>
              <button onClick={addBlackoutDate} disabled={!newBlackoutDate}
                style={{ background: !newBlackoutDate ? C.grayBd : C.redFill, color: C.white, border: "none", borderRadius: R.control, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: !newBlackoutDate ? "not-allowed" : "pointer", fontFamily: "inherit", alignSelf: "flex-end" }}>
                + Add
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Divisions & Teams</div>
          <div style={{ fontSize: 13, color: C.muted }}>Set up divisions and which locations each one can play at</div>
        </div>
        <button onClick={() => setShowNewDivision(v => !v)}
          style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "9px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          + New Division
        </button>
      </div>

      {showNewDivision && (
        <NewDivisionForm
          lid={lid}
          seasonId={seasonId}
          leagueLocations={leagueLocations}
          hideScheduleConfig={hideScheduleConfig}
          onDone={() => setShowNewDivision(false)}
        />
      )}

      {divisions.length === 0 && !showNewDivision && (
        <div style={{ textAlign: "center", padding: "2rem", color: C.muted, fontSize: 13 }}>
          No divisions yet. Create your first one to start setting up the season schedule.
        </div>
      )}

      {divisions.map(div => {
        const divisionTeams = teams.filter(t => t.divisionId === div.id);
        const expanded = expandedDivisionId === div.id;
        return (
          <div key={div.id} style={{ background: C.white, border: "0.5px solid " + C.border, borderLeft: "4px solid " + C.greenMid, borderRadius: R.card, marginBottom: 10, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => setExpandedDivisionId(expanded ? null : div.id)}>
              <div>
                {editingDivisionId === div.id ? (
                  <input
                    autoFocus
                    defaultValue={div.name}
                    onClick={e => e.stopPropagation()}
                    onBlur={e => renameDivision(div.id, e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") renameDivision(div.id, e.target.value); }}
                    style={{ padding: "4px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontWeight: 700, fontFamily: "inherit" }}
                  />
                ) : (
                  <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text }}>{div.name}</div>
                )}
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {div.sport} · {divisionTeams.length} team{divisionTeams.length === 1 ? "" : "s"} · eligible at {div.eligibleLocations && div.eligibleLocations.length ? div.eligibleLocations.join(", ") : "no locations set"}
                  {(div.maxGamesPerDay || div.maxGamesPerWeek) && (
                    <> · limit {div.maxGamesPerDay ? div.maxGamesPerDay + "/day" : ""}{div.maxGamesPerDay && div.maxGamesPerWeek ? ", " : ""}{div.maxGamesPerWeek ? div.maxGamesPerWeek + "/week" : ""}</>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => setEditingDivisionId(div.id)}
                  style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                  Rename
                </button>
                <button onClick={() => deleteDivision(div.id)}
                  style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
                  Delete
                </button>
              </div>
            </div>

            {expanded && (
              <div style={{ borderTop: "1px solid " + C.border, padding: "12px 14px", background: C.grayLt }}>
                {!hideScheduleConfig && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                    PLAY LIMITS (per team)
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Max games/day</label>
                      <input type="number" min="1" placeholder="No limit"
                        defaultValue={div.maxGamesPerDay === null || div.maxGamesPerDay === undefined ? "" : div.maxGamesPerDay}
                        onBlur={e => updateDivisionLimits(div.id, "maxGamesPerDay", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Max games/week</label>
                      <input type="number" min="1" placeholder="No limit"
                        defaultValue={div.maxGamesPerWeek === null || div.maxGamesPerWeek === undefined ? "" : div.maxGamesPerWeek}
                        onBlur={e => updateDivisionLimits(div.id, "maxGamesPerWeek", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}
                      />
                    </div>
                  </div>
                </div>
                )}
                {!hideScheduleConfig && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                    GAME LENGTH & BREAK TIME
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Hours</label>
                      <input type="number" min="0" placeholder="0" id={"gdh_" + div.id}
                        defaultValue={div.gameDurationMinutes === null || div.gameDurationMinutes === undefined ? "" : Math.floor(div.gameDurationMinutes / 60)}
                        onBlur={() => updateDivisionDuration(div.id)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Minutes</label>
                      <input type="number" min="0" max="59" placeholder="0" id={"gdm_" + div.id}
                        defaultValue={div.gameDurationMinutes === null || div.gameDurationMinutes === undefined ? "" : (div.gameDurationMinutes % 60)}
                        onBlur={() => updateDivisionDuration(div.id)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Break (min)</label>
                      <input type="number" min="0" placeholder="e.g. 15"
                        defaultValue={div.breakBetweenGamesMinutes === null || div.breakBetweenGamesMinutes === undefined ? "" : div.breakBetweenGamesMinutes}
                        onBlur={e => updateDivisionLimits(div.id, "breakBetweenGamesMinutes", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}
                      />
                    </div>
                  </div>
                </div>
                )}
                {!hideScheduleConfig && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                    WEEKLY AVAILABILITY
                  </label>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
                    Toggle which days games can be played, and the earliest/latest start time on each.
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {DAYS.map(day => {
                      const dayConfig = (div.availability && div.availability[day.key]) || { enabled: false, start: "17:30", end: "21:30" };
                      return (
                        <div key={day.key} style={{ display: "flex", alignItems: "center", gap: 8, background: C.white, borderRadius: R.card, padding: "7px 10px", border: "1px solid " + C.border, flexWrap: "wrap" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 100, cursor: "pointer" }}>
                            <input type="checkbox" checked={!!dayConfig.enabled}
                              onChange={e => updateDivisionAvailability(div.id, day.key, "enabled", e.target.checked)}
                            />
                            <span style={{ fontSize: 12, fontWeight: 600, color: dayConfig.enabled ? C.text : C.muted }}>{day.label}</span>
                          </label>
                          {dayConfig.enabled && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input type="time" defaultValue={dayConfig.start}
                                onBlur={e => updateDivisionAvailability(div.id, day.key, "start", e.target.value)}
                                style={{ padding: "5px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                              <span style={{ fontSize: 11, color: C.muted }}>to</span>
                              <input type="time" defaultValue={dayConfig.end}
                                onBlur={e => updateDivisionAvailability(div.id, day.key, "end", e.target.value)}
                                style={{ padding: "5px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                    ELIGIBLE LOCATIONS
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {namedLocations.map(loc => {
                      const on = (div.eligibleLocations || []).includes(loc);
                      return (
                        <button key={loc} type="button"
                          onClick={() => toggleLocation(loc, div.eligibleLocations || [], v => updateDivisionLocations(div.id, v))}
                          style={{ padding: "5px 12px", borderRadius: R.control, border: "1px solid " + (on ? C.green : C.border), background: on ? C.greenLt : C.white, color: on ? C.green : C.muted, fontSize: 12, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                          {loc}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                  TEAMS
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                  {divisionTeams.length === 0 && (
                    <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>No teams yet</div>
                  )}
                  {divisionTeams.map(team => {
                    const teamExpanded = expandedTeamId === team.id;
                    const conflictDays = team.conflictDays || [];
                    const conflictDates = team.conflictDates || [];
                    const conflictCount = conflictDays.length + conflictDates.length;
                    const assistantCoaches = team.assistantCoaches || [];
                    return (
                      <div key={team.id} style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px" }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 13, color: C.text }}>{team.name}</span>
                            {team.headCoach && <div style={{ fontSize: 11, color: C.muted }}>Coach: {team.headCoach}</div>}
                          </div>
                          <button onClick={() => { setExpandedTeamId(teamExpanded ? null : team.id); setNewConflictDate(""); setNewAssistantCoach(""); }}
                            style={{ background: conflictCount > 0 ? C.amberLt : C.grayLt, border: "1px solid " + (conflictCount > 0 ? C.amberBd : C.border), borderRadius: R.control, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: conflictCount > 0 ? C.amber : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                            Details{conflictCount > 0 ? " (" + conflictCount + ")" : ""}
                          </button>
                          <button onClick={() => deleteTeam(team.id)}
                            style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14 }}>
                            ×
                          </button>
                        </div>
                        {teamExpanded && (
                          <div style={{ borderTop: "1px solid " + C.border, padding: "10px", background: C.grayLt }}>
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Team name</label>
                              <input type="text" defaultValue={team.name}
                                onBlur={e => renameTeam(team.id, e.target.value)}
                                style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }} />
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 6 }}>
                              COACHES
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Head coach</label>
                              <input type="text" placeholder="Head coach name" defaultValue={team.headCoach || ""}
                                onBlur={e => updateTeamHeadCoach(team, e.target.value)}
                                style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }} />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Assistant coach(es)</label>
                              {assistantCoaches.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 }}>
                                  {assistantCoaches.map(name => (
                                    <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, background: C.white, borderRadius: R.card, padding: "5px 9px", border: "1px solid " + C.border }}>
                                      <span style={{ flex: 1, fontSize: 12, color: C.text }}>{name}</span>
                                      <button onClick={() => removeAssistantCoach(team, name)}
                                        style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}>
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 6 }}>
                                <input type="text" placeholder="Assistant coach name" value={newAssistantCoach}
                                  onChange={e => setNewAssistantCoach(e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") { addAssistantCoach(team, newAssistantCoach); setNewAssistantCoach(""); } }}
                                  style={{ flex: 1, padding: "6px 9px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }} />
                                <button onClick={() => { addAssistantCoach(team, newAssistantCoach); setNewAssistantCoach(""); }}
                                  style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "5px 10px", fontSize: 11, fontWeight: 700, color: C.green, cursor: "pointer", fontFamily: "inherit" }}>
                                  + Add
                                </button>
                              </div>
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 6 }}>
                              CAN'T PLAY ON (RECURRING)
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
                              {DAYS.map(day => {
                                const on = conflictDays.includes(day.key);
                                return (
                                  <button key={day.key} type="button" onClick={() => toggleTeamConflictDay(team, day.key)}
                                    style={{ padding: "4px 10px", borderRadius: R.control, border: "1px solid " + (on ? C.red : C.border), background: on ? C.redLt : C.white, color: on ? C.red : C.muted, fontSize: 11, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                                    {day.label.slice(0, 3)}
                                  </button>
                                );
                              })}
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 6 }}>
                              SPECIFIC BLACKOUT DATES
                            </div>
                            {conflictDates.length > 0 && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                                {conflictDates.map(dt => (
                                  <div key={dt} style={{ display: "flex", alignItems: "center", gap: 8, background: C.white, borderRadius: R.card, padding: "5px 9px", border: "1px solid " + C.border }}>
                                    <span style={{ flex: 1, fontSize: 12, color: C.text }}>{dt}</span>
                                    <button onClick={() => removeTeamConflictDate(team, dt)}
                                      style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}>
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              <input type="date" value={newConflictDate} onChange={e => setNewConflictDate(e.target.value)}
                                style={{ flex: 1, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                              <button onClick={() => { addTeamConflictDate(team, newConflictDate); setNewConflictDate(""); }}
                                disabled={!newConflictDate}
                                style={{ background: !newConflictDate ? C.grayBd : C.amber, color: C.white, border: "none", borderRadius: R.control, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                                Add date
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  <input
                    placeholder="Team name, e.g. Ironbirds"
                    value={newTeamName}
                    onChange={e => setNewTeamName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addTeam(div.id); }}
                    style={{ flex: 1, padding: "8px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", outline: "none" }}
                  />
                  <button onClick={() => addTeam(div.id)}
                    style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    Add team
                  </button>
                </div>
                <button onClick={() => setExpandedDivisionId(null)}
                  style={{ width: "100%", background: C.greenLt, color: C.green, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "9px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  ✓ Done
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GameScheduler({ lid, user, seasonId, leagueName, seasonName, fieldEvents, members, triggerNotify, sendEmail }) {
  const [divisions, setDivisions] = React.useState([]);
  const [teams, setTeams] = React.useState([]);
  const [games, setGames] = React.useState([]);
  const [closures, setClosures] = React.useState([]);
  const [leagueBlackoutDates, setLeagueBlackoutDates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [divisionFilter, setDivisionFilter] = React.useState(null);
  // "all", or a team id. Narrows only the rendered games list (visibleGames below) --
  // deliberately NOT divisionGames, which nine other consumers depend on being the
  // whole division. See the visibleGames comment.
  const [teamFilter, setTeamFilter] = React.useState("all");
  const [showNewGame, setShowNewGame] = React.useState(false);
  const [gameSaveMessage, setGameSaveMessage] = React.useState(null);
  const [newGame, setNewGame] = React.useState({ homeTeamId: "", awayTeamId: "", location: "", date: "", startTime: "09:00", endTime: "10:30" });
  const [conflicts, setConflicts] = React.useState([]);
  const [confirmedAnyway, setConfirmedAnyway] = React.useState(false);
  const [conflictOverrideIds, setConflictOverrideIds] = React.useState([]);
  const [editingScoreId, setEditingScoreId] = React.useState(null);
  const [editingGameId, setEditingGameId] = React.useState(null);
  const [gameEditDraft, setGameEditDraft] = React.useState(null);
  const [scoreDraft, setScoreDraft] = React.useState({ home: "", away: "" });
  const [showAutoScheduler, setShowAutoScheduler] = React.useState(false);
  const [seasonStart, setSeasonStart] = React.useState("");
  const [seasonEnd, setSeasonEnd] = React.useState("");
  const [timesEachPlays, setTimesEachPlays] = React.useState("1");
  const [scheduleMode, setScheduleMode] = React.useState("repeats");
  const [targetGamesPerTeam, setTargetGamesPerTeam] = React.useState("");
  const [balanceHomeAway, setBalanceHomeAway] = React.useState(false);
  const [gamesListExpanded, setGamesListExpanded] = React.useState(false);
  const [selectedGameIds, setSelectedGameIds] = React.useState({});
  const [editingPreviewIndex, setEditingPreviewIndex] = React.useState(null);
  const [previewEditDraft, setPreviewEditDraft] = React.useState(null);
  const [previewResult, setPreviewResult] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);
  const [savingSchedule, setSavingSchedule] = React.useState(false);
  const [replaceExistingAutoGames, setReplaceExistingAutoGames] = React.useState(true);

  const saveScore = async (gameId) => {
    if (!lid) return;
    const bothFilled = scoreDraft.home !== "" && scoreDraft.away !== "";
    const bothEmpty = scoreDraft.home === "" && scoreDraft.away === "";
    // Partial entry (one filled, one blank) isn't a valid state to save -- ignore.
    if (!bothFilled && !bothEmpty) return;
    // Both blank = clearing a previously-entered score back to "not yet scored".
    const homeScore = bothEmpty ? null : Number(scoreDraft.home);
    const awayScore = bothEmpty ? null : Number(scoreDraft.away);
    const status = bothEmpty ? "scheduled" : "complete";
    await updateDoc(doc(db, "leagues", lid, "games", gameId), {
      homeScore, awayScore, status
    });
    const g = games.find(x => x.id === gameId);
    if (g && g.calendarEventId) {
      try {
        await updateDoc(doc(db, "leagues", lid, "fieldEvents", g.calendarEventId), {
          // title rewritten alongside notes so a scored game self-heals into the
          // away-first order. Without it this path would write away-first notes
          // over a home-first title, and the resulting doc fails both backfill
          // predicates -- skipped permanently, wrong winner, invisible to the fix.
          // buildCalendarTitle takes the raw g (title doesn't depend on score);
          // the subtitle needs the spread because it does.
          title: buildCalendarTitle(g),
          notes: buildCalendarSubtitle({ ...g, homeScore, awayScore, status })
        });
      } catch (e) { /* linked event may have been removed independently -- not fatal */ }
    }
    setEditingScoreId(null);
    setScoreDraft({ home: "", away: "" });
  };

  const minsToTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
  };

  const dayKeyForDate = (dateObj) => {
    // JS getDay(): 0=Sun..6=Sat. Map onto our DAYS array order (mon..sun).
    const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    return map[dateObj.getDay()];
  };

  const buildRoundRobinPairings = (teamIds, repeats) => {
    // Standard circle method. Pads with a "bye" (null) if odd number of teams.
    let ids = teamIds.slice();
    const hasBye = ids.length % 2 !== 0;
    if (hasBye) ids.push(null);
    const n = ids.length;
    const rounds = n - 1;
    const half = n / 2;
    const allPairs = [];
    let arr = ids.slice();
    for (let r = 0; r < rounds; r++) {
      for (let i = 0; i < half; i++) {
        const a = arr[i], b = arr[n - 1 - i];
        if (a !== null && b !== null) {
          allPairs.push(r % 2 === 0 ? [a, b] : [b, a]);
        }
      }
      // rotate all but the first element
      const fixed = arr[0];
      const rest = arr.slice(1);
      rest.unshift(rest.pop());
      arr = [fixed, ...rest];
    }
    let pairings = [];
    for (let rep = 0; rep < repeats; rep++) {
      // alternate home/away on repeat rounds for fairness
      allPairs.forEach(p => pairings.push(rep % 2 === 0 ? p : [p[1], p[0]]));
    }
    return pairings;
  };

  const generatePreview = () => {
    if (!seasonStart || !seasonEnd || divisionTeams.length < 2 || !currentDivision) return;
    setGenerating(true);
    const division = currentDivision;
    const teamIds = divisionTeams.map(t => t.id);

    const durationMin = division.gameDurationMinutes || 60;
    const breakMin = division.breakBetweenGamesMinutes || 0;
    const eligibleLocations = (division.eligibleLocations || []).filter(Boolean);

    // Build the full slot calendar across the season date range.
    const slots = [];
    const start = new Date(seasonStart + "T12:00:00");
    const end = new Date(seasonEnd + "T12:00:00");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0];
      const dayKey = dayKeyForDate(d);
      const dayConfig = division.availability && division.availability[dayKey];
      if (!dayConfig || !dayConfig.enabled) continue;
      if (leagueBlackoutDates.includes(dateStr)) continue;
      eligibleLocations.forEach(loc => {
        const closedHere = closures.some(c => c.field === loc && c.date === dateStr);
        if (closedHere) return;
        let slotStart = timeToMins(dayConfig.start);
        const windowEnd = timeToMins(dayConfig.end);
        while (slotStart + durationMin <= windowEnd) {
          const slotEndTime = slotStart + durationMin;
          const alreadyTaken = games.some(g => g.location === loc && g.date === dateStr &&
            timeToMins(g.startTime) < slotEndTime && timeToMins(g.endTime) > slotStart);
          if (!alreadyTaken) {
            slots.push({ date: dateStr, location: loc, startTime: minsToTime(slotStart), endTime: minsToTime(slotEndTime) });
          }
          slotStart = slotEndTime + breakMin;
        }
      });
    }

    const teamDayCount = {};
    const teamWeekCount = {};
    const weekIndexFor = (dateStr) => Math.floor((new Date(dateStr + "T12:00:00") - start) / (7 * 86400000));
    const scheduledGames = [];

    const dayKeyOf = (teamId, dateStr) => teamDayCount[teamId + "|" + dateStr] || 0;
    const weekKeyOf = (teamId, weekIdx) => teamWeekCount[teamId + "|" + weekIdx] || 0;
    const teamBlocked = (team, dayKey, dateStr, weekIdx) =>
      (team.conflictDays || []).includes(dayKey) ||
      (team.conflictDates || []).includes(dateStr) ||
      (division.maxGamesPerDay && dayKeyOf(team.id, dateStr) >= division.maxGamesPerDay) ||
      (division.maxGamesPerWeek && weekKeyOf(team.id, weekIdx) >= division.maxGamesPerWeek);
    const recordAssignment = (homeId, awayId, dateStr, weekIdx) => {
      teamDayCount[homeId + "|" + dateStr] = dayKeyOf(homeId, dateStr) + 1;
      teamDayCount[awayId + "|" + dateStr] = dayKeyOf(awayId, dateStr) + 1;
      teamWeekCount[homeId + "|" + weekIdx] = weekKeyOf(homeId, weekIdx) + 1;
      teamWeekCount[awayId + "|" + weekIdx] = weekKeyOf(awayId, weekIdx) + 1;
    };

    let unscheduled = [];
    let shortfallTeams = [];
    const homeCount = {};
    const orientPair = (idA, idB) => {
      // When home/away balancing is on, whichever of the two teams has fewer
      // home games so far becomes home for this matchup, regardless of which
      // one was originally listed first.
      if (!balanceHomeAway) return [idA, idB];
      const homeA = homeCount[idA] || 0;
      const homeB = homeCount[idB] || 0;
      return homeA <= homeB ? [idA, idB] : [idB, idA];
    };
    const recordHome = (homeId) => { homeCount[homeId] = (homeCount[homeId] || 0) + 1; };
    const cap = targetGamesPerTeam === "" ? null : Math.max(1, Number(targetGamesPerTeam) || 0);
    const gamesPlayedTotal = {};

    if (scheduleMode === "balanced") {
      // Dynamic mode: for every open slot, pick whichever eligible pair has
      // played the fewest games so far (tie-broken by having faced each other
      // the least), so games end up spread as evenly as possible across the
      // whole season rather than following a fixed pairing order. If a
      // games-per-team cap is set, a team stops being eligible once it hits it.
      const pairsPlayed = {};
      slots.forEach(slot => {
        const dayKey = dayKeyForDate(new Date(slot.date + "T12:00:00"));
        const weekIdx = weekIndexFor(slot.date);
        let bestPair = null, bestScore = Infinity;
        for (let i = 0; i < teamIds.length; i++) {
          for (let j = i + 1; j < teamIds.length; j++) {
            const aId = teamIds[i], bId = teamIds[j];
            const teamA = divisionTeams.find(t => t.id === aId);
            const teamB = divisionTeams.find(t => t.id === bId);
            if (teamBlocked(teamA, dayKey, slot.date, weekIdx) || teamBlocked(teamB, dayKey, slot.date, weekIdx)) continue;
            if (cap !== null && ((gamesPlayedTotal[aId] || 0) >= cap || (gamesPlayedTotal[bId] || 0) >= cap)) continue;
            const alreadyPlayingThisSlot = scheduledGames.some(g => g.date === slot.date && g.startTime === slot.startTime && (g.homeTeamId === aId || g.awayTeamId === aId || g.homeTeamId === bId || g.awayTeamId === bId));
            if (alreadyPlayingThisSlot) continue;
            const pairKey = [aId, bId].sort().join("|");
            const score = ((gamesPlayedTotal[aId] || 0) + (gamesPlayedTotal[bId] || 0)) * 100 + (pairsPlayed[pairKey] || 0);
            if (score < bestScore) { bestScore = score; bestPair = [aId, bId]; }
          }
        }
        if (bestPair) {
          const [homeId, awayId] = orientPair(bestPair[0], bestPair[1]);
          scheduledGames.push({ divisionId: division.id, homeTeamId: homeId, awayTeamId: awayId, location: slot.location, date: slot.date, startTime: slot.startTime, endTime: slot.endTime });
          recordHome(homeId);
          gamesPlayedTotal[homeId] = (gamesPlayedTotal[homeId] || 0) + 1;
          gamesPlayedTotal[awayId] = (gamesPlayedTotal[awayId] || 0) + 1;
          const pairKey = [homeId, awayId].sort().join("|");
          pairsPlayed[pairKey] = (pairsPlayed[pairKey] || 0) + 1;
          recordAssignment(homeId, awayId, slot.date, weekIdx);
        }
      });
    } else {
      // Fixed mode: build a round-robin matchup list up front, repeated N
      // times, and fill slots in order with the first matchup that fits. If a
      // games-per-team cap is set, a matchup is skipped once either team is at it.
      const repeats = Math.max(1, Number(timesEachPlays) || 1);
      const queue = buildRoundRobinPairings(teamIds, repeats);
      slots.forEach(slot => {
        const dayKey = dayKeyForDate(new Date(slot.date + "T12:00:00"));
        const weekIdx = weekIndexFor(slot.date);
        for (let i = 0; i < queue.length; i++) {
          const [rawHomeId, rawAwayId] = queue[i];
          const [homeId, awayId] = orientPair(rawHomeId, rawAwayId);
          const home = divisionTeams.find(t => t.id === homeId);
          const away = divisionTeams.find(t => t.id === awayId);
          if (!home || !away) continue;
          if (teamBlocked(home, dayKey, slot.date, weekIdx) || teamBlocked(away, dayKey, slot.date, weekIdx)) continue;
          if (cap !== null && ((gamesPlayedTotal[homeId] || 0) >= cap || (gamesPlayedTotal[awayId] || 0) >= cap)) continue;
          const alreadyPlayingThisSlot = scheduledGames.some(g => g.date === slot.date && g.startTime === slot.startTime && (g.homeTeamId === homeId || g.awayTeamId === homeId || g.homeTeamId === awayId || g.awayTeamId === awayId));
          if (alreadyPlayingThisSlot) continue;
          scheduledGames.push({ divisionId: division.id, homeTeamId: homeId, awayTeamId: awayId, location: slot.location, date: slot.date, startTime: slot.startTime, endTime: slot.endTime });
          recordHome(homeId);
          gamesPlayedTotal[homeId] = (gamesPlayedTotal[homeId] || 0) + 1;
          gamesPlayedTotal[awayId] = (gamesPlayedTotal[awayId] || 0) + 1;
          recordAssignment(homeId, awayId, slot.date, weekIdx);
          queue.splice(i, 1);
          break;
        }
      });
      unscheduled = queue;
    }

    if (cap !== null) {
      shortfallTeams = divisionTeams.filter(t => (gamesPlayedTotal[t.id] || 0) < cap).map(t => ({ name: t.name, played: gamesPlayedTotal[t.id] || 0, target: cap }));
    }

    // Compaction pass: the matchup-selection logic above focuses on WHICH teams
    // play, not WHEN within the day, so it can leave real gaps behind whenever a
    // slot in the middle of the day had no valid matchup. This second pass keeps
    // each day/location's games in their originally-decided order but repacks
    // their times back-to-back (game length + admin's break time, no more),
    // starting from the division's daily window open time, while stepping around
    // any already-saved games at that same location/date that weren't part of
    // this batch.
    const compactByDayLocation = (list) => {
      const groups = {};
      list.forEach(g => { const key = g.date + "|" + g.location; (groups[key] = groups[key] || []).push(g); });
      Object.keys(groups).forEach(key => {
        const [dateStr, loc] = key.split("|");
        const dayKey = dayKeyForDate(new Date(dateStr + "T12:00:00"));
        const dayConfig = division.availability && division.availability[dayKey];
        const windowStart = dayConfig ? timeToMins(dayConfig.start) : timeToMins(groups[key][0].startTime);
        const blocking = games
          .filter(og => og.location === loc && og.date === dateStr)
          .map(og => [timeToMins(og.startTime), timeToMins(og.endTime)])
          .sort((a, b) => a[0] - b[0]);
        groups[key].sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));
        let cursor = windowStart;
        groups[key].forEach(g => {
          let moved = true;
          while (moved) {
            moved = false;
            for (const [bs, be] of blocking) {
              if (cursor < be && cursor + durationMin > bs) { cursor = be + breakMin; moved = true; }
            }
          }
          g.startTime = minsToTime(cursor);
          g.endTime = minsToTime(cursor + durationMin);
          cursor = cursor + durationMin + breakMin;
        });
      });
      return list;
    };
    compactByDayLocation(scheduledGames);

    setPreviewResult({ scheduled: scheduledGames, unscheduled, shortfallTeams, divisionId: division.id, mode: scheduleMode });
    setGenerating(false);
  };

  const confirmSchedule = async () => {
    if (!previewResult || !lid) return;
    setSavingSchedule(true);
    // Replace any previously auto-generated games for this division so re-running
    // the scheduler with new inputs never creates duplicate matchups. Manually
    // created games (no autoGenerated flag) are left untouched. Admin can uncheck
    // this to instead add the new batch on top of the old one.
    if (replaceExistingAutoGames) {
      const priorAutoGames = games.filter(g => g.divisionId === previewResult.divisionId && g.autoGenerated);
      for (const g of priorAutoGames) {
        if (g.calendarEventId) {
          try { await deleteDoc(doc(db, "leagues", lid, "fieldEvents", g.calendarEventId)); } catch (e) {}
        }
      }
      await commitBatchedOps(db, priorAutoGames.map(g => (batch) => batch.delete(doc(db, "leagues", lid, "games", g.id))));
    }
    await commitBatchedOps(db, previewResult.scheduled.map(g => {
      const id = "game_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
      return (batch) => batch.set(doc(db, "leagues", lid, "games", id), {
        id, ...g, homeScore: null, awayScore: null, status: "scheduled", autoGenerated: true, createdAt: new Date().toISOString()
      });
    }));
    setSavingSchedule(false);
    setPreviewResult(null);
    setShowAutoScheduler(false);
  };

  const discardPreview = () => setPreviewResult(null);
  const editInputsAndRegenerate = () => setPreviewResult(null);

  const removePreviewGame = (index) => {
    setPreviewResult(prev => ({ ...prev, scheduled: prev.scheduled.filter((_, i) => i !== index) }));
  };

  const startEditPreviewGame = (index) => {
    setEditingPreviewIndex(index);
    setPreviewEditDraft({ ...previewResult.scheduled[index] });
  };

  const savePreviewGameEdit = () => {
    setPreviewResult(prev => ({
      ...prev,
      scheduled: prev.scheduled.map((g, i) => i === editingPreviewIndex ? { ...previewEditDraft } : g)
    }));
    setEditingPreviewIndex(null);
    setPreviewEditDraft(null);
  };

  const exportGamesCSV = () => {
    if (!currentDivision) return;
    const escapeCell = (val) => {
      const s = (val === null || val === undefined) ? "" : String(val);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = [
      ["Date", "Start Time", "End Time", "Location", "Home Team", "Away Team", "Home Score", "Away Score", "Status"],
      ...divisionGames.map(g => {
        const home = teams.find(t => t.id === g.homeTeamId);
        const away = teams.find(t => t.id === g.awayTeamId);
        return [
          g.date, g.startTime, g.endTime, g.location,
          home ? home.name : "?", away ? away.name : "?",
          g.homeScore === null || g.homeScore === undefined ? "" : g.homeScore,
          g.awayScore === null || g.awayScore === undefined ? "" : g.awayScore,
          g.status || ""
        ];
      })
    ];
    const csv = rows.map(row => row.map(escapeCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const divisionSlug = currentDivision.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    link.href = url;
    link.setAttribute("download", "schedule-" + divisionSlug + "-" + new Date().toISOString().slice(0, 10) + ".csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportGameChangerCSV = () => {
    if (!currentDivision) return;
    const escapeCell = (val) => {
      const s = (val === null || val === undefined) ? "" : String(val);
      return '"' + s.replace(/"/g, '""') + '"';
    };
    const toGcDate = (dateStr) => {
      // GroundsPal stores "YYYY-MM-DD"; GameChanger wants "M/D/YYYY", no leading zeros.
      const [y, m, d] = dateStr.split("-").map(Number);
      return m + "/" + d + "/" + y;
    };
    const toGcTime = (timeStr) => {
      // GroundsPal stores 24-hour "HH:MM"; GameChanger wants "h:mm AM/PM".
      const [h, m] = timeStr.split(":").map(Number);
      const ampm = h >= 12 ? "PM" : "AM";
      const hr = h % 12 || 12;
      return hr + ":" + String(m).padStart(2, "0") + " " + ampm;
    };
    const rows = [
      ["date", "time", "home", "away", "location", "duration"],
      ...divisionGames.map(g => {
        const home = teams.find(t => t.id === g.homeTeamId);
        const away = teams.find(t => t.id === g.awayTeamId);
        const duration = timeToMins(g.endTime) - timeToMins(g.startTime);
        return [
          toGcDate(g.date), toGcTime(g.startTime),
          home ? home.name : "?", away ? away.name : "?",
          g.location, duration > 0 ? duration : ""
        ];
      })
    ];
    const csv = rows.map(row => row.map(escapeCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const slug = (s) => (s || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "");
    const filename = [slug(leagueName) || "League", slug(currentDivision.name) || "Division", slug(seasonName) || "Season"].join("-") + "-gamechanger.csv";
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  React.useEffect(() => {
    if (!lid || !seasonId) { setDivisions([]); setLoading(false); return; }
    const unsubDivisions = onSnapshot(query(collection(db, "leagues", lid, "divisions"), where("seasonId", "==", seasonId)), snap => {
      setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubTeams = onSnapshot(collection(db, "leagues", lid, "teams"), snap => {
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubGames = onSnapshot(collection(db, "leagues", lid, "games"), snap => {
      setGames(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubClosures = onSnapshot(collection(db, "leagues", lid, "fieldClosures"), snap => {
      setClosures(snap.docs.map(d => d.data()));
    });
    const unsubBlackouts = onSnapshot(collection(db, "leagues", lid, "blackoutDates"), snap => {
      setLeagueBlackoutDates(snap.docs.map(d => d.data().date));
    });
    return () => { unsubDivisions(); unsubTeams(); unsubGames(); unsubClosures(); unsubBlackouts(); };
  }, [lid, seasonId]);

  React.useEffect(() => {
    if (divisions.length === 0) { if (divisionFilter) setDivisionFilter(null); return; }
    if (!divisionFilter || !divisions.some(d => d.id === divisionFilter)) setDivisionFilter(divisions[0].id);
  }, [divisions]);

  // Team ids belong to a division, so a division change makes any active team filter
  // stale -- it would silently render zero games. Keyed on [divisionFilter] rather than
  // the division <select>'s onChange on purpose: that handler does NOT fire when the
  // change comes from the effect above, which is how a SEASON switch arrives (new
  // seasonId -> new divisions -> forced divisionFilter). Keying on the value catches both.
  //
  // Clearing selectedGameIds is the load-bearing half. Selection survives a filter
  // change, so without this you could Select All under team A, switch to team B, and
  // have "Delete Selected (12)" still armed against games that are no longer on screen --
  // bulkDeleteSelectedGames walks the raw games array and would delete them. The
  // division <select> already clears selection for exactly this reason; this matches it.
  React.useEffect(() => {
    setTeamFilter("all");
    setSelectedGameIds({});
  }, [divisionFilter]);

  const timeToMins = (t) => { if (!t) return 0; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ampm = h >= 12 ? "pm" : "am"; const hr = h % 12 || 12; return hr + (m > 0 ? ":" + String(m).padStart(2, "0") : "") + ampm; };

  const findConflicts = () => {
    const found = [];
    if (!newGame.location || !newGame.date) return found;
    if (leagueBlackoutDates.includes(newGame.date)) {
      found.push({ type: "blackout", label: newGame.date + " is a league-wide blackout date (set in Divisions & Teams above)." });
    }
    const closureHit = closures.find(c => c.field === newGame.location && c.date === newGame.date);
    if (closureHit) {
      found.push({ type: "closure", label: newGame.location + " is closed on " + newGame.date + " (" + closureHit.reason + ")" });
    }
    const currentSeasonDivisionIds = divisions.map(d => d.id);
    const overlapping = games.filter(g =>
      currentSeasonDivisionIds.includes(g.divisionId) &&
      g.location === newGame.location && g.date === newGame.date &&
      timeToMins(newGame.startTime) < timeToMins(g.endTime) &&
      timeToMins(newGame.endTime) > timeToMins(g.startTime)
    );
    overlapping.forEach(g => {
      const home = teams.find(t => t.id === g.homeTeamId);
      const away = teams.find(t => t.id === g.awayTeamId);
      found.push({ type: "game", id: g.id, raw: g, label: (away ? away.name : "?") + " vs " + (home ? home.name : "?") + " already scheduled " + fmtTime(g.startTime) + "–" + fmtTime(g.endTime) + " at " + newGame.location });
    });
    const overlappingEvents = (fieldEvents || []).filter(ev =>
      ev.field === newGame.location && ev.date === newGame.date && ev.status === "approved" &&
      timeToMins(newGame.startTime) < timeToMins(ev.endTime || "23:59") &&
      timeToMins(newGame.endTime) > timeToMins(ev.startTime || "00:00") &&
      !overlapping.some(g => g.calendarEventId === ev.id || ev.schedulerGameId === g.id) // skip the calendar copy of a game already listed -- deleting the game cleans up its linked event too
    );
    overlappingEvents.forEach(ev => {
      found.push({ type: "event", id: ev.id, raw: ev, label: "\"" + ev.title + "\" already on the calendar " + fmtTime(ev.startTime) + "–" + fmtTime(ev.endTime) + " at " + newGame.location });
    });
    return found;
  };

  const handleSaveClick = () => {
    // If conflicts aren't already being shown, compute them fresh and, if any exist,
    // show the resolution UI instead of saving right away.
    if (conflicts.length === 0) {
      const found = findConflicts();
      if (found.length > 0) {
        setConflicts(found);
        setConflictOverrideIds(found.filter(c => c.type === "game" || c.type === "event").map(c => c.id));
        return;
      }
    } else {
      // Conflicts are already on screen and being reviewed. Item conflicts (game/event)
      // are already resolved via their own per-item checkboxes, whatever state those
      // are in -- only rule-based warnings (blackout/closure) still need the explicit
      // "Schedule anyway" acknowledgement before proceeding.
      const hasRuleConflicts = conflicts.some(c => c.type === "blackout" || c.type === "closure");
      if (hasRuleConflicts && !confirmedAnyway) return;
    }
    createGame();
  };

  const createGame = async () => {
    if (!newGame.homeTeamId || !newGame.awayTeamId || !newGame.location || !newGame.date || !lid) return;
    try {
      // Delete any conflicting game/event the admin chose to override, to make room.
      const itemConflicts = conflicts.filter(c => c.type === "game" || c.type === "event");
      const overriddenCount = itemConflicts.filter(c => conflictOverrideIds.includes(c.id)).length;
      for (const c of itemConflicts) {
        if (!conflictOverrideIds.includes(c.id)) continue;
        if (c.type === "game") {
          if (c.raw && c.raw.calendarEventId) {
            try { await deleteDoc(doc(db, "leagues", lid, "fieldEvents", c.raw.calendarEventId)); } catch (e) {}
          }
          await deleteDoc(doc(db, "leagues", lid, "games", c.id));
        } else if (c.type === "event") {
          await deleteDoc(doc(db, "leagues", lid, "fieldEvents", c.id));
          if (triggerNotify && c.raw) {
            const creator = (members || []).find(m => m.name === c.raw.createdBy);
            triggerNotify("event", { action: "deleted", evData: { ...c.raw, creatorEmail: creator ? creator.email : null } });
          }
        }
      }

      const id = "game_" + Date.now();
      await setDoc(doc(db, "leagues", lid, "games", id), {
        id,
        divisionId: divisionFilter,
        homeTeamId: newGame.homeTeamId,
        awayTeamId: newGame.awayTeamId,
        location: newGame.location,
        date: newGame.date,
        startTime: newGame.startTime,
        endTime: newGame.endTime,
        homeScore: null,
        awayScore: null,
        status: "scheduled",
        createdAt: new Date().toISOString()
      });
      setNewGame({ homeTeamId: "", awayTeamId: "", location: "", date: "", startTime: "09:00", endTime: "10:30" });
      setConflicts([]);
      setConflictOverrideIds([]);
      setConfirmedAnyway(false);
      setShowNewGame(false);
      setGameSaveMessage({ type: "success", text: overriddenCount > 0 ? "Game saved — " + overriddenCount + " conflicting item" + (overriddenCount === 1 ? "" : "s") + " cancelled." : "Game saved." });
      setTimeout(() => setGameSaveMessage(null), 4000);
    } catch (err) {
      setGameSaveMessage({ type: "error", text: "Something went wrong saving this game. Nothing was changed — try again." });
      setTimeout(() => setGameSaveMessage(null), 6000);
    }
  };

  const deleteGame = async (gameId, calendarEventId) => {
    if (!window.confirm("Delete this game?")) return;
    if (calendarEventId) {
      try { await deleteDoc(doc(db, "leagues", lid, "fieldEvents", calendarEventId)); } catch (e) {}
    }
    await deleteDoc(doc(db, "leagues", lid, "games", gameId));
  };

  const buildCalendarTitle = (game) => {
    const home = teams.find(t => t.id === game.homeTeamId);
    const away = teams.find(t => t.id === game.awayTeamId);
    return (away ? away.name : "?") + " vs " + (home ? home.name : "?");
  };

  const buildCalendarSubtitle = (game) => {
    const division = divisions.find(d => d.id === game.divisionId);
    let subtitle = division ? division.name : "";
    if (game.status === "complete" && game.homeScore !== null && game.homeScore !== undefined && game.awayScore !== null && game.awayScore !== undefined) {
      // Away-first, matching buildCalendarTitle. These two strings render one
      // above the other on the Events calendar, so a scoreline in the opposite
      // order to the matchup would report the wrong winner -- silently, in a
      // view parents see.
      subtitle += (subtitle ? " " : "") + game.awayScore + "-" + game.homeScore;
    }
    return subtitle;
  };

  const toggleGameOnCalendar = async (game) => {
    if (game.calendarEventId) {
      // Currently on the calendar -- remove it.
      try { await deleteDoc(doc(db, "leagues", lid, "fieldEvents", game.calendarEventId)); } catch (e) {}
      await updateDoc(doc(db, "leagues", lid, "games", game.id), { calendarEventId: null });
      return;
    }
    // Not on the calendar yet -- publish it as an approved event everyone can see.
    const eventId = "cal_" + Date.now();
    await setDoc(doc(db, "leagues", lid, "fieldEvents", eventId), {
      id: eventId,
      title: buildCalendarTitle(game),
      field: game.location,
      eventType: "game",
      date: game.date,
      startTime: game.startTime,
      endTime: game.endTime,
      notes: buildCalendarSubtitle(game),
      slots: [],
      status: "approved",
      createdBy: (user && user.name) || "Admin",
      createdAt: new Date().toISOString(),
      schedulerGameId: game.id
    });
    await updateDoc(doc(db, "leagues", lid, "games", game.id), { calendarEventId: eventId });
  };

  const publishAllToCalendar = async () => {
    const unpublished = divisionGames.filter(g => !g.calendarEventId);
    if (unpublished.length === 0) return;
    if (!window.confirm("Publish " + unpublished.length + " game(s) to the shared Events calendar so all users can see them?")) return;
    for (const g of unpublished) {
      await toggleGameOnCalendar(g);
    }
  };

  const saveGameEdit = async (game) => {
    if (!gameEditDraft) return;
    if (gameEditDraft.homeTeamId === gameEditDraft.awayTeamId) {
      alert("Home and away must be different teams.");
      return;
    }
    const changes = {
      date: gameEditDraft.date,
      startTime: gameEditDraft.startTime,
      endTime: gameEditDraft.endTime,
      location: gameEditDraft.location,
      homeTeamId: gameEditDraft.homeTeamId,
      awayTeamId: gameEditDraft.awayTeamId
    };
    await updateDoc(doc(db, "leagues", lid, "games", game.id), changes);
    if (game.calendarEventId) {
      try {
        await updateDoc(doc(db, "leagues", lid, "fieldEvents", game.calendarEventId), {
          date: changes.date, startTime: changes.startTime, endTime: changes.endTime, field: changes.location,
          title: buildCalendarTitle({ ...game, ...changes }),
          notes: buildCalendarSubtitle({ ...game, ...changes })
        });
      } catch (e) { /* linked calendar event may have been removed independently -- not fatal */ }
    }
    setEditingGameId(null);
    setGameEditDraft(null);
  };

  const toggleGameSelected = (gameId) => {
    setSelectedGameIds(prev => {
      const next = { ...prev };
      if (next[gameId]) delete next[gameId]; else next[gameId] = true;
      return next;
    });
  };

  // MUST iterate visibleGames, not divisionGames. It used to walk divisionGames, which
  // was only correct because nothing narrowed the list; with a team filter active that
  // would select the entire division while showing one team's games -- and
  // bulkDeleteSelectedGames sits directly beside this in the same toolbar.
  const selectAllVisibleGames = () => {
    const next = {};
    visibleGames.forEach(g => { next[g.id] = true; });
    setSelectedGameIds(next);
  };

  const clearGameSelection = () => setSelectedGameIds({});

  const bulkDeleteSelectedGames = async () => {
    const ids = Object.keys(selectedGameIds);
    if (ids.length === 0) return;
    if (!window.confirm("Delete " + ids.length + " selected game(s)? This cannot be undone.")) return;
    for (const id of ids) {
      const g = games.find(x => x.id === id);
      if (g && g.calendarEventId) {
        try { await deleteDoc(doc(db, "leagues", lid, "fieldEvents", g.calendarEventId)); } catch (e) {}
      }
      await deleteDoc(doc(db, "leagues", lid, "games", id));
    }
    setSelectedGameIds({});
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 14 }}>
        Loading games...
      </div>
    );
  }

  if (divisions.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 13 }}>
        Create a division first (in Divisions & Teams above) before scheduling games.
      </div>
    );
  }

  const currentDivision = divisions.find(d => d.id === divisionFilter);
  const divisionTeams = teams.filter(t => t.divisionId === divisionFilter);
  const eligibleLocations = (currentDivision && currentDivision.eligibleLocations) || [];
  const divisionGames = games.filter(g => g.divisionId === divisionFilter).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const nowForDate = new Date();
  const todayStr = nowForDate.getFullYear() + "-" + String(nowForDate.getMonth() + 1).padStart(2, "0") + "-" + String(nowForDate.getDate()).padStart(2, "0");
  const isGamePast = (g) => (g.date || "") < todayStr || g.status === "complete";
  // A team's schedule is every game where it appears in EITHER slot. This narrowing is
  // kept separate from divisionGames because nine other consumers must keep seeing the
  // whole division: Standings (rows come from divisionTeams, so a filtered feed would
  // print every team with one team's record -- wrong and silent), publishAllToCalendar,
  // both CSV exports, the auto-scheduler's existingAutoCount and its two
  // some(g => g.autoGenerated) gates, and the three disabled={divisionGames.length === 0}
  // toolbar buttons. Only the rendered list and Select All follow the filter.
  const visibleGames = teamFilter === "all"
    ? divisionGames
    : divisionGames.filter(g => g.homeTeamId === teamFilter || g.awayTeamId === teamFilter);
  const upcomingGames = visibleGames.filter(g => !isGamePast(g));
  const pastGames = visibleGames.filter(g => isGamePast(g)).slice().reverse();

  const renderGameCard = (g) => {
    const home = teams.find(t => t.id === g.homeTeamId);
    const away = teams.find(t => t.id === g.awayTeamId);
    const closed = closures.some(c => c.field === g.location && c.date === g.date);
    const isComplete = g.status === "complete";
    const isEditingScore = editingScoreId === g.id;
    const isEditingGame = editingGameId === g.id;
    const gameDivision = divisions.find(d => d.id === g.divisionId);
    const gameLocations = (gameDivision && gameDivision.eligibleLocations && gameDivision.eligibleLocations.length ? gameDivision.eligibleLocations : [g.location]).filter(Boolean);
    return (
      <div key={g.id} style={{ background: selectedGameIds[g.id] ? C.blueLt : C.white, border: "1px solid " + (selectedGameIds[g.id] ? C.blueBd : (closed ? C.redBd : C.border)), borderRadius: R.control, padding: "10px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <input type="checkbox" checked={!!selectedGameIds[g.id]} onChange={() => toggleGameSelected(g.id)}
              style={{ marginTop: 4, width: 16, height: 16, flexShrink: 0, cursor: "pointer" }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {away ? away.name : "?"} <span style={{ color: C.muted, fontWeight: 400 }}>vs</span> {home ? home.name : "?"}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {g.date} · {fmtTime(g.startTime)}–{fmtTime(g.endTime)} · {g.location}
              </div>
              {closed && <div style={{ fontSize: 11, color: C.redDark, fontWeight: 700, marginTop: 2 }}>⚠ Location closed this date</div>}
              {/* Same CalendarCheck as the toggle button below, so the row doesn't show
                  two glyphs for one idea. Sized 13 to the 11px caption text, not 17 to
                  the button -- alignItems is flex-start because this line wraps on
                  narrow widths and the icon should hold the first line, not centre
                  itself against a two-line block. */}
              {g.calendarEventId && <div style={{ fontSize: 11, color: C.blue, marginTop: 2, display: "flex", alignItems: "flex-start", gap: 4 }}>
                <CalendarCheckIcon size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>On the shared Events calendar — edits here sync automatically</span>
              </div>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isComplete && !isEditingScore && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: C.greenLt, color: C.green, borderRadius: R.control, padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>
                  {g.awayScore} – {g.homeScore}
                </span>
                <button onClick={() => { setEditingScoreId(g.id); setScoreDraft({ home: String(g.homeScore), away: String(g.awayScore) }); }}
                  style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "4px 8px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                  Edit
                </button>
              </div>
            )}
            {!isComplete && !isEditingScore && (
              <button onClick={() => { setEditingScoreId(g.id); setScoreDraft({ home: "", away: "" }); }}
                style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.green, cursor: "pointer", fontFamily: "inherit" }}>
                Enter score
              </button>
            )}
            {!isEditingGame && (
              <button onClick={() => { setEditingGameId(g.id); setGameEditDraft({ date: g.date, startTime: g.startTime, endTime: g.endTime, location: g.location, homeTeamId: g.homeTeamId, awayTeamId: g.awayTeamId }); }}
                style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                Edit Details
              </button>
            )}
            <button onClick={() => toggleGameOnCalendar(g)}
              title={g.calendarEventId ? "Visible on the shared Events calendar — click to remove" : "Not on the shared Events calendar yet"}
              style={{ background: g.calendarEventId ? C.blueLt : C.grayLt, border: "1px solid " + (g.calendarEventId ? C.blueBd : C.border), borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: g.calendarEventId ? C.blue : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                {g.calendarEventId ? <CalendarCheckIcon size={17} strokeWidth={2} /> : <CalendarPlusIcon size={17} strokeWidth={2} />}
                {g.calendarEventId ? "Showing on Events Calendar" : "Show on Events Calendar"}
              </span>
            </button>
            <button onClick={() => deleteGame(g.id, g.calendarEventId)}
              style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
              Delete
            </button>
          </div>
        </div>
        {isEditingScore && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border, flexWrap: "wrap" }}>
            {/* Away-first, matching the matchup line above. The five elements are
                flat siblings in one flex row and NOTHING associates a name with an
                input -- these are spans, not labels, with no htmlFor and no wrapper.
                Source adjacency is the only thing making a name mean anything, which
                is why each input was moved carrying BOTH its own bindings: the away
                input keeps value={scoreDraft.away} and { away: ... }, the home input
                keeps value={scoreDraft.home} and { home: ... }. Reordering the JSX is
                purely visual; saveScore reads scoreDraft by key and never sees the
                DOM. Swapping the onChange keys instead would render identically and
                write every score into the opposite field. Do not do that.
                aria-labels guard on the team lookup the same way the spans do -- home
                and away are teams.find() results and are undefined for a deleted
                team, and an unguarded .name would throw in render, which with no
                error boundary anywhere in App.js blanks the entire screen. */}
            <span style={{ fontSize: 12, color: C.muted, minWidth: 70 }}>{away ? away.name : "Away"}</span>
            <input type="number" min="0" aria-label={(away ? away.name : "Away") + " score"} value={scoreDraft.away} onChange={e => setScoreDraft(Object.assign({}, scoreDraft, { away: e.target.value }))}
              style={{ width: 60, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
            <span style={{ fontSize: 12, color: C.muted }}>–</span>
            <input type="number" min="0" aria-label={(home ? home.name : "Home") + " score"} value={scoreDraft.home} onChange={e => setScoreDraft(Object.assign({}, scoreDraft, { home: e.target.value }))}
              style={{ width: 60, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
            <span style={{ fontSize: 12, color: C.muted, minWidth: 70 }}>{home ? home.name : "Home"}</span>
            {(() => {
              const bothFilled = scoreDraft.home !== "" && scoreDraft.away !== "";
              const bothEmpty = scoreDraft.home === "" && scoreDraft.away === "";
              const canSave = bothFilled || (bothEmpty && isComplete);
              return (
                <button onClick={() => saveScore(g.id)} disabled={!canSave}
                  style={{ background: canSave ? C.greenMid : C.grayBd, color: C.text, border: "none", borderRadius: R.control, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: canSave ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                  {bothEmpty && isComplete ? "Clear Score" : "Save"}
                </button>
              );
            })()}
            <button onClick={() => setEditingScoreId(null)}
              style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        )}
        {isEditingGame && gameEditDraft && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Home team</label>
                <select value={gameEditDraft.homeTeamId} onChange={e => setGameEditDraft({ ...gameEditDraft, homeTeamId: e.target.value })}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                  {teams.filter(t => t.divisionId === g.divisionId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 3 }}>Away team</label>
                <select value={gameEditDraft.awayTeamId} onChange={e => setGameEditDraft({ ...gameEditDraft, awayTeamId: e.target.value })}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                  {teams.filter(t => t.divisionId === g.divisionId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <input type="date" value={gameEditDraft.date} onChange={e => setGameEditDraft({ ...gameEditDraft, date: e.target.value })}
                style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
              <select value={gameEditDraft.location} onChange={e => setGameEditDraft({ ...gameEditDraft, location: e.target.value })}
                style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                {gameLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
              <input type="time" value={gameEditDraft.startTime} onChange={e => setGameEditDraft({ ...gameEditDraft, startTime: e.target.value })}
                style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
              <input type="time" value={gameEditDraft.endTime} onChange={e => setGameEditDraft({ ...gameEditDraft, endTime: e.target.value })}
                style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => saveGameEdit(g)}
                style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "7px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Save
              </button>
              <button onClick={() => { setEditingGameId(null); setGameEditDraft(null); }}
                style={{ background: C.grayBd, color: C.white, border: "none", borderRadius: R.control, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem", borderTop: "1px solid " + C.border }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Games</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Schedule games for a division. Location and closure conflicts are flagged before saving.</div>
        <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>DIVISION</label>
        <select value={divisionFilter || ""} onChange={e => { setDivisionFilter(e.target.value); setShowNewGame(false); setConflicts([]); setShowAutoScheduler(false); setPreviewResult(null); setSelectedGameIds({}); }}
          style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", background: C.white, width: "100%" }}>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name} ({d.sport})</option>)}
        </select>
      </div>

      {gameSaveMessage && (
        <div style={{ background: gameSaveMessage.type === "success" ? C.greenLt : C.redLt, border: "1px solid " + (gameSaveMessage.type === "success" ? C.greenBd : C.redBd), borderRadius: R.control, padding: "10px 12px", marginBottom: 14, fontSize: 13, fontWeight: 700, color: C.text }}>
          {gameSaveMessage.type === "success" ? "✓ " : "⚠ "}{gameSaveMessage.text}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        {/* Two different disabled conditions here, deliberately: the first two need
            teams to act on (you cannot schedule or generate without a matchup), the
            last three need games to act on (nothing to publish or export). Btn
            supplies the disabled treatment, so these now fade to 0.4 opacity in
            their own colours instead of turning solid C.grayBd. */}
        <Btn onClick={() => { setShowNewGame(v => !v); setConflicts([]); setConfirmedAnyway(false); setConflictOverrideIds([]); }}
          disabled={divisionTeams.length < 2} variant="primary">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <PlusIcon size={17} strokeWidth={2} />
            New Game
          </span>
        </Btn>
        <Btn onClick={() => { setShowAutoScheduler(v => !v); setPreviewResult(null); setReplaceExistingAutoGames(true); }}
          disabled={divisionTeams.length < 2} variant="white">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ZapIcon size={17} strokeWidth={2} />
            Auto-Generate Schedule
          </span>
        </Btn>
        <Btn onClick={publishAllToCalendar}
          disabled={divisionGames.length === 0} variant="white">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <CalendarPlusIcon size={17} strokeWidth={2} />
            Publish All to Events Calendar
          </span>
        </Btn>
        <Btn onClick={exportGamesCSV}
          disabled={divisionGames.length === 0} variant="white">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <DownloadIcon size={17} strokeWidth={2} />
            Export CSV
          </span>
        </Btn>
        <Btn onClick={exportGameChangerCSV}
          disabled={divisionGames.length === 0} variant="white">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <FileDownIcon size={17} strokeWidth={2} />
            GameChanger Export
          </span>
        </Btn>
      </div>

      {divisionTeams.length < 2 && (
        <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: 14 }}>
          This division needs at least 2 teams before games can be scheduled.
        </div>
      )}

      {eligibleLocations.length === 0 && divisionTeams.length >= 2 && (
        <div style={{ fontSize: 12, color: C.amber, marginBottom: 14 }}>
          No locations are marked eligible for this division yet — a schedule cannot be generated until at least one is set. Add locations under ⚙️ Settings → Locations, then mark them eligible for this division above.
        </div>
      )}

      {showAutoScheduler && !previewResult && (
        <div style={{ background: C.white, border: "2px solid " + C.blueBd, borderRadius: R.card, padding: "1rem", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>Auto-Generate Schedule</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Generates a preview using this division's availability windows, game length, break time, play limits, and each team's conflicts. Nothing is saved until you confirm.
          </div>
          {(() => {
            const existingAutoCount = divisionGames.filter(g => g.autoGenerated).length;
            if (existingAutoCount === 0) return null;
            return (
              <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.amber, marginBottom: 12 }}>
                This division already has {existingAutoCount} auto-generated game{existingAutoCount === 1 ? "" : "s"}. Confirming a new schedule below will replace them — any manually added games are kept.
              </div>
            );
          })()}
          {(!currentDivision || !currentDivision.availability || !DAYS.some(d => currentDivision.availability[d.key] && currentDivision.availability[d.key].enabled)) && (
            <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.amber, marginBottom: 12 }}>
              This division has no weekly availability days enabled yet. Set that in Divisions & Teams above before generating.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>SEASON START</label>
              <input type="date" value={seasonStart} onChange={e => setSeasonStart(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>SEASON END</label>
              <input type="date" value={seasonEnd} min={seasonStart} onChange={e => setSeasonEnd(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>GAMES PER TEAM (optional)</label>
            <input type="number" min="1" placeholder="Leave blank for no cap" value={targetGamesPerTeam} onChange={e => setTargetGamesPerTeam(e.target.value)}
              style={{ width: 160, padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              If set, no team will be scheduled for more than this many games total, regardless of the mode below.
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>SCHEDULING MODE</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: R.control, border: "1px solid " + (scheduleMode === "repeats" ? C.blueBd : C.border), background: scheduleMode === "repeats" ? C.blueLt : C.white, cursor: "pointer" }}>
                <input type="radio" checked={scheduleMode === "repeats"} onChange={() => setScheduleMode("repeats")} style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Specific number of times each team plays every opponent</div>
                  <div style={{ fontSize: 11, color: C.muted }}>You choose the number below. If a games-per-team cap is also set above, generation stops early for any team that hits it.</div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: R.control, border: "1px solid " + (scheduleMode === "balanced" ? C.blueBd : C.border), background: scheduleMode === "balanced" ? C.blueLt : C.white, cursor: "pointer" }}>
                <input type="radio" checked={scheduleMode === "balanced"} onChange={() => setScheduleMode("balanced")} style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Automatically balance games as evenly as possible</div>
                  <div style={{ fontSize: 11, color: C.muted }}>Fills every available slot up to the games-per-team cap above (or, with no cap set, fills the whole season), spreading opponents evenly.</div>
                </div>
              </label>
            </div>
          </div>
          {scheduleMode === "repeats" && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>TIMES EACH TEAM PLAYS EVERY OTHER TEAM</label>
              <input type="number" min="1" value={timesEachPlays} onChange={e => setTimesEachPlays(e.target.value)}
                style={{ width: 100, padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            </div>
          )}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: R.control, border: "1px solid " + (balanceHomeAway ? C.blueBd : C.border), background: balanceHomeAway ? C.blueLt : C.white, cursor: "pointer", marginBottom: 12 }}>
            <input type="checkbox" checked={balanceHomeAway} onChange={e => setBalanceHomeAway(e.target.checked)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Balance home and away games as evenly as possible</div>
              <div style={{ fontSize: 11, color: C.muted }}>Whichever team has had fewer home games so far gets home field for each new matchup, so it evens out across the season.</div>
            </div>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={generatePreview} disabled={!seasonStart || !seasonEnd || generating}
              style={{ flex: 1, background: (!seasonStart || !seasonEnd || generating) ? C.grayBd : C.blue, color: C.white, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: (!seasonStart || !seasonEnd) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {generating ? "Generating..." : "Generate Preview"}
            </button>
            <button onClick={() => setShowAutoScheduler(false)}
              style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {previewResult && (
        <div style={{ background: C.white, border: "2px solid " + C.blueBd, borderRadius: R.card, padding: "1rem", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>Preview — nothing saved yet</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            {previewResult.scheduled.length} game{previewResult.scheduled.length === 1 ? "" : "s"} ready to schedule. You can edit or remove any game below before saving.
          </div>

          {previewResult.scheduled.length > 0 && (() => {
            const counts = {};
            previewResult.scheduled.forEach(g => {
              counts[g.homeTeamId] = (counts[g.homeTeamId] || 0) + 1;
              counts[g.awayTeamId] = (counts[g.awayTeamId] || 0) + 1;
            });
            return (
              <div style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text, letterSpacing: "0.06em", marginBottom: 6 }}>GAMES PER TEAM</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {divisionTeams.map(t => (
                    <span key={t.id} style={{ fontSize: 11, background: C.white, borderRadius: R.control, padding: "2px 9px", color: C.text }}>
                      {t.name}: <strong>{counts[t.id] || 0}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {previewResult.scheduled.length > 0 && (() => {
            const homeCounts = {};
            const awayCounts = {};
            previewResult.scheduled.forEach(g => {
              homeCounts[g.homeTeamId] = (homeCounts[g.homeTeamId] || 0) + 1;
              awayCounts[g.awayTeamId] = (awayCounts[g.awayTeamId] || 0) + 1;
            });
            return (
              <div style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: "0.06em", marginBottom: 6 }}>HOME / AWAY BALANCE</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {divisionTeams.map(t => (
                    <span key={t.id} style={{ fontSize: 11, background: C.white, borderRadius: R.control, padding: "2px 9px", color: C.text }}>
                      {t.name}: <strong>{homeCounts[t.id] || 0}H</strong> / <strong>{awayCounts[t.id] || 0}A</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {previewResult.shortfallTeams && previewResult.shortfallTeams.length > 0 && (
            <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "10px 12px", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 4 }}>
                ⚠ {previewResult.shortfallTeams.length} team{previewResult.shortfallTeams.length === 1 ? "" : "s"} couldn't reach the target of {previewResult.shortfallTeams[0].target} games.
              </div>
              <div style={{ fontSize: 11, color: C.amber }}>
                {previewResult.shortfallTeams.map(t => t.name + " (" + t.played + "/" + t.target + ")").join(", ")}
                {eligibleLocations.length === 0
                  ? " — this division has no eligible locations set, which blocks games from being scheduled. Add locations under ⚙️ Settings → Locations, then mark them eligible for this division above and generate again."
                  : " — try extending the season dates or widening the weekly availability window, then generate again."}
              </div>
            </div>
          )}

          {previewResult.unscheduled.length > 0 && (
            <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "10px 12px", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 4 }}>
                ⚠ {previewResult.unscheduled.length} matchup{previewResult.unscheduled.length === 1 ? "" : "s"} couldn't be fit into the available slots.
              </div>
              <div style={{ fontSize: 11, color: C.amber }}>
                Try widening the weekly availability window, extending the season dates, or reducing "times each team plays" — then generate again.
              </div>
            </div>
          )}

          <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {previewResult.scheduled.map((g, i) => {
              const home = teams.find(t => t.id === g.homeTeamId);
              const away = teams.find(t => t.id === g.awayTeamId);
              const isEditing = editingPreviewIndex === i;
              return (
                <div key={i} style={{ background: C.grayLt, borderRadius: R.control, padding: "8px 12px" }}>
                  {!isEditing ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                          {away ? away.name : "?"} <span style={{ color: C.muted, fontWeight: 400 }}>vs</span> {home ? home.name : "?"}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted }}>{g.date} · {fmtTime(g.startTime)}–{fmtTime(g.endTime)} · {g.location}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => startEditPreviewGame(i)}
                          style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                          Edit
                        </button>
                        <button onClick={() => removePreviewGame(i)}
                          style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                        {away ? away.name : "?"} <span style={{ color: C.muted, fontWeight: 400 }}>vs</span> {home ? home.name : "?"}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        <input type="date" value={previewEditDraft.date}
                          onChange={e => setPreviewEditDraft({ ...previewEditDraft, date: e.target.value })}
                          style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                        <select value={previewEditDraft.location}
                          onChange={e => setPreviewEditDraft({ ...previewEditDraft, location: e.target.value })}
                          style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                          {(currentDivision.eligibleLocations || []).map(loc => <option key={loc} value={loc}>{loc}</option>)}
                        </select>
                        <input type="time" value={previewEditDraft.startTime}
                          onChange={e => setPreviewEditDraft({ ...previewEditDraft, startTime: e.target.value })}
                          style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                        <input type="time" value={previewEditDraft.endTime}
                          onChange={e => setPreviewEditDraft({ ...previewEditDraft, endTime: e.target.value })}
                          style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={savePreviewGameEdit}
                          style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "7px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                          Save
                        </button>
                        <button onClick={() => { setEditingPreviewIndex(null); setPreviewEditDraft(null); }}
                          style={{ background: C.grayBd, color: C.white, border: "none", borderRadius: R.control, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {divisionGames.some(g => g.autoGenerated) && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.amberBd, background: C.amberLt, cursor: "pointer", marginBottom: 12 }}>
              <input type="checkbox" checked={replaceExistingAutoGames} onChange={e => setReplaceExistingAutoGames(e.target.checked)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Replace the previous auto-generated schedule for this division</div>
                <div style={{ fontSize: 11, color: C.muted }}>
                  {replaceExistingAutoGames
                    ? "Checked — confirming will delete the old auto-generated games first, so there's nothing left to clean up manually."
                    : "Unchecked — the old auto-generated games will stay, and this new batch will be added on top of them."}
                </div>
              </div>
            </label>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={confirmSchedule} disabled={savingSchedule || previewResult.scheduled.length === 0}
              style={{ flex: 1, minWidth: 180, background: (savingSchedule || previewResult.scheduled.length === 0) ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              {savingSchedule ? "Saving..." : (divisionGames.some(g => g.autoGenerated) ? (replaceExistingAutoGames ? "Confirm & Replace Schedule" : "Confirm & Add to Schedule") : "Confirm & Save Schedule")}
            </button>
            <button onClick={editInputsAndRegenerate}
              style={{ background: C.blueLt, color: C.blue, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "10px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              ✏️ Edit Inputs & Regenerate
            </button>
            <button onClick={() => { discardPreview(); setShowAutoScheduler(false); }}
              style={{ background: C.redLt, color: C.redDark, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Discard & Close
            </button>
          </div>
        </div>
      )}

      {showNewGame && (
        <div style={{ background: C.white, border: "2px solid " + C.greenBd, borderRadius: R.card, padding: "1rem", marginBottom: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>HOME TEAM</label>
                <select value={newGame.homeTeamId} onChange={e => setNewGame(Object.assign({}, newGame, { homeTeamId: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white, width: "100%" }}>
                  <option value="">Select...</option>
                  {divisionTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>AWAY TEAM</label>
                <select value={newGame.awayTeamId} onChange={e => setNewGame(Object.assign({}, newGame, { awayTeamId: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white, width: "100%" }}>
                  <option value="">Select...</option>
                  {divisionTeams.filter(t => t.id !== newGame.homeTeamId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>LOCATION</label>
              <select value={newGame.location} onChange={e => setNewGame(Object.assign({}, newGame, { location: e.target.value }))}
                style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white, width: "100%" }}>
                <option value="">Select...</option>
                {eligibleLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>DATE</label>
                <input type="date" value={newGame.date} onChange={e => setNewGame(Object.assign({}, newGame, { date: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>START</label>
                <input type="time" value={newGame.startTime} onChange={e => setNewGame(Object.assign({}, newGame, { startTime: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>END</label>
                <input type="time" value={newGame.endTime} onChange={e => setNewGame(Object.assign({}, newGame, { endTime: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
              </div>
            </div>

            {conflicts.length > 0 && (() => {
              const ruleConflicts = conflicts.filter(c => c.type === "blackout" || c.type === "closure");
              const itemConflicts = conflicts.filter(c => c.type === "game" || c.type === "event");
              return (
              <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.redDark, marginBottom: 6 }}>⚠ Conflict{conflicts.length > 1 ? "s" : ""} found</div>

                {ruleConflicts.map((c, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.redDark, marginBottom: 3 }}>• {c.label}</div>
                ))}
                {ruleConflicts.length > 0 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, marginBottom: itemConflicts.length > 0 ? 10 : 0, cursor: "pointer" }}>
                    <input type="checkbox" checked={confirmedAnyway} onChange={e => setConfirmedAnyway(e.target.checked)} />
                    <span style={{ fontSize: 12, color: C.redDark, fontWeight: 600 }}>Schedule anyway</span>
                  </label>
                )}

                {itemConflicts.map(c => (
                  <label key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 8px", borderRadius: R.control, background: conflictOverrideIds.includes(c.id) ? "rgba(183,28,28,0.08)" : "transparent", cursor: "pointer", marginBottom: 4 }}>
                    <input type="checkbox" checked={conflictOverrideIds.includes(c.id)} onChange={e => setConflictOverrideIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 12, color: C.redDark }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: conflictOverrideIds.includes(c.id) ? C.red : C.muted, fontWeight: conflictOverrideIds.includes(c.id) ? 700 : 400 }}>
                        {conflictOverrideIds.includes(c.id) ? "Will be cancelled and deleted to make room" : "Leave it in place (your new game will overlap it)"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              );
            })()}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button onClick={handleSaveClick} disabled={!newGame.homeTeamId || !newGame.awayTeamId || !newGame.location || !newGame.date || (conflicts.some(c => c.type === "blackout" || c.type === "closure") && !confirmedAnyway)}
                style={{ flex: 1, background: (!newGame.homeTeamId || !newGame.awayTeamId || !newGame.location || !newGame.date || (conflicts.some(c => c.type === "blackout" || c.type === "closure") && !confirmedAnyway)) ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                {conflicts.length > 0 ? "Save Game" : "Save Game"}
              </button>
              <button onClick={() => { setShowNewGame(false); setConflicts([]); setConflictOverrideIds([]); setConfirmedAnyway(false); }}
                style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setGamesListExpanded(v => !v)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: C.blueLt, border: "0.5px solid " + C.blueBd, borderLeft: "6px solid " + C.blue, borderRadius: R.card, padding: "10px 14px", cursor: "pointer", fontFamily: "inherit", marginBottom: gamesListExpanded ? 8 : 0 }}>
        <span style={{ fontSize: 18, fontFamily: T.display, fontWeight: 700, textTransform: "uppercase", lineHeight: 1.1, color: C.text }}>
          Games ({teamFilter === "all" ? divisionGames.length : visibleGames.length + " of " + divisionGames.length}){upcomingGames.length > 0 && pastGames.length > 0 ? " — " + upcomingGames.length + " upcoming" : ""}
        </span>
        <span style={{ fontSize: 12, color: C.muted }}>{gamesListExpanded ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {gamesListExpanded && (
        divisionGames.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1.5rem", color: C.muted, fontSize: 13 }}>
            No games scheduled for this division yet.
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {/* appearance:none is doing the actual work here. The colours below were
                    already C.border/C.text -- identical to the Select All button beside it --
                    but a <select> without an appearance reset draws the native OS widget,
                    so the platform accent (blue on stock macOS, purple/pink if the user has
                    changed their system accent) painted over our declared border regardless.
                    Removing the native appearance also removes the dropdown arrow, hence the
                    inline SVG caret, tinted with C.muted so it stays on the neutral palette. */}
                <select value={teamFilter} onChange={e => { setTeamFilter(e.target.value); setSelectedGameIds({}); }}
                  style={{ padding: "5px 26px 5px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontWeight: 700, color: C.text, backgroundColor: C.white, fontFamily: "inherit", cursor: "pointer", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23" + C.muted.slice(1) + "' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 9px center" }}>
                  <option value="all">All Games</option>
                  {divisionTeams.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <button onClick={selectAllVisibleGames}
                  style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                  Select All
                </button>
                {Object.keys(selectedGameIds).length > 0 && (
                  <button onClick={clearGameSelection}
                    style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                    Clear Selection
                  </button>
                )}
              </div>
              {Object.keys(selectedGameIds).length > 0 && (
                <button onClick={bulkDeleteSelectedGames}
                  style={{ background: C.redFill, color: C.white, border: "none", borderRadius: R.control, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Trash2Icon size={17} strokeWidth={2} />
                    Delete Selected ({Object.keys(selectedGameIds).length})
                  </span>
                </button>
              )}
            </div>
            {visibleGames.length === 0 ? (
              /* Team filter matched nothing. Deliberately NOT the "No games scheduled for
                 this division yet" copy above -- the division does have games, and reusing
                 that wording here would read as data loss. Names the team and states the
                 division total so it's obvious this is a filter, not an empty schedule.
                 Rendered after the toolbar so the dropdown stays reachable to undo it. */
              <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "8px 2px" }}>
                No games for {(divisionTeams.find(t => t.id === teamFilter) || {}).name || "this team"} in this division. It has {divisionGames.length} game{divisionGames.length === 1 ? "" : "s"} scheduled across all teams — choose All Games to see them.
              </div>
            ) : (
              <>
                {upcomingGames.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {upcomingGames.map(g => renderGameCard(g))}
                  </div>
                )}
                {upcomingGames.length === 0 && (
                  <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "8px 2px" }}>
                    No upcoming games — all scheduled games are in the past.
                  </div>
                )}
                {pastGames.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", marginBottom: 8 }}>
                      PAST GAMES ({pastGames.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, opacity: 0.85 }}>
                      {pastGames.map(g => renderGameCard(g))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      )}

      {divisionTeams.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>Standings</div>
          {(() => {
            const rows = divisionTeams.map(t => ({ team: t, w: 0, l: 0, tcount: 0, pts: 0 }));
            const byId = {};
            rows.forEach(r => { byId[r.team.id] = r; });
            divisionGames.filter(g => g.status === "complete" && g.homeScore !== null && g.awayScore !== null).forEach(g => {
              const h = byId[g.homeTeamId], a = byId[g.awayTeamId];
              if (!h || !a) return;
              if (g.homeScore > g.awayScore) { h.w++; a.l++; h.pts += 2; }
              else if (g.homeScore < g.awayScore) { a.w++; h.l++; a.pts += 2; }
              else { h.tcount++; a.tcount++; h.pts += 1; a.pts += 1; }
            });
            rows.sort((a, b) => b.pts - a.pts || b.w - a.w);
            return (
              <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.grayLt }}>
                      <td style={{ padding: "8px 12px", fontWeight: 700, color: C.muted, fontSize: 11 }}>TEAM</td>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: C.muted, fontSize: 11, textAlign: "center" }}>W</td>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: C.muted, fontSize: 11, textAlign: "center" }}>L</td>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: C.muted, fontSize: 11, textAlign: "center" }}>T</td>
                      <td style={{ padding: "8px 12px", fontWeight: 700, color: C.muted, fontSize: 11, textAlign: "center" }}>PTS</td>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.team.id} style={{ borderTop: "1px solid " + C.border }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: C.text }}>{r.team.name}</td>
                        <td style={{ padding: "8px 8px", textAlign: "center", color: C.text }}>{r.w}</td>
                        <td style={{ padding: "8px 8px", textAlign: "center", color: C.text }}>{r.l}</td>
                        <td style={{ padding: "8px 8px", textAlign: "center", color: C.text }}>{r.tcount}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, color: C.green }}>{r.pts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function PlayersManagement({ lid, seasonId, isMobile }) {
  const [players, setPlayers] = React.useState([]);
  const [divisions, setDivisions] = React.useState([]);
  const [teams, setTeams] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [divisionFilter, setDivisionFilter] = React.useState("all");
  const [showAddPlayer, setShowAddPlayer] = React.useState(false);
  const emptyPlayerForm = { firstName: "", lastName: "", divisionId: "", gender: "", birthDate: "", parentFirstName: "", parentLastName: "", email: "", cellphone: "" };
  const [newPlayer, setNewPlayer] = React.useState(emptyPlayerForm);
  const [editingPlayerId, setEditingPlayerId] = React.useState(null);
  const [expandedPlayerId, setExpandedPlayerId] = React.useState(null);
  const [showAssignView, setShowAssignView] = React.useState(false);
  const [assignDivisionId, setAssignDivisionId] = React.useState(null);
  const [selectedForAssign, setSelectedForAssign] = React.useState({});
  const [dragPlayerId, setDragPlayerId] = React.useState(null);
  const [dragOverTeamKey, setDragOverTeamKey] = React.useState(null);
  const [editPlayerDraft, setEditPlayerDraft] = React.useState(null);
  const [csvStep, setCsvStep] = React.useState(null); // null | 'divisions' | 'teams' | 'confirm'
  const [csvRows, setCsvRows] = React.useState([]);
  const [csvProgramLabel, setCsvProgramLabel] = React.useState("");
  const [csvDivisionMap, setCsvDivisionMap] = React.useState({});
  const [csvTeamMap, setCsvTeamMap] = React.useState({});
  const [csvImporting, setCsvImporting] = React.useState(false);
  const [csvImportResult, setCsvImportResult] = React.useState(null);
  const [csvError, setCsvError] = React.useState("");

  React.useEffect(() => {
    if (!lid || !seasonId) { setPlayers([]); setDivisions([]); setTeams([]); setLoading(false); return; }
    const unsubPlayers = onSnapshot(query(collection(db, "leagues", lid, "players"), where("seasonId", "==", seasonId)), snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubDivisions = onSnapshot(query(collection(db, "leagues", lid, "divisions"), where("seasonId", "==", seasonId)), snap => {
      setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubTeams = onSnapshot(collection(db, "leagues", lid, "teams"), snap => {
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubPlayers(); unsubDivisions(); unsubTeams(); };
  }, [lid, seasonId]);

  // ─── CSV IMPORT (Sports Connect Enrollment_Details.csv export) ────────
  const parseCsvText = (text) => {
    // Minimal RFC4180-style parser: handles quoted fields, embedded commas,
    // escaped quotes ("") inside quotes, and \r\n / \n line endings.
    const rows = [];
    let row = [], field = "", inQuotes = false;
    const clean = text.replace(/^\uFEFF/, ""); // strip BOM if present
    for (let i = 0; i < clean.length; i++) {
      const c = clean[i];
      if (inQuotes) {
        if (c === '"') {
          if (clean[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
        } else { field += c; }
      } else {
        if (c === '"') { inQuotes = true; }
        else if (c === ',') { row.push(field); field = ""; }
        else if (c === "\n" || c === "\r") {
          if (c === "\r" && clean[i + 1] === "\n") i++;
          row.push(field); field = "";
          if (row.length > 1 || row[0] !== "") rows.push(row);
          row = [];
        } else { field += c; }
      }
    }
    if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
    return rows;
  };

  const norm = (s) => (s || "").trim().toLowerCase();

  const parseBirthDate = (str) => {
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str + "T12:00:00"); // manual-entry date input
    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); // Sports Connect export, e.g. "5/6/19"
    if (m) {
      let [, month, day, year] = m;
      year = year.length === 2 ? "20" + year : year;
      const d = new Date(Number(year), Number(month) - 1, Number(day), 12);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const calculateAge = (birthDateStr) => {
    const bd = parseBirthDate(birthDateStr);
    if (!bd) return null;
    const today = new Date();
    let age = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
    return (age >= 0 && age < 100) ? age : null;
  };

  const fmtBirthDate = (birthDateStr) => {
    const bd = parseBirthDate(birthDateStr);
    return bd ? bd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : birthDateStr;
  };

  const handleCsvFile = (file) => {
    setCsvError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCsvText(String(e.target.result));
        if (parsed.length < 2) { setCsvError("This file doesn't have any player rows in it."); return; }
        const headers = parsed[0].map(h => h.trim());
        const colIndex = (name) => headers.findIndex(h => norm(h) === norm(name));
        const idx = {
          division: colIndex("Division Name"),
          firstName: colIndex("Player First Name"),
          lastName: colIndex("Player Last Name"),
          gender: colIndex("Player Gender"),
          birthDate: colIndex("Player Birth Date"),
          parentFirst: colIndex("Account First Name"),
          parentLast: colIndex("Account Last Name"),
          email: colIndex("User Email"),
          cellphone: colIndex("Cellphone"),
          team: colIndex("Team Name"),
          program: colIndex("Program Name")
        };
        if (idx.division === -1 || idx.firstName === -1 || idx.lastName === -1) {
          setCsvError("This file is missing one of the required columns: Division Name, Player First Name, Player Last Name.");
          return;
        }
        const rows = parsed.slice(1).filter(r => r.some(c => c && c.trim())).map(r => ({
          divisionName: (r[idx.division] || "").trim(),
          firstName: (r[idx.firstName] || "").trim(),
          lastName: (r[idx.lastName] || "").trim(),
          gender: idx.gender > -1 ? (r[idx.gender] || "").trim() : "",
          birthDate: idx.birthDate > -1 ? (r[idx.birthDate] || "").trim() : "",
          parentFirstName: idx.parentFirst > -1 ? (r[idx.parentFirst] || "").trim() : "",
          parentLastName: idx.parentLast > -1 ? (r[idx.parentLast] || "").trim() : "",
          email: idx.email > -1 ? (r[idx.email] || "").trim() : "",
          cellphone: idx.cellphone > -1 ? (r[idx.cellphone] || "").trim() : "",
          teamName: idx.team > -1 ? (r[idx.team] || "").trim() : "",
          programName: idx.program > -1 ? (r[idx.program] || "").trim() : ""
        })).filter(r => r.divisionName && r.firstName && r.lastName);

        if (rows.length === 0) { setCsvError("No usable player rows found — check that Division Name, Player First Name, and Player Last Name are filled in."); return; }

        setCsvProgramLabel(rows[0].programName || "");
        setCsvRows(rows);

        // Build the division resolution map: auto-match to existing divisions by
        // name (case/whitespace-insensitive), default unmatched ones to "create new".
        const uniqueDivisions = [...new Set(rows.map(r => r.divisionName))];
        const divMap = {};
        uniqueDivisions.forEach(name => {
          const match = divisions.find(d => norm(d.name) === norm(name));
          divMap[name] = match ? { action: "existing", targetId: match.id } : { action: "create", targetId: null };
        });
        setCsvDivisionMap(divMap);
        setCsvStep("divisions");
      } catch (err) {
        setCsvError("Couldn't read that file. Make sure it's a CSV export from Sports Connect.");
      }
    };
    reader.readAsText(file);
  };

  const proceedToTeamsStep = () => {
    // Now that divisions are resolved, figure out which Team Name values need
    // resolving too -- skip blanks and "Unallocated" (Sports Connect's way of
    // saying a player hasn't been put on a team yet).
    const teamMap = {};
    csvRows.forEach(r => {
      if (!r.teamName || norm(r.teamName) === "unallocated") return;
      const key = r.divisionName + "||" + r.teamName;
      if (teamMap[key]) return;
      const divResolution = csvDivisionMap[r.divisionName];
      let match = null;
      if (divResolution && divResolution.action === "existing") {
        match = teams.find(t => t.divisionId === divResolution.targetId && norm(t.name) === norm(r.teamName));
      }
      teamMap[key] = match ? { action: "existing", targetId: match.id } : { action: "create", targetId: null };
    });
    setCsvTeamMap(teamMap);
    setCsvStep("teams");
  };

  const runCsvImport = async () => {
    setCsvImporting(true);
    const newDivisionIds = {};
    const newTeamIds = {};

    for (const [rawName, resolution] of Object.entries(csvDivisionMap)) {
      if (resolution.action === "create") {
        const id = "div_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
        await setDoc(doc(db, "leagues", lid, "divisions", id), {
          id, seasonId, name: rawName, sport: "Baseball", eligibleLocations: [],
          maxGamesPerDay: null, maxGamesPerWeek: null, gameDurationMinutes: null, breakBetweenGamesMinutes: null,
          availability: emptyAvailability(), createdAt: new Date().toISOString()
        });
        newDivisionIds[rawName] = id;
      }
    }

    const resolvedDivisionId = (rawName) => {
      const r = csvDivisionMap[rawName];
      return r.action === "existing" ? r.targetId : newDivisionIds[rawName];
    };

    for (const [key, resolution] of Object.entries(csvTeamMap)) {
      if (resolution.action === "create") {
        const [divName, teamName] = key.split("||");
        const id = "team_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
        await setDoc(doc(db, "leagues", lid, "teams", id), {
          id, name: teamName, divisionId: resolvedDivisionId(divName), createdAt: new Date().toISOString()
        });
        newTeamIds[key] = id;
      }
    }

    let created = 0;
    for (const r of csvRows) {
      const divisionId = resolvedDivisionId(r.divisionName);
      let teamId = null;
      if (r.teamName && norm(r.teamName) !== "unallocated") {
        const key = r.divisionName + "||" + r.teamName;
        const tr = csvTeamMap[key];
        teamId = tr ? (tr.action === "existing" ? tr.targetId : newTeamIds[key]) : null;
      }
      const id = "player_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
      await setDoc(doc(db, "leagues", lid, "players", id), {
        id, seasonId, divisionId, teamId,
        firstName: r.firstName, lastName: r.lastName, gender: r.gender, birthDate: r.birthDate,
        parentFirstName: r.parentFirstName, parentLastName: r.parentLastName,
        email: r.email, cellphone: r.cellphone, createdAt: new Date().toISOString()
      });
      created++;
    }

    setCsvImportResult({
      players: created,
      divisionsCreated: Object.keys(newDivisionIds).length,
      teamsCreated: Object.keys(newTeamIds).length
    });
    setCsvImporting(false);
    setCsvStep("confirm");
  };

  const closeCsvImport = () => {
    setCsvStep(null);
    setCsvRows([]);
    setCsvDivisionMap({});
    setCsvTeamMap({});
    setCsvImportResult(null);
    setCsvError("");
    setCsvProgramLabel("");
  };

  const downloadCsvTemplate = () => {
    const headers = ["Division Name", "Player First Name", "Player Last Name", "Player Gender", "Player Birth Date", "Account First Name", "Account Last Name", "User Email", "Cellphone", "Team Name", "Program Name"];
    const exampleRow = ["Majors", "Jane", "Smith", "Female", "5/6/16", "John", "Smith", "john.smith@example.com", "555-123-4567", "Unallocated", ""];
    const escapeCell = (val) => (/[",\n]/.test(val) ? '"' + val.replace(/"/g, '""') + '"' : val);
    const csv = [headers, exampleRow].map(row => row.map(escapeCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "groundspal-player-import-template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const addPlayer = async () => {
    if (!newPlayer.firstName.trim() || !newPlayer.lastName.trim() || !newPlayer.divisionId || !lid || !seasonId) return;
    const id = "player_" + Date.now();
    await setDoc(doc(db, "leagues", lid, "players", id), {
      id, seasonId,
      firstName: newPlayer.firstName.trim(), lastName: newPlayer.lastName.trim(),
      divisionId: newPlayer.divisionId, teamId: null,
      gender: newPlayer.gender, birthDate: newPlayer.birthDate,
      parentFirstName: newPlayer.parentFirstName.trim(), parentLastName: newPlayer.parentLastName.trim(),
      email: newPlayer.email.trim(), cellphone: newPlayer.cellphone.trim(),
      createdAt: new Date().toISOString()
    });
    setNewPlayer(emptyPlayerForm);
    setShowAddPlayer(false);
  };

  const startEditPlayer = (p) => {
    setEditingPlayerId(p.id);
    setEditPlayerDraft({ divisionId: p.divisionId, teamId: p.teamId || "" });
  };

  const savePlayerEdit = async (playerId) => {
    if (!editPlayerDraft) return;
    await updateDoc(doc(db, "leagues", lid, "players", playerId), {
      divisionId: editPlayerDraft.divisionId,
      teamId: editPlayerDraft.teamId || null
    });
    setEditingPlayerId(null);
    setEditPlayerDraft(null);
  };

  const deletePlayer = async (playerId) => {
    if (!window.confirm("Remove this player?")) return;
    await deleteDoc(doc(db, "leagues", lid, "players", playerId));
  };

  // ─── TEAM ASSIGNMENT (multi-select + drag-and-drop) ────────────────
  const toggleAssignSelect = (playerId) => {
    setSelectedForAssign(prev => {
      const next = { ...prev };
      if (next[playerId]) delete next[playerId]; else next[playerId] = true;
      return next;
    });
  };

  const movePlayersToTeam = async (playerIds, teamId) => {
    for (const id of playerIds) {
      await updateDoc(doc(db, "leagues", lid, "players", id), { teamId: teamId || null });
    }
    setSelectedForAssign({});
  };

  const movePlayerToTeamViaDrag = async (playerId, teamId) => {
    await updateDoc(doc(db, "leagues", lid, "players", playerId), { teamId: teamId || null });
  };

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 14 }}>Loading players...</div>;
  }

  const visiblePlayers = (divisionFilter === "all" ? players : players.filter(p => p.divisionId === divisionFilter))
    .slice().sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));

  return (
    <div style={{ padding: "1rem", borderTop: "1px solid " + C.border }}>
      {csvStep && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.white, borderRadius: R.card, padding: "1.5rem", maxWidth: 520, width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>

            {csvStep === "divisions" && (
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 4 }}>Step 1 of 2 — Divisions</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                  {csvRows.length} player{csvRows.length === 1 ? "" : "s"} found in the file.
                  {csvProgramLabel && " This file is for \"" + csvProgramLabel + "\" — make sure that matches the season you have selected."}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                  Review how each division from the file should map into GroundsPal.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {Object.keys(csvDivisionMap).map(rawName => {
                    const resolution = csvDivisionMap[rawName];
                    const rowCount = csvRows.filter(r => r.divisionName === rawName).length;
                    return (
                      <div key={rawName} style={{ background: C.grayLt, borderRadius: R.control, padding: "10px 12px" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                          "{rawName}" <span style={{ fontWeight: 400, color: C.muted }}>({rowCount} player{rowCount === 1 ? "" : "s"})</span>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <select value={resolution.action === "existing" ? resolution.targetId : "create"}
                            onChange={e => {
                              const val = e.target.value;
                              setCsvDivisionMap({ ...csvDivisionMap, [rawName]: val === "create" ? { action: "create", targetId: null } : { action: "existing", targetId: val } });
                            }}
                            style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                            <option value="create">+ Create new division "{rawName}"</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>Map to existing: {d.name}</option>)}
                          </select>
                          {resolution.action === "existing" && (
                            <span style={{ fontSize: 11, color: C.blue, fontWeight: 700 }}>✓ matched</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={proceedToTeamsStep}
                    style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                    Continue →
                  </button>
                  <button onClick={closeCsvImport}
                    style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {csvStep === "teams" && (
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 4 }}>Step 2 of 2 — Teams</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                  {Object.keys(csvTeamMap).length === 0
                    ? "No team assignments in this file — every player will import unassigned, ready to sort into teams."
                    : "Review how each team from the file should map into GroundsPal."}
                </div>
                {Object.keys(csvTeamMap).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {Object.keys(csvTeamMap).map(key => {
                      const [divName, teamName] = key.split("||");
                      const resolution = csvTeamMap[key];
                      const divResolution = csvDivisionMap[divName];
                      const existingTeamsInDiv = divResolution.action === "existing" ? teams.filter(t => t.divisionId === divResolution.targetId) : [];
                      const rowCount = csvRows.filter(r => r.divisionName === divName && r.teamName === teamName).length;
                      return (
                        <div key={key} style={{ background: C.grayLt, borderRadius: R.control, padding: "10px 12px" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                            "{teamName}" <span style={{ fontWeight: 400, color: C.muted }}>({divName} · {rowCount} player{rowCount === 1 ? "" : "s"})</span>
                          </div>
                          <select value={resolution.action === "existing" ? resolution.targetId : "create"}
                            onChange={e => {
                              const val = e.target.value;
                              setCsvTeamMap({ ...csvTeamMap, [key]: val === "create" ? { action: "create", targetId: null } : { action: "existing", targetId: val } });
                            }}
                            style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                            <option value="create">+ Create new team "{teamName}"</option>
                            {existingTeamsInDiv.map(t => <option key={t.id} value={t.id}>Map to existing: {t.name}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={runCsvImport} disabled={csvImporting}
                    style={{ flex: 1, background: csvImporting ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: csvImporting ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                    {csvImporting ? "Importing..." : "Import " + csvRows.length + " Player" + (csvRows.length === 1 ? "" : "s")}
                  </button>
                  <button onClick={() => setCsvStep("divisions")} disabled={csvImporting}
                    style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                    ← Back
                  </button>
                </div>
              </div>
            )}

            {csvStep === "confirm" && csvImportResult && (
              <div>
                <div style={{ fontSize: 32, textAlign: "center", marginBottom: 8 }}>✅</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 12, textAlign: "center" }}>Import complete</div>
                <div style={{ fontSize: 13, color: C.text, textAlign: "center", lineHeight: 1.8, marginBottom: 16 }}>
                  {csvImportResult.players} player{csvImportResult.players === 1 ? "" : "s"} imported<br />
                  {csvImportResult.divisionsCreated > 0 && <>{csvImportResult.divisionsCreated} new division{csvImportResult.divisionsCreated === 1 ? "" : "s"} created<br /></>}
                  {csvImportResult.teamsCreated > 0 && <>{csvImportResult.teamsCreated} new team{csvImportResult.teamsCreated === 1 ? "" : "s"} created<br /></>}
                </div>
                <button onClick={closeCsvImport}
                  style={{ width: "100%", background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  Done
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Players</div>
          <div style={{ fontSize: 13, color: C.muted }}>{players.length} player{players.length === 1 ? "" : "s"} this season</div>
        </div>
        {(() => {
          const assignBtn = (
            <Btn onClick={() => { setShowAssignView(v => !v); setSelectedForAssign({}); if (!assignDivisionId && divisions[0]) setAssignDivisionId(divisions[0].id); }}
              disabled={divisions.length === 0} variant={showAssignView ? "secondary" : "white"} full={isMobile}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <UserPlusIcon size={17} strokeWidth={2} />
                {showAssignView ? "Done Assigning" : "Assign to Teams"}
              </span>
            </Btn>
          );
          const templateBtn = (
            <Btn onClick={downloadCsvTemplate} variant="white" full={isMobile}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <DownloadIcon size={17} strokeWidth={2} />
                Download Template
              </span>
            </Btn>
          );
          const importBtn = (
            <Btn as="label" variant="white" full={isMobile}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <UploadIcon size={17} strokeWidth={2} />
                Import CSV
              </span>
              <input type="file" accept=".csv" style={{ display: "none" }}
                onChange={e => { if (e.target.files[0]) handleCsvFile(e.target.files[0]); e.target.value = ""; }} />
            </Btn>
          );
          const addBtn = (
            <Btn onClick={() => setShowAddPlayer(v => !v)} disabled={divisions.length === 0} variant="primary" full={isMobile}>
              + Add Player
            </Btn>
          );
          if (isMobile) {
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                {addBtn}
                {assignBtn}
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>{templateBtn}</div>
                  <div style={{ flex: 1 }}>{importBtn}</div>
                </div>
              </div>
            );
          }
          return (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {assignBtn}
              {templateBtn}
              {importBtn}
              {addBtn}
            </div>
          );
        })()}
      </div>

      {csvError && (
        <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 12, color: C.redDark, marginBottom: 14 }}>
          {csvError}
        </div>
      )}

      {divisions.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: 14 }}>
          Create at least one division above before adding players.
        </div>
      )}

      {showAddPlayer && divisions.length > 0 && (
        <div style={{ background: C.white, border: "2px solid " + C.greenBd, borderRadius: R.card, padding: "1rem", marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <input placeholder="First name *" value={newPlayer.firstName} onChange={e => setNewPlayer({ ...newPlayer, firstName: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            <input placeholder="Last name *" value={newPlayer.lastName} onChange={e => setNewPlayer({ ...newPlayer, lastName: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
            <select value={newPlayer.divisionId} onChange={e => setNewPlayer({ ...newPlayer, divisionId: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}>
              <option value="">Division *</option>
              {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={newPlayer.gender} onChange={e => setNewPlayer({ ...newPlayer, gender: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}>
              <option value="">Gender</option><option>Male</option><option>Female</option>
            </select>
            <input type="date" placeholder="Birth date" value={newPlayer.birthDate} onChange={e => setNewPlayer({ ...newPlayer, birthDate: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <input placeholder="Parent first name" value={newPlayer.parentFirstName} onChange={e => setNewPlayer({ ...newPlayer, parentFirstName: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            <input placeholder="Parent last name" value={newPlayer.parentLastName} onChange={e => setNewPlayer({ ...newPlayer, parentLastName: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <input placeholder="Email" value={newPlayer.email} onChange={e => setNewPlayer({ ...newPlayer, email: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
            <input placeholder="Cellphone" value={newPlayer.cellphone} onChange={e => setNewPlayer({ ...newPlayer, cellphone: e.target.value })}
              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addPlayer} disabled={!newPlayer.firstName.trim() || !newPlayer.lastName.trim() || !newPlayer.divisionId}
              style={{ flex: 1, background: (!newPlayer.firstName.trim() || !newPlayer.lastName.trim() || !newPlayer.divisionId) ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Add Player
            </button>
            <button onClick={() => { setShowAddPlayer(false); setNewPlayer(emptyPlayerForm); }}
              style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAssignView && divisions.length > 0 && (() => {
        const assignDivision = divisions.find(d => d.id === assignDivisionId) || divisions[0];
        const assignDivisionTeams = teams.filter(t => t.divisionId === assignDivision.id);
        const divisionPlayers = players.filter(p => p.divisionId === assignDivision.id);
        const unassignedPlayers = divisionPlayers.filter(p => !p.teamId);
        const selectedCount = Object.keys(selectedForAssign).length;

        const renderPlayerChip = (p) => (
          <div key={p.id}
            draggable
            onDragStart={() => setDragPlayerId(p.id)}
            onDragEnd={() => setDragPlayerId(null)}
            onClick={() => toggleAssignSelect(p.id)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderRadius: R.control, marginBottom: 4, cursor: "pointer",
              background: selectedForAssign[p.id] ? C.purpleLt : C.white,
              border: "1px solid " + (selectedForAssign[p.id] ? C.purpleBd : C.border),
              opacity: dragPlayerId === p.id ? 0.4 : 1
            }}>
            <input type="checkbox" checked={!!selectedForAssign[p.id]} onChange={() => toggleAssignSelect(p.id)} style={{ cursor: "pointer" }} />
            <span style={{ fontSize: 12, color: C.text }}>{p.firstName} {p.lastName}</span>
          </div>
        );

        const columnDropProps = (teamId) => ({
          onDragOver: (e) => { e.preventDefault(); setDragOverTeamKey(teamId || "unassigned"); },
          onDragLeave: () => setDragOverTeamKey(null),
          onDrop: (e) => {
            e.preventDefault();
            const draggedId = dragPlayerId;
            setDragOverTeamKey(null);
            setDragPlayerId(null);
            if (draggedId) movePlayerToTeamViaDrag(draggedId, teamId);
          }
        });

        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>DIVISION</label>
              <select value={assignDivision.id} onChange={e => { setAssignDivisionId(e.target.value); setSelectedForAssign({}); }}
                style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", background: C.white, width: "100%" }}>
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
              Tap to select players (or drag one directly), then move them onto a team. Works the same on desktop and mobile.
            </div>

            {selectedCount > 0 && (
              <div style={{ background: C.purpleLt, border: "1px solid " + C.purpleBd, borderRadius: R.control, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>{selectedCount} selected</span>
                <select onChange={e => { if (e.target.value !== "") movePlayersToTeam(Object.keys(selectedForAssign), e.target.value === "unassigned" ? null : e.target.value); e.target.value = ""; }}
                  defaultValue=""
                  style={{ padding: "6px 10px", borderRadius: R.control, border: "1px solid " + C.purpleBd, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                  <option value="" disabled>Move to team...</option>
                  <option value="unassigned">Unassigned</option>
                  {assignDivisionTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <button onClick={() => setSelectedForAssign({})}
                  style={{ background: "none", border: "none", color: C.purple, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Clear
                </button>
              </div>
            )}

            {assignDivisionTeams.length === 0 ? (
              <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: 12 }}>
                No teams in this division yet — create teams above before assigning players.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <div {...columnDropProps(null)}
                  style={{ background: dragOverTeamKey === "unassigned" ? C.grayBd : C.grayLt, borderRadius: R.control, padding: 10, border: "1px dashed " + C.border, minHeight: 120 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, marginBottom: 8 }}>Unassigned ({unassignedPlayers.length})</div>
                  {unassignedPlayers.map(renderPlayerChip)}
                </div>
                {assignDivisionTeams.map(t => {
                  const teamPlayers = divisionPlayers.filter(p => p.teamId === t.id);
                  return (
                    <div key={t.id} {...columnDropProps(t.id)}
                      style={{ background: dragOverTeamKey === t.id ? C.greenLt : C.white, borderRadius: R.control, padding: 10, border: "1px solid " + (dragOverTeamKey === t.id ? C.greenBd : C.border), minHeight: 120 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>{t.name} ({teamPlayers.length})</div>
                      {teamPlayers.length === 0 && <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>Drop players here</div>}
                      {teamPlayers.map(renderPlayerChip)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {!showAssignView && divisions.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>FILTER BY DIVISION</label>
          <select value={divisionFilter} onChange={e => setDivisionFilter(e.target.value)}
            style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", background: C.white, width: "100%" }}>
            <option value="all">All divisions ({players.length})</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name} ({players.filter(p => p.divisionId === d.id).length})</option>)}
          </select>
        </div>
      )}

      {!showAssignView && (visiblePlayers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1.5rem", color: C.muted, fontSize: 13 }}>
          No players yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {visiblePlayers.map(p => {
            const division = divisions.find(d => d.id === p.divisionId);
            const team = teams.find(t => t.id === p.teamId);
            const divisionTeams = teams.filter(t => t.divisionId === (editingPlayerId === p.id ? editPlayerDraft.divisionId : p.divisionId));
            const isEditing = editingPlayerId === p.id;
            const isExpanded = expandedPlayerId === p.id;
            const age = calculateAge(p.birthDate);
            const hasDetails = p.gender || p.birthDate || p.email || p.cellphone || p.parentFirstName;
            return (
              <div key={p.id} style={{ background: C.white, border: "0.5px solid " + C.border, borderLeft: "4px solid " + C.blue, borderRadius: R.card, padding: "10px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ cursor: hasDetails ? "pointer" : "default", flex: 1, minWidth: 160 }}
                    onClick={() => hasDetails && setExpandedPlayerId(isExpanded ? null : p.id)}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {p.firstName} {p.lastName}{age !== null && <span style={{ color: C.muted, fontWeight: 400 }}> ({age})</span>}
                      {hasDetails && <span style={{ color: C.blue, fontWeight: 400, fontSize: 11, marginLeft: 6 }}>{isExpanded ? "▲" : "▼"}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {division ? division.name : "No division"}{team ? " · " + team.name : " · Unassigned"}
                      {p.parentFirstName && " · Parent: " + p.parentFirstName + " " + p.parentLastName}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => (isEditing ? (setEditingPlayerId(null), setEditPlayerDraft(null)) : startEditPlayer(p))}
                      style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                      {/* Same UserPlus as the Players row toolbar button, because it is
                          the same action -- but sized 13, not 17. This is a raw 11px
                          <button>, not a 14px Btn, and it sits in a flex row beside an
                          11px "Delete" with identical 4px/10px padding. A 17 icon would
                          make this button visibly taller than its neighbour.
                          Icon only on the "Assign" branch: Cancel is the same button in
                          its other state and takes no icon. */}
                      {isEditing ? "Cancel" : <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><UserPlusIcon size={13} strokeWidth={2} />Assign to Team</span>}
                    </button>
                    <button onClick={() => deletePlayer(p.id)}
                      style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
                      Delete
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border, display: "flex", flexDirection: "column", gap: 4 }}>
                    {p.gender && <div style={{ fontSize: 12, color: C.text }}><span style={{ color: C.muted }}>Gender:</span> {p.gender}</div>}
                    {p.birthDate && <div style={{ fontSize: 12, color: C.text }}><span style={{ color: C.muted }}>Birth date:</span> {fmtBirthDate(p.birthDate)}{age !== null && " (age " + age + ")"}</div>}
                    {(p.parentFirstName || p.parentLastName) && <div style={{ fontSize: 12, color: C.text }}><span style={{ color: C.muted }}>Parent:</span> {p.parentFirstName} {p.parentLastName}</div>}
                    {p.email && <div style={{ fontSize: 12, color: C.text }}><span style={{ color: C.muted }}>Email:</span> {p.email}</div>}
                    {p.cellphone && <div style={{ fontSize: 12, color: C.text }}><span style={{ color: C.muted }}>Cellphone:</span> {p.cellphone}</div>}
                  </div>
                )}
                {isEditing && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <select value={editPlayerDraft.divisionId} onChange={e => setEditPlayerDraft({ divisionId: e.target.value, teamId: "" })}
                      style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                      {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <select value={editPlayerDraft.teamId} onChange={e => setEditPlayerDraft({ ...editPlayerDraft, teamId: e.target.value })}
                      style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                      <option value="">Unassigned</option>
                      {divisionTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button onClick={() => savePlayerEdit(p.id)}
                      style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      Save
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Exactly these 3 prepopulated volunteer-role defaults, no other suggested
// names (registration-parent-facing-v1-spec.md section 5, refined per Aaron's
// Aug 31 2026 scoping: no larger suggested-role list). Admin can toggle any
// of these on/off (never delete -- they can always turn one back on later)
// and add fully custom roles from scratch, which ARE deletable since the
// admin authored them. Stable ids so a toggle state survives re-saving the
// registration form.
//
// Seeded DISABLED so the admin opts in rather than out -- asking parents to
// volunteer is a decision a league should make deliberately, not inherit by
// default. Consequence worth knowing: ParentRegistrationFlow skips the
// volunteer step entirely when nothing is enabled, so an admin who ignores
// this section collects no volunteer interest at all. That's why the form
// section below says so explicitly when zero roles are on.
const DEFAULT_VOLUNTEER_ROLES = () => ([
  { id: 'default_head_coach', name: 'Head Coach', isDefault: true, enabled: false },
  { id: 'default_assistant_coach', name: 'Assistant Coach', isDefault: true, enabled: false },
  { id: 'default_team_parent', name: 'Team Parent', isDefault: true, enabled: false },
]);

// ─── CSV EXPORT ───────────────────────────────────────────────────────
// Lifted out of FieldOpsAppInner to module scope (Sep 4, 2026) so the
// registration roster export in RegistrationManager can share it. Pure
// move -- it closed over nothing, taking both the filename and the row
// matrix as arguments and using only browser APIs. Its two original
// callers (exportPlatformCoachesCSV, exportCrewStatsCSV) are unchanged.
// escapeCell handles the RFC4180 cases the Teams CSV importer's parser
// reads back (parseCsvText, ~line 2252): quoted fields, embedded commas,
// and doubled quotes. Rows are joined with \r\n, which that parser also
// accepts, so a file written here round-trips through it cleanly.
function downloadCSV(filename, rows) {
  const escapeCell = (val) => {
    const s = (val === null || val === undefined) ? '' : String(val);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = rows.map(row => row.map(escapeCell).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Normalizes a stored date of birth to ISO YYYY-MM-DD for export.
// parseBirthDate (~line 2281, the Teams importer's reader) accepts ONLY
// YYYY-MM-DD or M/D/YY(YY) -- anything else imports silently as a raw
// string with a null age, so a bad value is worse than no value here.
// The registration flow collects DOB from <input type="date"> and already
// stores ISO, so the M/D/YYYY branch is defensive rather than expected.
// Deliberately NOT falling back to new Date(s): parsing a bare date string
// lands at UTC midnight and toISOString() can shift it a day backwards in
// western timezones, which would silently move a child's birthday.
function toIsoBirthDate(value) {
  const s = (value || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    const year = m[3].length === 2 ? '20' + m[3] : m[3];
    return year + '-' + String(m[1]).padStart(2, '0') + '-' + String(m[2]).padStart(2, '0');
  }
  return '';
}

// An order the parent was actually CHARGED for. 'paid' is not sufficient on
// its own once refunds exist: a partially refunded order still holds children
// who paid and are registered, and a fully refunded one is still a real
// historical payment rather than an unpaid or abandoned attempt. Every place
// that used to test `status === 'paid'` has to ask this instead, or a refund
// silently reads as "never paid" -- which would move the money to Awaiting
// payment and put the family on the chase list.
function isMoneyTakenOrder(order) {
  const s = order && order.status;
  return s === "paid" || s === "partially_refunded" || s === "refunded";
}

// ─── REGISTRATION (v1) ────────────────────────────────────────────────
// Admin-side management of a league's parent registration + payment flow
// (registration-v1-spec.md). Scoped to the currently-selected season, same
// pattern as PlayersManagement/SchedulerSetup above -- reuses that season's
// divisions for per-division pricing rather than introducing a separate
// division concept. Phase 1 build: registration setup + pricing only (no
// public page / Stripe checkout yet -- see the "coming soon" note below).
function RegistrationManager({ lid, seasonId, seasonName, league, leagueLocations, connectSyncLoading, connectSyncError }) {
  const [registrations, setRegistrations] = React.useState([]);
  const [divisions, setDivisions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [showNewDivisionForm, setShowNewDivisionForm] = React.useState(false);
  const [connectLoading, setConnectLoading] = React.useState(false);
  const [connectError, setConnectError] = React.useState("");

  // Safari bfcache. startConnectOnboarding deliberately leaves connectLoading true
  // through the outbound redirect to Stripe -- that's correct double-submit protection,
  // not a leak. But Back from Stripe restores this page from memory with React state
  // intact (no remount, no effects re-run), so the button came back permanently
  // disabled and only a manual reload cleared it. Scoped on purpose: e.persisted only,
  // and only this one flag. A blanket "reset every loading flag on pageshow" would
  // touch ~27 flags that can't survive a restore in the first place.
  React.useEffect(() => {
    const onPageShow = e => { if (e.persisted) setConnectLoading(false); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  const [regLinkCopied, setRegLinkCopied] = React.useState(false);
  const emptyForm = () => ({
    opensAt: "", closesAt: "",
    divisionPrices: {}, // { [divisionId]: { price: string, active: bool, maxEnrollment: string } }
    samePriceForAll: true, samePriceValue: "",
    siblingDiscount: { second: "", third: "", fourth: "", subsequent: "" },
    volunteerRoles: DEFAULT_VOLUNTEER_ROLES(), // [{ id, name, isDefault, enabled }]
    requiredWaiverIds: [], // ids into leagues/{lid}/waivers, audience: 'registration' only
    customMessage: "" // shown on the success screen + confirmation email; blank = default text
  });
  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [newRoleName, setNewRoleName] = React.useState("");

  // Registration-audience waivers -- a second, independent authoring surface
  // into the same leagues/{lid}/waivers / waiverSignatures collections the
  // (Pro-gated) Waivers tab already uses, filtered to audience: 'registration'
  // so volunteer/admin-facing waivers from that tab never show up here or in
  // the parent-facing checkout flow. Deliberately NOT behind hasPro --
  // Registration itself has no plan-tier gate, and requiring Pro just to
  // attach a liability waiver to registration would be inconsistent with that.
  const [leagueWaivers, setLeagueWaivers] = React.useState([]);
  const [showNewRegWaiver, setShowNewRegWaiver] = React.useState(false);
  const [newRegWaiverName, setNewRegWaiverName] = React.useState("");
  const [newRegWaiverType, setNewRegWaiverType] = React.useState("text");
  const [newRegWaiverText, setNewRegWaiverText] = React.useState("");
  const [newRegWaiverFile, setNewRegWaiverFile] = React.useState(null);
  const [newRegWaiverRequiresSig, setNewRegWaiverRequiresSig] = React.useState(true);
  const [creatingRegWaiver, setCreatingRegWaiver] = React.useState(false);
  const [regWaiverError, setRegWaiverError] = React.useState("");

  // GroundsPal's flat per-checkout tech fee -- platform-set, not
  // league-editable (registration-v1-spec.md, confirmed $2.99 vs Sports
  // Connect's $3 benchmark).
  const TECH_FEE_CENTS = 299;

  React.useEffect(() => {
    if (!lid) { setRegistrations([]); setLoading(false); return; }
    const unsub = onSnapshot(collection(db, "leagues", lid, "registrations"), snap => {
      setRegistrations(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")));
      setLoading(false);
    });
    return () => unsub();
  }, [lid]);

  React.useEffect(() => {
    if (!lid) { setLeagueWaivers([]); return; }
    const unsub = onSnapshot(collection(db, "leagues", lid, "waivers"), snap => {
      setLeagueWaivers(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(w => w.audience === "registration"));
    });
    return () => unsub();
  }, [lid]);

  React.useEffect(() => {
    if (!lid || !seasonId) { setDivisions([]); return; }
    const unsub = onSnapshot(query(collection(db, "leagues", lid, "divisions"), where("seasonId", "==", seasonId)), snap => {
      setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [lid, seasonId]);

  const current = registrations.find(r => r.seasonId === seasonId) || null;

  // Open registrations this season's view can't show. The listener above is
  // already league-wide and unfiltered, so anything open under a different
  // season -- or under no resolvable season at all -- is sitting in
  // `registrations` right now; `current` just never surfaces it. That gap is
  // the whole bug: ParentRegistrationFlow resolves open registrations
  // league-wide (status == 'open', no season filter), so a registration the
  // admin can't see is still reachable by parents, uncloseable, and its
  // registrants uncountable. Deliberately NOT a fix to the plurality --
  // more than one open registration is intended, and the parent-side season
  // picker exists for exactly that. Rendered below so it can be closed
  // without season-hopping to find it.
  const otherOpen = registrations.filter(r => r.status === "open" && r.seasonId !== seasonId);

  // ─── ROSTER (admin-side registrant list + export) ──────────────────
  // A one-shot getDocs fan-out, NOT an onSnapshot. Two reasons: this is a
  // fan-out over registrants x {players, orders}, so a live listener would
  // hold 2N+1 subscriptions open for as long as the tab is mounted; and the
  // data only changes when a parent checks out, which no admin is watching
  // in real time. Refresh is manual, and rosterRaw stays the raw documents
  // so renaming a division re-derives the table without another read.
  const [rosterRaw, setRosterRaw] = React.useState(null);
  const [rosterLoading, setRosterLoading] = React.useState(false);
  const [rosterError, setRosterError] = React.useState("");
  // Only set on success. A failed refresh must not advance the stamp -- the
  // figures on screen are still the ones from the last good load, and saying
  // otherwise would be worse than showing nothing.
  const [rosterLoadedAt, setRosterLoadedAt] = React.useState(null);

  const loadRoster = React.useCallback(async () => {
    if (!lid) return;
    setRosterLoading(true);
    setRosterError("");
    try {
      const regSnap = await getDocs(collection(db, "leagues", lid, "registrants"));
      const raw = await Promise.all(regSnap.docs.map(async d => {
        const [playersSnap, ordersSnap] = await Promise.all([
          getDocs(collection(db, "leagues", lid, "registrants", d.id, "players")),
          getDocs(collection(db, "leagues", lid, "registrants", d.id, "orders")),
        ]);
        return {
          registrant: { id: d.id, ...d.data() },
          players: playersSnap.docs.map(p => ({ id: p.id, ...p.data() })),
          orders: ordersSnap.docs.map(o => ({ id: o.id, ...o.data() })),
        };
      }));
      setRosterRaw(raw);
      setRosterLoadedAt(new Date());
    } catch (e) {
      setRosterError("Couldn't load the roster. Please try again.");
    }
    setRosterLoading(false);
  }, [lid]);

  // ─── REFUNDS (admin-initiated, per player, on one order) ───────────
  // refundOrder holds an entry from roster.refundableOrders -- a whole order
  // doc with every cart line -- deliberately NOT a roster row. Rows collapse
  // to one winning order per child, so a two-child order can render as one
  // row and a refund driven off rows could act on a child not on screen.
  const [refundOrder, setRefundOrder] = React.useState(null);
  const [refundSelection, setRefundSelection] = React.useState([]);
  const [refundReason, setRefundReason] = React.useState("");
  const [refundBusy, setRefundBusy] = React.useState(false);
  const [refundError, setRefundError] = React.useState("");
  const [refundDone, setRefundDone] = React.useState("");

  const openRefund = (order) => {
    setRefundOrder(order);
    setRefundSelection([]);
    setRefundReason("");
    setRefundError("");
    setRefundDone("");
  };

  const toggleRefundPlayer = (playerId) => {
    setRefundSelection(sel => sel.includes(playerId)
      ? sel.filter(id => id !== playerId)
      : sel.concat([playerId]));
  };

  // The figure shown to the admin, summed from the SAME per-line allocation
  // the server recomputes from the order doc. The server never trusts this
  // number -- it exists so the amount on screen and the amount charged agree.
  const refundTotalCents = React.useMemo(() => {
    if (!refundOrder) return 0;
    return refundOrder.lines
      .filter(l => refundSelection.includes(l.playerId))
      .reduce((sum, l) => sum + l.parentPaidCents, 0);
  }, [refundOrder, refundSelection]);

  const submitRefund = async () => {
    if (!refundOrder || refundBusy) return;
    if (refundSelection.length === 0) { setRefundError("Select at least one athlete to refund."); return; }
    if (!refundReason.trim()) { setRefundError("A reason is required."); return; }
    setRefundBusy(true);
    setRefundError("");
    try {
      if (!auth.currentUser) throw new Error("not signed in");
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch("/api/sync-connect-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "refund-registration",
          idToken,
          leagueId: lid,
          registrantUid: refundOrder.registrantId,
          orderId: refundOrder.orderId,
          playerIds: refundSelection,
          reason: refundReason.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        // The endpoint's own message is surfaced verbatim where it has one --
        // it distinguishes cases the admin can act on (already refunded,
        // insufficient balance, missing payment reference) from ones they
        // can't, and a generic string would throw that away.
        setRefundError(result.error || "The refund didn't go through. No money has moved. Please try again.");
        setRefundBusy(false);
        return;
      }
      setRefundOrder(null);
      setRefundSelection([]);
      setRefundReason("");
      setRefundDone("Refund issued" + (result.stripeStatus === "pending" ? " and is processing at Stripe." : ". It can take 5–10 business days to reach the parent's statement."));
      // Refund state lives on the order doc, which this tab reads one-shot --
      // so re-read rather than patching local state, same as any other write.
      await loadRoster();
    } catch (e) {
      setRefundError("The refund didn't go through. No money has moved. Please try again.");
    }
    setRefundBusy(false);
  };

  // Loads once per league when a registration exists for this season. The
  // season filter is applied when deriving rows, not when reading, so
  // switching seasons re-derives without re-reading.
  const rosterRequested = React.useRef(null);
  React.useEffect(() => {
    if (!lid || !current) return;
    if (rosterRequested.current === lid) return;
    rosterRequested.current = lid;
    loadRoster();
  }, [lid, current, loadRoster]);

  // One row per player per season, built from ORDERS rather than from the
  // player docs. This is deliberate and is the whole reason orders are read
  // at all: registrationId/divisionId/divisionName/status on a player doc are
  // scalars overwritten on every registration (see CLAUDE.md, "Registration
  // data model"), so filtering player docs by this season's registration
  // would silently drop any child who has since registered for a later one.
  // The order's cart[] is an immutable per-checkout snapshot, so it still
  // describes what was actually bought this season.
  const roster = React.useMemo(() => {
    if (!rosterRaw || !current) return null;
    const divisionNameById = {};
    divisions.forEach(d => { divisionNameById[d.id] = d.name; });

    const rows = [];
    // Refund totals are accumulated from the ORDERS, not from rows. Money that
    // moved is money that moved regardless of whether the row it belongs to
    // survived the one-per-athlete collapse below, and a refund on a
    // superseded order still left the league's balance.
    let refundedGrossCents = 0;
    let refundedLeagueCents = 0;
    // Distinct athletes refunded this season, counted from the orders for the
    // same reason the money is. Counting `rows.filter(r => r.refunded)` instead
    // would undercount: a child who was refunded and then re-registered has a
    // live row that (correctly) wins the collapse, so their refund would vanish
    // from the count while its money stayed in the totals.
    const refundedPlayerIds = new Set();
    // Whole order docs for the refund UI, kept separate from rows for the same
    // reason: rows collapse to one winning order per child, so an order with
    // two cart lines can surface as a single row, and a refund UI driven by
    // rows could offer to refund a child it never displayed.
    const refundableOrders = [];
    rosterRaw.forEach(({ registrant, players, orders }) => {
      const playerById = {};
      players.forEach(p => { playerById[p.id] = p; });

      // Abandoned orders are excluded outright: the Checkout Session expired
      // without payment, so there is nothing owed and nobody registered.
      // Older orders predate the seasonId field, hence the registrationId
      // fallback.
      const seasonOrders = orders.filter(o => o.status !== "abandoned" &&
        (o.seasonId ? o.seasonId === seasonId : o.registrationId === current.id));

      // Collapse to one entry per athlete. A parent who abandoned a checkout
      // and started again has two live orders for the same child, so a paid
      // order always beats an unpaid one and the newer wins a tie.
      const byPlayer = {};
      seasonOrders.forEach(order => {
        const subtotalCents = order.subtotalCents || 0;
        const feesCents = (order.techFeeCents || 0) + (order.processingFeeCents || 0);
        // Refund state is PER PLAYER, not per order. A partially refunded order
        // still has children on it who are registered and fully paid, so the
        // order's own status can't answer this for a given line -- only
        // membership in a refunds[] entry can.
        const refundedIds = new Set();
        (order.refunds || []).forEach(r => (r.playerIds || []).forEach(id => refundedIds.add(id)));
        (order.cart || []).forEach(item => {
          const key = item.playerId || ((item.firstName || "") + "|" + (item.lastName || ""));
          // netCents is that child's division price net of sibling discount and
          // excludes the order-level tech + card processing fees, which are
          // never itemized per child at checkout. Allocated proportionally,
          // same method ParentAccountPage uses so a parent's receipt and this
          // roster agree to the cent.
          // The zero-subtotal branch is not defensive padding: a free division,
          // or a sibling discount that covers the price outright, nets every
          // line to 0 while the parent is still charged the tech + processing
          // fee. Allocating proportionally there divides by zero, and skipping
          // the fees would show $0.00 against a receipt that says $3.38. Split
          // evenly instead, so the rows still sum to what was actually paid.
          const feeShareCents = subtotalCents > 0
            ? Math.round(((item.netCents || 0) / subtotalCents) * feesCents)
            : Math.round(feesCents / ((order.cart || []).length || 1));
          const refunded = !!(item.playerId && refundedIds.has(item.playerId));
          const candidate = { order, item, amountCents: (item.netCents || 0) + feeShareCents, refunded };
          const prev = byPlayer[key];
          if (!prev) { byPlayer[key] = candidate; return; }
          // A refunded line never beats a live one. A parent who was refunded
          // and then registered the same child again has both on file, and the
          // live registration is the one that describes them now -- without
          // this, the newer-wins tie-break below could show a currently
          // registered child as REFUNDED.
          if (candidate.refunded !== prev.refunded) { if (prev.refunded) byPlayer[key] = candidate; return; }
          const prevPaid = isMoneyTakenOrder(prev.order);
          const candPaid = isMoneyTakenOrder(order);
          if (candPaid !== prevPaid) { if (candPaid) byPlayer[key] = candidate; return; }
          if ((order.createdAt || "") > (prev.order.createdAt || "")) byPlayer[key] = candidate;
        });
      });

      // Second pass over the same orders, for money and for the refund UI.
      // Deliberately not folded into the collapse above: that loop's unit is a
      // cart LINE competing for an athlete slot, this one's unit is a whole
      // ORDER, and conflating them is exactly the bug this separation avoids.
      const parentName = ((registrant.firstName || "") + " " + (registrant.lastName || "")).trim() || "—";
      seasonOrders.forEach(order => {
        const subtotalCents = order.subtotalCents || 0;
        const totalCents = order.totalCents || 0;
        const feesCents = (order.techFeeCents || 0) + (order.processingFeeCents || 0);
        const cart = order.cart || [];
        const feeShareFor = (item) => (subtotalCents > 0
          ? Math.round(((item.netCents || 0) / subtotalCents) * feesCents)
          : Math.round(feesCents / (cart.length || 1)));

        const refundedIds = new Set();
        (order.refunds || []).forEach(r => {
          (r.playerIds || []).forEach(id => { refundedIds.add(id); refundedPlayerIds.add(id); });
          refundedGrossCents += r.amountCents || 0;
          // leagueShareCents is what Stripe actually reversed out of the
          // connected account (transfer_reversal.amount), which is the only
          // figure that can be reconciled against the league's own balance.
          // The fallback reproduces Stripe's own proportional rule for the
          // rows written before the field existed, or if the expansion ever
          // came back without an amount -- it is not a guess, it's the same
          // ratio Stripe applies.
          if (r.leagueShareCents != null) refundedLeagueCents += r.leagueShareCents;
          else if (totalCents > 0) refundedLeagueCents += Math.round(((r.amountCents || 0) * subtotalCents) / totalCents);
        });

        if (!isMoneyTakenOrder(order)) return;
        refundableOrders.push({
          registrantId: registrant.id,
          parentName,
          email: registrant.email || "",
          orderId: order.id,
          createdAt: order.createdAt || "",
          status: order.status,
          totalCents,
          // Null on any order whose webhook wrote `session.payment_intent ||
          // null`. Carried through rather than filtered out, so the UI can say
          // why the order can't be refunded instead of hiding it and looking
          // like data loss.
          stripePaymentIntentId: order.stripePaymentIntentId || null,
          refunds: order.refunds || [],
          lines: cart.map(item => ({
            playerId: item.playerId || "",
            playerName: (((item.firstName || "") + " " + (item.lastName || "")).trim()) || item.playerName || "Unnamed player",
            divisionName: divisionNameById[item.divisionId] || item.divisionName || "",
            parentPaidCents: (item.netCents || 0) + feeShareFor(item),
            refunded: !!(item.playerId && refundedIds.has(item.playerId)),
          })),
        });
      });

      Object.keys(byPlayer).forEach(key => {
        const { order, item, amountCents, refunded } = byPlayer[key];
        const player = item.playerId ? playerById[item.playerId] : null;
        // Assumption B, confirmed: the cart snapshot carries divisionId as
        // well as divisionName, so the CURRENT division name wins and the
        // snapshot is only a fallback for a division that has since been
        // deleted. Without this, renaming a division would make the export
        // create a duplicate division on import instead of matching the
        // existing one (the importer matches on name, ~line 2359).
        const divisionName = (divisionNameById[item.divisionId] || item.divisionName || "").trim();
        const firstName = (item.firstName || "").trim();
        const lastName = (item.lastName || "").trim();
        const missing = [];
        if (!divisionName) missing.push("division");
        if (!firstName) missing.push("first name");
        if (!lastName) missing.push("last name");
        rows.push({
          key: registrant.id + "|" + key,
          registrantId: registrant.id,
          parentName: ((registrant.firstName || "") + " " + (registrant.lastName || "")).trim() || "—",
          parentFirstName: registrant.firstName || "",
          parentLastName: registrant.lastName || "",
          email: registrant.email || "",
          cellphone: registrant.phone || "",
          firstName, lastName,
          playerName: (firstName + " " + lastName).trim(),
          gender: (item.gender || (player && player.gender) || "").trim(),
          birthDate: toIsoBirthDate(player && player.dob),
          divisionName,
          divisionId: item.divisionId || "",
          orderId: order.id,
          orderDate: (order.createdAt || "").slice(0, 10),
          // Full timestamp, kept alongside the date-only string the CSV uses:
          // "recent registrations" has to break ties within a single day, which
          // the sliced form can't do.
          orderCreatedAt: order.createdAt || "",
          // The league's own share of this line -- division price net of any
          // sibling discount, and BEFORE the tech + card processing fees. Kept
          // separate from amountCents (which adds this line's fee share) because
          // the fees are grossed up onto the parent and never reach the league,
          // so every league-facing money figure has to be built from this one.
          netCents: item.netCents || 0,
          playerId: item.playerId || "",
          // The order's own status, not the player doc's -- same
          // state-not-history reason as above. Three values survive the
          // abandoned filter: a refunded LINE reads 'refunded' regardless of
          // whether the rest of its order is still live, because refund state
          // is per player.
          status: refunded ? "refunded" : (isMoneyTakenOrder(order) ? "paid" : "pending"),
          refunded,
          amountCents,
          // A row missing any of the importer's three required columns must
          // never reach the file: line ~2347 silently discards such a row
          // with no count and no warning, so it would become a child who
          // vanishes on import and nobody notices.
          // A refunded athlete is deliberately not exportable. The CSV exists to
          // seed Teams with children who are actually playing, and a refunded
          // registration is the opposite of that -- exporting them would put a
          // child who withdrew onto a real roster.
          exportable: missing.length === 0 && !refunded,
          missing,
        });
      });
    });

    rows.sort((a, b) =>
      a.lastName.localeCompare(b.lastName) ||
      a.firstName.localeCompare(b.firstName) ||
      a.playerName.localeCompare(b.playerName));

    refundableOrders.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

    return {
      rows,
      // Only the DATA-QUALITY exclusions, not every non-exportable row: the
      // warning this feeds tells an admin to go fix missing names/divisions,
      // which would be wrong advice for a refunded athlete who is correctly
      // being held back and needs no repair.
      excluded: rows.filter(r => !r.exportable && r.missing.length > 0),
      refundedGrossCents,
      refundedLeagueCents,
      refundedAthleteCount: refundedPlayerIds.size,
      refundableOrders,
    };
  }, [rosterRaw, divisions, seasonId, current]);

  // ─── DASHBOARD (derived from the roster -- no additional reads) ─────
  //
  // MONEY DEFINITION, and the reason every figure here is built from
  // netCents rather than from an order total: the GroundsPal tech fee and the
  // card processing fee are grossed up onto the PARENT at checkout
  // (api/create-registration-checkout-session.js) and paid to the platform
  // and to Stripe, never to the league. So the league's share of an order is
  // its subtotalCents, and totalCents would overstate their take by the fees
  // and disagree with their Stripe balance. Same statement the admin-facing
  // copy below the division list already makes.
  //
  // Why per-LINE netCents and not per-ORDER subtotalCents, which is what the
  // definition literally says: an order's cart lines sum to exactly its
  // subtotalCents (the checkout endpoint builds it that way, `subtotalCents +=
  // netCents`), so on any order whose lines all count these are the same
  // number. They diverge in exactly one case, and there the line-level figure
  // is the correct one. A parent who starts a checkout, abandons it without it
  // having expired yet, and checks out again has TWO non-abandoned orders
  // covering the same child; roster.rows already collapses those to one
  // athlete (paid beats pending, newer breaks the tie), so summing order
  // subtotals would bill the league for a child they'd be shown once, and a
  // partially-superseded order -- kids A+B pending, then A alone paid -- would
  // be counted wrong in both directions. Deriving from the same collapsed rows
  // means athlete counts, division revenue and the headline totals cannot
  // disagree with each other, and the per-division parts roll up to the whole
  // by construction rather than by coincidence.
  const dashboard = React.useMemo(() => {
    if (!roster || !current) return null;
    const rows = roster.rows;

    let collectedCents = 0;
    let pendingCents = 0;
    const families = new Set();
    const pendingFamilies = new Map();
    const statsByDivision = {};

    rows.forEach(r => {
      // A refunded athlete is not a registration. They leave every count and
      // every money figure here -- and because their netCents never enters
      // collectedCents in the first place, refunded money LEAVES Collected
      // rather than moving to Awaiting payment, which is what subtracting it
      // from the wrong side would have done. Their row still renders in the
      // table below with a REFUNDED badge; it just isn't counted.
      if (r.refunded) return;

      families.add(r.registrantId);
      if (r.status === "paid") collectedCents += r.netCents;
      else pendingCents += r.netCents;

      const key = r.divisionId || ("name:" + r.divisionName);
      if (!statsByDivision[key]) statsByDivision[key] = { count: 0, revenueCents: 0 };
      statsByDivision[key].count += 1;
      statsByDivision[key].revenueCents += r.netCents;

      if (r.status !== "pending") return;
      if (!pendingFamilies.has(r.registrantId)) {
        pendingFamilies.set(r.registrantId, {
          registrantId: r.registrantId, parentName: r.parentName, email: r.email,
          players: [], amountCents: 0,
        });
      }
      const fam = pendingFamilies.get(r.registrantId);
      fam.players.push(r.playerName || "Unnamed player");
      fam.amountCents += r.netCents;
    });

    // Division order deliberately mirrors the Division pricing list below, so
    // the two read as the same set of divisions rather than two orderings of
    // it. Anything a cart line references that is no longer in the
    // registration -- a division deleted mid-season -- is appended after, with
    // the cart snapshot's own name, so its registrants are never silently
    // dropped from the counts.
    const seen = {};
    const divisionRows = [];
    divisions.forEach(d => {
      const p = current.divisions && current.divisions[d.id];
      if (!p) return;
      seen[d.id] = true;
      const s = statsByDivision[d.id] || { count: 0, revenueCents: 0 };
      divisionRows.push({
        id: d.id, name: d.name, active: p.active !== false,
        // Absent on every registration saved before maxEnrollment existed, and
        // on any saved since with the field left blank. `!= null` rather than a
        // truthiness check: it is stored as null for "no cap", never 0, so a
        // present value is always a real cap. Null means render no capacity and
        // no bar at all -- NOT a bar against a denominator of zero.
        maxEnrollment: p.maxEnrollment != null ? p.maxEnrollment : null,
        count: s.count, revenueCents: s.revenueCents,
      });
    });
    Object.keys(statsByDivision).forEach(key => {
      if (seen[key]) return;
      const sample = rows.find(r => (r.divisionId || ("name:" + r.divisionName)) === key);
      divisionRows.push({
        id: key, name: (sample && sample.divisionName) || "(deleted division)",
        active: true, maxEnrollment: null, removed: true,
        count: statsByDivision[key].count, revenueCents: statsByDivision[key].revenueCents,
      });
    });

    const liveRows = rows.filter(r => !r.refunded);

    return {
      registered: liveRows.length,
      families: families.size,
      collectedCents,
      pendingCents,
      // Two different questions, deliberately two numbers. refundedLeagueCents
      // is what left the league's Stripe balance and is the one to reconcile
      // against it; refundedGrossCents is what the parents got back, which is
      // larger because it includes the fees they were charged on top. Both come
      // from Stripe's own response, not from our arithmetic.
      refundedLeagueCents: roster.refundedLeagueCents,
      refundedGrossCents: roster.refundedGrossCents,
      refundedCount: roster.refundedAthleteCount,
      pendingFamilies: Array.from(pendingFamilies.values())
        .sort((a, b) => b.amountCents - a.amountCents),
      divisionRows,
      recent: liveRows.slice()
        .sort((a, b) => (b.orderCreatedAt || "").localeCompare(a.orderCreatedAt || ""))
        .slice(0, 10),
    };
  }, [roster, current, divisions]);

  const usd = (cents) => "$" + ((cents || 0) / 100).toFixed(2);

  // The fee THIS registration actually charges, which is not necessarily
  // TECH_FEE_CENTS. saveRegistration stamps the constant onto the doc at save
  // time, so a registration saved before the constant last changed keeps
  // charging the old amount until it's edited and re-saved -- and the parent
  // side reads the stored value, not the constant
  // (api/create-registration-checkout-session.js: `reg.techFeeCents || 299`).
  // So copy describing an EXISTING registration has to interpolate this, while
  // copy in the create/edit form interpolates TECH_FEE_CENTS, since that's what
  // saving will stamp. Interpolating the constant in both places would fix one
  // silent lie by introducing another. The `||` coalescing deliberately matches
  // the API's, so the number shown and the number charged fall back together.
  const currentTechFeeCents = (current && current.techFeeCents) || TECH_FEE_CENTS;

  // The roster is a one-shot read that never refreshes itself, so during
  // registration week an admin who leaves this tab open is looking at figures
  // that quietly age. The stamp is the cue that they're a snapshot rather than
  // live. Carries the date too once it isn't today's, since a bare "as of
  // 11:58 PM" on a tab left open overnight reads as current and isn't.
  const rosterAsOf = React.useMemo(() => {
    if (!rosterLoadedAt) return "";
    const time = rosterLoadedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const isToday = rosterLoadedAt.toDateString() === new Date().toDateString();
    return isToday ? "as of " + time
      : "as of " + rosterLoadedAt.toLocaleDateString([], { month: "short", day: "numeric" }) + ", " + time;
  }, [rosterLoadedAt]);

  // ONE file that is both a report and valid input to the Teams CSV import.
  // The first ten headers are the importer's own spellings, verbatim, matched
  // case-insensitively on trimmed text (colIndex/norm, ~lines 2317/2279).
  // Team Name is always blank so every player lands unallocated rather than
  // creating teams from registration data. The last four are ours; the
  // importer looks columns up by name and never reads what it doesn't
  // recognize, so they ride along harmlessly.
  const exportRosterCSV = () => {
    if (!roster) return;
    const rows = [[
      "Division Name", "Player First Name", "Player Last Name", "Player Gender",
      "Player Birth Date", "Account First Name", "Account Last Name", "User Email",
      "Cellphone", "Team Name", "Order Date", "Order Id", "Payment Status", "Amount Paid",
    ]];
    roster.rows.filter(r => r.exportable).forEach(r => {
      rows.push([
        r.divisionName, r.firstName, r.lastName, r.gender,
        r.birthDate, r.parentFirstName, r.parentLastName, r.email,
        r.cellphone, "", r.orderDate, r.orderId,
        r.status === "paid" ? "Paid" : "Pending",
        // Blank rather than 0.00 on an unpaid row -- the column is named
        // "Amount Paid" and nothing has been paid yet.
        r.status === "paid" ? (r.amountCents / 100).toFixed(2) : "",
      ]);
    });
    const leagueSlug = ((league && league.leagueName) || "league").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    downloadCSV("groundspal-registration-roster-" + leagueSlug + "-" + new Date().toISOString().slice(0, 10) + ".csv", rows);
  };

  const startNew = () => {
    const dp = {};
    divisions.forEach(d => { dp[d.id] = { price: "", active: true, maxEnrollment: "" }; });
    setForm({ ...emptyForm(), divisionPrices: dp });
    setEditingId(null);
    setError("");
    setNewRoleName("");
    setShowNewRegWaiver(false);
    setRegWaiverError("");
    setShowForm(true);
  };

  const startEdit = (reg) => {
    const dp = {};
    divisions.forEach(d => {
      const existing = reg.divisions && reg.divisions[d.id];
      // maxEnrollment is optional and was added after registrations already
      // existed, so an absent value is "no cap", not bad data -- `!= null`
      // rather than a truthiness check, and no backfill.
      dp[d.id] = existing
        ? { price: String(existing.price), active: existing.active !== false, maxEnrollment: existing.maxEnrollment != null ? String(existing.maxEnrollment) : "" }
        : { price: "", active: true, maxEnrollment: "" };
    });
    setForm({
      opensAt: reg.opensAt || "", closesAt: reg.closesAt || "",
      divisionPrices: dp, samePriceForAll: false, samePriceValue: "",
      siblingDiscount: {
        second: reg.siblingDiscount && reg.siblingDiscount.second ? String(reg.siblingDiscount.second / 100) : "",
        third: reg.siblingDiscount && reg.siblingDiscount.third ? String(reg.siblingDiscount.third / 100) : "",
        fourth: reg.siblingDiscount && reg.siblingDiscount.fourth ? String(reg.siblingDiscount.fourth / 100) : "",
        subsequent: reg.siblingDiscount && reg.siblingDiscount.subsequent ? String(reg.siblingDiscount.subsequent / 100) : ""
      },
      // Registrations saved before this feature existed have no
      // volunteerRoles field yet -- seed the 3 defaults rather than showing
      // an empty list, same backfill-free approach used elsewhere in this
      // codebase (e.g. computeCartTotals' techFeeCents fallback).
      volunteerRoles: (reg.volunteerRoles && reg.volunteerRoles.length) ? reg.volunteerRoles : DEFAULT_VOLUNTEER_ROLES(),
      requiredWaiverIds: reg.requiredWaiverIds || [],
      customMessage: reg.customMessage || ""
    });
    setEditingId(reg.id);
    setError("");
    setNewRoleName("");
    setShowNewRegWaiver(false);
    setRegWaiverError("");
    setShowForm(true);
  };

  // Sets price only. This rebuilds each entry from scratch, so it has to carry
  // the other fields across explicitly or they get wiped -- spreading the
  // existing entry instead of listing them one by one means a field added to
  // the shape later survives this by default rather than silently vanishing
  // whenever an admin touches the same-price box.
  const applySamePriceToAll = (value) => {
    const dp = {};
    divisions.forEach(d => {
      const existing = form.divisionPrices[d.id] || { active: true, maxEnrollment: "" };
      dp[d.id] = { ...existing, price: value };
    });
    setForm(f => ({ ...f, samePriceValue: value, divisionPrices: dp }));
  };

  const toCents = (v) => { const n = parseFloat(v); return isNaN(n) || n < 0 ? 0 : Math.round(n * 100); };

  // Optional cap. Blank, non-numeric, or <= 0 all mean "no cap" and store null
  // rather than 0 -- a stored 0 would be indistinguishable from a real cap of
  // zero, and since capacity is informational the safe reading of a junk value
  // is "uncapped", not "closed".
  const toMaxEnrollment = (v) => {
    const n = parseInt(v, 10);
    return isNaN(n) || n <= 0 ? null : n;
  };

  // Defaults (Head Coach/Assistant Coach/Team Parent) are toggle-only -- an
  // admin can turn one back on later, so they're never removed from the
  // list. Custom roles are fully admin-authored, so a plain delete is fine
  // for those.
  const toggleRoleEnabled = (roleId) => {
    setForm(f => ({ ...f, volunteerRoles: f.volunteerRoles.map(r => r.id === roleId ? { ...r, enabled: !r.enabled } : r) }));
  };
  const addCustomRole = () => {
    const name = newRoleName.trim();
    if (!name) return;
    setForm(f => ({ ...f, volunteerRoles: [...f.volunteerRoles, { id: 'custom_' + Date.now(), name, isDefault: false, enabled: true }] }));
    setNewRoleName("");
  };
  const removeCustomRole = (roleId) => {
    setForm(f => ({ ...f, volunteerRoles: f.volunteerRoles.filter(r => r.id !== roleId) }));
  };

  const toggleRequiredWaiver = (waiverId) => {
    setForm(f => ({
      ...f,
      requiredWaiverIds: f.requiredWaiverIds.includes(waiverId)
        ? f.requiredWaiverIds.filter(id => id !== waiverId)
        : [...f.requiredWaiverIds, waiverId]
    }));
  };

  // Writes straight to the shared leagues/{lid}/waivers collection (same one
  // the Pro-gated Waivers tab reads), tagged audience: 'registration' so it
  // stays out of that tab's volunteer/admin-facing list and only shows up
  // here and in the parent checkout flow. Newly created waivers are
  // auto-checked into this registration's requiredWaiverIds -- an admin
  // creating one here is doing it specifically for this registration, so
  // that's the expected outcome, not an extra click.
  const createRegistrationWaiver = async () => {
    setRegWaiverError("");
    if (!newRegWaiverName.trim()) { setRegWaiverError("Give the waiver a name."); return; }
    if (newRegWaiverType === "text" && !newRegWaiverText.trim()) { setRegWaiverError("Enter the waiver text, or switch to uploading a file."); return; }
    if (newRegWaiverType === "file" && !newRegWaiverFile) { setRegWaiverError("Choose a file to upload."); return; }
    setCreatingRegWaiver(true);
    try {
      const id = "waiver_" + Date.now();
      let fileUrl = null, fileName = null;
      if (newRegWaiverType === "file") {
        const path = "leagues/" + lid + "/waivers/" + id + "/" + newRegWaiverFile.name;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, newRegWaiverFile);
        fileUrl = await getDownloadURL(fileRef);
        fileName = newRegWaiverFile.name;
      }
      await setDoc(doc(db, "leagues", lid, "waivers", id), {
        id, name: newRegWaiverName.trim(), contentType: newRegWaiverType,
        text: newRegWaiverType === "text" ? newRegWaiverText.trim() : "",
        fileUrl, fileName,
        requiresSignature: newRegWaiverRequiresSig,
        audience: 'registration',
        createdAt: new Date().toISOString(),
      });
      setForm(f => ({ ...f, requiredWaiverIds: [...f.requiredWaiverIds, id] }));
      setNewRegWaiverName(""); setNewRegWaiverType("text"); setNewRegWaiverText("");
      setNewRegWaiverFile(null); setNewRegWaiverRequiresSig(true);
      setShowNewRegWaiver(false);
    } catch (e) {
      setRegWaiverError("Couldn't save that waiver. Try again.");
    }
    setCreatingRegWaiver(false);
  };

  const saveRegistration = async () => {
    setError("");
    // The write-level half of the no-season guard. `seasonId` is written
    // straight onto the doc below, so a registration saved in this state is
    // the orphan: open to parents, invisible to every season's view of this
    // page. Empty `seasonName` counts -- it means the selected id resolves to
    // no season on this league, which is what the selector above reports as
    // "No season selected". The early return further down normally keeps this
    // unreachable; kept here anyway because this is the function that does
    // the damage.
    if (!seasonId || !seasonName) { setError("Pick a season in the season selector above first — a registration saved without one is still reachable by parents, but won't show up on this page."); return; }
    if (!form.opensAt || !form.closesAt) { setError("Set both an opens and closes date."); return; }
    if (new Date(form.closesAt) < new Date(form.opensAt)) { setError("Closes date must be on or after the opens date."); return; }
    const divisionsOut = {};
    let anyPriced = false;
    Object.keys(form.divisionPrices).forEach(divId => {
      const entry = form.divisionPrices[divId];
      const priceCents = toCents(entry.price);
      if (entry.active && entry.price !== "" && priceCents >= 0) anyPriced = true;
      // maxEnrollment is informational only -- nothing reads it to gate
      // registration, and it is deliberately absent from the parent-facing
      // flow. It exists so an admin can compare enrollment against a target.
      divisionsOut[divId] = { price: priceCents / 100, active: !!entry.active, maxEnrollment: toMaxEnrollment(entry.maxEnrollment) };
    });
    if (divisions.length > 0 && !anyPriced) { setError("Set a price for at least one active division."); return; }
    setSaving(true);
    try {
      const payload = {
        seasonId, seasonName: seasonName || "",
        opensAt: form.opensAt, closesAt: form.closesAt,
        divisions: divisionsOut,
        siblingDiscount: {
          second: toCents(form.siblingDiscount.second),
          third: toCents(form.siblingDiscount.third),
          fourth: toCents(form.siblingDiscount.fourth),
          subsequent: toCents(form.siblingDiscount.subsequent)
        },
        techFeeCents: TECH_FEE_CENTS,
        volunteerRoles: form.volunteerRoles,
        requiredWaiverIds: form.requiredWaiverIds,
        customMessage: form.customMessage,
        updatedAt: new Date().toISOString()
      };
      if (editingId) {
        await updateDoc(doc(db, "leagues", lid, "registrations", editingId), payload);
      } else {
        const id = "reg_" + Date.now();
        await setDoc(doc(db, "leagues", lid, "registrations", id), { ...payload, id, status: "draft", createdAt: new Date().toISOString() });
      }
      setShowForm(false);
    } catch (e) {
      setError("Could not save this registration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (reg, status) => {
    await updateDoc(doc(db, "leagues", lid, "registrations", reg.id), { status, updatedAt: new Date().toISOString() });
  };

  // ─── STRIPE CONNECT ONBOARDING ──────────────────────────────────
  // Same pattern as handleManageBilling: grab an ID token, ask the
  // serverless function for a Stripe-hosted onboarding link, redirect.
  // create-connect-account.js reuses the league's existing connected
  // account if one was already started, so this is safe to call again
  // for a league that's mid-onboarding.
  const payoutsConnected = !!(league && league.stripeConnectChargesEnabled && league.stripeConnectPayoutsEnabled);
  const payoutsStarted = !!(league && league.stripeConnectAccountId);

  const startConnectOnboarding = async () => {
    if (connectLoading || !lid) return;
    setConnectError("");
    setConnectLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('/api/create-connect-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leagueId: lid, idToken })
      });
      const result = await response.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        setConnectError(result.error || "We couldn't start payout setup right now. Please try again.");
        setConnectLoading(false);
      }
    } catch (e) {
      setConnectError("We couldn't start payout setup right now. Please check your connection and try again.");
      setConnectLoading(false);
    }
  };

  if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 13 }}>Loading registration…</div>;

  // The existing guard, widened. It already refused to render the create form
  // when no season was selected, but only tested `seasonId` -- it missed the
  // case where an id IS selected and resolves to no season on this league
  // (which is what the season selector above shows as "No season selected",
  // and what `seasonName === ""` means, since every season doc is written with
  // a non-empty name). That case rendered a fully working form whose header
  // read "New Registration — " and whose Save wrote a registration no season's
  // view could ever list again. Blocking the render blocks create, edit, and
  // Publish / Open in one place rather than three.
  if (!seasonId || !seasonName) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: C.muted, fontSize: 13 }}>
        Pick a season in the season selector above — or create one — to start setting up registration.
      </div>
    );
  }

  return (
    <div style={{ padding: "0 14px 14px" }}>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1rem 1.25rem", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 4 }}>Payouts</div>
            {payoutsConnected ? (
              <div style={{ fontSize: 12, color: C.muted }}>Registration payments will be paid out to your connected Stripe account.</div>
            ) : payoutsStarted ? (
              <div style={{ fontSize: 12, color: C.muted }}>Stripe still needs a bit more info before payments can go through — finish setup to start accepting registrations.</div>
            ) : (
              <div style={{ fontSize: 12, color: C.muted }}>Connect a Stripe account so parents' payments can be paid out to your league. Required before registration checkout will work.</div>
            )}
          </div>
          {payoutsConnected ? (
            <Badge bg={C.greenLt} color={C.green}>PAYOUTS CONNECTED</Badge>
          ) : (
            <Btn onClick={startConnectOnboarding} variant="primary" size="sm" disabled={connectLoading || connectSyncLoading}>
              {(connectLoading || connectSyncLoading) ? "Connecting…" : payoutsStarted ? "Continue payout setup" : "Set up payouts with Stripe"}
            </Btn>
          )}
        </div>
        {(connectError || connectSyncError) && <div style={{ color: C.redDark, background: C.redLt, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginTop: 10 }}>{connectError || connectSyncError}</div>}
      </div>

      {!current && !showForm && (
        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem 1.5rem", textAlign: "center" }}>
          <CreditCardIcon size={36} color={C.muted} strokeWidth={1.5} style={{ marginBottom: 10 }} />
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 6 }}>No registration open yet</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>
            Set division pricing and open/close dates for {seasonName}, then publish it to get a Register link for your league's public page — parents will use it to sign their players up and pay.
          </div>
          <Btn onClick={startNew} variant="primary">+ New Registration</Btn>
        </div>
      )}

      {current && !showForm && (
        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{current.seasonName || seasonName} Registration</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                <span>{current.opensAt} – {current.closesAt}</span>
                <Badge bg={current.status === "open" ? C.greenLt : current.status === "closed" ? C.redLt : C.grayLt} color={current.status === "open" ? C.green : current.status === "closed" ? C.redDark : C.muted}>{(current.status || "draft").toUpperCase()}</Badge>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={() => startEdit(current)} variant="secondary" size="sm">Edit</Btn>
              {current.status !== "open" && <Btn onClick={() => setStatus(current, "open")} variant="primary" size="sm">Publish / Open</Btn>}
              {current.status === "open" && <Btn onClick={() => setStatus(current, "closed")} variant="secondary" size="sm">Close registration</Btn>}
            </div>
          </div>

          {current.status === "open" && !payoutsConnected && (
            <div style={{ color: C.amber, background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginTop: 12 }}>
              Payouts aren't finished connecting yet — parents will hit an error at checkout until Stripe onboarding is complete. Finish it in the Payouts section above.
            </div>
          )}

          {/* ─── SECTION 1: headline ─────────────────────────────────
              Money here is the LEAGUE's share, not what parents were
              charged -- see the money definition on the dashboard memo.
              The roster table further down shows the parent-facing amount
              (share + that line's fee allocation), which is why the two
              columns are labelled differently and don't tie out. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginTop: 16 }}>
            <div style={{ background: C.surface, borderRadius: R.control, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, color: C.text }}>{dashboard ? dashboard.registered : "—"}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>Registered athletes</div>
            </div>
            <div style={{ background: C.surface, borderRadius: R.control, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, color: C.text }}>{dashboard ? dashboard.families : "—"}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{dashboard && dashboard.families === 1 ? "Family" : "Families"}</div>
            </div>
            <div style={{ background: C.greenLt, borderRadius: R.control, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, color: C.green }}>{dashboard ? usd(dashboard.collectedCents) : "—"}</div>
              <div style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Collected — your share</div>
            </div>
            <div style={{ background: C.surface, borderRadius: R.control, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, color: C.text }}>{dashboard ? usd(dashboard.pendingCents) : "—"}</div>
              {/* "Awaiting payment", never "Unpaid" as an assertion: a just-paid
                  order reads pending for the seconds before Stripe's
                  checkout.session.completed webhook lands, so this number is
                  briefly high by design and must not accuse anyone. */}
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
                Awaiting payment
                {dashboard && dashboard.pendingFamilies.length > 0 &&
                  " · " + dashboard.pendingFamilies.length + (dashboard.pendingFamilies.length === 1 ? " family" : " families")}
              </div>
            </div>
          </div>

          {/* Only once something has actually been refunded. A permanent
              "$0.00 refunded" card would give a rare event standing equal to
              the four figures above it, on every league's screen forever. */}
          {dashboard && dashboard.refundedCount > 0 && (
            <div style={{ marginTop: 8, background: C.surface, border: "1px solid " + C.border, borderRadius: R.card, padding: "9px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
                Refunded · {dashboard.refundedCount} {dashboard.refundedCount === 1 ? "athlete" : "athletes"}
                {/* The parent-facing figure is deliberately the secondary one:
                    the number an admin reconciles against their Stripe balance
                    is what left THEIR account, and the larger gross number
                    would otherwise read as their loss when the fee portion was
                    never theirs. */}
                <span style={{ display: "block", fontWeight: 400, marginTop: 2 }}>
                  {usd(dashboard.refundedGrossCents)} returned to parents, including fees
                </span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, whiteSpace: "nowrap" }}>
                −{usd(dashboard.refundedLeagueCents)}
                <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: C.muted, textAlign: "right" }}>from your balance</span>
              </div>
            </div>
          )}

          {/* Same statement as the static copy under Division pricing, worded
              the same way on purpose -- these two must not appear to disagree
              about who pays the fees. */}
          <div style={{ marginTop: 8, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
            Your share is division pricing only. The {usd(currentTechFeeCents)} GroundsPal tech fee and standard card processing costs are added at checkout on top of it and paid by the parent — neither is deducted from your organization payout.
          </div>

          {/* ─── SECTION 2: enrollment by division ────────────────────── */}
          {dashboard && dashboard.divisionRows.length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>Enrollment by division</div>
              {dashboard.divisionRows.map(d => {
                const capped = d.maxEnrollment != null;
                const over = capped && d.count > d.maxEnrollment;
                // Capacity is informational by design (0f4a616), so going over
                // is a legitimate state to display, not an error to clamp or
                // guard against. The bar caps at 100% width and turns amber;
                // the numbers underneath still say 26 / 24.
                const pct = capped ? Math.min(100, Math.round((d.count / d.maxEnrollment) * 100)) : 0;
                return (
                  <div key={d.id} style={{ padding: "7px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, fontSize: 13 }}>
                      <span style={{ color: d.active ? C.text : C.muted, fontWeight: 600 }}>
                        {d.name}{!d.active ? " (inactive)" : ""}
                        {d.removed && <span style={{ fontWeight: 400, color: C.muted }}> · no longer in this registration</span>}
                      </span>
                      <span style={{ whiteSpace: "nowrap", color: C.text }}>
                        <strong>{d.count}</strong>
                        {capped && <span style={{ color: over ? C.amber : C.muted }}> / {d.maxEnrollment}</span>}
                        <span style={{ color: C.muted, fontWeight: 400 }}> · {usd(d.revenueCents)}</span>
                      </span>
                    </div>
                    {capped && (
                      /* Literal 3, not an R token: this is half of height 5, i.e. a capsule cap on a progress
                         indicator, not a control corner. Squaring it also squares the inner fill via overflow: hidden. */
                      <div style={{ marginTop: 5, height: 5, borderRadius: 3, background: C.grayBd, overflow: "hidden" }}>
                        <div style={{ width: pct + "%", height: "100%", background: over ? C.amber : C.greenMid }} />
                      </div>
                    )}
                    {over && (
                      <div style={{ marginTop: 4, fontSize: 11, color: C.amber }}>
                        {d.count - d.maxEnrollment} over the {d.maxEnrollment} you set. Capacity is a guide — it doesn't stop anyone registering.
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ marginTop: 8, fontSize: 11, color: C.muted }}>
                Revenue is your share and adds up to collected plus awaiting payment above.
              </div>
            </div>
          )}

          {/* ─── SECTION 3: who hasn't paid ───────────────────────────── */}
          {dashboard && dashboard.pendingFamilies.length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Awaiting payment</div>
              {/* Phrased as "hasn't come through yet", never "hasn't paid" --
                  same webhook-lag reason as the headline card above. */}
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
                These families started a registration that hasn't come through as paid yet. A payment made in the last few minutes can still show here while it finishes processing.
              </div>
              {dashboard.pendingFamilies.map(f => (
                <div key={f.registrantId} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "7px 0", borderBottom: "1px solid " + C.border }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f.parentName}</div>
                    {f.email && <div style={{ fontSize: 11, color: C.muted, wordBreak: "break-all" }}>{f.email}</div>}
                    <div style={{ fontSize: 11, color: C.muted }}>{f.players.join(", ")}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{usd(f.amountCents)}</div>
                </div>
              ))}
            </div>
          )}

          {/* ─── SECTION 4: recent registrations ──────────────────────── */}
          {dashboard && dashboard.recent.length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>
                Recent registrations{dashboard.registered > dashboard.recent.length ? " · latest " + dashboard.recent.length : ""}
              </div>
              {dashboard.recent.map(r => (
                <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: C.text }}>{r.playerName || "—"}</div>
                    <div style={{ color: C.muted, fontSize: 11 }}>
                      {r.divisionName || "—"} · {r.parentName}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ color: C.muted, fontSize: 11 }}>{r.orderDate || "—"}</div>
                    <Badge bg={r.status === "paid" ? C.greenLt : C.grayLt} color={r.status === "paid" ? C.green : C.muted}>
                      {r.status === "paid" ? "PAID" : "AWAITING PAYMENT"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Roster</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Applies to the dashboard above as much as to the table
                    below -- both derive from this one load -- but it sits with
                    the control that changes it. */}
                {rosterAsOf && !rosterLoading && (
                  <span style={{ fontSize: 11, color: C.muted }}>{rosterAsOf}</span>
                )}
                <Btn onClick={loadRoster} variant="secondary" size="sm" disabled={rosterLoading}>
                  {rosterLoading ? "Loading..." : "Refresh"}
                </Btn>
                <Btn onClick={exportRosterCSV} variant="secondary" size="sm"
                  disabled={rosterLoading || !roster || roster.rows.filter(r => r.exportable).length === 0}>
                  Export CSV
                </Btn>
              </div>
            </div>

            {rosterError && (
              <div style={{ color: C.redDark, background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginBottom: 10 }}>
                {rosterError}
              </div>
            )}

            {rosterLoading && !roster && (
              <div style={{ fontSize: 12, color: C.muted }}>Loading roster...</div>
            )}

            {roster && roster.rows.length === 0 && !rosterLoading && (
              <div style={{ fontSize: 12, color: C.muted }}>No registrations yet this season.</div>
            )}

            {roster && roster.excluded.length > 0 && (
              <div style={{ color: C.amber, background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginBottom: 10 }}>
                <strong>{roster.excluded.length} {roster.excluded.length === 1 ? "row is" : "rows are"} left out of the CSV export.</strong>{" "}
                The Teams importer requires a division name, first name and last name, and silently drops any row missing one — so these are held back rather than lost on import. Missing:{" "}
                {roster.excluded.map(r => (r.playerName || "unnamed player") + " (" + r.missing.join(", ") + ")").join("; ")}.
              </div>
            )}

            {roster && roster.rows.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: C.muted, fontSize: 11, fontWeight: 700 }}>
                      <th style={{ padding: "6px 8px", borderBottom: "1px solid " + C.border }}>Parent</th>
                      <th style={{ padding: "6px 8px", borderBottom: "1px solid " + C.border }}>Player</th>
                      <th style={{ padding: "6px 8px", borderBottom: "1px solid " + C.border }}>Division</th>
                      <th style={{ padding: "6px 8px", borderBottom: "1px solid " + C.border }}>Payment</th>
                      {/* "Parent paid", not "Amount": this column is the
                          parent-facing charge (division price net of sibling
                          discount, PLUS that line's share of the tech and card
                          processing fees), while every figure in the dashboard
                          above is the league's own share and excludes those
                          fees. Two different numbers for the same order appear
                          on this one screen, so both have to say which they
                          are. */}
                      <th style={{ padding: "6px 8px", borderBottom: "1px solid " + C.border, textAlign: "right" }}>Parent paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.rows.map(r => (
                      <tr key={r.key}>
                        <td style={{ padding: "7px 8px", borderBottom: "1px solid " + C.border, color: C.text }}>
                          <div style={{ fontWeight: 600 }}>{r.parentName}</div>
                          {r.email && <div style={{ fontSize: 11, color: C.muted }}>{r.email}</div>}
                        </td>
                        <td style={{ padding: "7px 8px", borderBottom: "1px solid " + C.border, color: C.text, fontWeight: 600 }}>
                          {r.playerName || "—"}
                          {/* Gated on missing fields, not on !exportable:
                              refunded rows are also held out of the export, but
                              deliberately -- badging them "NOT IN EXPORT" next
                              to a REFUNDED badge would read as a data problem
                              to go fix rather than the intended outcome. */}
                          {r.missing.length > 0 && (
                            <span style={{ marginLeft: 6 }}>
                              <Badge bg={C.amberLt} color={C.amber}>NOT IN EXPORT</Badge>
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "7px 8px", borderBottom: "1px solid " + C.border, color: C.text }}>{r.divisionName || "—"}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "1px solid " + C.border }}>
                          <Badge
                            bg={r.status === "refunded" ? C.redLt : r.status === "paid" ? C.greenLt : C.grayLt}
                            color={r.status === "refunded" ? C.redDark : r.status === "paid" ? C.green : C.muted}
                          >
                            {r.status === "refunded" ? "REFUNDED" : r.status === "paid" ? "PAID" : "AWAITING PAYMENT"}
                          </Badge>
                        </td>
                        <td style={{ padding: "7px 8px", borderBottom: "1px solid " + C.border, color: C.text, textAlign: "right" }}>
                          ${(r.amountCents / 100).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ─── SECTION 6: refunds ───────────────────────────────────────
              Listed per ORDER, not per roster row. An order is what Stripe
              charged and what a refund acts on, and one order can cover
              several children; the roster table above shows one row per
              child and collapses duplicates, so it is the wrong shape to
              drive money movement from. */}
          {roster && roster.refundableOrders.length > 0 && (
            <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Refunds</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
                Refund a whole order or just one athlete on it. Refunds come out of your Stripe balance.
              </div>

              {refundDone && (
                <div style={{ color: C.green, background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginBottom: 10 }}>
                  {refundDone}
                </div>
              )}

              {roster.refundableOrders.map(o => {
                const allRefunded = o.lines.length > 0 && o.lines.every(l => l.refunded);
                return (
                  <div key={o.registrantId + "|" + o.orderId} style={{ borderBottom: "1px solid " + C.border, padding: "9px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                        {o.parentName}
                        <span style={{ fontWeight: 400, color: C.muted, fontSize: 11 }}>{o.createdAt ? " · " + o.createdAt.slice(0, 10) : ""}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {o.lines.map(l => l.playerName + (l.refunded ? " (refunded)" : "")).join(", ") || "—"}
                      </div>
                      {!o.stripePaymentIntentId && (
                        // Shown rather than hidden: the order is real and paid,
                        // so silently omitting it would look like missing data.
                        <div style={{ fontSize: 11, color: C.amber, marginTop: 3 }}>
                          This order has no payment reference saved, so it can't be refunded here. Refund it from the Stripe dashboard.
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{usd(o.totalCents)}</div>
                        {o.refunds.length > 0 && (
                          <div style={{ fontSize: 11, color: C.redDark }}>
                            −{usd(o.refunds.reduce((s, r) => s + (r.amountCents || 0), 0))} refunded
                          </div>
                        )}
                      </div>
                      {allRefunded ? (
                        <Badge bg={C.redLt} color={C.redDark}>FULLY REFUNDED</Badge>
                      ) : (
                        <Btn onClick={() => openRefund(o)} variant="secondary" size="sm" disabled={!o.stripePaymentIntentId}>
                          Refund
                        </Btn>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Division pricing</div>
            {divisions.length === 0 && !showNewDivisionForm && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, color: C.muted }}>No divisions in this season yet.</div>
                <button onClick={() => setShowNewDivisionForm(true)}
                  style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  + Create a division
                </button>
              </div>
            )}
            {divisions.length === 0 && showNewDivisionForm && (
              <NewDivisionForm
                lid={lid}
                seasonId={seasonId}
                leagueLocations={leagueLocations}
                hideScheduleConfig={true}
                onDone={() => setShowNewDivisionForm(false)}
              />
            )}
            {divisions.map(d => {
              const p = current.divisions && current.divisions[d.id];
              if (!p) return null;
              return (
                <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: p.active ? C.text : C.muted }}>
                  <span>{d.name}{!p.active ? " (inactive)" : ""}</span>
                  <span style={{ fontWeight: 700 }}>
                    ${p.price.toFixed(2)}
                    {/* Absent on anything saved before maxEnrollment existed --
                        renders nothing at all, which is the correct "no cap"
                        display, so no backfill is needed. */}
                    {p.maxEnrollment != null && <span style={{ fontWeight: 400, color: C.muted }}> · max {p.maxEnrollment}</span>}
                  </span>
                </div>
              );
            })}
          </div>

          {!!(current.volunteerRoles && current.volunteerRoles.some(r => r.enabled)) && (
            <div style={{ marginTop: 14, borderTop: "1px solid " + C.border, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Volunteer roles shown to parents</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {current.volunteerRoles.filter(r => r.enabled).map(r => r.name).join(', ')}
              </div>
            </div>
          )}

          {!!(current.requiredWaiverIds && current.requiredWaiverIds.length) && (
            <div style={{ marginTop: 14, borderTop: "1px solid " + C.border, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Waivers required at checkout</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {current.requiredWaiverIds.map(id => (leagueWaivers.find(w => w.id === id) || {}).name || "(deleted waiver)").join(', ')}
              </div>
            </div>
          )}

          <div style={{ marginTop: 12, fontSize: 11, color: C.muted }}>
            A {usd(currentTechFeeCents)} GroundsPal tech fee and standard card processing costs are both added at checkout, on top of division pricing — neither is deducted from your organization payout.
          </div>

          <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
            {league && league.slug ? (
              <div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Public registration page link:</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.grayLt, borderRadius: R.control, fontSize: 13, color: C.text, wordBreak: "break-all" }}>
                  {window.location.host}/register/{league.slug}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/register/' + league.slug); setRegLinkCopied(true); setTimeout(() => setRegLinkCopied(false), 2000); }}
                  style={{ marginTop: 8, width: "100%", background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  {regLinkCopied ? "✓ Copied!" : "Copy Link"}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.muted }}>
                Public registration page link: not set up yet. Add one under ⚙️ Settings → Public Registration Link, then it'll appear here.
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 14 }}>{editingId ? "Edit Registration" : "New Registration"} — {seasonName}</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginBottom: 14 }}>
            <FieldInput label="OPENS" type="date" value={form.opensAt} onChange={e => setForm(f => ({ ...f, opensAt: e.target.value }))} />
            <FieldInput label="CLOSES" type="date" value={form.closesAt} onChange={e => setForm(f => ({ ...f, closesAt: e.target.value }))} />
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Division pricing</div>
          {divisions.length === 0 ? (
            <div style={{ marginBottom: 14 }}>
              {!showNewDivisionForm ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 12, color: C.muted }}>No divisions in this season yet.</div>
                  <button onClick={() => setShowNewDivisionForm(true)}
                    style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    + Create a division
                  </button>
                </div>
              ) : (
                <NewDivisionForm
                  lid={lid}
                  seasonId={seasonId}
                  leagueLocations={leagueLocations}
                  hideScheduleConfig={true}
                  onDone={() => setShowNewDivisionForm(false)}
                />
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={form.samePriceForAll} onChange={e => setForm(f => ({ ...f, samePriceForAll: e.target.checked }))} />
                  Same price for all divisions
                </label>
                {form.samePriceForAll && (
                  <input type="number" min="0" step="0.01" placeholder="$0.00" value={form.samePriceValue}
                    onChange={e => applySamePriceToAll(e.target.value)}
                    style={{ width: 100, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <span style={{ flex: 1 }} />
                <span style={{ width: 100 }}>Price</span>
                <span style={{ width: 100 }}>Max (optional)</span>
              </div>
              {divisions.map(d => {
                const entry = form.divisionPrices[d.id] || { price: "", active: true, maxEnrollment: "" };
                return (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, flex: 1 }}>
                      <input type="checkbox" checked={entry.active} onChange={e => setForm(f => ({ ...f, divisionPrices: { ...f.divisionPrices, [d.id]: { ...entry, active: e.target.checked } } }))} />
                      {d.name}
                    </label>
                    <input type="number" min="0" step="0.01" placeholder="$0.00" value={entry.price} disabled={form.samePriceForAll}
                      onChange={e => setForm(f => ({ ...f, divisionPrices: { ...f.divisionPrices, [d.id]: { ...entry, price: e.target.value } } }))}
                      style={{ width: 100, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", opacity: form.samePriceForAll ? 0.6 : 1 }} />
                    {/* Never disabled by samePriceForAll -- that checkbox is about
                        price, and a cap is per-division by nature. */}
                    <input type="number" min="1" step="1" placeholder="No cap" value={entry.maxEnrollment}
                      onChange={e => setForm(f => ({ ...f, divisionPrices: { ...f.divisionPrices, [d.id]: { ...entry, maxEnrollment: e.target.value } } }))}
                      style={{ width: 100, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                Max is a target for your own planning — it's not shown to parents and doesn't stop anyone registering once a division is full.
              </div>
            </>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "16px 0 8px" }}>Sibling discount (per player, off list price)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
            {["second", "third", "fourth", "subsequent"].map(key => (
              <div key={key}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>{key} child</div>
                <input type="number" min="0" step="0.01" placeholder="$0.00" value={form.siblingDiscount[key]}
                  onChange={e => setForm(f => ({ ...f, siblingDiscount: { ...f.siblingDiscount, [key]: e.target.value } }))}
                  style={{ width: "100%", padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "16px 0 4px" }}>Volunteer roles (optional)</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
            Shown to parents during registration so they can express interest. Selecting a role only registers interest — it doesn't assign anyone. Turn a default role off rather than deleting it if you don't need it right now; you can turn it back on later.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {form.volunteerRoles.map(role => (
              <div key={role.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text }}>
                  <input type="checkbox" checked={role.enabled} onChange={() => toggleRoleEnabled(role.id)} />
                  {role.name}
                </label>
                {!role.isDefault && (
                  <button type="button" onClick={() => removeCustomRole(role.id)}
                    style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          {!form.volunteerRoles.some(r => r.enabled) && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
              No roles enabled — parents won't be asked about volunteering.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="Add a custom role…" value={newRoleName} onChange={e => setNewRoleName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomRole(); } }}
              style={{ flex: 1, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
            <button type="button" onClick={addCustomRole}
              style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "6px 14px", fontWeight: 700, fontSize: 12, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
              + Add
            </button>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "16px 0 4px" }}>Waivers</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
            Shown inline during checkout — parents must review and sign any waiver checked below before they can pay. This is separate from the Waivers tab; those waivers are for volunteers/admins and never show up here.
          </div>
          {leagueWaivers.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              {leagueWaivers.map(w => (
                <label key={w.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.requiredWaiverIds.includes(w.id)} onChange={() => toggleRequiredWaiver(w.id)} />
                  {w.name}
                  {w.requiresSignature === false && <span style={{ fontSize: 11, color: C.muted }}>(no signature required)</span>}
                </label>
              ))}
            </div>
          )}

          {showNewRegWaiver ? (
            <div style={{ background: C.grayLt, borderRadius: R.control, padding: "10px 12px", marginBottom: 10 }}>
              <input placeholder="Waiver name, e.g. Assumption of Risk" value={newRegWaiverName} onChange={e => setNewRegWaiverName(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <button type="button" onClick={() => setNewRegWaiverType("text")}
                  style={{ flex: 1, padding: "6px", borderRadius: R.control, border: "1px solid " + (newRegWaiverType === "text" ? C.greenMid : C.border), background: newRegWaiverType === "text" ? C.greenLt : C.white, fontSize: 11, fontWeight: 700, color: newRegWaiverType === "text" ? C.text : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                  Type it in
                </button>
                <button type="button" onClick={() => setNewRegWaiverType("file")}
                  style={{ flex: 1, padding: "6px", borderRadius: R.control, border: "1px solid " + (newRegWaiverType === "file" ? C.greenMid : C.border), background: newRegWaiverType === "file" ? C.greenLt : C.white, fontSize: 11, fontWeight: 700, color: newRegWaiverType === "file" ? C.text : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                  Upload a file
                </button>
              </div>
              {newRegWaiverType === "text" ? (
                <textarea placeholder="Paste or type the full waiver text here..." value={newRegWaiverText} onChange={e => setNewRegWaiverText(e.target.value)}
                  rows={5}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", marginBottom: 8, resize: "vertical" }} />
              ) : (
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setNewRegWaiverFile(e.target.files[0] || null)}
                  style={{ width: "100%", marginBottom: 8, fontSize: 12 }} />
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={newRegWaiverRequiresSig} onChange={e => setNewRegWaiverRequiresSig(e.target.checked)} />
                <span style={{ fontSize: 12, color: C.text }}>Requires a signature to acknowledge</span>
              </label>
              {regWaiverError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "6px 10px", fontSize: 11, color: C.redDark, marginBottom: 8 }}>{regWaiverError}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={createRegistrationWaiver} disabled={creatingRegWaiver}
                  style={{ flex: 1, background: creatingRegWaiver ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "8px", fontWeight: 700, fontSize: 13, cursor: creatingRegWaiver ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  {creatingRegWaiver ? "Saving..." : "Create Waiver"}
                </button>
                <button type="button" onClick={() => { setShowNewRegWaiver(false); setRegWaiverError(""); }}
                  style={{ background: C.white, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "8px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowNewRegWaiver(true)}
              style={{ background: C.greenMid, border: "none", borderRadius: R.control, padding: "6px 14px", fontWeight: 700, fontSize: 12, color: C.text, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
              + New Waiver
            </button>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "16px 0 4px" }}>Custom confirmation message (optional)</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
            {'Shown on the success screen right after checkout, and at the top of the confirmation email parents receive. Leave blank to use the default "Thanks for registering with ' + ((league && league.leagueName) || 'your league') + '…" message.'}
          </div>
          <textarea placeholder={"Thanks for registering with " + ((league && league.leagueName) || "your league") + ". Here's a summary of your order:"}
            value={form.customMessage} onChange={e => setForm(f => ({ ...f, customMessage: e.target.value }))}
            rows={3}
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", marginBottom: 14, resize: "vertical" }} />

          <div style={{ fontSize: 11, color: C.muted, margin: "6px 0 14px" }}>
            {/* The form, so this is TECH_FEE_CENTS -- the fee saving will stamp
                onto the doc -- not the fee an existing registration carries. */}
            A {usd(TECH_FEE_CENTS)} GroundsPal tech fee is added to what the registrant pays at checkout — it's not deducted from your payout.
          </div>

          {error && <div style={{ color: C.redDark, background: C.redLt, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={saveRegistration} variant="primary" disabled={saving}>{saving ? "Saving…" : "Save"}</Btn>
            <Btn onClick={() => { setShowForm(false); setError(""); }} variant="secondary" disabled={saving}>Cancel</Btn>
          </div>
        </div>
      )}

      {!showForm && otherOpen.length > 0 && (
        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Also open in other seasons</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
            {otherOpen.length === 1
              ? "This registration is open and reachable by parents on your public registration link, but it isn't part of " + seasonName + ", so it doesn't show above. Switch to its season to edit it, or close it here."
              : "These registrations are open and reachable by parents on your public registration link, but they aren't part of " + seasonName + ", so they don't show above. Switch to a registration's season to edit it, or close it here."}
          </div>
          {otherOpen.map(reg => (
            <div key={reg.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, borderTop: "1px solid " + C.border, paddingTop: 10, marginTop: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{reg.seasonName ? reg.seasonName + " Registration" : "Registration"}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{(reg.seasonName || "No season") + " · " + reg.opensAt + " – " + reg.closesAt}</div>
              </div>
              {/* Close only, same handler and (lack of) confirm step as the
                  close button on the card above. No Edit: editing reads this
                  season's divisions for pricing, so it would silently edit the
                  wrong registration unless the admin switched seasons first --
                  at which point the card above is the right place to do it. */}
              <Btn onClick={() => setStatus(reg, "closed")} variant="secondary" size="sm">Close registration</Btn>
            </div>
          ))}
        </div>
      )}

      {/* ─── REFUND MODAL ────────────────────────────────────────────────
          Rendered here, outside every conditional branch above, so it can't
          be unmounted by a state change on the section that opened it. */}
      {refundOrder && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: C.white, borderRadius: R.card, padding: 20, maxWidth: 420, width: "100%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 2 }}>Refund registration</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
              {refundOrder.parentName}{refundOrder.createdAt ? " · " + refundOrder.createdAt.slice(0, 10) : ""}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 6 }}>WHO ARE YOU REFUNDING?</div>
            {refundOrder.lines.map(l => {
              const checked = refundSelection.includes(l.playerId);
              // A line with no playerId can't be refunded: the server keys
              // idempotency on playerId, so an unidentifiable line could be
              // refunded twice. Only possible on carts written before playerId
              // was recorded.
              const selectable = !l.refunded && !!l.playerId;
              return (
                <label key={l.playerId || l.playerName} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 0", borderBottom: "1px solid " + C.border, cursor: selectable ? "pointer" : "default", opacity: selectable ? 1 : 0.55 }}>
                  <input type="checkbox" checked={checked} disabled={!selectable}
                    onChange={() => toggleRefundPlayer(l.playerId)}
                    style={{ width: 16, height: 16, cursor: selectable ? "pointer" : "default" }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text }}>{l.playerName}</span>
                    <span style={{ display: "block", fontSize: 11, color: C.muted }}>
                      {l.divisionName || "—"}{l.refunded ? " · already refunded" : (!l.playerId ? " · can't be refunded here" : "")}
                    </span>
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{usd(l.parentPaidCents)}</span>
                </label>
              );
            })}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: C.text }}>Refund total</span>
              <span style={{ fontWeight: 800, color: C.text }}>{usd(refundTotalCents)}</span>
            </div>
            {/* The parent gets back the fees too -- they were grossed up onto
                the parent at checkout, so a refund that returned only the
                division price would leave them short of what they paid. */}
            <div style={{ fontSize: 11, color: C.muted, marginTop: -4, marginBottom: 12, lineHeight: 1.5 }}>
              This is what the parent gets back, including the tech and card processing fees they paid. Your own balance is reduced by your share of it, not the whole amount.
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4 }}>REASON (REQUIRED)</div>
            <input value={refundReason} onChange={e => setRefundReason(e.target.value)} maxLength={500}
              placeholder="e.g. Withdrew before the season started"
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", marginBottom: 12 }} />

            <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "9px 10px", fontSize: 11.5, color: C.amber, lineHeight: 1.5, marginBottom: 12 }}>
              <strong>This takes money out of your Stripe balance.</strong> If this registration has already been paid out to your bank account, your Stripe balance can go negative and Stripe will debit your bank account to cover it.
            </div>

            {refundError && (
              <div style={{ color: C.redDark, background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>
                {refundError}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setRefundOrder(null); setRefundError(""); }} disabled={refundBusy}
                style={{ flex: 1, background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "9px", fontSize: 13, fontWeight: 600, cursor: refundBusy ? "default" : "pointer", fontFamily: "inherit", color: C.muted }}>
                Cancel
              </button>
              <button onClick={submitRefund} disabled={refundBusy || refundSelection.length === 0 || !refundReason.trim()}
                style={{ flex: 1, background: C.redFill, color: C.white, border: "none", borderRadius: R.control, padding: "9px", fontSize: 13, fontWeight: 700, cursor: (refundBusy || refundSelection.length === 0 || !refundReason.trim()) ? "default" : "pointer", fontFamily: "inherit", opacity: (refundBusy || refundSelection.length === 0 || !refundReason.trim()) ? 0.55 : 1 }}>
                {refundBusy ? "Refunding…" : "Refund " + usd(refundTotalCents)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SeasonManagerCard(props) {
  const {
    seasons, currentSeasonId, setCurrentSeasonId,
    showSeasonMenu, setShowSeasonMenu,
    editingSeasonId, setEditingSeasonId,
    editSeasonLabel, setEditSeasonLabel, editSeasonCustomLabel, setEditSeasonCustomLabel, editSeasonYear, setEditSeasonYear,
    startEditSeason, saveSeasonEdit,
    duplicateSeason, archiveSeason, unarchiveSeason,
    showArchivedSeasons, setShowArchivedSeasons,
    creatingSeason, setCreatingSeason,
    newSeasonLabel, setNewSeasonLabel, newSeasonCustomLabel, setNewSeasonCustomLabel, newSeasonYear, setNewSeasonYear,
    createSeason
  } = props;

  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "0.85rem 1rem", margin: "1rem", position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em" }}>SEASON</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>
            {seasons.find(s => s.id === currentSeasonId) ? seasons.find(s => s.id === currentSeasonId).name : "No season selected"}
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); setShowSeasonMenu(v => !v); }}
          style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "7px 14px", fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
          Switch / Manage {showSeasonMenu ? "▲" : "▼"}
        </button>
      </div>
      {showSeasonMenu && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 12, borderTop: "1px solid " + C.border, paddingTop: 12 }}>
          {seasons.filter(s => s.status !== "archived").map(s => (
            editingSeasonId === s.id ? (
              <div key={s.id} style={{ background: C.grayLt, borderRadius: R.control, padding: 10, marginBottom: 6 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <select value={editSeasonLabel} onChange={e => setEditSeasonLabel(e.target.value)}
                    style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                    <option>Fall</option><option>Winter</option><option>Spring</option><option>Summer</option><option>Other</option>
                  </select>
                  {editSeasonLabel === "Other" && (
                    <input placeholder="Season name" value={editSeasonCustomLabel} onChange={e => setEditSeasonCustomLabel(e.target.value)}
                      style={{ flex: 1, padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit" }} />
                  )}
                  <select value={editSeasonYear} onChange={e => setEditSeasonYear(e.target.value)}
                    style={{ padding: "6px 8px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 12, fontFamily: "inherit", background: C.white }}>
                    {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={saveSeasonEdit} style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                  <button onClick={() => setEditingSeasonId(null)} style={{ background: C.grayBd, color: C.white, border: "none", borderRadius: R.control, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: R.control, background: s.id === currentSeasonId ? C.greenLt : "transparent", marginBottom: 4, flexWrap: "wrap" }}>
                <button onClick={() => { setCurrentSeasonId(s.id); setShowSeasonMenu(false); }}
                  style={{ flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: s.id === currentSeasonId ? 700 : 500, color: s.id === currentSeasonId ? C.green : C.text }}>
                  {s.name}
                </button>
                <button onClick={() => startEditSeason(s)}
                  style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                  Edit
                </button>
                <button onClick={() => duplicateSeason(s.id)}
                  style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                  Duplicate
                </button>
                <button onClick={() => archiveSeason(s.id)}
                  style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                  Archive
                </button>
              </div>
            )
          ))}

          {seasons.some(s => s.status === "archived") && (
            <div style={{ marginTop: 6 }}>
              <button onClick={() => setShowArchivedSeasons(v => !v)}
                style={{ background: "none", border: "none", color: C.blue, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "4px 0" }}>
                {showArchivedSeasons ? "Hide" : "Show"} archived seasons ({seasons.filter(s => s.status === "archived").length})
              </button>
              {showArchivedSeasons && seasons.filter(s => s.status === "archived").map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: R.control, opacity: 0.7, marginBottom: 4 }}>
                  <button onClick={() => { setCurrentSeasonId(s.id); setShowSeasonMenu(false); }}
                    style={{ flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, color: C.muted }}>
                    {s.name} (archived)
                  </button>
                  <button onClick={() => unarchiveSeason(s.id)}
                    style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: C.green, cursor: "pointer", fontFamily: "inherit" }}>
                    Unarchive
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border }}>
            {!creatingSeason ? (
              <button onClick={() => setCreatingSeason(true)}
                style={{ background: C.greenMid, border: "none", borderRadius: R.control, padding: "7px 14px", fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                + New season
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <select value={newSeasonLabel} onChange={e => setNewSeasonLabel(e.target.value)}
                    style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}>
                    <option>Fall</option><option>Winter</option><option>Spring</option><option>Summer</option><option>Other</option>
                  </select>
                  {newSeasonLabel === "Other" && (
                    <input placeholder="Season name" value={newSeasonCustomLabel} onChange={e => setNewSeasonCustomLabel(e.target.value)}
                      style={{ flex: 1, padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
                  )}
                  <select value={newSeasonYear} onChange={e => setNewSeasonYear(e.target.value)}
                    style={{ padding: "7px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", background: C.white }}>
                    {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => createSeason(newSeasonLabel, newSeasonCustomLabel, newSeasonYear)}
                    disabled={newSeasonLabel === "Other" && !newSeasonCustomLabel.trim()}
                    style={{ flex: 1, background: (newSeasonLabel === "Other" && !newSeasonCustomLabel.trim()) ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    Create
                  </button>
                  <button onClick={() => { setCreatingSeason(false); setNewSeasonLabel("Fall"); setNewSeasonCustomLabel(""); setNewSeasonYear(new Date().getFullYear()); }}
                    style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "7px 12px", fontSize: 12, color: C.muted, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WaiversManagement({ lid, user }) {
  const [waivers, setWaivers] = React.useState([]);
  const [signatures, setSignatures] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNewWaiver, setShowNewWaiver] = React.useState(false);
  const [newWaiverName, setNewWaiverName] = React.useState("");
  const [newWaiverType, setNewWaiverType] = React.useState("text");
  const [newWaiverText, setNewWaiverText] = React.useState("");
  const [newWaiverFile, setNewWaiverFile] = React.useState(null);
  const [newWaiverRequiresSig, setNewWaiverRequiresSig] = React.useState(true);
  const [creatingWaiver, setCreatingWaiver] = React.useState(false);
  const [createError, setCreateError] = React.useState("");
  const [expandedWaiverId, setExpandedWaiverId] = React.useState(null);
  const [signDraftName, setSignDraftName] = React.useState("");
  const [signing, setSigning] = React.useState(false);
  const [viewingSignaturesId, setViewingSignaturesId] = React.useState(null);

  const isAdmin = user && (user.role === "admin" || user.role === "coadmin");
  // The app's own `user` object carries name/role/leagueId but NOT a uid --
  // none of the setUser() call sites set one -- so identity here has to come
  // off the Firebase Auth session directly. Every path that produces a `user`
  // (volunteer signup, both sign-in phases, admin/co-admin creation, session
  // restore via onAuthStateChanged) establishes a real Auth session first, so
  // this is populated whenever this component can render.
  const myUid = auth.currentUser ? auth.currentUser.uid : null;

  React.useEffect(() => {
    if (!lid) return;
    const unsubWaivers = onSnapshot(collection(db, "leagues", lid, "waivers"), snap => {
      setWaivers(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")));
      setLoading(false);
    });
    // Signature reads are now admin-or-own-signature at the rules layer, and
    // Firestore rules are not filters: an unconstrained listen over the whole
    // collection is rejected outright for a non-admin, rather than quietly
    // returning the subset they're allowed to see. So the query has to encode
    // the same constraint the rule enforces. Admins keep the unconstrained
    // listen (they need every signature for the admin signature list); a
    // volunteer gets only their own, which is all mySignatureFor needs.
    let unsubSigs = () => {};
    if (isAdmin) {
      unsubSigs = onSnapshot(collection(db, "leagues", lid, "waiverSignatures"), snap => {
        setSignatures(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    } else if (myUid) {
      unsubSigs = onSnapshot(query(collection(db, "leagues", lid, "waiverSignatures"), where("signedByUid", "==", myUid)), snap => {
        setSignatures(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    }
    return () => { unsubWaivers(); unsubSigs(); };
  }, [lid, isAdmin, myUid]);

  // Matches on uid, not email. signedByEmail was never a usable identity key
  // here: admin/co-admin `user` objects don't carry an email at all, so this
  // comparison was `null === undefined` for every admin who ever signed a
  // waiver -- it silently never matched, and they saw "Sign required" on
  // waivers they'd already signed. No email fallback, deliberately: it would
  // be dead code either way, since an admin has no email to fall back to and
  // a non-admin's listener above can't return a doc without signedByUid.
  const mySignatureFor = (waiverId) => signatures.find(s => s.waiverId === waiverId && s.signedByUid && s.signedByUid === myUid);

  const resetNewWaiverForm = () => {
    setNewWaiverName(""); setNewWaiverType("text"); setNewWaiverText("");
    setNewWaiverFile(null); setNewWaiverRequiresSig(true); setCreateError("");
    setShowNewWaiver(false);
  };

  const createWaiver = async () => {
    setCreateError("");
    if (!newWaiverName.trim()) { setCreateError("Give the waiver a name."); return; }
    if (newWaiverType === "text" && !newWaiverText.trim()) { setCreateError("Enter the waiver text, or switch to uploading a file."); return; }
    if (newWaiverType === "file" && !newWaiverFile) { setCreateError("Choose a file to upload."); return; }
    setCreatingWaiver(true);
    try {
      const id = "waiver_" + Date.now();
      let fileUrl = null, fileName = null;
      if (newWaiverType === "file") {
        const path = "leagues/" + lid + "/waivers/" + id + "/" + newWaiverFile.name;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, newWaiverFile);
        fileUrl = await getDownloadURL(fileRef);
        fileName = newWaiverFile.name;
      }
      await setDoc(doc(db, "leagues", lid, "waivers", id), {
        id, name: newWaiverName.trim(), contentType: newWaiverType,
        text: newWaiverType === "text" ? newWaiverText.trim() : "",
        fileUrl, fileName,
        requiresSignature: newWaiverRequiresSig,
        // Waivers created here (the Waivers tab) are volunteer/admin-facing,
        // same as this feature always has been. Waivers created inline from
        // the Registration tab (see RegistrationManager) are tagged
        // audience: 'registration' instead, so the parent-facing checkout
        // flow only ever pulls those in, never these. Existing waivers from
        // before this field existed have no audience set at all, which the
        // registration-side filter (audience === 'registration') already
        // excludes correctly with no backfill needed.
        audience: 'volunteer',
        createdAt: new Date().toISOString(),
        createdBy: (user && user.name) || "Admin"
      });
      resetNewWaiverForm();
    } catch (err) {
      setCreateError("Couldn't save that waiver. Try again.");
    }
    setCreatingWaiver(false);
  };

  const deleteWaiver = async (waiverId) => {
    if (!window.confirm("Delete this waiver? Existing signatures for it will be kept for your records.")) return;
    await deleteDoc(doc(db, "leagues", lid, "waivers", waiverId));
  };

  const submitSignature = async (waiver) => {
    const nameToSign = (signDraftName || "").trim() || ((user && user.name) || "").trim();
    if (!nameToSign) return;
    setSigning(true);
    const id = "sig_" + Date.now();
    await setDoc(doc(db, "leagues", lid, "waiverSignatures", id), {
      id, waiverId: waiver.id, waiverName: waiver.name,
      signedByName: nameToSign,
      // The identity key the read rule keys on. Distinct from signedByEmail,
      // which stays for display in the admin signature list but is nullable
      // on both signing paths -- a rule keyed on it would grant a signed-in
      // user with no email read access to every other null-email signature.
      signedByUid: myUid,
      signedByEmail: (user && user.email) || null,
      signedByRole: (user && user.role) || null,
      signedAt: new Date().toISOString()
    });
    setSigning(false);
    setSignDraftName("");
  };

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 14 }}>Loading waivers...</div>;
  }

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>📝 Waivers</div>
          <div style={{ fontSize: 13, color: C.muted }}>
            {isAdmin ? "Create waivers for volunteers and admins to review and sign." : "Review and sign any waivers that apply to you."}
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowNewWaiver(v => !v)}
            style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "9px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            + New Waiver
          </button>
        )}
      </div>

      {showNewWaiver && (
        <div style={{ background: C.white, border: "2px solid " + C.greenBd, borderRadius: R.card, padding: "1rem", marginBottom: 16 }}>
          <input placeholder="Waiver name, e.g. Volunteer Code of Conduct" value={newWaiverName} onChange={e => setNewWaiverName(e.target.value)}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", marginBottom: 10 }} />

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button onClick={() => setNewWaiverType("text")}
              style={{ flex: 1, padding: "8px", borderRadius: R.control, border: "1px solid " + (newWaiverType === "text" ? C.greenMid : C.border), background: newWaiverType === "text" ? C.greenLt : C.white, fontSize: 12, fontWeight: 700, color: newWaiverType === "text" ? C.text : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
              Type it in
            </button>
            <button onClick={() => setNewWaiverType("file")}
              style={{ flex: 1, padding: "8px", borderRadius: R.control, border: "1px solid " + (newWaiverType === "file" ? C.greenMid : C.border), background: newWaiverType === "file" ? C.greenLt : C.white, fontSize: 12, fontWeight: 700, color: newWaiverType === "file" ? C.text : C.muted, cursor: "pointer", fontFamily: "inherit" }}>
              Upload a file
            </button>
          </div>

          {newWaiverType === "text" ? (
            <textarea placeholder="Paste or type the full waiver text here..." value={newWaiverText} onChange={e => setNewWaiverText(e.target.value)}
              rows={6}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", marginBottom: 10, resize: "vertical" }} />
          ) : (
            <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setNewWaiverFile(e.target.files[0] || null)}
              style={{ width: "100%", marginBottom: 10, fontSize: 13 }} />
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={newWaiverRequiresSig} onChange={e => setNewWaiverRequiresSig(e.target.checked)} />
            <span style={{ fontSize: 13, color: C.text }}>Requires a signature to acknowledge</span>
          </label>

          {createError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark, marginBottom: 10 }}>{createError}</div>}

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createWaiver} disabled={creatingWaiver}
              style={{ flex: 1, background: creatingWaiver ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: creatingWaiver ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {creatingWaiver ? "Saving..." : "Create Waiver"}
            </button>
            <button onClick={resetNewWaiverForm}
              style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {waivers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1.5rem", color: C.muted, fontSize: 13 }}>
          No waivers yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {waivers.map(w => {
            const isExpanded = expandedWaiverId === w.id;
            const mySig = mySignatureFor(w.id);
            const waiverSigs = signatures.filter(s => s.waiverId === w.id);
            const viewingSigs = viewingSignaturesId === w.id;
            return (
              <div key={w.id} style={{ background: C.white, border: "0.5px solid " + C.border, borderLeft: "4px solid " + (w.requiresSignature ? C.amber : C.blue), borderRadius: R.card, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ cursor: "pointer", flex: 1, minWidth: 160 }} onClick={() => { const next = isExpanded ? null : w.id; setExpandedWaiverId(next); if (next) setSignDraftName((user && user.name) || ""); }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {w.name} <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      Created {new Date(w.createdAt).toLocaleDateString()}
                      {w.requiresSignature && (mySig ? " · ✓ You signed this" : " · Sign required")}
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setViewingSignaturesId(viewingSigs ? null : w.id)}
                        style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                        Signatures ({waiverSigs.length})
                      </button>
                      <button onClick={() => deleteWaiver(w.id)}
                        style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {viewingSigs && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border }}>
                    {waiverSigs.length === 0 ? (
                      <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>No one has signed this yet.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {waiverSigs.map(s => (
                          <div key={s.id} style={{ fontSize: 12, color: C.text, display: "flex", justifyContent: "space-between" }}>
                            <span>{s.signedByName}</span>
                            <span style={{ color: C.muted }}>{new Date(s.signedAt).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + C.border }}>
                    {w.contentType === "text" ? (
                      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap", background: C.grayLt, borderRadius: R.control, padding: 12, marginBottom: 12, maxHeight: 260, overflowY: "auto" }}>
                        {w.text}
                      </div>
                    ) : (
                      <a href={w.fileUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "8px 14px", fontSize: 13, fontWeight: 700, color: C.blue, textDecoration: "none", marginBottom: 12 }}>
                        ⬇ View / Download {w.fileName || "file"}
                      </a>
                    )}

                    {w.requiresSignature && (
                      mySig ? (
                        <div style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "10px 12px", fontSize: 12, color: C.text }}>
                          ✓ Signed by <b>{mySig.signedByName}</b> on {new Date(mySig.signedAt).toLocaleDateString()}
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input placeholder="Type your full name to sign" value={signDraftName}
                            onChange={e => setSignDraftName(e.target.value)}
                            style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
                          <span style={{ fontSize: 11, color: C.muted }}>{new Date().toLocaleDateString()}</span>
                          <button onClick={() => submitSignature(w)} disabled={signing || !signDraftName.trim()}
                            style={{ background: (signing || !signDraftName.trim()) ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                            {signing ? "Submitting..." : "Sign & Submit"}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SponsorAdCarousel({ lid, isPromoLeague }) {
  const [banners, setBanners] = React.useState([]);
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (!lid) return;
    const unsub = onSnapshot(query(collection(db, 'sponsoredBanners'), where('active', '==', true)), snap => {
      const all = snap.docs.map(d => d.data());
      const eligible = all.filter(b => b.scope === 'all' || (b.leagueIds || []).includes(lid));
      setBanners(eligible);
      setIndex(0);
    });
    return () => unsub();
  }, [lid]);

  React.useEffect(() => {
    if (banners.length < 2) return;
    const timer = setInterval(() => setIndex(i => (i + 1) % banners.length), 6000);
    return () => clearInterval(timer);
  }, [banners.length]);

  if (banners.length === 0) return null;
  const banner = banners[index];

  return (
    <div style={{ marginBottom: 14, maxWidth: 500, margin: "0 auto 14px" }}>
      <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 3, textAlign: "right" }}>SPONSORED</div>
      <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer sponsored"
        style={{ display: "block", borderRadius: R.control, overflow: "hidden", border: "1px solid " + C.border, textDecoration: "none", background: C.grayLt }}>
        {banner.imageUrl && (
          <img src={banner.imageUrl} alt={banner.name} style={{ display: "block", width: "100%", aspectRatio: "25 / 8", objectFit: "cover" }} />
        )}
      </a>
      {banners.length > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 6 }}>
          {banners.map((b, i) => (
            <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: i === index ? C.text : C.border }} />
          ))}
        </div>
      )}
      {isPromoLeague && (
        <div style={{ fontSize: 10, color: C.muted, textAlign: "center", marginTop: 6, lineHeight: 1.4 }}>
          Ads are removed on paid League plans. Your league currently has complimentary League access — ads will stop once billing starts.
        </div>
      )}
    </div>
  );
}

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

// Public registration link slug (registration-parent-facing-v1-spec.md) --
// lowercase, non-alphanumeric collapsed to single hyphens, no leading/
// trailing hyphens. Same normalization used for both suggesting a slug and
// validating whatever the admin types in.
function slugify(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
  if (d > 0) return d + "d ago";
  if (h > 0) return h + "h ago";
  if (m > 0) return m + "m ago";
  return "just now";
}

function fmtDuration(ms) {
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (h > 0 && m > 0) return h + "h " + m + "m";
  if (h > 0) return h + "h";
  return m + "m";
}

// Renders "—" rather than the literal string "Invalid Date" when the input is
// missing or unparseable. Found on staging: one league doc carries a createdAt
// this cannot read, and the card rendered "Created Invalid Date". "Invalid
// Date" is never a desirable thing to show a user anywhere in the app, so the
// guard lives in the formatter rather than at each of the ~16 call sites.
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// A league whose leagueName is missing or blank is still a REAL league -- it
// must keep its place in every count and every list, so this is a display
// fallback and never a filter. Without it, `names.join(', ')` on a list
// containing an empty string produces a leading comma and one visible name for
// two leagues, which reads as a broken count rather than a nameless record.
function leagueLabel(name) {
  const s = typeof name === "string" ? name.trim() : "";
  return s || "(unnamed league)";
}

// ── Date comparators that cannot return NaN ────────────────────────────────
// `new Date(b.x) - new Date(a.x)` yields NaN the moment either side is missing
// or unparseable, and a comparator that returns NaN is UNDEFINED BEHAVIOUR for
// Array.prototype.sort -- the engine may produce any order at all. Not
// theoretical: one staging league doc with an unparseable createdAt made the
// same league list sort two different ways on two different screens.
//
// CHOSEN ORDER: undated records sort LAST in a descending ("newest first")
// list -- an unknown date is treated as infinitely old, so a bad date can
// never jump to the top of a "most recent" list and pass itself off as new.
// Two undated records compare equal and Array.prototype.sort is
// specified-stable, so they hold their input order instead of shuffling.
function dateMs(v) {
  if (!v) return NaN;
  const t = new Date(v).getTime();
  return isNaN(t) ? NaN : t;
}
function byDateDesc(key) {
  return (a, b) => {
    const av = dateMs(a && a[key]), bv = dateMs(b && b[key]);
    const aBad = isNaN(av), bBad = isNaN(bv);
    if (aBad && bBad) return 0;
    if (aBad) return 1;
    if (bBad) return -1;
    return bv - av;
  };
}

function Avatar({ name, size = 32, bg = C.greenLt, fg = C.green }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.floor(size * 0.38), fontWeight: 700, color: fg, flexShrink: 0 }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

function Badge({ children, bg, color }) {
  return React.createElement("span", { style: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: R.control, background: bg, color, display: "inline-block" } }, children);
}

function ConfirmModal({ modal, onConfirm, onCancel }) {
  if (!modal) return null;
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.white, borderRadius: R.card, padding: 20, maxWidth: 340, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        {modal.title && <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 8 }}>{modal.title}</div>}
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.5 }}>{modal.message}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel}
            style={{ flex: 1, background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "9px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: C.muted }}>
            {modal.cancelLabel || "Cancel"}
          </button>
          <button onClick={onConfirm}
            style={{ flex: 1, background: modal.danger ? C.redFill : C.greenMid, color: modal.danger ? C.white : C.text, border: "none", borderRadius: R.control, padding: "9px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {modal.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// One-time existing-member login popup for the coach-identification question
// (coach-flag-feature-spec.md, Section 4). "No" saves immediately since it's a
// complete answer either way. "Yes" reveals the optional Head/Assistant step;
// closing at that point still counts as a full "Yes" answer (isCoach: true,
// coachRole: null if nothing was picked) per the spec -- only closing BEFORE
// choosing Yes/No is a true dismiss that doesn't set coachPromptSeen.
function CoachPromptModal({ show, leagueName, isCoach, coachRole, saving, onChangeIsCoach, onChangeCoachRole, onDone, onClose }) {
  if (!show) return null;
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.white, borderRadius: R.card, padding: 20, maxWidth: 360, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", position: "relative" }}>
        <button onClick={onClose} aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", fontSize: 16, color: C.muted, cursor: "pointer", lineHeight: 1 }}>
          ✕
        </button>
        <CoachPrompt leagueName={leagueName} isCoach={isCoach} coachRole={coachRole} onChangeIsCoach={onChangeIsCoach} onChangeCoachRole={onChangeCoachRole} />
        {isCoach === true && (
          <button onClick={onDone} disabled={saving}
            style={{ marginTop: 16, width: "100%", background: saving ? C.grayBd : C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "9px", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Saving…" : "Done"}
          </button>
        )}
      </div>
    </div>
  );
}

// Small pill shown next to a person's name wherever they're listed (superadmin
// dashboard) to surface the coach-identification fields (isCoach/coachRole) set
// via CoachPrompt/CoachPromptModal. Renders nothing if isCoach isn't true --
// "No" and "not yet answered" look identical here, which is correct since
// neither should visually stand out. Uses the same ClipboardListIcon as the
// Coaches nav tab -- a sport-neutral icon, since the coach flag has no sport
// dimension (a league can span multiple sport divisions).
function CoachBadge({ isCoach, coachRole }) {
  if (!isCoach) return null;
  const label = coachRole === 'head' ? 'Head Coach' : coachRole === 'assistant' ? 'Assistant Coach' : 'Coach';
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: R.control, background: "#FFF4E0", color: "#8A5A00", whiteSpace: "nowrap" }}>
      <ClipboardListIcon size={11} strokeWidth={2.5} style={{ flexShrink: 0 }} /> {label}
    </span>
  );
}

// Superadmin routes. The tab order here is also the rail's nav order.
// `ads` deliberately has a route even though the Advertising SCREEN itself is
// untouched by the redesign -- without one, a refresh or a Back out of
// Advertising would land on a path the dashboard can't interpret.
const SA_ROUTES = [
  { tab: "dashboard", path: "/superadmin" },
  { tab: "leagues", path: "/superadmin/leagues" },
  { tab: "promos", path: "/superadmin/promo-codes" },
  { tab: "ads", path: "/superadmin/advertising" }
];

// WHY A PREFIX MATCH AND NOT `=== '/superadmin'`:
// three separate places used to compare the pathname for exact equality. Once
// the console has sub-routes (/superadmin/leagues etc.), an exact check is
// false at those URLs -- so a refresh on a sub-route fell through to the
// ordinary league-lookup path and SILENTLY SIGNED A SUPERADMIN INTO THEIR OWN
// LEAGUE instead of showing the console. Every superadmin path test goes
// through this one function so that can't drift apart again.
function isSuperadminPath(p) {
  const path = typeof p === "string" ? p : window.location.pathname;
  return path === "/superadmin" || path.startsWith("/superadmin/");
}

// Same exact-or-prefix-with-trailing-slash shape as isSuperadminPath, and for
// the same reason: a bare startsWith would resolve /superadmin/leagues-foo to
// the Leagues tab, leaving the URL and the rail's active state disagreeing and
// giving the URL-sync effect two sources of truth. Anything that is not an
// exact route or a path BELOW one falls back to the dashboard.
function saTabFromPath(p) {
  const path = typeof p === "string" ? p : window.location.pathname;
  const match = SA_ROUTES.find(r =>
    r.path !== "/superadmin" && (path === r.path || path.startsWith(r.path + "/"))
  );
  return match ? match.tab : "dashboard";
}

// ═══════════════════════════════════════════════════════════════════════════
// SUPERADMIN DESIGN SYSTEM  (/superadmin only — see CLAUDE.md)
//
// These tokens and primitives are a SEPARATE system from the league app's
// C / R / T objects and Btn / FieldInput / ConfirmModal. That separation is
// deliberate and load-bearing, not duplication for its own sake:
//
//   - C.greenMid, R.card = 0 and FieldInput's 1.5px borders are wired into
//     the parent registration flow, the Stripe checkout screens and the
//     refund modal. Retuning them to the superadmin spec would restyle real
//     money-handling surfaces as a side effect of an internal-tool redesign.
//   - So the superadmin screens get their own SA / Sa* namespace, and the
//     shared components are reused ONLY where they already fit unchanged.
//
// Everything here is prefixed `Sa` / `SA` / `.sa--` so it is greppable and so
// no selector can escape into the rest of the app. See SaStyles for why the
// CSS prefix specifically matters.
// ═══════════════════════════════════════════════════════════════════════════
const SA = {
  black: "#000000",
  ink: "#161616",
  ink2: "#242424",
  white: "#FFFFFF",
  paper: "#F2F2F2",
  line: "#E2E2E2",
  mute: "#666666",
  railMute: "#9A9A9A",
  railLine: "#2E2E2E",
  accent: "#1DFE1B",
  area: "rgba(29,254,27,.12)",
  scrim: "rgba(0,0,0,.6)",
  // radii / control heights / rail row, straight from the handoff
  r1: 2, r2: 4, r3: 8,
  hSm: 32, hMd: 40, hLg: 48,
  railRow: 44,
  sh1: "0 1px 2px rgba(0,0,0,.06)",
  sh2: "0 2px 8px rgba(0,0,0,.10)",
  sh3: "0 8px 24px rgba(0,0,0,.18)",
  ease: "cubic-bezier(.2,0,.2,1)"
};

// The superadmin spec mandates hover, press and focus states on effectively
// every control. App.js has no stylesheet for components (11 onMouseEnter
// handlers in ~15k lines) and inline styles cannot express :hover at all, so
// a real <style> block is the only way to honour the spec without hanging
// mouse handlers off several dozen elements.
//
// EVERY selector here is namespaced `.sa--*`. That is not cosmetic. A bare
// `::-webkit-scrollbar` or a bare element selector in this block would apply
// to the ENTIRE app, including the parent registration flow and the Stripe
// checkout screens, because there is exactly one document and this <style>
// is global the moment it mounts. Keep the prefix on every rule, including
// the scrollbar and mask rules, and never add a bare element selector.
function SaStyles() {
  return (
    <style>{`
.sa--root, .sa--root * { box-sizing: border-box; }

.sa--nav { transition: background 180ms ${SA.ease}, color 180ms ${SA.ease}, border-color 180ms ${SA.ease}; }
.sa--nav:hover { background: ${SA.ink2}; color: ${SA.white}; }
.sa--nav:hover .sa--navcount { color: ${SA.white}; }

.sa--btn { transition: background 180ms ${SA.ease}, color 180ms ${SA.ease}, border-color 180ms ${SA.ease}, box-shadow 180ms ${SA.ease}; }
.sa--btn:not(:disabled):active { transform: translateY(1px); }
.sa--btn:disabled { opacity: .4; cursor: not-allowed; }
.sa--btn-primary:not(:disabled):hover { background: ${SA.ink2}; color: ${SA.white}; }
.sa--btn-secondary:not(:disabled):hover { background: ${SA.ink}; color: ${SA.white}; }
.sa--btn-ghost:not(:disabled):hover { color: ${SA.ink}; background: ${SA.paper}; }
.sa--btn-railghost:not(:disabled):hover { color: ${SA.accent}; }
/* Phase 2. The SA palette is deliberately monochrome, so it has no red of its
   own -- this READS the shared C.redFill/C.redDark rather than minting a
   second destructive red, which keeps "destructive" one colour app-wide. It
   is a read of a shared token, not a change to one. */
.sa--btn-danger:not(:disabled):hover { background: ${C.redDark}; border-color: ${C.redDark}; }
.sa--btn-dangerghost:not(:disabled):hover { color: ${C.white}; background: ${C.redFill}; border-color: ${C.redFill}; }
.sa--btn:focus-visible { outline: 2px solid ${SA.accent}; outline-offset: 2px; }
/* Yes/No and Head/Assistant in SaCoachEdit. Only the UNSELECTED state gets a
   hover -- the selected one is already SA.accent, and darkening it on hover
   would read as a second, different "on" state. */
.sa--choice:not(:disabled):not([aria-pressed="true"]):hover { border-color: ${SA.ink}; background: ${SA.paper}; }

.sa--input, .sa--select { transition: border-color 120ms ${SA.ease}; }
.sa--input:focus, .sa--select:focus { outline: none; border-color: ${SA.accent}; }

.sa--card { transition: background 180ms ${SA.ease}, border-color 180ms ${SA.ease}, box-shadow 180ms ${SA.ease}; }
.sa--card-click:hover { background: ${SA.paper}; border-color: ${SA.ink}; box-shadow: ${SA.sh2}; }
.sa--row:hover { background: ${SA.paper}; }

.sa--tab { transition: color 180ms ${SA.ease}, border-color 180ms ${SA.ease}; }
.sa--tab:hover { color: ${SA.ink}; }
.sa--chip { transition: background 180ms ${SA.ease}, color 180ms ${SA.ease}, border-color 180ms ${SA.ease}; }
.sa--chip:not(.sa--chip-on):hover { border-color: ${SA.ink}; color: ${SA.ink}; }

/* Horizontal scrollers for the six metric tabs and six range chips. At 375px
   neither row fits; they scroll rather than wrap, because a wrapped tab strip
   changes height as the active underline moves between lines, which shifts
   the chart below it on every selection. */
.sa--tabstrip { overflow-x: auto; overflow-y: hidden; flex-wrap: nowrap; -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; }
.sa--tabstrip::-webkit-scrollbar { display: none; }
.sa--fademask { -webkit-mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 20px), transparent 100%); mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 20px), transparent 100%); }

.sa--scrim { animation: sa--fadein 180ms ${SA.ease}; }
@keyframes sa--fadein { from { opacity: 0; } to { opacity: 1; } }
`}</style>
  );
}

function SaBtn({ children, onClick, variant = "primary", size = "md", disabled, full, title, iconOnly }) {
  const v = {
    primary: { background: SA.accent, color: SA.black, border: "1px solid " + SA.accent },
    secondary: { background: "transparent", color: SA.ink, border: "1px solid " + SA.ink },
    ghost: { background: "transparent", color: SA.mute, border: "1px solid transparent" },
    railghost: { background: "transparent", color: SA.railMute, border: "1px solid transparent" },
    // Phase 2. `danger` is the filled confirm inside SaConfirm; `dangerghost`
    // is the quiet trash affordance that OPENS it. The loud one is the one
    // that actually destroys something -- the icon button only asks.
    danger: { background: C.redFill, color: C.white, border: "1px solid " + C.redFill },
    dangerghost: { background: "transparent", color: C.redFill, border: "1px solid transparent" }
  }[variant];
  const h = size === "sm" ? SA.hSm : size === "lg" ? SA.hLg : SA.hMd;
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={"sa--btn sa--btn-" + variant}
      style={{
        ...v, height: h, padding: iconOnly ? 0 : "0 14px", width: iconOnly ? h : (full ? "100%" : undefined),
        borderRadius: SA.r1, fontFamily: "inherit", fontSize: 13, fontWeight: 700,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0, whiteSpace: "nowrap"
      }}>
      {children}
    </button>
  );
}

function SaInput({ label, value, onChange, placeholder, type = "text", onKeyDown, inputMode }) {
  // NOTE: src/index.css forces every input/select to 16px !important below
  // 639px as an iOS auto-zoom guard. That is deliberate shipped behaviour and
  // is NOT overridden here -- so this renders at 14px on desktop and 16px on
  // mobile. The 40px control height is what carries the spec's proportions at
  // both sizes, which is why it never shrinks below 40 in a form.
  return (
    <label style={{ display: "block" }}>
      {label && <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: SA.mute, marginBottom: 6 }}>{label}</div>}
      <input className="sa--input" type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown} inputMode={inputMode}
        style={{ width: "100%", height: SA.hMd, padding: "0 12px", border: "1px solid " + SA.line, borderRadius: SA.r2, background: SA.white, color: SA.ink, fontFamily: "inherit", fontSize: 14 }} />
    </label>
  );
}

// Select twin of SaInput -- same label treatment, same 40px control, same
// focus ring via the shared .sa--select rule. Same index.css caveat too: the
// 16px !important floor below 639px applies to selects as well as inputs, and
// is deliberately not overridden.
function SaSelect({ label, value, onChange, children }) {
  return (
    <label style={{ display: "block" }}>
      {label && <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: SA.mute, marginBottom: 6 }}>{label}</div>}
      <select className="sa--select" value={value} onChange={onChange}
        style={{ width: "100%", height: SA.hMd, padding: "0 10px", border: "1px solid " + SA.line, borderRadius: SA.r2, background: SA.white, color: SA.ink, fontFamily: "inherit", fontSize: 14 }}>
        {children}
      </select>
    </label>
  );
}

function SaCard({ children, style, className = "", onClick }) {
  return (
    <div onClick={onClick} className={"sa--card " + className}
      style={{ background: SA.white, border: "1px solid " + SA.line, borderRadius: SA.r3, ...style }}>
      {children}
    </div>
  );
}

function SaEyebrow({ children, style }) {
  return <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: SA.mute, ...style }}>{children}</div>;
}

// Shared shell for the league drawer, the create-league drawer and (below
// 640px) the off-canvas rail. Portalled so it escapes any transformed or
// overflow-hidden ancestor. Closes on scrim click and on Escape; the spec
// asks for a 180ms scrim fade and explicitly no panel slide-and-bounce.
// `z` exists so a confirm can stack ON TOP of the drawer that launched it.
// Both would otherwise sit at 4000 and the winner would be decided by portal
// append order, which is a property of render sequence rather than intent.
function SaDrawer({ open, onClose, kicker, title, children, side = "right", width = 440, z = 4000 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="sa--scrim" style={{ position: "fixed", inset: 0, background: SA.scrim, zIndex: z }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          position: "absolute", top: 0, bottom: 0, [side]: 0,
          width: "min(100vw, " + width + "px)",
          background: side === "left" ? SA.black : SA.white,
          [side === "left" ? "borderRight" : "borderLeft"]: "1px solid " + (side === "left" ? SA.railLine : SA.line),
          boxShadow: SA.sh3, display: "flex", flexDirection: "column", overflow: "hidden"
        }}>
        {title !== undefined && (
          <div style={{ padding: 20, borderBottom: "1px solid " + SA.line, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {kicker && <SaEyebrow style={{ marginBottom: 4 }}>{kicker}</SaEyebrow>}
              <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, lineHeight: 1.05, color: SA.ink }}>{title}</div>
            </div>
            <SaBtn variant="ghost" size="sm" iconOnly onClick={onClose} title="Close"><XIcon size={18} /></SaBtn>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

// Small uppercase status pill. One definition so the Leagues cards and the
// dashboard's Recent-leagues rows cannot drift apart -- they were the same
// hand-written span in two places before Phase 2.
function SaPill({ children, solid }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
      padding: "3px 8px", borderRadius: SA.r1, whiteSpace: "nowrap",
      background: solid ? SA.ink : "transparent", color: solid ? SA.white : SA.mute,
      border: "1px solid " + (solid ? SA.ink : SA.line)
    }}>{children}</span>
  );
}

// One row per person in the league drawer -- admin, co-admin or volunteer.
// DEFINED AT MODULE SCOPE ON PURPOSE. Declaring this inside the dashboard's
// render would give React a brand-new component type on every render, so every
// keystroke in the Leagues search box would unmount and remount every row.
// `onRemove` is optional: the league's own admin has no Remove action, because
// removing the owner is what Delete League is for.
function SaPersonRow({ name, sub, isCoach, coachRole, tag, onEditCoach, onRemove, removeTitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid " + SA.line }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: SA.paper, border: "1px solid " + SA.line, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: SA.ink, flexShrink: 0 }}>
        {(name || "?").slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: SA.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name || "—"}</div>
        {sub && <div style={{ fontSize: 12, color: SA.mute, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
      </div>
      <CoachBadge isCoach={isCoach} coachRole={coachRole} />
      {tag && <SaPill>{tag}</SaPill>}
      <SaBtn variant="ghost" size="sm" iconOnly title="Edit coach status" onClick={onEditCoach}><PencilIcon size={15} strokeWidth={2} /></SaBtn>
      {onRemove && (
        <SaBtn variant="dangerghost" size="sm" iconOnly title={removeTitle || "Remove"} onClick={onRemove}><Trash2Icon size={15} strokeWidth={2} /></SaBtn>
      )}
    </div>
  );
}

// Confirmation for destructive superadmin actions. Reuses SaDrawer rather than
// being a second overlay implementation, so scrim, Escape, portalling and the
// fade all behave identically to every other superadmin overlay.
//
// WHY NOT THE SHARED <ConfirmModal />: it is wired into the league app and the
// registration/refund surfaces, and neither of its two branches is legal in
// this design system (one is C.redFill, one is C.greenMid). Restyling it to
// suit an internal tool would repaint money-handling screens. This is the same
// reasoning that produced the SA namespace in the first place.
//
// A native window.confirm was what these three actions used before. It is
// modal to the browser, unstyleable, and reads as a different application
// appearing in the middle of a redesigned console.
function SaConfirm({ open, kicker, title, body, confirmLabel, onConfirm, onCancel, busy }) {
  return (
    <SaDrawer open={open} onClose={onCancel} kicker={kicker} title={title} width={460} z={4200}>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: SA.ink }}>{body}</div>
        {/* STACKED, NOT A ROW, and Cancel leads. The row version placed a
            `full` danger button -- width:100%, and SaBtn sets flexShrink:0 --
            beside a nowrap Cancel, so the pair measured wider than the panel.
            The content column is overflowY:auto, which computes overflow-x to
            auto as well, so it scrolled: Cancel went off the right edge and on
            the delete-league confirm the only visible button was "Delete this
            league". The escape hatch was the hidden one.
            Stacking makes overflow structurally impossible at any width, and
            Cancel first puts the safe action ahead of the destructive one in
            both reading order and tab order -- a keyboard Return after opening
            the confirm lands on Cancel, and reaching the destructive button
            means travelling past it. The two are the same size on purpose:
            Cancel must never be the smaller target of the pair. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SaBtn variant="secondary" full disabled={busy} onClick={onCancel}>Cancel</SaBtn>
          <SaBtn variant="danger" full disabled={busy} onClick={onConfirm}>{busy ? "Working…" : confirmLabel}</SaBtn>
        </div>
      </div>
    </SaDrawer>
  );
}

// Superadmin coach-flag editor. Deliberately NOT CoachPromptModal, and the
// reason is not styling.
//
// CoachPromptModal asks a person about themselves -- "Are you a coach or
// assistant coach for X?" -- which is correct at signup and in the login
// popup, where the reader IS the subject, and its wording is marked
// do-not-rephrase in its own comment. In the league drawer the reader is
// never the subject: an admin, several co-admins and several volunteers are
// all editable from one drawer, and the shared wording names none of them. A
// superadmin looking at "Are you a coach...?" has nothing on screen telling
// them whose record the next click writes, which is a live risk of flagging
// the wrong person. So this component NAMES the subject, in the title and
// again in the question.
//
// It is also a drawer rather than a viewport-centred card, so it opens over
// the drawer that launched it instead of across the screen from the row that
// was clicked.
//
// DISMISSING THIS DRAWER IS A CANCEL. onClose writes nothing; only "Save"
// commits a Yes. onChangeIsCoach still saves immediately on "No", because
// "No" is a complete answer with nothing left to confirm.
//
// That is the one behavioural difference from CoachPromptModal, whose own
// close COMMITS an outstanding Yes -- correct there, since that person is
// answering about themselves and discarding their answer would lose their
// input. Here the editor is writing someone else's record inside a console
// where SaConfirm has already established that dismissing a drawer means
// nothing happened, and these two drawers look identical. See
// closePlatformCoachEdit for the full reasoning; the two close paths share no
// state and must not be re-merged.
function SaCoachEdit({ open, name, leagueName, isCoach, coachRole, saving, onChangeIsCoach, onChangeCoachRole, onDone, onClose }) {
  const who = name || "this person";
  // A plain render helper called as choice(...), NOT a component rendered as
  // <Choice/>. Declaring a component inside another render gives React a new
  // component type every render and remounts its subtree; calling a function
  // that returns JSX does not.
  const choice = (label, on, onPick, grow) => (
    <button type="button" onClick={onPick} aria-pressed={on} disabled={saving}
      className="sa--btn sa--choice"
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        flex: grow ? 1 : undefined, width: grow ? undefined : "100%",
        height: SA.hMd, padding: "0 12px", borderRadius: SA.r1,
        fontFamily: "inherit", fontSize: 13, fontWeight: 700, textAlign: "left",
        background: on ? SA.accent : SA.white, color: on ? SA.black : SA.ink,
        border: "1px solid " + (on ? SA.accent : SA.line),
        cursor: saving ? "not-allowed" : "pointer"
      }}>
      {label}
      {on && <CheckIcon size={16} strokeWidth={3} />}
    </button>
  );
  return (
    // z above SaConfirm's 4200. The two are mutually exclusive in practice --
    // each one's scrim covers the person rows that open the other -- so the
    // order only has to be deterministic, not dynamic.
    <SaDrawer open={open} onClose={onClose} kicker="Coach status" title={name || "—"} width={420} z={4400}>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.5, color: SA.ink }}>
          Is {who} a coach for {leagueName}?
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {choice("Yes", isCoach === true, () => onChangeIsCoach(true), true)}
          {choice("No", isCoach === false, () => onChangeIsCoach(false), true)}
        </div>
        {isCoach === true && (
          <>
            <div>
              <SaEyebrow style={{ marginBottom: 8 }}>Which one?</SaEyebrow>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {choice("Head coach", coachRole === 'head', () => onChangeCoachRole('head'))}
                {choice("Assistant coach", coachRole === 'assistant', () => onChangeCoachRole('assistant'))}
              </div>
              {/* Optional by design in the original feature -- a coach who is
                  neither is a valid answer, so this never reads as unfinished. */}
              <div style={{ fontSize: 12, color: SA.mute, marginTop: 8, lineHeight: 1.5 }}>
                Optional. Leaving this unset still records {who} as a coach.
              </div>
            </div>
            <SaBtn variant="primary" full disabled={saving} onClick={onDone}>
              {saving ? "Saving…" : "Save"}
            </SaBtn>
          </>
        )}
      </div>
    </SaDrawer>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", disabled, full, as: Tag = "button" }) {
  const styles = {
    primary: { bg: C.greenMid, color: C.text, border: "none" },
    secondary: { bg: C.greenLt, color: C.text, border: "1px solid " + C.greenBd },
    danger: { bg: C.redLt, color: C.redDark, border: "1px solid " + C.redBd },
    ghost: { bg: "transparent", color: C.muted, border: "1px solid " + C.border },
    blue: { bg: C.blueLt, color: C.blue, border: "1px solid " + C.blueBd },
    blueSolid: { bg: C.blue, color: C.white, border: "none" },
    purple: { bg: C.purpleLt, color: C.purple, border: "1px solid " + C.purpleBd },
    white: { bg: C.white, color: C.text, border: "1px solid " + C.border }
  };
  const sizes = { sm: "6px 12px", md: "9px 16px", lg: "12px 22px" };
  const s = styles[variant];
  return (
    <Tag onClick={onClick} disabled={Tag === "button" ? disabled : undefined}
      style={{ background: s.bg, color: s.color, border: s.border, borderRadius: R.control, padding: sizes[size], fontSize: size === "sm" ? 12 : 14, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", width: full ? "100%" : undefined, opacity: disabled ? 0.4 : 1, fontFamily: "inherit", ...(Tag === "button" ? {} : { display: "inline-block", textAlign: "center", boxSizing: "border-box" }) }}>
      {children}
    </Tag>
  );
}

function FieldInput({ label, type = "text", placeholder, value, onChange, onKeyDown }) {
  return (
    <div>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>{label}</label>}
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} onKeyDown={onKeyDown}
        style={{ width: "100%", padding: "10px 13px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
    </div>
  );
}

// Coach-identification question (coach-flag-feature-spec.md, Section 2).
// Wording/structure is final, do not rephrase: a Yes/No question first,
// with the Head/Assistant follow-up shown only after "Yes" -- never as a
// forced choice, since a plain volunteer shouldn't feel required to pick
// one of those two options. Shared between the inline signup forms and the
// existing-member login popup.
function CoachPrompt({ leagueName, isCoach, coachRole, onChangeIsCoach, onChangeCoachRole }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>
        Are you a coach or assistant coach for {leagueName}?
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" onClick={() => onChangeIsCoach(true)}
          style={{ flex: 1, padding: "9px 16px", borderRadius: R.control, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
            background: isCoach === true ? C.greenMid : C.white, color: C.text, border: "1.5px solid " + (isCoach === true ? C.text : C.border) }}>
          Yes
        </button>
        <button type="button" onClick={() => onChangeIsCoach(false)}
          style={{ flex: 1, padding: "9px 16px", borderRadius: R.control, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
            background: isCoach === false ? C.greenMid : C.white, color: C.text, border: "1.5px solid " + (isCoach === false ? C.text : C.border) }}>
          No
        </button>
      </div>
      {isCoach === true && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 8 }}>Which one?</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
              <input type="radio" name="coachRole" checked={coachRole === 'head'} onChange={() => onChangeCoachRole('head')} />
              Head coach
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
              <input type="radio" name="coachRole" checked={coachRole === 'assistant'} onChange={() => onChangeCoachRole('assistant')} />
              Assistant coach
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function Logo() {
  return (
    <div style={{ width: 90, height: 90, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img src={LOGO_URL} alt="GroundsPal" style={{ width: 90, height: 90, objectFit: "contain", borderRadius: R.control }} />
    </div>
  );
}

function TitleComboBox({ value, onChange, titles, onAddTitle, placeholder }) {
  const [open, setOpen] = React.useState(false);
  const [localTitles, setLocalTitles] = React.useState(titles);
  const [showAddConfirm, setShowAddConfirm] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setLocalTitles(titles); }, [titles]);

  const filtered = localTitles.filter(t => t.toLowerCase().includes(value.toLowerCase()) && t.toLowerCase() !== value.toLowerCase());
  const exactMatch = localTitles.some(t => t.toLowerCase() === value.trim().toLowerCase());
  const canAdd = value.trim().length > 2 && !exactMatch;

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder || "Task title *"}
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); setShowAddConfirm(false); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          style={{ flex: 1, padding: "9px 12px", borderRadius: R.control, border: "1px solid #E5E5E5", fontSize: 14, fontFamily: "inherit", outline: "none" }}
        />
        {canAdd && (
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); onAddTitle(value.trim()); setShowAddConfirm(true); setTimeout(() => setShowAddConfirm(false), 2000); }}
            title="Save to dropdown list"
            style={{ background: "#E8F9ED", border: "1px solid #A5D6A7", borderRadius: R.control, padding: "0 10px", fontSize: 13, fontWeight: 700, color: "#34C759", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
            {showAddConfirm ? "✓ Saved!" : "+ Save"}
          </button>
        )}
      </div>
      {open && value.trim().length > 0 && (filtered.length > 0 || localTitles.length > 0) && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #E5E5E5", borderRadius: R.card, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 999, maxHeight: 220, overflowY: "auto", marginTop: 3 }}>
          {(filtered.length > 0 ? filtered : localTitles).map((t, i) => (
            <div key={i}
              onMouseDown={e => { e.preventDefault(); onChange(t); setOpen(false); }}
              style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", borderBottom: "1px solid #F3F3F2", color: "#1C1C1E", fontWeight: 500 }}
              onMouseEnter={e => e.currentTarget.style.background = "#E8F9ED"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              {t}
            </div>
          ))}
          {filtered.length === 0 && localTitles.length === 0 && (
            <div style={{ padding: "10px 14px", fontSize: 13, color: "#8E8E93", fontStyle: "italic" }}>No saved titles yet — type and click "+ Save" to add one</div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, onClick }) {
  const pr = PRIORITY[task.priority];
  const taskStatusColor = task.status === 'open' ? '#185fa5' : task.status === 'in-progress' ? '#ef9f27' : '#34c759';
  return (
    <div onClick={onClick} style={{ background: C.cardBg, border: "0.5px solid " + C.border, borderLeft: "3px solid " + taskStatusColor, borderRadius: R.card, padding: "0.75rem 1rem", cursor: "pointer" }}
      onMouseEnter={e => e.currentTarget.style.background = C.grayLt}
      onMouseLeave={e => e.currentTarget.style.background = C.cardBg}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 18, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : (task.location ? [task.location] : [])).length > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: C.muted }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : [task.location]).join(', ')}
              </span>
            )}
            <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: C.muted }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {timeAgo(task.createdAt)}
            </span>
            {(task.groups || []).map(g => (
              <span key={g} style={{ fontSize: 10, color: "#7C3AED", background: "#EDE9FE", padding: "1px 7px", borderRadius: R.control, fontWeight: 600 }}>{g}</span>
            ))}
          </div>
          {task.dueDate && (
            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: new Date(task.dueDate) < new Date() ? C.red : C.amber, display: "flex", alignItems: "center", gap: 3 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Due {fmtDate(task.dueDate + 'T12:00:00')}{new Date(task.dueDate) < new Date() ? " · Overdue" : ""}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
          <Badge bg={pr.bg} color={pr.text}>{pr.label}</Badge>
          {task.assignedTo
            ? <div style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{task.assignedTo}</div>
            : <div style={{ fontSize: 11, color: C.blue, fontWeight: 700 }}>Unassigned</div>
          }
          {task.updates && task.updates.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: C.muted }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {task.updates.length}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Confetti({ active }) {
  const [particles, setParticles] = React.useState([]);

  React.useEffect(() => {
    if (!active) { setParticles([]); return; }
    const colors = ["#FFD700","#FF6B6B","#4ECDC4","#45B7D1","#96CEB4","#FFEAA7","#DDA0DD","#98D8C8","#F7DC6F","#BB8FCE","#2ECC71","#E74C3C","#3498DB","#F39C12"];
    const newParticles = Array.from({ length: 120 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -10 - Math.random() * 20,
      size: 6 + Math.random() * 8,
      color: colors[Math.floor(Math.random() * colors.length)],
      speedX: (Math.random() - 0.5) * 3,
      speedY: 2 + Math.random() * 4,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
      opacity: 1
    }));
    setParticles(newParticles);

    let frame;
    let startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      setParticles(prev => prev.map(p => ({
        ...p,
        x: p.x + p.speedX * 0.5,
        y: p.y + p.speedY * 0.4,
        rotation: p.rotation + p.rotationSpeed,
        opacity: elapsed > 2500 ? Math.max(0, 1 - (elapsed - 2500) / 1500) : 1
      })).filter(p => p.y < 115 && p.opacity > 0));
      if (elapsed < 4000) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  if (!active && particles.length === 0) return null;
  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: p.x + "%",
          top: p.y + "%",
          width: p.shape === 'circle' ? p.size : p.size * 1.4,
          height: p.shape === 'circle' ? p.size : p.size * 0.6,
          background: p.color,
          borderRadius: p.shape === 'circle' ? "50%" : "2px",
          transform: "rotate(" + p.rotation + "deg)",
          opacity: p.opacity,
          transition: "opacity 0.1s"
        }} />
      ))}
      <div style={{ position: "absolute", top: "35%", left: "50%", transform: "translateX(-50%)", textAlign: "center", animation: "none" }}>
        <div style={{ fontSize: 52, marginBottom: 6 }}>🎉</div>
        <div style={{ fontFamily: T.display, background: "rgba(0,0,0,0.75)", color: "#fff", borderRadius: R.control, padding: "10px 22px", fontSize: 18, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", whiteSpace: "nowrap", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          Task Complete! 🙌
        </div>
      </div>
    </div>
  );
}

function BellIcon({ count, onClick }) {
  return (
    <button onClick={onClick} style={{ position: "relative", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: R.control, padding: "5px 10px", color: "rgba(255,255,255,0.9)", fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}>
      🔔
      {count > 0 && (
        <span style={{ position: "absolute", top: -6, right: -6, background: C.redFill, color: C.white, borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid " + C.greenMid }}>
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}

function NotificationsPanel({ notifications, userName, onDismiss, onDismissAll, onClose, isMobile, fieldEvents, onApproveGroupDate, onRejectGroupDate, onApproveGroupAll, onRejectGroupAll }) {
  const [expandedGroups, setExpandedGroups] = useState([]);
  const toggleExpand = (id) => setExpandedGroups(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  return (
    <div style={{ position: "fixed", top: 56, right: isMobile ? 0 : 14, left: isMobile ? 0 : "auto", width: isMobile ? "100%" : 320, background: C.white, borderRadius: R.card, border: "1px solid " + C.border, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", zIndex: 1000, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Notifications</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {notifications.length > 0 && <button onClick={onDismissAll} style={{ background: "none", border: "none", fontSize: 12, color: C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Clear all</button>}
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, color: C.muted, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
      </div>
      <div style={{ maxHeight: 360, overflowY: "auto" }}>
        {notifications.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: C.muted, fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
            No new notifications
          </div>
        ) : (
          notifications.map(n => {
            const isUnread = !(n.readBy && n.readBy.includes(userName));
            if (n.type === 'event_request_group') {
              const isExpanded = expandedGroups.includes(n.id);
              const dates = n.dates || [];
              const conflictDates = n.conflictDates || [];
              const pendingDates = dates.filter(d => (fieldEvents || []).some(e => e.requestGroupId === n.requestGroupId && e.date === d && e.status === 'pending'));
              const reviewedCount = dates.length - pendingDates.length;
              return (
                <div key={n.id} style={{ padding: "12px 16px", borderBottom: "1px solid " + C.border, background: isUnread ? C.blueLt : C.white }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>📅</div>
                    <div style={{ flex: 1, cursor: "pointer" }} onClick={() => toggleExpand(n.id)}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: isUnread ? 700 : 400, lineHeight: 1.4 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                        {timeAgo(n.createdAt)}{reviewedCount > 0 && pendingDates.length > 0 ? ' · ' + reviewedCount + '/' + dates.length + ' reviewed' : ''}{pendingDates.length === 0 && dates.length > 0 ? ' · all reviewed' : ''} · {isExpanded ? '▲ hide dates' : '▼ show dates'}
                      </div>
                    </div>
                    <button onClick={() => onDismiss(n.id)} style={{ background: "none", border: "none", fontSize: 14, color: C.muted, cursor: "pointer", flexShrink: 0, padding: "2px 4px" }}>×</button>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 8, marginLeft: 30 }}>
                      {pendingDates.length > 1 && (
                        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                          <button onClick={() => onApproveGroupAll(n.requestGroupId)} style={{ flex: 1, background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "5px 8px", fontSize: 11, fontWeight: 700, color: C.green, cursor: "pointer", fontFamily: "inherit" }}>Approve All ({pendingDates.length})</button>
                          <button onClick={() => onRejectGroupAll(n.requestGroupId)} style={{ flex: 1, background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "5px 8px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>Decline All</button>
                        </div>
                      )}
                      {dates.map(d => {
                        const stillPending = pendingDates.includes(d);
                        const flagged = conflictDates.includes(d);
                        return (
                          <div key={d} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", fontSize: 12, color: C.text, opacity: stillPending ? 1 : 0.5 }}>
                            <span>{d}{flagged ? ' ⚠️' : ''}</span>
                            {stillPending ? (
                              <span style={{ display: "flex", gap: 8 }}>
                                <button onClick={() => onApproveGroupDate(n.requestGroupId, d)} style={{ background: "none", border: "none", color: C.green, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✓</button>
                                <button onClick={() => onRejectGroupDate(n.requestGroupId, d)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✗</button>
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: C.muted }}>reviewed</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={n.id} style={{ padding: "12px 16px", borderBottom: "1px solid " + C.border, display: "flex", gap: 10, alignItems: "flex-start", background: isUnread ? C.blueLt : C.white }}>
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>
                  {n.type === 'task_claimed' ? '🙋' : n.type === 'coadmin_joined' ? '🤝' : '📋'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: isUnread ? 700 : 400, lineHeight: 1.4 }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
                </div>
                <button onClick={() => onDismiss(n.id)} style={{ background: "none", border: "none", fontSize: 14, color: C.muted, cursor: "pointer", flexShrink: 0, padding: "2px 4px" }}>×</button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}



// ─── BILLING / STRIPE PRICE IDS ───────────────────────────────────────────────
// Live Price IDs are selected by REACT_APP_STRIPE_LIVE, a build variable scoped
// to Vercel's Production environment only. Every other environment (Preview,
// localhost) has it unset and falls through to test-mode IDs, so testing never
// risks touching real billing. The check is deliberately strict (=== 'true'), so
// a missing, empty, or misspelled value fails closed to test IDs rather than
// silently going live.
//
// This replaced a window.location.hostname === 'groundspal.com' check, which
// broke when the app moved to app.groundspal.com (groundspal.com is now the
// separate groundspal-landing project) -- the hostname never matched, so
// production silently resolved test-mode price IDs. Branching on a build
// variable is the same mechanism src/firebase.js already uses, and the one that
// survived the domain split.
const IS_LIVE_ENV = process.env.REACT_APP_STRIPE_LIVE === 'true';
const PRICE_IDS = IS_LIVE_ENV ? {
  pro_monthly: 'price_1TsTWsGEc1fmCpMfzZtuLGIG',
  pro_annual: 'price_1TsTWsGEc1fmCpMfaMtMO9yZ',
  league_monthly: 'price_1TsTWsGEc1fmCpMfi9JMa55q',
  league_annual: 'price_1TsTWrGEc1fmCpMfkGCDCjbz',
} : {
  pro_monthly: 'price_1Ts2TR7zfsezczPngM8oOJih',
  pro_annual: 'price_1Ts2Vo7zfsezczPnJNdWug49',
  league_monthly: 'price_1Ts2Z87zfsezczPn5AnqBlB4',
  league_annual: 'price_1Ts2ZZ7zfsezczPnJxofyhmn',
};

const sendGroupMe = (text, botId) => {
  if (!botId) return;
  fetch("https://api.groupme.com/v3/bots/post", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bot_id: botId, text })
  }).catch(() => {});
};


// ─── DEMO DATA ───────────────────────────────────────────────────────────────
const DEMO_LEAGUE = { id: 'demo', adminName: 'Coach Rivera', name: 'Riverside Youth Baseball', zipCode: '90210', locations: ['Majors Field', 'Minors Field', 'Pee Wee Field', 'Batting Cages'] };
const DEMO_LOCATIONS = ['Majors Field', 'Minors Field', 'Pee Wee Field', 'Batting Cages'];
const DEMO_MEMBERS = [
  { name: 'Jamie Rodriguez', role: 'volunteer', joinedAt: '2026-03-01' },
  { name: 'Marcus Thompson', role: 'volunteer', joinedAt: '2026-03-05' },
  { name: 'Sarah Chen', role: 'volunteer', joinedAt: '2026-03-10' },
  { name: 'Tyler Brooks', role: 'volunteer', joinedAt: '2026-03-15' },
  { name: 'Lisa Park', role: 'volunteer', joinedAt: '2026-04-01' },
];
const DEMO_TASKS = [
  { id: 'd1', title: 'Mow and line Majors Field', description: 'Cut grass to 2 inches, chalk baselines and batters box', status: 'open', priority: 'high', location: 'Majors Field', locations: ['Majors Field'], assignedTo: null, createdBy: 'Coach Rivera', createdAt: '2026-06-01T09:00:00Z', dueDate: '2026-06-05', updates: [] },
  { id: 'd2', title: 'Repair Minors Field pitching mound', description: 'Fill in holes and pack clay, check rubber placement', status: 'in-progress', priority: 'high', location: 'Minors Field', locations: ['Minors Field'], assignedTo: 'Marcus Thompson', createdBy: 'Coach Rivera', createdAt: '2026-05-28T10:00:00Z', dueDate: '2026-06-04', updates: [{ author: 'Marcus Thompson', message: 'Started work on the mound. Need more clay.', timestamp: '2026-06-02T14:00:00Z', system: false }] },
  { id: 'd3', title: 'Install new dugout benches', description: 'Replace old wooden benches in both dugouts', status: 'open', priority: 'medium', location: 'Majors Field', locations: ['Majors Field'], assignedTo: null, createdBy: 'Coach Rivera', createdAt: '2026-05-30T08:00:00Z', dueDate: '2026-06-10', updates: [] },
  { id: 'd4', title: 'Check and repair outfield fencing', description: 'Walk entire perimeter, mark any gaps or damaged sections', status: 'in-progress', priority: 'medium', location: 'Minors Field', locations: ['Minors Field'], assignedTo: 'Sarah Chen', createdBy: 'Coach Rivera', createdAt: '2026-05-29T11:00:00Z', dueDate: '2026-06-06', updates: [] },
  { id: 'd5', title: 'Clean and organize equipment shed', description: 'Sort helmets, bats, and catcher gear. Dispose of broken equipment.', status: 'open', priority: 'low', location: 'Pee Wee Field', locations: ['Pee Wee Field'], assignedTo: null, createdBy: 'Coach Rivera', createdAt: '2026-06-01T12:00:00Z', dueDate: '', updates: [] },
];
const DEMO_ARCHIVE = [
  { id: 'da1', title: 'Paint scoreboard numbers', status: 'complete', assignedTo: 'Jamie Rodriguez', completedAt: '2026-05-20T15:00:00Z', createdAt: '2026-05-15T09:00:00Z', location: 'Majors Field' },
  { id: 'da2', title: 'Drag and prep all infields', status: 'complete', assignedTo: 'Marcus Thompson', completedAt: '2026-05-22T10:00:00Z', createdAt: '2026-05-20T09:00:00Z', location: 'Minors Field' },
  { id: 'da3', title: 'Replace home plate on Varsity', status: 'complete', assignedTo: 'Jamie Rodriguez', completedAt: '2026-05-25T14:00:00Z', createdAt: '2026-05-23T09:00:00Z', location: 'Majors Field' },
  { id: 'da4', title: 'Fertilize and water all fields', status: 'complete', assignedTo: 'Sarah Chen', completedAt: '2026-05-18T12:00:00Z', createdAt: '2026-05-17T09:00:00Z', location: 'Pee Wee Field' },
  { id: 'da5', title: 'Fix sprinkler heads in left field', status: 'complete', assignedTo: 'Tyler Brooks', completedAt: '2026-05-15T16:00:00Z', createdAt: '2026-05-14T09:00:00Z', location: 'Minors Field' },
  { id: 'da6', title: 'Power wash dugout floors', status: 'complete', assignedTo: 'Lisa Park', completedAt: '2026-05-10T11:00:00Z', createdAt: '2026-05-09T09:00:00Z', location: 'Majors Field' },
];
const DEMO_BOARD = [
  { id: 'db1', title: 'Opening Day Prep — All Hands!', body: 'Hey crew! Opening Day is June 7th. We need everyone available Saturday June 6th to do final prep on all four fields. Mowing, dragging, lining, and cleaning dugouts. Should take about 3 hours. Pizza provided! 🍕', author: 'Coach Rivera', createdAt: '2026-06-01T08:00:00Z', likes: ['Jamie Rodriguez', 'Marcus Thompson'] },
  { id: 'db2', title: 'New Equipment Arrived', body: 'The new drag mats and field conditioner bags have arrived and are in the equipment shed. Please use the new drag mats on the infields going forward — the old ones are worn out and being retired.', author: 'Coach Rivera', createdAt: '2026-05-28T10:30:00Z', likes: ['Sarah Chen'] },
];
const DEMO_EVENTS = [
  { id: 'de1', title: 'Opening Day Game', field: 'Majors Field', date: new Date().toISOString().split('T')[0], startTime: '10:00', endTime: '13:00', eventType: 'game', status: 'approved', createdBy: 'Coach Rivera', notes: 'Season opener vs. Eastside Eagles' },
  { id: 'de2', title: 'JV Practice', field: 'Minors Field', date: new Date().toISOString().split('T')[0], startTime: '14:00', endTime: '16:00', eventType: 'practice', status: 'approved', createdBy: 'Coach Rivera', notes: '' },
  { id: 'de3', title: 'Varsity Practice', field: 'Pee Wee Field', date: new Date(Date.now()+86400000).toISOString().split('T')[0], startTime: '09:00', endTime: '11:30', eventType: 'practice', status: 'approved', createdBy: 'Coach Rivera', notes: 'Focus on pitching' },
  { id: 'de4', title: 'League Championship', field: 'Majors Field', date: new Date(Date.now()+172800000).toISOString().split('T')[0], startTime: '11:00', endTime: '14:00', eventType: 'game', status: 'approved', createdBy: 'Coach Rivera', notes: 'Bring extra staff for crowd management' },
];
const DEMO_SUPPLIES = [
  { id: 'ds1', name: 'Field Chalk (50 lb bags)', quantity: 8, unit: 'bags', notes: 'Low — reorder soon', purchased: false },
  { id: 'ds2', name: 'Drag Mat', quantity: 2, unit: 'units', notes: 'New ones arrived May 28', purchased: false },
  { id: 'ds3', name: 'Clay / Mound Mix', quantity: 3, unit: 'bags', notes: '', purchased: false },
  { id: 'ds4', name: 'Bases (complete set)', quantity: 1, unit: 'set', notes: 'Backup set', purchased: false },
];
const DEMO_USER = { name: 'Demo Visitor', role: 'admin', email: 'demo@groundspal.com' };

function LocationMultiSelect({ value, onChange, locations }) {
  const selected = Array.isArray(value) ? value : (value && value !== "" ? [value] : []);
  const toggle = (loc) => {
    if (selected.includes(loc)) onChange(selected.filter(l => l !== loc));
    else onChange([...selected, loc]);
  };
  const filtered = locations.filter(l => l && l.trim());
  if (filtered.length === 0) return (
    <div style={{ fontSize: 13, color: "#8E8E93", fontStyle: "italic", padding: "4px 0" }}>No locations set. Add them in Settings.</div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {filtered.map(loc => {
        const on = selected.includes(loc);
        return (
          <div key={loc} onClick={() => toggle(loc)}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: R.control, border: "1px solid " + (on ? "#A3E6B8" : "#E5E5EA"), background: on ? "#E8F9ED" : "#F2F2F7", cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontSize: 14, fontWeight: on ? 700 : 400, color: on ? "#1A7A35" : "#1C1C1E" }}>&#128205; {loc}</span>
            <div style={{ width: 44, height: 24, borderRadius: R.control, background: on ? "#34C759" : "#D6D6D4", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.3)", transition: "left 0.2s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Public, parent-facing registration page at /register/{slug}
// (registration-parent-facing-v1-spec.md). Resolves the slug to a league via
// the leagueSlugs lookup collection, then hands off to ParentAuthGate for
// account creation/sign-in. Full multi-kid/cart/checkout flow is a later,
// separate piece of this same build.
function PublicRegistrationPage({ slug, view = 'register' }) {
  const [status, setStatus] = React.useState('loading'); // 'loading' | 'not-found' | 'found' | 'error'
  const [leagueId, setLeagueId] = React.useState(null);
  const [leagueName, setLeagueName] = React.useState('');
  // Threaded down to the success screen's "want to volunteer later?" contact
  // line (see ParentRegistrationFlow) -- these already exist on every league
  // doc and are already used elsewhere in the app for admin contact.
  const [adminName, setAdminName] = React.useState('');
  const [adminEmail, setAdminEmail] = React.useState('');
  // Whether this league can actually take a payment. Read off the same league
  // doc already fetched below -- no extra read. Gates the whole flow, see the
  // RegistrationSetupPending branch under the effect.
  const [chargesEnabled, setChargesEnabled] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const slugSnap = await getDoc(doc(db, 'leagueSlugs', slug));
        if (!slugSnap.exists()) { if (!cancelled) setStatus('not-found'); return; }
        const foundLeagueId = slugSnap.data().leagueId;
        const leagueSnap = await getDoc(doc(db, 'leagues', foundLeagueId));
        if (!leagueSnap.exists()) { if (!cancelled) setStatus('not-found'); return; }
        if (!cancelled) {
          const ld = leagueSnap.data();
          setLeagueId(foundLeagueId); setLeagueName(ld.leagueName || 'this league');
          setAdminName(ld.adminName || ''); setAdminEmail(ld.adminEmail || '');
          setChargesEnabled(!!ld.stripeConnectChargesEnabled);
          setStatus('found');
        }
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Payouts gate for the registration flow, deliberately the FIRST thing after
  // the league resolves -- ahead of the auth gate, not somewhere inside the
  // flow. Without this a parent can create an account and fill in every player,
  // division, waiver and volunteer answer, and only find out at "Proceed to
  // payment" that the league never connected Stripe -- that failure is the
  // server's 400 from create-registration-checkout-session.js rendered after
  // the click.
  //
  // Condition is `stripeConnectChargesEnabled` and nothing else, matching that
  // endpoint's check exactly. Not payouts-enabled, not details-submitted: if
  // the two sides test different fields they can disagree, and a client that
  // lets a parent through to a server that refuses is the exact bug this is
  // fixing, just harder to spot. The server check stays as-is regardless --
  // an admin can disconnect mid-session, so this is UX and that is enforcement.
  //
  // The 'account' view is exempt, and that exemption is load-bearing rather
  // than a convenience: this field mirrors Stripe's live card_payments
  // capability, not "did onboarding ever finish," so it can go inactive on a
  // long-established league when requirements come due or the account gets
  // restricted. Gating the account page on it would hide past receipts and
  // registrations from parents who already paid, and tell them registration
  // isn't open when they've already completed it. Reaching that page at all
  // requires a registrant doc for this league, so on a league that never
  // connected it's only reachable by someone who signed up and couldn't pay --
  // which the page's existing empty states already handle.
  if (status === 'found' && view !== 'account' && !chargesEnabled) return <RegistrationSetupPending leagueName={leagueName} adminEmail={adminEmail} />;

  if (status === 'found') return <ParentAuthGate leagueId={leagueId} leagueName={leagueName} adminName={adminName} adminEmail={adminEmail} slug={slug} view={view} />;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.grayLt, padding: 20 }}>
      <div style={{ background: C.white, borderRadius: R.card, border: '1px solid ' + C.border, padding: '2rem', maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {status === 'loading' && <div style={{ color: C.muted, fontSize: 14 }}>Loading...</div>}
        {status === 'not-found' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><SearchXIcon size={38} strokeWidth={1.5} color={C.text} /></div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>Registration page not found</div>
            <div style={{ fontSize: 13, color: C.muted }}>This link doesn't match any league. Double-check the URL with your league admin.</div>
          </>
        )}
        {status === 'error' && <div style={{ fontSize: 13, color: C.muted }}>Something went wrong loading this page. Please try again in a moment.</div>}
      </div>
    </div>
  );
}

// Shown in place of the whole registration flow when a league's public page is
// live but it can't take payment yet (see the gate in PublicRegistrationPage).
// Same ParentAuthShell chrome and same centered emoji / bold title / muted body
// treatment as the "no registration currently open" screen, since to a parent
// these are the same situation: nothing to do here right now.
//
// Deliberately says "the league administrator" rather than interpolating
// adminName. That name was entered for internal use and has never been
// presented to parents -- an admin who typed something informal there didn't
// consent to it appearing on a public page. adminEmail is different: it's a
// contact channel, and the flow already surfaces it to parents on the success
// screen. The whole contact clause drops when there's no adminEmail rather
// than leaving a sentence trailing off at "administrator at ".
function RegistrationSetupPending({ leagueName, adminEmail }) {
  return (
    <ParentAuthShell leagueName={leagueName}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><HourglassIcon size={38} strokeWidth={1.5} color={C.text} /></div>
        <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>Registration isn't open yet</div>
        <div style={{ fontSize: 13, color: C.muted }}>
          This league is still getting set up. Check back soon
          {adminEmail ? (
            <>, or contact the league administrator at{' '}
              <a href={'mailto:' + adminEmail} style={{ color: C.green, fontWeight: 700 }}>{adminEmail}</a> with questions.</>
          ) : '.'}
        </div>
      </div>
    </ParentAuthShell>
  );
}

// Parent account creation / sign-in for a resolved public registration page
// (registration-parent-facing-v1-spec.md, section 2). Owns its own
// onAuthStateChanged listener -- this route never mounts FieldOpsAppInner,
// so there's no other auth-restoration effect running here. A registrant
// (leagues/{leagueId}/registrants/{uid}) is this league's parent/patron
// account, separate from admin/co-admin/volunteer members -- see the
// isLinkedAccountHolder rule comment in firestore.rules.
// Single source of truth for parent phone validation. Shared by the signup and
// complete-profile forms in ParentAuthGate and the contact form in
// ParentAccountPage, which all write the SAME registrant.phone field -- if they
// disagreed on what counts as valid, a number accepted at signup could be
// rejected when the parent later edits their contact info, or vice versa.
//
// Deliberately permissive: digit count only, no format enforcement. 15 is the
// E.164 maximum, so a number pasted with a country code still passes. This is
// the same rule the admin SMS field already applies -- extracted here rather
// than reimplemented, so there is only one definition to change.
function isValidPhoneNumber(v) {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

function ParentAuthGate({ leagueId, leagueName, adminName, adminEmail, slug, view = 'register' }) {
  const [authChecking, setAuthChecking] = React.useState(true);
  const [firebaseUser, setFirebaseUser] = React.useState(null);
  const [registrant, setRegistrant] = React.useState(null);
  const [registrantChecked, setRegistrantChecked] = React.useState(false);

  const [mode, setMode] = React.useState('signup'); // 'signup' | 'signin' -- only relevant while signed out
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [formLoading, setFormLoading] = React.useState(false);
  const [formError, setFormError] = React.useState('');

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setAuthChecking(false);
    });
    return unsub;
  }, []);

  // Once signed in (fresh signup, sign-in, or an existing session from
  // elsewhere in the app), check whether this uid already has a registrant
  // doc for THIS league -- a parent can be signed in as an admin/volunteer/
  // registrant of another league under the same Firebase Auth session.
  React.useEffect(() => {
    if (!firebaseUser) { setRegistrant(null); setRegistrantChecked(false); return; }
    let cancelled = false;
    (async () => {
      const snap = await getDoc(doc(db, 'leagues', leagueId, 'registrants', firebaseUser.uid));
      if (cancelled) return;
      setRegistrant(snap.exists() ? snap.data() : null);
      setRegistrantChecked(true);
    })();
    return () => { cancelled = true; };
  }, [firebaseUser, leagueId]);

  const writeRegistrantDoc = async (uid, fName, lName, emailVal, phoneVal) => {
    const name = fName + ' ' + lName;
    const data = {
      uid, firstName: fName, lastName: lName, email: emailVal,
      // Same registrant.phone field ParentAccountPage's contact form edits --
      // collected here so it's populated from the start rather than depending
      // on a parent voluntarily opening My Account later, which most never do.
      // Deliberately NOT also copied into the accountHolders entry below: that
      // would be a second copy of one value, and the contact form updates only
      // this one, so the two would silently drift apart on the first edit.
      phone: phoneVal,
      createdAt: new Date().toISOString(),
      // Forward-compat for multiple account holders per family (spec section
      // 2's open implementation question) -- populated correctly from the
      // start so it doesn't need a later backfill once that's built.
      accountHolderUids: [uid],
      accountHolders: [{ uid, name, email: emailVal, role: 'primary' }],
    };
    await setDoc(doc(db, 'leagues', leagueId, 'registrants', uid), data, { merge: true });
    setRegistrant(data);
    setRegistrantChecked(true);
  };

  const handleParentSignup = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !password.trim()) { setFormError('Please fill in all fields.'); return; }
    if (!isValidPhoneNumber(phone)) { setFormError("That doesn't look like a valid phone number."); return; }
    if (password.length < 6) { setFormError('Password must be at least 6 characters.'); return; }
    setFormLoading(true); setFormError('');
    const trimmedEmail = email.trim().toLowerCase();
    let uid;
    try {
      const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      uid = cred.user.uid;
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('email-already-in-use')) {
        // Same parent, a second league (registrant-vs-account distinction --
        // Firebase Auth can't have two accounts for one email, so this is
        // the same uid getting a second, league-scoped registrant doc).
        try {
          const cred = await signInWithEmailAndPassword(auth, trimmedEmail, password);
          uid = cred.user.uid;
        } catch (signInErr) {
          setFormError("An account with this email already exists, but that password doesn't match it. Try signing in instead.");
          setFormLoading(false); return;
        }
      } else if (msg.includes('invalid-email')) {
        setFormError('Please enter a valid email address.');
        setFormLoading(false); return;
      } else {
        setFormError('Something went wrong creating your account. Please try again.');
        setFormLoading(false); return;
      }
    }
    try {
      await writeRegistrantDoc(uid, firstName.trim(), lastName.trim(), trimmedEmail, phone.trim());
    } catch (e) {
      setFormError('Your account was created, but we couldn\u2019t save your info. Please refresh and try again.');
    }
    setFormLoading(false);
  };

  const handleParentSignIn = async () => {
    if (!email.trim() || !password.trim()) { setFormError('Please enter your email and password.'); return; }
    setFormLoading(true); setFormError('');
    try {
      await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      // onAuthStateChanged + the registrant-lookup effect above pick up the rest.
    } catch {
      setFormError('Invalid email or password. Please try again.');
    }
    setFormLoading(false);
  };

  // Already authenticated (e.g. an existing admin/volunteer/other-league
  // session in the same browser) but no registrant doc for THIS league yet
  // -- collect name only, email is already known and locked.
  const handleCompleteProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) { setFormError('Please enter your first and last name.'); return; }
    if (!phone.trim()) { setFormError('Please enter a phone number.'); return; }
    if (!isValidPhoneNumber(phone)) { setFormError("That doesn't look like a valid phone number."); return; }
    setFormLoading(true); setFormError('');
    try {
      await writeRegistrantDoc(firebaseUser.uid, firstName.trim(), lastName.trim(), (firebaseUser.email || '').toLowerCase(), phone.trim());
    } catch (e) {
      setFormError('Something went wrong saving your info. Please try again.');
    }
    setFormLoading(false);
  };

  if (authChecking || (firebaseUser && !registrantChecked)) {
    return <ParentAuthShell leagueName={leagueName}><div style={{ textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading...</div></ParentAuthShell>;
  }

  // Signed in, registrant doc exists for this league -- hand off to either
  // the real multi-kid/division/cart flow (registration-parent-facing-v1-
  // spec.md, sections 3-4) or, for the /account URL, the read-only "My
  // Account" page (past registrations, volunteer history, contact info).
  if (firebaseUser && registrant) {
    if (view === 'account') {
      return <ParentAccountPage leagueId={leagueId} leagueName={leagueName} slug={slug} registrant={registrant} firebaseUser={firebaseUser} />;
    }
    return <ParentRegistrationFlow leagueId={leagueId} leagueName={leagueName} adminName={adminName} adminEmail={adminEmail} registrant={registrant} firebaseUser={firebaseUser} slug={slug} />;
  }

  // Signed in, but no registrant doc for this league yet.
  if (firebaseUser && registrantChecked && !registrant) {
    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Names the account holder explicitly, and says where player names go.
              This page's next step collects players, so an unqualified "your name"
              reads as ambiguous here and a parent can enter a child's name without
              noticing until later. Same reason the labels below say YOUR. */}
          <div style={{ fontSize: 13, color: C.muted }}>You're signed in as <b>{firebaseUser.email}</b>. We just need the parent or guardian's name to finish setting up your account for {leagueName}. You'll add your players in the next step.</div>
          <FieldInput label="YOUR FIRST NAME" placeholder="e.g. Jamie" value={firstName} onChange={e => setFirstName(e.target.value)} />
          <FieldInput label="YOUR LAST NAME" placeholder="e.g. Rivera" value={lastName} onChange={e => setLastName(e.target.value)} />
          <div>
            <FieldInput label="YOUR PHONE NUMBER" type="tel" placeholder="(555) 123-4567" value={phone} onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCompleteProfile()} />
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>The number you'd like a coach or league official to use if they need to reach you.</div>
          </div>
          {formError &&<div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark }}>{formError}</div>}
          <Btn onClick={handleCompleteProfile} disabled={formLoading} full>{formLoading ? 'Saving...' : 'Continue \u2192'}</Btn>
        </div>
      </ParentAuthShell>
    );
  }

  // Signed out -- Sign Up / Sign In toggle.
  return (
    <ParentAuthShell leagueName={leagueName}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: C.grayLt, borderRadius: R.control, padding: 4 }}>
        <button onClick={() => { setMode('signup'); setFormError(''); }}
          style={{ flex: 1, padding: '8px 12px', borderRadius: R.control, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: mode === 'signup' ? C.white : 'transparent', color: mode === 'signup' ? C.text : C.muted }}>
          Sign Up
        </button>
        <button onClick={() => { setMode('signin'); setFormError(''); }}
          style={{ flex: 1, padding: '8px 12px', borderRadius: R.control, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: mode === 'signin' ? C.white : 'transparent', color: mode === 'signin' ? C.text : C.muted }}>
          Sign In
        </button>
      </div>
      {mode === 'signup' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: C.muted, marginTop: -8, marginBottom: -4 }}>Create a parent account to register for {leagueName}. We just need the parent or guardian's name — you'll add your players in the next step.</div>
          <FieldInput label="YOUR FIRST NAME" placeholder="e.g. Jamie" value={firstName} onChange={e => setFirstName(e.target.value)} />
          <FieldInput label="YOUR LAST NAME" placeholder="e.g. Rivera" value={lastName} onChange={e => setLastName(e.target.value)} />
          <FieldInput label="EMAIL ADDRESS" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
          <div>
            <FieldInput label="YOUR PHONE NUMBER" type="tel" placeholder="(555) 123-4567" value={phone} onChange={e => setPhone(e.target.value)} />
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>The number you'd like a coach or league official to use if they need to reach you.</div>
          </div>
          <FieldInput label="CREATE A PASSWORD" type="password" placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleParentSignup()} />
          {formError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark }}>{formError}</div>}
          <div style={{ fontSize: 11, color: C.muted, textAlign: 'center' }}>
            By creating an account, you agree to our <a href="/terms" style={{ color: C.muted }}>Terms</a> and <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
          </div>
          <Btn onClick={handleParentSignup} disabled={formLoading} full>{formLoading ? 'Creating your account...' : 'Create Account \u2192'}</Btn>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FieldInput label="EMAIL ADDRESS" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
          <FieldInput label="PASSWORD" type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleParentSignIn()} />
          {formError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark }}>{formError}</div>}
          <Btn onClick={handleParentSignIn} disabled={formLoading} full>{formLoading ? 'Signing in...' : 'Sign In \u2192'}</Btn>
        </div>
      )}
    </ParentAuthShell>
  );
}

// Shared page chrome for ParentAuthGate's various states -- a module-level
// component (not defined inside ParentAuthGate's render) specifically so its
// identity stays stable across re-renders; a component defined inline in a
// parent's render body gets recreated every render, which would make React
// unmount/remount this whole subtree (and drop input focus) on every
// keystroke in the form fields below.
//
// ICON CONVENTION for the status screens rendered inside this shell (setup
// pending, not found, welcome back, cancelled, success): lucide-react icons at
// size 38 / strokeWidth 1.5, NOT emoji. Sep 2, 2026 -- this deliberately
// reverses the Aug 31 decision to standardise this flow on emoji. That
// decision wasn't wrong, its premise was: emoji were already here, so matching
// them was the consistent choice. Then this flow became the surface where
// parents hand over card details, and informal-by-default stopped being free.
// lucide-react already carries the app's visual language everywhere else, so
// the consistency argument now points the other way. strokeWidth is 1.5 rather
// than lucide's default 2, which reads heavy at this size and doesn't match
// the location cards. Only the success icon takes colour (C.green, the
// text-safe dark green -- never C.greenMid, per the standing contrast rule);
// the rest are C.text so nothing reads as an error when it isn't one.
// Scope is this flow only -- emoji on admin surfaces and demo blocks stays.
function ParentAuthShell({ leagueName, children, maxWidth = 400 }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.grayLt, padding: 20 }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4, textAlign: 'center' }}>{leagueName}</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: '1.5rem', textAlign: 'center' }}>Registration</div>
      <div style={{ background: C.white, borderRadius: R.card, border: '1px solid ' + C.border, padding: '1.75rem', width: '100%', maxWidth, boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}

// Sibling-discount assignment, mirrored from
// api/create-registration-checkout-session.js's discountKeyByIndex logic
// (index-in-cart -> discount tier, NOT which kid is actually younger) so the
// running total shown here matches what Stripe will actually charge to the
// cent. registration.siblingDiscount values are already stored in cents
// (see RegistrationManager's saveRegistration). Kept as a small duplicated
// helper rather than shared code -- see the "discount formula duplication"
// flag from the step-3 plan; if the server-side formula ever changes, this
// needs a matching manual update.
const SIBLING_DISCOUNT_KEYS = ['', 'second', 'third', 'fourth'];
function computeCartTotals(kids, registration) {
  const divisionPricing = (registration && registration.divisions) || {};
  const siblingDiscount = (registration && registration.siblingDiscount) || {};
  const techFeeCents = (registration && registration.techFeeCents) || 299;
  let subtotalCents = 0;
  const perKid = kids.map((kid, i) => {
    const division = divisionPricing[kid.divisionId] || {};
    const listPriceCents = Math.round((division.price || 0) * 100);
    const discountKey = i < SIBLING_DISCOUNT_KEYS.length ? SIBLING_DISCOUNT_KEYS[i] : 'subsequent';
    const discountCents = discountKey ? (siblingDiscount[discountKey] || 0) : 0;
    const netCents = Math.max(0, listPriceCents - discountCents);
    subtotalCents += netCents;
    return { listPriceCents, discountCents, netCents };
  });
  return { perKid, subtotalCents, techFeeCents, totalBeforeProcessingCents: subtotalCents + techFeeCents };
}

const GENDER_OPTIONS = [['male', 'Male'], ['female', 'Female'], ['x', 'X']];

// Sentinel id for the volunteer-interest step's "Not at this time" card --
// see ParentRegistrationFlow's toggleRole/finishVolunteerStep.
const NOT_VOLUNTEERING_ID = '__not_volunteering__';

// Multi-kid registration flow for an authenticated registrant
// (registration-parent-facing-v1-spec.md, sections 3-4): resolves the
// league's currently open registration(s), collects one or more players
// (name/DOB/gender/division), shows a running cart with the sibling
// discount applied (see computeCartTotals above), then hands off to the
// existing create-registration-checkout-session endpoint.
function ParentRegistrationFlow({ leagueId, leagueName, adminName, adminEmail, registrant, firebaseUser, slug }) {
  const [step, setStep] = React.useState('resolving'); // resolving | none | choose-season | kid-count | kid-details | cart | waivers | volunteer | success | cancelled
  const [resolveError, setResolveError] = React.useState('');
  const [openRegistrations, setOpenRegistrations] = React.useState([]);
  const [selectedReg, setSelectedReg] = React.useState(null);
  const [divisions, setDivisions] = React.useState([]);

  const [kidCountInput, setKidCountInput] = React.useState('1');
  const [kids, setKids] = React.useState([]);
  const [kidIndex, setKidIndex] = React.useState(0);
  const [kidError, setKidError] = React.useState('');

  // Athletes this parent has registered before, for the reuse picker on the
  // kid-details step. Without this the client never sent a playerId, so
  // registering the same kid in a second season created a duplicate
  // registrants/{uid}/players doc and My Account showed two athletes -- the
  // reuse seam already existed server-side in
  // create-registration-checkout-session.js, it was just never exercised.
  // Best-effort: on a read failure this stays empty and the flow falls back to
  // the plain blank form rather than blocking registration entirely.
  const [savedPlayers, setSavedPlayers] = React.useState([]);

  // Selected volunteer-role ids for the card-select step below the cart --
  // non-committing, per spec section 5 (interest only, no members/{uid}
  // assignment). Written to the leagues/{lid}/volunteerRequests collection,
  // which already existed in firestore.rules with exactly this permission
  // shape but had zero code referencing it until this build.
  const [selectedRoleIds, setSelectedRoleIds] = React.useState([]);

  // Waiver step (blocking, unlike volunteer interest) -- registrationWaiverDocs.md/
  // registration-parent-facing-v1-spec.md section 6. selectedReg.requiredWaiverIds
  // (admin-picked, see RegistrationManager) points at leagues/{lid}/waivers docs;
  // fetched fresh here rather than trusted from any cached list, since a parent
  // could be mid-session while an admin edits the required set. waiverSignState is
  // keyed by waiverId: { checked, name }.
  const [requiredWaivers, setRequiredWaivers] = React.useState([]);
  const [waiverLoading, setWaiverLoading] = React.useState(false);
  const [waiverSignState, setWaiverSignState] = React.useState({});
  const [waiverSubmitting, setWaiverSubmitting] = React.useState(false);
  const [waiverError, setWaiverError] = React.useState('');

  const [checkoutLoading, setCheckoutLoading] = React.useState(false);
  const [checkoutError, setCheckoutError] = React.useState('');

  // Safari bfcache -- same defect and same reasoning as the connectLoading effect in
  // RegistrationManager. handleCheckout keeps checkoutLoading true through
  // window.location.href = result.url (double-submit protection, left as-is); Back from
  // Stripe Checkout restores this page with the flag still true and the pay button dead.
  // e.persisted only, this one flag only -- not a blanket reset.
  React.useEffect(() => {
    const onPageShow = e => { if (e.persisted) setCheckoutLoading(false); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Populated only on the post-Stripe-redirect success screen -- see the
  // ?registration=success handling below. Left null if the order fetch
  // fails or no orderId was present, in which case the success screen
  // falls back to a plain static confirmation rather than an error.
  const [orderDetails, setOrderDetails] = React.useState(null);
  // Also success-screen-only. Stripe's redirect is a real page navigation
  // (not client-side routing), so the in-memory selectedRoleIds from the
  // volunteer step above is gone by the time this mounts fresh -- this is
  // fetched from the volunteerRequests doc written just before checkout,
  // not carried over in memory.
  const [volunteerInterestNames, setVolunteerInterestNames] = React.useState([]);
  // Also success-screen-only, same best-effort fetch pattern as
  // volunteerInterestNames above -- the registration's admin-set
  // customMessage, fetched by registrationId off the order once it's
  // loaded. Empty string falls back to the default static text below.
  const [regMessage, setRegMessage] = React.useState('');

  const loadDivisionsFor = async (registration) => {
    const snap = await getDocs(query(collection(db, 'leagues', leagueId, 'divisions'), where('seasonId', '==', registration.seasonId)));
    setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const resolveOpenRegistrations = async () => {
    setStep('resolving');
    try {
      const snap = await getDocs(query(collection(db, 'leagues', leagueId, 'registrations'), where('status', '==', 'open')));
      const regs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (regs.length === 0) { setStep('none'); return; }
      if (regs.length === 1) {
        setSelectedReg(regs[0]);
        await loadDivisionsFor(regs[0]);
        setStep('kid-count');
        return;
      }
      setOpenRegistrations(regs);
      setStep('choose-season');
    } catch (e) {
      setResolveError('Something went wrong loading registration for this league. Please try again in a moment.');
      setStep('none');
    }
  };

  // Landing back from Stripe Checkout (success_url/cancel_url, both built in
  // create-registration-checkout-session.js as
  // /register/{slug}?registration=success|cancelled&order={orderId}).
  // Checked first, before the normal open-registration resolution, and the
  // query string is stripped immediately via replaceState so a later manual
  // refresh of this same URL doesn't keep re-showing a stale success/cancel
  // screen instead of letting the parent start a fresh registration.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const registrationParam = params.get('registration');
    const orderId = params.get('order');

    if (registrationParam !== 'success' && registrationParam !== 'cancelled') {
      resolveOpenRegistrations();
      return;
    }

    window.history.replaceState({}, '', window.location.pathname);

    if (registrationParam === 'cancelled') {
      setStep('cancelled');
      return;
    }

    let cancelled = false;
    (async () => {
      if (orderId) {
        try {
          const snap = await getDoc(doc(db, 'leagues', leagueId, 'registrants', firebaseUser.uid, 'orders', orderId));
          if (!cancelled && snap.exists()) {
            const orderData = { id: snap.id, ...snap.data() };
            setOrderDetails(orderData);
            // Best-effort -- if this fails or the registration has no
            // customMessage set, the success screen just falls back to the
            // default static text below. Same "don't block the main
            // confirmation" approach as the volunteer-picks fetch below.
            if (orderData.registrationId) {
              try {
                const regSnap = await getDoc(doc(db, 'leagues', leagueId, 'registrations', orderData.registrationId));
                if (!cancelled && regSnap.exists() && regSnap.data().customMessage) {
                  setRegMessage(regSnap.data().customMessage);
                }
              } catch (e) {
                // Non-critical -- see comment above.
              }
            }
            // Best-effort -- if this fails or finds nothing, the success
            // screen just doesn't show the volunteer-picks block, same
            // "don't block the main confirmation" approach as the order
            // fetch above. No orderId is stored on the volunteerRequests doc
            // (it's written before checkout creates the order), so this
            // matches on registrant+registration and takes the most recent
            // one -- good enough for v1 since a parent registering the same
            // open registration twice in one session is an edge case, not
            // the common path.
            try {
              const vrSnap = await getDocs(query(
                collection(db, 'leagues', leagueId, 'volunteerRequests'),
                where('registrantUid', '==', firebaseUser.uid),
                where('registrationId', '==', orderData.registrationId)
              ));
              if (!cancelled && !vrSnap.empty) {
                const mostRecent = vrSnap.docs.map(d => d.data()).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
                setVolunteerInterestNames(mostRecent.roleNames || []);
              }
            } catch (e) {
              // Non-critical -- see comment above.
            }
          }
        } catch (e) {
          // Payment already succeeded (Stripe redirected here) -- fall back
          // to the plain static confirmation below rather than surfacing an
          // error for what isn't really a failure from the parent's side.
        }
      }
      if (!cancelled) setStep('success');
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  // Separate from the registration-resolution effect above so a slow or failed
  // players read can never delay the main flow. Permitted client-side by the
  // existing registrants/{uid}/players read rule (the operand that matches here
  // is the path wildcard request.auth.uid == registrantId, which a list query
  // can evaluate) -- no rules change and no index needed.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'leagues', leagueId, 'registrants', firebaseUser.uid, 'players'));
        if (cancelled) return;
        setSavedPlayers(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => ((a.firstName || '') + (a.lastName || '')).localeCompare((b.firstName || '') + (b.lastName || '')))
        );
      } catch (e) {
        // Non-critical -- see comment on savedPlayers above.
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId, firebaseUser.uid]);

  const chooseSeason = async (reg) => {
    setSelectedReg(reg);
    await loadDivisionsFor(reg);
    setStep('kid-count');
  };

  const activeDivisionOptions = React.useMemo(() => {
    if (!selectedReg) return [];
    const pricing = selectedReg.divisions || {};
    return divisions
      .filter(d => pricing[d.id] && pricing[d.id].active !== false)
      .map(d => ({ id: d.id, name: d.name, price: pricing[d.id].price || 0 }));
  }, [selectedReg, divisions]);

  // playerId is null for a new athlete and set to an existing
  // registrants/{uid}/players doc id when the parent reuses one -- it's passed
  // straight through to the checkout endpoint, which decides new-vs-reuse off
  // it. pickerDone is local UI state only (never sent anywhere): it records
  // that this slot has been resolved to either a saved athlete or a new one, so
  // the picker doesn't re-show over an already-filled form.
  const emptyKid = () => ({ playerId: null, firstName: '', lastName: '', dob: '', gender: '', divisionId: '', pickerDone: false });

  const startKidDetails = () => {
    const n = Math.max(1, Math.min(10, parseInt(kidCountInput, 10) || 1));
    setKids(Array.from({ length: n }, emptyKid));
    setKidIndex(0);
    setKidError('');
    setStep('kid-details');
  };

  const updateKid = (patch) => {
    setKids(prev => prev.map((k, i) => i === kidIndex ? { ...k, ...patch } : k));
  };

  // Saved athletes still selectable for the current slot. An athlete already
  // picked in another slot of this same cart is hidden: two slots sharing one
  // playerId would write two order lines pointing at one player doc, and the
  // second batch.set would clobber the first's division.
  const availableSavedPlayers = React.useMemo(
    () => savedPlayers.filter(p => !kids.some((k, i) => i !== kidIndex && k.playerId === p.id)),
    [savedPlayers, kids, kidIndex]
  );

  const selectSavedPlayer = (p) => {
    // Name/gender prefill and stay editable. Division is deliberately NOT
    // prefilled -- kids age up, and last season's division is frequently the
    // wrong answer this season.
    setKidError('');
    updateKid({
      playerId: p.id,
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      dob: p.dob || '',
      gender: p.gender || '',
      divisionId: '',
      pickerDone: true,
    });
  };

  const selectNewPlayer = () => {
    setKidError('');
    updateKid({ ...emptyKid(), pickerDone: true });
  };

  // Guards against the picker reappearing over a slot the parent has already
  // typed into. Reachable: with one saved athlete and two slots, picking them in
  // slot 1 hides them from slot 2's picker (so slot 2 shows the blank form
  // directly, leaving pickerDone false) -- but going back and switching slot 1
  // to a new athlete frees them again, and slot 2 would otherwise re-show a
  // picker over the form, where "Add a new athlete" would wipe the entry.
  const kidIsBlank = (k) => !k.firstName && !k.lastName && !k.dob && !k.divisionId;

  const validateCurrentKid = () => {
    const k = kids[kidIndex];
    if (!k.firstName.trim() || !k.lastName.trim()) return 'Please enter this player\u2019s first and last name.';
    if (!k.dob) return 'Please enter this player\u2019s date of birth.';
    if (!k.divisionId) return 'Please select a division.';
    return '';
  };

  const goToNextKid = () => {
    const err = validateCurrentKid();
    if (err) { setKidError(err); return; }
    setKidError('');
    if (kidIndex < kids.length - 1) {
      setKidIndex(kidIndex + 1);
    } else {
      setStep('cart');
    }
  };

  const goToPrevKid = () => {
    setKidError('');
    // Back out of a filled form to the picker first (so a mis-picked athlete is
    // correctable), and only then to the previous slot / kid count.
    if (kids[kidIndex] && kids[kidIndex].pickerDone) {
      updateKid({ pickerDone: false });
      return;
    }
    if (kidIndex > 0) { setKidIndex(kidIndex - 1); } else { setStep('kid-count'); }
  };

  const editKidFromCart = (i) => {
    setKidIndex(i);
    setKidError('');
    setStep('kid-details');
  };

  const totals = React.useMemo(() => computeCartTotals(kids, selectedReg), [kids, selectedReg]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const idToken = await firebaseUser.getIdToken();
      const cart = kids.map(k => {
        const division = activeDivisionOptions.find(d => d.id === k.divisionId);
        return {
          // Omitted (not null) for a new athlete -- the endpoint branches on
          // truthiness to decide reuse vs. a fresh player doc. Any edits the
          // parent made above ride along in firstName/lastName/gender and are
          // merged onto the existing doc server-side; the order's own cart
          // snapshot keeps its denormalized copy, so a later name edit never
          // rewrites what a past receipt says.
          ...(k.playerId ? { playerId: k.playerId } : {}),
          firstName: k.firstName.trim(), lastName: k.lastName.trim(),
          playerDOB: k.dob, gender: k.gender,
          divisionId: k.divisionId, divisionName: division ? division.name : '',
        };
      });
      const response = await fetch('/api/create-registration-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leagueId, registrationId: selectedReg.id, idToken, cart }),
      });
      const result = await response.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        setCheckoutError(result.error || "We couldn't start checkout right now. Please try again.");
        setCheckoutLoading(false);
      }
    } catch (e) {
      setCheckoutError("We couldn't start checkout right now. Please check your connection and try again.");
      setCheckoutLoading(false);
    }
  };

  // Sentinel id for the "Not at this time" card -- mutually exclusive with
  // real role selections (picking it clears any selected roles, and picking
  // a role clears it), so a parent can't submit both an explicit decline and
  // an interest pick at once. Distinct from clicking "Skip": this writes a
  // status: 'declined' volunteerRequests doc so the admin can tell "asked
  // and said no" apart from "never answered," which Skip alone can't convey.
  const toggleRole = (roleId) => {
    if (roleId === NOT_VOLUNTEERING_ID) {
      setSelectedRoleIds(prev => prev.includes(NOT_VOLUNTEERING_ID) ? [] : [NOT_VOLUNTEERING_ID]);
      return;
    }
    setSelectedRoleIds(prev => {
      const withoutDecline = prev.filter(id => id !== NOT_VOLUNTEERING_ID);
      return withoutDecline.includes(roleId) ? withoutDecline.filter(id => id !== roleId) : [...withoutDecline, roleId];
    });
  };

  // Second half of proceedFromCart below -- also called after the waiver
  // step finishes. Skips the volunteer card-select step entirely if this
  // registration has no enabled roles (either the admin never set any up,
  // or turned them all off), so a registration created before this feature
  // shipped behaves exactly as it did before.
  const proceedPastWaivers = () => {
    const enabledRoles = (selectedReg.volunteerRoles || []).filter(r => r.enabled);
    if (enabledRoles.length === 0) { handleCheckout(); return; }
    setStep('volunteer');
  };

  // From the cart's "Proceed to payment" button. Waivers are a hard
  // requirement (unlike volunteer interest), so they're checked first --
  // fetched fresh from Firestore rather than trusted from anywhere cached,
  // since selectedReg was loaded once at the start of this session and an
  // admin could have changed the required set since. Skips straight to
  // proceedPastWaivers if this registration has no required waivers.
  const proceedFromCart = async () => {
    const waiverIds = selectedReg.requiredWaiverIds || [];
    if (waiverIds.length === 0) { proceedPastWaivers(); return; }
    setWaiverError('');
    setWaiverLoading(true);
    try {
      const docs = await Promise.all(waiverIds.map(id => getDoc(doc(db, 'leagues', leagueId, 'waivers', id))));
      setRequiredWaivers(docs.filter(d => d.exists()).map(d => ({ id: d.id, ...d.data() })));
      setWaiverSignState({});
      setStep('waivers');
    } catch (e) {
      setWaiverError("Couldn't load this registration's waiver(s). Please try again.");
    }
    setWaiverLoading(false);
  };

  const updateWaiverSignState = (waiverId, patch) => {
    setWaiverSignState(prev => ({ ...prev, [waiverId]: { ...prev[waiverId], ...patch } }));
  };

  // Unlike volunteer interest, this is a hard requirement -- a signature
  // write failure here keeps the parent on this step (with an error) rather
  // than silently proceeding to checkout, since a missing signature record
  // is a real problem for a required waiver, not a "best effort" one.
  const finishWaiverStep = async () => {
    setWaiverError('');
    const toSign = requiredWaivers.filter(w => w.requiresSignature !== false);
    const allSigned = toSign.every(w => waiverSignState[w.id] && waiverSignState[w.id].checked && (waiverSignState[w.id].name || '').trim());
    if (!allSigned) { setWaiverError('Please review and sign every required waiver before continuing.'); return; }
    setWaiverSubmitting(true);
    try {
      const playerNames = kids.map(k => (k.firstName + ' ' + k.lastName).trim());
      await Promise.all(toSign.map(w => addDoc(collection(db, 'leagues', leagueId, 'waiverSignatures'), {
        waiverId: w.id,
        waiverName: w.name,
        signedByName: waiverSignState[w.id].name.trim(),
        // See the note on the volunteer-side write: uid is the identity key
        // the read rule uses, email is display only. firebaseUser is the
        // Firebase Auth user object itself, so uid is guaranteed here.
        signedByUid: firebaseUser.uid,
        signedByEmail: firebaseUser.email || null,
        signedByRole: 'registrant',
        registrationId: selectedReg.id,
        playerNames,
        signedAt: new Date().toISOString(),
      })));
    } catch (e) {
      setWaiverError("Couldn't save your signature. Please try again.");
      setWaiverSubmitting(false);
      return;
    }
    setWaiverSubmitting(false);
    proceedPastWaivers();
  };

  // Interest is non-committing and explicitly not gated on payment (spec
  // section 5), so this write happens here -- before checkout redirects to
  // Stripe -- regardless of whether the payment itself ends up succeeding.
  const finishVolunteerStep = async (skipped) => {
    if (!skipped && selectedRoleIds.length > 0) {
      // "Not at this time" is an explicit decline, not a role pick -- written
      // with its own status so the admin can tell "asked and said no" apart
      // from "never answered" (a plain Skip, which writes nothing at all).
      const declinedNow = selectedRoleIds.includes(NOT_VOLUNTEERING_ID);
      const enabledRoles = (selectedReg.volunteerRoles || []).filter(r => r.enabled);
      const roleNames = declinedNow ? [] : enabledRoles.filter(r => selectedRoleIds.includes(r.id)).map(r => r.name);
      try {
        await addDoc(collection(db, 'leagues', leagueId, 'volunteerRequests'), {
          registrantUid: firebaseUser.uid,
          registrantName: (registrant && (registrant.firstName + ' ' + registrant.lastName).trim()) || firebaseUser.email || '',
          registrationId: selectedReg.id,
          roleIds: declinedNow ? [] : selectedRoleIds,
          roleNames,
          status: declinedNow ? 'declined' : 'interested',
          createdAt: new Date().toISOString(),
        });
      } catch (e) {
        // Non-critical, secondary write -- same "don't block the primary
        // flow" pattern already used elsewhere in this app (e.g. SMS save
        // handlers). A parent who expressed interest shouldn't be blocked
        // from completing checkout by a transient write failure here; worst
        // case their interest just isn't recorded and they can mention it
        // to the admin directly.
      }
    }
    handleCheckout();
  };

  if (step === 'resolving') {
    return <ParentAuthShell leagueName={leagueName}><div style={{ textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading...</div></ParentAuthShell>;
  }

  if (step === 'success') {
    const cart = (orderDetails && orderDetails.cart) || null;
    const pending = orderDetails && orderDetails.status === 'pending';
    return (
      <ParentAuthShell leagueName={leagueName} maxWidth={460}>
        <div style={{ textAlign: 'center', marginBottom: cart ? 18 : 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><CircleCheckIcon size={38} strokeWidth={1.5} color={C.green} /></div>
          <div style={{ fontWeight: 800, fontSize: 17, color: C.text, marginBottom: 6 }}>You're registered!</div>
          <div style={{ fontSize: 13, color: C.muted }}>
            {regMessage || (cart ? 'Thanks for registering with ' + leagueName + '. Here\u2019s a summary of your order:' : 'Thanks for registering with ' + leagueName + '. We\u2019ll be in touch.')}
          </div>
        </div>
        {cart && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {cart.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid ' + C.border, borderRadius: R.control, padding: '10px 12px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{item.playerName}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{item.divisionName}</div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>${((item.netCents || 0) / 100).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, color: C.text, borderTop: '1px solid ' + C.border, paddingTop: 10 }}>
              <span>Total paid</span><span>${((orderDetails.totalCents || 0) / 100).toFixed(2)}</span>
            </div>
            {pending && (
              <div style={{ marginTop: 12, background: C.amberLt, border: '1px solid ' + C.amberBd, borderRadius: R.control, padding: '10px 12px', fontSize: 12, color: C.amber }}>
                Finalizing your payment -- this page will update shortly. Feel free to refresh in a moment.
              </div>
            )}
          </>
        )}
        {volunteerInterestNames.length > 0 && (
          <div style={{ marginTop: cart ? 14 : 18, background: C.greenLt, border: '1px solid ' + C.greenBd, borderRadius: R.control, padding: '10px 12px', fontSize: 12, color: C.green }}>
            You expressed interest in volunteering as: {volunteerInterestNames.join(', ')}. {leagueName} will follow up with next steps.
          </div>
        )}
        {adminEmail && (
          <div style={{ marginTop: 16, borderTop: '1px solid ' + C.border, paddingTop: 14, fontSize: 12, color: C.muted, textAlign: 'center' }}>
            Want to volunteer later? Contact {adminName || 'your league admin'} at{' '}
            <a href={'mailto:' + adminEmail} style={{ color: C.green, fontWeight: 700 }}>{adminEmail}</a>.
          </div>
        )}
        {slug && (
          <div style={{ marginTop: 14 }}>
            <a href={'/register/' + slug + '/account'} style={{ display: 'block', textAlign: 'center', padding: '9px 16px', borderRadius: R.control, background: C.greenLt, color: C.text, border: '1px solid ' + C.greenBd, fontSize: 14, fontWeight: 700, textDecoration: 'none', boxSizing: 'border-box' }}>View my account</a>
          </div>
        )}
      </ParentAuthShell>
    );
  }

  if (step === 'cancelled') {
    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><ShoppingCartIcon size={38} strokeWidth={1.5} color={C.text} /></div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>Checkout was cancelled</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>You weren't charged. You can pick up where you left off whenever you're ready.</div>
          <Btn onClick={resolveOpenRegistrations} full>Back to registration</Btn>
        </div>
      </ParentAuthShell>
    );
  }

  if (step === 'none') {
    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><UserCheckIcon size={38} strokeWidth={1.5} color={C.text} /></div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>Welcome, {registrant.firstName}!</div>
          <div style={{ fontSize: 13, color: C.muted }}>{resolveError || ('There\u2019s no registration currently open for ' + leagueName + '. Check back later or contact your league admin.')}</div>
          {slug && (
            <div style={{ marginTop: 14 }}>
              <a href={'/register/' + slug + '/account'} style={{ fontSize: 12, color: C.muted, textDecoration: 'underline' }}>View my account</a>
            </div>
          )}
        </div>
      </ParentAuthShell>
    );
  }

  if (step === 'choose-season') {
    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 12 }}>{leagueName} has more than one registration open. Which one?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {openRegistrations.map(reg => (
            <button key={reg.id} onClick={() => chooseSeason(reg)}
              style={{ textAlign: 'left', padding: '12px 14px', borderRadius: R.control, border: '1.5px solid ' + C.border, background: C.white, cursor: 'pointer', fontFamily: 'inherit' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{reg.seasonName || 'Registration'}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{reg.opensAt} – {reg.closesAt}</div>
            </button>
          ))}
        </div>
      </ParentAuthShell>
    );
  }

  if (step === 'kid-count') {
    const n = Math.max(1, Math.min(10, parseInt(kidCountInput, 10) || 1));
    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>How many kids are you registering today?</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{selectedReg.seasonName || 'Registration'}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginBottom: 20 }}>
          <button onClick={() => setKidCountInput(String(Math.max(1, n - 1)))}
            style={{ width: 40, height: 40, borderRadius: R.control, border: '1.5px solid ' + C.border, background: C.white, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>−</button>
          <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: C.text, minWidth: 40, textAlign: 'center' }}>{n}</div>
          <button onClick={() => setKidCountInput(String(Math.min(10, n + 1)))}
            style={{ width: 40, height: 40, borderRadius: R.control, border: '1.5px solid ' + C.border, background: C.white, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+</button>
        </div>
        <Btn onClick={startKidDetails} full>{'Continue \u2192'}</Btn>
      </ParentAuthShell>
    );
  }

  if (step === 'kid-details') {
    const k = kids[kidIndex];

    // Picker, shown only when there's actually something to pick -- a
    // first-time parent (or one whose saved athletes are all already in this
    // cart) goes straight to the blank form with no empty-list UI.
    if (!k.pickerDone && kidIsBlank(k) && availableSavedPlayers.length > 0) {
      return (
        <ParentAuthShell leagueName={leagueName}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>PLAYER {kidIndex + 1} OF {kids.length}</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>Register an athlete you've added before</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>You'll pick a division and confirm their details on the next screen.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {availableSavedPlayers.map(p => (
              <button key={p.id} type="button" onClick={() => selectSavedPlayer(p)}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: R.control, border: '1.5px solid ' + C.border, background: C.white, cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{((p.firstName || '') + ' ' + (p.lastName || '')).trim() || p.name || 'Athlete'}</div>
                {/* DOB is the disambiguator between same-named athletes, so it's
                    shown on the card as well as the review screen. Parsed at noon
                    -- these are date-only 'YYYY-MM-DD' strings, and bare Date()
                    parsing treats them as UTC midnight, which renders a day early
                    in US timezones. */}
                {p.dob && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Born {new Date(p.dob + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
              </button>
            ))}
            <button type="button" onClick={selectNewPlayer}
              style={{ textAlign: 'left', padding: '12px 14px', borderRadius: R.control, border: '1.5px dashed ' + C.border, background: C.white, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, color: C.text }}>
              + Add a new athlete
            </button>
          </div>
          {/* goToPrevKid, not a duplicate of its body: pickerDone is false on
              this screen, so its back-to-picker branch doesn't fire and it
              falls through to exactly the previous-slot / kid-count logic. */}
          <div style={{ marginTop: 14 }}>
            <Btn onClick={goToPrevKid} variant="secondary">{'\u2190 Back'}</Btn>
          </div>
        </ParentAuthShell>
      );
    }

    return (
      <ParentAuthShell leagueName={leagueName}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>PLAYER {kidIndex + 1} OF {kids.length}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FieldInput label="FIRST NAME" placeholder="e.g. Jamie" value={k.firstName} onChange={e => updateKid({ firstName: e.target.value })} />
          <FieldInput label="LAST NAME" placeholder="e.g. Rivera" value={k.lastName} onChange={e => updateKid({ lastName: e.target.value })} />
          {/* Locked only when there's actually a DOB to show. A saved athlete
              with no dob (not producible by the checkout endpoint, which
              requires one, but possible via console-entered data) would
              otherwise hit a dead end: validation demands a DOB the locked
              field gives no way to supply. */}
          {k.playerId && k.dob ? (
            // Locked for a reused athlete: DOB is what the parent uses to
            // confirm they picked the right kid, not something to edit here. A
            // real correction is a rarer, riskier change (it can move a kid
            // between divisions and invalidate past registrations), so it stays
            // out of the happy path rather than sitting one typo away.
            // Rendered as static text, not a disabled date input, which reads
            // as broken rather than intentional.
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>DATE OF BIRTH</label>
              <div style={{ padding: '10px 13px', borderRadius: R.card, border: '1.5px solid ' + C.border, background: C.surface, fontSize: 14, color: C.text }}>
                {new Date(k.dob + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          ) : (
            <FieldInput label="DATE OF BIRTH" type="date" value={k.dob} onChange={e => updateKid({ dob: e.target.value })} />
          )}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>GENDER</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {GENDER_OPTIONS.map(([value, label]) => (
                <button key={value} type="button" onClick={() => updateKid({ gender: value })}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: R.control, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                    background: k.gender === value ? C.greenMid : C.white, color: C.text, border: '1.5px solid ' + (k.gender === value ? C.text : C.border) }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>DIVISION</label>
            <select value={k.divisionId} onChange={e => updateKid({ divisionId: e.target.value })}
              style={{ width: '100%', padding: '10px 13px', borderRadius: R.control, border: '1.5px solid ' + C.border, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: C.white }}>
              <option value="">Select a division…</option>
              {activeDivisionOptions.map(d => (
                <option key={d.id} value={d.id}>{d.name} — ${Number(d.price).toFixed(2)}</option>
              ))}
            </select>
          </div>
          {kidError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark }}>{kidError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={goToPrevKid} variant="secondary">{'\u2190 Back'}</Btn>
            <div style={{ flex: 1 }}><Btn onClick={goToNextKid} full>{kidIndex < kids.length - 1 ? 'Next player \u2192' : 'Review cart \u2192'}</Btn></div>
          </div>
        </div>
      </ParentAuthShell>
    );
  }

  if (step === 'cart') {
  return (
    <ParentAuthShell leagueName={leagueName} maxWidth={460}>
      <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 14 }}>Review & pay</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {kids.map((k, i) => {
          const division = activeDivisionOptions.find(d => d.id === k.divisionId);
          const t = totals.perKid[i] || { listPriceCents: 0, discountCents: 0, netCents: 0 };
          return (
            <div key={i} style={{ border: '1px solid ' + C.border, borderRadius: R.control, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{k.firstName} {k.lastName}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{division ? division.name : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {t.discountCents > 0 && <div style={{ fontSize: 11, color: C.muted, textDecoration: 'line-through' }}>${(t.listPriceCents / 100).toFixed(2)}</div>}
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>${(t.netCents / 100).toFixed(2)}</div>
                </div>
              </div>
              <button onClick={() => editKidFromCart(i)}
                style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, color: C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
                Edit
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ borderTop: '1px solid ' + C.border, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text }}>
          <span>Subtotal</span><span>${(totals.subtotalCents / 100).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text }}>
          <span>GroundsPal technology fee</span><span>${(totals.techFeeCents / 100).toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, color: C.text, marginTop: 4 }}>
          <span>Total</span><span>${(totals.totalBeforeProcessingCents / 100).toFixed(2)}</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Plus a small card processing fee, calculated at checkout.</div>
      </div>
      {(checkoutError || waiverError) && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark, marginTop: 12 }}>{checkoutError || waiverError}</div>}
      <div style={{ marginTop: 16 }}>
        <Btn onClick={proceedFromCart} disabled={checkoutLoading || waiverLoading} full>{waiverLoading ? 'Loading...' : checkoutLoading ? 'Starting checkout...' : 'Proceed to payment \u2192'}</Btn>
      </div>
    </ParentAuthShell>
  );
  }

  if (step === 'waivers') {
  return (
    <ParentAuthShell leagueName={leagueName} maxWidth={460}>
      <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>Please review & sign</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        {leagueName} requires the following before checkout can continue.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
        {requiredWaivers.map(w => {
          const blocking = w.requiresSignature !== false;
          const s = waiverSignState[w.id] || { checked: false, name: '' };
          return (
            <div key={w.id} style={{ border: '1px solid ' + C.border, borderRadius: R.control, padding: '12px 14px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 8 }}>{w.name}</div>
              {w.contentType === 'text' ? (
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', background: C.grayLt, borderRadius: R.control, padding: 10, marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
                  {w.text}
                </div>
              ) : (
                <a href={w.fileUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.blueLt, border: '1px solid ' + C.blueBd, borderRadius: R.control, padding: '8px 14px', fontSize: 12, fontWeight: 700, color: C.blue, textDecoration: 'none', marginBottom: 10 }}>
                  ⬇ View / Download {w.fileName || 'document'}
                </a>
              )}
              {blocking && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={s.checked} onChange={e => updateWaiverSignState(w.id, { checked: e.target.checked })} />
                    <span style={{ fontSize: 12, color: C.text }}>I have read and agree to the above.</span>
                  </label>
                  <input placeholder="Type your full legal name to sign" value={s.name} onChange={e => updateWaiverSignState(w.id, { name: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 13, fontFamily: 'inherit' }} />
                </>
              )}
            </div>
          );
        })}
      </div>
      {waiverError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark, marginBottom: 12 }}>{waiverError}</div>}
      <Btn onClick={finishWaiverStep} disabled={waiverSubmitting} full>{waiverSubmitting ? 'Saving...' : 'Continue \u2192'}</Btn>
    </ParentAuthShell>
  );
  }

  // step === 'volunteer' -- only reached when this registration has at
  // least one enabled role (see proceedPastWaivers above); skipped entirely
  // otherwise, including for registrations saved before this feature shipped.
  const enabledRoles = (selectedReg.volunteerRoles || []).filter(r => r.enabled);
  return (
    <ParentAuthShell leagueName={leagueName} maxWidth={460}>
      <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>Want to help out?</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        Volunteer — {kids.length > 1 ? 'your players are' : (kids[0] ? kids[0].firstName + ' is' : 'your player is')} registered here. Selecting a role just registers your interest; {leagueName} will follow up. This won't affect your registration or checkout.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {enabledRoles.map(role => {
          const selected = selectedRoleIds.includes(role.id);
          return (
            <button key={role.id} type="button" onClick={() => toggleRole(role.id)}
              style={{ textAlign: 'left', padding: '12px 14px', borderRadius: R.control, cursor: 'pointer', fontFamily: 'inherit',
                background: selected ? C.greenLt : C.white, border: '1.5px solid ' + (selected ? C.green : C.border),
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{role.name}</span>
              <span style={{ fontSize: 16, color: selected ? C.green : C.border }}>{selected ? '\u2713' : ''}</span>
            </button>
          );
        })}
        {(() => {
          const declined = selectedRoleIds.includes(NOT_VOLUNTEERING_ID);
          return (
            <button type="button" onClick={() => toggleRole(NOT_VOLUNTEERING_ID)}
              style={{ textAlign: 'left', padding: '12px 14px', borderRadius: R.control, cursor: 'pointer', fontFamily: 'inherit',
                background: declined ? C.grayLt : C.white, border: '1.5px solid ' + (declined ? C.text : C.border),
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>Not at this time</span>
              <span style={{ fontSize: 16, color: declined ? C.text : C.border }}>{declined ? '\u2713' : ''}</span>
            </button>
          );
        })()}
      </div>
      {checkoutError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark, marginBottom: 12 }}>{checkoutError}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn onClick={() => finishVolunteerStep(true)} variant="secondary" disabled={checkoutLoading}>Skip</Btn>
        <div style={{ flex: 1 }}><Btn onClick={() => finishVolunteerStep(false)} disabled={checkoutLoading} full>{checkoutLoading ? 'Starting checkout...' : 'Continue to payment \u2192'}</Btn></div>
      </div>
    </ParentAuthShell>
  );
}

// Read-only "My Account" page for a signed-in registrant, at
// /register/{slug}/account -- past registrations grouped per athlete, past
// volunteer-interest picks, and an editable contact-info section (phone,
// address, secondary/co-parent contact -- the fields explicitly deferred
// from account creation in registration-parent-facing-v1-spec.md, section
// 2). Deliberately league-scoped, matching how registrants themselves are
// league-scoped (one registrant doc per league, not a cross-league parent
// identity) -- a true cross-league "see all my kids everywhere" portal is
// a separate, bigger future piece, not this build.
// Statuses a parent can see against ONE LINE of an order on My Account -- not
// order statuses. 'abandoned' is filtered out before render (see the orders
// fetch below), and 'partially_refunded' never reaches here at all: it is an
// order-level fact, resolved per player into 'refunded' or 'paid' when the
// groups are built, because a partially refunded order has both kinds of line
// on it. The lookup keeps its neutral fallback so any future status still
// renders a state rather than leaving a date and an amount unexplained.
const ORDER_STATUS_BADGE = {
  pending: { label: 'Pending', bg: C.amberLt, color: C.amber },
  paid: { label: 'Paid', bg: C.greenLt, color: C.green },
  refunded: { label: 'Refunded', bg: C.redLt, color: C.redDark },
};

function ParentAccountPage({ leagueId, leagueName, slug, registrant, firebaseUser }) {
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [athleteGroups, setAthleteGroups] = React.useState([]); // [{ playerId, playerName, orders: [...] }]
  const [volunteerHistory, setVolunteerHistory] = React.useState([]);
  const [registrationNames, setRegistrationNames] = React.useState({}); // { [registrationId]: seasonName }

  const [phone, setPhone] = React.useState(registrant.phone || '');
  const [street, setStreet] = React.useState((registrant.address && registrant.address.street) || '');
  const [city, setCity] = React.useState((registrant.address && registrant.address.city) || '');
  const [state, setState] = React.useState((registrant.address && registrant.address.state) || '');
  const [zip, setZip] = React.useState((registrant.address && registrant.address.zip) || '');
  const [secondaryName, setSecondaryName] = React.useState(registrant.secondaryContactName || '');
  const [secondaryPhone, setSecondaryPhone] = React.useState(registrant.secondaryContactPhone || '');
  const [secondaryEmail, setSecondaryEmail] = React.useState(registrant.secondaryContactEmail || '');
  const [contactSaving, setContactSaving] = React.useState(false);
  const [contactError, setContactError] = React.useState('');
  const [contactSaved, setContactSaved] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ordersSnap, vrSnap] = await Promise.all([
          getDocs(query(collection(db, 'leagues', leagueId, 'registrants', firebaseUser.uid, 'orders'), orderBy('createdAt', 'desc'))),
          getDocs(query(collection(db, 'leagues', leagueId, 'volunteerRequests'), where('registrantUid', '==', firebaseUser.uid))),
        ]);
        if (cancelled) return;

        // Abandoned orders are hidden from the parent, never deleted. Stripe
        // fires checkout.session.expired ~24h after an unfinished checkout and
        // the webhook flips the order to 'abandoned' (see stripe-webhook.js),
        // so a genuinely dead attempt ages out of this view on Stripe's own
        // expiry state rather than on a staleness cutoff we'd have to guess at.
        // Pending orders deliberately keep showing no matter how recent or old:
        // a real payment reads as pending for a moment before the webhook lands,
        // and hiding it would make a parent think their registration vanished.
        // The docs stay in Firestore permanently -- an unfinished checkout is
        // not a debt and there's nothing to reconcile, and the record is the
        // evidence if a parent later reports an attempt that went wrong.
        // Filtered before grouping so an athlete whose orders were ALL abandoned
        // drops off the page entirely instead of rendering an empty-bodied card.
        const orders = ordersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(o => o.status !== 'abandoned');
        const vrs = vrSnap.docs.map(d => d.data()).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

        // Group cart line items per athlete (playerId), not per order --
        // an athlete can appear across multiple registrations/orders over
        // time, and the point of this page is "show me this kid's history,"
        // not a flat order list. No new fields needed: orders already carry
        // a per-item playerId/playerName/divisionName/netCents snapshot.
        // netCents alone is just that player's division price (net of sibling
        // discount) -- it excludes the order-level tech fee/card processing
        // fee, so it understates what was actually charged for that player's
        // line item. Neither fee is itemized per-player at checkout time, so
        // each player's share is allocated proportionally to their netCents'
        // share of the order subtotal (rounded to the nearest cent -- across
        // multiple players in one order this can be off by a cent or two from
        // the order's real totalCents, acceptable for this display).
        const groups = {};
        orders.forEach(order => {
          const subtotal = order.subtotalCents || 0;
          const feesCents = (order.techFeeCents || 0) + (order.processingFeeCents || 0);
          // Refund state is PER PLAYER, and the order's own status can't stand
          // in for it: on a 'partially_refunded' order, using the order status
          // for every line would tell a parent their still-registered child was
          // refunded. Read the playerIds off refunds[] instead.
          const refundedIds = new Set();
          (order.refunds || []).forEach(r => (r.playerIds || []).forEach(id => refundedIds.add(id)));
          (order.cart || []).forEach(item => {
            const key = item.playerId || item.playerName;
            if (!groups[key]) groups[key] = { playerId: item.playerId, playerName: item.playerName, orders: [] };
            const feeShareCents = subtotal > 0 ? Math.round(((item.netCents || 0) / subtotal) * feesCents) : 0;
            const refunded = !!(item.playerId && refundedIds.has(item.playerId));
            groups[key].orders.push({
              orderId: order.id,
              // The status of THIS LINE, not of the order. A line on a money-
              // taken order that wasn't refunded reads 'paid' as it always did.
              status: refunded ? 'refunded' : (isMoneyTakenOrder(order) ? 'paid' : order.status),
              createdAt: order.createdAt,
              registrationId: order.registrationId,
              divisionName: item.divisionName,
              totalPaidCents: (item.netCents || 0) + feeShareCents,
            });
          });
        });

        // Resolve registration -> season name for both orders and volunteer
        // picks in one pass, deduped by id -- registrations are already
        // world-readable, no rules change needed.
        const regIds = new Set();
        orders.forEach(o => { if (o.registrationId) regIds.add(o.registrationId); });
        vrs.forEach(v => { if (v.registrationId) regIds.add(v.registrationId); });
        const namesEntries = await Promise.all(Array.from(regIds).map(async id => {
          try {
            const snap = await getDoc(doc(db, 'leagues', leagueId, 'registrations', id));
            return [id, snap.exists() ? (snap.data().seasonName || 'Registration') : null];
          } catch (e) {
            return [id, null];
          }
        }));
        if (cancelled) return;

        setAthleteGroups(Object.values(groups));
        setVolunteerHistory(vrs);
        setRegistrationNames(Object.fromEntries(namesEntries.filter(([, name]) => name)));
        setLoading(false);
      } catch (e) {
        if (!cancelled) { setLoadError("Couldn't load your account. Please try again."); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId, firebaseUser.uid]);

  const saveContactInfo = async () => {
    setContactError(''); setContactSaved(false);
    // Uses the same isValidPhoneNumber as the signup form, which now collects
    // this field up front. Still allows blank here: registrants created before
    // that change have no phone, and this form is the only place they can edit
    // an address or secondary contact -- rejecting a blank phone would lock
    // them out of unrelated edits. See the note in CHANGELOG about closing this.
    const trimmedPhone = phone.trim();
    if (trimmedPhone !== '' && !isValidPhoneNumber(trimmedPhone)) {
      setContactError("That doesn't look like a valid phone number.");
      return;
    }
    const trimmedSecondaryPhone = secondaryPhone.trim();
    if (trimmedSecondaryPhone !== '' && !isValidPhoneNumber(trimmedSecondaryPhone)) {
      setContactError("That doesn't look like a valid secondary contact phone number.");
      return;
    }
    setContactSaving(true);
    try {
      await updateDoc(doc(db, 'leagues', leagueId, 'registrants', firebaseUser.uid), {
        phone: trimmedPhone,
        address: { street: street.trim(), city: city.trim(), state: state.trim(), zip: zip.trim() },
        secondaryContactName: secondaryName.trim(),
        secondaryContactPhone: trimmedSecondaryPhone,
        secondaryContactEmail: secondaryEmail.trim(),
      });
      setContactSaved(true);
      setTimeout(() => setContactSaved(false), 2500);
    } catch (e) {
      setContactError("Couldn't save your info. Please try again.");
    }
    setContactSaving(false);
  };

  return (
    <ParentAuthShell leagueName={leagueName} maxWidth={520}>
      <div style={{ fontWeight: 800, fontSize: 17, color: C.text, marginBottom: 4 }}>My Account</div>

      {/* Past registrations first: it's what a parent opens this page to see.
          Contact info sits last -- it's a form most parents fill once and never
          touch again, so it no longer pushes the content they came for below
          the fold. This reverses the earlier contact-info-first ordering, which
          was chosen before there was any real content below it to weigh
          against. Deliberately no collapsing on any section: a real parent has
          one or two kids and a few seasons, so the list is short. */}
      <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>Past registrations</div>
      {loading ? (
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>Loading...</div>
      ) : loadError ? (
        <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark, marginBottom: 18 }}>{loadError}</div>
      ) : athleteGroups.length === 0 ? (
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>No registrations yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
          {athleteGroups.map(group => (
            <div key={group.playerId || group.playerName} style={{ border: '1px solid ' + C.border, borderRadius: R.control, padding: '10px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 6 }}>{group.playerName}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.orders.map((o, i) => {
                  const badge = ORDER_STATUS_BADGE[o.status] || { label: o.status || 'Unknown', bg: C.grayLt, color: C.muted };
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 12 }}>
                      <div>
                        <div style={{ color: C.text }}>{registrationNames[o.registrationId] || 'Registration'} — {o.divisionName}</div>
                        <div style={{ color: C.muted, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                          <Badge bg={badge.bg} color={badge.color}>{badge.label}</Badge>
                        </div>
                        {/* Worded to be true both of a checkout the parent
                            abandoned and of a real payment still finalizing --
                            those are the same 'pending' status with no field to
                            tell them apart. Saying "not completed" would be a
                            false alarm in the second case, and the natural
                            reaction to it is to register and pay a second time. */}
                        {o.status === 'pending' && (
                          <div style={{ color: C.muted, marginTop: 3 }}>Not confirmed — you have not been charged.</div>
                        )}
                        {/* No refund amount is shown: the figure on the right is
                            this line's share of the order, and a refund is
                            issued against the order, so quoting a per-line
                            refund total here could disagree with the parent's
                            bank statement by a cent. The bank is the receipt. */}
                        {o.status === 'refunded' && (
                          <div style={{ color: C.muted, marginTop: 3 }}>Refunded — it can take 5–10 business days to appear on your statement.</div>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>${((o.totalPaidCents || 0) / 100).toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden entirely on a load failure, header included. Both branches
          below would otherwise be reached with volunteerHistory still empty,
          so a failed fetch rendered "No volunteer picks yet." -- which asserts
          something we don't know. We didn't find out that the parent has no
          volunteer interest, we failed to find out at all. The red error
          banner in the section above already reports the failure once, so
          repeating it here would just be noise. */}
      {!loadError && (
        <>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>Volunteer interest</div>
          {/* Mirrors the past-registrations chain directly above rather than
              hiding the section until it loads. Both sections resolve off the
              same Promise.all, so suppressing this one would leave its
              neighbour showing "Loading..." while this one popped in and
              reflowed Contact info down the page -- trading a static empty
              header for content shifting under the parent, and making two
              adjacent sections behave differently enough to read as a bug. */}
          {loading ? (
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>Loading...</div>
          ) : volunteerHistory.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>No volunteer picks yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {volunteerHistory.map((v, i) => (
                <div key={i} style={{ border: '1px solid ' + C.border, borderRadius: R.control, padding: '10px 12px', fontSize: 12 }}>
                  <div style={{ color: C.text }}>
                    {v.status === 'declined' ? 'Not volunteering at this time' : (v.roleNames || []).join(', ') || 'Interested'}
                  </div>
                  <div style={{ color: C.muted, marginTop: 2 }}>{registrationNames[v.registrationId] || 'Registration'} · {v.createdAt ? new Date(v.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>Contact info</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
        <FieldInput label="PHONE" placeholder="(555) 123-4567" value={phone} onChange={e => setPhone(e.target.value)} />
        <FieldInput label="STREET ADDRESS" placeholder="123 Main St" value={street} onChange={e => setStreet(e.target.value)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 2 }}><FieldInput label="CITY" value={city} onChange={e => setCity(e.target.value)} /></div>
          <div style={{ flex: 1 }}><FieldInput label="STATE" value={state} onChange={e => setState(e.target.value)} /></div>
          <div style={{ flex: 1 }}><FieldInput label="ZIP" value={zip} onChange={e => setZip(e.target.value)} /></div>
        </div>
        <div style={{ borderTop: '1px solid ' + C.border, paddingTop: 12, marginTop: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 10 }}>Secondary / co-parent contact (optional)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <FieldInput label="NAME" value={secondaryName} onChange={e => setSecondaryName(e.target.value)} />
            <FieldInput label="PHONE" placeholder="(555) 123-4567" value={secondaryPhone} onChange={e => setSecondaryPhone(e.target.value)} />
            <FieldInput label="EMAIL" type="email" value={secondaryEmail} onChange={e => setSecondaryEmail(e.target.value)} />
          </div>
        </div>
        {contactError && <div style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '10px 12px', fontSize: 13, color: C.redDark }}>{contactError}</div>}
        <Btn onClick={saveContactInfo} disabled={contactSaving} full>{contactSaving ? 'Saving...' : (contactSaved ? 'Saved \u2713' : 'Save contact info')}</Btn>
      </div>

      {slug && (
        <div style={{ marginTop: 18, borderTop: '1px solid ' + C.border, paddingTop: 14, textAlign: 'center' }}>
          <a href={'/register/' + slug} style={{ fontSize: 12, color: C.green, fontWeight: 700, textDecoration: 'none' }}>+ Register another player</a>
        </div>
      )}
    </ParentAuthShell>
  );
}

function FieldOpsAppInner() {
  const [user, setUser] = useState(null);
  const [league, setLeague] = useState(null);
  const [screen, setScreen] = useState('landing');
  // ─── MULTI-LEAGUE SUPPORT ──────────────────────────────────────────
  // Every league a signed-in person has access to (admin, co-admin, or volunteer
  // membership), so a switcher can be shown when there's more than one. Populated
  // at login and kept in sync when joining a new league while already signed in.
  const [myLeagues, setMyLeagues] = useState([]); // [{ leagueId, leagueName, role, isMainAdmin, memberId }]
  const [showLeagueSwitcher, setShowLeagueSwitcher] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMemberName, setEditingMemberName] = useState('');
  const [showOtherMenu, setShowOtherMenu] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  const [showSeasonMenu, setShowSeasonMenu] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [appDownloadTarget, setAppDownloadTarget] = useState('tryouts');
  const [showArchivedSeasons, setShowArchivedSeasons] = useState(false);
  const [creatingSeason, setCreatingSeason] = useState(false);
  const [newSeasonLabel, setNewSeasonLabel] = useState("Fall");
  const [newSeasonCustomLabel, setNewSeasonCustomLabel] = useState("");
  const [newSeasonYear, setNewSeasonYear] = useState(new Date().getFullYear());
  const [editingSeasonId, setEditingSeasonId] = useState(null);
  const [editSeasonLabel, setEditSeasonLabel] = useState("Fall");
  const [editSeasonCustomLabel, setEditSeasonCustomLabel] = useState("");
  const [editSeasonYear, setEditSeasonYear] = useState(new Date().getFullYear());
  const backfillRanRef = React.useRef(false);
  const [otherMenuPos, setOtherMenuPos] = useState({ bottom: 0, right: 0 });
  const otherBtnRef = React.useRef(null);
  const [pendingLeagueChoices, setPendingLeagueChoices] = useState(null); // set only when login finds >1 league, to drive the picker screen
  // ─── VOLUNTEER AUTH ────────────────────────────────────────────────
  const [volSignupPassword, setVolSignupPassword] = useState(""); // used when joining a league for the first time
  const [volLoginPassword, setVolLoginPassword] = useState(""); // used for returning sign-in
  const [volNeedsPasswordSetup, setVolNeedsPasswordSetup] = useState(false); // true when a legacy pre-password account is found by email
  const [volSetupPassword, setVolSetupPassword] = useState(""); // the new password a legacy volunteer sets on migration
  const [volSetupAccount, setVolSetupAccount] = useState(null); // the volunteerAccounts doc data found for the legacy migration flow
  const [forgotPasswordMsg, setForgotPasswordMsg] = useState(""); // shared confirmation text for both admin and volunteer "forgot password" links
  const [appLoading, setAppLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [archive, setArchive] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [members, setMembers] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [showAssignMenuPT, setShowAssignMenuPT] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth >= 640 && window.innerWidth < 900);
  const [boardPosts, setBoardPosts] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [fieldEvents, setFieldEvents] = useState([]);
  const [calWeekStart, setCalWeekStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d; });
  const [selectedMobileDay, setSelectedMobileDay] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', field: '', dates: [''], startTime: '09:00', endTime: '10:00', notes: '', eventType: 'game' });
  const [calImageData, setCalImageData] = useState('');
  const [newEventSlots, setNewEventSlots] = useState([]);
  // Recurrence builder state for the Request/New Event form (recurring event
  // requests feature) -- kept separate from `newEvent` like `newEventSlots`
  // and `calImageData` already are, so it can be reset independently.
  const EMPTY_RECURRENCE = { enabled: false, pattern: 'weekly', daysOfWeek: [], startDate: '', endDate: '' };
  const [newEventRecurrence, setNewEventRecurrence] = useState(EMPTY_RECURRENCE);
  // Per-date conflict info for the live preview list (recurrence-generated or
  // manually-entered dates), keyed by date string: { [date]: { conflict: bool, source: 'event'|'game'|null } }
  const [datesConflictInfo, setDatesConflictInfo] = useState({});
  // Dates the requester has manually removed from the preview list because a
  // conflict was flagged (kept separate from newEvent.dates/recurrence so the
  // underlying generated list doesn't have to be mutated).
  const [excludedPreviewDates, setExcludedPreviewDates] = useState([]);
  // Guards saveEvent against duplicate submissions (e.g. a double-click before
  // the form closes) writing the same date(s) twice as separate docs.
  const [savingEvent, setSavingEvent] = useState(false);
  const [fieldStatusOpen, setFieldStatusOpen] = useState(false);
  const [showFieldStatusInfo, setShowFieldStatusInfo] = useState(false);
  const [slotsNotification, setSlotsNotification] = useState(null);
  const [signupNotification, setSignupNotification] = useState(null);
  const [mySignupsOpen, setMySignupsOpen] = useState(false);
  const [signingUpSlot, setSigningUpSlot] = useState(null);
  const [signupComment, setSignupComment] = useState('');
  const [viewingEvent, setViewingEvent] = useState(null);
  const [showMyEvents, setShowMyEvents] = useState(false);
  const [boardNotification, setBoardNotification] = useState(null);
  const [crewSortAsc, setCrewSortAsc] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [dTab, setDTab] = useState('tasks');
  const [dShowNewTask, setDShowNewTask] = useState(false);
  const [dShowNewEvent, setDShowNewEvent] = useState(false);
  const [dShowSettings, setDShowSettings] = useState(false);
  const [dClosedFields, setDClosedFields] = useState([]);
  const [dShowNewSupply, setDShowNewSupply] = useState(false);
  const [dHoveredTab, setDHoveredTab] = useState(null);
  // `expandedLeagues` lived here. Phase 2 replaced the superadmin Leagues
  // expand/collapse row with a drawer keyed on `saLeagueId`, which was its only
  // consumer -- leaving it declared would have added a no-unused-vars warning.
  const [fieldClosures, setFieldClosures] = useState([]);
  const [fieldClosureNotification, setFieldClosureNotification] = useState(null);
  const [promoExpiryBannerDismissed, setPromoExpiryBannerDismissed] = useState(false);
  const [notifyChannel, setNotifyChannel] = useState(null);
  const [notifyModalPending, setNotifyModalPending] = useState(null);
  const [fieldChangeBatch, setFieldChangeBatch] = useState({});
  const fieldBatchTimerRef = React.useRef(null);
  const notifyChannelRef = React.useRef(null);
  // Event approve/reject notifications: mirrors the field-closure batching
  // pattern above. Remembers the admin's yes/no "notify requester" choice for
  // 2 minutes (eventNotifyChoiceRef) so approving/rejecting several dates in a
  // row (e.g. "Approve All" on a multi-date recurring request) doesn't re-prompt
  // per date, and batches the actual outcomes per requestGroupId so the
  // requester gets ONE consolidated email listing every approved/rejected date
  // instead of one email per date.
  const [eventNotifyBatch, setEventNotifyBatch] = useState({});
  const eventBatchTimerRef = React.useRef(null);
  const eventNotifyChoiceRef = React.useRef(null);
  const eventChoiceTimerRef = React.useRef(null);
  // "Approve All" / "Reject All" fire several triggerNotify('event', ...) calls
  // back-to-back (one await per date), which can outrun a single render cycle.
  // Refs (not state) so every call -- even ones from a stale closure captured
  // earlier in the loop -- sees the same up-to-date values: accumulate every
  // undecided payload here rather than only keeping the most recent one, and
  // only pop the modal open once per undecided burst.
  const eventPendingNotifiesRef = React.useRef([]);
  const eventModalPendingRef = React.useRef(false);
  const [showExtendedClosure, setShowExtendedClosure] = useState(false);
  const [extClosureFields, setExtClosureFields] = useState([]);
  const [extClosureStart, setExtClosureStart] = useState('');
  const [extClosureEnd, setExtClosureEnd] = useState('');
  const [extClosureReason, setExtClosureReason] = useState('Weather');
  const [extClosureMessage, setExtClosureMessage] = useState('');
  const [extClosureConflicts, setExtClosureConflicts] = useState([]);
  const [pendingSaveConflicts, setPendingSaveConflicts] = useState(null);
  const [pendingSaveConflictItems, setPendingSaveConflictItems] = useState([]);
  const [pendingSaveOverrideIds, setPendingSaveOverrideIds] = useState([]);
  // Approval-time conflict check -- mirrors pendingSaveConflicts above, but
  // triggered when approving an existing pending event request rather than
  // creating a new one. A conflicting game/event may not have existed yet
  // when the request was originally submitted (or the requester's own
  // submit-time check flagged it but the admin approved anyway) -- either
  // way, approveEvent previously wrote status:'approved' with zero conflict
  // awareness, letting a game and an event silently double-book the same
  // location/date/time. This applies regardless of event type (game vs.
  // practice vs. other) -- only one thing can hold a given location/date/
  // time slot.
  const [pendingApproveConflict, setPendingApproveConflict] = useState(null);
  const [pendingApproveConflictItems, setPendingApproveConflictItems] = useState([]);
  const [pendingApproveOverrideIds, setPendingApproveOverrideIds] = useState([]);
  const [approvingConflict, setApprovingConflict] = useState(false);
  const [extClosureCancelIds, setExtClosureCancelIds] = useState([]);
  const [extClosureStep, setExtClosureStep] = useState('form');
  const [editingExtBatchId, setEditingExtBatchId] = useState(null); // set when editing an existing extended closure instead of creating a new one
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zipSaved, setZipSaved] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", body: "", important: false });
  const [taskSort, setTaskSort] = useState('default');
  const [locationFilter, setLocationFilter] = useState(null);
  const [leagueLocations, setLeagueLocations] = useState(["", "", "", "", ""]);
  const [editingLocations, setEditingLocations] = useState(null);
  const [locationsSaved, setLocationsSaved] = useState(false);
  // Public registration link (registration-parent-facing-v1-spec.md) --
  // slug lives on the league doc, uniqueness enforced via the leagueSlugs/
  // {slug} lookup collection.
  const [leagueSlugInput, setLeagueSlugInput] = useState('');
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugSaved, setSlugSaved] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [slugSuggesting, setSlugSuggesting] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);
  const [leagueGroups, setLeagueGroups] = useState([]);
  const [editingGroups, setEditingGroups] = useState(null);
  const [groupsSaved, setGroupsSaved] = useState(false);
  const [groupFilter, setGroupFilter] = useState(null);
  const [readBoardPosts, setReadBoardPosts] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('groundspal-read-posts') || '[]')); } catch { return new Set(); }
  });
  const [taskTitles, setTaskTitles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectArchive, setProjectArchive] = useState([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [showNewProjectTask, setShowNewProjectTask] = useState(false);
  const [newProjectTask, setNewProjectTask] = useState({ title: "", description: "", location: "", priority: "medium", dueDate: "" });
  const [projectTaskFilter, setProjectTaskFilter] = useState("all");
  const [editingProjectTask, setEditingProjectTask] = useState(null);
  const [selectedProjectTaskId, setSelectedProjectTaskId] = useState(null);
  const [projectUpdateText, setProjectUpdateText] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const showConfirm = (title, message, onConfirm, options = {}) => {
    setConfirmModal({ title, message, onConfirm, danger: options.danger, confirmLabel: options.confirmLabel, cancelLabel: options.cancelLabel });
  };
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState("tasks");
  // "Review Request" links in event-request emails (?screen=events) land here
  // once, on first mount, so a click-through can be routed straight to the
  // Events tab as soon as the admin's league finishes loading -- read once so
  // an in-app tab switch afterward doesn't keep re-triggering it.
  const deepLinkScreenRef = React.useRef(typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('screen') : null);
  // Stripe's Connect onboarding redirect (create-connect-account.js's
  // return_url) lands here as ?connect=return -- read once, same pattern
  // as deepLinkScreenRef above, so an in-app re-render afterward doesn't
  // keep re-triggering the sync call below.
  const deepLinkConnectRef = React.useRef(typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('connect') : null);
  // RegistrationManager (where the Payouts card and its own connectLoading/
  // connectError state live) only mounts when tab === "registration" -- it
  // isn't necessarily in the tree yet when the ?connect=return redirect
  // lands back on whatever the default tab is. This top-level state exists
  // so the sync effect below always has somewhere safe to report a failure,
  // regardless of which tab is showing when it runs; passed down to
  // RegistrationManager as props so the Payouts card can surface it too.
  const [connectSyncLoading, setConnectSyncLoading] = useState(false);
  const [connectSyncError, setConnectSyncError] = useState("");
  const [taskFilter, setTaskFilter] = useState("all");
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewSupply, setShowNewSupply] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", locations: [], location: "", groups: [], priority: "medium", dueDate: "" });
  const [newSupply, setNewSupply] = useState({ item: "", quantity: "", notes: "" });
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminName, setAdminName] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [signupZipCode, setSignupZipCode] = useState("");
  // Coach-identification question (see coach-flag-feature-spec.md), asked
  // inline on each signup form. isCoach starts undefined (not yet answered,
  // distinct from an explicit "No") so the Yes/No buttons render unselected;
  // an unanswered question is treated as "No" at submit time, not blocked.
  const [adminIsCoach, setAdminIsCoach] = useState(undefined);
  const [adminCoachRole, setAdminCoachRole] = useState(null);
  const [volIsCoach, setVolIsCoach] = useState(undefined);
  const [volCoachRole, setVolCoachRole] = useState(null);
  const [coAdminIsCoach, setCoAdminIsCoach] = useState(undefined);
  const [coAdminCoachRole, setCoAdminCoachRole] = useState(null);
  const [volName, setVolName] = useState("");
  const [volEmail, setVolEmail] = useState("");
  const [signinEmail, setSigninEmail] = useState("");
  const [signinLoading, setSigninLoading] = useState(false);
  const [signinError, setSigninError] = useState("");
  const [volCode, setVolCode] = useState("");
  const [coAdminName, setCoAdminName] = useState("");
  const [coAdminEmail, setCoAdminEmail] = useState("");
  const [coAdminPassword, setCoAdminPassword] = useState("");
  const [coAdminCode, setCoAdminCode] = useState("");
  // One-time existing-member login popup (Section 4 of the coach-flag spec) --
  // shown once per league, per signed-in user, when their coachPromptSeen
  // field is missing/false. coachPromptFor tracks which actor type answered
  // ('volunteer' | 'admin' | 'coadmin') so the save handler knows where to write.
  const [showCoachPrompt, setShowCoachPrompt] = useState(false);
  const [coachPromptFor, setCoachPromptFor] = useState(null);
  const [coachPromptIsCoach, setCoachPromptIsCoach] = useState(undefined);
  const [coachPromptCoachRole, setCoachPromptCoachRole] = useState(null);
  const [coachPromptSaving, setCoachPromptSaving] = useState(false);
  const coachPromptCheckedRef = React.useRef(null);
  const [claimName, setClaimName] = useState("");
  const [claimEmail, setClaimEmail] = useState("");
  const [claimPassword, setClaimPassword] = useState("");
  const [claimCode, setClaimCode] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [createdCode, setCreatedCode] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);
  const [coAdminCodeCopied, setCoAdminCodeCopied] = useState(false);
  const [billingLoading, setBillingLoading] = useState(null); // holds the priceId currently being checked out, or null
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual' toggle for the upgrade cards
  // Volunteer "My Account" — SMS opt-in
  const [myPhone, setMyPhone] = useState("");
  const [mySmsConsent, setMySmsConsent] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountSaved, setAccountSaved] = useState(false);
  const [accountError, setAccountError] = useState("");
  // Admin/Co-Admin "League Settings" — SMS opt-in (their own number)
  const [adminPhone, setAdminPhone] = useState("");
  const [adminSmsConsent, setAdminSmsConsent] = useState(false);
  // Per-category toggles, only relevant once adminSmsConsent is on -- lets an
  // admin/co-admin get texts for field closures without also getting one for
  // every incoming event request, or vice versa. Default true (both on) so
  // existing opted-in admins keep getting everything unless they turn one off.
  const [adminSmsPrefFieldClosures, setAdminSmsPrefFieldClosures] = useState(true);
  const [adminSmsPrefEventRequests, setAdminSmsPrefEventRequests] = useState(true);
  const [adminSmsSaving, setAdminSmsSaving] = useState(false);
  const [adminSmsSaved, setAdminSmsSaved] = useState(false);
  const [adminSmsError, setAdminSmsError] = useState("");
  // Change Password (available to any authenticated role — admin, co-admin, volunteer)
  const [currentPasswordInput, setCurrentPasswordInput] = useState("");
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [confirmNewPasswordInput, setConfirmNewPasswordInput] = useState("");
  const [passwordChangeSaving, setPasswordChangeSaving] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [platformEmail, setPlatformEmail] = useState('');
  const [platformBanners, setPlatformBanners] = useState([]);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const emptyBannerDraft = { name: '', linkUrl: '', imageFile: null, imageUrl: '', active: false, scope: 'all', leagueIds: [] };
  const [bannerDraft, setBannerDraft] = useState(emptyBannerDraft);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [emulating, setEmulating] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [hideInactivePromoCodes, setHideInactivePromoCodes] = useState(true);
  const [promoDeletingCode, setPromoDeletingCode] = useState(null);
  const [promoForm, setPromoForm] = useState({ code: '', type: 'free_year', value: '', maxRedemptions: '', expiresAt: '', restrictedToLeagueId: '' });
  const [newLeagueForm, setNewLeagueForm] = useState({ name: '', zipCode: '' });
  const [creatingLeague, setCreatingLeague] = useState(false);
  const [createLeagueError, setCreateLeagueError] = useState('');
  const [justCreatedLeague, setJustCreatedLeague] = useState(null); // { leagueName, claimCode }
  const [claimCodeCopied, setClaimCodeCopied] = useState(false);
  const [promoCreating, setPromoCreating] = useState(false);
  const [promoCreateError, setPromoCreateError] = useState('');
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoRedeemLoading, setPromoRedeemLoading] = useState(false);
  const [promoRedeemMessage, setPromoRedeemMessage] = useState('');
  const [platformPassword, setPlatformPassword] = useState('');
  const [platformError, setPlatformError] = useState('');
  const [platformData, setPlatformData] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [selectedStat, setSelectedStat] = useState('totalLeagues');
  const [statRange, setStatRange] = useState('7d');
  const [statHistory, setStatHistory] = useState({ docs: [], trackingSince: null });
  const [statHistoryLoading, setStatHistoryLoading] = useState(false);

  // ─── SUPERADMIN SHELL (redesign, Phase 1) ────────────────────────────────
  // saTab is initialised lazily from the URL so a refresh or a shared link to
  // /superadmin/leagues lands on the right tab rather than always snapping
  // back to the dashboard. See isSuperadminPath for why the pathname checks
  // in the auth effect had to become prefix matches for this to be safe.
  const [saTab, setSaTab] = useState(() => saTabFromPath());
  const [saRailOpen, setSaRailOpen] = useState(false);   // mobile off-canvas rail
  const [saCreateOpen, setSaCreateOpen] = useState(false); // create-league drawer
  const saTabStripRef = useRef(null);
  const saRangeStripRef = useRef(null);

  // ─── SUPERADMIN LEAGUES (redesign, Phase 2) ──────────────────────────────
  // saLeagueId holds an ID, not a league object. The drawer looks the league
  // back up out of platformData on every render, so removing a co-admin or a
  // volunteer from inside the drawer shows up immediately -- a snapshotted
  // object would keep rendering the person who was just removed.
  const [saLeagueId, setSaLeagueId] = useState(null);
  const [saLeagueQuery, setSaLeagueQuery] = useState('');
  const [saLeagueFilter, setSaLeagueFilter] = useState('all'); // all | unclaimed | plan | free
  const [saClaimCopied, setSaClaimCopied] = useState(false);
  // { kicker, title, body, confirmLabel, onConfirm } -- see SaConfirm.
  const [saConfirm, setSaConfirm] = useState(null);
  const [saConfirmBusy, setSaConfirmBusy] = useState(false);

  // ─── SUPERADMIN PROMO CODES (redesign, Phase 3) ──────────────────────────
  // The create form was an always-open block above the list; it is a drawer
  // now, matching create-league. promoForm / promoCreating / promoCreateError
  // and the four handlers are unchanged -- this is only what opens the form.
  const [saPromoOpen, setSaPromoOpen] = useState(false);

  const cancelSaConfirm = () => { if (!saConfirmBusy) setSaConfirm(null); };
  const runSaConfirm = async () => {
    if (!saConfirm || saConfirmBusy) return;
    setSaConfirmBusy(true);
    try {
      await saConfirm.onConfirm();
    } catch (e) {
      // The three actions behind this all update platformData optimistically
      // and have no error surface of their own; a failure leaves the list
      // as-is and the action can simply be retried.
    }
    setSaConfirmBusy(false);
    setSaConfirm(null);
  };

  // The league drawer -- and the confirm and coach editor that stack on it --
  // render OUTSIDE the tab switch, because a modal that lives inside one render
  // path is a modal some other render path can forget (the recurring
  // ConfirmModal bug class in CLAUDE.md). The cost of that placement is that
  // nothing structurally ties them to the tab they belong to, so a drawer
  // opened on Leagues would keep rendering on top of the Dashboard after a tab
  // change. Every tab change -- rail click, in-page link, or browser Back --
  // goes through one of the two callers below, so clearing here covers all of
  // them. A caller that WANTS a drawer open on arrival (the Dashboard's
  // Recent-leagues rows) sets saLeagueId AFTER calling saGo, not before.
  const saCloseOverlays = () => {
    setSaLeagueId(null);
    setSaConfirm(null);
    setPlatformCoachEdit(null);
  };

  // Navigate between superadmin tabs. Advertising is a real screen swap (that
  // screen is deliberately untouched by the redesign); everything else stays
  // on platform-dashboard and just changes which body renders.
  const saGo = (tab) => {
    const route = SA_ROUTES.find(r => r.tab === tab);
    if (!route) return;
    setSaRailOpen(false);
    saCloseOverlays();
    if (window.location.pathname !== route.path) {
      window.history.pushState({ saTab: tab }, '', route.path);
    }
    setSaTab(tab);
    if (tab === 'ads') setScreen('platform-advertising');
    else setScreen('platform-dashboard');
  };

  // Back/forward within the console, INCLUDING while emulating a league.
  //
  // MOUNTED MORE OFTEN, NOT ACTING MORE BROADLY -- these are two separate
  // conditions and they must stay separate:
  //   * WHEN IT IS MOUNTED widened to include `emulating`, because emulation
  //     sets screen='app' and the old gate detached the listener for the whole
  //     emulation session -- which is exactly when Back was unhandled.
  //   * WHAT IT ACTS ON did not widen at all. The isSuperadminPath() check
  //     below still runs first on every pop and still bails on anything that
  //     is not a superadmin path, so this can never hijack a pop belonging to
  //     the registration flow's own history handling.
  useEffect(() => {
    if (!emulating && screen !== 'platform-dashboard' && screen !== 'platform-advertising') return;
    const onPop = () => {
      if (!isSuperadminPath()) return;
      const tab = saTabFromPath();
      // Back out of an emulated league returns to the console. handleSignOut's
      // emulating branch is reused rather than re-implemented: it also flushes
      // pending notify batches, which a hand-rolled teardown here would drop
      // on the floor. That branch is deliberately not modified -- only called.
      if (emulating) handleSignOut();
      // Back is the ONE tab change that does not go through saGo, so it needs
      // the same overlay teardown. Without it, Back out of a league drawer
      // opened from the Dashboard's Recent-leagues rows lands on the Dashboard
      // with that drawer still covering it.
      saCloseOverlays();
      setSaTab(tab);
      setScreen(tab === 'ads' ? 'platform-advertising' : 'platform-dashboard');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [screen, emulating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bring the selected metric tab / range chip into view inside its own
  // horizontal scroller.
  //
  // WHY NOT A BARE scrollIntoView(): with no options it defaults to
  // block:'start', which scrolls the nearest scrollable ancestor in BOTH axes
  // -- so selecting a chip would yank the whole dashboard vertically to put
  // that chip at the top of the viewport. block/inline:'nearest' scrolls only
  // as far as needed and leaves the vertical axis alone when the element is
  // already vertically visible, which it always is here.
  //
  // This runs on selection as well as on mount: the strips are scrollable at
  // 375px, so a selection made by keyboard, or restored from the URL, can sit
  // outside the visible slice just as easily as the initial one can.
  const saScrollSelectedIntoView = (stripRef) => {
    const strip = stripRef.current;
    if (!strip) return;
    const el = strip.querySelector('[data-sa-selected="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  useEffect(() => {
    if (screen !== 'platform-dashboard' || saTab !== 'dashboard') return;
    saScrollSelectedIntoView(saTabStripRef);
  }, [screen, saTab, selectedStat]);
  useEffect(() => {
    if (screen !== 'platform-dashboard' || saTab !== 'dashboard') return;
    saScrollSelectedIntoView(saRangeStripRef);
  }, [screen, saTab, statRange]);

  // Keep the URL honest for the one navigation the redesign does not own: the
  // Advertising screen's own "← Back" sets screen directly without touching
  // history, which would otherwise leave the address bar on
  // /superadmin/advertising while the dashboard is showing.
  useEffect(() => {
    if (screen !== 'platform-dashboard') return;
    if (!isSuperadminPath()) return;
    if (saTab === 'ads') { setSaTab('dashboard'); return; }
    const route = SA_ROUTES.find(r => r.tab === saTab);
    if (route && window.location.pathname !== route.path) {
      window.history.replaceState({ saTab }, '', route.path);
    }
  }, [screen, saTab]);

  // ─── SUPERADMIN: coach-flag editing (superadmin-coach-edit-spec.md) ──────
  // platformCoachEdit identifies which record is being corrected: { type:
  // 'admin'|'coadmin'|'volunteer', leagueId, uid, name, leagueName }. Reuses
  // the same CoachPromptModal/CoachPrompt UI built for the original
  // coach-flag feature rather than a new control.
  const [platformCoachEdit, setPlatformCoachEdit] = useState(null);
  const [platformCoachEditIsCoach, setPlatformCoachEditIsCoach] = useState(undefined);
  const [platformCoachEditCoachRole, setPlatformCoachEditCoachRole] = useState(null);
  const [platformCoachEditSaving, setPlatformCoachEditSaving] = useState(false);

  // ─── AUTH STATE ──────────────────────────────────────────────────
  // Mobile detection
  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 640);
      setSidebarCollapsed(window.innerWidth >= 640 && window.innerWidth < 900);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ─── PLATFORM VISIT TRACKING ─────────────────────────────────────
  // Fire-and-forget counter increment, once per app load, feeding the
  // superadmin dashboard's "Platform Visits" stat/trend. Silent failure is
  // intentional here -- a blocked/offline write should never affect the
  // real app, same pattern used for SMS save handlers elsewhere.
  useEffect(() => {
    setDoc(doc(db, 'platformStats', 'visits'), { count: increment(1) }, { merge: true }).catch(() => {});
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // On the platform console, check super-admin status FIRST and stop there --
        // otherwise a super admin who also happens to be a league co-admin (as this
        // account is) would get silently logged into that league instead on refresh.
        if (isSuperadminPath()) {
          const superAdminDoc = await getDoc(doc(db, 'superAdmins', firebaseUser.uid));
          if (superAdminDoc.exists()) {
            await loadPlatformData();
            // Honour the sub-route on a refresh. Advertising is a separate
            // screen, so landing on /superadmin/advertising has to resolve to
            // that screen -- otherwise the dashboard renders with saTab='ads',
            // which matches no tab body, and the URL-sync effect quietly
            // rewrites the address bar back to /superadmin.
            setScreen(saTabFromPath() === 'ads' ? 'platform-advertising' : 'platform-dashboard');
            loadPromoCodes();
          } else {
            setScreen('platform-login');
          }
          setAppLoading(false);
          return;
        }
        // Gather every league this account has admin-level access to — as main
        // admin OR co-admin — instead of stopping at the first match, so someone
        // who administers more than one league is shown all of them.
        const mainAdminQuery = query(collection(db, 'leagues'), where('adminUid', '==', firebaseUser.uid));
        const mainAdminSnap = await getDocs(mainAdminQuery);
        const coAdminQuery = query(collection(db, 'leagues'), where('coAdminUids', 'array-contains', firebaseUser.uid));
        const coAdminSnap = await getDocs(coAdminQuery);
        const adminLeagueChoices = [
          ...mainAdminSnap.docs.map(d => {
            const data = { id: d.id, ...d.data() };
            return { leagueId: d.id, leagueName: data.leagueName, role: 'admin', isMainAdmin: true, name: data.adminName };
          }),
          ...coAdminSnap.docs.map(d => {
            const data = { id: d.id, ...d.data() };
            const coAdminInfo = (data.coAdmins || []).find(ca => ca.uid === firebaseUser.uid);
            return { leagueId: d.id, leagueName: data.leagueName, role: 'admin', isMainAdmin: false, name: coAdminInfo ? coAdminInfo.name : "Co-Admin" };
          })
        ];
        // Also check whether this same account (matched by email) has any volunteer
        // memberships — the same person can be an admin somewhere and a volunteer
        // elsewhere under the same login, and both should appear in one combined list.
        let volLeagueChoices = [];
        if (firebaseUser.email) {
          const emailKey = firebaseUser.email.toLowerCase().replace(/[^a-z0-9]/g, '_');
          const accountDoc = await getDoc(doc(db, 'volunteerAccounts', emailKey));
          if (accountDoc.exists() && accountDoc.data().uid === firebaseUser.uid) {
            volLeagueChoices = buildVolLeagueChoices(accountDoc.data(), firebaseUser.email);
          }
        }
        const allChoices = [...adminLeagueChoices, ...volLeagueChoices];
        if (allChoices.length > 0) {
          setMyLeagues(allChoices);
          if (!isSuperadminPath()) {
            if (allChoices.length === 1) {
              await enterLeague(allChoices[0]);
            } else {
              setPendingLeagueChoices(allChoices);
              setScreen('choose-league');
            }
          }
          // On /superadmin, a regular account's league access is intentionally not
          // loaded or navigated to — matches the previous defensive behavior.
          setAppLoading(false);
          return;
        }
      }
      setAppLoading(false);
    });
    return () => unsubAuth();
  }, []);

  // Consumes the ?screen=events deep link (see deepLinkScreenRef above) once
  // the league finishes loading and the main app screen is showing -- covers
  // both an already-logged-in admin clicking the email link and one who had
  // to log in first (the query string survives that redirect either way).
  useEffect(() => {
    if (screen === 'app' && league && deepLinkScreenRef.current === 'events') {
      setTab('calendar');
      deepLinkScreenRef.current = null;
      const url = new URL(window.location.href);
      url.searchParams.delete('screen');
      window.history.replaceState({}, '', url);
    }
  }, [screen, league]);

  // Consumes the ?connect=return deep link (see deepLinkConnectRef above)
  // once the league finishes loading -- a pull-based companion to
  // stripe-webhook.js's v2 Connect case. That webhook event only fires on
  // a *change*, so an account that finished onboarding before the webhook
  // destination existed (or a delivery that's missed/delayed) would
  // otherwise never sync. leagueId is read from this same `league` object
  // the gating condition already depends on, not from the URL or a stale
  // ref, so this can't fire with a wrong/undefined id. Firestore write
  // completion is picked up automatically by the existing league doc
  // listener below -- no response-driven UI update needed on success.
  useEffect(() => {
    if (screen === 'app' && league && deepLinkConnectRef.current === 'return') {
      const leagueId = league.id;
      deepLinkConnectRef.current = null;
      const url = new URL(window.location.href);
      url.searchParams.delete('connect');
      // create-connect-account.js appends these to return_url/refresh_url
      // on non-prod (Deployment Protection bypass, see that file) -- strip
      // them here too so the bypass secret doesn't linger in the browser's
      // address bar/history once we're done with it.
      url.searchParams.delete('x-vercel-protection-bypass');
      url.searchParams.delete('x-vercel-set-bypass-cookie');
      window.history.replaceState({}, '', url);

      (async () => {
        setConnectSyncError("");
        setConnectSyncLoading(true);
        try {
          // auth.currentUser can briefly be null right after this redirect
          // if Firebase's auth state is still rehydrating -- fail
          // gracefully rather than throw unhandled; the admin can retry
          // via "Continue payout setup" below once auth settles.
          if (!auth.currentUser) throw new Error('not signed in');
          const idToken = await auth.currentUser.getIdToken();
          const response = await fetch('/api/sync-connect-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ leagueId, idToken })
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.ok) {
            // Not fatal -- the webhook may still land normally on its own,
            // and the admin can always retry via the button below. Falls
            // back to the normal unconnected-state UI rather than a dead
            // spinner or a silent failure.
            setConnectSyncError("We couldn't confirm your payout setup automatically. If it doesn't update in a moment, click below to continue.");
          }
        } catch (e) {
          setConnectSyncError("We couldn't confirm your payout setup automatically. If it doesn't update in a moment, click below to continue.");
        } finally {
          setConnectSyncLoading(false);
        }
      })();
    }
  }, [screen, league]);

  // ─── REAL-TIME LISTENERS ─────────────────────────────────────────
  useEffect(() => {
    if (!league || !user) return;
    const lid = league.id;
    const unsubTasks = onSnapshot(collection(db, 'leagues', lid, 'tasks'), snap => {
      setTasks(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    });
    const unsubArchive = onSnapshot(collection(db, 'leagues', lid, 'archive'), snap => {
      setArchive(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)));
    });
    const unsubSupplies = onSnapshot(collection(db, 'leagues', lid, 'supplies'), snap => {
      setSupplies(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)));
    });
    // Live league updates (co-admin list changes)
    const unsubLeague = onSnapshot(doc(db, 'leagues', lid), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setLeague({ id: snap.id, ...data });
        if (data.zipCode) setZipCode(data.zipCode);
        if (data.locations) setLeagueLocations(data.locations);
        if (data.groups) setLeagueGroups(data.groups);
      }
    });
    const unsubMembers = onSnapshot(collection(db, 'leagues', lid, 'members'), snap => {
      setMembers(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.joinedAt) - new Date(a.joinedAt)));
    });
    const unsubTitles = onSnapshot(collection(db, 'leagues', lid, 'taskTitles'), snap => {
      const titles = snap.docs.map(d => d.data().title).sort();
      setTaskTitles(titles);
    });
    const unsubBoard = onSnapshot(collection(db, 'leagues', lid, 'board'), snap => {
      setBoardPosts(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    });
    const unsubProjects = onSnapshot(collection(db, 'leagues', lid, 'projects'), snap => {
      setProjects(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    });
    const notifQuery = query(collection(db, 'leagues', lid, 'notifications'), where('forRole', '==', user.role));
    const unsubNotifs = onSnapshot(notifQuery, snap => {
      const all = snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setNotifications(all);
    });
    return () => { unsubTasks(); unsubArchive(); unsubSupplies(); unsubLeague(); unsubMembers(); unsubTitles(); unsubBoard(); unsubProjects(); unsubNotifs(); };
  }, [league && league.id, user && user.role]);

  // ─── NOTIFICATION HELPERS ────────────────────────────────────────
  // `extra` is an optional structured payload (e.g. requestGroupId/dates/
  // conflictDates for event_request_group) spread onto the doc alongside the
  // 3 original fields -- kept optional so every existing call site is unaffected.
  const createNotification = async (type, message, forRole, extra) => {
    if (!league) return;
    const id = "n" + Date.now();
    await setDoc(doc(db, 'leagues', league.id, 'notifications', id), { id, type, message, forRole, createdAt: new Date().toISOString(), readBy: [], ...(extra || {}) });
  };

  const dismissNotification = async (notifId) => {
    if (!league || !user) return;
    await updateDoc(doc(db, 'leagues', league.id, 'notifications', notifId), { readBy: arrayUnion(user.name) });
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  };

  const dismissAllNotifications = async () => {
    if (!league || !user) return;
    for (const n of notifications) {
      await updateDoc(doc(db, 'leagues', league.id, 'notifications', n.id), { readBy: arrayUnion(user.name) });
    }
    setNotifications([]);
  };

  const unreadCount = notifications.filter(n => !n.readBy || !n.readBy.includes(user && user.name)).length;

  // ─── AUTH ACTIONS ────────────────────────────────────────────────
  const handleAdminSignup = async () => {
    if (!leagueName.trim() || !adminName.trim() || !adminEmail.trim() || !adminPassword.trim()) { setAuthError("Please fill in all fields."); return; }
    if (adminPassword.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    if (signupZipCode.trim().length !== 5 || !/^\d{5}$/.test(signupZipCode.trim())) { setAuthError("Please enter a valid 5-digit zip code."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, adminEmail.trim(), adminPassword);
      const leagueId = "league_" + Date.now();
      const volCode = generateCode();
      const caCode = generateCode();
      await setDoc(doc(db, 'leagues', leagueId), {
        leagueName: leagueName.trim(), adminName: adminName.trim(), adminEmail: adminEmail.trim(),
        adminUid: cred.user.uid, leagueCode: volCode, coAdminCode: caCode,
        coAdmins: [], coAdminUids: [], createdAt: new Date().toISOString(),
        locations: ["", "", "", "", ""], zipCode: signupZipCode.trim(),
        // Coach-identification question (coach-flag-feature-spec.md) -- main
        // admin has no separate per-league doc, so these live as flat fields
        // directly on the league doc, matching adminName/adminEmail/adminUid.
        // An unanswered question at signup is treated as "No", not blocked.
        adminIsCoach: adminIsCoach === true, adminCoachRole: adminIsCoach === true ? adminCoachRole : null, adminCoachPromptSeen: true
      });
      const leagueData = { id: leagueId, leagueName: leagueName.trim(), adminName: adminName.trim(), adminEmail: adminEmail.trim(), leagueCode: volCode, coAdminCode: caCode, coAdmins: [], coAdminUids: [],
        adminIsCoach: adminIsCoach === true, adminCoachRole: adminIsCoach === true ? adminCoachRole : null, adminCoachPromptSeen: true };
      setLeague(leagueData);
      setUser({ name: adminName.trim(), role: 'admin', leagueId, isMainAdmin: true });
      setPromoRedeemMessage(''); setPromoCodeInput('');
      setCreatedCode(volCode);
      setScreen('show-code');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('email-already-in-use')) setAuthError("An account with this email already exists.");
      else if (msg.includes('invalid-email')) setAuthError("Please enter a valid email address.");
      else setAuthError("Signup failed. Please try again.");
    }
    setAuthLoading(false);
  };

  const handleAdminLogin = async () => {
    if (!adminEmail.trim() || !adminPassword.trim()) { setAuthError("Please enter your email and password."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      await signInWithEmailAndPassword(auth, adminEmail.trim(), adminPassword);
    } catch {
      setAuthError("Invalid email or password. Please try again.");
    }
    // Reset after the try/catch, not only in the catch. The success path navigates via
    // onAuthStateChanged, but if that doesn't fire promptly the flag stayed true forever
    // and authLoading is shared by all five auth handlers, so every button on the card
    // jammed -- not just Sign In. Matches handleAdminSignup/volunteer/claim/co-admin.
    setAuthLoading(false);
  };

  // Shared by both the admin/co-admin card and the volunteer card — sends Firebase's
  // built-in reset email. Always shows the same confirmation regardless of whether
  // the email actually has an account, so this can't be used to probe for valid emails.
  const handleForgotPassword = async (email) => {
    if (!email || !email.trim()) { setForgotPasswordMsg("Enter your email above first, then tap this link."); return; }
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (e) { /* still show the same message either way, see comment above */ }
    setForgotPasswordMsg("If an account exists for that email, a password reset link is on its way.");
  };

  const handleVolunteerJoin = async () => {
    if (!volName.trim() || !volCode.trim() || !volEmail.trim() || !volSignupPassword.trim()) { setAuthError("Please fill in all fields, including a password."); return; }
    if (volSignupPassword.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      // UX pre-check ONLY — this catches a typo'd code before a Firebase Auth
      // account gets created, so a wrong code doesn't leave an orphaned account
      // behind. It is NOT the security check: `leagues` is world-readable and
      // this runs in the browser, so it can trivially be skipped. api/join-league
      // re-resolves the same code with the Admin SDK, and that server-side
      // lookup is the real gate on membership.
      const q = query(collection(db, 'leagues'), where('leagueCode', '==', volCode.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) { setAuthError("League code not found. Check with your administrator."); setAuthLoading(false); return; }
      const email = volEmail.trim().toLowerCase();
      let firebaseUser;
      try {
        const cred = await createUserWithEmailAndPassword(auth, email, volSignupPassword);
        firebaseUser = cred.user;
      } catch (err) {
        if ((err.message || '').includes('email-already-in-use')) {
          // This email already has an account (e.g. joining a second league) — verify
          // the password matches instead of silently failing or creating a duplicate.
          try {
            const cred = await signInWithEmailAndPassword(auth, email, volSignupPassword);
            firebaseUser = cred.user;
          } catch (signInErr) {
            setAuthError("An account with this email already exists, but that password doesn't match it. Try 'Sign In as Volunteer' instead.");
            setAuthLoading(false); return;
          }
        } else if ((err.message || '').includes('invalid-email')) {
          setAuthError("Please enter a valid email address.");
          setAuthLoading(false); return;
        } else {
          setAuthError("Something went wrong creating your account. Please try again.");
          setAuthLoading(false); return;
        }
      }
      // The member doc write moved server-side on Sep 4, 2026 (privilege
      // escalation fix). It used to happen right here, and firestore.rules
      // allowed it purely because the doc id matched the caller's uid — the
      // invite code was only ever checked by the client code above, so anyone
      // could skip it and write themselves into any league from the console.
      // api/join-league validates the code with the Admin SDK instead, and the
      // rules no longer permit a volunteer to create their own member doc.
      const idToken = await firebaseUser.getIdToken();
      let joinResult;
      try {
        // AbortController timeout — without this, a request that hangs (e.g.
        // intercepted by Vercel Deployment Protection on a preview URL, which
        // is exactly what happened on Aug 22, 2026) leaves the fetch promise
        // pending forever, so neither the success path nor the catch below is
        // ever reached and the button sticks on its loading state permanently.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        let resp;
        try {
          resp = await fetch('/api/join-league', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'volunteer',
              leagueCode: volCode.trim(), name: volName.trim(), idToken,
              // Coach-identification question (coach-flag-feature-spec.md), answered
              // inline on this signup form. Unanswered is treated as "No", not blocked.
              isCoach: volIsCoach === true, coachRole: volIsCoach === true ? volCoachRole : null
            }),
            signal: controller.signal
          });
        } finally {
          clearTimeout(timeoutId);
        }
        joinResult = await resp.json();
        if (!resp.ok) throw new Error(joinResult.error || 'Failed to join league');
      } catch (joinErr) {
        // Log the FULL error — network failures, timeouts (name 'AbortError'),
        // and non-JSON responses (e.g. an auth wall returning HTML) all land here.
        console.error('[join-league] volunteer request failed', { name: joinErr.name, message: joinErr.message, time: new Date().toISOString() });
        // The Auth account already exists at this point — don't leave the user on a
        // generic failure that invites a retry, which would only fail with
        // email-already-in-use and compound the confusion.
        setAuthError("Your account was created, but we couldn't add you to the league. Please contact your administrator, or try signing in — your account already exists.");
        setAuthLoading(false); return;
      }
      // Re-read the league rather than reusing the pre-check query's snapshot:
      // the server resolved the code independently, and this makes the id we
      // render from the one the server actually wrote to.
      const leagueSnap = await getDoc(doc(db, 'leagues', joinResult.leagueId));
      setLeague({ id: joinResult.leagueId, ...(leagueSnap.exists() ? leagueSnap.data() : {}) });
      setUser({ name: volName.trim(), role: 'volunteer', leagueId: joinResult.leagueId, email });
      setScreen('app');
    } catch {
      setAuthError("Something went wrong. Please try again.");
    }
    setAuthLoading(false);
  };

  // Phase 1: look up the account by email only, to decide whether to show a normal
  // password field or a "create your password" migration prompt for legacy accounts.
  const handleVolunteerEmailLookup = async () => {
    if (!signinEmail.trim()) { setSigninError("Please enter your email address."); return; }
    setSigninLoading(true); setSigninError("");
    try {
      const emailKey = signinEmail.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
      const accountDoc = await getDoc(doc(db, 'volunteerAccounts', emailKey));
      if (!accountDoc.exists()) {
        setSigninError("No account found with that email. Please join your league first using an invite code.");
        setSigninLoading(false); return;
      }
      const account = accountDoc.data();
      setVolSetupAccount(account);
      setVolNeedsPasswordSetup(!account.uid);
    } catch(e) {
      setSigninError("Something went wrong. Please try again.");
    }
    setSigninLoading(false);
  };

  // Builds the normalized league-choice list from a volunteerAccounts doc — shared
  // by both the returning sign-in path and the legacy migration path below.
  const buildVolLeagueChoices = (account, email) => {
    const rawLeagues = (account.leagues && account.leagues.length > 0) ? account.leagues : [{ leagueId: account.leagueId, name: account.name }];
    const seenIds = new Set();
    return rawLeagues
      .filter(l => { if (!l.leagueId || seenIds.has(l.leagueId)) return false; seenIds.add(l.leagueId); return true; })
      .map(l => ({ leagueId: l.leagueId, leagueName: l.leagueName, role: 'volunteer', name: l.name || account.name, memberId: l.memberId, email }));
  };

  // Phase 2a: returning volunteer who already has a real password — normal sign-in.
  const handleVolunteerSignIn = async () => {
    if (!volLoginPassword.trim()) { setSigninError("Please enter your password."); return; }
    setSigninLoading(true); setSigninError("");
    try {
      const email = signinEmail.trim().toLowerCase();
      await signInWithEmailAndPassword(auth, email, volLoginPassword);
      const volLeagueChoices = buildVolLeagueChoices(volSetupAccount, email);
      if (volLeagueChoices.length === 0) {
        setSigninError("Your league could not be found. Please contact your administrator.");
        setSigninLoading(false); return;
      }
      setMyLeagues(volLeagueChoices);
      if (volLeagueChoices.length === 1) {
        await enterLeague(volLeagueChoices[0]);
      } else {
        setPendingLeagueChoices(volLeagueChoices);
        setScreen('choose-league');
      }
    } catch(e) {
      const msg = e.message || '';
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) setSigninError("Incorrect password. Please try again.");
      else setSigninError("Something went wrong. Please try again.");
    }
    setSigninLoading(false);
  };

  // Phase 2b: legacy account with no password on file yet — set one now, migrate
  // their existing membership(s) to be keyed by the new Firebase UID so security
  // rules (which check membership by UID) recognize them going forward.
  const handleVolunteerPasswordSetup = async () => {
    if (!volSetupPassword.trim()) { setSigninError("Please choose a password."); return; }
    if (volSetupPassword.length < 6) { setSigninError("Password must be at least 6 characters."); return; }
    setSigninLoading(true); setSigninError("");
    try {
      const email = signinEmail.trim().toLowerCase();
      const cred = await createUserWithEmailAndPassword(auth, email, volSetupPassword);
      // The re-key itself moved server-side on Sep 4, 2026, alongside volunteer
      // join: copying a legacy member doc onto a UID-keyed one is a member-doc
      // CREATE, and the rules no longer let a volunteer create their own. It had
      // to move rather than just be re-permitted — leaving a self-create branch
      // open for this one case is the escalation, verbatim.
      //
      // The server does NOT trust volunteerAccounts to say which leagues to
      // migrate into (that collection is writable by any signed-in user); it
      // requires an existing member doc whose email matches the verified token.
      // Leagues that can't be proven that way come back in `skipped`.
      const idToken = await cred.user.getIdToken();
      let migrateResult;
      try {
        // Same AbortController timeout as the join paths — see the comment in
        // handleVolunteerJoin for why a hung fetch is a real failure mode here.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        let resp;
        try {
          resp = await fetch('/api/join-league', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'migrate-volunteer', idToken }),
            signal: controller.signal
          });
        } finally {
          clearTimeout(timeoutId);
        }
        migrateResult = await resp.json();
        if (!resp.ok) throw new Error(migrateResult.error || 'Failed to finish account setup');
      } catch (migrateErr) {
        console.error('[join-league] volunteer migration failed', { name: migrateErr.name, message: migrateErr.message, time: new Date().toISOString() });
        // The password is now set on a real Auth account, so "try again" would
        // fail with email-already-in-use. Say so instead of a generic error.
        setSigninError("Your password was set, but we couldn't finish setting up your account. Please try signing in with it, or contact your administrator.");
        setSigninLoading(false); return;
      }
      const volLeagueChoices = (migrateResult.leagues || []).map(l => ({ ...l, role: 'volunteer', email }));
      if (volLeagueChoices.length === 0) {
        setSigninError("Your league could not be found. Please contact your administrator.");
        setSigninLoading(false); return;
      }
      setMyLeagues(volLeagueChoices);
      if (volLeagueChoices.length === 1) {
        await enterLeague(volLeagueChoices[0]);
      } else {
        setPendingLeagueChoices(volLeagueChoices);
        setScreen('choose-league');
      }
    } catch(e) {
      const msg = e.message || '';
      if (msg.includes('email-already-in-use')) setSigninError("This email already has a password set. Please sign in with your password instead.");
      else setSigninError("Something went wrong. Please try again.");
    }
    setSigninLoading(false);
  };

  const handleClaimLeagueSignup = async () => {
    if (!claimName.trim() || !claimEmail.trim() || !claimPassword.trim() || !claimCode.trim()) { setAuthError("Please fill in all fields."); return; }
    if (claimPassword.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      // Find league by owner claim code
      const q = query(collection(db, 'leagues'), where('ownerClaimCode', '==', claimCode.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) { setAuthError("League claim code not found. Check with GroundsPal."); setAuthLoading(false); return; }
      const leagueDoc = snap.docs[0];
      const leagueData = { id: leagueDoc.id, ...leagueDoc.data() };
      if (leagueData.adminUid) { setAuthError("This league has already been claimed by an owner."); setAuthLoading(false); return; }
      // Create Firebase Auth account
      const cred = await createUserWithEmailAndPassword(auth, claimEmail.trim(), claimPassword);
      await updateDoc(doc(db, 'leagues', leagueDoc.id), {
        adminName: claimName.trim(), adminEmail: claimEmail.trim(), adminUid: cred.user.uid, ownerClaimCode: null
      });
      const updatedLeague = { ...leagueData, adminName: claimName.trim(), adminEmail: claimEmail.trim(), adminUid: cred.user.uid, ownerClaimCode: null };
      setLeague(updatedLeague);
      setUser({ name: claimName.trim(), role: 'admin', leagueId: leagueDoc.id, isMainAdmin: true });
      setScreen('app');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('email-already-in-use')) setAuthError("An account with this email already exists.");
      else if (msg.includes('invalid-email')) setAuthError("Please enter a valid email address.");
      else setAuthError("Signup failed. Please try again.");
    }
    setAuthLoading(false);
  };

  const handleCoAdminSignup = async () => {
    if (!coAdminName.trim() || !coAdminEmail.trim() || !coAdminPassword.trim() || !coAdminCode.trim()) { setAuthError("Please fill in all fields."); return; }
    if (coAdminPassword.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      // Find league by co-admin code
      const q = query(collection(db, 'leagues'), where('coAdminCode', '==', coAdminCode.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) { setAuthError("Co-admin code not found. Check with your administrator."); setAuthLoading(false); return; }
      const leagueDoc = snap.docs[0];
      const leagueData = { id: leagueDoc.id, ...leagueDoc.data() };
      // Create Firebase Auth account
      const cred = await createUserWithEmailAndPassword(auth, coAdminEmail.trim(), coAdminPassword);
      // Add to league's co-admins -- moved server-side (coadmin-signup-fix-spec.md).
      // firestore.rules only allows leagues/{lid} updates from someone already in
      // coAdminUids/adminUid; a brand-new co-admin, by definition, isn't in that
      // array yet, so this write can NEVER succeed directly from the client -- this
      // is exactly what silently orphaned a real user's account (Jordan Free,
      // Aug 21, 2026). api/join-league.js uses the Admin SDK to bypass that
      // chicken-and-egg problem after verifying the code server-side.
      // Coach-identification question (coach-flag-feature-spec.md) -- co-admins
      // have no separate per-league doc, so these live on the co-admin's own
      // object inside the coAdmins array, using the same field names as the
      // volunteer member doc (isCoach/coachRole/coachPromptSeen) so the field
      // names line up across all three actor types. Unanswered = "No".
      const idToken = await cred.user.getIdToken();
      try {
        console.log('[join-league] starting co-admin request', { email: coAdminEmail.trim(), coAdminCode: coAdminCode.trim(), time: new Date().toISOString() });
        // AbortController timeout -- without this, a request that hangs (e.g.
        // intercepted/stalled by something in front of the app, a dropped
        // connection, etc.) leaves this fetch's promise pending forever, which
        // means the code never reaches either the success path or the catch
        // block below -- the exact "stuck on Joining league... forever" bug.
        // A hard timeout guarantees this always resolves one way or another.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        let resp;
        try {
          resp = await fetch('/api/join-league', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'coadmin',
              coAdminCode: coAdminCode.trim(), name: coAdminName.trim(), email: coAdminEmail.trim(), idToken,
              isCoach: coAdminIsCoach === true, coachRole: coAdminIsCoach === true ? coAdminCoachRole : null
            }),
            signal: controller.signal
          });
        } finally {
          clearTimeout(timeoutId);
        }
        console.log('[join-league] co-admin response received', { status: resp.status, ok: resp.ok });
        const joinResult = await resp.json();
        if (!resp.ok) throw new Error(joinResult.error || 'Failed to join league');
      } catch (joinErr) {
        // Log the FULL error -- including network failures, timeouts (name
        // 'AbortError'), and non-JSON responses (e.g. an auth wall returning
        // an HTML/JSON block instead of our function's real response) -- none
        // of which were visible before this, since only Firebase Auth errors
        // had logging/messaging.
        console.error('[join-league] co-admin request failed', { name: joinErr.name, message: joinErr.message, time: new Date().toISOString() });
        // The Auth account already exists at this point -- don't leave the user
        // stuck on a generic "signup failed" message that invites a retry (which
        // would only fail with email-already-in-use and compound the confusion,
        // as happened with the original incident).
        setAuthError("Your account was created, but we couldn't add you to the league. Please contact your administrator, or try signing in — your account already exists.");
        setAuthLoading(false);
        return;
      }
      const newCoAdmin = { uid: cred.user.uid, name: coAdminName.trim(), email: coAdminEmail.trim(), addedAt: new Date().toISOString(),
        isCoach: coAdminIsCoach === true, coachRole: coAdminIsCoach === true ? coAdminCoachRole : null, coachPromptSeen: true };
      // Notify main admin -- safe to stay client-side: by this point the
      // server-side join above has already committed, so the rules' co-admin
      // check (isLeagueCoAdmin) now sees this uid in coAdminUids and allows it.
      const updatedLeague = { ...leagueData, coAdmins: [...(leagueData.coAdmins || []), newCoAdmin], coAdminUids: [...(leagueData.coAdminUids || []), cred.user.uid] };
      setLeague(updatedLeague);
      setUser({ name: coAdminName.trim(), role: 'admin', leagueId: leagueDoc.id, isMainAdmin: false });
      // Write notification for admin
      const nid = "n" + Date.now();
      await setDoc(doc(db, 'leagues', leagueDoc.id, 'notifications', nid), {
        id: nid, type: 'coadmin_joined', message: coAdminName.trim() + ' has joined as a co-administrator.',
        forRole: 'admin', createdAt: new Date().toISOString(), readBy: []
      });
      setScreen('app');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('email-already-in-use')) setAuthError("An account with this email already exists.");
      else if (msg.includes('invalid-email')) setAuthError("Please enter a valid email address.");
      else setAuthError("Signup failed. Please try again.");
    }
    setAuthLoading(false);
  };

  // Listen to project tasks when a project is selected
  useEffect(() => {
    const leagueId = league ? league.id : null;
    if (!leagueId || !selectedProjectId) { setProjectTasks([]); setProjectArchive([]); return; }
    const unsubPT = onSnapshot(collection(db, 'leagues', leagueId, 'projects', selectedProjectId, 'tasks'), snap => {
      setProjectTasks(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    });
    const unsubPA = onSnapshot(collection(db, 'leagues', leagueId, 'projects', selectedProjectId, 'archive'), snap => {
      setProjectArchive(snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)));
    });
    return () => { unsubPT(); unsubPA(); };
  }, [selectedProjectId, league]);

  const loadPlatformData = async () => {
    const leaguesSnap = await getDocs(collection(db, 'leagues'));
    const leagueList = await Promise.all(leaguesSnap.docs.map(async (leagueDoc) => {
      const ld = leagueDoc.data();
      const [tasksSnap, archiveSnap, membersSnap] = await Promise.all([
        getDocs(collection(db, 'leagues', leagueDoc.id, 'tasks')),
        getDocs(collection(db, 'leagues', leagueDoc.id, 'archive')),
        getDocs(collection(db, 'leagues', leagueDoc.id, 'members'))
      ]);
      const coAdminCount = ld.coAdmins ? ld.coAdmins.length : 0;

      // ── Phase 2: ZERO ADDITIONAL READS ──────────────────────────────────
      // The three getDocs above already fetched every task and archive doc in
      // full; only `.size` was kept and the documents themselves discarded.
      // `dueDate` is on task docs and `completedAt` is on archive docs, so
      // both figures below are derived from data that has ALREADY been paid
      // for. Do not "optimise" either of these into its own count query --
      // that would add reads to replace reads that are already happening.
      //
      // OVERDUE PREDICATE IS COPIED, NOT REWRITTEN. The league app's task card
      // tests `new Date(task.dueDate) < new Date()`, and a bare "YYYY-MM-DD"
      // from an <input type="date"> parses as UTC midnight -- so in any zone
      // behind UTC a task due TODAY already reads as overdue there. That is
      // pre-existing behaviour and it is reproduced here deliberately: a
      // console that applied a stricter rule would report a different overdue
      // count than the admin sees on their own Tasks tab, which is exactly the
      // two-surfaces-disagree failure the byDateDesc work was cleaning up.
      // NaN from an unparseable date compares false, so bad data is not
      // counted rather than being counted arbitrarily.
      const nowMs = Date.now();
      const sevenDaysAgoMs = nowMs - 7 * 864e5;
      let overdueTasks = 0;
      tasksSnap.docs.forEach(d => {
        const due = d.data().dueDate;
        if (due && new Date(due).getTime() < nowMs) overdueTasks += 1;
      });
      let completedLast7 = 0;
      archiveSnap.docs.forEach(d => {
        const ms = dateMs(d.data().completedAt);
        if (!isNaN(ms) && ms >= sevenDaysAgoMs) completedLast7 += 1;
      });

      return {
        id: leagueDoc.id,
        leagueName: ld.leagueName,
        adminName: ld.adminName,
        adminEmail: ld.adminEmail,
        adminUid: ld.adminUid || null,
        ownerClaimCode: ld.ownerClaimCode || null,
        createdAt: ld.createdAt,
        plan: ld.plan || 'free',
        planActive: !!ld.planActive,
        stripeCustomerId: ld.stripeCustomerId || null,
        planSource: ld.planSource || null,
        planExpiresAt: ld.planExpiresAt || null,
        adminIsCoach: !!ld.adminIsCoach,
        adminCoachRole: ld.adminCoachRole || null,
        coAdminCount,
        coAdmins: ld.coAdmins || [],
        members: membersSnap.docs.map(d => ({ ...d.data(), id: d.id })).sort(byDateDesc('joinedAt')),
        volunteerCount: membersSnap.size,
        activeTasks: tasksSnap.size,
        completedTasks: archiveSnap.size,
        overdueTasks,
        completedLast7
      };
    }));
    let totalAdmins = 0, totalCoAdmins = 0, totalVolunteers = 0, totalTasks = 0, totalCompleted = 0;
    for (const l of leagueList) {
      if (l.adminUid) totalAdmins += 1;
      totalCoAdmins += l.coAdminCount;
      totalVolunteers += l.volunteerCount;
      totalTasks += l.activeTasks;
      totalCompleted += l.completedTasks;
    }
    const visitsDoc = await getDoc(doc(db, 'platformStats', 'visits'));
    const totalVisits = visitsDoc.exists() ? (visitsDoc.data().count || 0) : 0;
    setPlatformData({
      leagues: leagueList.slice().sort(byDateDesc('createdAt')),
      totalLeagues: leaguesSnap.size,
      totalAdmins,
      totalCoAdmins,
      totalVolunteers,
      totalTasks,
      totalCompleted,
      totalVisits
    });
  };

  // ─── PLATFORM STAT HISTORY (dailyStats) ──────────────────────────────────
  // Query-side support for the superadmin trend chart. Reads the
  // dailyStats/{YYYY-MM-DD} snapshots written by the capture-daily-stats.js
  // cron job. This IS wired into the chart UI now (the range chips and the
  // selected-stat series on the platform dashboard) -- an earlier version of
  // this comment said it wasn't, which was true when written and is not now.
  // "Today" is intentionally not a supported range here: the live
  // platformData already reflects right now, and today's snapshot may not
  // have run yet, so callers must show live data for "Today" instead of
  // querying this. There is deliberately no 'today' key in
  // DASHBOARD_STAT_RANGE_DAYS and loadStatHistory throws on unknown keys --
  // routing a "Today" chip through it is a bug, not a missing feature.
  const DASHBOARD_STAT_RANGE_DAYS = { '7d': 7, '1m': 30, '6m': 182, 'annual': 365 };

  // Matches the same value shown on the live stat cards -- "Admins" sums
  // adminUid-present leagues and co-admins together, so the historical
  // series has to combine the same two stored fields to stay consistent.
  const getDailyStatValue = (dailyStatDoc, statKey) => {
    if (statKey === 'totalAdmins') return dailyStatDoc.totalAdmins + dailyStatDoc.totalCoAdmins;
    return dailyStatDoc[statKey];
  };

  // rangeKey: '7d' | '1m' | '6m' | 'annual' | 'all'
  // Returns { docs: [dailyStats snapshot objects, oldest first], trackingSince: 'YYYY-MM-DD' | null }.
  // trackingSince is always the *overall* earliest snapshot that exists,
  // not just the earliest one inside the requested range -- so a 6M view
  // on a dataset that only goes back 10 days can still say "Tracking since
  // [10 days ago]" instead of showing an empty or misleadingly truncated
  // chart with no explanation.
  const loadStatHistory = async (rangeKey) => {
    let rangeQuery;
    if (rangeKey === 'all') {
      rangeQuery = query(collection(db, 'dailyStats'), orderBy(documentId(), 'asc'));
    } else {
      const days = DASHBOARD_STAT_RANGE_DAYS[rangeKey];
      if (!days) throw new Error(`Unknown stat history range: ${rangeKey}`);
      const start = new Date();
      start.setDate(start.getDate() - days);
      const startStr = start.toISOString().slice(0, 10);
      rangeQuery = query(collection(db, 'dailyStats'), where(documentId(), '>=', startStr), orderBy(documentId(), 'asc'));
    }
    const snap = await getDocs(rangeQuery);
    const docs = snap.docs.map(d => d.data());

    let trackingSince = null;
    if (rangeKey === 'all') {
      trackingSince = docs.length ? docs[0].date : null;
    } else {
      const earliestSnap = await getDocs(query(collection(db, 'dailyStats'), orderBy(documentId(), 'asc'), limit(1)));
      trackingSince = earliestSnap.empty ? null : earliestSnap.docs[0].data().date;
    }

    return { docs, trackingSince };
  };

  const handlePlatformLogin = async () => {
    if (!platformEmail.trim() || !platformPassword.trim()) {
      setPlatformError("Please enter your email and password.");
      return;
    }
    setPlatformError("");
    setPlatformLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, platformEmail.trim(), platformPassword);
      const superAdminDoc = await getDoc(doc(db, 'superAdmins', cred.user.uid));
      if (!superAdminDoc.exists()) {
        setPlatformError("This account is not authorized for platform admin access.");
        await signOut(auth);
        setPlatformLoading(false);
        return;
      }
      await loadPlatformData();
      setScreen('platform-dashboard');
      loadPromoCodes();
    } catch(e) {
      setPlatformError("Sign-in failed: " + e.message);
    }
    setPlatformLoading(false);
  };

  // ─── PROMO CODES (super admin) ────────────────────────────────
  const loadPromoCodes = async () => {
    setPromoCodesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'promoCodes'));
      setPromoCodes(snap.docs.map(d => d.data()).sort(byDateDesc('createdAt')));
    } catch (e) {
      console.error('loadPromoCodes error:', e);
    }
    setPromoCodesLoading(false);
  };

  // RETURNS true ON SUCCESS. The only reason it returns anything is so the
  // Phase 3 create-code drawer can close itself on success and STAY OPEN on
  // failure with the API's own error still on screen. The request payload is
  // untouched -- api/promo-codes owns the validation (type must be free_year
  // or percent_off; a percent_off code needs a value of 1-100; duplicate codes
  // 409) and its message is surfaced verbatim rather than pre-empted here,
  // because a client-side copy of those rules is a second source of truth that
  // can drift out of step with the endpoint.
  const handleCreatePromoCode = async () => {
    if (!promoForm.code.trim()) { setPromoCreateError('Please enter a code.'); return false; }
    setPromoCreating(true);
    setPromoCreateError('');
    let created = false;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('/api/promo-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          idToken,
          code: promoForm.code,
          type: promoForm.type,
          value: promoForm.value ? Number(promoForm.value) : null,
          maxRedemptions: promoForm.maxRedemptions ? Number(promoForm.maxRedemptions) : null,
          expiresAt: promoForm.expiresAt || null,
          restrictedToLeagueId: promoForm.restrictedToLeagueId || null,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        setPromoCreateError(result.error || 'Failed to create code.');
      } else {
        setPromoForm({ code: '', type: 'free_year', value: '', maxRedemptions: '', expiresAt: '', restrictedToLeagueId: '' });
        loadPromoCodes();
        created = true;
      }
    } catch (e) {
      setPromoCreateError(e.message);
    }
    setPromoCreating(false);
    return created;
  };

  const handleTogglePromoCode = async (code, active) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/api/promo-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'toggle', idToken, code, active }),
      });
      loadPromoCodes();
    } catch (e) {
      alert('Failed to update code: ' + e.message);
    }
  };

  // NO CONFIRM OF ITS OWN. This deletes immediately when called; the caller is
  // responsible for asking first. Phase 3 moved the native window.confirm that
  // used to sit here into SaConfirm at the call site, for the same reason the
  // three Leagues actions moved in Phase 2 -- and because the two cases the old
  // string distinguished (redeemed vs never redeemed) are genuinely different
  // decisions that deserve more room than a browser dialog gives them.
  const handleDeletePromoCode = async (code) => {
    setPromoDeletingCode(code);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('/api/promo-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', idToken, code }),
      });
      const result = await response.json();
      if (!response.ok) {
        alert('Failed to delete code: ' + (result.error || 'Unknown error'));
      } else {
        loadPromoCodes();
      }
    } catch (e) {
      alert('Failed to delete code: ' + e.message);
    }
    setPromoDeletingCode(null);
  };

  // ─── PROMO CODE REDEMPTION (league admin) ──────────────────────
  const handleRedeemPromoCode = async () => {
    if (!promoCodeInput.trim() || !lid) return;
    setPromoRedeemLoading(true);
    setPromoRedeemMessage('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('/api/promo-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'redeem', code: promoCodeInput, leagueId: lid, idToken }),
      });
      const result = await response.json();
      if (!response.ok) {
        setPromoRedeemMessage(result.error || 'Failed to redeem code.');
      } else {
        setPromoRedeemMessage('success');
        setPromoCodeInput('');
      }
    } catch (e) {
      setPromoRedeemMessage(e.message);
    }
    setPromoRedeemLoading(false);
  };

  const handleCreateLeaguePlatform = async () => {
    if (!newLeagueForm.name.trim()) { setCreateLeagueError('League name is required.'); return; }
    setCreatingLeague(true); setCreateLeagueError('');
    try {
      const leagueId = "league_" + Date.now();
      const volCode = generateCode();
      const caCode = generateCode();
      const ownerCode = generateCode();
      await setDoc(doc(db, 'leagues', leagueId), {
        leagueName: newLeagueForm.name.trim(), adminName: null, adminEmail: null, adminUid: null,
        leagueCode: volCode, coAdminCode: caCode, ownerClaimCode: ownerCode,
        coAdmins: [], coAdminUids: [], createdAt: new Date().toISOString(),
        locations: ["", "", "", "", ""], zipCode: newLeagueForm.zipCode.trim()
      });
      setJustCreatedLeague({ leagueName: newLeagueForm.name.trim(), claimCode: ownerCode });
      setNewLeagueForm({ name: '', zipCode: '' });
      await loadPlatformData();
    } catch (e) {
      setCreateLeagueError('Failed to create league: ' + e.message);
    }
    setCreatingLeague(false);
  };

  const deletePlatformLeague = async (leagueId) => {
    // Delete all subcollections then the league doc
    const subcols = ['tasks', 'archive', 'supplies', 'members', 'notifications'];
    const ops = [];
    for (const sub of subcols) {
      const snap = await getDocs(collection(db, 'leagues', leagueId, sub));
      snap.docs.forEach(d => ops.push((batch) => batch.delete(doc(db, 'leagues', leagueId, sub, d.id))));
    }
    ops.push((batch) => batch.delete(doc(db, 'leagues', leagueId)));
    await commitBatchedOps(db, ops);
    setPlatformData(prev => ({
      ...prev,
      leagues: prev.leagues.filter(l => l.id !== leagueId),
      totalLeagues: prev.totalLeagues - 1
    }));
  };

  const deletePlatformVolunteer = async (leagueId, memberId, leagueName) => {
    await deleteDoc(doc(db, 'leagues', leagueId, 'members', memberId));
    setPlatformData(prev => ({
      ...prev,
      totalVolunteers: prev.totalVolunteers - 1,
      leagues: prev.leagues.map(l => l.id !== leagueId ? l : {
        ...l,
        volunteerCount: l.volunteerCount - 1,
        members: l.members.filter(m => m.id !== memberId)
      })
    }));
  };

  const deletePlatformCoAdmin = async (leagueId, coAdminUid, coAdminName) => {
    const leagueRef = doc(db, 'leagues', leagueId);
    const leagueSnap = await getDoc(leagueRef);
    if (!leagueSnap.exists()) return;
    const ld = leagueSnap.data();
    const newCoAdmins = (ld.coAdmins || []).filter(ca => ca.uid !== coAdminUid);
    const newCoAdminUids = (ld.coAdminUids || []).filter(uid => uid !== coAdminUid);
    await updateDoc(leagueRef, { coAdmins: newCoAdmins, coAdminUids: newCoAdminUids });
    setPlatformData(prev => ({
      ...prev,
      totalCoAdmins: prev.totalCoAdmins - 1,
      leagues: prev.leagues.map(l => l.id !== leagueId ? l : {
        ...l,
        coAdminCount: l.coAdminCount - 1,
        coAdmins: l.coAdmins.filter(ca => ca.uid !== coAdminUid)
      })
    }));
  };

  // ─── SUPERADMIN: coach-flag corrections (superadmin-coach-edit-spec.md) ──
  // Only isCoach/coachRole are touched -- coachPromptSeen is deliberately left
  // as-is so a superadmin correction doesn't cause the member to be
  // re-prompted on their next login.
  const updatePlatformAdminCoachFlag = async (leagueId, { isCoach, coachRole }) => {
    const flagUpdate = { adminIsCoach: isCoach === true, adminCoachRole: isCoach === true ? coachRole : null };
    await updateDoc(doc(db, 'leagues', leagueId), flagUpdate);
    setPlatformData(prev => ({
      ...prev,
      leagues: prev.leagues.map(l => l.id !== leagueId ? l : { ...l, ...flagUpdate })
    }));
  };

  const updatePlatformCoAdminCoachFlag = async (leagueId, coAdminUid, { isCoach, coachRole }) => {
    const leagueRef = doc(db, 'leagues', leagueId);
    const leagueSnap = await getDoc(leagueRef);
    if (!leagueSnap.exists()) return;
    const ld = leagueSnap.data();
    const updatedCoAdmins = (ld.coAdmins || []).map(ca =>
      ca.uid === coAdminUid ? { ...ca, isCoach: isCoach === true, coachRole: isCoach === true ? coachRole : null } : ca
    );
    await updateDoc(leagueRef, { coAdmins: updatedCoAdmins });
    setPlatformData(prev => ({
      ...prev,
      leagues: prev.leagues.map(l => l.id !== leagueId ? l : { ...l, coAdmins: updatedCoAdmins })
    }));
  };

  const updatePlatformMemberCoachFlag = async (leagueId, memberId, { isCoach, coachRole }) => {
    const flagUpdate = { isCoach: isCoach === true, coachRole: isCoach === true ? coachRole : null };
    await updateDoc(doc(db, 'leagues', leagueId, 'members', memberId), flagUpdate);
    setPlatformData(prev => ({
      ...prev,
      leagues: prev.leagues.map(l => l.id !== leagueId ? l : {
        ...l,
        members: l.members.map(m => m.id !== memberId ? m : { ...m, ...flagUpdate })
      })
    }));
  };

  const openPlatformCoachEdit = (type, leagueId, uid, name, leagueName, currentIsCoach, currentCoachRole) => {
    setPlatformCoachEdit({ type, leagueId, uid, name, leagueName });
    setPlatformCoachEditIsCoach(!!currentIsCoach);
    setPlatformCoachEditCoachRole(currentCoachRole || null);
  };

  const savePlatformCoachEdit = async (isCoach, coachRole) => {
    if (!platformCoachEdit) return;
    setPlatformCoachEditSaving(true);
    try {
      if (platformCoachEdit.type === 'admin') {
        await updatePlatformAdminCoachFlag(platformCoachEdit.leagueId, { isCoach, coachRole });
      } else if (platformCoachEdit.type === 'coadmin') {
        await updatePlatformCoAdminCoachFlag(platformCoachEdit.leagueId, platformCoachEdit.uid, { isCoach, coachRole });
      } else if (platformCoachEdit.type === 'volunteer') {
        await updatePlatformMemberCoachFlag(platformCoachEdit.leagueId, platformCoachEdit.uid, { isCoach, coachRole });
      }
    } catch (e) {
      // non-critical -- edit can simply be retried
    }
    setPlatformCoachEditSaving(false);
    setPlatformCoachEdit(null);
  };

  // "No" is a complete answer -- save immediately, matching the original
  // coach-flag feature's behavior (handleCoachPromptChangeIsCoach).
  const handlePlatformCoachEditChangeIsCoach = (val) => {
    setPlatformCoachEditIsCoach(val);
    if (val === false) savePlatformCoachEdit(false, null);
  };

  // DISMISSING THE SUPERADMIN COACH EDITOR IS ALWAYS A CANCEL. Escape, the
  // scrim and the ✕ discard; only "Save" commits a Yes. "No" still writes
  // immediately from handlePlatformCoachEditChangeIsCoach above, because "No"
  // is a complete answer in itself and there is nothing left to confirm.
  //
  // THIS DELIBERATELY DIVERGES FROM closeCoachPrompt, WHICH COMMITS ON
  // DISMISS. Do not "restore consistency" between the two -- they are correct
  // for opposite reasons, and the two functions share no state:
  //   - closeCoachPrompt is the person answering about THEMSELVES at signup or
  //     login. Discarding an answer they already gave would lose their own
  //     input, so committing on dismiss is right there and stays.
  //   - this is a superadmin editing SOMEONE ELSE'S record, in a UI where
  //     SaConfirm has already taught that dismissing a drawer means nothing
  //     happened. SaCoachEdit is the same shape, so committing on dismiss
  //     would mean opening a person's record to look at it, clicking Yes to
  //     see the role options, pressing Escape, and silently writing to another
  //     person's record with nothing on screen saying so.
  //
  // The saving guard matches SaConfirm's busy behaviour: a dismiss mid-write
  // cannot pull the drawer out from under an in-flight save.
  const closePlatformCoachEdit = () => {
    if (platformCoachEditSaving) return;
    setPlatformCoachEdit(null);
  };

  // ─── PROJECT ACTIONS ────────────────────────────────────────────
  const createProject = async () => {
    if (!newProject.name.trim()) return;
    const id = "proj_" + Date.now();
    await setDoc(doc(db, 'leagues', lid, 'projects', id), {
      id, name: newProject.name.trim(), description: newProject.description.trim(),
      createdBy: user.name, createdAt: new Date().toISOString(), taskCount: 0
    });
    setNewProject({ name: "", description: "" });
    setShowNewProject(false);
  };

  const deleteProject = async (projectId) => {
    const tasksSnap = await getDocs(collection(db, 'leagues', lid, 'projects', projectId, 'tasks'));
    const archSnap = await getDocs(collection(db, 'leagues', lid, 'projects', projectId, 'archive'));
    const ops = [
      ...tasksSnap.docs.map(d => (batch) => batch.delete(doc(db, 'leagues', lid, 'projects', projectId, 'tasks', d.id))),
      ...archSnap.docs.map(d => (batch) => batch.delete(doc(db, 'leagues', lid, 'projects', projectId, 'archive', d.id))),
      (batch) => batch.delete(doc(db, 'leagues', lid, 'projects', projectId))
    ];
    await commitBatchedOps(db, ops);
    setSelectedProjectId(null);
  };

  const createProjectTask = async () => {
    const leagueId = league ? league.id : null;
    if (!newProjectTask.title.trim() || !selectedProjectId || !leagueId) return;
    const id = "pt_" + Date.now();
    await setDoc(doc(db, 'leagues', leagueId, 'projects', selectedProjectId, 'tasks', id), {
      id, ...newProjectTask, createdBy: user.name, createdAt: new Date().toISOString(),
      assignedTo: null, status: "open", updates: [], completedAt: null
    });
    await createNotification('task_created', 'New project task: "' + newProjectTask.title.trim() + '"', 'volunteer');
    setNewProjectTask({ title: "", description: "", location: "", priority: "medium", dueDate: "" });
    setShowNewProjectTask(false);
  };

  const claimProjectTask = async (taskId) => {
    const task = projectTasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: user.name + " claimed this task.", author: user.name, timestamp: ts, system: true }]);
    await updateDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), { assignedTo: user.name, status: "in-progress", updates });
    await createNotification('task_claimed', user.name + ' claimed project task: "' + task.title + '"', 'admin');
  };

  const unclaimProjectTask = async (taskId) => {
    const task = projectTasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: user.name + " unclaimed this task.", author: user.name, timestamp: ts, system: true }]);
    await updateDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), { assignedTo: null, status: "open", updates });
  };

  const assignProjectTask = async (taskId, assigneeName) => {
    const task = projectTasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{
      message: "Task assigned to " + assigneeName + " by " + user.name + ".",
      author: user.name, timestamp: ts, system: true
    }]);
    await updateDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), {
      assignedTo: assigneeName,
      status: "in-progress",
      updates
    });
    await createNotification('task_claimed', user.name + ' assigned project task "' + task.title + '" to ' + assigneeName, 'admin');
  };

  const sendProjectTaskUpdate = async (taskId) => {
    if (!projectUpdateText.trim()) return;
    const task = projectTasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: projectUpdateText.trim(), author: user.name, timestamp: ts }]);
    await updateDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), { updates });
    setProjectUpdateText("");
  };

  const completeProjectTask = async (taskId) => {
    const task = projectTasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const done = Object.assign({}, task, {
      status: "complete", completedAt: ts,
      duration: Date.now() - new Date(task.createdAt).getTime(),
      updates: (task.updates || []).concat([{ message: "Task marked complete by " + user.name + ".", author: user.name, timestamp: ts, system: true }])
    });
    await setDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'archive', taskId), done);
    await deleteDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId));
    setSelectedProjectTaskId(null);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
  };

  const deleteProjectTask = async (taskId) => {
    await deleteDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId));
    setSelectedProjectTaskId(null);
  };

  const updateProjectTask = async (taskId, changes) => {
    await updateDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), changes);
    setEditingProjectTask(null);
  };

  const restoreProjectTask = async (taskId) => {
    const task = projectArchive.find(t => t.id === taskId);
    if (!task) return;
    const restored = Object.assign({}, task, { status: "open", assignedTo: null, completedAt: null, duration: null,
      updates: (task.updates || []).concat([{ message: "Task restored by " + user.name + ".", author: user.name, timestamp: new Date().toISOString(), system: true }]) });
    await setDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'tasks', taskId), restored);
    await deleteDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'archive', taskId));
  };

  const deleteArchivedProjectTask = async (taskId) => {
    await deleteDoc(doc(db, 'leagues', lid, 'projects', selectedProjectId, 'archive', taskId));
  };

  // ─── WEATHER ──────────────────────────────────────────────────────
  const weatherCodeInfo = (code) => {
    if (code === 0) return { label: "Clear Sky", icon: "☀️" };
    if (code <= 3) return { label: "Partly Cloudy", icon: "⛅" };
    if (code <= 48) return { label: "Foggy", icon: "🌫️" };
    if (code <= 55) return { label: "Drizzle", icon: "🌦️" };
    if (code <= 65) return { label: "Rain", icon: "🌧️" };
    if (code <= 75) return { label: "Snow", icon: "❄️" };
    if (code <= 82) return { label: "Rain Showers", icon: "🌦️" };
    if (code <= 99) return { label: "Thunderstorm", icon: "⛈️" };
    return { label: "Unknown", icon: "🌡️" };
  };

  const saveGroups = async (grps) => {
    const cleaned = grps.filter(g => g && g.trim());
    await updateDoc(doc(db, 'leagues', lid), { groups: cleaned });
    setLeagueGroups(cleaned);
    setEditingGroups(null);
    setGroupsSaved(true);
    setTimeout(() => setGroupsSaved(false), 2000);
  };

  const saveLocations = async (locs) => {
    if (!lid) return;
    await updateDoc(doc(db, 'leagues', lid), { locations: locs });
    setLeagueLocations(locs);
    setLocationsSaved(true);
    setTimeout(() => setLocationsSaved(false), 2500);
    setEditingLocations(null);
  };

  const suggestAvailableSlug = async (base) => {
    let candidate = slugify(base) || 'league';
    if (candidate.length < 3) candidate = candidate + '-league';
    let n = 2;
    while (true) {
      const snap = await getDoc(doc(db, 'leagueSlugs', candidate));
      if (!snap.exists()) return candidate;
      candidate = slugify(base) + '-' + n;
      n++;
    }
  };

  const startEditSlug = async () => {
    setSlugError('');
    setEditingSlug(true);
    if (league && league.slug) {
      setLeagueSlugInput(league.slug);
    } else {
      setSlugSuggesting(true);
      const suggestion = await suggestAvailableSlug(league && league.leagueName);
      setLeagueSlugInput(suggestion);
      setSlugSuggesting(false);
    }
  };

  const saveLeagueSlug = async () => {
    if (!lid) return;
    const candidate = slugify(leagueSlugInput);
    setSlugError('');
    if (candidate.length < 3 || candidate.length > 60) {
      setSlugError('Link name must be 3\u201360 characters (letters, numbers, and hyphens).');
      return;
    }
    const applyChange = async () => {
      setSlugSaving(true);
      try {
        const oldSlug = league && league.slug;
        await runTransaction(db, async (tx) => {
          const newSlugRef = doc(db, 'leagueSlugs', candidate);
          const newSlugSnap = await tx.get(newSlugRef);
          if (newSlugSnap.exists() && newSlugSnap.data().leagueId !== lid) {
            throw new Error('TAKEN');
          }
          tx.set(newSlugRef, { leagueId: lid });
          if (oldSlug && oldSlug !== candidate) {
            tx.delete(doc(db, 'leagueSlugs', oldSlug));
          }
          tx.update(doc(db, 'leagues', lid), { slug: candidate });
        });
        setSlugSaved(true);
        setEditingSlug(false);
        setTimeout(() => setSlugSaved(false), 2500);
        // No manual setLeague() call needed -- the live onSnapshot listener on
        // leagues/{lid} picks up the new slug automatically, same as
        // saveLocations never manually touches `league` either.
      } catch (e) {
        setSlugError(e.message === 'TAKEN' ? 'That link name is already taken \u2014 try another.' : 'Could not save. Please try again.');
      }
      setSlugSaving(false);
    };
    if (league && league.slug && league.slug !== candidate) {
      showConfirm(
        'Change public registration link?',
        'Changing this will break any previously shared link \u2014 parents using the old link will get a "not found" page. Continue?',
        applyChange,
        { danger: true, confirmLabel: 'Change Link' }
      );
    } else {
      applyChange();
    }
  };

  const saveZipCode = async () => {
    if (!lid || !zipCode.trim()) return;
    await updateDoc(doc(db, 'leagues', lid), { zipCode: zipCode.trim() });
    setZipSaved(true);
    setTimeout(() => setZipSaved(false), 2500);
    fetchWeather(zipCode.trim());
  };

  const fetchWeather = async (zip) => {
    if (!zip) return;
    setWeatherLoading(true);
    setWeatherError('');
    try {
      // Get coordinates from zip code
      const geoRes = await fetch("https://api.zippopotam.us/us/" + zip);
      if (!geoRes.ok) { setWeatherError("Zip code not found. Please check and try again."); setWeatherLoading(false); return; }
      const geoData = await geoRes.json();
      const lat = parseFloat(geoData.places[0].latitude);
      const lng = parseFloat(geoData.places[0].longitude);
      const city = geoData.places[0]["place name"] + ", " + geoData.places[0]["state abbreviation"];

      // Fetch hourly forecast from Open-Meteo
      const wxRes = await fetch(
        "https://api.open-meteo.com/v1/forecast?latitude=" + lat + "&longitude=" + lng +
        "&hourly=temperature_2m,precipitation_probability,weathercode,windspeed_10m,apparent_temperature" +
        "&daily=temperature_2m_max,temperature_2m_min,weathercode,precipitation_probability_max,sunrise,sunset" +
        "&temperature_unit=fahrenheit&windspeed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=3"
      );
      const wxData = await wxRes.json();
      setWeatherData({ ...wxData, city, lat, lng });
    } catch(e) {
      setWeatherError("Could not load weather. Please try again.");
    }
    setWeatherLoading(false);
  };

  // Auto-fetch weather when league loads with saved zip
  React.useEffect(() => {
    if (league && league.zipCode && !weatherData && !weatherLoading) {
      fetchWeather(league.zipCode);
    }
  }, [league]);

  // ─── BOARD ACTIONS ──────────────────────────────────────────────
  const createBoardPost = async () => {
    if (!newPost.body.trim()) return;
    const id = "post_" + Date.now();
    const isImportant = !!newPost.important;
    await setDoc(doc(db, 'leagues', lid, 'board', id), {
      id, title: newPost.title.trim(), body: newPost.body.trim(),
      author: user.name, createdAt: new Date().toISOString(), important: isImportant
    });
    setNewPost({ title: "", body: "", important: false });
    const importantPrefix = isImportant ? "🚩 Important: " : "";
    const gmMsg = "📌 New Board Post" + (newPost.title.trim() ? ": " + newPost.title.trim() : "") + "\n" + importantPrefix + newPost.body.trim() + "\n— Posted by " + user.name;
    if (hasPro) {
      sendGroupMe(gmMsg, league && league.groupmeBotId);
      const memberEmails = (members || []).map(m => m.email).filter(Boolean);
      if (memberEmails.length > 0) {
        sendEmail('board_announcement', memberEmails, {
          leagueName: league ? (league.leagueName || 'Your League') : 'Your League',
          postTitle: (isImportant ? "🚩 Important: " : "") + (newPost.title.trim() || 'New Announcement'),
          postBody: newPost.body.trim(),
          authorName: user.name
        });
      }
    }
    setShowNewPost(false);
  };

  const deleteBoardPost = async (postId) => {
    await deleteDoc(doc(db, 'leagues', lid, 'board', postId));
  };

  // ─── REQUEST INSTRUCTIONS ────────────────────────────────────────
  const requestInstructions = async (taskId, taskTitle) => {
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const msg = user.name + " is requesting more instructions for this task.";
    const updates = (task.updates || []).concat([{ message: msg, author: user.name, timestamp: ts, system: true, type: "instruction_request" }]);
    await updateDoc(doc(db, 'leagues', lid, 'tasks', taskId), { updates });
    await createNotification('task_claimed', "❓ " + user.name + ' needs more instructions for: "' + taskTitle + '"', 'admin');
  };

  const removeVolunteer = async (memberId) => {
    if (!lid) return;
    await deleteDoc(doc(db, 'leagues', lid, 'members', memberId));
  };

  // Renaming only updates the member doc itself -- task assignedTo and other
  // places that reference this person still store their OLD name as a plain
  // string, so existing task history won't follow the rename automatically.
  const saveMemberName = async (memberId) => {
    const trimmed = editingMemberName.trim();
    if (!trimmed || !lid) { setEditingMemberId(null); return; }
    await updateDoc(doc(db, 'leagues', lid, 'members', memberId), { name: trimmed });
    setEditingMemberId(null);
  };

  const saveCoAdminName = async (uid) => {
    const trimmed = editingMemberName.trim();
    if (!trimmed || !lid || !league) { setEditingMemberId(null); return; }
    const updated = (league.coAdmins || []).map(ca => ca.uid === uid ? { ...ca, name: trimmed } : ca);
    await updateDoc(doc(db, 'leagues', lid), { coAdmins: updated });
    setEditingMemberId(null);
  };

  // ─── SUPERADMIN: coach-identification CSV export ────────────────────
  // Exports every admin/co-admin/volunteer across every league, with their
  // coach-identification status (coach-flag-feature-spec.md), for the
  // platform owner. Includes everyone (not just confirmed coaches) with a
  // Yes/No column so it can be filtered either way in a spreadsheet.
  const exportPlatformCoachesCSV = () => {
    if (!platformData || !platformData.leagues) return;
    const rows = [['League', 'Name', 'Email', 'Role', 'Is Coach', 'Coach Role']];
    const roleLabel = (r) => r === 'head' ? 'Head Coach' : r === 'assistant' ? 'Assistant Coach' : '';
    platformData.leagues.forEach(lg => {
      if (lg.adminUid) {
        rows.push([lg.leagueName, lg.adminName, lg.adminEmail, 'Admin', lg.adminIsCoach ? 'Yes' : 'No', lg.adminIsCoach ? roleLabel(lg.adminCoachRole) : '']);
      }
      (lg.coAdmins || []).forEach(ca => {
        rows.push([lg.leagueName, ca.name, ca.email, 'Co-Admin', ca.isCoach ? 'Yes' : 'No', ca.isCoach ? roleLabel(ca.coachRole) : '']);
      });
      (lg.members || []).forEach(m => {
        rows.push([lg.leagueName, m.name, m.email || '', 'Volunteer', m.isCoach ? 'Yes' : 'No', m.isCoach ? roleLabel(m.coachRole) : '']);
      });
    });
    downloadCSV('groundspal-coaches-' + new Date().toISOString().slice(0,10) + '.csv', rows);
  };

  const exportCrewStatsCSV = (memberStats) => {
    if (!hasLeague) {
      alert('CSV export is a League-tier feature. Upgrade to League to unlock it.');
      return;
    }
    const rows = [
      ['Name', 'Role', 'Tasks Completed', 'Volunteer Event Hours', 'Last Task Completed', 'Last Completed Date'],
      ...memberStats.map(m => [
        m.name,
        m.role,
        m.completed,
        m.eventHours,
        m.lastCompleted ? m.lastCompleted.title : '',
        m.lastCompleted ? new Date(m.lastCompleted.completedAt || m.lastCompleted.createdAt).toLocaleDateString('en-US') : ''
      ])
    ];
    const leagueSlug = (league && league.leagueName ? league.leagueName : 'league').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    downloadCSV('groundspal-crew-stats-' + leagueSlug + '-' + new Date().toISOString().slice(0,10) + '.csv', rows);
  };

  const handleEmulateLeague = async (leagueId) => {
    try {
      const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
      if (!leagueDoc.exists()) { alert('League not found.'); return; }
      const data = leagueDoc.data();
      // Push a history entry OF OUR OWN for the emulation session. This is what
      // makes the "URL always agrees with the console" invariant structural
      // rather than conventional: without it, the first Back popped whatever
      // sat beneath the console -- possibly a pre-console entry like '/' --
      // and left the console rendered at a URL it does not own, so a refresh
      // then dropped the superadmin out of the console entirely.
      //
      // The entry deliberately reuses the CURRENT superadmin path rather than
      // inventing an /emulating route, which buys two things: the entry
      // underneath is guaranteed to be a superadmin path (Emulate is only
      // reachable from inside the console), and a refresh mid-emulation lands
      // on a real console tab. The fallback covers the theoretically-impossible
      // case of arriving here from off-console.
      const returnPath = isSuperadminPath() ? window.location.pathname : '/superadmin/leagues';
      window.history.pushState({ saEmulating: true }, '', returnPath);
      setLeague({ id: leagueDoc.id, ...data });
      setUser({ name: 'Super Admin', role: 'admin', leagueId, isMainAdmin: true, isEmulating: true });
      setEmulating(true);
      setPromoRedeemMessage(''); setPromoCodeInput('');
      setScreen('app');
    } catch (e) {
      alert('Failed to load league: ' + e.message);
    }
  };

  const handleSignOut = async () => {
    // Sign-out is a same-tab SPA state change, not a real page unload, so the
    // visibilitychange/pagehide safety net above won't catch it -- flush
    // explicitly here too, otherwise a field closure or event decision made
    // right before signing out could sit in the 2-minute queue and never send.
    flushPendingNotifyBatches();
    if (emulating) {
      // Exiting emulation returns to the platform console -- never signs out of the
      // real super-admin Firebase session, since that would kick you out of /superadmin too.
      setUser(null); setLeague(null); setEmulating(false); setScreen('platform-dashboard');
      setTasks([]); setArchive([]); setSupplies([]); setNotifications([]);
      return;
    }
    await signOut(auth);
    setUser(null); setLeague(null); setScreen('landing');
    setTasks([]); setArchive([]); setSupplies([]); setNotifications([]);
    setMyLeagues([]); setPendingLeagueChoices(null); setShowLeagueSwitcher(false);
    setAdminEmail(""); setAdminPassword(""); setAdminName(""); setLeagueName("");
    setVolName(""); setVolCode(""); setVolEmail(""); setCoAdminName(""); setCoAdminEmail(""); setCoAdminPassword(""); setCoAdminCode(""); setSigninEmail(""); setSigninError("");
    setVolSignupPassword(""); setVolLoginPassword(""); setVolNeedsPasswordSetup(false); setVolSetupPassword(""); setVolSetupAccount(null);
    setAuthError("");
    setAdminIsCoach(undefined); setAdminCoachRole(null); setVolIsCoach(undefined); setVolCoachRole(null); setCoAdminIsCoach(undefined); setCoAdminCoachRole(null);
    setShowCoachPrompt(false); setCoachPromptFor(null); setCoachPromptIsCoach(undefined); setCoachPromptCoachRole(null);
    coachPromptCheckedRef.current = null;
  };

  // Enters a specific league from a normalized choice — used by the post-login
  // picker (when someone belongs to more than one league) and by the in-app
  // switcher (moving between leagues without a full sign-out).
  const enterLeague = async (choice) => {
    try {
      const leagueDoc = await getDoc(doc(db, 'leagues', choice.leagueId));
      if (!leagueDoc.exists()) return;
      const leagueData = { id: leagueDoc.id, ...leagueDoc.data() };
      setLeague(leagueData);
      if (choice.role === 'volunteer') {
        setUser({ name: choice.name, role: 'volunteer', leagueId: choice.leagueId, email: choice.email || null });
      } else {
        setUser({ name: choice.name, role: 'admin', leagueId: choice.leagueId, isMainAdmin: choice.isMainAdmin });
      }
      // Clear previous league's data so nothing stale flashes before the new
      // league's onSnapshot listeners catch up — same pattern as sign-out.
      setTasks([]); setArchive([]); setSupplies([]); setNotifications([]);
      setPromoRedeemMessage(''); setPromoCodeInput('');
      setScreen('app');
      setShowLeagueSwitcher(false);
      setPendingLeagueChoices(null);
    } catch (e) {}
  };

  const lid = league ? league.id : null;

  // ─── RECURRING EVENT REQUESTS: generateRecurringDates helper ──────
  // Generates a sorted list of 'YYYY-MM-DD' date strings for a recurrence
  // definition. Pure function (no state/Firestore access) so it can drive
  // both the New/Request Event form's live preview and the final submission
  // from one source of truth. Capped at MAX_RECURRING_OCCURRENCES so a
  // "daily, no end date" style request can't generate an unbounded list.
  const MAX_RECURRING_OCCURRENCES = 52;
  const recurLocalDateStr = (d) => { const yr = d.getFullYear(); const mo = String(d.getMonth() + 1).padStart(2, '0'); const day = String(d.getDate()).padStart(2, '0'); return `${yr}-${mo}-${day}`; };
  const generateRecurringDates = (pattern, daysOfWeek, startDate, endDate) => {
    if (!startDate || !endDate || !daysOfWeek || daysOfWeek.length === 0) return [];
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
    const dates = [];
    if (pattern === 'monthly') {
      const dayOfMonth = start.getDate();
      let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end && dates.length < MAX_RECURRING_OCCURRENCES) {
        const candidate = new Date(cursor.getFullYear(), cursor.getMonth(), dayOfMonth);
        if (candidate.getMonth() === cursor.getMonth() && candidate >= start && candidate <= end) dates.push(recurLocalDateStr(candidate));
        cursor.setMonth(cursor.getMonth() + 1);
      }
    } else {
      // weekly / biweekly / custom all walk week-by-week picking matching
      // weekdays; biweekly advances two weeks at a time instead of one.
      const stepDays = pattern === 'biweekly' ? 14 : 7;
      let weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // back up to that week's Sunday
      while (weekStart <= end && dates.length < MAX_RECURRING_OCCURRENCES) {
        daysOfWeek.forEach(dow => {
          if (dates.length >= MAX_RECURRING_OCCURRENCES) return;
          const candidate = new Date(weekStart);
          candidate.setDate(candidate.getDate() + dow);
          if (candidate >= start && candidate <= end) dates.push(recurLocalDateStr(candidate));
        });
        weekStart.setDate(weekStart.getDate() + stepDays);
      }
    }
    return [...new Set(dates)].sort();
  };

  // Live conflict pre-check for the New/Request Event form's date preview
  // (manually-entered or recurrence-generated), so a requester can see and
  // remove conflicting dates before submitting instead of only finding out
  // at submit time. Mirrors the Events tab's own hasConflict/findGameConflicts
  // logic (duplicated here since those are scoped inside the calendar tab's
  // render function, not reachable from a top-level hook) -- same pattern
  // this file already uses for small per-scope helpers like timeToMins/fmtTime.
  useEffect(() => {
    if (!showEventForm || !lid) { return; }
    const pTimeToMins = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const previewDates = newEventRecurrence.enabled
      ? generateRecurringDates(newEventRecurrence.pattern, newEventRecurrence.daysOfWeek, newEventRecurrence.startDate, newEventRecurrence.endDate)
      : (newEvent.dates || []).filter(Boolean);
    if (previewDates.length === 0) { setDatesConflictInfo({}); return; }
    const field = newEvent.field || (leagueLocations.filter(l => l && l.trim())[0]) || '';
    let cancelled = false;
    (async () => {
      let currentSeasonDivisionIds = null;
      if (currentSeasonId) {
        try {
          const divSnap = await getDocs(query(collection(db, 'leagues', lid, 'divisions'), where('seasonId', '==', currentSeasonId)));
          currentSeasonDivisionIds = divSnap.docs.map(d => d.id);
        } catch (e) { currentSeasonDivisionIds = null; }
      }
      const info = {};
      const excludeId = editingEvent ? editingEvent.id : null;
      for (const date of previewDates) {
        if (cancelled) return;
        const eventConflict = fieldEvents.some(e => e.field === field && e.date === date && e.id !== excludeId && e.status === 'approved' &&
          pTimeToMins(newEvent.startTime) < pTimeToMins(e.endTime || '23:59') && pTimeToMins(newEvent.endTime || '23:59') > pTimeToMins(e.startTime || '00:00'));
        let gameConflict = false;
        if (!eventConflict) {
          try {
            const snap = await getDocs(query(collection(db, 'leagues', lid, 'games'), where('location', '==', field), where('date', '==', date)));
            const matches = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => pTimeToMins(newEvent.startTime) < pTimeToMins(g.endTime || '23:59') && pTimeToMins(newEvent.endTime || '23:59') > pTimeToMins(g.startTime || '00:00'));
            gameConflict = currentSeasonDivisionIds ? matches.some(g => currentSeasonDivisionIds.includes(g.divisionId)) : matches.length > 0;
          } catch (e) { /* preview-only lookup -- a failed check just skips the warning, submission re-verifies for real */ }
        }
        if (eventConflict || gameConflict) info[date] = { conflict: true, source: eventConflict ? 'event' : 'game' };
      }
      if (!cancelled) setDatesConflictInfo(info);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEventForm, lid, newEvent.field, newEvent.startTime, newEvent.endTime, JSON.stringify(newEvent.dates), newEventRecurrence.enabled, newEventRecurrence.pattern, JSON.stringify(newEventRecurrence.daysOfWeek), newEventRecurrence.startDate, newEventRecurrence.endDate, fieldEvents, currentSeasonId, editingEvent]);

  // ─── EVENT REQUEST APPROVE/REJECT ─────────────────────────────────
  // Lives at top level (not inside the Calendar tab's IIFE) so the header's
  // always-rendered NotificationsPanel can drive approve/decline directly from
  // a grouped request notification, not just from the Calendar tab itself.
  // notifyIfGroupResolved fires exactly one consolidated notification back to
  // the requester once every date in a requestGroupId has been reviewed
  // (approved or rejected/deleted) -- not per-date, not a running tally.
  const notifyIfGroupResolved = async (ev) => {
    if (!ev || !ev.requestGroupId || !lid) return;
    const pendingSnap = await getDocs(query(collection(db, 'leagues', lid, 'fieldEvents'), where('requestGroupId', '==', ev.requestGroupId), where('status', '==', 'pending')));
    if (pendingSnap.size > 0) return; // other dates in this request are still awaiting review
    await createNotification('event_request_resolved', '📋 Your event request "' + ev.title + '" has been fully reviewed.', 'volunteer');
  };
  const approveTimeToMins = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  // Combined conflict check run right before approving a pending event
  // request: other already-approved fieldEvents AND Scheduler games at the
  // same location/date with an overlapping time, regardless of event type
  // (a game and a practice can't share a slot any more than two games can).
  // Checks the games collection directly (not just fieldEvents) since a
  // Scheduler game doesn't have to be published to the calendar to hold its
  // time slot -- see findGameConflicts in the Calendar tab for the same
  // pattern used at event-creation time.
  const findApprovalConflicts = async (ev) => {
    const evConflicts = fieldEvents.filter(e => e.status === 'approved' && e.field === ev.field && e.date === ev.date && e.id !== ev.id &&
      approveTimeToMins(ev.startTime) < approveTimeToMins(e.endTime || '23:59') && approveTimeToMins(ev.endTime || '23:59') > approveTimeToMins(e.startTime || '00:00'));
    const snap = await getDocs(query(collection(db, 'leagues', lid, 'games'), where('location', '==', ev.field), where('date', '==', ev.date)));
    let gmConflicts = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g =>
      approveTimeToMins(ev.startTime) < approveTimeToMins(g.endTime || '23:59') && approveTimeToMins(ev.endTime || '23:59') > approveTimeToMins(g.startTime || '00:00') && g.calendarEventId !== ev.id);
    if (gmConflicts.length > 0 && currentSeasonId) {
      const divSnap = await getDocs(query(collection(db, 'leagues', lid, 'divisions'), where('seasonId', '==', currentSeasonId)));
      const currentSeasonDivisionIds = divSnap.docs.map(d => d.id);
      gmConflicts = gmConflicts.filter(g => currentSeasonDivisionIds.includes(g.divisionId));
    }
    return [
      ...evConflicts.map(e => ({ ...e, _source: 'event' })),
      ...gmConflicts.filter(g => !evConflicts.some(e => e.schedulerGameId === g.id)).map(g => ({ ...g, _source: 'game' })),
    ];
  };
  const finalizeApproveEvent = async (ev) => {
    await updateDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id), { status: 'approved' });
    if (ev.requestGroupId) { await notifyIfGroupResolved(ev); }
    else { await createNotification('event_approved', '✅ Your event "' + ev.title + '" on ' + ev.date + ' has been approved!', 'volunteer'); }
    const creator = members.find(m => m.name === ev.createdBy);
    triggerNotify('event', { action: 'approved', evData: { ...ev, creatorEmail: creator ? creator.email : null, creatorPhone: creator ? creator.phone : null, creatorSmsConsent: !!(creator && creator.smsConsent) } });
  };
  // Returns 'conflict' (and pauses on the resolution modal) when a live
  // conflict is found instead of approving immediately -- callers that loop
  // over multiple dates (approveEventGroupAll) check for this return value
  // and stop, since only one conflict modal can be shown at a time.
  const approveEvent = async (id) => {
    const ev = fieldEvents.find(e => e.id === id);
    if (!ev) return;
    const conflicts = await findApprovalConflicts(ev);
    if (conflicts.length > 0) {
      setPendingApproveConflict(ev);
      setPendingApproveConflictItems(conflicts);
      setPendingApproveOverrideIds(conflicts.map(c => c.id));
      return 'conflict';
    }
    await finalizeApproveEvent(ev);
  };
  const resolveApproveConflictAndApprove = async () => {
    if (!pendingApproveConflict || approvingConflict) return;
    if (pendingApproveOverrideIds.length < pendingApproveConflictItems.length) return; // every listed conflict must be resolved (checked) before this can be approved
    setApprovingConflict(true);
    try {
      const ev = pendingApproveConflict;
      for (const c of pendingApproveConflictItems) {
        if (c._source === 'event') {
          const creator = members.find(m => m.name === c.createdBy);
          await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', c.id));
          triggerNotify('event', { action: 'deleted', evData: { ...c, creatorEmail: creator ? creator.email : null } });
        } else if (c._source === 'game') {
          if (c.calendarEventId) { try { await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', c.calendarEventId)); } catch (e) {} }
          await deleteDoc(doc(db, 'leagues', lid, 'games', c.id));
        }
      }
      setPendingApproveConflict(null);
      setPendingApproveConflictItems([]);
      setPendingApproveOverrideIds([]);
      await finalizeApproveEvent(ev);
    } finally {
      setApprovingConflict(false);
    }
  };
  const rejectEvent = async (id) => {
    const ev = fieldEvents.find(e => e.id === id);
    showConfirm('Reject Event', 'This event request will be rejected and deleted. The volunteer will be notified.', async () => {
      await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', id));
      if (ev) {
        if (ev.requestGroupId) { await notifyIfGroupResolved(ev); }
        else { await createNotification('event_rejected', '❌ Your event request "' + ev.title + '" on ' + ev.date + ' was not approved.', 'volunteer'); }
        const creator = members.find(m => m.name === ev.createdBy);
        triggerNotify('event', { action: 'rejected', evData: { ...ev, creatorEmail: creator ? creator.email : null } });
      }
    }, { danger: true, confirmLabel: 'Reject' });
  };
  // Per-date approve/reject driven by the grouped notification UI, looked up by
  // requestGroupId+date against live fieldEvents state rather than needing the
  // notification doc itself to carry individual Firestore doc IDs.
  const approveEventGroupDate = async (requestGroupId, date) => {
    const ev = fieldEvents.find(e => e.requestGroupId === requestGroupId && e.date === date && e.status === 'pending');
    if (ev) await approveEvent(ev.id);
  };
  const rejectEventGroupDate = async (requestGroupId, date) => {
    const ev = fieldEvents.find(e => e.requestGroupId === requestGroupId && e.date === date && e.status === 'pending');
    if (ev) await rejectEvent(ev.id);
  };
  const approveEventGroupAll = async (requestGroupId) => {
    const pending = fieldEvents.filter(e => e.requestGroupId === requestGroupId && e.status === 'pending');
    for (const ev of pending) {
      // A conflict pauses on the resolution modal -- stop here rather than
      // continuing to approve later dates out from under the admin; once
      // they resolve it, re-running Approve All picks up where it left off.
      const result = await approveEvent(ev.id);
      if (result === 'conflict') break;
    }
  };
  const rejectEventGroupAll = async (requestGroupId) => {
    const pending = fieldEvents.filter(e => e.requestGroupId === requestGroupId && e.status === 'pending');
    if (pending.length === 0) return;
    showConfirm('Reject All', 'All ' + pending.length + ' remaining date(s) in this request will be rejected and deleted. The volunteer will be notified.', async () => {
      for (const ev of pending) { await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id)); }
      await notifyIfGroupResolved(pending[0]);
      // Notify for every rejected date, not just the first -- each call feeds
      // the same requestGroupId batch, so the requester still gets one
      // consolidated email listing all of them.
      for (const ev of pending) {
        const creator = members.find(m => m.name === ev.createdBy);
        triggerNotify('event', { action: 'rejected', evData: { ...ev, creatorEmail: creator ? creator.email : null } });
      }
    }, { danger: true, confirmLabel: 'Reject All' });
  };

  // ─── PLAN TIER (for feature gating) ──────────────────────────────
  const planTier = (league && league.planActive && league.plan) || 'free'; // 'free' | 'pro' | 'league'
  const hasPro = planTier === 'pro' || planTier === 'league';
  const hasLeague = planTier === 'league';
  // Feature access treats promo-granted League the same as paid League ("it's like
  // they paid"), but ad-free specifically should not extend to promo access, since
  // that's real ad revenue GroundsPal would be giving up for a league that isn't
  // actually paying anything.
  const isPromoLeague = hasLeague && !!(league && league.planSource && league.planSource.startsWith('promo:'));
  const isPayingLeague = hasLeague && !isPromoLeague;

  // ─── "MAY I WRITE AS THIS LEAGUE'S OWNER?" — THE ONE DEFINITION ──────────────
  // THIS IS NOT THE SAME QUESTION AS `user.isMainAdmin`, AND THE DIFFERENCE IS THE
  // WHOLE POINT. `handleEmulateLeague` builds a synthetic user with
  // `isMainAdmin: true` and NO uid and NO email, so a superadmin emulating a league
  // is indistinguishable from its owner to any branch that reads `isMainAdmin`
  // directly. That is fine for RENDERING -- emulation is supposed to show admin UI,
  // and the ~43 render gates in this file deliberately keep reading `isMainAdmin`
  // straight. It is NOT fine for WRITING.
  //
  // Why only the isMainAdmin branch is dangerous: the co-admin fallbacks key on
  // `auth.currentUser.uid` and look the actor up in `league.coAdmins`, and the
  // volunteer paths call findMyMemberDoc() -- a superadmin is in neither, so those
  // branches find nothing and no-op safely. The isMainAdmin branch is the only one
  // needing no lookup: it writes flat `admin*` fields straight onto leagues/{lid},
  // so it always succeeds, and always attributes the superadmin's action to the
  // real owner.
  //
  // STANDING RULE: any future write keyed to league-OWNER identity calls this
  // instead of reading `isMainAdmin` directly. Guarding sites one at a time does
  // not scale -- the next such write would silently reintroduce the bug.
  // Null-safe by construction: called from effects that run before `user` is set.
  const canWriteAsAdmin = () => !!(user && user.isMainAdmin && !user.isEmulating);

  // Resolves the signed-in volunteer's own member doc. Prefers matching by Firebase
  // UID (accurate even if two docs share a name, e.g. an old pre-migration doc left
  // behind alongside the new one) and falls back to name matching for members an
  // admin added directly who haven't signed up with a real account themselves.
  const findMyMemberDoc = () => {
    const myUid = auth.currentUser && auth.currentUser.uid;
    if (myUid) {
      const byUid = members.find(m => m.uid === myUid);
      if (byUid) return byUid;
    }
    return members.find(m => m.name === user.name);
  };

  // ─── SMS OPT-IN: load existing values when the relevant screen opens ──
  useEffect(() => {
    if (screen === 'my-account' && user && members) {
      const me = findMyMemberDoc();
      if (me) {
        setMyPhone(me.phone || "");
        setMySmsConsent(!!me.smsConsent);
      }
    }
  }, [screen, user, members]);

  useEffect(() => {
    if (screen === 'show-code' && league && user) {
      if (user.isMainAdmin) {
        setAdminPhone(league.adminPhone || "");
        setAdminSmsConsent(!!league.adminSmsConsent);
        setAdminSmsPrefFieldClosures(!league.adminSmsPrefs || league.adminSmsPrefs.fieldClosures !== false);
        setAdminSmsPrefEventRequests(!league.adminSmsPrefs || league.adminSmsPrefs.eventRequests !== false);
      } else {
        const myUid = auth.currentUser && auth.currentUser.uid;
        const me = (league.coAdmins || []).find(ca => ca.uid === myUid);
        setAdminPhone((me && me.phone) || "");
        setAdminSmsConsent(!!(me && me.smsConsent));
        setAdminSmsPrefFieldClosures(!(me && me.smsPrefs) || me.smsPrefs.fieldClosures !== false);
        setAdminSmsPrefEventRequests(!(me && me.smsPrefs) || me.smsPrefs.eventRequests !== false);
      }
    }
  }, [screen, league, user]);

  const saveMyAccountSms = async () => {
    if (!lid || !user) return;
    setAccountSaving(true); setAccountError("");
    try {
      const meDoc = findMyMemberDoc();
      if (meDoc) {
        await updateDoc(doc(db, 'leagues', lid, 'members', meDoc.id), {
          phone: myPhone.trim(),
          smsConsent: mySmsConsent,
          smsConsentedAt: mySmsConsent ? new Date().toISOString() : null
        });
        // Keep the volunteerAccounts lookup doc in sync (used for returning sign-in)
        if (meDoc.email) {
          const emailKey = meDoc.email.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
          // uid and email are written even though nothing here changes them.
          // This is a merge setDoc, so it CREATES the doc when one doesn't
          // exist yet, and the volunteerAccounts write rule proves ownership
          // from request.resource.data.uid. Without these two fields a create
          // would carry no uid at all and be denied -- the save would fail for
          // exactly the members who have no lookup doc yet. Harmless on the
          // ordinary update path, where both values are already what's stored.
          await setDoc(doc(db, 'volunteerAccounts', emailKey), {
            uid: auth.currentUser ? auth.currentUser.uid : null,
            email: meDoc.email.trim().toLowerCase(),
            phone: myPhone.trim(), smsConsent: mySmsConsent
          }, { merge: true });
        }
      }
      setAccountSaved(true);
      setTimeout(() => setAccountSaved(false), 2500);
    } catch (e) {
      setAccountError("Couldn't save. Please try again.");
    }
    setAccountSaving(false);
  };

  const saveAdminSms = async () => {
    if (!lid || !user || !league) return;
    // COMPLIANCE, not data hygiene. adminSmsConsent/adminSmsConsentedAt are A2P 10DLC
    // consent records on a live, approved Twilio campaign. A superadmin must never be
    // able to create a consent record attributed to someone who did not give it, so
    // this is blocked outright rather than redirected somewhere harmless -- losing the
    // ability to edit a league's SMS settings by emulation is the correct trade.
    // The Save button is disabled alongside this so the block is visible rather than a
    // silent no-op; this stays as the backstop for any other path into the handler.
    if (user.isEmulating) {
      setAdminSmsError("SMS settings must be changed by the league's own admin. Super Admin mode can't record a consent on someone else's behalf.");
      return;
    }
    setAdminSmsError("");
    const trimmedPhone = adminPhone.trim();
    const digitsOnly = trimmedPhone.replace(/\D/g, "");
    if (trimmedPhone !== "" && (digitsOnly.length < 10 || digitsOnly.length > 15)) {
      setAdminSmsError("That doesn't look like a valid phone number. Please enter a number like (555) 123-4567.");
      return;
    }
    setAdminSmsSaving(true);
    try {
      const smsPrefs = { fieldClosures: adminSmsPrefFieldClosures, eventRequests: adminSmsPrefEventRequests };
      if (canWriteAsAdmin()) {
        await updateDoc(doc(db, 'leagues', lid), {
          adminPhone: trimmedPhone,
          adminSmsConsent: adminSmsConsent,
          adminSmsConsentedAt: adminSmsConsent ? new Date().toISOString() : null,
          adminSmsPrefs: smsPrefs
        });
      } else {
        const myUid = auth.currentUser && auth.currentUser.uid;
        const updatedCoAdmins = (league.coAdmins || []).map(ca =>
          ca.uid === myUid
            ? { ...ca, phone: trimmedPhone, smsConsent: adminSmsConsent, smsConsentedAt: adminSmsConsent ? new Date().toISOString() : null, smsPrefs }
            : ca
        );
        await updateDoc(doc(db, 'leagues', lid), { coAdmins: updatedCoAdmins });
      }
      setAdminSmsSaved(true);
      setTimeout(() => setAdminSmsSaved(false), 2500);
    } catch (e) {
      // silent fail is consistent with saveLocations/saveZipCode elsewhere in this file
    }
    setAdminSmsSaving(false);
  };

  // ─── CHANGE PASSWORD (any authenticated role) ───────────────────────
  // Firebase requires a recent sign-in for sensitive account changes, so this
  // re-authenticates with the current password first before setting a new one.
  const handleChangePassword = async () => {
    if (!currentPasswordInput.trim() || !newPasswordInput.trim() || !confirmNewPasswordInput.trim()) {
      setPasswordChangeError("Please fill in all fields."); return;
    }
    if (newPasswordInput.length < 6) { setPasswordChangeError("New password must be at least 6 characters."); return; }
    if (newPasswordInput !== confirmNewPasswordInput) { setPasswordChangeError("New passwords don't match."); return; }
    setPasswordChangeSaving(true); setPasswordChangeError("");
    try {
      const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPasswordInput);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPasswordInput);
      setPasswordChangeSuccess(true);
      setCurrentPasswordInput(""); setNewPasswordInput(""); setConfirmNewPasswordInput("");
      setTimeout(() => setPasswordChangeSuccess(false), 3000);
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) setPasswordChangeError("Current password is incorrect.");
      else if (msg.includes('weak-password')) setPasswordChangeError("Please choose a stronger password.");
      else setPasswordChangeError("Something went wrong. Please try again.");
    }
    setPasswordChangeSaving(false);
  };

  // ─── LOCAL DATE HELPER (avoids UTC day-boundary drift for "today" checks) ──
  const getLocalDateStr = (d = new Date()) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${day}`;
  };

  // ─── BOARD NOTIFICATION "SEEN" TRACKING (account-level, not localStorage) ──
  // Storing this on the person's own record means it survives new devices,
  // incognito, and cleared browser data — unlike localStorage, which resets
  // in all of those cases and previously caused old posts to look "new."
  const markBoardSeen = async (timestamp) => {
    if (!lid || !user) return;
    try {
      if (user.role === 'volunteer') {
        const me = findMyMemberDoc();
        if (me) await updateDoc(doc(db, 'leagues', lid, 'members', me.id), { lastSeenBoardPost: timestamp });
      } else if (user.isEmulating) {
        // An emulating superadmin has no board-seen marker of their own, and the
        // owner's is not theirs to move. Doing nothing is the whole fix: this is
        // reached AUTOMATICALLY from the board effect's "silently catch up" branch
        // on first load, so merely opening a league used to backdate the owner's
        // marker and cost them the new-post notification for every existing post.
        return;
      } else if (canWriteAsAdmin()) {
        await updateDoc(doc(db, 'leagues', lid), { lastSeenBoardPost: timestamp });
      } else {
        const myUid = auth.currentUser && auth.currentUser.uid;
        const updatedCoAdmins = (league.coAdmins || []).map(ca =>
          ca.uid === myUid ? { ...ca, lastSeenBoardPost: timestamp } : ca
        );
        await updateDoc(doc(db, 'leagues', lid), { coAdmins: updatedCoAdmins });
      }
    } catch (e) {
      // non-critical — if this fails, the popup just shows again next session
    }
  };

  // ─── EMAIL HELPER ────────────────────────────────────────────────
  const sendEmail = async (type, to, data) => {
    if (!to || (Array.isArray(to) && to.length === 0)) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, to, data, idToken, leagueId: lid })
      });
    } catch(e) { console.error('sendEmail error:', e); }
  };

  // SMS notifications -- limited to exactly the 5 message types approved on the
  // A2P 10DLC campaign. Recipients must already be filtered by smsConsent+phone
  // by the caller; this does not re-check consent itself.
  const sendSMS = async (type, to, data) => {
    const recipients = (Array.isArray(to) ? to : [to]).filter(Boolean);
    if (recipients.length === 0) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, to: recipients, data, idToken, leagueId: lid })
      });
    } catch(e) { console.error('sendSMS error:', e); }
  };

  // ─── BILLING / UPGRADE ──────────────────────────────────────────
  const handleUpgrade = async (priceId) => {
    if (!lid || billingLoading) return;
    setBillingLoading(priceId);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leagueId: lid, priceId, adminEmail: auth.currentUser && auth.currentUser.email })
      });
      const result = await response.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        alert('Something went wrong starting checkout: ' + (result.error || 'Unknown error'));
        setBillingLoading(null);
      }
    } catch (e) {
      alert('Something went wrong starting checkout: ' + e.message);
      setBillingLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!lid || billingLoading) return;
    setBillingLoading('portal');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leagueId: lid, idToken })
      });
      const result = await response.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        alert(result.error || "We couldn't open billing management right now. Please try again or contact support.");
        setBillingLoading(null);
      }
    } catch (e) {
      alert("We couldn't open billing management right now. Please check your connection and try again.");
      setBillingLoading(null);
    }
  };

  // Admin/co-admin phone numbers opted into SMS, reading straight off the
  // league doc (every admin/co-admin's saved phone+consent+prefs), not just
  // the currently-signed-in admin's own form fields above. prefKey scopes
  // this to one notification category ('fieldClosures' | 'eventRequests') so
  // an admin who only wants one kind of text doesn't get the other -- prefs
  // default to true (on) when unset, matching existing opted-in admins who
  // saved before per-category toggles existed.
  const getAdminSmsPhones = React.useCallback((prefKey) => {
    if (!league) return [];
    const prefOn = (prefs) => !prefs || prefs[prefKey] !== false;
    const mainAdmin = (league.adminSmsConsent && league.adminPhone && prefOn(league.adminSmsPrefs)) ? [league.adminPhone] : [];
    const coAdmins = (league.coAdmins || []).filter(ca => ca.smsConsent && ca.phone && prefOn(ca.smsPrefs)).map(ca => ca.phone);
    return [...mainAdmin, ...coAdmins];
  }, [league]);

  // ─── FIELD CHANGE BATCH / DEBOUNCE ──────────────────────────────
  const flushFieldBatch = React.useCallback((batch, channel) => {
    if (!league || !batch || Object.keys(batch).length === 0) return;
    if (!hasLeague) return; // Field closure/reopen notifications are a League-tier feature
    const closures = Object.entries(batch).filter(([,v]) => v.type === 'close');
    const reopens  = Object.entries(batch).filter(([,v]) => v.type === 'open');
    const dateStr  = new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
    const adminName = user ? user.name : 'Admin';
    if (channel === 'groupme' || channel === 'both') {
      const botId = league.groupmeBotId;
      if (botId) {
        if (closures.length > 0) {
          const lines = closures.map(([field, v]) => field + ' - ' + v.reason + (v.message ? ' (' + v.message + ')' : '')).join('\n');
          sendGroupMe('FIELD CLOSURE NOTICE\n' + dateStr + '\n\n' + lines + '\n\n- ' + adminName, botId);
        }
        if (reopens.length > 0) {
          const lines = reopens.map(([field]) => field).join('\n');
          sendGroupMe('FIELDS REOPENED\n' + dateStr + '\n\n' + lines + '\n\n- ' + adminName, botId);
        }
      }
    }
    if (channel === 'email' || channel === 'both') {
      const memberEmails = (members || []).map(m => m.email).filter(Boolean);
      const leagueName = league.leagueName || 'Your League';
      if (closures.length > 0 && memberEmails.length > 0) {
        sendEmail('field_closure', memberEmails, { leagueName, dateStr, adminName, fields: closures.map(([field, v]) => ({ field, reason: v.reason, message: v.message || '' })) });
      }
      if (reopens.length > 0 && memberEmails.length > 0) {
        sendEmail('field_reopen', memberEmails, { leagueName, dateStr, adminName, fields: reopens.map(([field]) => ({ field })) });
      }
    }
    // SMS is opted into separately from the email/GroupMe channel above, so it
    // always fires (when consented) regardless of which channel was chosen.
    // Admins/co-admins who've opted into "field closures" texts get these too,
    // on top of volunteers -- their phone/consent live on the league doc, not
    // the members collection, so they're merged in separately here.
    const memberPhones = (members || []).filter(m => m.smsConsent && m.phone).map(m => m.phone);
    const allPhones = [...new Set([...memberPhones, ...getAdminSmsPhones('fieldClosures')])];
    if (allPhones.length > 0) {
      // The approved SMS template names one field per message, so send one text
      // per closed/reopened field rather than bundling them into a single message.
      // One text per flush, listing every field involved -- not one text per
      // field -- so closing several fields at once doesn't spam volunteers.
      if (closures.length > 0) sendSMS('field_closure', allPhones, { fields: closures.map(([field, v]) => ({ field, reason: v.reason })) });
      if (reopens.length > 0) sendSMS('field_reopen', allPhones, { fields: reopens.map(([field]) => field) });
    }
  }, [league, user, members, hasLeague, getAdminSmsPhones]);

  const queueFieldChange = React.useCallback((fieldName, type, reason, message) => {
    setFieldChangeBatch(prev => {
      const next = { ...prev };
      if (type === 'open' && next[fieldName] && next[fieldName].type === 'close') { delete next[fieldName]; }
      else if (type === 'close' && next[fieldName] && next[fieldName].type === 'open') { next[fieldName] = { type, reason, message }; }
      else { next[fieldName] = { type, reason, message }; }
      return next;
    });
    if (fieldBatchTimerRef.current) clearTimeout(fieldBatchTimerRef.current);
    fieldBatchTimerRef.current = setTimeout(() => {
      setFieldChangeBatch(current => { flushFieldBatch(current, notifyChannelRef.current); return {}; });
    }, 2 * 60 * 1000);
  }, [flushFieldBatch]);

  // ─── EVENT APPROVE/REJECT NOTIFY BATCH / DEBOUNCE ───────────────
  // Same shape as the field-change batch above: every approve/reject is
  // queued (keyed by requestGroupId, or the event's own id if it has no
  // group) instead of emailed immediately, and a 2-minute debounce timer
  // flushes the whole batch as ONE email per requester listing every
  // approved/rejected date -- so "Approve All" on a multi-date request
  // sends one email, not one per date.
  const flushEventBatch = React.useCallback((batch) => {
    if (!league || !batch || Object.keys(batch).length === 0) return;
    const leagueName = league.leagueName || 'Your League';
    const adminName = user ? user.name : 'Admin';
    const fmtDate = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
    Object.values(batch).forEach(entry => {
      const { evData, approvedDates, rejectedDates } = entry;
      if (!evData || !evData.creatorEmail) return;
      if (approvedDates.length === 0 && rejectedDates.length === 0) return;
      sendEmail('event_request_decided', [evData.creatorEmail], {
        leagueName, eventTitle: evData.title, field: evData.field || '', startTime: evData.startTime || '', endTime: evData.endTime || '',
        approvedDates: approvedDates.map(fmtDate), rejectedDates: rejectedDates.map(fmtDate), decidedBy: adminName,
      });
      if (evData.creatorPhone && evData.creatorSmsConsent && approvedDates.length > 0) {
        sendSMS('event_approved', [evData.creatorPhone], { eventDate: fmtDate(approvedDates[0]), field: evData.field || '' });
      }
      // No SMS for rejections -- "event cancelled" isn't one of the 5 approved SMS templates.
    });
  }, [league, user]);

  const queueEventChange = React.useCallback((payload) => {
    const { action, evData } = payload;
    const key = evData.requestGroupId || evData.id;
    setEventNotifyBatch(prev => {
      const next = { ...prev };
      const existing = next[key] || { evData, approvedDates: [], rejectedDates: [] };
      const approvedDates = action === 'approved' ? [...new Set([...existing.approvedDates, evData.date])] : existing.approvedDates;
      const rejectedDates = (action === 'rejected' || action === 'deleted') ? [...new Set([...existing.rejectedDates, evData.date])] : existing.rejectedDates;
      next[key] = { evData: { ...existing.evData, ...evData }, approvedDates, rejectedDates };
      return next;
    });
    if (eventBatchTimerRef.current) clearTimeout(eventBatchTimerRef.current);
    eventBatchTimerRef.current = setTimeout(() => {
      setEventNotifyBatch(current => { flushEventBatch(current); return {}; });
    }, 2 * 60 * 1000);
  }, [flushEventBatch]);

  // Both batches above are purely client-side (a React state object + a
  // setTimeout) with nothing durable behind them -- if the tab closes, the
  // user signs out, or the page navigates away before the 2-minute timer
  // fires, a queued field-closure or event-decision notification would
  // otherwise vanish silently with no email ever sent. flushPendingNotifyBatches
  // sends whatever's currently queued immediately (bypassing the debounce) so
  // those exit paths can't lose a notification. Safe to call even when both
  // batches are empty -- flushFieldBatch/flushEventBatch no-op on {}.
  const flushPendingNotifyBatches = React.useCallback(() => {
    if (fieldBatchTimerRef.current) { clearTimeout(fieldBatchTimerRef.current); fieldBatchTimerRef.current = null; }
    if (eventBatchTimerRef.current) { clearTimeout(eventBatchTimerRef.current); eventBatchTimerRef.current = null; }
    if (Object.keys(fieldChangeBatch).length > 0) { flushFieldBatch(fieldChangeBatch, notifyChannelRef.current); setFieldChangeBatch({}); }
    if (Object.keys(eventNotifyBatch).length > 0) { flushEventBatch(eventNotifyBatch); setEventNotifyBatch({}); }
  }, [fieldChangeBatch, eventNotifyBatch, flushFieldBatch, flushEventBatch]);

  // Covers the "tab closes / app is backgrounded before the timer fires" exit
  // path -- doesn't cover a same-tab sign-out click (SPA state change, no
  // real page unload), which is handled separately inside handleSignOut.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flushPendingNotifyBatches(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushPendingNotifyBatches);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flushPendingNotifyBatches);
    };
  }, [flushPendingNotifyBatches]);

  // ─── NOTIFY MODAL HANDLER ────────────────────────────────────────
  const handleNotifyChoice = (channel) => {
    const pending = notifyModalPending;
    setNotifyModalPending(null);
    if (!pending) return;
    if (pending.type === 'field') {
      setNotifyChannel(channel);
      notifyChannelRef.current = channel;
      const { fieldName, fields, action, reason, message } = pending;
      const fieldNames = fields || [fieldName];
      fieldNames.forEach(fn => queueFieldChange(fn, action, reason, message));
    } else if (pending.type === 'event') {
      // Remember this yes/no choice for 2 minutes, same expiry pattern as the
      // field notifyChannelRef, so approving/rejecting several dates in a row
      // (or across a whole request group) doesn't re-prompt for each one.
      eventNotifyChoiceRef.current = channel;
      eventModalPendingRef.current = false;
      if (eventChoiceTimerRef.current) clearTimeout(eventChoiceTimerRef.current);
      eventChoiceTimerRef.current = setTimeout(() => { eventNotifyChoiceRef.current = null; }, 2 * 60 * 1000);
      // Flush everything that piled up while this modal was still unanswered
      // (e.g. the rest of an "Approve All" loop), not just the payload that
      // happened to be showing when the admin clicked.
      const queued = eventPendingNotifiesRef.current;
      eventPendingNotifiesRef.current = [];
      if (channel === 'yes') queued.forEach(p => queueEventChange(p));
    }
  };

  const triggerNotify = (type, payload) => {
    if (type === 'field') {
      const fieldNames = payload.fields || [payload.fieldName];
      if (notifyChannelRef.current !== null) {
        fieldNames.forEach(fieldName => queueFieldChange(fieldName, payload.action, payload.reason, payload.message));
      } else {
        setNotifyModalPending({ type, ...payload });
      }
    } else if (type === 'event') {
      if (!payload.evData || !payload.evData.creatorEmail) return;
      if (eventNotifyChoiceRef.current === 'yes') {
        queueEventChange(payload);
      } else if (eventNotifyChoiceRef.current === 'no') {
        // Remembered "no" for this window -- skip without prompting again.
      } else {
        // Undecided: accumulate (don't overwrite) and only open the modal
        // once for this burst of calls.
        eventPendingNotifiesRef.current.push(payload);
        if (!eventModalPendingRef.current) {
          eventModalPendingRef.current = true;
          setNotifyModalPending({ type, ...payload });
        }
      }
    }
  };

  // ─── EXTENDED CLOSURE ───────────────────────────────────────────
  const getDatesInRange = (start, end) => {
    const dates = [];
    const cur = new Date(start + 'T12:00:00');
    const last = new Date(end + 'T12:00:00');
    while (cur <= last) { dates.push(cur.toISOString().split('T')[0]); cur.setDate(cur.getDate() + 1); }
    return dates;
  };

  const checkExtClosureConflicts = () => {
    if (!extClosureFields.length || !extClosureStart || !extClosureEnd) return;
    const dates = getDatesInRange(extClosureStart, extClosureEnd);
    const conflicts = fieldEvents.filter(ev => ev.status === 'approved' && extClosureFields.includes(ev.field) && dates.includes(ev.date));
    if (conflicts.length > 0) { setExtClosureConflicts(conflicts); setExtClosureCancelIds(conflicts.map(e => e.id)); setExtClosureStep('conflicts'); }
    else { submitExtClosure([]); }
  };

  const submitExtClosure = async (cancelIds) => {
    const dates = getDatesInRange(extClosureStart, extClosureEnd);
    const leagueName = league ? (league.leagueName || 'Your League') : 'Your League';
    const adminName = user ? user.name : 'Admin';
    // Editing an existing extended closure: remove its old docs first, then
    // recreate under the same batch ID so it's still recognized as one group.
    if (editingExtBatchId) {
      const oldDocs = fieldClosures.filter(cl => cl.extendedBatchId === editingExtBatchId);
      for (const d of oldDocs) { await deleteDoc(doc(db, 'leagues', lid, 'fieldClosures', d.id)); }
    }
    const batchId = editingExtBatchId || ('ext' + Date.now());
    for (const field of extClosureFields) {
      for (const date of dates) {
        const id = 'cl' + Date.now() + '_' + field.replace(/\s/g,'') + '_' + date;
        await setDoc(doc(db, 'leagues', lid, 'fieldClosures', id), { id, field, reason: extClosureReason, message: extClosureMessage || '', date, closedBy: adminName, closedAt: new Date().toISOString(), extended: true, extendedBatchId: batchId });
      }
    }
    for (const ev of extClosureConflicts.filter(e => cancelIds.includes(e.id))) {
      await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id));
      const creator = members.find(m => m.name === ev.createdBy);
      const dateStr = new Date(ev.date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
      const emailsToNotify = new Set();
      if (creator && creator.email) emailsToNotify.add(creator.email);
      (ev.slots || []).forEach(slot => { (slot.signups || []).forEach(sg => { const m = members.find(mb => mb.name === sg.name); if (m && m.email) emailsToNotify.add(m.email); }); });
      if (emailsToNotify.size > 0) { sendEmail('event_cancelled', Array.from(emailsToNotify), { leagueName, eventTitle: ev.title, eventDate: dateStr, startTime: ev.startTime || '', endTime: ev.endTime || '', field: ev.field || '', cancelledBy: adminName }); }
    }
    const dateRangeStr = new Date(extClosureStart + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' }) + (extClosureStart !== extClosureEnd ? ' – ' + new Date(extClosureEnd + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' }) : '');
    triggerNotify('field', { fields: extClosureFields, action: 'close', reason: extClosureReason + ' (' + dateRangeStr + ')', message: extClosureMessage || '' });
    setShowExtendedClosure(false); setExtClosureFields([]); setExtClosureStart(''); setExtClosureEnd(''); setExtClosureReason('Weather'); setExtClosureMessage(''); setExtClosureConflicts([]); setExtClosureCancelIds([]); setExtClosureStep('form'); setEditingExtBatchId(null);
  };

  // Deletes every closure doc belonging to one extended-closure batch.
  const removeExtClosureBatch = async (batchId) => {
    const docsToRemove = fieldClosures.filter(cl => cl.extendedBatchId === batchId);
    for (const d of docsToRemove) { await deleteDoc(doc(db, 'leagues', lid, 'fieldClosures', d.id)); }
    if (docsToRemove.length > 0) {
      triggerNotify('field', { fields: [...new Set(docsToRemove.map(d => d.field))], action: 'open', reason: '', message: '' });
    }
  };

  // Loads an existing extended closure batch back into the form for editing.
  const startEditExtClosure = (batchId) => {
    const batchDocs = fieldClosures.filter(cl => cl.extendedBatchId === batchId);
    if (batchDocs.length === 0) return;
    const uniqueFields = [...new Set(batchDocs.map(d => d.field))];
    const sortedDates = batchDocs.map(d => d.date).sort();
    setExtClosureFields(uniqueFields);
    setExtClosureStart(sortedDates[0]);
    setExtClosureEnd(sortedDates[sortedDates.length - 1]);
    setExtClosureReason(batchDocs[0].reason || 'Weather');
    setExtClosureMessage(batchDocs[0].message || '');
    setEditingExtBatchId(batchId);
    setExtClosureStep('form');
    setShowExtendedClosure(true);
  };

  // ─── TASK ACTIONS ────────────────────────────────────────────────
  const claimTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: user.name + " claimed this task.", author: user.name, timestamp: ts, system: true }]);
    await updateDoc(doc(db, 'leagues', lid, 'tasks', taskId), { assignedTo: user.name, status: "in-progress", updates });
    await createNotification('task_claimed', user.name + ' accepted the task: "' + task.title + '"', 'admin');
  };

  const unclaimTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: user.name + " unclaimed this task.", author: user.name, timestamp: ts, system: true }]);
    await updateDoc(doc(db, 'leagues', lid, 'tasks', taskId), { assignedTo: null, status: "open", updates });
  };

  const sendUpdate = async (taskId) => {
    if (!updateText.trim()) return;
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{ message: updateText.trim(), author: user.name, timestamp: ts }]);
    await updateDoc(doc(db, 'leagues', lid, 'tasks', taskId), { updates });
    setUpdateText("");
  };

  const completeTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const done = Object.assign({}, task, { status: "complete", completedAt: ts, duration: Date.now() - new Date(task.createdAt).getTime(), updates: (task.updates || []).concat([{ message: "Task marked complete by " + user.name + ".", author: user.name, timestamp: ts, system: true }]) });
    await setDoc(doc(db, 'leagues', lid, 'archive', taskId), done);
    await deleteDoc(doc(db, 'leagues', lid, 'tasks', taskId));
    if (user.role === 'volunteer') await createNotification('task_claimed', user.name + ' completed the task: "' + task.title + '"', 'admin');
    setSelectedTaskId(null);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
    const completedAt = new Date(ts);
    const timeStr = completedAt.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) + ' at ' + completedAt.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'});
    if (hasPro) {
      sendGroupMe('✅ Task Completed!\n' + 'Task: ' + task.title + '\nCompleted by: ' + (task.assignedTo || user.name) + '\nTime: ' + timeStr, league && league.groupmeBotId);
    }
  };

  const saveTaskTitle = async (title) => {
    if (!title.trim() || !lid) return;
    const id = "title_" + title.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    await setDoc(doc(db, 'leagues', lid, 'taskTitles', id), { title: title.trim(), addedBy: user.name, addedAt: new Date().toISOString() });
  };

  const createTask = async () => {
    if (!newTask.title.trim()) return;
    const id = "t" + Date.now();
    await setDoc(doc(db, 'leagues', lid, 'tasks', id), Object.assign({ id }, newTask, { createdBy: user.name, createdAt: new Date().toISOString(), assignedTo: null, status: "open", updates: [], completedAt: null }));
    await createNotification('task_created', 'New task available: "' + newTask.title.trim() + '"' + (newTask.location ? ' — ' + newTask.location : ''), 'volunteer');
    await saveTaskTitle(newTask.title);
    setNewTask({ title: "", description: "", location: "", priority: "medium", dueDate: "" });
    setShowNewTask(false);
  };

  const restoreTask = async (taskId) => {
    const task = archive.find(t => t.id === taskId);
    if (!task) return;
    const restored = Object.assign({}, task, {
      status: "open", assignedTo: null, completedAt: null, duration: null,
      updates: (task.updates || []).concat([{ message: "Task restored from archive by " + user.name + ".", author: user.name, timestamp: new Date().toISOString(), system: true }])
    });
    await setDoc(doc(db, 'leagues', lid, 'tasks', taskId), restored);
    await deleteDoc(doc(db, 'leagues', lid, 'archive', taskId));
    setSelectedTaskId(null);
  };

  const updateTask = async (id, changes) => {
    await updateDoc(doc(db, 'leagues', lid, 'tasks', id), changes);
    setEditingTask(null);
  };

  const assignTask = async (taskId, assigneeName) => {
    const task = tasks.find(t => t.id === taskId);
    const ts = new Date().toISOString();
    const updates = (task.updates || []).concat([{
      message: "Task assigned to " + assigneeName + " by " + user.name + ".",
      author: user.name, timestamp: ts, system: true
    }]);
    await updateDoc(doc(db, 'leagues', lid, 'tasks', taskId), {
      assignedTo: assigneeName,
      status: "in-progress",
      updates
    });
    await createNotification('task_claimed', user.name + ' assigned "' + task.title + '" to ' + assigneeName, 'admin');
    const assignee = members.find(m => m.name === assigneeName);
    if (assignee && assignee.smsConsent && assignee.phone) {
      sendSMS('task_assigned', [assignee.phone], { taskTitle: task.title, field: task.location || '' });
    }
  };

  const deleteTask = async (id) => { await deleteDoc(doc(db, 'leagues', lid, 'tasks', id)); setSelectedTaskId(null); };
  const deleteArchivedTask = async (id) => { await deleteDoc(doc(db, 'leagues', lid, 'archive', id)); };

  const addSupply = async () => {
    if (!newSupply.item.trim()) return;
    const id = "s" + Date.now();
    await setDoc(doc(db, 'leagues', lid, 'supplies', id), Object.assign({ id }, newSupply, { addedBy: user.name, addedAt: new Date().toISOString(), purchased: false }));
    setNewSupply({ item: "", quantity: "", notes: "" });
    setShowNewSupply(false);
  };

  const markPurchased = async (id) => { await updateDoc(doc(db, 'leagues', lid, 'supplies', id), { purchased: true, purchasedAt: new Date().toISOString() }); };
  const unmarkPurchased = async (id) => { await updateDoc(doc(db, 'leagues', lid, 'supplies', id), { purchased: false, purchasedAt: null }); };
  const removeSupply = async (id) => { await deleteDoc(doc(db, 'leagues', lid, 'supplies', id)); };

  // Check for secret admin URL path, plus public legal pages.
  // These redirect via window.location rather than setScreen() because the
  // internal-screen approach raced against the auth-restoration effect below --
  // if a session was found, it would call setScreen('app') and silently override
  // this, sending signed-in users to the dashboard instead of the policy page.
  // A hard redirect navigates away immediately, so there's nothing left to race.
  useEffect(() => {
    if (isSuperadminPath()) {
      setScreen('platform-login');
    } else if (window.location.pathname === '/terms') {
      window.location.href = 'https://thethinkerygroup.com/terms-of-service/';
    } else if (window.location.pathname === '/privacy') {
      window.location.href = 'https://thethinkerygroup.com/privacy-policy/';
    }
  }, []);

  // ─── SPONSORED BANNERS (superadmin-managed ad inventory) ───────────────
  useEffect(() => {
    if (screen !== 'platform-dashboard' && screen !== 'platform-advertising') return;
    const unsub = onSnapshot(collection(db, 'sponsoredBanners'), snap => {
      setPlatformBanners(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
    });
    return () => unsub();
  }, [screen]);

  // ─── PLATFORM STAT HISTORY (dailyStats trend chart) ─────────────────────
  // "Today" is intentionally not queried from Firestore -- it just reflects
  // live platformData, same value as the stat cards above the chart.
  useEffect(() => {
    if (screen !== 'platform-dashboard') return;
    if (statRange === 'today') {
      setStatHistory({ docs: [], trackingSince: null });
      return;
    }
    let cancelled = false;
    setStatHistoryLoading(true);
    loadStatHistory(statRange).then(result => {
      if (cancelled) return;
      setStatHistory(result);
      setStatHistoryLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setStatHistory({ docs: [], trackingSince: null });
      setStatHistoryLoading(false);
    });
    return () => { cancelled = true; };
  }, [screen, statRange]);

  const openNewBannerForm = () => {
    setEditingBannerId(null);
    setBannerDraft(emptyBannerDraft);
    setBannerError('');
    setShowBannerForm(true);
  };

  const openEditBannerForm = (banner) => {
    setEditingBannerId(banner.id);
    setBannerDraft({
      name: banner.name, linkUrl: banner.linkUrl, imageFile: null, imageUrl: banner.imageUrl || '',
      active: banner.active, scope: banner.scope, leagueIds: banner.leagueIds || []
    });
    setBannerError('');
    setShowBannerForm(true);
  };

  const saveBanner = async () => {
    setBannerError('');
    if (!bannerDraft.name.trim()) { setBannerError('Give the banner a name.'); return; }
    if (!bannerDraft.linkUrl.trim()) { setBannerError('A link URL is required — the whole banner is clickable.'); return; }
    if (!bannerDraft.imageFile && !bannerDraft.imageUrl) { setBannerError('Upload a banner image.'); return; }
    if (bannerDraft.scope === 'specific' && bannerDraft.leagueIds.length === 0) { setBannerError('Select at least one league, or switch to All leagues.'); return; }

    // Cap enforcement: only one active global banner at a time (auto-swaps, like
    // the reference site); at most 2 active specific banners per targeted league.
    const others = platformBanners.filter(b => b.id !== editingBannerId);
    if (bannerDraft.active) {
      if (bannerDraft.scope === 'all') {
        // handled below by deactivating other globals after save
      } else {
        for (const lgId of bannerDraft.leagueIds) {
          const existing = others.filter(b => b.active && b.scope === 'specific' && (b.leagueIds || []).includes(lgId));
          if (existing.length >= 2) {
            const lg = platformData && platformData.leagues.find(l => l.id === lgId);
            setBannerError((lg ? lg.leagueName : lgId) + ' already has 2 active sponsored banners (' + existing.map(b => b.name).join(', ') + '). Deactivate one first.');
            return;
          }
        }
      }
    }

    setBannerSaving(true);
    try {
      let imageUrl = bannerDraft.imageUrl;
      const id = editingBannerId || ('banner_' + Date.now());
      if (bannerDraft.imageFile) {
        const path = 'sponsoredBanners/' + id + '/' + bannerDraft.imageFile.name;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, bannerDraft.imageFile);
        imageUrl = await getDownloadURL(fileRef);
      }
      await setDoc(doc(db, 'sponsoredBanners', id), {
        id, name: bannerDraft.name.trim(), linkUrl: bannerDraft.linkUrl.trim(), imageUrl,
        active: bannerDraft.active, scope: bannerDraft.scope,
        leagueIds: bannerDraft.scope === 'specific' ? bannerDraft.leagueIds : [],
        createdAt: editingBannerId ? (platformBanners.find(b => b.id === editingBannerId) || {}).createdAt || new Date().toISOString() : new Date().toISOString()
      });
      if (bannerDraft.active && bannerDraft.scope === 'all') {
        for (const b of others) {
          if (b.active && b.scope === 'all') await updateDoc(doc(db, 'sponsoredBanners', b.id), { active: false });
        }
      }
      setShowBannerForm(false);
      setBannerDraft(emptyBannerDraft);
      setEditingBannerId(null);
    } catch (err) {
      setBannerError('Something went wrong saving that banner. Try again.');
    }
    setBannerSaving(false);
  };

  const toggleBannerActive = async (banner) => {
    if (!banner.active) {
      const others = platformBanners.filter(b => b.id !== banner.id);
      if (banner.scope === 'specific') {
        for (const lgId of (banner.leagueIds || [])) {
          const existing = others.filter(b => b.active && b.scope === 'specific' && (b.leagueIds || []).includes(lgId));
          if (existing.length >= 2) {
            alert('That league already has 2 active sponsored banners. Deactivate one first.');
            return;
          }
        }
      }
      await updateDoc(doc(db, 'sponsoredBanners', banner.id), { active: true });
      if (banner.scope === 'all') {
        for (const b of others) {
          if (b.active && b.scope === 'all') await updateDoc(doc(db, 'sponsoredBanners', b.id), { active: false });
        }
      }
    } else {
      await updateDoc(doc(db, 'sponsoredBanners', banner.id), { active: false });
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this sponsored banner?')) return;
    await deleteDoc(doc(db, 'sponsoredBanners', bannerId));
  };

  useEffect(() => {
    if (!lid) return;
    const unsub = onSnapshot(collection(db, 'leagues', lid, 'fieldClosures'), snap => {
      const closures = snap.docs.map(d => d.data());
      setFieldClosures(closures);
      // Check if any closure affects current user's events today (local date, not UTC —
      // avoids the popup flipping to the wrong day near midnight in the league's timezone)
      const today = getLocalDateStr();
      const todayClosures = closures.filter(cl => cl.date === today);
      if (todayClosures.length > 0 && user) {
        // Show popup for the most recent closure
        const latest = todayClosures.sort((a,b) => new Date(b.closedAt) - new Date(a.closedAt))[0];
        const seenKey = 'groundspal-closure-seen-' + latest.id;
        if (!localStorage.getItem(seenKey)) {
          setFieldClosureNotification(latest);
        }
      }
    });
    return () => unsub();
  }, [lid, user]);

    useEffect(() => {
    if (!lid) return;
    const unsub = onSnapshot(collection(db, 'leagues', lid, 'fieldEvents'), snap => setFieldEvents(snap.docs.map(d => d.data())));
    return () => unsub();
  }, [lid]);

  // ─── SEASONS ────────────────────────────────────────────────────
  useEffect(() => {
    if (!lid) return;
    const unsub = onSnapshot(collection(db, 'leagues', lid, 'seasons'), snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setSeasons(list);
    });
    return () => unsub();
  }, [lid]);

  // One-time migration: if this league has never had a season created, make a
  // default one and quietly attach any pre-existing (un-seasoned) divisions to
  // it, so nothing already built in Scheduler disappears or breaks.
  useEffect(() => {
    if (!lid || !user || user.role !== 'admin' || backfillRanRef.current) return;
    if (seasons.length > 0) { backfillRanRef.current = true; return; }
    (async () => {
      const existingSnap = await getDocs(collection(db, 'leagues', lid, 'seasons'));
      if (!existingSnap.empty) { backfillRanRef.current = true; return; }
      backfillRanRef.current = true;
      const seasonId = 'season_' + Date.now();
      const now = new Date();
      const month = now.getMonth();
      const seasonLabel = month <= 1 || month === 11 ? 'Winter' : month <= 4 ? 'Spring' : month <= 7 ? 'Summer' : 'Fall';
      const year = now.getFullYear();
      await setDoc(doc(db, 'leagues', lid, 'seasons', seasonId), {
        id: seasonId, seasonLabel, customLabel: '', year, name: seasonLabel + ' ' + year,
        startDate: '', endDate: '', status: 'active', createdAt: new Date().toISOString()
      });
      const divisionsSnap = await getDocs(collection(db, 'leagues', lid, 'divisions'));
      for (const d of divisionsSnap.docs) {
        if (!d.data().seasonId) {
          await updateDoc(doc(db, 'leagues', lid, 'divisions', d.id), { seasonId });
        }
      }
    })();
  }, [lid, user, seasons]);

  // Default to the most recently created active season once seasons load.
  useEffect(() => {
    if (currentSeasonId || seasons.length === 0) return;
    const firstActive = seasons.find(s => s.status !== 'archived') || seasons[0];
    if (firstActive) setCurrentSeasonId(firstActive.id);
  }, [seasons, currentSeasonId]);

  const createSeason = async (seasonLabel, customLabel, year) => {
    const label = seasonLabel === 'Other' ? customLabel.trim() : seasonLabel;
    if (!label || !lid) return;
    const seasonId = 'season_' + Date.now();
    await setDoc(doc(db, 'leagues', lid, 'seasons', seasonId), {
      id: seasonId, seasonLabel, customLabel: seasonLabel === 'Other' ? customLabel.trim() : '', year: Number(year),
      name: label + ' ' + year, startDate: '', endDate: '', status: 'active', createdAt: new Date().toISOString()
    });
    setCurrentSeasonId(seasonId);
    setNewSeasonLabel('Fall');
    setNewSeasonCustomLabel('');
    setNewSeasonYear(new Date().getFullYear());
    setCreatingSeason(false);
    setShowSeasonMenu(false);
  };

  const startEditSeason = (season) => {
    setEditingSeasonId(season.id);
    setEditSeasonLabel(season.seasonLabel || 'Other');
    setEditSeasonCustomLabel(season.seasonLabel === 'Other' ? (season.customLabel || season.name) : '');
    setEditSeasonYear(season.year || new Date().getFullYear());
  };

  const saveSeasonEdit = async () => {
    const label = editSeasonLabel === 'Other' ? editSeasonCustomLabel.trim() : editSeasonLabel;
    if (!label || !editingSeasonId) return;
    await updateDoc(doc(db, 'leagues', lid, 'seasons', editingSeasonId), {
      seasonLabel: editSeasonLabel, customLabel: editSeasonLabel === 'Other' ? editSeasonCustomLabel.trim() : '',
      year: Number(editSeasonYear), name: label + ' ' + editSeasonYear
    });
    setEditingSeasonId(null);
  };

  const archiveSeason = async (seasonId) => {
    await updateDoc(doc(db, 'leagues', lid, 'seasons', seasonId), { status: 'archived' });
    if (currentSeasonId === seasonId) {
      const nextActive = seasons.find(s => s.id !== seasonId && s.status !== 'archived');
      setCurrentSeasonId(nextActive ? nextActive.id : null);
    }
  };

  const unarchiveSeason = async (seasonId) => {
    await updateDoc(doc(db, 'leagues', lid, 'seasons', seasonId), { status: 'active' });
  };

  const duplicateSeason = async (sourceSeasonId) => {
    const source = seasons.find(s => s.id === sourceSeasonId);
    if (!source) return;
    const newSeasonId = 'season_' + Date.now();
    const seasonLabel = source.seasonLabel || 'Other';
    const customLabel = source.seasonLabel === 'Other' ? (source.customLabel || source.name) : '';
    const year = (source.year || new Date().getFullYear()) + 1;
    const label = seasonLabel === 'Other' ? customLabel : seasonLabel;
    await setDoc(doc(db, 'leagues', lid, 'seasons', newSeasonId), {
      id: newSeasonId, seasonLabel, customLabel, year, name: label + ' ' + year,
      startDate: '', endDate: '', status: 'active', createdAt: new Date().toISOString()
    });
    const divisionsSnap = await getDocs(query(collection(db, 'leagues', lid, 'divisions'), where('seasonId', '==', sourceSeasonId)));
    for (const dDoc of divisionsSnap.docs) {
      const oldData = dDoc.data();
      const newDivisionId = 'div_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
      await setDoc(doc(db, 'leagues', lid, 'divisions', newDivisionId), {
        ...oldData, id: newDivisionId, seasonId: newSeasonId, createdAt: new Date().toISOString()
      });
      const teamsSnap = await getDocs(query(collection(db, 'leagues', lid, 'teams'), where('divisionId', '==', dDoc.id)));
      for (const tDoc of teamsSnap.docs) {
        const newTeamId = 'team_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
        await setDoc(doc(db, 'leagues', lid, 'teams', newTeamId), {
          id: newTeamId, name: tDoc.data().name, divisionId: newDivisionId, createdAt: new Date().toISOString()
        });
      }
    }
    setCurrentSeasonId(newSeasonId);
    setShowSeasonMenu(false);
  };

  // ─── BOARD NOTIFICATION ──────────────────────────────────────────
  // "Seen" tracking lives on the person's account (see markBoardSeen above), not
  // localStorage, so a new device/browser/incognito session doesn't get treated
  // as "never seen anything" and surface an old post as if it were brand new.
  const prevBoardCountRef = React.useRef(null);
  useEffect(() => {
    if (!boardPosts || boardPosts.length === 0 || !user || !lid) return;
    // Wait until we have the data needed to determine "last seen" — otherwise
    // this would misfire as a false "first load" before members/league arrive.
    if (user.role === 'volunteer' && members.length === 0) return;
    if (user.role === 'admin' && !league) return;

    const latest = boardPosts.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b);

    let lastSeen = null;
    if (user.role === 'volunteer') {
      const me = findMyMemberDoc();
      lastSeen = me && me.lastSeenBoardPost;
    } else if (user.isMainAdmin) {
      lastSeen = league.lastSeenBoardPost;
    } else {
      const myUid = auth.currentUser && auth.currentUser.uid;
      const me = (league.coAdmins || []).find(ca => ca.uid === myUid);
      lastSeen = me && me.lastSeenBoardPost;
    }

    if (prevBoardCountRef.current !== null && boardPosts.length > prevBoardCountRef.current) {
      // A new post arrived while this session was already open — show it live.
      setBoardNotification(latest);
    } else if (prevBoardCountRef.current === null) {
      if (!lastSeen) {
        // Brand-new account with no seen-history at all — silently catch up
        // instead of surfacing whatever the latest post happens to be.
        markBoardSeen(latest.createdAt);
      } else if (new Date(latest.createdAt) > new Date(lastSeen)) {
        setBoardNotification(latest);
      }
    }
    prevBoardCountRef.current = boardPosts.length;
  }, [boardPosts, user, lid, members, league]);

  // ─── COACH IDENTIFICATION: one-time login popup (coach-flag-feature-spec.md, Section 4) ──
  // Checks the signed-in user's own record for coachPromptSeen once league/members have
  // loaded, and shows the popup exactly once per league per user if it's missing. Scoped
  // per-league by design -- coachPromptCheckedRef only suppresses re-checks for the league
  // already checked, so switching leagues re-evaluates independently.
  useEffect(() => {
    if (!user || !lid || !league) return;
    // A superadmin emulating a league is NOT that league's admin answering a question
    // about themselves. Without this, the isMainAdmin branch below fires and
    // saveCoachPromptAnswer's 'admin' branch writes adminIsCoach/adminCoachRole onto the
    // LEAGUE doc -- recording the superadmin's answer as the real owner's coach status
    // (which feeds the coaches CSV export) and setting adminCoachPromptSeen: true, which
    // permanently suppresses the prompt for the actual owner with nothing surfacing that
    // it happened.
    //
    // Reads as "claims to be the main admin, but may not write as them" -- i.e. is
    // emulating. Scoped to the isMainAdmin path on purpose: canWriteAsAdmin() is also
    // false for volunteers and co-admins, who SHOULD still get this prompt. Returning
    // before coachPromptCheckedRef is set is deliberate too, so a superadmin passing
    // through doesn't consume the owner's one-time check.
    if (user.isMainAdmin && !canWriteAsAdmin()) return;
    if (user.role === 'volunteer' && members.length === 0) return;
    if (coachPromptCheckedRef.current === lid) return;

    let seen, actorType;
    if (user.role === 'volunteer') {
      const me = findMyMemberDoc();
      if (!me) return;
      seen = !!me.coachPromptSeen;
      actorType = 'volunteer';
    } else if (user.isMainAdmin) {
      seen = !!league.adminCoachPromptSeen;
      actorType = 'admin';
    } else {
      const myUid = auth.currentUser && auth.currentUser.uid;
      const me = (league.coAdmins || []).find(ca => ca.uid === myUid);
      if (!me) return;
      seen = !!me.coachPromptSeen;
      actorType = 'coadmin';
    }

    coachPromptCheckedRef.current = lid;
    if (!seen) {
      setCoachPromptFor(actorType);
      setCoachPromptIsCoach(undefined);
      setCoachPromptCoachRole(null);
      setShowCoachPrompt(true);
    }
  }, [user, lid, league, members]);

  const saveCoachPromptAnswer = async (isCoach, coachRole) => {
    if (!lid || !user || !coachPromptFor) { setShowCoachPrompt(false); return; }
    setCoachPromptSaving(true);
    try {
      if (coachPromptFor === 'volunteer') {
        const meDoc = findMyMemberDoc();
        if (meDoc) {
          await updateDoc(doc(db, 'leagues', lid, 'members', meDoc.id), {
            isCoach: isCoach === true, coachRole: isCoach === true ? coachRole : null, coachPromptSeen: true
          });
        }
      } else if (coachPromptFor === 'admin') {
        await updateDoc(doc(db, 'leagues', lid), {
          adminIsCoach: isCoach === true, adminCoachRole: isCoach === true ? coachRole : null, adminCoachPromptSeen: true
        });
      } else if (coachPromptFor === 'coadmin') {
        const myUid = auth.currentUser && auth.currentUser.uid;
        const updatedCoAdmins = (league.coAdmins || []).map(ca =>
          ca.uid === myUid ? { ...ca, isCoach: isCoach === true, coachRole: isCoach === true ? coachRole : null, coachPromptSeen: true } : ca
        );
        await updateDoc(doc(db, 'leagues', lid), { coAdmins: updatedCoAdmins });
      }
    } catch (e) {
      // non-critical -- if this fails, the popup just shows again next login
    }
    setCoachPromptSaving(false);
    setShowCoachPrompt(false);
  };

  // "No" is a complete, equally-weighted answer -- save it immediately rather than
  // waiting for a separate submit step.
  const handleCoachPromptChangeIsCoach = (val) => {
    setCoachPromptIsCoach(val);
    if (val === false) saveCoachPromptAnswer(false, null);
  };

  // Closing the popup: before any answer is picked, this is a true dismiss and must
  // NOT set coachPromptSeen (spec Section 4). Once "Yes" has been picked, closing still
  // counts as a full answer (isCoach: true, coachRole possibly still null).
  const closeCoachPrompt = () => {
    if (coachPromptIsCoach === true) saveCoachPromptAnswer(true, coachPromptCoachRole);
    else setShowCoachPrompt(false);
  };

  // ─── DEMO MODE ──────────────────────────────────────────────────────────────
  if (demoMode) {
    const dTasks = DEMO_TASKS; const dArchive = DEMO_ARCHIVE; const dMembers = DEMO_MEMBERS;
    const dBoard = DEMO_BOARD; const dEvents = DEMO_EVENTS; const dSupplies = DEMO_SUPPLIES;
    const dUser = DEMO_USER; const dLeague = DEMO_LEAGUE; const dLocs = DEMO_LOCATIONS;
    const dStats = { open: dTasks.filter(t=>t.status==='open').length, ip: dTasks.filter(t=>t.status==='in-progress').length, arch: dArchive.length, sup: dSupplies.length };
    const dAlert = (msg) => alert('🔒 ' + msg + '\n\nSign up for free to use this feature!');
    return (
      <div style={{ background: '#F2F2F7', minHeight: '100vh', fontFamily: T.body }}>
        {/* Demo Banner */}
        <div style={{ background: '#1A7A35', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>👀 You're viewing a demo league — data is not real</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background: '#fff', color: '#1A7A35', border: 'none', borderRadius: R.control, padding: '6px 14px', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Create Free Account →</button>
            <button onClick={() => setDShowSettings(v => !v)} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: R.control, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>⚙️ Settings</button>
            <button onClick={() => setDemoMode(false)} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: R.control, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Exit Demo</button>
          </div>
        </div>
        {/* Demo Settings Panel */}
        {dShowSettings && (
          <div style={{ background: '#fff', borderBottom: '2px solid #E5E5E5', padding: '16px', maxHeight: 480, overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', marginBottom: 16 }}>⚙️ League Settings</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Invite Code */}
              <div style={{ background: '#E6F1FB', border: '2px solid #378ADD', borderRadius: R.control, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 6 }}>🙋 VOLUNTEER INVITE CODE</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#378ADD', letterSpacing: 6, textAlign: 'center', padding: '8px 0' }}>DEMO42</div>
                <div style={{ fontSize: 12, color: '#8E8E93', textAlign: 'center' }}>Share this with volunteers so they can join your league</div>
              </div>
              {/* Locations */}
              <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: R.card, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1C1C1E' }}>📍 Locations</div>
                    <div style={{ fontSize: 12, color: '#8E8E93' }}>Name the locations used in your league</div>
                  </div>
                  <button onClick={() => dAlert('Edit your field locations')} style={{ background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: R.control, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#1565C0', fontWeight: 700, fontFamily: 'inherit' }}>✏️ Edit</button>
                </div>
                {['Majors Field','Minors Field','Pee Wee Field','Batting Cages'].map((f,i) => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F2F2F7', borderRadius: R.control, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#8E8E93', width: 18 }}>{i+1}.</span>
                    <span style={{ fontSize: 14, color: '#1C1C1E' }}>{f}</span>
                  </div>
                ))}
                <button onClick={() => dAlert('Add new field locations')} style={{ background: '#E8F9ED', border: '1px dashed #A5D6A7', borderRadius: R.control, padding: '8px', width: '100%', color: '#34C759', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>+ Add Location</button>
              </div>
              {/* Weather */}
              <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: R.card, padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1C1C1E', marginBottom: 4 }}>☁️ Weather Forecast</div>
                <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 10 }}>Enter your league zip code to show weather on the dashboard</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input disabled defaultValue="90210" style={{ flex: 1, padding: '9px 12px', borderRadius: R.control, border: '1px solid #E5E5E5', fontSize: 14, fontFamily: 'inherit', background: '#F2F2F7', fontWeight: 700, letterSpacing: 2 }} />
                  <button onClick={() => dAlert('Save your zip code for weather')} style={{ background: '#34C759', color: '#1C1C1E', border: 'none', borderRadius: R.control, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
                </div>
                <div style={{ fontSize: 11, color: '#34C759', marginTop: 6, fontWeight: 600 }}>✓ Currently set to: 90210</div>
              </div>
              {/* Group Notifications */}
              <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: R.card, padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1C1C1E', marginBottom: 4 }}>💬 Group Notifications</div>
                <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 10 }}>Connect a messaging group to receive task and event alerts</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {['GroupMe','Band','Remind','Other'].map(p => (
                    <div key={p} style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: R.control, border: '2px solid ' + (p==='GroupMe'?'#34C759':'#E5E5EA'), background: p==='GroupMe'?'#E8F9ED':'#fff', fontSize: 11, fontWeight: 700, color: p==='GroupMe'?'#34C759':'#8E8E93', cursor: 'pointer' }}>{p}</div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input disabled placeholder="Paste GroupMe Bot ID..." style={{ flex: 1, padding: '9px 12px', borderRadius: R.control, border: '1px solid #E5E5E5', fontSize: 13, fontFamily: 'inherit', background: '#F2F2F7' }} />
                  <button onClick={() => dAlert('Connect your GroupMe bot')} style={{ background: '#34C759', color: '#1C1C1E', border: 'none', borderRadius: R.control, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
                </div>
                <div style={{ background: '#F2F2F7', borderRadius: R.control, padding: '10px 12px', marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C1E', marginBottom: 4 }}>Messages sent to your group:</div>
                  <div style={{ fontSize: 11, color: '#8E8E93', lineHeight: 1.8 }}>✅ Task completed · 📌 New board post · 🚫 Field closed · ✅ Field reopened</div>
                </div>
              </div>
            </div>
            <div style={{ background: '#1A7A35', borderRadius: R.control, padding: '14px 16px', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#fff' }}>Set up your own league settings</div>
              <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background: '#fff', color: '#1A7A35', border: 'none', borderRadius: R.control, padding: '8px 16px', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Create Free Account →</button>
            </div>
          </div>
        )}

        {/* Demo App Header */}
        <div style={{ background: '#34C759', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={LOGO_URL} alt="GroundsPal" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: R.control }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1C1C1E' }}>Riverside Youth Baseball</div>
              <div style={{ fontSize: 11, color: 'rgba(28,28,30,0.7)' }}>Admin · Coach Rivera</div>
            </div>
          </div>
        </div>
        {/* Demo Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, padding: '12px 16px 0' }}>
          {[
            { label: 'Open', value: dStats.open, color: '#1565C0', bg: '#E3F2FD', icon: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>' },
            { label: 'In Progress', value: dStats.ip, color: '#E65100', bg: '#FFF3E0', icon: '<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>' },
            { label: 'Completed', value: dStats.arch, color: '#34C759', bg: '#E8F9ED', icon: '<polyline points="20 6 9 17 4 12"/>' },
            { label: 'Supplies', value: dStats.sup, color: '#501313', bg: '#FCEBEB', icon: '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>' }
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius: R.card, border:'1px solid #E5E5E5', padding:'14px 8px', textAlign:'center' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: s.icon }} />
              </div>
              <div style={{ fontSize:20, fontWeight:700, color:'#1C1C1E' }}>{s.value}</div>
              <div style={{ fontSize:10, color:'#8E8E93', marginTop:3, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Demo Weather */}
        <div style={{ background: 'linear-gradient(135deg, #1976D2, #1565C0)', borderRadius: R.control, margin: '12px 16px 0', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 36 }}>⛅</span>
              <div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>82°F</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Partly Cloudy · Feels 87°</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>↑88° ↓74°</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>💧 12% · 🌬️ 6mph</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Riverside, CA</div>
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: R.control, padding: '8px 12px', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>✅</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Good conditions for field work</span>
          </div>
        </div>

        {/* Demo Tabs */}
        {(() => {
          const C2 = { green:'#1A7A35', greenMid:'#34C759', greenLt:'#E8F9ED', greenBd:'#A3E6B8', amber:'#E65100', amberLt:'#FFF3E0', amberBd:'#FFCC02', red:'#501313', redLt:'#FCEBEB', redBd:'#E24B4A', blue:'#1565C0', blueLt:'#E3F2FD', blueBd:'#90CAF9', text:'#1C1C1E', muted:'#8E8E93', border:'#E5E5EA', white:'#FFFFFF', surface:'#F2F2F7', grayLt:'#F2F2F7', grayBd:'#D6D6D4' };
          const tabs = [
            {k:'tasks',l:'Tasks',tip:'View, create and manage field maintenance tasks. Assign them to volunteers, set priorities and due dates, and track progress from open to complete.'},
            {k:'events',l:'📅 Events',tip:'Request field time, schedule games and practices on a field calendar broken down by location.'},
            {k:'board',l:'📌 Board',tip:'Keep your volunteers posted with announcements, updates, and reminders. Crew members can like and see who liked each post.'},
            {k:'crewstats',l:'🏅 Crew Stats',tip:'Leaderboard showing your most active volunteers based on completed tasks. Recognizes your top contributors.'},
            {k:'supplies',l:'Supplies',tip:'Lets the league know when supplies are needed. Log what you have and mark items as purchased.'},
          ];
          return (
            <div>
              <div style={{ display:'flex', borderBottom:'2px solid #E5E5E5', padding:'0 16px', background:'#fff', overflowX:'auto' }}>
                {tabs.map(t => (
                  <button key={t.k} onClick={() => setDTab(t.k)} onMouseEnter={() => setDHoveredTab(t.k)} onMouseLeave={() => setDHoveredTab(null)} style={{ padding:'12px 14px', background:'none', border:'none', borderBottom: dTab===t.k ? '2px solid #2E7D32' : '2px solid transparent', marginBottom:'-2px', fontWeight: dTab===t.k ? 700 : 400, color: dTab===t.k ? '#34C759' : '#8E8E93', cursor:'pointer', fontSize:14, whiteSpace:'nowrap', fontFamily:'inherit' }}>{t.l}</button>
                ))}
              </div>
              {dHoveredTab && (() => {
                const tip = tabs.find(t => t.k === dHoveredTab);
                return tip ? (
                  <div style={{ background:'#1A7A35', color:'#fff', padding:'10px 16px', fontSize:12, lineHeight:1.6, borderBottom:'2px solid #2E7D32' }}>
                    <span style={{ fontWeight:700 }}>{tip.l.replace('📅 ','').replace('📌 ','').replace('🏅 ','')}:</span> {tip.tip}
                  </div>
                ) : null;
              })()}
              <div style={{ padding:'14px 16px' }}>
                {dTab === 'tasks' && (
                  <div>
                    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                      <button onClick={() => setDShowNewTask(true)} style={{ background:'#34C759', color:'#1C1C1E', border:'none', borderRadius: R.control, padding:'8px 16px', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>+ New Task</button>
                    </div>
                    {dShowNewTask && (
                      <div style={{ background:'#fff', border:'2px solid #A5D6A7', borderRadius: R.card, padding:16, marginBottom:16, position:'relative', overflow:'hidden' }}>
                        <div style={{ fontWeight:800, fontSize:15, color:'#1C1C1E', marginBottom:14 }}>New Task</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                          <input placeholder="Task title *" disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', background:'#F2F2F7', color:'#1C1C1E' }} defaultValue="Mow and prep Majors Field" />
                          <textarea placeholder="Description (optional)" disabled rows={2} style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', resize:'none', background:'#F2F2F7' }} defaultValue="Cut grass to 2 inches and chalk all baselines" />
                          <div>
                            <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:6 }}>SELECT LOCATION(S)</div>
                            {['Majors Field','Minors Field','Pee Wee Field','Batting Cages'].map(loc => (
                              <div key={loc} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius: R.control, border:'1px solid ' + (loc==='Majors Field' ? '#A3E6B8' : '#E5E5EA'), background: loc==='Majors Field' ? '#E8F9ED' : '#F2F2F7', marginBottom:6 }}>
                                <span style={{ fontSize:14, fontWeight: loc==='Majors Field'?700:400, color: loc==='Majors Field'?'#1A7A35':'#1C1C1E' }}>📍 {loc}</span>
                                <div style={{ width:44, height:24, borderRadius: R.control, background: loc==='Majors Field'?'#34C759':'#D6D6D4', position:'relative', flexShrink:0 }}>
                                  <div style={{ position:'absolute', top:3, left: loc==='Majors Field'?23:3, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>PRIORITY</div>
                              <select disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, background:'#F2F2F7', fontFamily:'inherit', width:'100%' }}><option>High</option></select>
                            </div>
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>DUE DATE</div>
                              <input type="date" disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', width:'100%', boxSizing:'border-box', background:'#F2F2F7' }} />
                            </div>
                          </div>
                        </div>
                        <div style={{ background:'rgba(27,94,32,0.97)', borderRadius: R.control, padding:'14px 16px', marginTop:14, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:13, color:'#fff' }}>Ready to create real tasks?</div>
                            <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:2 }}>Sign up free — takes 2 minutes</div>
                          </div>
                          <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background:'#fff', color:'#1A7A35', border:'none', borderRadius: R.control, padding:'8px 16px', fontWeight:800, fontSize:13, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>Create Free Account →</button>
                        </div>
                        <button onClick={() => setDShowNewTask(false)} style={{ position:'absolute', top:12, right:12, background:'#F2F2F7', border:'none', borderRadius: R.control, width:28, height:28, cursor:'pointer', fontSize:16, color:'#8E8E93' }}>✕</button>
                      </div>
                    )}
                    {dTasks.map(t => (
                      <div key={t.id} onClick={() => dAlert('View task details, claim tasks, and add updates')} style={{ background:'#fff', borderRadius: R.card, border:'1px solid #E5E5E5', padding:'14px 16px', marginBottom:10, cursor:'pointer' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, flexWrap:'wrap', gap:4 }}>
                          <span style={{ background: t.status==='open' ? '#E3F2FD' : '#FFF3E0', color: t.status==='open' ? '#1565C0' : '#E65100', borderRadius: R.control, padding:'2px 8px', fontSize:11, fontWeight:700 }}>{t.status==='open' ? 'Open' : 'In Progress'}</span>
                          <span style={{ background: t.priority==='high' ? '#FCEBEB' : t.priority==='medium' ? '#FFF3E0' : '#F2F2F7', color: t.priority==='high' ? '#501313' : t.priority==='medium' ? '#E65100' : '#8E8E93', borderRadius: R.control, padding:'2px 8px', fontSize:11, fontWeight:700 }}>{t.priority}</span>
                        </div>
                        <div style={{ fontWeight:700, fontSize:14, color:'#1C1C1E', marginBottom:4 }}>{t.title}</div>
                        <div style={{ fontSize:12, color:'#8E8E93' }}>📍 {t.location}{t.assignedTo ? ' · Assigned to ' + t.assignedTo : ' · Unassigned'}</div>
                        {t.dueDate && <div style={{ fontSize:11, color:'#E65100', marginTop:4 }}>Due {t.dueDate}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {dTab === 'events' && (() => {
                  const today = new Date();
                  const weekStart = new Date(today); weekStart.setDate(today.getDate() - today.getDay()); weekStart.setHours(0,0,0,0);
                  const days = Array.from({length:7}, (_,i) => { const d = new Date(weekStart); d.setDate(d.getDate()+i); return d; });
                  const isToday = (d) => d.toDateString() === today.toDateString();
                  const fmtDay = (d) => d.toLocaleDateString('en-US',{weekday:'short'});
                  const fmtTime = (t) => { if(!t) return ''; const [h,m]=t.split(':').map(Number); const ap=h>=12?'pm':'am'; const hr=h%12||12; return hr+(m>0?':'+String(m).padStart(2,'0'):'')+ap; };
                  const demoFields = ['Majors Field','Minors Field','Pee Wee Field','Batting Cages'];
                  const getEventsForFieldDay = (field, date) => {
                    const ds = date.toISOString().split('T')[0];
                    return DEMO_EVENTS.filter(e => e.field===field && e.date===ds);
                  };
                  return (
                    <div>
                      {/* Location Status */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E', marginBottom: 8 }}>🚫 Location Status</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {['Majors Field','Minors Field','Pee Wee Field','Batting Cages'].map(field => {
                            const isClosed = dClosedFields.includes(field);
                            return (
                              <div key={field} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius: R.control, border:'1px solid '+(isClosed?'#E24B4A':'#E5E5EA'), background:isClosed?'#FCEBEB':'#fff', flexWrap:'wrap', gap:8 }}>
                                <div>
                                  <div style={{ fontWeight:700, fontSize:13, color:isClosed?'#501313':'#1C1C1E' }}>{isClosed?'🚫 ':'✅ '}{field}</div>
                                  {isClosed && <div style={{ fontSize:11, color:'#501313' }}>Closed due to weather</div>}
                                </div>
                                {isClosed ? (
                                  <button onClick={() => setDClosedFields(prev => prev.filter(f => f !== field))} style={{ background:'#fff', border:'1px solid #E5E5E5', borderRadius: R.control, padding:'5px 12px', cursor:'pointer', fontSize:12, fontWeight:700, color:'#1C1C1E', fontFamily:'inherit' }}>Reopen</button>
                                ) : (
                                  <div>
                                    <div style={{ fontSize:10, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>CLOSE DUE TO:</div>
                                    <div style={{ display:'flex', gap:6 }}>
                                      {['Weather','Location Conditions','Other'].map(reason => (
                                        <button key={reason} onClick={() => setDClosedFields(prev => [...prev, field])} style={{ background:'#F2F2F7', border:'1px solid #E5E5E5', borderRadius: R.control, padding:'4px 10px', cursor:'pointer', fontSize:11, fontWeight:700, color:'#1C1C1E', fontFamily:'inherit' }}>{reason}</button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <button style={{ background:'#fff', border:'1px solid #E5E5E5', borderRadius: R.control, width:32, height:32, cursor:'pointer', fontSize:18 }}>‹</button>
                          <button style={{ background:'#fff', border:'1px solid #E5E5E5', borderRadius: R.control, width:32, height:32, cursor:'pointer', fontSize:18 }}>›</button>
                          <button style={{ background:'#fff', border:'1px solid #E5E5E5', borderRadius: R.control, padding:'5px 12px', cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>Today</button>
                          <span style={{ fontWeight:700, fontSize:13, color:'#1C1C1E' }}>{days[0].toLocaleDateString('en-US',{month:'short',day:'numeric'})} – {days[6].toLocaleDateString('en-US',{month:'short',day:'numeric'})}, {days[0].getFullYear()}</span>
                        </div>
                        <button onClick={() => setDShowNewEvent(true)} style={{ background:'#34C759', color:'#1C1C1E', border:'none', borderRadius: R.control, padding:'7px 14px', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>+ New Event</button>
                      </div>
                      <div style={{ display:'flex', gap:16, marginBottom:10 }}>
                        {[{color:'#1565C0',label:'Game'},{color:'#34C759',label:'Practice'},{color:'#E65100',label:'Other'}].map(t => (
                          <div key={t.label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <div style={{ width:10, height:10, borderRadius: R.control, background:t.color }} />
                            <span style={{ fontSize:11, color:'#8E8E93' }}>{t.label}</span>
                          </div>
                        ))}
                      </div>
                      {dShowNewEvent && (
                        <div style={{ background:'#fff', border:'2px solid #A5D6A7', borderRadius: R.card, padding:16, marginBottom:16, position:'relative' }}>
                          <div style={{ fontWeight:800, fontSize:15, color:'#1C1C1E', marginBottom:14 }}>New Event</div>
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:6 }}>EVENT TYPE *</div>
                              <div style={{ display:'flex', gap:8 }}>
                                {[{k:'game',l:'Game',color:'#1565C0'},{k:'practice',l:'Practice',color:'#34C759'},{k:'other',l:'Other',color:'#E65100'}].map(t => (
                                  <div key={t.k} style={{ flex:1, textAlign:'center', padding:'8px 4px', borderRadius: R.control, border:'2px solid '+(t.k==='game'?t.color:'#E5E5EA'), background:t.k==='game'?t.color:'#fff', color:t.k==='game'?'#fff':'#8E8E93', fontWeight:700, fontSize:13, cursor:'pointer' }}>{t.l}</div>
                                ))}
                              </div>
                            </div>
                            <input placeholder="Event title *" disabled defaultValue="Opening Day Game" style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', background:'#F2F2F7' }} />
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>DATE(S) *</div>
                                <input type="date" disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', width:'100%', boxSizing:'border-box', background:'#F2F2F7' }} />
                                <button style={{ fontSize:12, color:'#34C759', background:'none', border:'none', cursor:'pointer', padding:'4px 0', fontFamily:'inherit' }}>+ Add another date</button>
                              </div>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>FIELD *</div>
                                <select disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, background:'#F2F2F7', fontFamily:'inherit', width:'100%' }}>
                                  <option>Majors Field</option>
                                  <option>Minors Field</option>
                                  <option>Pee Wee Field</option>
                                  <option>Batting Cages</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>START TIME</div>
                                <input type="time" disabled defaultValue="10:00" style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', width:'100%', boxSizing:'border-box', background:'#F2F2F7' }} />
                              </div>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>END TIME</div>
                                <input type="time" disabled defaultValue="12:00" style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', width:'100%', boxSizing:'border-box', background:'#F2F2F7' }} />
                              </div>
                            </div>
                            <textarea placeholder="Notes (optional)" disabled rows={2} style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', resize:'none', background:'#F2F2F7' }} defaultValue="vs. Eastside All-Stars" />
                          </div>
                          <div style={{ background:'rgba(27,94,32,0.97)', borderRadius: R.control, padding:'14px 16px', marginTop:14, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                            <div>
                              <div style={{ fontWeight:800, fontSize:13, color:'#fff' }}>Ready to schedule real events?</div>
                              <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:2 }}>Sign up free — takes 2 minutes</div>
                            </div>
                            <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background:'#fff', color:'#1A7A35', border:'none', borderRadius: R.control, padding:'8px 16px', fontWeight:800, fontSize:13, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>Create Free Account →</button>
                          </div>
                          <button onClick={() => setDShowNewEvent(false)} style={{ position:'absolute', top:12, right:12, background:'#F2F2F7', border:'none', borderRadius: R.control, width:28, height:28, cursor:'pointer', fontSize:16, color:'#8E8E93' }}>✕</button>
                        </div>
                      )}
                      <div style={{ overflowX:'auto' }}>
                        <div style={{ minWidth:700 }}>
                          <div style={{ display:'grid', gridTemplateColumns:'80px repeat(7,1fr)', marginBottom:6 }}>
                            <div />
                            {days.map((d,i) => (
                              <div key={i} style={{ textAlign:'center', padding:'4px 2px', borderRadius: R.control, background: isToday(d)?'#1A7A35':'transparent', margin:'0 2px' }}>
                                <div style={{ fontSize:9, fontWeight:700, color: isToday(d)?'rgba(255,255,255,0.8)':'#8E8E93', letterSpacing:'0.06em' }}>{fmtDay(d).toUpperCase()}</div>
                                <div style={{ fontSize:16, fontWeight:700, color: isToday(d)?'#fff':'#1C1C1E' }}>{d.getDate()}</div>
                                <div style={{ fontSize:9, color: isToday(d)?'rgba(255,255,255,0.7)':'#8E8E93' }}>{d.toLocaleDateString('en-US',{month:'short'})}</div>
                              </div>
                            ))}
                          </div>
                          {demoFields.map(field => (
                            <div key={field} style={{ display:'grid', gridTemplateColumns:'80px repeat(7,1fr)', marginBottom:4, alignItems:'stretch' }}>
                              <div style={{ padding:'6px 4px', fontSize:11, fontWeight:700, color:'#1C1C1E', background:'#F2F2F7', borderRadius: R.control, textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center', marginRight:3 }}>{field}</div>
                              {days.map((d,i) => {
                                const evs = getEventsForFieldDay(field, d);
                                return (
                                  <div key={i} onClick={() => dAlert('Click events to view details or add new events')} style={{ minHeight:48, background: isToday(d)?'#E8F9ED':'#F2F2F7', borderRadius: R.control, padding:3, margin:'0 2px', cursor:'pointer', border:'1px solid '+(isToday(d)?'#A3E6B8':'transparent'), boxSizing:'border-box' }}>
                                    {evs.length===0 && <div style={{ height:'100%', minHeight:42, display:'flex', alignItems:'center', justifyContent:'center', color:'#D6D6D4', fontSize:18, opacity:0.6 }}>+</div>}
                                    {evs.map(ev => (
                                      <div key={ev.id} style={{ background: ev.eventType==='game'?'#1565C0':ev.eventType==='practice'?'#34C759':'#E65100', borderRadius: R.control, padding:'4px 6px', marginBottom:2 }}>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</div>
                                        <div style={{ fontSize:9, color:'rgba(255,255,255,0.85)' }}>{fmtTime(ev.startTime)}{ev.endTime?' – '+fmtTime(ev.endTime):''}</div>
                                        <div style={{ fontSize:9, color:'rgba(255,255,255,0.7)' }}>by {ev.createdBy}</div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {dTab === 'board' && (
                  <div>
                    {dBoard.map(post => (
                      <div key={post.id} style={{ background:'#fff', borderRadius: R.card, border:'1px solid #E5E5E5', marginBottom:12, overflow:'hidden' }}>
                        <div style={{ background:'#34C759', padding:'12px 16px' }}>
                          <div style={{ fontWeight:800, fontSize:14, color:'#1C1C1E' }}>{post.title}</div>
                          <div style={{ fontSize:11, color:'rgba(28,28,30,0.75)', marginTop:2 }}>Posted by {post.author}</div>
                        </div>
                        <div style={{ padding:'14px 16px' }}>
                          <p style={{ fontSize:14, color:'#1C1C1E', margin:0, lineHeight:1.7 }}>{post.body}</p>
                          <div style={{ borderTop:'1px solid #E5E5E5', marginTop:12, paddingTop:10 }}>
                            <button onClick={() => dAlert('Like board posts')} style={{ background:'#fff', border:'2px solid #1565C0', borderRadius: R.control, padding:'5px 14px', fontSize:13, fontWeight:700, color:'#1565C0', cursor:'pointer', fontFamily:'inherit' }}>👍 Like {post.likes.length > 0 ? '· ' + post.likes.length : ''}</button>
                            {post.likes.length > 0 && <span style={{ fontSize:12, color:'#8E8E93', marginLeft:10 }}>{post.likes.join(', ')}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => dAlert('Post announcements to your crew')} style={{ background:'#E8F9ED', border:'2px dashed #A5D6A7', borderRadius: R.control, padding:'14px', width:'100%', color:'#34C759', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>+ Post Announcement</button>
                  </div>
                )}
                {dTab === 'crewstats' && (
                  <div>
                    <div style={{ fontWeight:800, fontSize:16, marginBottom:4 }}>🏅 Crew Stats</div>
                    <div style={{ fontSize:12, color:'#8E8E93', marginBottom:16 }}>Tasks completed by each crew member</div>
                    {[{name:'Coach Rivera',role:'admin',count:0},{name:'Jamie Rodriguez',role:'volunteer',count:3},{name:'Marcus Thompson',role:'volunteer',count:2},{name:'Sarah Chen',role:'volunteer',count:2},{name:'Tyler Brooks',role:'volunteer',count:1},{name:'Lisa Park',role:'volunteer',count:1}].sort((a,b)=>b.count-a.count).map((m,idx) => (
                      <div key={m.name} style={{ background:'#fff', borderRadius: R.card, border:'1px solid #E5E5E5', padding:'14px 16px', marginBottom:8, display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:40, height:40, borderRadius:'50%', background: idx===0 ? '#E65100' : idx===1 ? '#D6D6D4' : idx===2 ? '#cd7f32' : '#E8F9ED', display:'flex', alignItems:'center', justifyContent:'center', fontSize: idx<3?18:13, fontWeight:800, color: idx<3?'#fff':'#34C759', flexShrink:0 }}>
                          {idx===0?'🥇':idx===1?'🥈':idx===2?'🥉':idx+1}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:14 }}>{m.name}</div>
                          <div style={{ fontSize:11, color:'#8E8E93', textTransform:'capitalize' }}>{m.role}</div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:24, fontWeight:800, color: m.count>0?'#34C759':'#8E8E93', lineHeight:1 }}>{m.count}</div>
                          <div style={{ fontSize:11, color:'#8E8E93' }}>task{m.count!==1?'s':''} done</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {dTab === 'supplies' && (
                  <div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                      <div style={{ fontWeight:800, fontSize:16 }}>🧰 Supplies</div>
                      <button onClick={() => setDShowNewSupply(v => !v)} style={{ background:'#34C759', color:'#1C1C1E', border:'none', borderRadius: R.control, padding:'7px 14px', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>+ Request Supply</button>
                    </div>
                    {dShowNewSupply && (
                      <div style={{ background:'#fff', border:'2px solid #A5D6A7', borderRadius: R.card, padding:16, marginBottom:16, position:'relative' }}>
                        <div style={{ fontWeight:800, fontSize:15, color:'#1C1C1E', marginBottom:14 }}>Supply Request</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                          <input placeholder="Supply name *" disabled defaultValue="Infield Conditioner (50 lb bags)" style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', background:'#F2F2F7' }} />
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>QUANTITY NEEDED</div>
                              <input type="number" disabled defaultValue="4" style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', width:'100%', boxSizing:'border-box', background:'#F2F2F7' }} />
                            </div>
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>UNIT</div>
                              <select disabled style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, background:'#F2F2F7', fontFamily:'inherit', width:'100%' }}>
                                <option>bags</option><option>units</option><option>cases</option><option>boxes</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize:11, fontWeight:700, color:'#8E8E93', letterSpacing:'0.08em', marginBottom:4 }}>URGENCY</div>
                            <div style={{ display:'flex', gap:8 }}>
                              {[{k:'low',l:'Low',c:'#34C759'},{k:'medium',l:'Medium',c:'#E65100'},{k:'high',l:'High — Needed ASAP',c:'#501313'}].map(u => (
                                <div key={u.k} style={{ flex:1, textAlign:'center', padding:'7px 4px', borderRadius: R.control, border:'2px solid '+(u.k==='medium'?u.c:'#E5E5EA'), background:u.k==='medium'?'#FFF3E0':'#fff', fontSize:11, fontWeight:700, color:u.k==='medium'?u.c:'#8E8E93', cursor:'pointer' }}>{u.l}</div>
                              ))}
                            </div>
                          </div>
                          <textarea placeholder="Notes (optional)" disabled rows={2} style={{ padding:'9px 12px', borderRadius: R.control, border:'1px solid #E5E5E5', fontSize:14, fontFamily:'inherit', resize:'none', background:'#F2F2F7' }} defaultValue="Running low before weekend games — need at least 4 bags." />
                        </div>
                        <div style={{ background:'rgba(27,94,32,0.97)', borderRadius: R.control, padding:'14px 16px', marginTop:14, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:13, color:'#fff' }}>Track all your field supplies</div>
                            <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:2 }}>Sign up free — takes 2 minutes</div>
                          </div>
                          <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background:'#fff', color:'#1A7A35', border:'none', borderRadius: R.control, padding:'8px 16px', fontWeight:800, fontSize:13, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>Create Free Account →</button>
                        </div>
                        <button onClick={() => setDShowNewSupply(false)} style={{ position:'absolute', top:12, right:12, background:'#F2F2F7', border:'none', borderRadius: R.control, width:28, height:28, cursor:'pointer', fontSize:16, color:'#8E8E93' }}>✕</button>
                      </div>
                    )}
                    {dSupplies.map(s => (
                      <div key={s.id} style={{ background:'#fff', borderRadius: R.card, border:'1px solid #E5E5E5', padding:'14px 16px', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:14 }}>{s.name}</div>
                          <div style={{ fontSize:12, color:'#8E8E93' }}>Qty: {s.quantity} {s.unit}{s.notes ? ' · ' + s.notes : ''}</div>
                        </div>
                        <button onClick={() => dAlert('Mark supplies as purchased')} style={{ background:'#E8F9ED', border:'1px solid #A5D6A7', borderRadius: R.control, padding:'6px 12px', color:'#34C759', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>✓ Mark Purchased</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* Demo Footer CTA */}
        <div style={{ background:'#1A7A35', padding:'20px 16px', textAlign:'center', marginTop:20 }}>
          <div style={{ fontWeight:800, fontSize:16, color:'#fff', marginBottom:6 }}>Ready to set up your league?</div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,0.8)', marginBottom:14 }}>Free to use. Takes less than 2 minutes to get started.</div>
          <button onClick={() => { setDemoMode(false); setScreen('admin-signup'); }} style={{ background:'#fff', color:'#1A7A35', border:'none', borderRadius: R.control, padding:'12px 28px', fontWeight:800, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>Create Your Free League →</button>
        </div>
      </div>
    );
  }

  if (appLoading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400, color: C.muted, fontSize: 14 }}>Loading GroundsPal...</div>
  );

  // ─── LANDING ─────────────────────────────────────────────────────
  if (screen === 'landing') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, letterSpacing: "-0.5px", textAlign: "center" }}>GroundsPal</div>
      <div style={{ fontSize: 13, color: C.muted, marginTop: 4, marginBottom: "2rem", textAlign: "center" }}>Your League Grounds Maintenance Pal</div>
      <div style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Returning Users */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>RETURNING USER</div>
        <button onClick={() => { setScreen('signin'); setSigninError(""); setSigninEmail(""); setAdminEmail(""); setAdminPassword(""); }}
          style={{ background: C.greenMid, color: C.text, border: "1.5px solid " + C.text, borderRadius: R.control, padding: "16px 18px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "left", width: "100%", marginBottom: 16 }}>
          
          <div>Sign In</div>
          <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.75, marginTop: 2 }}>Admin or volunteer — sign back in to your league</div>
        </button>

        {/* Demo Mode — entry point hidden 2026-08-07, demo screen is stale vs. current sidebar redesign. See CLAUDE.md/CHANGELOG for context. Underlying demoMode code intentionally left intact below. */}

      {/* New Users */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>NEW TO GROUNDS PAL</div>
        {[
          { scr: 'admin-signup', icon: "", title: "Set Up My League", sub: "Create a new league as an administrator", bg: C.white, color: C.text, border: "1.5px solid " + C.greenBd },
          { scr: 'volunteer-join', icon: "", title: "Join as Volunteer", sub: "Enter your league invite code to get started", bg: C.white, color: C.text, border: "1.5px solid " + C.greenBd },
          { scr: 'coadmin-signup', icon: "", title: "Join as Co-Admin", sub: "Use your co-admin invite code to join a league", bg: C.white, color: C.text, border: "1.5px solid " + C.greenBd },
          { scr: 'claim-league', icon: "", title: "Claim a League", sub: "Use your claim code to become the owner of a league set up for you", bg: C.white, color: C.text, border: "1.5px solid " + C.greenBd }
        ].map(btn => (
          <button key={btn.scr} onClick={() => { setScreen(btn.scr); setAuthError(""); }}
            style={{ background: btn.bg, color: btn.color, border: btn.border, borderRadius: R.control, padding: "16px 18px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            {btn.icon && <div style={{ fontSize: 20, marginBottom: 3 }}>{btn.icon}</div>}
            <div>{btn.title}</div>
            <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>{btn.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ marginTop: 32, display: "flex", gap: 10, alignItems: "center", justifyContent: "center" }}>
        <a href="/privacy" style={{ fontSize: 11, color: C.muted, textDecoration: "none" }}>Privacy Policy</a>
        <span style={{ fontSize: 11, color: C.border }}>·</span>
        <a href="/terms" style={{ fontSize: 11, color: C.muted, textDecoration: "none" }}>Terms of Service</a>
      </div>

    </div>
  );

  // ─── ADMIN SIGNUP ─────────────────────────────────────────────────
  if (screen === 'admin-signup') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Set Up Your League</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Create your account and league in one step</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="LEAGUE / ORGANIZATION NAME" placeholder="e.g. Houston Youth Baseball" value={leagueName} onChange={e => setLeagueName(e.target.value)} />
          <FieldInput label="YOUR FULL NAME" placeholder="e.g. Aaron Warren" value={adminName} onChange={e => setAdminName(e.target.value)} />
          <FieldInput label="YOUR EMAIL ADDRESS" type="email" placeholder="you@example.com" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} />
          <FieldInput label="CREATE A PASSWORD" type="password" placeholder="At least 6 characters" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} />
          <FieldInput label="LEAGUE ZIP CODE" placeholder="e.g. 77001" value={signupZipCode} onChange={e => setSignupZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))} onKeyDown={e => e.key === 'Enter' && handleAdminSignup()} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: -8 }}>Used for weather forecasts and timezone-accurate field closures.</div>
          <div style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.card, padding: "12px 14px" }}>
            <CoachPrompt leagueName={leagueName.trim() || "your league"} isCoach={adminIsCoach} coachRole={adminCoachRole}
              onChangeIsCoach={setAdminIsCoach} onChangeCoachRole={setAdminCoachRole} />
          </div>
          {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{authError}</div>}
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center" }}>
            By creating an account, you agree to our <a href="/terms" style={{ color: C.muted }}>Terms</a> and <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
          </div>
          <Btn onClick={handleAdminSignup} disabled={authLoading} full>{authLoading ? "Creating your league..." : "Create League & Account →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── ADMIN LOGIN ──────────────────────────────────────────────────
  if (screen === 'admin-login') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Admin Login</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Sign in to manage your league</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="EMAIL ADDRESS" type="email" placeholder="you@example.com" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} />
          <FieldInput label="PASSWORD" type="password" placeholder="Your password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdminLogin()} />
          {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{authError}</div>}
          <Btn onClick={handleAdminLogin} disabled={authLoading} full>{authLoading ? "Signing in..." : "Sign In →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── CO-ADMIN SIGNUP ──────────────────────────────────────────────
  if (screen === 'coadmin-signup') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Join as Co-Admin</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Use the co-admin code your league administrator shared with you</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="YOUR FULL NAME" placeholder="e.g. Jamie Lee" value={coAdminName} onChange={e => setCoAdminName(e.target.value)} />
          <FieldInput label="YOUR EMAIL ADDRESS" type="email" placeholder="you@example.com" value={coAdminEmail} onChange={e => setCoAdminEmail(e.target.value)} />
          <FieldInput label="CREATE A PASSWORD" type="password" placeholder="At least 6 characters" value={coAdminPassword} onChange={e => setCoAdminPassword(e.target.value)} />
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>CO-ADMIN INVITE CODE</label>
            <input className="code-entry-input" placeholder="e.g. XY4Z9K" value={coAdminCode} onChange={e => setCoAdminCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && handleCoAdminSignup()}
              style={{ width: "100%", padding: "10px 13px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 24, fontWeight: 800, outline: "none", boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "0.25em", textAlign: "center", color: C.purple }} />
          </div>
          <div style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.card, padding: "12px 14px" }}>
            <CoachPrompt leagueName="your league" isCoach={coAdminIsCoach} coachRole={coAdminCoachRole}
              onChangeIsCoach={setCoAdminIsCoach} onChangeCoachRole={setCoAdminCoachRole} />
          </div>
          {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{authError}</div>}
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center" }}>
            By creating an account, you agree to our <a href="/terms" style={{ color: C.muted }}>Terms</a> and <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
          </div>
          <Btn onClick={handleCoAdminSignup} disabled={authLoading} variant="purple" full>{authLoading ? "Joining league..." : "Join as Co-Admin →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── CLAIM A LEAGUE (superadmin-created league, no owner yet) ──────
  if (screen === 'claim-league') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Claim Your League</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Use the claim code GroundsPal shared with you to become the owner</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="YOUR FULL NAME" placeholder="e.g. Jamie Lee" value={claimName} onChange={e => setClaimName(e.target.value)} />
          <FieldInput label="YOUR EMAIL ADDRESS" type="email" placeholder="you@example.com" value={claimEmail} onChange={e => setClaimEmail(e.target.value)} />
          <FieldInput label="CREATE A PASSWORD" type="password" placeholder="At least 6 characters" value={claimPassword} onChange={e => setClaimPassword(e.target.value)} />
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>LEAGUE CLAIM CODE</label>
            <input className="code-entry-input" placeholder="e.g. XY4Z9K" value={claimCode} onChange={e => setClaimCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && handleClaimLeagueSignup()}
              style={{ width: "100%", padding: "10px 13px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 24, fontWeight: 800, outline: "none", boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "0.25em", textAlign: "center", color: C.blue }} />
          </div>
          {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{authError}</div>}
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center" }}>
            By creating an account, you agree to our <a href="/terms" style={{ color: C.muted }}>Terms</a> and <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
          </div>
          <Btn onClick={handleClaimLeagueSignup} disabled={authLoading} variant="blueSolid" full>{authLoading ? "Claiming league..." : "Claim League →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── SIGN IN (RETURNING USERS) ────────────────────────────────────────────
  if (screen === 'signin') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Welcome Back</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Sign in to your league</div>
      <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 10 }}>

        <div style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "10px 14px" }}>
          <div style={{ fontSize: 12, color: C.amber, fontWeight: 600 }}>
            First time? Use "Join as Volunteer" on the home screen with your invite code. You only need to do that once.
          </div>
        </div>

        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.grayLt, borderBottom: "1px solid " + C.border }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Volunteer</div>
            <div style={{ fontSize: 12, color: C.muted }}>{volSetupAccount ? (volNeedsPasswordSetup ? "Let's secure your account with a password" : "Enter your password") : "Sign in with the email you used when joining"}</div>
          </div>
          <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
            {!volSetupAccount && (
              <>
                <FieldInput type="email" placeholder="your@email.com" value={signinEmail} onChange={e => setSigninEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVolunteerEmailLookup()} />
                {signinError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark }}>{signinError}</div>}
                <Btn onClick={handleVolunteerEmailLookup} disabled={signinLoading} variant="blue" full>{signinLoading ? "Looking up your account..." : "Continue →"}</Btn>
              </>
            )}
            {volSetupAccount && !volNeedsPasswordSetup && (
              <>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{signinEmail.trim()}</div>
                <FieldInput type="password" placeholder="Your password" value={volLoginPassword} onChange={e => setVolLoginPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVolunteerSignIn()} />
                {signinError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark }}>{signinError}</div>}
                <Btn onClick={handleVolunteerSignIn} disabled={signinLoading} variant="blue" full>{signinLoading ? "Signing in..." : "Sign In as Volunteer →"}</Btn>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button onClick={() => { setVolSetupAccount(null); setVolLoginPassword(""); setSigninError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Use a different email</button>
                  <button onClick={() => handleForgotPassword(signinEmail)} style={{ background: "none", border: "none", color: C.blue, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Forgot password?</button>
                </div>
                {forgotPasswordMsg && <div style={{ fontSize: 12, color: C.muted }}>{forgotPasswordMsg}</div>}
              </>
            )}
            {volSetupAccount && volNeedsPasswordSetup && (
              <>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{signinEmail.trim()}</div>
                <div style={{ fontSize: 12, color: C.muted }}>We've updated how volunteers sign in. Set a password now and you'll use it next time instead of just your email.</div>
                <FieldInput type="password" placeholder="Choose a password (6+ characters)" value={volSetupPassword} onChange={e => setVolSetupPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVolunteerPasswordSetup()} />
                {signinError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark }}>{signinError}</div>}
                <Btn onClick={handleVolunteerPasswordSetup} disabled={signinLoading} variant="blue" full>{signinLoading ? "Setting up..." : "Set Password & Continue →"}</Btn>
                <button onClick={() => { setVolSetupAccount(null); setVolSetupPassword(""); setSigninError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Use a different email</button>
              </>
            )}
          </div>
        </div>

        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.grayLt, borderBottom: "1px solid " + C.border }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Admin / Co-Admin</div>
            <div style={{ fontSize: 12, color: C.muted }}>Sign in with your email and password</div>
          </div>
          <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
            <FieldInput type="email" placeholder="your@email.com" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} />
            <FieldInput type="password" placeholder="Your password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdminLogin()} />
            {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark }}>{authError}</div>}
            <Btn onClick={handleAdminLogin} disabled={authLoading} full>{authLoading ? "Signing in..." : "Sign In as Admin →"}</Btn>
            <div style={{ textAlign: "right" }}>
              <button onClick={() => handleForgotPassword(adminEmail)} style={{ background: "none", border: "none", color: C.blue, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Forgot password?</button>
            </div>
            {forgotPasswordMsg && <div style={{ fontSize: 12, color: C.muted }}>{forgotPasswordMsg}</div>}
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); setSigninError(""); setVolSetupAccount(null); setVolNeedsPasswordSetup(false); setVolLoginPassword(""); setVolSetupPassword(""); setForgotPasswordMsg(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>

      </div>
    </div>
  );

  // ─── VOLUNTEER JOIN ───────────────────────────────────────────────
  if (screen === 'volunteer-join') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <Logo />
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Join a League</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>Enter the invite code your admin shared with you</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="YOUR NAME" placeholder="e.g. Marcus Rodriguez" value={volName} onChange={e => setVolName(e.target.value)} />
          <FieldInput label="YOUR EMAIL" type="email" placeholder="you@example.com" value={volEmail} onChange={e => setVolEmail(e.target.value)} />
          <FieldInput label="CREATE A PASSWORD" type="password" placeholder="At least 6 characters" value={volSignupPassword} onChange={e => setVolSignupPassword(e.target.value)} />
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>LEAGUE INVITE CODE</label>
            <input className="code-entry-input" placeholder="e.g. AB3X9K" value={volCode} onChange={e => setVolCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && handleVolunteerJoin()}
              style={{ width: "100%", padding: "10px 13px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 28, fontWeight: 800, outline: "none", boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "0.25em", textAlign: "center", color: C.green }} />
          </div>
          <div style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.card, padding: "12px 14px" }}>
            <CoachPrompt leagueName="your league" isCoach={volIsCoach} coachRole={volCoachRole}
              onChangeIsCoach={setVolIsCoach} onChangeCoachRole={setVolCoachRole} />
          </div>
          {authError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{authError}</div>}
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center" }}>
            By joining, you agree to our <a href="/terms" style={{ color: C.muted }}>Terms</a> and <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>.
          </div>
          <Btn onClick={handleVolunteerJoin} disabled={authLoading} full>{authLoading ? "Looking up league..." : "Join League →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setScreen('landing'); setAuthError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── MY ACCOUNT (VOLUNTEER) ────────────────────────────────────────
  // ─── CHOOSE LEAGUE (shown when a login finds more than one league) ─────
  if (screen === 'choose-league') return (
    <div style={{ minHeight: 520, padding: "2rem", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ maxWidth: 440, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Choose a League</div>
          <div style={{ fontSize: 13, color: C.muted }}>You're part of more than one league — pick one to continue.</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(pendingLeagueChoices || []).map(choice => (
            <button key={choice.leagueId} onClick={() => enterLeague(choice)}
              style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "14px 16px", textAlign: "left", cursor: "pointer", fontFamily: "inherit", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{choice.leagueName || "League"}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{choice.role === 'volunteer' ? 'Volunteer' : choice.isMainAdmin ? 'Owner · Admin' : 'Co-Admin'}</div>
              </div>
              <span style={{ color: C.muted, fontSize: 16 }}>→</span>
            </button>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={handleSignOut} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Sign out</button>
        </div>
      </div>
    </div>
  );

  if (screen === 'my-account') return (
    <div style={{ minHeight: 520, padding: "2rem", background: C.surface }}>
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <button onClick={() => { setScreen('app'); setCurrentPasswordInput(""); setNewPasswordInput(""); setConfirmNewPasswordInput(""); setPasswordChangeError(""); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: "1.5rem" }}>← Back to Dashboard</button>

        <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>👤 My Account</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.5rem" }}>{user && user.name}</div>

        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.5rem", marginBottom: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.1em", marginBottom: 10 }}>📱 TEXT NOTIFICATIONS</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
            Add your phone number to receive text alerts about task assignments and field closures.
          </div>
          <FieldInput label="PHONE NUMBER" type="tel" placeholder="(555) 123-4567" value={myPhone} onChange={e => setMyPhone(e.target.value)} />

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={mySmsConsent} onChange={e => setMySmsConsent(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              I agree to receive SMS notifications from GroundsPal. If you opt in to SMS notifications, GroundsPal will text you about field closures, volunteer task assignments, and event updates related to your league. Message and data rates may apply. Message frequency varies based on your league's activity. You can opt out at any time by replying STOP, or by disabling SMS notifications in your account settings. Reply HELP for help. Your phone number will not be sold or used for marketing purposes unrelated to GroundsPal notifications. See our <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>Terms of Service</a>.
            </span>
          </label>

          {accountError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark, marginTop: 12 }}>{accountError}</div>}

          <div style={{ marginTop: 16 }}>
            <Btn onClick={saveMyAccountSms} disabled={accountSaving} full>
              {accountSaving ? "Saving..." : accountSaved ? "✓ Saved!" : "Save"}
            </Btn>
          </div>
        </div>

        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.5rem", marginBottom: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.1em", marginBottom: 10 }}>🔒 CHANGE PASSWORD</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FieldInput label="CURRENT PASSWORD" type="password" placeholder="Your current password" value={currentPasswordInput} onChange={e => setCurrentPasswordInput(e.target.value)} />
            <FieldInput label="NEW PASSWORD" type="password" placeholder="At least 6 characters" value={newPasswordInput} onChange={e => setNewPasswordInput(e.target.value)} />
            <FieldInput label="CONFIRM NEW PASSWORD" type="password" placeholder="Re-enter new password" value={confirmNewPasswordInput} onChange={e => setConfirmNewPasswordInput(e.target.value)} />
          </div>
          {passwordChangeError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark, marginTop: 12 }}>{passwordChangeError}</div>}
          <div style={{ marginTop: 16 }}>
            <Btn onClick={handleChangePassword} disabled={passwordChangeSaving} full>
              {passwordChangeSaving ? "Updating..." : passwordChangeSuccess ? "✓ Password Updated!" : "Update Password"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── SHOW CODE / LEAGUE SETTINGS ─────────────────────────────────
  if (screen === 'show-code') return (
    <div style={{ minHeight: 520, padding: "2rem", background: C.surface }}>
      {/* Every early return that can call showConfirm needs its own
          <ConfirmModal /> -- this is a separate return from the dashboard's, so
          without this line showConfirm sets state with nothing on screen
          reflecting it, and the dialog only appears once you navigate back out
          to a render that does mount it. Third occurrence of this (task detail,
          project task detail, now Settings); see CLAUDE.md for the real fix. */}
      <ConfirmModal modal={confirmModal} onConfirm={() => { if (confirmModal && confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal(null); }} onCancel={() => setConfirmModal(null)} />
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <button onClick={() => setScreen(league && user ? 'app' : 'landing')} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: "1.5rem" }}>← Back to Dashboard</button>

        <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>⚙️ League Settings</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.5rem" }}>{league && league.leagueName}</div>

        {/* Team Management */}
        <button onClick={() => { setScreen('app'); setTab('team'); }}
          style={{ width: "100%", background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "12px 16px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>👥</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Team Management</div>
              <div style={{ fontSize: 12, color: C.muted }}>Manage volunteers, admins and crew</div>
            </div>
          </div>
          <span style={{ color: C.muted, fontSize: 16 }}>›</span>
        </button>
        <div style={{ background: C.white, borderRadius: R.card, border: "2px solid #378ADD", padding: "1.5rem", marginBottom: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.1em", marginBottom: 6 }}>🙋 VOLUNTEER INVITE CODE</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Share this with volunteers so they can join your league</div>
          <div style={{ fontSize: 44, fontWeight: 800, color: "#378ADD", letterSpacing: "0.2em", textAlign: "center", marginBottom: 12 }}>{league && league.leagueCode}</div>
          <button onClick={() => { navigator.clipboard.writeText(league.leagueCode); setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }}
            style={{ background: "#E6F1FB", color: "#042C53", border: "1px solid #378ADD", borderRadius: R.control, padding: "9px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit" }}>
            {codeCopied ? "✓ Copied!" : "Copy Volunteer Code"}
          </button>
        </div>

        {/* Co-Admin Code — only visible to main admin */}
        {user && user.isMainAdmin && (
          <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.purpleBd, padding: "1.5rem", marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.1em", marginBottom: 6 }}>🤝 CO-ADMIN INVITE CODE</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Share this only with people you want to give admin access</div>
            <div style={{ fontSize: 44, fontWeight: 800, color: C.purple, letterSpacing: "0.2em", textAlign: "center", marginBottom: 12 }}>{league && league.coAdminCode}</div>
            <Btn onClick={() => { navigator.clipboard.writeText(league.coAdminCode); setCoAdminCodeCopied(true); setTimeout(() => setCoAdminCodeCopied(false), 2000); }} variant="purple" full>{coAdminCodeCopied ? "✓ Copied!" : "Copy Co-Admin Code"}</Btn>

            {/* Co-admin list */}
            <div style={{ marginTop: "1.25rem" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 10 }}>CURRENT CO-ADMINS ({league && league.coAdmins ? league.coAdmins.length : 0})</div>
              {(!league || !league.coAdmins || league.coAdmins.length === 0) ? (
                <div style={{ background: C.grayLt, borderRadius: R.control, padding: "12px", textAlign: "center", fontSize: 13, color: C.muted }}>No co-admins yet. Share the code above to invite one.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {league.coAdmins.map((ca, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: C.purpleLt, borderRadius: R.control, padding: "10px 14px" }}>
                      <Avatar name={ca.name} size={36} bg={C.purpleBd} fg={C.purple} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{ca.name}</div>
                        <div style={{ fontSize: 12, color: C.muted }}>{ca.email} · Joined {fmtDate(ca.addedAt)}</div>
                      </div>
                      <Badge bg={C.purpleLt} color={C.purple}>Co-Admin</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing & Plan — visible to any admin (main or co-admin) */}
        {user && user.role === "admin" && (() => {
          const currentPlan = (league && league.plan) || 'free';
          const planActive = league && league.planActive;
          const planLabel = currentPlan === 'league' ? 'League' : currentPlan === 'pro' ? 'Pro' : 'Free';
          const planColor = currentPlan === 'league' ? C.blue : currentPlan === 'pro' ? C.green : C.muted;
          const planBg = currentPlan === 'league' ? C.blueLt : currentPlan === 'pro' ? C.greenLt : C.grayLt;

          const plans = [
            {
              key: 'pro',
              name: 'Pro',
              monthlyPrice: PRICE_IDS.pro_monthly,
              annualPrice: PRICE_IDS.pro_annual,
              monthlyLabel: '$12/mo',
              annualLabel: '$99/yr',
              perks: ['Volunteer stats & history', 'Supply request tracking', 'Group messaging integration', 'Waivers', 'Up to 3 co-admins'],
            },
            {
              key: 'league',
              name: 'League',
              monthlyPrice: PRICE_IDS.league_monthly,
              annualPrice: PRICE_IDS.league_annual,
              monthlyLabel: '$25/mo',
              annualLabel: '$199/yr',
              perks: ['Everything in Pro', 'Season Scheduler & auto-scheduling', 'Location closure notifications', 'CSV export & reporting', 'Ad-free experience', 'Unlimited co-admins'],
            },
          ];

          return (
            <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
              <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 24 }}>💳</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Billing & Plan</div>
                      <div style={{ fontSize: 12, color: C.muted }}>Manage your GroundsPal subscription</div>
                    </div>
                  </div>
                  <Badge bg={planBg} color={planColor}>{planLabel}{currentPlan !== 'free' && !planActive ? ' (inactive)' : ''}</Badge>
                </div>

                {currentPlan === 'free' && (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16 }}>
                      {['monthly', 'annual'].map(cycle => (
                        <div key={cycle} onClick={() => setBillingCycle(cycle)}
                          style={{ padding: "6px 16px", borderRadius: R.control, cursor: "pointer", fontSize: 12, fontWeight: 700, border: "1.5px solid " + (billingCycle === cycle ? C.green : C.border), background: billingCycle === cycle ? C.greenLt : C.white, color: billingCycle === cycle ? C.green : C.muted }}>
                          {cycle === 'monthly' ? 'Monthly' : 'Annual (save ~30%)'}
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {plans.map(p => {
                        const priceId = billingCycle === 'monthly' ? p.monthlyPrice : p.annualPrice;
                        const priceLabel = billingCycle === 'monthly' ? p.monthlyLabel : p.annualLabel;
                        const isLoading = billingLoading === priceId;
                        return (
                          <div key={p.key} style={{ border: "1.5px solid " + (p.key === 'league' ? C.blueBd : C.greenBd), borderRadius: R.control, padding: "14px 16px", background: p.key === 'league' ? C.blueLt : C.greenLt }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                              <div style={{ fontWeight: 800, fontSize: 15, color: p.key === 'league' ? C.blue : C.green }}>{p.name}</div>
                              <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{priceLabel}</div>
                            </div>
                            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.8, marginBottom: 12 }}>
                              {p.perks.map((perk, i) => <div key={i}>✓ {perk}</div>)}
                            </div>
                            <Btn onClick={() => handleUpgrade(priceId)} disabled={!!billingLoading} variant={p.key === 'league' ? 'blueSolid' : 'primary'} full>
                              {isLoading ? "Redirecting to checkout..." : "Upgrade to " + p.name}
                            </Btn>
                          </div>
                        );
                      })}
                    </div>

                  </>
                )}

                {currentPlan !== 'free' && (
                  <div style={{ background: C.grayLt, borderRadius: R.control, padding: "14px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 13, color: C.text, marginBottom: 12 }}>
                      {planActive ? "Your league is on the " + planLabel + " plan." : "Your subscription is inactive."}
                    </div>
                    {league && league.stripeCustomerId ? (
                      <Btn onClick={handleManageBilling} disabled={!!billingLoading} variant="secondary" full>
                        {billingLoading === 'portal' ? "Opening billing portal..." : "Manage Billing"}
                      </Btn>
                    ) : (
                      <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
                        {league && league.planSource && league.planSource.startsWith('promo:')
                          ? "You have complimentary League access via a promo code — no billing on file."
                          : "No billing account on file."}
                        {league && league.planExpiresAt && (
                          <div style={{ marginTop: 4 }}>
                            Access expires {new Date(league.planExpiresAt).toLocaleDateString()}.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Promo code redemption -- available to free AND Pro leagues (Pro can
                    upgrade to League this way); hidden once already on League since
                    free_year codes only ever grant League, never anything higher. */}
                {currentPlan !== 'league' && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid " + C.border }}>
                    {promoRedeemMessage === 'success' ? (
                      <div style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.text, textAlign: "center", fontWeight: 600 }}>
                        🎉 Code applied! Your league is now on the League plan.
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 700 }}>
                          {currentPlan === 'pro' ? "HAVE A LEAGUE UPGRADE CODE?" : "HAVE A PROMO CODE?"}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input placeholder="Enter code" value={promoCodeInput}
                            onChange={e => setPromoCodeInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRedeemPromoCode()}
                            style={{ flex: 1, padding: "8px 10px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit" }} />
                          <Btn onClick={handleRedeemPromoCode} disabled={promoRedeemLoading} size="sm">
                            {promoRedeemLoading ? "Applying..." : "Apply"}
                          </Btn>
                        </div>
                        {promoRedeemMessage && promoRedeemMessage !== 'success' && (
                          <div style={{ fontSize: 12, color: C.redDark, marginTop: 6 }}>{promoRedeemMessage}</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Group Notifications */}
        {(user && (user.role === "admin" || user.role === "coadmin")) && !hasPro && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>💬</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>Group Notifications is a Pro feature</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>Connect GroupMe, Band, or another messaging platform to send task and board alerts to your group automatically.</div>
              <Btn onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} variant="primary">See Upgrade Options Above</Btn>
            </div>
          </div>
        )}
        {(user && (user.role === "admin" || user.role === "coadmin")) && hasPro && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 24 }}>💬</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Group Notifications</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Connect a messaging group to receive task and event alerts</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>MESSAGING PLATFORM</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["GroupMe", "Band", "Remind", "Other"].map(platform => {
                      const current = (league && league.messagingPlatform) || "GroupMe";
                      return (
                        <div key={platform} onClick={async () => { await updateDoc(doc(db, "leagues", lid), { messagingPlatform: platform }); }} style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: R.control, border: "2px solid " + (current === platform ? C.green : C.border), background: current === platform ? C.greenLt : C.white, cursor: "pointer", fontSize: 12, fontWeight: 700, color: current === platform ? C.green : C.muted }}>
                          {platform}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 4 }}>
                    {((league && league.messagingPlatform) || "GroupMe") === "GroupMe" ? "GROUPME BOT ID" : "WEBHOOK OR BOT ID"}
                  </label>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>
                    {((league && league.messagingPlatform) || "GroupMe") === "GroupMe" && (
                      <span>Go to <strong>dev.groupme.com</strong> → Bots → Create Bot for your group → copy the Bot ID and paste it below.</span>
                    )}
                    {((league && league.messagingPlatform) || "GroupMe") === "Band" && (
                      <span>In your Band app → Settings → Developer → Enable Bot → copy the Bot Token.</span>
                    )}
                    {((league && league.messagingPlatform) || "GroupMe") === "Remind" && (
                      <span>Remind integration coming soon. Use GroupMe or Band for now.</span>
                    )}
                    {((league && league.messagingPlatform) || "GroupMe") === "Other" && (
                      <span>Paste your webhook URL or bot ID below.</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      placeholder="Paste bot ID or webhook URL..."
                      defaultValue={(league && league.groupmeBotId) || ""}
                      id="groupme-bot-input"
                      style={{ flex: 1, padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", outline: "none" }}
                    />
                    <Btn onClick={async () => {
                      const val = document.getElementById("groupme-bot-input").value.trim();
                      await updateDoc(doc(db, "leagues", lid), { groupmeBotId: val });
                      alert("Saved! Test it by completing a task.");
                    }}>Save</Btn>
                  </div>
                  {league && league.groupmeBotId && <div style={{ fontSize: 11, color: C.green, marginTop: 6, fontWeight: 600 }}>✓ Connected — messages will be sent to your group</div>}
                  {league && !league.groupmeBotId && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Not connected — no messages will be sent</div>}
                </div>
                <div style={{ background: C.grayLt, borderRadius: R.control, padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Messages sent to your group:</div>
                  <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.8 }}>
                    ✅ When a task is completed<br/>
                    📌 When a new board post is made<br/>
                    🚫 When a field is closed for the day<br/>
                    ✅ When a closed field is reopened
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {(user && (user.role === "admin" || user.role === "coadmin")) && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 24 }}>📱</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Text Notifications (Your Number)</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Add your phone number to receive text alerts for your league</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <FieldInput label="PHONE NUMBER" type="tel" placeholder="(555) 123-4567" value={adminPhone} onChange={e => { setAdminPhone(e.target.value); setAdminSmsError(""); }} />
                {adminSmsError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{adminSmsError}</div>}
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={adminSmsConsent} onChange={e => setAdminSmsConsent(e.target.checked)}
                    style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                    I agree to receive SMS notifications from GroundsPal. If you opt in to SMS notifications, GroundsPal will text you about field closures, volunteer task assignments, and event updates related to your league. Message and data rates may apply. Message frequency varies based on your league's activity. You can opt out at any time by replying STOP, or by disabling SMS notifications in your account settings. Reply HELP for help. Your phone number will not be sold or used for marketing purposes unrelated to GroundsPal notifications. See our <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>Terms of Service</a>.
                  </span>
                </label>
                {adminSmsConsent && (
                  <div style={{ background: C.grayLt, borderRadius: R.control, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Which texts do you want to receive?</div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={adminSmsPrefFieldClosures} onChange={e => setAdminSmsPrefFieldClosures(e.target.checked)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: C.muted }}>🚫 Field closures & reopenings</span>
                    </label>
                    {EVENT_REQUEST_SMS_LIVE && (
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="checkbox" checked={adminSmsPrefEventRequests} onChange={e => setAdminSmsPrefEventRequests(e.target.checked)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: C.muted }}>📅 Event requests awaiting your approval</span>
                      </label>
                    )}
                  </div>
                )}
                {/* Gated on isEmulating, NOT on !canWriteAsAdmin(): this card is also
                    shown to co-admins, who save through the coAdmins branch and are
                    legitimately allowed to. canWriteAsAdmin() is false for them too. */}
                {user && user.isEmulating && (
                  <div style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 12px", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                    SMS settings must be changed by the league's own admin — Super Admin mode can't record a consent on someone else's behalf.
                  </div>
                )}
                <Btn onClick={saveAdminSms} disabled={adminSmsSaving || !!(user && user.isEmulating)} full>
                  {adminSmsSaving ? "Saving..." : adminSmsSaved ? "✓ Saved!" : "Save"}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {(user && (user.role === "admin" || user.role === "coadmin")) && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 24 }}>🔒</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Change Password</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Update the password for your account</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <FieldInput label="CURRENT PASSWORD" type="password" placeholder="Your current password" value={currentPasswordInput} onChange={e => setCurrentPasswordInput(e.target.value)} />
                <FieldInput label="NEW PASSWORD" type="password" placeholder="At least 6 characters" value={newPasswordInput} onChange={e => setNewPasswordInput(e.target.value)} />
                <FieldInput label="CONFIRM NEW PASSWORD" type="password" placeholder="Re-enter new password" value={confirmNewPasswordInput} onChange={e => setConfirmNewPasswordInput(e.target.value)} />
              </div>
              {passwordChangeError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark, marginTop: 12 }}>{passwordChangeError}</div>}
              <div style={{ marginTop: 16 }}>
                <Btn onClick={handleChangePassword} disabled={passwordChangeSaving} full>
                  {passwordChangeSaving ? "Updating..." : passwordChangeSuccess ? "✓ Password Updated!" : "Update Password"}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {user && user.role === "admin" && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>📍</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Locations</div>
                    <div style={{ fontSize: 12, color: C.muted }}>Name the locations used in your league</div>
                  </div>
                </div>
                {!editingLocations && (
                  <button onClick={() => setEditingLocations([...leagueLocations])}
                    style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                    ✏️ Edit
                  </button>
                )}
              </div>
              {!editingLocations ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leagueLocations.map((loc, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.grayLt, borderRadius: R.control }}>
                      <span style={{ fontSize: 13, color: C.muted, fontWeight: 700, width: 20 }}>{i + 1}.</span>
                      <span style={{ fontSize: 14, color: loc ? C.text : C.muted, fontStyle: loc ? "normal" : "italic" }}>
                        {loc || "Not set yet — tap Edit to add"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {editingLocations.map((loc, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, color: C.muted, fontWeight: 700, width: 20, flexShrink: 0 }}>{i + 1}.</span>
                      <input
                        placeholder={"e.g. Field A, Court 1, Main Gym..."}
                        value={loc}
                        onChange={e => { const u = [...editingLocations]; u[i] = e.target.value; setEditingLocations(u); }}
                        style={{ flex: 1, padding: "8px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }}
                      />
                      <button onClick={() => { const u = editingLocations.filter((_, j) => j !== i); setEditingLocations(u); }} style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, width: 28, height: 28, cursor: "pointer", fontSize: 14, color: C.redDark, flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setEditingLocations([...editingLocations, ""])} style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "8px 12px", cursor: "pointer", fontSize: 13, color: C.green, fontWeight: 700, fontFamily: "inherit", width: "100%", marginTop: 4 }}>+ Add Location</button>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button onClick={() => saveLocations(editingLocations)}
                      style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {locationsSaved ? "✓ Saved!" : "Save Locations"}
                    </button>
                    <button onClick={() => setEditingLocations(null)}
                      style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {user && user.role === "admin" && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>🔗</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Public Registration Link</div>
                    <div style={{ fontSize: 12, color: C.muted }}>The web address parents use to register online</div>
                  </div>
                </div>
                {!editingSlug && league && league.slug && (
                  <button onClick={startEditSlug} style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                    ✏️ Edit
                  </button>
                )}
              </div>
              {!editingSlug && league && league.slug && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.grayLt, borderRadius: R.control, marginBottom: 10, fontSize: 13, color: C.text, wordBreak: "break-all" }}>
                    {window.location.host}/register/{league.slug}
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/register/' + league.slug); setSlugCopied(true); setTimeout(() => setSlugCopied(false), 2000); }}
                    style={{ width: "100%", background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                    {slugCopied ? "✓ Copied!" : "Copy Link"}
                  </button>
                </div>
              )}
              {!editingSlug && league && !league.slug && (
                <div>
                  <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>
                    Set a link name so parents can find and register for your league online.
                  </div>
                  <button onClick={startEditSlug} style={{ width: "100%", background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "10px 12px", cursor: "pointer", fontSize: 13, color: C.green, fontWeight: 700, fontFamily: "inherit" }}>
                    {slugSuggesting ? "Finding an available name..." : "Set Up Your Link"}
                  </button>
                </div>
              )}
              {editingSlug && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.05em", marginBottom: 6 }}>
                    {window.location.host}/register/
                  </div>
                  <input
                    value={leagueSlugInput}
                    onChange={e => { setLeagueSlugInput(e.target.value); setSlugError(""); }}
                    placeholder="your-league-name"
                    disabled={slugSuggesting}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 8 }}
                  />
                  {slugError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark, marginBottom: 8 }}>{slugError}</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={saveLeagueSlug} disabled={slugSaving || slugSuggesting} style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {slugSaving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={() => { setEditingSlug(false); setSlugError(""); }} style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {user && user.role === "admin" && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>👥</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Task Groups</div>
                    <div style={{ fontSize: 12, color: C.muted }}>Organize tasks by crew or team</div>
                  </div>
                </div>
                {!editingGroups && (
                  <button onClick={() => setEditingGroups([...leagueGroups])}
                    style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>
                    ✏️ Edit
                  </button>
                )}
              </div>
              {!editingGroups ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leagueGroups.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.muted, fontStyle: "italic" }}>No groups yet — tap Edit to add</div>
                  ) : leagueGroups.map((g, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.grayLt, borderRadius: R.control }}>
                      <span style={{ fontSize: 13, color: C.muted, fontWeight: 700, width: 20 }}>{i + 1}.</span>
                      <span style={{ fontSize: 14, color: C.text }}>{g}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {editingGroups.map((g, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, color: C.muted, fontWeight: 700, width: 20, flexShrink: 0 }}>{i + 1}.</span>
                      <input placeholder="e.g. Infield Crew, Outfield Crew..."
                        value={g}
                        onChange={e => { const u = [...editingGroups]; u[i] = e.target.value; setEditingGroups(u); }}
                        style={{ flex: 1, padding: "8px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                      <button onClick={() => setEditingGroups(editingGroups.filter((_, j) => j !== i))}
                        style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, width: 28, height: 28, cursor: "pointer", fontSize: 14, color: C.redDark, flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setEditingGroups([...editingGroups, ""])}
                    style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "8px 12px", cursor: "pointer", fontSize: 13, color: C.green, fontWeight: 700, fontFamily: "inherit", width: "100%", marginTop: 4 }}>+ Add Group</button>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button onClick={() => saveGroups(editingGroups)}
                      style={{ flex: 1, background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      {groupsSaved ? "✓ Saved!" : "Save Groups"}
                    </button>
                    <button onClick={() => setEditingGroups(null)}
                      style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {user && user.role === "admin" && (
          <div style={{ marginTop: "1rem", width: "100%", maxWidth: 480 }}>
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 24 }}>🌤️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Weather Forecast</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Enter your league zip code to show weather on the dashboard</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="code-entry-input" placeholder="e.g. 77001" value={zipCode} onChange={e => setZipCode(e.target.value)} maxLength={5}
                  onKeyDown={e => e.key === "Enter" && saveZipCode()}
                  style={{ flex: 1, padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 20, fontFamily: "inherit", outline: "none", letterSpacing: "0.15em", fontWeight: 800, textAlign: "center" }} />
                <Btn onClick={saveZipCode} disabled={zipCode.length !== 5}>{zipSaved ? "✓ Saved!" : "Save"}</Btn>
              </div>
              {league && league.zipCode && (
                <div style={{ fontSize: 12, color: C.green, marginTop: 8, fontWeight: 600 }}>✓ Currently set to: {league.zipCode}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── PLATFORM LOGIN ──────────────────────────────────────────────────────────
  // ─── LEGAL PAGES (public, no auth required) ──────────────────────
  const LegalPage = ({ title, updated, children }) => (
    <div style={{ minHeight: 520, background: C.surface, padding: "2rem 1.25rem" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <a href="/" style={{ fontSize: 13, color: C.green, textDecoration: "none", fontWeight: 700 }}>← Back to GroundsPal</a>
        <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem", marginTop: 16 }}>
          <div style={{ fontFamily: T.display, fontSize: 24, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 24 }}>Last updated: {updated}</div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7 }}>{children}</div>
        </div>
      </div>
    </div>
  );

  const LegalH2 = ({ children }) => <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginTop: 24, marginBottom: 8 }}>{children}</div>;
  const LegalP = ({ children }) => <p style={{ margin: "0 0 14px" }}>{children}</p>;
  const LegalUl = ({ children }) => <ul style={{ margin: "0 0 14px", paddingLeft: 20 }}>{children}</ul>;
  const LegalLi = ({ children }) => <li style={{ marginBottom: 6 }}>{children}</li>;

  if (screen === 'privacy') return (
    <LegalPage title="Privacy Policy" updated="July 1, 2026">
      <LegalP>GroundsPal ("we," "us," or "our") is a product of The Thinkery Group. This Privacy Policy explains what information we collect through the GroundsPal platform, how we use it, and the choices you have.</LegalP>

      <LegalH2>Information We Collect</LegalH2>
      <LegalUl>
        <LegalLi><strong>Account information:</strong> name, email address, and password when you create a GroundsPal account.</LegalLi>
        <LegalLi><strong>Phone number:</strong> if you opt in to SMS notifications, we collect and store your phone number to send you those messages.</LegalLi>
        <LegalLi><strong>League and team data:</strong> information related to your league, team, field assignments, volunteer tasks, and event scheduling that you or your league administrator enters into GroundsPal.</LegalLi>
        <LegalLi><strong>Payment information:</strong> if your league subscribes to a paid plan, payment is processed by Stripe. We do not store your full credit card number; Stripe handles this directly.</LegalLi>
        <LegalLi><strong>Communications data:</strong> if GroundsPal is connected to GroupMe on your league's behalf, relevant scheduling and notification data may be shared with that integration per your league's settings.</LegalLi>
        <LegalLi><strong>Usage data:</strong> basic analytics about how you use GroundsPal (pages visited, features used) to help us improve the platform.</LegalLi>
      </LegalUl>

      <LegalH2>SMS Notifications</LegalH2>
      <LegalP>If you opt in to SMS notifications, GroundsPal will text you about field closures, volunteer task assignments, and event updates related to your league. Message and data rates may apply. Message frequency varies based on your league's activity. You can opt out at any time by replying STOP, or by disabling SMS notifications in your account settings. Reply HELP for help. Your phone number will not be sold or used for marketing purposes unrelated to GroundsPal notifications.</LegalP>

      <LegalH2>How We Use Your Information</LegalH2>
      <LegalP>We use your information to operate GroundsPal, including creating and managing your account, sending notifications you've opted into, processing payments for paid league plans, and improving the platform.</LegalP>

      <LegalH2>Information Sharing</LegalH2>
      <LegalP>We do not sell your personal information. We share information with third-party service providers only as needed to operate GroundsPal, including:</LegalP>
      <LegalUl>
        <LegalLi>Twilio (SMS delivery)</LegalLi>
        <LegalLi>Stripe (payment processing)</LegalLi>
        <LegalLi>GroupMe (if your league connects this integration)</LegalLi>
        <LegalLi>Our hosting and infrastructure providers</LegalLi>
      </LegalUl>

      <LegalH2>Data Related to Minors</LegalH2>
      <LegalP>GroundsPal accounts are intended for adult league volunteers, coaches, and administrators. If your league enters roster information that includes minors (such as player names or ages), that information is provided and managed by the league administrator, and league administrators are responsible for ensuring they have appropriate consent to enter such information. GroundsPal does not knowingly collect personal information directly from children under 13.</LegalP>

      <LegalH2>Data Retention</LegalH2>
      <LegalP>We retain your information for as long as your account is active, or as needed to provide the service. You may request deletion of your account and associated data at any time by contacting us.</LegalP>

      <LegalH2>Your Rights</LegalH2>
      <LegalP>You may access, update, or delete your account information at any time through your account settings, or by contacting us at the email below. Depending on your location, you may have additional rights under applicable law (such as CCPA for California residents).</LegalP>

      <LegalH2>Cookies</LegalH2>
      <LegalP>GroundsPal may use cookies for basic site functionality and analytics.</LegalP>

      <LegalH2>Changes to This Policy</LegalH2>
      <LegalP>We may update this Privacy Policy from time to time. Continued use of GroundsPal after changes constitutes acceptance of the updated policy.</LegalP>

      <LegalH2>Contact</LegalH2>
      <LegalP>Questions about this Privacy Policy can be directed to <a href="mailto:groundspal@gmail.com" style={{ color: C.green }}>groundspal@gmail.com</a>.</LegalP>
    </LegalPage>
  );

  if (screen === 'terms') return (
    <LegalPage title="Terms of Service" updated="July 1, 2026">
      <LegalP>By creating an account or using GroundsPal, you agree to these Terms of Service.</LegalP>

      <LegalH2>Description of Service</LegalH2>
      <LegalP>GroundsPal is a web-based field maintenance and league management platform for youth sports leagues, provided by The Thinkery Group.</LegalP>

      <LegalH2>Accounts</LegalH2>
      <LegalP>You must provide accurate information when creating a GroundsPal account. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</LegalP>

      <LegalH2>Acceptable Use</LegalH2>
      <LegalP>You agree not to use GroundsPal for any unlawful purpose, to misrepresent your identity, or to interfere with the platform's operation or security.</LegalP>

      <LegalH2>SMS Notifications</LegalH2>
      <LegalP>By opting in to SMS notifications, you consent to receive text messages from GroundsPal related to your league's field closures, task assignments, and event updates. Message and data rates may apply. You may opt out at any time by replying STOP.</LegalP>

      <LegalH2>Subscriptions and Payment</LegalH2>
      <LegalP>Some GroundsPal features require a paid league subscription. Payment is processed through Stripe. Subscriptions renew automatically unless canceled. Refunds, if any, are handled on a case-by-case basis.</LegalP>

      <LegalH2>League Content</LegalH2>
      <LegalP>League administrators are responsible for the accuracy of information entered into GroundsPal, including roster, scheduling, and volunteer data, and for ensuring they have the right to enter and share such information within their league.</LegalP>

      <LegalH2>Intellectual Property</LegalH2>
      <LegalP>All content, design, and technology underlying GroundsPal are the property of The Thinkery Group and may not be copied or reproduced without permission.</LegalP>

      <LegalH2>Termination</LegalH2>
      <LegalP>We reserve the right to suspend or terminate accounts that violate these terms.</LegalP>

      <LegalH2>No Warranty</LegalH2>
      <LegalP>GroundsPal is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.</LegalP>

      <LegalH2>Limitation of Liability</LegalH2>
      <LegalP>To the fullest extent permitted by law, The Thinkery Group is not liable for indirect, incidental, or consequential damages arising from your use of GroundsPal.</LegalP>

      <LegalH2>Changes to These Terms</LegalH2>
      <LegalP>We may update these Terms of Service at any time. Continued use of GroundsPal after changes constitutes acceptance of the updated terms.</LegalP>

      <LegalH2>Contact</LegalH2>
      <LegalP>Questions about these Terms can be directed to <a href="mailto:groundspal@gmail.com" style={{ color: C.green }}>groundspal@gmail.com</a>.</LegalP>
    </LegalPage>
  );

  if (screen === 'platform-login') return (
    <div style={{ minHeight: 520, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", background: C.surface }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>🔐</div>
      <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>Platform Admin</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: "1.75rem" }}>GroundsPal usage dashboard</div>
      <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.75rem", width: "100%", maxWidth: 360 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <FieldInput label="EMAIL" type="email" placeholder="Your admin email" value={platformEmail} onChange={e => setPlatformEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePlatformLogin()} />
          <FieldInput label="PASSWORD" type="password" placeholder="Your password" value={platformPassword} onChange={e => setPlatformPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePlatformLogin()} />
          {platformError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "10px 12px", fontSize: 13, color: C.redDark }}>{platformError}</div>}
          <Btn onClick={handlePlatformLogin} disabled={platformLoading} full>{platformLoading ? "Loading data..." : "View Dashboard →"}</Btn>
        </div>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => setScreen('landing')} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ─── PLATFORM DASHBOARD ───────────────────────────────────────────────────────
  // STAT_META drives three surfaces at once: the stat strip tiles, the activity
  // card's metric tabs, and the chart's colour. The `order` array is the single
  // source of truth for the sequence so the tiles and the tabs can never drift
  // out of sync with each other.
  const STAT_META = {
    totalLeagues: { label: "Total Leagues", short: "Leagues", color: "#75C83C", textOnFill: "#111111" },
    totalAdmins: { label: "Admins", short: "Admins", color: C.purple || "#6A1B9A", textOnFill: "#fff" },
    totalVolunteers: { label: "Volunteers", short: "Volunteers", color: C.blue, textOnFill: "#fff" },
    totalTasks: { label: "Active Tasks", short: "Tasks", color: C.amber, textOnFill: "#fff" },
    totalCompleted: { label: "Completed Tasks", short: "Completed", color: "#75C83C", textOnFill: "#111111" },
    totalVisits: { label: "Platform Visits", short: "Visits", color: "#0E9F8E", textOnFill: "#fff" }
  };
  const SA_STAT_ORDER = ["totalLeagues", "totalAdmins", "totalVolunteers", "totalTasks", "totalCompleted", "totalVisits"];

  if (screen === 'platform-dashboard' && platformData) {
    const liveStatValues = {
      totalLeagues: platformData.totalLeagues,
      totalAdmins: platformData.totalAdmins + platformData.totalCoAdmins,
      totalVolunteers: platformData.totalVolunteers,
      totalTasks: platformData.totalTasks,
      totalCompleted: platformData.totalCompleted,
      totalVisits: platformData.totalVisits
    };
    const meta = STAT_META[selectedStat];
    const fmtTick = ds => { const parts = String(ds).split('-'); return parts[1] + '/' + parts[2]; };

    // Recency line under a stat tile. Only Completed Tasks has one: it is the
    // single stat whose lifetime total says nothing about whether anything is
    // happening now, and it is the only one whose source docs carry a
    // per-record timestamp that loadPlatformData already had in hand. The
    // other five are point-in-time counts with no "last 7 days" to compute.
    const completedLast7 = platformData.leagues.reduce((n, lg) => n + (lg.completedLast7 || 0), 0);
    const statSub = { totalCompleted: completedLast7 + " in the last 7 days" };

    // ── Chart series ──────────────────────────────────────────────────────
    // Platform Visits is stored CUMULATIVELY (capture-daily-stats snapshots a
    // running counter), so charting it raw draws a line that only ever climbs
    // and says nothing about activity. It is differenced into per-day figures
    // instead. The cost is real and is surfaced in the UI rather than hidden:
    // differencing n snapshots yields n-1 days, so the OLDEST snapshot in any
    // range can never produce a figure -- there is nothing before it to
    // subtract. Every other stat is a point-in-time count and is charted as-is.
    const isCumulative = selectedStat === 'totalVisits';
    const rawPoints = statHistory.docs.map(d => ({ date: d.date, value: getDailyStatValue(d, selectedStat) }));
    const points = isCumulative
      ? rawPoints.slice(1).map((p, i) => ({ date: p.date, value: Math.max(0, p.value - rawPoints[i].value) }))
      : rawPoints;

    // DELIBERATE WORDING: "over this period", not the spec's "vs prior period".
    // No prior-window data is loaded, and fetching it would mean a second
    // Firestore query per range change. This compares the first and last point
    // of the window actually on screen, which is what the number really is.
    const delta = points.length >= 2 ? points[points.length - 1].value - points[0].value : null;

    // ── Recent leagues ────────────────────────────────────────────────────
    // byDateDesc is the SAME comparator loadPlatformData uses for the Leagues
    // tab. That is the point: this list previously sorted by string compare
    // while the Leagues tab sorted by date subtraction, so one bad createdAt
    // put the same league in two different positions on two screens.
    const recentLeagues = [...platformData.leagues].sort(byDateDesc('createdAt')).slice(0, 5);

    // ── Needs attention ───────────────────────────────────────────────────
    // Every item here is derived from data ALREADY loaded. Nothing in this
    // panel triggers a new read -- including "overdue tasks", which Phase 2
    // unlocked by making loadPlatformData keep the dueDate it was already
    // fetching and throwing away. See the comment there before changing how
    // overdue is defined; the predicate intentionally matches the league app's.
    const overdueLeagues = platformData.leagues.filter(lg => lg.overdueTasks > 0);
    const overdueCount = overdueLeagues.reduce((n, lg) => n + lg.overdueTasks, 0);
    const unclaimed = platformData.leagues.filter(lg => !lg.adminUid);
    const noVolunteers = platformData.leagues.filter(lg => !lg.volunteerCount);
    const soonIso = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
    const todayIso = new Date().toISOString().slice(0, 10);
    const expiringPromos = promoCodes.filter(pc => pc.active && pc.expiresAt && pc.expiresAt >= todayIso && pc.expiresAt <= soonIso);
    const attention = [
      overdueCount ? { key: 'overdue', icon: <CalendarIcon size={16} strokeWidth={2} />, text: overdueCount + (overdueCount === 1 ? ' task is overdue' : ' tasks are overdue'), detail: 'Across ' + overdueLeagues.length + (overdueLeagues.length === 1 ? ' league: ' : ' leagues: ') + overdueLeagues.map(l => leagueLabel(l.leagueName)).join(', '), tab: 'leagues' } : null,
      unclaimed.length ? { key: 'unclaimed', icon: <ClockIcon size={16} strokeWidth={2} />, text: unclaimed.length + (unclaimed.length === 1 ? ' league is unclaimed' : ' leagues are unclaimed'), detail: unclaimed.map(l => leagueLabel(l.leagueName)).join(', '), tab: 'leagues' } : null,
      expiringPromos.length ? { key: 'promos', icon: <TagIcon size={16} strokeWidth={2} />, text: expiringPromos.length + (expiringPromos.length === 1 ? ' promo code expires' : ' promo codes expire') + ' within 30 days', detail: expiringPromos.map(p => p.code).join(', '), tab: 'promos' } : null,
      noVolunteers.length ? { key: 'novols', icon: <UserRoundIcon size={16} strokeWidth={2} />, text: noVolunteers.length + (noVolunteers.length === 1 ? ' league has' : ' leagues have') + ' no volunteers', detail: noVolunteers.map(l => leagueLabel(l.leagueName)).join(', '), tab: 'leagues' } : null
    ].filter(Boolean);

    const navItems = [
      { tab: 'dashboard', label: 'Dashboard', icon: ChartNoAxesColumnIcon, count: null },
      { tab: 'leagues', label: 'Leagues', icon: UsersIcon, count: platformData.totalLeagues },
      { tab: 'promos', label: 'Promo codes', icon: TagIcon, count: promoCodes.length },
      { tab: 'ads', label: 'Advertising', icon: MegaphoneIcon, count: null }
    ];

    const pageTitle = { dashboard: 'Dashboard', leagues: 'Leagues', promos: 'Promo codes' }[saTab] || 'Dashboard';
    const pageSub = {
      dashboard: 'Platform owner view only · private',
      leagues: platformData.totalLeagues + (platformData.totalLeagues === 1 ? ' league' : ' leagues') + ' on the platform',
      promos: promoCodes.length + (promoCodes.length === 1 ? ' code' : ' codes') + ' created'
    }[saTab] || '';

    // ── Leagues tab ───────────────────────────────────────────────────────
    // Search and filter run client-side over the already-loaded array. The
    // platform has single-digit leagues and every field searched is already in
    // memory, so this costs no reads and needs no Firestore index.
    //
    // The chips say "On a plan" and "Free", NOT "Paid" and "Unpaid". A promo or
    // comped league has plan !== 'free' while having paid nothing -- that is
    // exactly the paying-vs-promo distinction CLAUDE.md warns about, and a chip
    // labelled "Paid" would quietly restate it wrongly. The per-card
    // Stripe/Promo pill is where that difference is actually readable.
    const saLeagueQ = saLeagueQuery.trim().toLowerCase();
    const saLeagues = platformData.leagues.filter(lg => {
      if (saLeagueFilter === 'unclaimed' && lg.adminUid) return false;
      if (saLeagueFilter === 'plan' && lg.plan === 'free') return false;
      if (saLeagueFilter === 'free' && lg.plan !== 'free') return false;
      if (!saLeagueQ) return true;
      return [lg.leagueName, lg.adminName, lg.adminEmail, lg.ownerClaimCode]
        .some(v => typeof v === 'string' && v.toLowerCase().includes(saLeagueQ));
    });

    // Looked up by ID on every render rather than snapshotted into state. Two
    // things fall out of that for free: removing a co-admin or a volunteer from
    // inside the drawer updates the drawer immediately, and deleting the league
    // makes this undefined, which closes the drawer without a second setState.
    const saLeague = saLeagueId ? platformData.leagues.find(l => l.id === saLeagueId) : null;

    // ── Promo codes tab (Phase 3) ─────────────────────────────────────────
    // The show/hide toggle is kept and is not cosmetic: hiding inactive codes
    // by default is right, but hiding them with no way back would remove the
    // ability to REACTIVATE a deactivated code, which is a real capability and
    // the whole reason Deactivate needs no confirm.
    const saInactivePromos = promoCodes.filter(pc => !pc.active);
    const saVisiblePromos = promoCodes.filter(pc => !hideInactivePromoCodes || pc.active);
    // A restricted code names the league it is locked to. leagueLabel supplies
    // the fallback for a league with no name -- that already worked in the
    // create form's select and must keep working here. A league that has since
    // been deleted still leaves its id on the code, so that case is named too
    // rather than rendering a blank.
    const promoLeagueName = (id) => {
      const lg = platformData.leagues.find(l => l.id === id);
      return lg ? leagueLabel(lg.leagueName) : "Deleted league";
    };

    const railInner = (
      <>
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid " + SA.railLine, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, color: SA.white, lineHeight: 1.1 }}>GroundsPal</div>
            <SaEyebrow style={{ color: SA.railMute, marginTop: 4, fontSize: 11 }}>Platform</SaEyebrow>
          </div>
          {/* Scrim-tap and Escape both close the drawer, but neither is
              discoverable on a phone -- so the off-canvas rail gets a real
              close control. The permanent desktop rail has nothing to close. */}
          {isMobile && (
            <SaBtn variant="railghost" size="sm" iconOnly onClick={() => setSaRailOpen(false)} title="Close menu"><XIcon size={18} /></SaBtn>
          )}
        </div>
        <div style={{ padding: "12px 12px 0", flex: 1, overflowY: "auto" }}>
          {navItems.map(n => {
            const on = saTab === n.tab;
            const Icon = n.icon;
            return (
              <button key={n.tab} type="button" onClick={() => saGo(n.tab)} className="sa--nav"
                style={{
                  width: "100%", height: SA.railRow, display: "flex", alignItems: "center", gap: 10,
                  padding: "0 12px", background: on ? SA.ink2 : "transparent",
                  // The 2px left border is present on EVERY row, transparent when
                  // inactive, so selecting a row cannot shift its label sideways.
                  borderLeft: "2px solid " + (on ? SA.accent : "transparent"),
                  borderTop: "none", borderRight: "none", borderBottom: "none",
                  color: on ? SA.white : SA.railMute, fontFamily: "inherit", fontSize: 14,
                  fontWeight: on ? 700 : 500, cursor: "pointer", textAlign: "left", borderRadius: 0
                }}>
                <Icon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.label}</span>
                {n.count !== null && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: on ? SA.accent : SA.railMute, fontVariantNumeric: "tabular-nums" }}>{n.count}</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: "auto", padding: 12, borderTop: "1px solid " + SA.railLine }}>
          <div style={{ padding: "0 12px 10px", minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: SA.white, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{platformEmail || 'Superadmin'}</div>
            <div style={{ fontSize: 12, color: SA.railMute }}>Signed in</div>
          </div>
          <SaBtn variant="railghost" size="sm" full
            onClick={async () => { await signOut(auth); setScreen('landing'); setPlatformData(null); setPlatformPassword(""); setPlatformEmail(""); }}>
            <LogOutIcon size={15} strokeWidth={2} /> Sign out
          </SaBtn>
        </div>
      </>
    );

    return (
    <div style={{ display: "flex", minHeight: "100vh", background: SA.paper, color: SA.ink }}>
      <SaStyles />
      {/* Mounted ONCE here, above the tab switch, so no tab body can be the one
          render path that forgets it -- the recurring ConfirmModal bug class
          documented in CLAUDE.md. Both modals are reachable from more than one
          tab (coach edits from Leagues, deletes from Leagues and Promo codes). */}
      <SaCoachEdit open={!!platformCoachEdit}
        name={platformCoachEdit && platformCoachEdit.name}
        leagueName={platformCoachEdit && platformCoachEdit.leagueName}
        isCoach={platformCoachEditIsCoach} coachRole={platformCoachEditCoachRole} saving={platformCoachEditSaving}
        onChangeIsCoach={handlePlatformCoachEditChangeIsCoach} onChangeCoachRole={setPlatformCoachEditCoachRole}
        onDone={() => savePlatformCoachEdit(true, platformCoachEditCoachRole)} onClose={closePlatformCoachEdit} />
      <ConfirmModal modal={confirmModal} onConfirm={() => { if (confirmModal && confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal(null); }} onCancel={() => setConfirmModal(null)} />

      {/* Rail: permanent at >=640px, off-canvas drawer below that. */}
      {!isMobile && (
        <div style={{ flex: "0 0 232px", width: 232, background: SA.black, borderRight: "1px solid " + SA.railLine, display: "flex", flexDirection: "column", position: "sticky", top: 0, alignSelf: "flex-start", height: "100vh" }}>
          {railInner}
        </div>
      )}
      {isMobile && (
        <SaDrawer open={saRailOpen} onClose={() => setSaRailOpen(false)} side="left" width={272}>
          <div style={{ display: "flex", flexDirection: "column", minHeight: "100%", background: SA.black }}>{railInner}</div>
        </SaDrawer>
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ background: SA.white, borderBottom: "1px solid " + SA.line, padding: isMobile ? "14px 16px" : "20px 32px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {isMobile && (
            <SaBtn variant="secondary" size="sm" iconOnly onClick={() => setSaRailOpen(true)} title="Menu"><MenuIcon size={18} /></SaBtn>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.display, fontSize: isMobile ? 22 : 28, fontWeight: 700, lineHeight: 1.1, color: SA.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pageTitle}</div>
            {!isMobile && <div style={{ fontSize: 13, color: SA.mute, marginTop: 4 }}>{pageSub}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SaBtn variant="secondary" size={isMobile ? "sm" : "md"} onClick={exportPlatformCoachesCSV} title="Export coaches CSV" iconOnly={isMobile}>
              <DownloadIcon size={16} strokeWidth={2} />{!isMobile && <span>Export coaches</span>}
            </SaBtn>
            <SaBtn variant="primary" size={isMobile ? "sm" : "md"} onClick={() => { setCreateLeagueError(""); setSaCreateOpen(true); }} title="New league" iconOnly={isMobile}>
              <PlusIcon size={16} strokeWidth={2.5} />{!isMobile && <span>New league</span>}
            </SaBtn>
          </div>
        </div>

        <div style={{ padding: isMobile ? 16 : "24px 32px", flex: 1 }}>

        {saTab === 'dashboard' && (
          <>
            {/* Stat strip */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 20 }}>
              {SA_STAT_ORDER.map(key => {
                const m = STAT_META[key];
                const on = selectedStat === key;
                return (
                  <SaCard key={key} className="sa--card-click" onClick={() => setSelectedStat(key)}
                    style={{ padding: 16, cursor: "pointer", borderColor: on ? SA.ink : SA.line, boxShadow: on ? SA.sh1 : undefined }}>
                    <SaEyebrow style={{ fontSize: 11 }}>{m.label}</SaEyebrow>
                    <div style={{ fontFamily: T.display, fontSize: 40, fontWeight: 700, lineHeight: 1.05, color: SA.ink, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                      {liveStatValues[key]}
                    </div>
                    {statSub[key] && (
                      <div style={{ fontSize: 12, color: SA.mute, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{statSub[key]}</div>
                    )}
                  </SaCard>
                );
              })}
            </div>

            {/* Activity */}
            <SaCard style={{ marginBottom: 20 }}>
              <div className="sa--tabstrip sa--fademask" ref={saTabStripRef}
                style={{ display: "flex", gap: 0, borderBottom: "1px solid " + SA.line, padding: "0 4px" }}>
                {SA_STAT_ORDER.map(key => {
                  const on = selectedStat === key;
                  return (
                    <button key={key} type="button" data-sa-selected={on ? "true" : "false"}
                      onClick={() => setSelectedStat(key)} className="sa--tab"
                      style={{
                        background: "none", border: "none", borderBottom: "2px solid " + (on ? SA.accent : "transparent"),
                        padding: "14px 12px", marginBottom: -1, color: on ? SA.ink : SA.mute,
                        fontFamily: "inherit", fontSize: 12, fontWeight: 700, letterSpacing: ".08em",
                        textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0
                      }}>
                      {STAT_META[key].short}
                    </button>
                  );
                })}
              </div>

              <div style={{ padding: isMobile ? 16 : 20 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <SaEyebrow style={{ fontSize: 11 }}>{meta.label}</SaEyebrow>
                    <div style={{ fontFamily: T.display, fontSize: 32, fontWeight: 700, lineHeight: 1.05, color: SA.ink, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                      {liveStatValues[selectedStat]}
                    </div>
                    {statRange !== 'today' && delta !== null && (
                      <div style={{ fontSize: 12, color: SA.mute, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <TrendingUpIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                        {(delta > 0 ? "+" : "") + delta} over this period
                      </div>
                    )}
                  </div>
                  <div className="sa--tabstrip" ref={saRangeStripRef} style={{ display: "flex", gap: 6, maxWidth: "100%" }}>
                    {[["today", "Today"], ["7d", "7D"], ["1m", "1M"], ["6m", "6M"], ["annual", "Annual"], ["all", "All"]].map(([key, label]) => {
                      const on = statRange === key;
                      return (
                        <button key={key} type="button" data-sa-selected={on ? "true" : "false"}
                          onClick={() => setStatRange(key)} className={"sa--chip" + (on ? " sa--chip-on" : "")}
                          style={{
                            height: 28, padding: "0 10px", borderRadius: SA.r1,
                            background: on ? SA.ink : SA.white, color: on ? SA.white : SA.mute,
                            border: "1px solid " + (on ? SA.ink : SA.line),
                            fontFamily: "inherit", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                            textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0
                          }}>{label}</button>
                      );
                    })}
                  </div>
                </div>

                {/* "Today" is deliberately NOT a chart range. There is one
                    snapshot per day, so a single day has no series to draw --
                    and DASHBOARD_STAT_RANGE_DAYS has no 'today' key, so routing
                    it through loadStatHistory would throw. It shows the live
                    value instead, which is what it has always meant. */}
                {statRange === 'today' ? (
                  <div style={{ padding: "28px 0", textAlign: "center" }}>
                    <div style={{ fontFamily: T.display, fontSize: 56, fontWeight: 700, lineHeight: 1, color: SA.ink, fontVariantNumeric: "tabular-nums" }}>{liveStatValues[selectedStat]}</div>
                    <div style={{ fontSize: 13, color: SA.mute, marginTop: 8 }}>Live value, right now</div>
                  </div>
                ) : statHistoryLoading ? (
                  <div style={{ padding: "48px 0", textAlign: "center", fontSize: 13, color: SA.mute }}>Loading trend…</div>
                ) : points.length < 2 ? (
                  <div style={{ padding: "40px 0", textAlign: "center" }}>
                    <div style={{ fontSize: 13, color: SA.mute }}>
                      {isCumulative && rawPoints.length === 1
                        ? "Platform Visits needs two snapshots before a daily figure exists — the first day has nothing to compare against."
                        : statHistory.trackingSince
                          ? "Not enough history in this range yet. Tracking since " + statHistory.trackingSince + "."
                          : "Tracking starts today — check back tomorrow for trend data."}
                    </div>
                  </div>
                ) : (() => {
                  // Spec geometry: 620x220 viewBox, gridlines at y=20/80/140/200,
                  // zero-baseline scale with 15% headroom.
                  const n = points.length;
                  const values = points.map(p => p.value);
                  const max = Math.max(...values);
                  const scale = max > 0 ? max * 1.15 : 1;
                  const xFor = i => (n === 1 ? 310 : (i * 620) / (n - 1));
                  const yFor = v => 200 - (v / scale) * 180;
                  const coords = points.map((p, i) => [xFor(i), yFor(p.value)]);
                  const line = coords.map(c => c[0].toFixed(1) + "," + c[1].toFixed(1)).join(" ");
                  const area = "0,200 " + line + " 620,200";
                  return (
                    <div>
                      <svg viewBox="0 0 620 220" preserveAspectRatio="none" style={{ width: "100%", height: 220, display: "block" }}>
                        {[20, 80, 140, 200].map(y => (
                          <line key={y} x1="0" y1={y} x2="620" y2={y} stroke={SA.line} strokeWidth="1" vectorEffect="non-scaling-stroke" />
                        ))}
                        <polygon points={area} fill={SA.area} />
                        <polyline points={line} fill="none" stroke={SA.accent} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
                        {coords.map((c, i) => (
                          <circle key={i} cx={c[0]} cy={c[1]} r="3.5" fill={SA.white} stroke={SA.accent} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                        ))}
                      </svg>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: SA.mute, marginTop: 8 }}>
                        <span>{fmtTick(points[0].date)}</span>
                        <span>{fmtTick(points[n - 1].date)}</span>
                      </div>
                      <div style={{ fontSize: 11, color: SA.mute, marginTop: 10 }}>
                        {isCumulative && <span>New visits per day, differenced from the cumulative counter. </span>}
                        {statHistory.trackingSince && <span>Tracking since {statHistory.trackingSince}.</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </SaCard>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 16, marginBottom: 20 }}>
              {/* Recent leagues */}
              <SaCard>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid " + SA.line, display: "flex", alignItems: "center", gap: 10 }}>
                  <SaEyebrow style={{ flex: 1 }}>Recent leagues</SaEyebrow>
                  <button type="button" onClick={() => saGo('leagues')}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: SA.ink, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    View all <ArrowRightIcon size={14} strokeWidth={2.5} />
                  </button>
                </div>
                {recentLeagues.length === 0 ? (
                  <div style={{ padding: 20, fontSize: 13, color: SA.mute }}>No leagues yet. Use “New league” to create the first one.</div>
                ) : recentLeagues.map(lg => (
                  // Opens THIS league, matching what clicking its card on the
                  // Leagues tab does. It used to call saGo('leagues') with no
                  // argument from lg at all, so every row -- each with its own
                  // chevron -- landed on the same generic destination already
                  // covered by "View all" above. The drawer stays in state
                  // rather than the URL, consistent with the Leagues tab;
                  // saGo runs FIRST because it clears the open drawer (see
                  // saCloseOverlays), so setting the id after it is what makes
                  // the drawer arrive open.
                  <div key={lg.id} className="sa--row"
                    onClick={() => { saGo('leagues'); setSaClaimCopied(false); setSaLeagueId(lg.id); }}
                    style={{ padding: "12px 20px", borderBottom: "1px solid " + SA.line, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: SA.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{leagueLabel(lg.leagueName)}</div>
                      <div style={{ fontSize: 12, color: SA.mute, marginTop: 2 }}>
                        {"Created " + fmtDate(lg.createdAt)} · {lg.volunteerCount} volunteers
                      </div>
                    </div>
                    {!lg.adminUid && <SaPill>Unclaimed</SaPill>}
                    {lg.plan !== 'free' && <SaPill solid>{lg.plan}</SaPill>}
                    <ChevronRightIcon size={16} strokeWidth={2} style={{ color: SA.mute, flexShrink: 0 }} />
                  </div>
                ))}
              </SaCard>

              {/* Needs attention */}
              <SaCard>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid " + SA.line }}>
                  <SaEyebrow>Needs attention</SaEyebrow>
                </div>
                {attention.length === 0 ? (
                  <div style={{ padding: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: SA.mute }}>
                    <CheckIcon size={16} strokeWidth={2.5} /> Nothing needs attention.
                  </div>
                ) : attention.map(a => (
                  <div key={a.key} className="sa--row" onClick={() => saGo(a.tab)}
                    style={{ padding: "12px 20px", borderBottom: "1px solid " + SA.line, display: "flex", gap: 10, cursor: "pointer" }}>
                    <span style={{ color: SA.mute, flexShrink: 0, marginTop: 1 }}>{a.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: SA.ink }}>{a.text}</div>
                      <div style={{ fontSize: 12, color: SA.mute, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail}</div>
                    </div>
                  </div>
                ))}
              </SaCard>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: SA.mute }}>
              <span>Last refreshed {new Date().toLocaleTimeString()}</span>
              <SaBtn variant="ghost" size="sm" onClick={handlePlatformLogin}><RefreshCwIcon size={14} strokeWidth={2} /> Refresh data</SaBtn>
            </div>
          </>
        )}

        {/* ── PROMO CODES TAB (Phase 3) ────────────────────────────────────
            Restyles what Phase 1 moved in here verbatim. Everything the old
            block could do survives, because the API is the constraint and it
            has not changed: only `free_year` and `percent_off` are valid types
            (api/promo-codes rejects anything else outright, so a third option
            would be a UI that cannot succeed), a `percent_off` code still
            carries its own 1-100 value, a code can still be restricted to one
            league, and inactive codes can still be shown and reactivated.
            The create form is a drawer now rather than an always-open block,
            matching create-league; the list is cards, matching Leagues. */}
        {saTab === 'promos' && (
          <>
            {/* Toolbar. The count on the right restates what is on screen when
                the inactive ones are hidden, so "3 codes" in the rail and a
                list of 2 never look like a miscount. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <SaBtn variant="primary" onClick={() => { setPromoCreateError(''); setSaPromoOpen(true); }}>
                <PlusIcon size={16} strokeWidth={2.5} /> New code
              </SaBtn>
              {saInactivePromos.length > 0 && (
                <button type="button" onClick={() => setHideInactivePromoCodes(v => !v)}
                  className={"sa--chip" + (hideInactivePromoCodes ? "" : " sa--chip-on")}
                  style={{
                    height: 28, padding: "0 10px", borderRadius: SA.r1,
                    background: hideInactivePromoCodes ? SA.white : SA.ink,
                    color: hideInactivePromoCodes ? SA.mute : SA.white,
                    border: "1px solid " + (hideInactivePromoCodes ? SA.line : SA.ink),
                    fontFamily: "inherit", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                    textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0
                  }}>
                  {hideInactivePromoCodes
                    ? "Show " + saInactivePromos.length + " inactive"
                    : "Hide inactive"}
                </button>
              )}
              {!isMobile && <div style={{ flex: 1 }} />}
              <div style={{ fontSize: 12, color: SA.mute, fontVariantNumeric: "tabular-nums" }}>
                {saVisiblePromos.length === promoCodes.length
                  ? promoCodes.length + (promoCodes.length === 1 ? " code" : " codes")
                  : "Showing " + saVisiblePromos.length + " of " + promoCodes.length}
              </div>
            </div>

            {promoCodesLoading ? (
              <SaCard style={{ padding: 20, fontSize: 13, color: SA.mute }}>Loading codes…</SaCard>
            ) : promoCodes.length === 0 ? (
              <SaCard style={{ padding: 20, fontSize: 13, color: SA.mute }}>
                No promo codes yet. Use “New code” to create the first one.
              </SaCard>
            ) : saVisiblePromos.length === 0 ? (
              <SaCard style={{ padding: 20, fontSize: 13, color: SA.mute }}>
                Every code is inactive. Use “Show {saInactivePromos.length} inactive” above to see them.
              </SaCard>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
                {saVisiblePromos.map(pc => {
                  const pct = pc.type === 'percent_off';
                  const expired = !!pc.expiresAt && pc.expiresAt < todayIso;
                  const usedUp = !!pc.maxRedemptions && pc.timesRedeemed >= pc.maxRedemptions;
                  return (
                    <SaCard key={pc.code} style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, background: pc.active ? SA.white : SA.paper }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, letterSpacing: ".06em", lineHeight: 1.15, color: SA.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pc.code}</div>
                        <div style={{ fontSize: 12, color: SA.mute, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(pct ? pc.value + "% off via Stripe" : "Free year of League tier") + " · Created " + fmtDate(pc.createdAt)}
                        </div>
                      </div>
                      {/* "Inactive" is the solid pill: it is the one state that
                          changes what the code DOES, and it has to survive being
                          read at a glance on a greyed card. Expired and fully
                          redeemed are outline -- they are consequences of the
                          numbers printed directly below them. */}
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {!pc.active && <SaPill solid>Inactive</SaPill>}
                        {pc.restrictedToLeagueId && <SaPill>{promoLeagueName(pc.restrictedToLeagueId)} only</SaPill>}
                        {expired && <SaPill>Expired</SaPill>}
                        {usedUp && <SaPill>Fully redeemed</SaPill>}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", borderTop: "1px solid " + SA.line, paddingTop: 10 }}>
                        {[
                          ["Redeemed", pc.timesRedeemed + (pc.maxRedemptions ? " / " + pc.maxRedemptions : ""), pc.maxRedemptions ? null : "No limit"],
                          ["Expires", pc.expiresAt || "—", pc.expiresAt ? null : "Never"]
                        ].map(([label, value, note], i) => (
                          <div key={label} style={{ paddingLeft: i === 0 ? 0 : 10, borderLeft: i === 0 ? "none" : "1px solid " + SA.line, minWidth: 0 }}>
                            <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, lineHeight: 1.1, color: SA.ink, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
                            <div style={{ fontSize: 11, color: SA.mute, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}{note ? " · " + note : ""}</div>
                          </div>
                        ))}
                      </div>
                      {/* Deactivate gets NO confirm on purpose: it is reversible
                          from the row next to it, and putting a confirm in front
                          of a reversible action is what teaches people to click
                          through the ones that are not. Delete is the opposite
                          and routes through SaConfirm. */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, borderTop: "1px solid " + SA.line, paddingTop: 12 }}>
                        <SaBtn variant="secondary" size="sm" onClick={() => handleTogglePromoCode(pc.code, !pc.active)}>
                          {pc.active ? "Deactivate" : "Reactivate"}
                        </SaBtn>
                        <div style={{ flex: 1 }} />
                        <SaBtn variant="dangerghost" size="sm" iconOnly title={"Delete " + pc.code}
                          disabled={promoDeletingCode === pc.code}
                          onClick={() => setSaConfirm({
                            kicker: "Permanent",
                            title: "Delete “" + pc.code + "”?",
                            body: pc.timesRedeemed > 0 ? (
                              <>
                                <div><strong>{pc.code}</strong> has been redeemed {pc.timesRedeemed} {pc.timesRedeemed === 1 ? "time" : "times"}.</div>
                                <div style={{ marginTop: 10 }}>
                                  Deleting it does <strong>not</strong> affect the {pc.timesRedeemed === 1 ? "league" : "leagues"} that already redeemed it — they keep the access it granted. It removes the code itself, so it can no longer be redeemed and its redemption count is no longer recorded anywhere.
                                </div>
                                <div style={{ marginTop: 10, color: SA.mute }}>
                                  If the goal is only to stop new redemptions, Deactivate does that and can be undone.
                                </div>
                              </>
                            ) : (
                              <>
                                <div>Permanently delete <strong>{pc.code}</strong>. It has never been redeemed, so nothing loses access.</div>
                                <div style={{ marginTop: 10, color: SA.mute }}>This cannot be undone.</div>
                              </>
                            ),
                            confirmLabel: "Delete this code",
                            onConfirm: () => handleDeletePromoCode(pc.code)
                          })}>
                          <Trash2Icon size={15} strokeWidth={2} />
                        </SaBtn>
                      </div>
                    </SaCard>
                  );
                })}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: SA.mute, marginTop: 20 }}>
              <SaBtn variant="ghost" size="sm" onClick={loadPromoCodes} disabled={promoCodesLoading}>
                <RefreshCwIcon size={14} strokeWidth={2} /> Refresh codes
              </SaBtn>
            </div>
          </>
        )}

        {/* ── LEAGUES TAB ──────────────────────────────────────────────────
            Phase 2 replaces the moved-verbatim list Phase 1 dropped in here.
            Every capability of that list survives -- emulate, delete league,
            coach-flag edit on admin/co-admin/volunteer, remove co-admin, remove
            volunteer, copy claim code -- but the expand/collapse row is now a
            drawer and the three window.confirm() calls are now SaConfirm.
            The "ALL LEAGUES (n)" heading is gone because the rail already names
            and counts this tab, and the page header repeats the count. */}
        {saTab === 'leagues' && (
          <>
            {/* Filter bar. The chips filter; the field searches name, admin name,
                admin email and claim code -- claim code included on purpose,
                because handing one out is the only reason to go looking for a
                league you cannot name. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <input className="sa--input" value={saLeagueQuery} onChange={e => setSaLeagueQuery(e.target.value)}
                placeholder="Search leagues, admins, claim codes…"
                style={{ flex: isMobile ? "1 1 100%" : "0 1 320px", minWidth: 0, height: SA.hMd, padding: "0 12px", border: "1px solid " + SA.line, borderRadius: SA.r2, background: SA.white, color: SA.ink, fontFamily: "inherit", fontSize: 14 }} />
              <div className="sa--tabstrip" style={{ display: "flex", gap: 6, maxWidth: "100%" }}>
                {[['all', 'All'], ['unclaimed', 'Unclaimed'], ['plan', 'On a plan'], ['free', 'Free']].map(([key, label]) => {
                  const on = saLeagueFilter === key;
                  return (
                    <button key={key} type="button"
                      onClick={() => setSaLeagueFilter(key)} className={"sa--chip" + (on ? " sa--chip-on" : "")}
                      style={{
                        height: 28, padding: "0 10px", borderRadius: SA.r1,
                        background: on ? SA.ink : SA.white, color: on ? SA.white : SA.mute,
                        border: "1px solid " + (on ? SA.ink : SA.line),
                        fontFamily: "inherit", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                        textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0
                      }}>{label}</button>
                  );
                })}
              </div>
              {!isMobile && <div style={{ flex: 1 }} />}
              <div style={{ fontSize: 12, color: SA.mute, fontVariantNumeric: "tabular-nums" }}>
                {saLeagues.length === platformData.leagues.length
                  ? saLeagues.length + (saLeagues.length === 1 ? " league" : " leagues")
                  : "Showing " + saLeagues.length + " of " + platformData.leagues.length}
              </div>
            </div>

            {saLeagues.length === 0 ? (
              <SaCard style={{ padding: 20, fontSize: 13, color: SA.mute }}>
                {platformData.leagues.length === 0
                  ? "No leagues yet. Use “New league” to create the first one."
                  : "No leagues match this search or filter."}
              </SaCard>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
                {saLeagues.map(lg => (
                  <SaCard key={lg.id} className="sa--card-click"
                    onClick={() => { setSaClaimCopied(false); setSaLeagueId(lg.id); }}
                    style={{ padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.15, color: SA.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{leagueLabel(lg.leagueName)}</div>
                        <div style={{ fontSize: 12, color: SA.mute, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(lg.adminUid ? (lg.adminName || "—") : "Awaiting owner") + " · Created " + fmtDate(lg.createdAt)}
                        </div>
                      </div>
                      <ChevronRightIcon size={16} strokeWidth={2} style={{ color: SA.mute, flexShrink: 0, marginTop: 4 }} />
                    </div>
                    {/* "Stripe" vs "Promo / comp" is the paying-vs-promo split, and
                        it stays visible on the card rather than only in the drawer
                        -- it is the one thing about a league that a chip labelled
                        "On a plan" deliberately does not tell you. */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {!lg.adminUid && <SaPill>Unclaimed</SaPill>}
                      {lg.plan !== 'free' && <SaPill solid>{lg.plan}{!lg.planActive ? ' · inactive' : ''}</SaPill>}
                      {lg.plan !== 'free' && <SaPill>{lg.stripeCustomerId ? "Stripe" : "Promo / comp"}</SaPill>}
                      {lg.overdueTasks > 0 && <SaPill>{lg.overdueTasks} overdue</SaPill>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderTop: "1px solid " + SA.line, paddingTop: 10 }}>
                      {[["Co-admins", lg.coAdminCount], ["Volunteers", lg.volunteerCount], ["Active", lg.activeTasks], ["Done", lg.completedTasks]].map(([label, value], i) => (
                        <div key={label} style={{ paddingLeft: i === 0 ? 0 : 10, borderLeft: i === 0 ? "none" : "1px solid " + SA.line, minWidth: 0 }}>
                          <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, lineHeight: 1.1, color: SA.ink, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                          <div style={{ fontSize: 11, color: SA.mute, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </SaCard>
                ))}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: SA.mute, marginTop: 20 }}>
              <span>Last refreshed {new Date().toLocaleTimeString()}</span>
              <SaBtn variant="ghost" size="sm" onClick={handlePlatformLogin}><RefreshCwIcon size={14} strokeWidth={2} /> Refresh data</SaBtn>
            </div>
          </>
        )}

        </div>
      </div>

      {/* League detail. This is what the old expand/collapse row became.
          The design spec draws a single "VOLUNTEERS (n) / Active in the last
          30 days" line and, by drawing only that, would have removed the
          volunteer list, the co-admin list, BOTH Remove actions and coach-flag
          editing for all three roles. All of it is kept, extending below the
          count -- functional parity is the gate at every phase boundary and the
          designer was working from a mockup, not the live console.
          "View & Edit as League" leads because the spec does not know emulation
          exists and gave it no slot at all, and it is the most-used action here.
          The claim-code block is gated on ownerClaimCode (the spec draws it
          unconditionally); the code is nulled the moment a league is claimed,
          so an ungated block would show an empty box on every real league. */}
      {/* The guard covers BOTH overlays that can sit on top of this drawer.
          Every SaDrawer mounts its own window keydown listener, so without it
          one Escape would close the confirm (or the coach editor) and the
          league drawer underneath it in the same keystroke. Scrim clicks need
          no guard -- those overlays sit at a higher z and swallow the click. */}
      <SaDrawer open={!!saLeague} onClose={() => { if (!saConfirm && !platformCoachEdit) setSaLeagueId(null); }}
        kicker={saLeague && !saLeague.adminUid ? "Unclaimed league" : "League"}
        title={saLeague ? leagueLabel(saLeague.leagueName) : ""} width={520}>
        {saLeague && (
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 22 }}>

            <div>
              <SaBtn variant="primary" full onClick={() => { setSaLeagueId(null); handleEmulateLeague(saLeague.id); }}>
                <EyeIcon size={16} strokeWidth={2} /> View &amp; Edit as League
              </SaBtn>
              <div style={{ fontSize: 12, color: SA.mute, marginTop: 8 }}>
                Opens this league’s own admin view. “Exit to Console” brings you back here and does not sign you out.
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", border: "1px solid " + SA.line, borderRadius: SA.r3, overflow: "hidden" }}>
              {[["Co-admins", saLeague.coAdminCount], ["Volunteers", saLeague.volunteerCount], ["Active", saLeague.activeTasks], ["Done", saLeague.completedTasks]].map(([label, value], i) => (
                <div key={label} style={{ padding: "12px 10px", borderLeft: i === 0 ? "none" : "1px solid " + SA.line, minWidth: 0 }}>
                  <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1.05, color: SA.ink, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                  <div style={{ fontSize: 11, color: SA.mute, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
                </div>
              ))}
            </div>

            <div>
              <SaEyebrow style={{ marginBottom: 10 }}>Details</SaEyebrow>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                {[
                  ["Created", fmtDate(saLeague.createdAt)],
                  ["Plan", saLeague.plan === 'free' ? "Free" : (saLeague.plan + (saLeague.planActive ? "" : " · inactive"))],
                  saLeague.plan !== 'free' ? ["Billing", saLeague.stripeCustomerId ? "Stripe subscription" : "Promo / comped"] : null,
                  (saLeague.plan !== 'free' && !saLeague.stripeCustomerId && saLeague.planExpiresAt) ? ["Expires", fmtDate(saLeague.planExpiresAt)] : null,
                  ["Overdue tasks", String(saLeague.overdueTasks || 0)]
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} style={{ display: "flex", gap: 12 }}>
                    <div style={{ flex: "0 0 110px", color: SA.mute }}>{label}</div>
                    <div style={{ flex: 1, minWidth: 0, fontWeight: 700, color: SA.ink }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {saLeague.adminUid ? (
              <div>
                <SaEyebrow style={{ marginBottom: 4 }}>Admin</SaEyebrow>
                {/* No Remove action on the owner by design: removing the owner is
                    what Delete League is, and a half-deleted league with no
                    adminUid and no claim code cannot be recovered from this UI. */}
                <SaPersonRow name={saLeague.adminName} sub={saLeague.adminEmail}
                  isCoach={saLeague.adminIsCoach} coachRole={saLeague.adminCoachRole} tag="Owner"
                  onEditCoach={() => openPlatformCoachEdit('admin', saLeague.id, saLeague.adminUid, saLeague.adminName, leagueLabel(saLeague.leagueName), saLeague.adminIsCoach, saLeague.adminCoachRole)} />
              </div>
            ) : (
              <div>
                <SaEyebrow style={{ marginBottom: 10 }}>Awaiting owner</SaEyebrow>
                {saLeague.ownerClaimCode ? (
                  <>
                    <div style={{ border: "1px solid " + SA.line, borderRadius: SA.r3, padding: "18px 16px", textAlign: "center", background: SA.paper }}>
                      <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, letterSpacing: ".2em", color: SA.ink }}>{saLeague.ownerClaimCode}</div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <SaBtn variant="secondary" full
                        onClick={() => { navigator.clipboard.writeText(saLeague.ownerClaimCode); setSaClaimCopied(true); setTimeout(() => setSaClaimCopied(false), 2000); }}>
                        {saClaimCopied ? <><CheckIcon size={16} strokeWidth={2.5} /> Copied</> : "Copy claim code"}
                      </SaBtn>
                    </div>
                    <div style={{ fontSize: 12, color: SA.mute, marginTop: 8 }}>
                      The future owner enters this on the “Claim a League” screen. It stops working once the league is claimed.
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: SA.mute }}>No claim code on file.</div>
                )}
              </div>
            )}

            <div>
              <SaEyebrow style={{ marginBottom: 4 }}>Co-admins ({saLeague.coAdmins ? saLeague.coAdmins.length : 0})</SaEyebrow>
              {(!saLeague.coAdmins || saLeague.coAdmins.length === 0) ? (
                <div style={{ fontSize: 13, color: SA.mute, borderTop: "1px solid " + SA.line, paddingTop: 10 }}>No co-admins.</div>
              ) : saLeague.coAdmins.map(ca => (
                <SaPersonRow key={ca.uid} name={ca.name} sub={ca.email} isCoach={ca.isCoach} coachRole={ca.coachRole}
                  onEditCoach={() => openPlatformCoachEdit('coadmin', saLeague.id, ca.uid, ca.name, leagueLabel(saLeague.leagueName), ca.isCoach, ca.coachRole)}
                  removeTitle="Remove co-admin"
                  onRemove={() => setSaConfirm({
                    kicker: "Co-admin",
                    title: "Remove " + (ca.name || "this co-admin") + "?",
                    body: <>They lose admin access to <strong>{leagueLabel(saLeague.leagueName)}</strong> immediately. Their GroundsPal account is not deleted, and they can rejoin with the league’s co-admin invite code.</>,
                    confirmLabel: "Remove co-admin",
                    onConfirm: () => deletePlatformCoAdmin(saLeague.id, ca.uid, ca.name)
                  })} />
              ))}
            </div>

            <div>
              {/* The spec's subtitle for this count -- "Active in the last 30
                  days" -- is deliberately NOT rendered. `members` is every
                  volunteer ever joined, and there is no per-volunteer activity
                  timestamp loaded here, so printing it would be a false claim
                  about the number beside it. */}
              <SaEyebrow style={{ marginBottom: 4 }}>Volunteers ({saLeague.members ? saLeague.members.length : 0})</SaEyebrow>
              {(!saLeague.members || saLeague.members.length === 0) ? (
                <div style={{ fontSize: 13, color: SA.mute, borderTop: "1px solid " + SA.line, paddingTop: 10 }}>No volunteers yet.</div>
              ) : saLeague.members.map(m => (
                <SaPersonRow key={m.id} name={m.name} sub={"Joined " + fmtDate(m.joinedAt)} isCoach={m.isCoach} coachRole={m.coachRole}
                  onEditCoach={() => openPlatformCoachEdit('volunteer', saLeague.id, m.id, m.name, leagueLabel(saLeague.leagueName), m.isCoach, m.coachRole)}
                  removeTitle="Remove volunteer"
                  onRemove={() => setSaConfirm({
                    kicker: "Volunteer",
                    title: "Remove " + (m.name || "this volunteer") + "?",
                    body: <>They lose access to <strong>{leagueLabel(saLeague.leagueName)}</strong> immediately. Tasks they already completed stay in the league’s archive, and they can rejoin with the volunteer invite code.</>,
                    confirmLabel: "Remove volunteer",
                    onConfirm: () => deletePlatformVolunteer(saLeague.id, m.id, leagueLabel(saLeague.leagueName))
                  })} />
              ))}
            </div>

            {/* Delete league. The spec's treatment -- a ghost trash icon -- is
                followed, but the confirm behind it is deliberately heavier than
                the window.confirm() it replaces. deletePlatformLeague sweeps
                five subcollections and the league has roughly fourteen, so it
                orphans projects, seasons, divisions, teams, players, games,
                waivers, registrants and calendar events. That sweep is NOT
                fixed in this phase (out of scope), which is exactly why the
                barrier to firing it is raised rather than lowered. */}
            <div style={{ borderTop: "1px solid " + SA.line, paddingTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: SA.ink }}>Delete league</div>
                <div style={{ fontSize: 12, color: SA.mute, marginTop: 2 }}>Permanent. There is no undo and no export.</div>
              </div>
              <SaBtn variant="dangerghost" iconOnly title="Delete league"
                onClick={() => setSaConfirm({
                  kicker: "Permanent",
                  title: "Delete “" + leagueLabel(saLeague.leagueName) + "”?",
                  body: <>
                    <div>This permanently deletes <strong>{leagueLabel(saLeague.leagueName)}</strong> and all of its data. It cannot be undone and there is no export.</div>
                    <div style={{ marginTop: 10 }}>
                      {saLeague.volunteerCount} {saLeague.volunteerCount === 1 ? "volunteer" : "volunteers"}, {saLeague.coAdminCount} {saLeague.coAdminCount === 1 ? "co-admin" : "co-admins"}
                      {saLeague.adminUid ? " and its admin" : ""} lose access immediately, along with {saLeague.activeTasks} active and {saLeague.completedTasks} completed tasks.
                    </div>
                    <div style={{ marginTop: 10, color: SA.mute }}>
                      Known limitation: this deletes the league document plus its tasks, archive, supplies, members and notifications. Other subcollections — projects, seasons, divisions, teams, players, games, waivers, registrants and calendar events — are left behind in Firestore as orphaned documents and have to be cleaned up by hand.
                    </div>
                  </>,
                  confirmLabel: "Delete this league",
                  onConfirm: async () => { await deletePlatformLeague(saLeague.id); setSaLeagueId(null); }
                })}>
                <Trash2Icon size={18} strokeWidth={2} />
              </SaBtn>
            </div>

          </div>
        )}
      </SaDrawer>

      {/* One confirm for every destructive action on this screen -- delete
          league, remove co-admin, remove volunteer (Phase 2) and delete promo
          code (Phase 3) -- replacing four native window.confirm() dialogs that
          read as a different application in the middle of a redesigned
          console. Mounted HERE, beside the drawers and
          outside every tab body, for the same reason CoachPromptModal is
          mounted above the tab switch: a modal that lives inside one render
          path is a modal some other render path can forget -- the recurring
          ConfirmModal bug class in CLAUDE.md. SaConfirm renders through
          SaDrawer at z=4200, above the league drawer's 4000, so the stacking
          is explicit rather than decided by portal append order. */}
      <SaConfirm open={!!saConfirm} kicker={saConfirm && saConfirm.kicker}
        title={saConfirm ? saConfirm.title : ""} body={saConfirm && saConfirm.body}
        confirmLabel={saConfirm ? saConfirm.confirmLabel : ""}
        onConfirm={runSaConfirm} onCancel={cancelSaConfirm} busy={saConfirmBusy} />

      {/* Create league. Was an always-open form block on the old dashboard;
          it is a drawer now, but the handler, the validation, the error text
          and the claim-code success state are the same ones. The claim code
          is NOT recoverable from anywhere else in this UI at creation time,
          so the success state stays until it is explicitly dismissed -- it
          does not auto-close on save. */}
      <SaDrawer open={saCreateOpen} onClose={() => setSaCreateOpen(false)} kicker="Platform" title="New league">
        <div style={{ padding: 20 }}>
          {justCreatedLeague ? (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: SA.ink, marginBottom: 6 }}>“{leagueLabel(justCreatedLeague.leagueName)}” created — no owner yet</div>
              <div style={{ fontSize: 13, color: SA.mute, marginBottom: 16 }}>
                Give this claim code to the league’s future owner. They enter it on the “Claim a League” screen to create their account and become the admin.
              </div>
              <div style={{ border: "1px solid " + SA.line, borderRadius: SA.r3, padding: "20px 16px", textAlign: "center", marginBottom: 16, background: SA.paper }}>
                <div style={{ fontFamily: T.display, fontSize: 32, fontWeight: 700, letterSpacing: ".2em", color: SA.ink }}>{justCreatedLeague.claimCode}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <SaBtn variant="primary" full onClick={() => { navigator.clipboard.writeText(justCreatedLeague.claimCode); setClaimCodeCopied(true); setTimeout(() => setClaimCodeCopied(false), 2000); }}>
                  {claimCodeCopied ? <><CheckIcon size={16} strokeWidth={2.5} /> Copied</> : "Copy claim code"}
                </SaBtn>
                <SaBtn variant="secondary" onClick={() => { setJustCreatedLeague(null); setSaCreateOpen(false); }}>Done</SaBtn>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 13, color: SA.mute }}>
                Creates a league with no owner yet. You can configure it fully from Leagues → “View &amp; Edit as League,” then hand the claim code to a partner so they can create their account and become the owner.
              </div>
              <SaInput label="League name" placeholder="e.g. Houston Youth Baseball" value={newLeagueForm.name}
                onChange={e => setNewLeagueForm({ ...newLeagueForm, name: e.target.value })} />
              <SaInput label="Zip code (optional)" placeholder="77058" inputMode="numeric" value={newLeagueForm.zipCode}
                onChange={e => setNewLeagueForm({ ...newLeagueForm, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) })} />
              {createLeagueError && <div style={{ fontSize: 13, color: SA.ink, border: "1px solid " + SA.line, borderLeft: "3px solid " + SA.ink, borderRadius: SA.r2, padding: "10px 12px" }}>{createLeagueError}</div>}
              <SaBtn variant="primary" full disabled={creatingLeague} onClick={handleCreateLeaguePlatform}>
                {creatingLeague ? "Creating…" : <><PlusIcon size={16} strokeWidth={2.5} /> Create league</>}
              </SaBtn>
            </div>
          )}
        </div>
      </SaDrawer>

      {/* Create promo code (Phase 3). Same drawer treatment as create-league,
          and the same handler/validation/error text as the block it replaces.
          It DOES auto-close on success, unlike create-league -- a promo code is
          fully recoverable from the list behind it, whereas a claim code is
          shown exactly once and nowhere else.
          The percent field is conditional and required, not optional: the API
          rejects a percent_off code whose value is not 1-100, so the field is
          load-bearing for that code type and cannot be dropped. */}
      <SaDrawer open={saPromoOpen} onClose={() => { if (!promoCreating) setSaPromoOpen(false); }} kicker="Platform" title="New code">
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 13, color: SA.mute }}>
            A league admin enters this on their Billing &amp; Plan page. A free-year code grants League tier without payment; a percent-off code discounts a real Stripe subscription.
          </div>
          <SaInput label="Code" placeholder="e.g. FOUNDING2026" value={promoForm.code}
            onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} />
          {/* Exactly two options, because api/promo-codes accepts exactly two.
              Anything else is a 400, so a third entry here would be a control
              that cannot succeed. */}
          <SaSelect label="What it grants" value={promoForm.type}
            onChange={e => setPromoForm({ ...promoForm, type: e.target.value })}>
            <option value="free_year">Free year of League tier</option>
            <option value="percent_off">Percent off (Stripe)</option>
          </SaSelect>
          {promoForm.type === 'percent_off' && (
            <div>
              <SaInput label="Percent off" type="number" inputMode="numeric" placeholder="e.g. 20" value={promoForm.value}
                onChange={e => setPromoForm({ ...promoForm, value: e.target.value })} />
              <div style={{ fontSize: 12, color: SA.mute, marginTop: 6 }}>Required, 1–100. Creates a matching Stripe coupon and promotion code.</div>
            </div>
          )}
          <div>
            <SaInput label="Max redemptions" type="number" inputMode="numeric" placeholder="Leave blank for unlimited" value={promoForm.maxRedemptions}
              onChange={e => setPromoForm({ ...promoForm, maxRedemptions: e.target.value })} />
            <div style={{ fontSize: 12, color: SA.mute, marginTop: 6 }}>Blank means unlimited.</div>
          </div>
          <div>
            <SaInput label="Expires" placeholder="YYYY-MM-DD" value={promoForm.expiresAt}
              onChange={e => setPromoForm({ ...promoForm, expiresAt: e.target.value })} />
            <div style={{ fontSize: 12, color: SA.mute, marginTop: 6 }}>Optional. Blank means it never expires.</div>
          </div>
          {/* leagueLabel supplies the fallback name for a league whose own name
              is blank -- that already worked here and is not regressed. */}
          <SaSelect label="Restrict to one league" value={promoForm.restrictedToLeagueId}
            onChange={e => setPromoForm({ ...promoForm, restrictedToLeagueId: e.target.value })}>
            <option value="">Any league can redeem</option>
            {platformData.leagues.map(lg => <option key={lg.id} value={lg.id}>{leagueLabel(lg.leagueName)}</option>)}
          </SaSelect>
          {/* The API's own message, verbatim. The two it produces most often --
              a percent_off code with no value, and a duplicate code -- are both
              things the form cannot know on its own without keeping a second
              copy of the endpoint's rules on the client. */}
          {promoCreateError && <div style={{ fontSize: 13, color: SA.ink, border: "1px solid " + SA.line, borderLeft: "3px solid " + C.redFill, borderRadius: SA.r2, padding: "10px 12px" }}>{promoCreateError}</div>}
          <SaBtn variant="primary" full disabled={promoCreating}
            onClick={async () => { if (await handleCreatePromoCode()) setSaPromoOpen(false); }}>
            {promoCreating ? "Creating…" : <><PlusIcon size={16} strokeWidth={2.5} /> Create code</>}
          </SaBtn>
        </div>
      </SaDrawer>
    </div>
    );
  }

  // ─── PLATFORM ADVERTISING ────────────────────────────────────────────────
  if (screen === 'platform-advertising') return (
    <div style={{ background: C.surface, minHeight: 600 }}>
      <div style={{ background: "#111111", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* NAVIGATION ONLY -- this screen is otherwise deliberately untouched
              by the redesign (see the seams note in CLAUDE.md), and its styling
              here is pre-redesign on purpose. It called setScreen directly,
              which swapped the rendered screen without moving the URL, so the
              Dashboard rendered while the address bar still read
              /superadmin/advertising. saGo is the console's one routing entry
              point: it pushes the matching path, sets saTab so the rail agrees
              with what is on screen, and runs saCloseOverlays so no overlay
              outlives the tab change -- the same three things every other tab
              change does. */}
          <button onClick={() => saGo('dashboard')} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: R.control, padding: "5px 11px", color: "rgba(255,255,255,0.8)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.white }}>Sponsored Banners</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Ad inventory shown on the Tasks tab</div>
          </div>
        </div>
        <button onClick={openNewBannerForm}
          style={{ background: C.greenMid, border: "none", borderRadius: R.control, padding: "6px 14px", color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          + New Sponsored Banner
        </button>
      </div>

      <div style={{ padding: "16px 14px" }}>
        {showBannerForm && (
          <div style={{ background: C.white, border: "1.5px solid #75C83C", borderRadius: R.card, padding: "1rem", marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>{editingBannerId ? "Edit banner" : "New sponsored banner"}</div>
            <input placeholder="Name, e.g. Bruce Bolt 10% Off" value={bannerDraft.name} onChange={e => setBannerDraft({ ...bannerDraft, name: e.target.value })}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", marginBottom: 10 }} />
            <input placeholder="Link URL, e.g. https://brucebolt.us" value={bannerDraft.linkUrl} onChange={e => setBannerDraft({ ...bannerDraft, linkUrl: e.target.value })}
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", marginBottom: 10 }} />
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Banner image {bannerDraft.imageUrl && !bannerDraft.imageFile && "(current image will be kept unless you choose a new one)"}</label>
              <input type="file" accept="image/*" onChange={e => setBannerDraft({ ...bannerDraft, imageFile: e.target.files[0] || null })} style={{ fontSize: 13 }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>TARGETING</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <button onClick={() => setBannerDraft({ ...bannerDraft, scope: "all", leagueIds: [] })}
                  style={{ flex: 1, padding: "8px", borderRadius: R.control, border: (bannerDraft.scope === "all" ? "2.5px solid #75C83C" : "1.5px solid #444441"), background: bannerDraft.scope === "all" ? C.greenLt : C.white, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                  All leagues
                </button>
                <button onClick={() => setBannerDraft({ ...bannerDraft, scope: "specific" })}
                  style={{ flex: 1, padding: "8px", borderRadius: R.control, border: (bannerDraft.scope === "specific" ? "2.5px solid #75C83C" : "1.5px solid #444441"), background: bannerDraft.scope === "specific" ? C.greenLt : C.white, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
                  Specific leagues
                </button>
              </div>
              {bannerDraft.scope === "specific" && platformData && (
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid " + C.border, borderRadius: R.control, padding: 8 }}>
                  {platformData.leagues.map(lg => {
                    const on = bannerDraft.leagueIds.includes(lg.id);
                    return (
                      <label key={lg.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", cursor: "pointer" }}>
                        <input type="checkbox" checked={on} onChange={() => setBannerDraft({ ...bannerDraft, leagueIds: on ? bannerDraft.leagueIds.filter(id => id !== lg.id) : [...bannerDraft.leagueIds, lg.id] })} />
                        <span style={{ fontSize: 13 }}>{leagueLabel(lg.leagueName)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={bannerDraft.active} onChange={e => setBannerDraft({ ...bannerDraft, active: e.target.checked })} />
              <span style={{ fontSize: 13 }}>Active</span>
            </label>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
              Only one global banner can be active at a time (activating this one deactivates any other global banner). Each league can have up to 2 active specific banners at once, on top of whatever global banner is running.
            </div>
            {bannerError && <div style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "8px 12px", fontSize: 12, color: C.redDark, marginBottom: 10 }}>{bannerError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveBanner} disabled={bannerSaving}
                style={{ flex: 1, background: bannerSaving ? C.grayBd : "#75C83C", color: C.text, border: "none", borderRadius: R.control, padding: "10px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                {bannerSaving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => { setShowBannerForm(false); setBannerDraft(emptyBannerDraft); setEditingBannerId(null); }}
                style={{ background: C.grayLt, color: C.muted, border: "1px solid " + C.border, borderRadius: R.control, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {platformBanners.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: C.muted, fontSize: 13 }}>No sponsored banners yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {platformBanners.map(b => (
              <div key={b.id} style={{ background: C.white, border: "1.5px solid #444441", borderRadius: R.card, padding: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {b.imageUrl && <img src={b.imageUrl} alt="" style={{ width: 80, height: 44, objectFit: "cover", borderRadius: R.control, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{b.linkUrl}</div>
                  <div style={{ fontSize: 11, color: C.blue, marginTop: 2 }}>
                    {b.scope === "all" ? "All leagues" : (b.leagueIds || []).length + " league" + ((b.leagueIds || []).length === 1 ? "" : "s")}
                  </div>
                </div>
                <div onClick={() => toggleBannerActive(b)} style={{ cursor: "pointer", width: 44, height: 24, borderRadius: R.control, background: b.active ? C.greenMid : "#D6D6D4", position: "relative", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: b.active ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
                </div>
                <button onClick={() => openEditBannerForm(b)} style={{ background: C.blueLt, border: "1px solid " + C.blueBd, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.blue, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                <button onClick={() => deleteBanner(b.id)} style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ─── MAIN APP ─────────────────────────────────────────────────────
  const selectedTask = selectedTaskId ? (tasks.find(t => t.id === selectedTaskId) || archive.find(t => t.id === selectedTaskId)) : null;
  const stats = { open: tasks.filter(t => t.status === "open").length, ip: tasks.filter(t => t.status === "in-progress").length, mine: tasks.filter(t => t.assignedTo === (user && user.name)).length, arch: archive.length, sup: supplies.filter(s => !s.purchased).length };
  const LOCATIONS = leagueLocations.filter(l => l.trim() !== '');
  const filtered = (() => {
    let list = tasks.filter(t => {
      if (taskFilter === "open") return t.status === "open";
      if (taskFilter === "in-progress") return t.status === "in-progress";
      if (taskFilter === "mine") return t.assignedTo === (user && user.name);
      return true;
    }).filter(t => !locationFilter || (Array.isArray(t.locations) && t.locations.length > 0 ? t.locations.includes(locationFilter) : t.location === locationFilter))
      .filter(t => !groupFilter || (t.groups || []).includes(groupFilter));
    if (taskSort === "priority") {
      const order = { high: 0, medium: 1, low: 2 };
      list = [...list].sort((a, b) => (order[a.priority] ?? 1) - (order[b.priority] ?? 1));
    } else if (taskSort === "unclaimed") {
      list = [...list].filter(t => !t.assignedTo).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .concat([...list].filter(t => t.assignedTo));
    }
    return list;
  })();

  // ─── TASK DETAIL ──────────────────────────────────────────────────
  if (selectedTask) {
    const task = selectedTask;
    const st = STATUS[task.status]; const pr = PRIORITY[task.priority];
    const isArchived = !!archive.find(t => t.id === task.id);
    const canClaim = !isArchived && user && !task.assignedTo;
    const canUpdate = !isArchived && user && (task.assignedTo === user.name || user.role === "admin");
    const canComplete = !isArchived && (canUpdate || (user && user.role === "admin"));
    const canDelete = user && user.role === "admin" && !isArchived;
    return (
      <div style={{ background: C.surface, minHeight: 600 }}>
        <Confetti active={showConfetti} />
        <ConfirmModal modal={confirmModal} onConfirm={() => { if (confirmModal && confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal(null); }} onCancel={() => setConfirmModal(null)} />
        <div style={{ background: C.greenMid, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setSelectedTaskId(null)} style={{ background: "rgba(255,255,255,0.18)", border: "none", borderRadius: R.control, padding: "6px 12px", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
          <div style={{ fontSize: 14, color: "rgba(0,0,0,0.7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase" }}>{task.title}</div>
        </div>
        <div style={{ padding: "1rem 1.25rem", maxWidth: 680, margin: "0 auto" }}>
          <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: "1rem" }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              <Badge bg={st.bg} color={st.color}>{st.label}</Badge>
              <Badge bg={pr.bg} color={pr.text}>{pr.label} Priority</Badge>
              {isArchived && <Badge bg={C.grayLt} color={C.muted}>Archived</Badge>}
            </div>
            <h2 style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, color: C.text, margin: "0 0 8px", lineHeight: 1.2, textTransform: "uppercase" }}>{task.title}</h2>
            {task.description && <p style={{ color: C.muted, fontSize: 14, margin: "0 0 12px", lineHeight: 1.6 }}>{task.description}</p>}
            {/* Metadata row: icon size == font size (13 on 13px text). That is the
                house convention -- TaskCard has drawn 11px icons against 11px text
                since before this pass -- and the metrics agree: a lucide glyph has
                ~22/24 of its box in ink, so 13 gives 11.9px against Roboto 13's
                12.0px ascender, effectively an exact match. At 14 the ink is 12.8px,
                overshooting the ascender, and the icons sit visibly proud of the
                capitals. Each item is its own inline-flex so its icon centres on its
                own line when the row wraps, rather than on the tallest item in the
                row. The warning glyph on the overdue branch is deliberately left as
                emoji -- it belongs to the warning/check cluster held for a later pass. */}
            <div style={{ display: "flex", gap: 14, fontSize: 13, color: C.muted, flexWrap: "wrap" }}>
              {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : (task.location ? [task.location] : [])).length > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MapPinIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />{(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : [task.location]).join(', ')}</span>}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><ClockIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />{timeAgo(task.createdAt)}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><UserIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />{task.assignedTo || "Unassigned"}</span>
              {task.dueDate && (
                <span style={{ color: new Date(task.dueDate) < new Date() ? C.red : C.amber, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <CalendarIcon size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
                  Due {fmtDate(task.dueDate + 'T12:00:00')}{new Date(task.dueDate) < new Date() ? " ⚠️ Overdue" : ""}
                </span>
              )}
              {isArchived && task.completedAt && <span>✅ {fmtDate(task.completedAt)}</span>}
              {isArchived && task.duration && <span>⏱ {fmtDuration(task.duration)}</span>}
            </div>
          </div>
          <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 14 }}>ACTIVITY LOG</div>
            {(!task.updates || task.updates.length === 0) ? <p style={{ color: C.muted, fontSize: 13, fontStyle: "italic", margin: 0 }}>No updates yet.</p> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {task.updates.map((u, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <Avatar name={u.author} size={34} bg={u.system ? C.grayLt : C.greenLt} fg={u.system ? C.muted : C.green} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 3 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{u.author}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>{timeAgo(u.timestamp)}</span>
                      </div>
                      <p style={{ fontSize: 13, color: u.type === "instruction_request" ? C.blue : (u.system ? C.muted : C.text), margin: 0, fontStyle: u.system ? "italic" : "normal", lineHeight: 1.5, fontWeight: u.type === "instruction_request" ? 600 : "normal" }}>{u.message}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                        {!u.system && <button onClick={() => { const liked = (u.likes||[]).includes(user.name); const newUpdates = (task.updates||[]).map(upd => upd.timestamp === u.timestamp ? Object.assign({}, upd, { likes: liked ? (upd.likes||[]).filter(n => n !== user.name) : [...(upd.likes||[]), user.name] }) : upd); updateDoc(doc(db, 'leagues', lid, 'tasks', task.id), { updates: newUpdates }); }} style={{ background: (u.likes||[]).includes(user && user.name) ? C.blueLt : "none", border: "1px solid " + ((u.likes||[]).includes(user && user.name) ? C.blueBd : "transparent"), borderRadius: R.control, padding: "2px 8px", cursor: "pointer", fontSize: 12, color: (u.likes||[]).includes(user && user.name) ? C.blue : C.muted }}>👍 {(u.likes||[]).length > 0 ? (u.likes||[]).length : "Like"}</button>}
                        {(u.likes||[]).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>{(u.likes||[]).join(", ")}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {(canClaim || canUpdate || canDelete) && (
            <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", display: "flex", flexDirection: "column", gap: 10 }}>
              {canClaim && <Btn onClick={() => claimTask(task.id)} full>🙋 Claim This Task</Btn>}
              {!isArchived && user && task.assignedTo === user.name && task.status !== "complete" && (
                <Btn onClick={() => {
                  showConfirm("Unclaim Task", "This task will go back to the open task list.", () => unclaimTask(task.id));
                }} variant="white">↩ Unclaim Task</Btn>
              )}
              {!isArchived && user && task.createdBy !== user.name && (
                <Btn onClick={() => { requestInstructions(task.id, task.title); window.alert("Your request has been sent! Check back for updates in the activity log."); }} variant="blue"><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><HelpCircleIcon size={17} strokeWidth={2} />Get More Instructions</span></Btn>
              )}
              {!isArchived && user && user.role === "admin" && (
                <div style={{ position: "relative" }}>
                  <Btn onClick={() => setShowAssignMenu(v => !v)} variant="secondary" full>
                    {/* The trailing → is a typographic arrow, not an emoji, and is
                        left as-is: it signals that this button opens a menu. */}
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <UserPlusIcon size={17} strokeWidth={2} />
                      Assign to Someone →
                    </span>
                  </Btn>
                  {showAssignMenu && (() => {
                    const allMembers = [
                      ...(members.map(m => ({ name: m.name, role: "Volunteer" }))),
                      ...((league && league.coAdmins || []).map(ca => ({ name: ca.name, role: "Co-Admin" }))),
                      { name: league && league.adminName, role: "Admin" }
                    ].filter(m => m.name && m.name !== task.assignedTo);
                    return (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.white, border: "1px solid " + C.border, borderRadius: R.card, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 999, maxHeight: 240, overflowY: "auto", marginTop: 4 }}>
                        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", borderBottom: "1px solid " + C.border }}>SELECT A PERSON</div>
                        {allMembers.length === 0 ? (
                          <div style={{ padding: "12px", fontSize: 13, color: C.muted, fontStyle: "italic" }}>No other members found</div>
                        ) : allMembers.map((m, i) => (
                          <div key={i} onClick={() => { assignTask(task.id, m.name); setShowAssignMenu(false); }}
                            style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderBottom: "1px solid " + C.border }}
                            onMouseEnter={e => e.currentTarget.style.background = C.greenLt}
                            onMouseLeave={e => e.currentTarget.style.background = C.white}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.greenLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.green, flexShrink: 0 }}>
                              {m.name.slice(0, 2).toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{m.name}</div>
                              <div style={{ fontSize: 11, color: C.muted }}>{m.role}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
              {canUpdate && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>POST UPDATE</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="text" placeholder="Add a status update..." value={updateText} onChange={e => setUpdateText(e.target.value)} onKeyDown={e => e.key === "Enter" && sendUpdate(task.id)}
                      style={{ flex: 1, padding: "9px 12px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 14, outline: "none", fontFamily: "inherit" }} />
                    <Btn onClick={() => sendUpdate(task.id)} variant="blue" disabled={!updateText.trim()}>Send</Btn>
                  </div>
                </div>
              )}
              {(canComplete || canDelete) && (
                <div style={{ display: "flex", gap: 8 }}>
                  {canComplete && <Btn onClick={() => completeTask(task.id)} variant="secondary" full>✓ Mark as Complete</Btn>}
                  {canDelete && <Btn onClick={() => deleteTask(task.id)} variant="danger">Delete</Btn>}
                  {canDelete && <Btn onClick={() => setEditingTask({ title: task.title, description: task.description || "", locations: Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : (task.location ? [task.location] : []), location: task.location || "", groups: task.groups || [], priority: task.priority, dueDate: task.dueDate || "" })} variant="white"><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><PencilIcon size={17} strokeWidth={2} />Edit</span></Btn>}
                </div>
              )}
              {editingTask && (
                <div style={{ background: C.grayLt, borderRadius: R.card, padding: "1rem", border: "1px solid " + C.greenBd }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 10 }}>EDIT TASK</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <TitleComboBox value={editingTask.title} onChange={v => setEditingTask(Object.assign({}, editingTask, { title: v }))} titles={taskTitles} onAddTitle={saveTaskTitle} placeholder="Task title *" />
                    <textarea placeholder="Description (optional)" value={editingTask.description} onChange={e => setEditingTask(Object.assign({}, editingTask, { description: e.target.value }))} rows={2}
                      style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                    <LocationMultiSelect value={editingTask.locations || []} onChange={locs => setEditingTask(Object.assign({}, editingTask, { locations: locs, location: locs.length > 0 ? locs[0] : "" }))} locations={leagueLocations} />
                    {leagueGroups.length > 0 && (
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>GROUPS (optional)</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {leagueGroups.map(g => {
                            const on = (editingTask.groups || []).includes(g);
                            return (
                              <button key={g} type="button" onClick={() => {
                                const cur = editingTask.groups || [];
                                setEditingTask(Object.assign({}, editingTask, { groups: on ? cur.filter(x => x !== g) : [...cur, g] }));
                              }} style={{ padding: "5px 12px", borderRadius: R.control, border: "1px solid " + (on ? "#7C3AED" : C.border), background: on ? "#EDE9FE" : C.white, color: on ? "#7C3AED" : C.muted, fontSize: 12, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                                {g}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <select value={editingTask.priority} onChange={e => setEditingTask(Object.assign({}, editingTask, { priority: e.target.value }))}
                      style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit" }}>
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                    </select>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>DUE DATE (optional)</label>
                      <input type="date" value={editingTask.dueDate} onChange={e => setEditingTask(Object.assign({}, editingTask, { dueDate: e.target.value }))}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn onClick={() => updateTask(task.id, editingTask)} disabled={!editingTask.title.trim()} full>Save Changes</Btn>
                      <Btn onClick={() => setEditingTask(null)} variant="ghost">Cancel</Btn>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── DASHBOARD ────────────────────────────────────────────────────
  return (
    <div style={{ background: C.pageBg, minHeight: "100vh", position: "relative", display: "flex", flexDirection: "column" }} onClick={() => { if (showNotifications) setShowNotifications(false); if (showLeagueSwitcher) setShowLeagueSwitcher(false); if (showOtherMenu) setShowOtherMenu(false); if (showSeasonMenu) setShowSeasonMenu(false); }}>
      <Confetti active={showConfetti} />
      <ConfirmModal modal={confirmModal} onConfirm={() => { if (confirmModal && confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal(null); }} onCancel={() => setConfirmModal(null)} />
      <CoachPromptModal show={showCoachPrompt} leagueName={league && league.leagueName} isCoach={coachPromptIsCoach} coachRole={coachPromptCoachRole}
        saving={coachPromptSaving} onChangeIsCoach={handleCoachPromptChangeIsCoach} onChangeCoachRole={setCoachPromptCoachRole}
        onDone={() => saveCoachPromptAnswer(true, coachPromptCoachRole)} onClose={closeCoachPrompt} />
      {slotsNotification && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: C.greenMid, color: C.text, borderRadius: R.control, padding: '14px 20px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', gap: 12, maxWidth: 380, width: '90%' }}>
          <span style={{ fontSize: 22 }}>✅</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>Volunteer slots saved!</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{slotsNotification.count} slot{slotsNotification.count !== 1 ? 's' : ''} added to "{slotsNotification.title}"</div>
          </div>
          <button onClick={() => setSlotsNotification(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: R.control, width: 28, height: 28, cursor: 'pointer', color: C.white, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>✕</button>
        </div>
      )}
      {boardNotification && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: C.green, color: C.white, borderRadius: R.control, padding: '14px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', maxWidth: 360, width: 'calc(100% - 48px)', cursor: 'pointer' }}
          onClick={() => { markBoardSeen(boardNotification.createdAt); setTab('board'); setBoardNotification(null); }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📌</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 2 }}>{boardNotification.title || 'New Board Post'}</div>
              <div style={{ fontSize: 12, opacity: 0.9, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{boardNotification.body}</div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>by {boardNotification.author} · {new Date(boardNotification.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})} at {new Date(boardNotification.createdAt).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}</div>
            </div>
            <div onClick={e => { e.stopPropagation(); markBoardSeen(boardNotification.createdAt); setBoardNotification(null); }} style={{ fontSize: 18, opacity: 0.7, lineHeight: 1, marginTop: -2, padding: '0 4px' }}>✕</div>
          </div>
        </div>
      )}
      {/* ── Notify Channel Modal ─────────────────────────────── */}
      {notifyModalPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: R.card, padding: '24px 20px', maxWidth: 360, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 22, marginBottom: 8, textAlign: 'center' }}>📣</div>
            {notifyModalPending.type === 'event' ? (
              <>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', textAlign: 'center', marginBottom: 6 }}>Notify Event Requester?</div>
                <div style={{ fontSize: 13, color: '#8E8E93', textAlign: 'center', marginBottom: 4 }}>
                  Send <strong>{notifyModalPending.evData && notifyModalPending.evData.creatorEmail}</strong> an email letting them know about their event request?
                </div>
                <div style={{ fontSize: 11, color: '#AEAEB2', textAlign: 'center', marginBottom: 20, fontStyle: 'italic' }}>Your choice will apply to all event decisions for the next 2 minutes -- multiple dates will be combined into one email.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={() => handleNotifyChoice('yes')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #1A7A35', background: '#E8F9ED', fontWeight: 700, fontSize: 14, color: '#1A7A35', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>📧</span> Yes, send email</button>
                  <button onClick={() => handleNotifyChoice('no')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>🔕</span> No, don't notify</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', textAlign: 'center', marginBottom: 6 }}>Notify Your Team?</div>
                <div style={{ fontSize: 13, color: '#8E8E93', textAlign: 'center', marginBottom: 4 }}>How would you like to notify your volunteers?</div>
                <div style={{ fontSize: 11, color: '#AEAEB2', textAlign: 'center', marginBottom: 8, fontStyle: 'italic' }}>Your choice will apply to all field changes for the next 2 minutes.</div>
                <div style={{ fontSize: 11, color: '#1565C0', textAlign: 'center', marginBottom: 20, lineHeight: 1.4 }}>📱 Volunteers who've opted into text alerts will also get a text automatically, regardless of your choice below.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {league && league.groupmeBotId && (<button onClick={() => handleNotifyChoice('groupme')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #E5E5EA', background: '#F2F2F7', fontWeight: 700, fontSize: 14, color: '#1C1C1E', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>💬</span> GroupMe</button>)}
                  <button onClick={() => handleNotifyChoice('email')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #E5E5EA', background: '#F2F2F7', fontWeight: 700, fontSize: 14, color: '#1C1C1E', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>📧</span> Email</button>
                  {league && league.groupmeBotId && (<button onClick={() => handleNotifyChoice('both')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #1A7A35', background: '#E8F9ED', fontWeight: 700, fontSize: 14, color: '#1A7A35', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>✅</span> Both GroupMe & Email</button>)}
                  <button onClick={() => handleNotifyChoice('skip')} style={{ padding: '12px 16px', borderRadius: R.control, border: '1.5px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: 18 }}>🔕</span> {league && league.groupmeBotId ? 'Skip Email / GroupMe' : 'Skip Email'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* ── Approval Conflict Modal (approving a pending event request that overlaps an already-approved event or Scheduler game) ── */}
      {pendingApproveConflict && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: R.card, padding: '24px 20px', maxWidth: 460, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', marginBottom: 4 }}>⚠️ Scheduling Conflict</div>
            <div style={{ fontSize: 13, color: '#8E8E93', marginBottom: 12 }}>
              This request overlaps with the existing item(s) listed below at the same location, date, and time — only one can be approved for that slot, regardless of event type. To approve this request, every conflicting item below must be checked (cancelled). If you'd rather keep an existing item, reject this request instead.
            </div>
            <div style={{ padding: '10px 12px', borderRadius: R.control, border: '1.5px solid #75C83C', background: '#F1FAEA', marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#2F5A16', letterSpacing: '0.05em', marginBottom: 2 }}>REQUEST BEING APPROVED</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E' }}>{pendingApproveConflict.title}</div>
              <div style={{ fontSize: 11, color: '#444441' }}>{pendingApproveConflict.date} · {pendingApproveConflict.startTime}–{pendingApproveConflict.endTime} · {pendingApproveConflict.field}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.05em', marginBottom: 6 }}>CONFLICTS WITH (EXISTING)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {pendingApproveConflictItems.map(c => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: R.control, border: '1px solid ' + (pendingApproveOverrideIds.includes(c.id) ? '#E24B4A' : '#E5E5EA'), background: pendingApproveOverrideIds.includes(c.id) ? '#FCEBEB' : '#fff', cursor: 'pointer' }}>
                  <input type="checkbox" checked={pendingApproveOverrideIds.includes(c.id)} onChange={e => setPendingApproveOverrideIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} style={{ accentColor: '#E24B4A', marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E' }}>
                      {c._source === 'game' ? (c.title || 'Scheduled game') : c.title}
                      <span style={{ fontWeight: 400, color: '#8E8E93', marginLeft: 6, fontSize: 11 }}>{c._source === 'game' ? 'Scheduler game' : 'Event'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#8E8E93' }}>{c.date} · {c.startTime}–{c.endTime} · {c._source === 'game' ? c.location : c.field}</div>
                    {c._source === 'game' && !c.calendarEventId && (<div style={{ fontSize: 11, color: '#8E8E93', marginTop: 2, fontStyle: 'italic' }}>Not published to the Events calendar yet — still holds this time slot in Scheduler.</div>)}
                    {c._source === 'event' && c.slots && c.slots.some(s => (s.signups || []).length > 0) && (<div style={{ fontSize: 11, color: '#501313', marginTop: 2 }}>⚠️ Has volunteer signups</div>)}
                    {pendingApproveOverrideIds.includes(c.id) ? <div style={{ fontSize: 11, color: '#501313', marginTop: 2, fontWeight: 700 }}>Will be cancelled and deleted — the request wins this slot</div> : <div style={{ fontSize: 11, color: '#501313', marginTop: 2, fontWeight: 700 }}>Will be kept — request can't be approved until this is checked</div>}
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setPendingApproveConflict(null); setPendingApproveConflictItems([]); setPendingApproveOverrideIds([]); }} disabled={approvingConflict} style={{ flex: 1, padding: '11px', borderRadius: R.control, border: '1px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: approvingConflict ? 'default' : 'pointer', opacity: approvingConflict ? 0.6 : 1, fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={resolveApproveConflictAndApprove} disabled={approvingConflict || pendingApproveOverrideIds.length < pendingApproveConflictItems.length} style={{ flex: 2, padding: '11px', borderRadius: R.control, border: 'none', background: C.redFill, color: '#fff', fontWeight: 700, fontSize: 14, cursor: (approvingConflict || pendingApproveOverrideIds.length < pendingApproveConflictItems.length) ? 'default' : 'pointer', opacity: (approvingConflict || pendingApproveOverrideIds.length < pendingApproveConflictItems.length) ? 0.6 : 1, fontFamily: 'inherit' }}>{approvingConflict ? 'Approving…' : 'Cancel Conflicts & Approve'}</button>
            </div>
          </div>
        </div>
      )}
      {fieldClosureNotification && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: C.redFill, color: '#fff', borderRadius: R.control, padding: '14px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', maxWidth: 380, width: 'calc(100% - 48px)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 22 }}>🚫</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 3 }}>{fieldClosureNotification.field} is CLOSED Today</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>Reason: {fieldClosureNotification.reason}{fieldClosureNotification.message ? ' — ' + fieldClosureNotification.message : ''}</div>
              {/* 0.9, not 0.85. White at 0.85 on C.redFill measures 4.42:1 and still
                  misses AA's 4.5:1 for normal text; 0.9 measures 4.80:1 and clears it.
                  Now equal to the Reason line above, so the visual hierarchy here is
                  carried by font size (12 vs 11) rather than by opacity. */}
              <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4 }}>Closed by {fieldClosureNotification.closedBy}</div>
            </div>
            <div onClick={() => { localStorage.setItem('groundspal-closure-seen-' + fieldClosureNotification.id, '1'); setFieldClosureNotification(null); }} style={{ fontSize: 18, opacity: 0.8, cursor: 'pointer', padding: '0 4px' }}>✕</div>
          </div>
        </div>
      )}
      {emulating && (
        <div style={{ background: "#7C3AED", color: "#fff", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700 }}>
            👁️ Viewing & editing as <strong>{league && leagueLabel(league.leagueName)}</strong> — Super Admin mode
          </div>
          <button onClick={handleSignOut} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: R.control, padding: "4px 12px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Exit to Console
          </button>
        </div>
      )}
      {(() => {
        if (promoExpiryBannerDismissed) return null;
        if (!user || user.role !== 'admin') return null;
        if (!league || !league.planSource || !league.planSource.startsWith('promo:') || !league.planExpiresAt) return null;
        const daysLeft = Math.ceil((new Date(league.planExpiresAt) - new Date()) / 86400000);
        if (daysLeft > 30 || daysLeft < 0) return null;
        const todayKey = 'groundspal-promo-expiry-dismissed-' + league.id + '-' + new Date().toISOString().slice(0,10);
        if (localStorage.getItem(todayKey)) return null;
        return (
          <div style={{ background: daysLeft <= 7 ? C.redFill : "#E65100", color: "#fff", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              ⏳ Your league's complimentary League access expires in {daysLeft} day{daysLeft === 1 ? '' : 's'} ({new Date(league.planExpiresAt).toLocaleDateString()}). Add a plan now to avoid losing League features.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setScreen('show-code')} style={{ background: "#fff", color: daysLeft <= 7 ? "#E24B4A" : "#E65100", border: "none", borderRadius: R.control, padding: "5px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Add Plan
              </button>
              <button onClick={() => { localStorage.setItem(todayKey, '1'); setPromoExpiryBannerDismissed(true); }} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: R.control, padding: "5px 12px", color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Remind me tomorrow
              </button>
            </div>
          </div>
        );
      })()}
      <div style={{ background: C.headerBg, borderBottom: "3px solid " + C.headerBorder, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: R.control, overflow: "hidden", flexShrink: 0, background: "#0D0D0D", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={LOGO_URL} alt="GroundsPal" style={{ width: 34, height: 34, objectFit: "contain", borderRadius: R.control }} />
          </div>
          <div>
            <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 800, color: C.headerText, letterSpacing: "-0.3px" }}>{league && league.leagueName ? league.leagueName : "GroundsPal"}</div>
            <div style={{ fontSize: 11, color: "#1A7A35", fontWeight: 600 }}>
              {user && user.role === "admin" ? (user.isMainAdmin ? "Owner" : "Co-Admin") : "Volunteer"} · {user && user.name}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", position: "relative" }}>
          <BellIcon count={unreadCount} onClick={() => setShowNotifications(v => !v)} />
          {myLeagues.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button onClick={e => { e.stopPropagation(); setShowLeagueSwitcher(v => !v); }}
                style={{ background: C.btnBg, border: "1px solid " + C.btnBorder, borderRadius: R.control, padding: "5px 11px", color: C.btnText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", height: 32, display: "flex", alignItems: "center", gap: 4 }}>
                🔀 Switch League
              </button>
              {showLeagueSwitcher && (
                <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 38, right: 0, background: C.white, border: '1px solid ' + C.border, borderRadius: R.card, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 220, zIndex: 50, overflow: 'hidden' }}>
                  {myLeagues.map(choice => (
                    <button key={choice.leagueId} onClick={() => enterLeague(choice)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: choice.leagueId === lid ? C.greenLt : C.white, border: 'none', borderBottom: '1px solid ' + C.border, cursor: 'pointer', fontFamily: 'inherit' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{choice.leagueName || 'League'}{choice.leagueId === lid ? ' ✓' : ''}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{choice.role === 'volunteer' ? 'Volunteer' : choice.isMainAdmin ? 'Owner · Admin' : 'Co-Admin'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {user && user.role === "admin" && (
            <button onClick={() => setScreen('show-code')}
              style={{ background: C.btnBg, border: "1px solid " + C.btnBorder, borderRadius: R.control, padding: "5px 11px", color: C.btnText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", height: 32, display: "flex", alignItems: "center" }}>
              ⚙️ Settings
            </button>
          )}
          {user && user.role === "volunteer" && (
            <button onClick={() => setScreen('my-account')}
              style={{ background: C.btnBg, border: "1px solid " + C.btnBorder, borderRadius: R.control, padding: "5px 11px", color: C.btnText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", height: 32, display: "flex", alignItems: "center" }}>
              👤 My Account
            </button>
          )}
          <button onClick={handleSignOut} style={{ background: C.btnBg, border: "1px solid " + C.btnBorder, borderRadius: R.control, padding: "5px 11px", color: C.btnText, fontSize: 12, cursor: "pointer", fontFamily: "inherit", height: 32, display: "flex", alignItems: "center" }}>Sign Out</button>
        </div>

        {showNotifications && (
          <div onClick={e => e.stopPropagation()}>
            <NotificationsPanel
              notifications={notifications}
              userName={user && user.name}
              onDismiss={dismissNotification}
              onDismissAll={dismissAllNotifications}
              onClose={() => setShowNotifications(false)}
              isMobile={isMobile}
              fieldEvents={fieldEvents}
              onApproveGroupDate={approveEventGroupDate}
              onRejectGroupDate={rejectEventGroupDate}
              onApproveGroupAll={approveEventGroupAll}
              onRejectGroupAll={rejectEventGroupAll}
            />
          </div>
        )}
      </div>

      <div style={{ display: "flex", flex: 1 }}>
        {!isMobile && (
          <div style={{ width: sidebarCollapsed ? 64 : 210, flexShrink: 0, background: "#111111" }}>
          <div style={{ display: "flex", flexDirection: "column", padding: "14px 0", position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", padding: sidebarCollapsed ? "0 0 16px" : "0 8px 16px", justifyContent: sidebarCollapsed ? "center" : "flex-start" }}>
              {!sidebarCollapsed && <span style={{ fontFamily: T.display, fontWeight: 700, fontSize: 18, lineHeight: 1.1, textTransform: "uppercase", letterSpacing: ".02em", color: "#fff", whiteSpace: "nowrap" }}>GroundsPal</span>}
            </div>
            {[
              { k: "tasks", l: "Tasks", Icon: CheckSquareIcon },
              { k: "calendar", l: "Events", Icon: CalendarClockIcon },
              { k: "board", l: "Board", Icon: MegaphoneIcon },
              { k: "projects", l: "Projects", Icon: FolderOpenIcon },
              { k: "coaches", l: "Coaches", Icon: ClipboardListIcon },
              ...(user && user.role === "admin" ? [{ k: "teams", l: "Teams", Icon: UsersIcon }] : []),
              ...(user && user.role === "admin" ? [{ k: "scheduler", l: "Scheduler", Icon: CalendarRangeIcon }] : []),
              ...(user && user.role === "admin" ? [{ k: "registration", l: "Registration", Icon: CreditCardIcon }] : []),
              ...(user && user.role === "admin" ? [{ k: "tryouts", l: "Tryouts", Icon: TrophyIcon }] : [])
            ].map(t => {
              const isActive = tab === t.k;
              const showBoardDot = t.k === "board" && boardPosts.some(p => !readBoardPosts.has(p.id));
              const eventsPendingCount = t.k === "calendar" && user && (user.role === "admin" || user.role === "coadmin") ? fieldEvents.filter(e => e.status === "pending").length : 0;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} title={sidebarCollapsed ? t.l : undefined}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: sidebarCollapsed ? "10px" : "9px 14px", margin: sidebarCollapsed ? "1px 6px" : "1px 8px", borderRadius: R.control, border: "none", background: isActive ? "#75C83C" : "transparent", color: isActive ? "#111111" : "#fff", fontWeight: isActive ? 800 : 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", justifyContent: sidebarCollapsed ? "center" : "flex-start", position: "relative", whiteSpace: "nowrap" }}>
                  <t.Icon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                  {!sidebarCollapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t.l}</span>}
                  {!sidebarCollapsed && showBoardDot && (
                    <span style={{ marginLeft: "auto", width: 8, height: 8, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
                  )}
                  {!sidebarCollapsed && eventsPendingCount > 0 && (
                    <span style={{ marginLeft: "auto", width: 8, height: 8, borderRadius: "50%", border: "1.5px solid #fff", background: "transparent", boxSizing: "border-box", flexShrink: 0 }} />
                  )}
                  {sidebarCollapsed && showBoardDot && (
                    <span style={{ position: "absolute", top: 4, right: 4, width: 7, height: 7, borderRadius: "50%", background: C.red }} />
                  )}
                  {sidebarCollapsed && eventsPendingCount > 0 && (
                    <span style={{ position: "absolute", top: 4, right: 4, width: 7, height: 7, borderRadius: "50%", border: "1.5px solid #fff", background: "transparent", boxSizing: "border-box" }} />
                  )}
                </button>
              );
            })}
            <div style={{ height: 1, background: "#2A2A2A", margin: sidebarCollapsed ? "8px 10px" : "8px 12px" }} />
            {[
              { k: "supplies", l: "Supplies" + (stats.sup > 0 ? " · " + stats.sup : ""), Icon: PackageIcon },
              { k: "archive", l: "Archive", Icon: ArchiveIcon },
              { k: "crewstats", l: "Stats", Icon: AwardIcon },
              { k: "waivers", l: "Waivers", Icon: FileTextIcon }
            ].map(t => {
              const isActive = tab === t.k;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} title={sidebarCollapsed ? t.l : undefined}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: sidebarCollapsed ? "10px" : "9px 14px", margin: sidebarCollapsed ? "1px 6px" : "1px 8px", borderRadius: R.control, border: "none", background: isActive ? "#75C83C" : "transparent", color: isActive ? "#111111" : "#fff", fontWeight: isActive ? 800 : 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", justifyContent: sidebarCollapsed ? "center" : "flex-start", whiteSpace: "nowrap" }}>
                  <t.Icon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                  {!sidebarCollapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t.l}</span>}
                </button>
              );
            })}
          </div>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
      {tab === "tasks" && (
      <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: isMobile ? 5 : 8 }}>
        {[
          { label: "Open", value: stats.open, color: C.blue, bg: C.blueLt, icon: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>' },
          { label: "In Progress", value: stats.ip, color: C.amber, bg: C.amberLt, icon: '<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>' },
          { label: "Completed", value: stats.arch, color: C.green, bg: C.greenLt, icon: '<polyline points="20 6 9 17 4 12"/>' },
          { label: "Supplies", value: stats.sup, color: C.redDark, bg: C.redLt, icon: '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>' }
        ].map(s => (
          <div key={s.label} style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: isMobile ? "8px 6px" : "10px 6px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <div style={{ width: isMobile ? 24 : 26, height: isMobile ? 24 : 26, borderRadius: "50%", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: s.icon }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontSize: isMobile ? 16 : 17, fontWeight: 700, color: C.text, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, lineHeight: 1.2, marginTop: 1 }}>{isMobile && s.label === "In Progress" ? "In Prog" : s.label}</div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Weather Widget — full, Tasks tab only */}
      {tab === "tasks" && league && league.zipCode && (() => {
        if (weatherLoading) return (
          <div style={{ margin: "0 14px 10px", background: C.blueLt, borderRadius: R.control, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>⏳</span>
            <span style={{ fontSize: 13, color: C.blue }}>Loading weather...</span>
          </div>
        );
        if (!weatherData) return null;
        const now = new Date();
        const currentHourIdx = weatherData.hourly.time.findIndex(t => new Date(t) >= now);
        const idx = currentHourIdx >= 0 ? currentHourIdx : 0;
        const temp = Math.round(weatherData.hourly.temperature_2m[idx]);
        const feelsLike = Math.round(weatherData.hourly.apparent_temperature[idx]);
        const precip = weatherData.hourly.precipitation_probability[idx];
        const wind = Math.round(weatherData.hourly.windspeed_10m[idx]);
        const code = weatherData.hourly.weathercode[idx];
        const wx = weatherCodeInfo(code);
        const hi = Math.round(weatherData.daily.temperature_2m_max[0]);
        const lo = Math.round(weatherData.daily.temperature_2m_min[0]);
        const isBad = precip > 60 || temp < 32 || temp > 100;
        const isCaution = !isBad && (precip > 30 || wind > 25 || temp > 90);
        const suitBg = isBad ? "#FCEBEB" : isCaution ? "#FFF8E1" : C.greenLt;
        const suitBorder = isBad ? C.redBd : isCaution ? "#FFD54F" : C.greenBd;
        const suitColor = isBad ? C.redDark : isCaution ? C.amber : C.text;
        const suitText = isBad ? "⛔ Not ideal for field work" : isCaution ? "⚠️ Proceed with caution" : "✅ Good conditions for field work";
        return (
          <div style={{ margin: "0 14px 10px", background: "linear-gradient(135deg, #1565C0 0%, #1976D2 100%)", borderRadius: R.control, padding: isMobile ? "10px 40px 10px 14px" : "8px 40px 8px 14px", color: C.white, position: "relative" }}>
            <button onClick={() => fetchWeather(league.zipCode)}
              style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: R.control, padding: "4px 7px", color: C.white, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
              ↻
            </button>
            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 22 }}>{wx.icon}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{temp}°F</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.8)" }}>{wx.label} · Feels {feelsLike}°</div>
                  </div>
                </div>
                <div style={{ height: 1, background: "rgba(255,255,255,0.25)" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 10, color: "rgba(255,255,255,0.85)" }}>
                    <span>↑{hi}° ↓{lo}°</span>
                    <span>💧{precip}% · 💨{wind}mph</span>
                    <span style={{ opacity: 0.7 }}>{weatherData.city}</span>
                  </div>
                  <div style={{ background: suitBg, borderRadius: R.control, padding: "5px 10px", fontSize: 11, fontWeight: 700, color: suitColor, border: "1px solid " + suitBorder, textAlign: "center" }}>
                    {suitText}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-evenly", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 26 }}>{wx.icon}</span>
                  <div>
                    <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{temp}°F</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap" }}>{wx.label} · Feels {feelsLike}°</div>
                  </div>
                </div>
                <div style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.25)" }} />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, flexShrink: 0, fontSize: 10, color: "rgba(255,255,255,0.85)" }}>
                  <div style={{ whiteSpace: "nowrap" }}>↑{hi}° ↓{lo}°</div>
                  <div style={{ whiteSpace: "nowrap" }}>💧{precip}% · 💨{wind}mph</div>
                  <div style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{weatherData.city}</div>
                </div>
                <div style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.25)" }} />
                <div style={{ flexShrink: 0 }}>
                  <div style={{ background: suitBg, borderRadius: R.control, padding: "5px 10px", fontSize: 11, fontWeight: 700, color: suitColor, border: "1px solid " + suitBorder, textAlign: "center", whiteSpace: "nowrap" }}>
                    {suitText}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Weather chip — compact, Events tab only */}
      {tab === "calendar" && league && league.zipCode && (() => {
        if (weatherLoading) return (
          <div style={{ margin: "10px 14px 0", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.muted }}>
            <span>⏳</span><span>Loading weather...</span>
          </div>
        );
        if (!weatherData) return null;
        const now = new Date();
        const currentHourIdx = weatherData.hourly.time.findIndex(t => new Date(t) >= now);
        const idx = currentHourIdx >= 0 ? currentHourIdx : 0;
        const temp = Math.round(weatherData.hourly.temperature_2m[idx]);
        const precip = weatherData.hourly.precipitation_probability[idx];
        const code = weatherData.hourly.weathercode[idx];
        const wx = weatherCodeInfo(code);
        return (
          <div style={{ margin: "10px 14px 0", display: "inline-flex", alignItems: "center", gap: 8, background: C.blueLt, borderRadius: R.control, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: C.blue }}>
            <span style={{ fontSize: 16 }}>{wx.icon}</span>
            <span>{temp}°F · {wx.label}</span>
            <span style={{ color: C.muted, fontWeight: 600 }}>· 💧{precip}%</span>
          </div>
        );
      })()}

      {tab === "tasks" && (
        <div style={{ padding: isMobile ? "10px" : "14px" }}>
          {!isPayingLeague && <SponsorAdCarousel lid={lid} isPromoLeague={isPromoLeague} />}
          {/* Location card buttons */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>SELECT A LOCATION</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 8 }}>
              {LOCATIONS.map(loc => {
                const openCount = tasks.filter(t => t.status === "open" && (Array.isArray(t.locations) && t.locations.length > 0 ? t.locations.includes(loc) : t.location === loc)).length;
                const inProgressCount = tasks.filter(t => t.status === "in-progress" && (Array.isArray(t.locations) && t.locations.length > 0 ? t.locations.includes(loc) : t.location === loc)).length;
                const isSelected = locationFilter === loc;
                return (
                  <button key={loc} onClick={() => setLocationFilter(isSelected ? null : loc)}
                    style={{ background: isSelected ? C.greenMid : C.white, border: "2px solid " + (isSelected ? C.greenMid : C.border), borderRadius: R.control, padding: "7px 6px", cursor: "pointer", fontFamily: "inherit", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = C.greenBd; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = C.border; }}>
                    <div style={{ width: 26, height: 26, borderRadius: R.control, background: isSelected ? "rgba(255,255,255,0.2)" : C.greenLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isSelected ? C.text : C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isSelected ? C.text : C.text, lineHeight: 1.3 }}>{loc}</div>
                      <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 2, flexWrap: "wrap" }}>
                        {openCount > 0 && (<span style={{ fontSize: 10, fontWeight: 700, color: isSelected ? C.text : C.green }}>{openCount} open</span>)}
                        {inProgressCount > 0 && (<span style={{ fontSize: 10, fontWeight: 700, color: isSelected ? C.text : C.amber }}>{inProgressCount} active</span>)}
                        {openCount === 0 && inProgressCount === 0 && (<span style={{ fontSize: 10, color: isSelected ? "rgba(28,28,30,0.7)" : C.muted }}>No open tasks</span>)}
                      </div>
                    </div>
                  </button>
                );
              })}
              <button onClick={() => setLocationFilter(null)}
                style={{ background: !locationFilter ? C.greenMid : C.white, border: "2px solid " + (!locationFilter ? C.greenMid : C.border), borderRadius: R.control, padding: "7px 6px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onMouseEnter={e => { if (locationFilter) e.currentTarget.style.borderColor = C.greenBd; }}
                onMouseLeave={e => { if (locationFilter) e.currentTarget.style.borderColor = C.border; }}>
                <div style={{ width: 26, height: 26, borderRadius: R.control, background: !locationFilter ? "rgba(255,255,255,0.2)" : C.grayLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={!locationFilter ? C.text : C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: !locationFilter ? C.text : C.text }}>All Tasks</div>
                  <div style={{ marginTop: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: !locationFilter ? C.text : C.muted }}>{tasks.length} total</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
          {/* Sort controls */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em" }}>SORT:</span>
            {[
              { k: "default", l: "Newest" },
              { k: "priority", l: "Priority" },
              { k: "unclaimed", l: "Longest Unclaimed" }
            ].map(s => (
              <button key={s.k} onClick={() => setTaskSort(s.k)}
                style={{ padding: "4px 10px", borderRadius: R.control, border: "1px solid " + (taskSort === s.k ? "rgba(0,0,0,0.2)" : C.border), background: taskSort === s.k ? C.blue : C.white, color: taskSort === s.k ? C.white : C.muted, fontSize: 11, fontWeight: taskSort === s.k ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                {s.l}
              </button>
            ))}
          </div>

          {leagueGroups.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em" }}>GROUP:</span>
              <button onClick={() => setGroupFilter(null)}
                style={{ padding: "4px 10px", borderRadius: R.control, border: "1px solid " + (!groupFilter ? "#7C3AED" : C.border), background: !groupFilter ? "#EDE9FE" : C.white, color: !groupFilter ? "#7C3AED" : C.muted, fontSize: 11, fontWeight: !groupFilter ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                All
              </button>
              {leagueGroups.map(g => (
                <button key={g} onClick={() => setGroupFilter(groupFilter === g ? null : g)}
                  style={{ padding: "4px 10px", borderRadius: R.control, border: "1px solid " + (groupFilter === g ? "#7C3AED" : C.border), background: groupFilter === g ? "#EDE9FE" : C.white, color: groupFilter === g ? "#7C3AED" : C.muted, fontSize: 11, fontWeight: groupFilter === g ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                  {g}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 5, flex: 1, flexWrap: "wrap" }}>
              {[{ k: "all", l: "All (" + tasks.length + ")" }, { k: "open", l: "Open (" + stats.open + ")" }, { k: "in-progress", l: "In Progress (" + stats.ip + ")" }, { k: "mine", l: "Mine (" + stats.mine + ")" }].map(f => (
                <button key={f.k} onClick={() => setTaskFilter(f.k)}
                  style={{ padding: "5px 11px", borderRadius: R.control, border: "1px solid " + (taskFilter === f.k ? C.greenMid : C.border), background: taskFilter === f.k ? C.greenLt : C.white, color: taskFilter === f.k ? C.text : C.muted, fontSize: 12, fontWeight: taskFilter === f.k ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                  {f.l}
                </button>
              ))}
            </div>
            <Btn onClick={() => setShowNewTask(v => !v)} size="sm">+ New Task</Btn>
          </div>

          {showNewTask && (
            <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.greenBd, padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>New Task</div>
                <button onClick={() => setShowNewTask(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20 }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <TitleComboBox value={newTask.title} onChange={v => setNewTask(Object.assign({}, newTask, { title: v }))} titles={taskTitles} onAddTitle={saveTaskTitle} placeholder="Task title *" />
                <textarea placeholder="Description (optional)" value={newTask.description} onChange={e => setNewTask(Object.assign({}, newTask, { description: e.target.value }))} rows={2} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                <LocationMultiSelect value={newTask.locations || []} onChange={locs => setNewTask(Object.assign({}, newTask, { locations: locs, location: locs.length > 0 ? locs[0] : "" }))} locations={leagueLocations} />
                {leagueGroups.length > 0 && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>GROUPS (optional)</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {leagueGroups.map(g => {
                        const on = (newTask.groups || []).includes(g);
                        return (
                          <button key={g} type="button" onClick={() => {
                            const cur = newTask.groups || [];
                            setNewTask(Object.assign({}, newTask, { groups: on ? cur.filter(x => x !== g) : [...cur, g] }));
                          }} style={{ padding: "5px 12px", borderRadius: R.control, border: "1px solid " + (on ? "#7C3AED" : C.border), background: on ? "#EDE9FE" : C.white, color: on ? "#7C3AED" : C.muted, fontSize: 12, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                            {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <select value={newTask.priority} onChange={e => setNewTask(Object.assign({}, newTask, { priority: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit" }}>
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>DUE DATE (optional)</label>
                  <input type="date" value={newTask.dueDate} onChange={e => setNewTask(Object.assign({}, newTask, { dueDate: e.target.value }))}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                </div>
                <Btn onClick={createTask} disabled={!newTask.title.trim()} full>Create Task</Btn>
              </div>
            </div>
          )}

          {filtered.length === 0
            ? <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: C.muted }}><div style={{ fontSize: 32, marginBottom: 8 }}>✅</div><div style={{ fontSize: 14 }}>No tasks here.</div></div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{filtered.map(t => <TaskCard key={t.id} task={t} onClick={() => setSelectedTaskId(t.id)} />)}</div>
          }
        </div>
      )}

      {tab === "archive" && (
        <div style={{ padding: "14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 12 }}>COMPLETED TASKS — {archive.length} TOTAL</div>
          {archive.length === 0
            ? <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: C.muted, fontSize: 14 }}>No completed tasks yet.</div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {archive.map(task => (
                <div key={task.id} onClick={() => setSelectedTaskId(task.id)}
                  style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "1rem 1.25rem", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBd}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 5, marginBottom: 6, flexWrap: "wrap" }}>
                        <Badge bg={C.greenLt} color={C.green}>✓ Complete</Badge>
                        <Badge bg={PRIORITY[task.priority].bg} color={PRIORITY[task.priority].text}>{PRIORITY[task.priority].label}</Badge>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>{task.title}</div>
                      {/* Archive card metadata row. This text is 12px, not the task
                          detail's 13, so these are 12 -- same icon-size == font-size
                          rule, applied to a different text size. The duration glyph
                          was not in this pass's mapping and stays emoji for now. */}
                      <div style={{ fontSize: 12, color: C.muted, display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : (task.location ? [task.location] : [])).length > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MapPinIcon size={12} strokeWidth={2} style={{ flexShrink: 0 }} />{(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : [task.location]).join(', ')}</span>}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><UserIcon size={12} strokeWidth={2} style={{ flexShrink: 0 }} />{task.assignedTo || "Unknown"}</span>
                        {task.completedAt && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CalendarIcon size={12} strokeWidth={2} style={{ flexShrink: 0 }} />{fmtDate(task.completedAt)}</span>}
                        {task.duration && <span>⏱ {fmtDuration(task.duration)}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                      {task.duration && <div style={{ background: C.greenLt, borderRadius: R.control, padding: "6px 10px", textAlign: "center" }}><div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{fmtDuration(task.duration)}</div><div style={{ fontSize: 9, color: C.text, fontWeight: 700 }}>DURATION</div></div>}
                      {user && user.role === "admin" && (
                        <div style={{ display: "flex", gap: 5 }}>
                          <button onClick={e => { e.stopPropagation(); showConfirm("Restore Task", "This task will be moved back to the active task list.", () => restoreTask(task.id)); }}
                            style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.amber, cursor: "pointer", fontFamily: "inherit" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <ArchiveRestoreIcon size={13} strokeWidth={2} />
                              Restore
                            </span>
                          </button>
                          <button onClick={e => { e.stopPropagation(); showConfirm("Delete Task", "This archived task will be permanently deleted and cannot be recovered.", () => deleteArchivedTask(task.id), { danger: true, confirmLabel: "Delete" }); }}
                            title="Delete permanently" aria-label="Delete permanently"
                            style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center" }}>
                            {/* Icon-only, so it needs title/aria-label: the trash emoji it
                                replaces at least carried a screen-reader name of its own,
                                an inline SVG carries none. Sized 13 to match the 11px
                                Restore button beside it, same padding. */}
                            <Trash2Icon size={13} strokeWidth={2} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}

      {tab === "supplies" && (
        <div style={{ padding: "14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Supply Requests</div>
              <div style={{ fontSize: 12, color: C.muted }}>Items the league needs to purchase</div>
            </div>
            <Btn onClick={() => setShowNewSupply(v => !v)} size="sm">+ Add Item</Btn>
          </div>

          {showNewSupply && (
            <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.redBd, padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Request a Supply Item</div>
                <button onClick={() => setShowNewSupply(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20 }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <input placeholder="Item name *  (e.g. Chalk / Line Paint)" value={newSupply.item} onChange={e => setNewSupply(Object.assign({}, newSupply, { item: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                <input placeholder="Quantity needed  (e.g. 3 cans, 1 unit)" value={newSupply.quantity} onChange={e => setNewSupply(Object.assign({}, newSupply, { quantity: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                <textarea placeholder="Notes or reason (optional)" value={newSupply.notes} onChange={e => setNewSupply(Object.assign({}, newSupply, { notes: e.target.value }))} rows={2} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                <Btn onClick={addSupply} disabled={!newSupply.item.trim()} full>Submit Request</Btn>
              </div>
            </div>
          )}

          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 10 }}>PENDING ({supplies.filter(s => !s.purchased).length})</div>
            {supplies.filter(s => !s.purchased).length === 0
              ? <div style={{ background: C.greenLt, borderRadius: R.control, padding: "1rem", textAlign: "center", fontSize: 13, color: C.text, fontWeight: 600 }}>✓ No pending supply requests</div>
              : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {supplies.filter(s => !s.purchased).map(s => (
                  <div key={s.id} style={{ background: C.white, border: "1px solid " + C.redBd, borderLeft: "4px solid " + C.red, borderRadius: R.card, padding: "1rem 1.25rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 2 }}>{s.item}</div>
                        {s.quantity && <div style={{ fontSize: 13, color: C.muted, marginBottom: 3 }}>Qty: <strong>{s.quantity}</strong></div>}
                        {s.notes && <div style={{ fontSize: 13, color: C.text, marginBottom: 5 }}>{s.notes}</div>}
                        <div style={{ fontSize: 11, color: C.muted }}>Requested by <strong>{s.addedBy}</strong> · {timeAgo(s.addedAt)}</div>
                      </div>
                      {user && user.role === "admin" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                          <Btn onClick={() => markPurchased(s.id)} variant="secondary" size="sm">✓ Purchased</Btn>
                          <Btn onClick={() => removeSupply(s.id)} variant="danger" size="sm">Remove</Btn>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            }
          </div>

          {supplies.filter(s => s.purchased).length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 10 }}>PURCHASED</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {supplies.filter(s => s.purchased).map(s => (
                  <div key={s.id} style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.card, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ textDecoration: "line-through", color: C.muted, fontSize: 14, fontWeight: 600 }}>{s.item}</span>
                      {s.quantity && <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>{s.quantity}</span>}
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Requested by {s.addedBy}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
                      <Badge bg={C.greenLt} color={C.green}>✓ Purchased</Badge>
                      {s.purchasedAt && <span style={{ fontSize: 10, color: C.muted }}>{fmtDate(s.purchasedAt)}</span>}
                      {user && user.role === "admin" && (
                        <button onClick={() => { showConfirm("Unmark as Purchased", "Mark \"" + s.item + "\" as not yet purchased?", () => unmarkPurchased(s.id)); }}
                          style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "3px 8px", fontSize: 11, fontWeight: 700, color: C.amber, cursor: "pointer", fontFamily: "inherit" }}>
                          ↩ Undo
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "projects" && (() => {
        // If a project is selected, show project detail with its tasks
        if (selectedProjectId) {
          const project = projects.find(p => p.id === selectedProjectId);
          if (!project) { setSelectedProjectId(null); return null; }

          const filteredPT = projectTasks.filter(t => {
            if (projectTaskFilter === "open") return t.status === "open";
            if (projectTaskFilter === "in-progress") return t.status === "in-progress";
            if (projectTaskFilter === "mine") return t.assignedTo === (user && user.name);
            return true;
          });

          const selectedPT = selectedProjectTaskId
            ? (projectTasks.find(t => t.id === selectedProjectTaskId) || projectArchive.find(t => t.id === selectedProjectTaskId))
            : null;

          // Project task detail view
          if (selectedPT) {
            const task = selectedPT;
            const st = STATUS[task.status]; const pr = PRIORITY[task.priority];
            const isArch = !!projectArchive.find(t => t.id === task.id);
            const canClaimPT = !isArch && user && !task.assignedTo;
            const canUpdatePT = !isArch && user && (task.assignedTo === user.name || user.role === "admin");
            const canCompletePT = !isArch && canUpdatePT;
            const canDeletePT = user && user.role === "admin" && !isArch;
            return (
              <div>
                <Confetti active={showConfetti} />
                <div style={{ padding: "14px" }}>
                  <button onClick={() => { setSelectedProjectTaskId(null); setEditingProjectTask(null); }}
                    style={{ background: "none", border: "none", color: C.blue, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 12 }}>
                    ← Back to {project.name}
                  </button>
                  <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: "1rem" }}>
                    <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                      <Badge bg={st.bg} color={st.color}>{st.label}</Badge>
                      <Badge bg={pr.bg} color={pr.text}>{pr.label} Priority</Badge>
                      {isArch && <Badge bg={C.grayLt} color={C.muted}>Archived</Badge>}
                    </div>
                    <h2 style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, margin: "0 0 8px" }}>{task.title}</h2>
                    {task.description && <p style={{ color: C.muted, fontSize: 14, margin: "0 0 12px", lineHeight: 1.6 }}>{task.description}</p>}
                    <div style={{ display: "flex", gap: 14, fontSize: 13, color: C.muted, flexWrap: "wrap" }}>
                      {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : (task.location ? [task.location] : [])).length > 0 && <span>📍 {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations : [task.location]).join(', ')}</span>}
                      <span>🕐 {timeAgo(task.createdAt)}</span>
                      <span>👤 {task.assignedTo || "Unassigned"}</span>
                      {task.dueDate && <span style={{ color: new Date(task.dueDate) < new Date() ? C.red : C.amber, fontWeight: 700 }}>📅 Due {fmtDate(task.dueDate + "T12:00:00")}{new Date(task.dueDate) < new Date() ? " ⚠️" : ""}</span>}
                      {isArch && task.completedAt && <span>✅ {fmtDate(task.completedAt)}</span>}
                      {isArch && task.duration && <span>⏱ {fmtDuration(task.duration)}</span>}
                    </div>
                  </div>
                  <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: "1rem" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 14 }}>ACTIVITY LOG</div>
                    {(!task.updates || task.updates.length === 0) ? <p style={{ color: C.muted, fontSize: 13, fontStyle: "italic", margin: 0 }}>No updates yet.</p> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {task.updates.map((u, i) => (
                          <div key={i} style={{ display: "flex", gap: 10 }}>
                            <Avatar name={u.author} size={34} bg={u.system ? C.grayLt : C.greenLt} fg={u.system ? C.muted : C.green} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 3 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{u.author}</span>
                                <span style={{ fontSize: 11, color: C.muted }}>{timeAgo(u.timestamp)}</span>
                              </div>
                              <p style={{ fontSize: 13, color: u.type === "instruction_request" ? C.blue : (u.system ? C.muted : C.text), margin: 0, fontStyle: u.system ? "italic" : "normal", lineHeight: 1.5, fontWeight: u.type === "instruction_request" ? 600 : "normal" }}>{u.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(canClaimPT || canUpdatePT || canDeletePT) && (
                    <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", display: "flex", flexDirection: "column", gap: 10 }}>
                      {canClaimPT && <Btn onClick={() => claimProjectTask(task.id)} full>🙋 Claim This Task</Btn>}
                      {!isArch && user && task.assignedTo === user.name && (
                        <Btn onClick={() => { showConfirm("Unclaim Task", "This task will go back to the open task list.", () => unclaimProjectTask(task.id)); }} variant="white">↩ Unclaim Task</Btn>
                      )}
                      {!isArch && user && user.role === "admin" && (
                        <div style={{ position: "relative" }}>
                          <Btn onClick={() => setShowAssignMenuPT(v => !v)} variant="secondary" full>
                            👤 Assign to Someone →
                          </Btn>
                          {showAssignMenuPT && (() => {
                            const allMembers = [
                              ...(members.map(m => ({ name: m.name, role: "Volunteer" }))),
                              ...((league && league.coAdmins || []).map(ca => ({ name: ca.name, role: "Co-Admin" }))),
                              { name: league && league.adminName, role: "Admin" }
                            ].filter(m => m.name && m.name !== task.assignedTo);
                            return (
                              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.white, border: "1px solid " + C.border, borderRadius: R.card, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 999, maxHeight: 240, overflowY: "auto", marginTop: 4 }}>
                                <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", borderBottom: "1px solid " + C.border }}>SELECT A PERSON</div>
                                {allMembers.length === 0 ? (
                                  <div style={{ padding: "12px", fontSize: 13, color: C.muted, fontStyle: "italic" }}>No other members found</div>
                                ) : allMembers.map((m, i) => (
                                  <div key={i} onClick={() => { assignProjectTask(task.id, m.name); setShowAssignMenuPT(false); }}
                                    style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderBottom: "1px solid " + C.border }}
                                    onMouseEnter={e => e.currentTarget.style.background = C.greenLt}
                                    onMouseLeave={e => e.currentTarget.style.background = C.white}>
                                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.greenLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.green, flexShrink: 0 }}>
                                      {m.name.slice(0, 2).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{m.name}</div>
                                      <div style={{ fontSize: 11, color: C.muted }}>{m.role}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {canUpdatePT && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>POST UPDATE</div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <input type="text" placeholder="Add a status update..." value={projectUpdateText} onChange={e => setProjectUpdateText(e.target.value)} onKeyDown={e => e.key === "Enter" && sendProjectTaskUpdate(task.id)}
                              style={{ flex: 1, padding: "9px 12px", borderRadius: R.control, border: "1.5px solid " + C.border, fontSize: 14, outline: "none", fontFamily: "inherit" }} />
                            <Btn onClick={() => sendProjectTaskUpdate(task.id)} variant="blue" disabled={!projectUpdateText.trim()}>Send</Btn>
                          </div>
                        </div>
                      )}
                      {(canCompletePT || canDeletePT) && (
                        <div style={{ display: "flex", gap: 8 }}>
                          {canCompletePT && <Btn onClick={() => completeProjectTask(task.id)} variant="secondary" full>✓ Mark as Complete</Btn>}
                          {canDeletePT && <Btn onClick={() => { showConfirm("Delete Task", "This task will be permanently deleted.", () => deleteProjectTask(task.id), { danger: true, confirmLabel: "Delete" }); }} variant="danger">Delete</Btn>}
                          {canDeletePT && <Btn onClick={() => setEditingProjectTask({ title: task.title, description: task.description || "", location: task.location || "", priority: task.priority, dueDate: task.dueDate || "" })} variant="white">✏️ Edit</Btn>}
                        </div>
                      )}
                      {editingProjectTask && (
                        <div style={{ background: C.grayLt, borderRadius: R.card, padding: "1rem", border: "1px solid " + C.greenBd }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 10 }}>EDIT TASK</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <TitleComboBox value={editingProjectTask.title} onChange={v => setEditingProjectTask(Object.assign({}, editingProjectTask, { title: v }))} titles={taskTitles} onAddTitle={saveTaskTitle} placeholder="Task title *" />
                            <textarea placeholder="Description" value={editingProjectTask.description} onChange={e => setEditingProjectTask(Object.assign({}, editingProjectTask, { description: e.target.value }))} rows={2}
                              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                            <select value={editingProjectTask.location} onChange={e => setEditingProjectTask(Object.assign({}, editingProjectTask, { location: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit", width: "100%" }}>{leagueLocations.filter(l => l.trim()).map(l => <option key={l} value={l}>{l}</option>)}<option value="Other">Other / Custom</option></select>
                            <select value={editingProjectTask.priority} onChange={e => setEditingProjectTask(Object.assign({}, editingProjectTask, { priority: e.target.value }))}
                              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit" }}>
                              <option value="low">Low Priority</option>
                              <option value="medium">Medium Priority</option>
                              <option value="high">High Priority</option>
                            </select>
                            <input type="date" value={editingProjectTask.dueDate} onChange={e => setEditingProjectTask(Object.assign({}, editingProjectTask, { dueDate: e.target.value }))}
                              style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                            <div style={{ display: "flex", gap: 8 }}>
                              <Btn onClick={() => updateProjectTask(task.id, editingProjectTask)} disabled={!editingProjectTask.title.trim()} full>Save Changes</Btn>
                              <Btn onClick={() => setEditingProjectTask(null)} variant="ghost">Cancel</Btn>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Project task list view
          const openCount = projectTasks.filter(t => t.status === "open").length;
          const ipCount = projectTasks.filter(t => t.status === "in-progress").length;
          return (
            <div style={{ padding: "14px" }}>
              <Confetti active={showConfetti} />
              <button onClick={() => { setSelectedProjectId(null); setProjectTaskFilter("all"); }}
                style={{ background: "none", border: "none", color: C.blue, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 12 }}>
                ← All Projects
              </button>

              {/* Project Header */}
              <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "1.25rem", marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div>
                    <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, lineHeight: 1.1, textTransform: "uppercase", color: C.text, marginBottom: 4 }}>{project.name}</div>
                    {project.description && <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{project.description}</div>}
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Created by {project.createdBy} · {timeAgo(project.createdAt)}</div>
                  </div>
                  {user && user.role === "admin" && (
                    <Btn onClick={() => { showConfirm("Delete Project", "This will permanently delete the project and all its tasks. This cannot be undone.", () => deleteProject(selectedProjectId), { danger: true, confirmLabel: "Delete Project" }); }} variant="danger" size="sm">Delete Project</Btn>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14 }}>
                  {[
                    { label: "Open", value: openCount, color: C.blue, bg: C.blueLt, icon: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>' },
                    { label: "In Progress", value: ipCount, color: C.amber, bg: C.amberLt, icon: '<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>' },
                    { label: "Completed", value: projectArchive.length, color: C.green, bg: C.greenLt, icon: '<polyline points="20 6 9 17 4 12"/>' }
                  ].map(s => (
                    <div key={s.label} style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "12px 4px", textAlign: "center" }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: s.icon }} />
                      </div>
                      <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 700, color: C.text, lineHeight: 1, marginBottom: 3 }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Task Filters & Add */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 5, flex: 1, flexWrap: "wrap" }}>
                  {[{ k: "all", l: "All (" + projectTasks.length + ")" }, { k: "open", l: "Open (" + openCount + ")" }, { k: "in-progress", l: "In Progress (" + ipCount + ")" }, { k: "mine", l: "Mine (" + projectTasks.filter(t => t.assignedTo === (user && user.name)).length + ")" }].map(f => (
                    <button key={f.k} onClick={() => setProjectTaskFilter(f.k)}
                      style={{ padding: "5px 11px", borderRadius: R.control, border: "1px solid " + (projectTaskFilter === f.k ? C.greenMid : C.border), background: projectTaskFilter === f.k ? C.greenLt : C.white, color: projectTaskFilter === f.k ? C.text : C.muted, fontSize: 12, fontWeight: projectTaskFilter === f.k ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
                      {f.l}
                    </button>
                  ))}
                </div>
                <Btn onClick={() => setShowNewProjectTask(v => !v)} size="sm">+ Add Task</Btn>
              </div>

              {/* New Project Task Form */}
              {showNewProjectTask && (
                <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.greenBd, padding: "1.25rem", marginBottom: "1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>New Task</div>
                    <button onClick={() => setShowNewProjectTask(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20 }}>×</button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                    <TitleComboBox value={newProjectTask.title} onChange={v => setNewProjectTask(Object.assign({}, newProjectTask, { title: v }))} titles={taskTitles} onAddTitle={saveTaskTitle} placeholder="Task title *" />
                    <textarea placeholder="Description (optional)" value={newProjectTask.description} onChange={e => setNewProjectTask(Object.assign({}, newProjectTask, { description: e.target.value }))} rows={2} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                    <select value={newProjectTask.location} onChange={e => setNewProjectTask(Object.assign({}, newProjectTask, { location: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit", width: "100%" }}>{leagueLocations.filter(l => l.trim()).map(l => <option key={l} value={l}>{l}</option>)}<option value="Other">Other / Custom</option></select>
                    <select value={newProjectTask.priority} onChange={e => setNewProjectTask(Object.assign({}, newProjectTask, { priority: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, background: C.white, fontFamily: "inherit" }}>
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                    </select>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>DUE DATE (optional)</label>
                      <input type="date" value={newProjectTask.dueDate} onChange={e => setNewProjectTask(Object.assign({}, newProjectTask, { dueDate: e.target.value }))} style={{ width: "100%", padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <Btn onClick={createProjectTask} disabled={!newProjectTask.title.trim()} full>Add Task to Project</Btn>
                  </div>
                </div>
              )}

              {/* Active Tasks */}
              {filteredPT.length === 0 && projectArchive.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2rem", color: C.muted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <div style={{ fontSize: 14 }}>No tasks yet. Click "+ Add Task" to get started.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {filteredPT.map(task => {
                    const st = STATUS[task.status]; const pr = PRIORITY[task.priority];
                    return (
                      <div key={task.id} onClick={() => setSelectedProjectTaskId(task.id)}
                        style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "1rem 1.25rem", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBd}
                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 5, marginBottom: 6, flexWrap: "wrap" }}>
                              <Badge bg={st.bg} color={st.color}>{st.label}</Badge>
                              <Badge bg={pr.bg} color={pr.text}>{pr.label}</Badge>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{task.title}</div>
                            {task.location && <div style={{ fontSize: 12, color: C.muted }}>📍 {(Array.isArray(task.locations) && task.locations.length > 0 ? task.locations.join(', ') : task.location) || ''} · {timeAgo(task.createdAt)}</div>}
                            {task.dueDate && <div style={{ fontSize: 11, fontWeight: 700, color: new Date(task.dueDate) < new Date() ? C.red : C.amber }}>📅 Due {fmtDate(task.dueDate + "T12:00:00")}{new Date(task.dueDate) < new Date() ? " · Overdue" : ""}</div>}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            {task.assignedTo ? <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{task.assignedTo}</div> : <div style={{ fontSize: 12, color: C.blue, fontWeight: 700, background: C.blueLt, padding: "2px 8px", borderRadius: R.control }}>Open</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Completed / Archive */}
              {projectArchive.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 10 }}>COMPLETED ({projectArchive.length})</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {projectArchive.map(task => (
                      <div key={task.id} style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.card, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: C.muted, textDecoration: "line-through" }}>{task.title}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>👤 {task.assignedTo || "Unknown"} · ✅ {task.completedAt ? fmtDate(task.completedAt) : ""} {task.duration ? "· ⏱ " + fmtDuration(task.duration) : ""}</div>
                        </div>
                        {user && user.role === "admin" && (
                          <div style={{ display: "flex", gap: 5 }}>
                            <button onClick={() => restoreProjectTask(task.id)} style={{ background: C.amberLt, border: "1px solid " + C.amberBd, borderRadius: R.control, padding: "3px 8px", fontSize: 11, fontWeight: 700, color: C.amber, cursor: "pointer", fontFamily: "inherit" }}>↩</button>
                            <button onClick={() => { showConfirm("Delete Task", "This archived task will be permanently deleted.", () => deleteArchivedProjectTask(task.id), { danger: true, confirmLabel: "Delete" }); }} style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "3px 8px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>🗑</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Projects list view
        return (
          <div style={{ padding: "14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Projects</div>
                <div style={{ fontSize: 12, color: C.muted }}>Task groups for specific initiatives</div>
              </div>
              <Btn onClick={() => setShowNewProject(v => !v)} size="sm">+ New Project</Btn>
            </div>

            {showNewProject && (
              <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.greenBd, padding: "1.25rem", marginBottom: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>New Project</div>
                  <button onClick={() => setShowNewProject(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20 }}>×</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  <input placeholder="Project name *  (e.g. Spring Season Prep)" value={newProject.name} onChange={e => setNewProject(Object.assign({}, newProject, { name: e.target.value }))} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                  <textarea placeholder="Description (optional)" value={newProject.description} onChange={e => setNewProject(Object.assign({}, newProject, { description: e.target.value }))} rows={2} style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                  <Btn onClick={createProject} disabled={!newProject.name.trim()} full>Create Project</Btn>
                </div>
              </div>
            )}

            {projects.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: C.muted }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📁</div>
                <div style={{ fontSize: 14, marginBottom: 4 }}>No projects yet</div>
                <div style={{ fontSize: 12 }}>Create a project to group related tasks together</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {projects.map(project => (
                  <div key={project.id} onClick={() => setSelectedProjectId(project.id)}
                    style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "1.25rem", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBd}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 4 }}>📁 {project.name}</div>
                        {project.description && <div style={{ fontSize: 13, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>{project.description}</div>}
                        <div style={{ fontSize: 12, color: C.muted }}>Created by {project.createdBy} · {timeAgo(project.createdAt)}</div>
                      </div>
                      <div style={{ background: C.greenLt, borderRadius: R.control, padding: "6px 12px", textAlign: "center", flexShrink: 0 }}>
                        <div style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>View →</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "board" && (() => {
        // Mark all current posts as read when board is viewed
        if (boardPosts.some(p => !readBoardPosts.has(p.id))) {
          const newRead = new Set([...readBoardPosts, ...boardPosts.map(p => p.id)]);
          setReadBoardPosts(newRead);
          try { localStorage.setItem('groundspal-read-posts', JSON.stringify([...newRead])); } catch {}
        }
        return (
        <div style={{ padding: "14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              {/* Megaphone, at the sidebar's own size=17, because this header names the
    Board nav destination and now carries that destination's exact icon. */}
<div style={{ fontWeight: 700, fontSize: 15, color: C.text, display: "flex", alignItems: "center", gap: 7 }}>
  <MegaphoneIcon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
  Message Board
</div>
              <div style={{ fontSize: 12, color: C.muted }}>Announcements and updates from the admin team</div>
            </div>
            {user && user.role === "admin" && (
              <Btn onClick={() => setShowNewPost(v => !v)} size="sm">+ Post</Btn>
            )}
          </div>

          {showNewPost && user && user.role === "admin" && (
            <div style={{ background: C.white, borderRadius: R.card, border: "2px solid " + C.greenBd, padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>New Post</div>
                <button onClick={() => setShowNewPost(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20 }}>×</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <input placeholder="Title (optional)" value={newPost.title} onChange={e => setNewPost(Object.assign({}, newPost, { title: e.target.value }))}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
                <textarea placeholder="Write your message here... *" value={newPost.body} onChange={e => setNewPost(Object.assign({}, newPost, { body: e.target.value }))} rows={4}
                  style={{ padding: "9px 12px", borderRadius: R.control, border: "1px solid " + C.border, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
                  <input type="checkbox" checked={!!newPost.important} onChange={e => setNewPost(Object.assign({}, newPost, { important: e.target.checked }))}
                    style={{ width: 16, height: 16, cursor: "pointer" }} />
                  🚩 Mark as important
                </label>
                <Btn onClick={createBoardPost} disabled={!newPost.body.trim()} full>Post Message</Btn>
              </div>
            </div>
          )}

          {boardPosts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: C.muted }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📌</div>
              <div style={{ fontSize: 14, marginBottom: 4 }}>No posts yet</div>
              {user && user.role === "admin" && <div style={{ fontSize: 12 }}>Click "+ Post" to leave a message for your volunteers</div>}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {boardPosts.map(post => {
                const isImportant = !!post.important;
                const accentColor = isImportant ? "#E24B4A" : "#75C83C";
                return (
                <div key={post.id} style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, borderLeft: "4px solid " + accentColor, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{post.title || "Message from " + post.author}</div>
                        {isImportant && (
                          <span style={{ background: "#FCEBEB", color: "#501313", borderRadius: R.control, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                            🚩 Important
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        Posted by {post.author} · {timeAgo(post.createdAt)} · {fmtDate(post.createdAt)}
                      </div>
                    </div>
                    {user && user.role === "admin" && (
                      <button onClick={() => { showConfirm("Delete Post", "This post will be permanently deleted.", () => deleteBoardPost(post.id), { danger: true, confirmLabel: "Delete" }); }}
                        style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "4px 10px", fontSize: 12, color: C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                        Delete
                      </button>
                    )}
                  </div>
                  <div style={{ padding: "0 1.25rem 1rem" }}>
                    <p style={{ fontSize: 14, color: C.text, margin: 0, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{post.body}</p>
                    <div style={{ borderTop: "1px solid " + C.border, marginTop: 12, paddingTop: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={() => { if (!user) return; const liked = (post.likes||[]).includes(user.name); updateDoc(doc(db, 'leagues', lid, 'board', post.id), { likes: liked ? arrayRemove(user.name) : arrayUnion(user.name) }); }} style={{ background: (post.likes||[]).includes(user && user.name) ? C.blue : C.white, border: "2px solid " + C.blue, borderRadius: R.control, padding: "6px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: (post.likes||[]).includes(user && user.name) ? C.white : C.blue, fontFamily: "inherit" }}>
                          👍 {(post.likes||[]).includes(user && user.name) ? "Liked" : "Like"}{(post.likes||[]).length > 0 ? " · " + (post.likes||[]).length : ""}
                        </button>
                        {(post.likes||[]).length > 0 && <span style={{ fontSize: 12, color: C.muted }}>{(post.likes||[]).join(", ")}</span>}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      );
      })()}



      {tab === "calendar" && (() => {
        const days = Array.from({length: 7}, (_, i) => { const d = new Date(calWeekStart); d.setDate(d.getDate() + i); return d; });
        const isToday = (d) => { const t = new Date(); return d.getDate()===t.getDate() && d.getMonth()===t.getMonth() && d.getFullYear()===t.getFullYear(); };
        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short' });
        const fmtTime = (t) => { if (!t) return ''; const [h, m] = t.split(':').map(Number); const ampm = h >= 12 ? 'pm' : 'am'; const hr = h % 12 || 12; return hr + (m > 0 ? ':' + String(m).padStart(2,'0') : '') + ampm; };
        const canManage = user && (user.role === "admin" || user.role === "coadmin");
        const fields = leagueLocations.filter(l => l && l.trim());
        const EVENT_STYLE = {
          game: { chipBg: '#378ADD', chipBorder: 'none', textMain: '#fff', textSub: 'rgba(255,255,255,0.85)', textFaint: 'rgba(255,255,255,0.7)', legendBg: '#E6F1FB', legendText: '#042C53', label: 'Game' },
          practice: { chipBg: 'transparent', chipBorder: '1.5px solid #888780', textMain: '#444441', textSub: '#6b6b66', textFaint: '#8a8a84', legendBg: 'transparent', legendText: '#444441', label: 'Practice' },
          other: { chipBg: '#D85A30', chipBorder: 'none', textMain: '#fff', textSub: 'rgba(255,255,255,0.85)', textFaint: 'rgba(255,255,255,0.7)', legendBg: '#FAECE7', legendText: '#4A1B0C', label: 'Other' }
        };
        const getEventStyle = (type) => EVENT_STYLE[type] || EVENT_STYLE.game;
        const getEventsForFieldDay = (field, date) => {
          const ds = date.toISOString().split('T')[0];
          return fieldEvents
            .filter(e => e.field === field && e.date === ds && (e.status === 'approved' || !e.status || canManage || (e.createdBy === (user && user.name))))
            .slice()
            .sort((a, b) => {
              if (!a.startTime && !b.startTime) return 0;
              if (!a.startTime) return 1;
              if (!b.startTime) return -1;
              return a.startTime.localeCompare(b.startTime);
            });
        };
        const pendingEvents = fieldEvents.filter(e => e.status === 'pending');
        // "My Events" section -- everything the current user requested/created
        // that hasn't happened yet (pending or approved). Status always sorts
        // before date: every pending row comes before every approved row, and
        // within each cluster, soonest date+time first. Declined requests are
        // excluded by construction, not filtered -- rejectEvent deletes the doc
        // rather than flagging it, so there's no "declined" status to exclude.
        const myEvents = fieldEvents
          .filter(ev => ev.createdBy === (user && user.name) && (ev.status === 'pending' || ev.status === 'approved') && ev.date >= getLocalDateStr())
          .slice()
          .sort((a, b) => {
            if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
            const aKey = a.date + '|' + (a.startTime || '');
            const bKey = b.date + '|' + (b.startTime || '');
            return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
          });
        const myEventsPendingCount = myEvents.filter(e => e.status === 'pending').length;
        // Live conflict check against events already approved in the system --
        // requesterFlaggedConflict (set at submission time) can go stale if an
        // admin approves something else in the meantime, so this re-checks
        // against the current fieldEvents state every render instead of relying
        // only on that stored flag.
        const findApprovedConflictsFor = (ev) => fieldEvents.filter(e => e.status === 'approved' && e.field === ev.field && e.date === ev.date && e.id !== ev.id && timeToMins(ev.startTime) < timeToMins(e.endTime || '23:59') && timeToMins(ev.endTime || '23:59') > timeToMins(e.startTime || '00:00'));
        const prevWeek = () => { const d = new Date(calWeekStart); d.setDate(d.getDate() - 7); setCalWeekStart(d); const sd = new Date(selectedMobileDay); sd.setDate(sd.getDate() - 7); setSelectedMobileDay(sd); };
        const nextWeek = () => { const d = new Date(calWeekStart); d.setDate(d.getDate() + 7); setCalWeekStart(d); const sd = new Date(selectedMobileDay); sd.setDate(sd.getDate() + 7); setSelectedMobileDay(sd); };
        const goToday = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); setCalWeekStart(d); const t = new Date(); t.setHours(0,0,0,0); setSelectedMobileDay(t); };
        const timeToMins = (t) => { if (!t) return 0; const [h,m] = t.split(':').map(Number); return h*60+m; };
        const findGameConflicts = async (field, date, start, end) => {
          const snap = await getDocs(query(collection(db, 'leagues', lid, 'games'), where('location', '==', field), where('date', '==', date)));
          const allMatches = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => timeToMins(start) < timeToMins(g.endTime || '23:59') && timeToMins(end || '23:59') > timeToMins(g.startTime || '00:00'));
          if (allMatches.length === 0 || !currentSeasonId) return allMatches;
          // Games only store a divisionId, not a seasonId directly, and the games
          // collection spans the league's entire history -- cross-reference against
          // the current season's divisions so a leftover game from an old season
          // doesn't get flagged as a live conflict.
          const divSnap = await getDocs(query(collection(db, 'leagues', lid, 'divisions'), where('seasonId', '==', currentSeasonId)));
          const currentSeasonDivisionIds = divSnap.docs.map(d => d.id);
          return allMatches.filter(g => currentSeasonDivisionIds.includes(g.divisionId));
        };
        // previewDates/finalDates: the effective list of dates this submission
        // will create, whether from recurrence generation or manual entry, minus
        // anything the requester removed from the conflict preview below.
        const previewDates = newEventRecurrence.enabled
          ? generateRecurringDates(newEventRecurrence.pattern, newEventRecurrence.daysOfWeek, newEventRecurrence.startDate, newEventRecurrence.endDate)
          : (newEvent.dates || []).filter(Boolean);
        const finalDates = previewDates.filter(d => !excludedPreviewDates.includes(d));
        const saveEvent = async () => {
          const eventField = newEvent.field || fields[0] || '';
          if (!newEvent.title.trim() || !eventField || finalDates.length === 0 || savingEvent) return;
          setSavingEvent(true);
          try {
            const status = canManage ? 'approved' : 'pending';
            // Dedupe -- a manually-entered date row can end up matching another
            // row (or, previously, a double-submit) and would otherwise write the
            // same date twice as two separate docs.
            const validDates = [...new Set(finalDates)];
            // Every new (non-edit) submission -- manual multi-date or recurrence-
            // generated -- gets a shared requestGroupId so the notification/review
            // system can treat it as one request instead of N separate ones.
            const requestGroupId = editingEvent ? null : ('grp' + Date.now() + Math.random().toString(36).slice(2, 8));

            // Non-admin requests are no longer hard-blocked on conflicts -- the live
            // preview above already let the requester see and remove conflicting
            // dates before submitting. Any conflict they chose to leave in (using
            // the same datesConflictInfo that drives that preview) is flagged
            // per-date so the reviewing admin can see and decide, rather than the
            // whole request being silently blocked.
            const conflictDates = !canManage ? validDates.filter(d => datesConflictInfo[d] && datesConflictInfo[d].conflict) : [];

            // Admins get a resolution choice instead of either a hard block or (the old
            // behavior) no warning at all.
            if (canManage) {
              const allConflicts = [];
              for (const date of validDates) {
                const tempId = editingEvent ? editingEvent.id : 'ev' + Date.now() + date;
                const gmConflicts = await findGameConflicts(eventField, date, newEvent.startTime, newEvent.endTime);
                gmConflicts.forEach(g => allConflicts.push({ ...g, _source: 'game', _date: date }));
                const evConflicts = fieldEvents.filter(e => e.field === eventField && e.date === date && e.id !== tempId && e.status === 'approved' && timeToMins(newEvent.startTime) < timeToMins(e.endTime || '23:59') && timeToMins(newEvent.endTime || '23:59') > timeToMins(e.startTime || '00:00') && !gmConflicts.some(g => g.calendarEventId === e.id || e.schedulerGameId === g.id));
                evConflicts.forEach(e => allConflicts.push({ ...e, _source: 'event', _date: date }));
              }
              if (allConflicts.length > 0) {
                setPendingSaveConflicts({ kind: 'event', validDates, eventField, status, requestGroupId });
                setPendingSaveOverrideIds(allConflicts.map(c => c.id));
                setPendingSaveConflictItems(allConflicts);
                return;
              }
            }

            await finalizeSaveEvent(validDates, eventField, status, [], requestGroupId, conflictDates);
          } finally {
            setSavingEvent(false);
          }
        };

        const finalizeSaveEvent = async (validDates, eventField, status, blockedDates, requestGroupId, conflictDates) => {
          const datesToSave = validDates.filter(d => !blockedDates.includes(d));
          const recurrenceData = (!editingEvent && newEventRecurrence.enabled) ? { pattern: newEventRecurrence.pattern, daysOfWeek: newEventRecurrence.daysOfWeek, startDate: newEventRecurrence.startDate, endDate: newEventRecurrence.endDate } : null;
          for (const date of datesToSave) {
            const id = editingEvent ? editingEvent.id : 'ev' + Date.now() + '_' + date;
            await setDoc(doc(db, 'leagues', lid, 'fieldEvents', id), { id, title: newEvent.title.trim(), field: eventField, eventType: newEvent.eventType||'game', date, startTime: newEvent.startTime, endTime: newEvent.endTime, notes: newEvent.notes || '', slots: newEventSlots, image: calImageData || (editingEvent ? editingEvent.image || '' : ''), status: editingEvent ? editingEvent.status : status, createdBy: editingEvent ? editingEvent.createdBy : user.name, createdAt: editingEvent ? editingEvent.createdAt : new Date().toISOString(), schedulerGameId: editingEvent ? (editingEvent.schedulerGameId || null) : null, requestGroupId: editingEvent ? (editingEvent.requestGroupId || null) : (requestGroupId || null), recurrence: editingEvent ? (editingEvent.recurrence || null) : recurrenceData, requesterFlaggedConflict: editingEvent ? !!editingEvent.requesterFlaggedConflict : ((conflictDates || []).includes(date)) });
          }
          // If this event was published from a Scheduler game, push date/time/location
          // changes made here back onto that game so Scheduler stays in sync.
          if (editingEvent && editingEvent.schedulerGameId && datesToSave.length > 0) {
            try {
              await updateDoc(doc(db, 'leagues', lid, 'games', editingEvent.schedulerGameId), {
                date: datesToSave[0], startTime: newEvent.startTime, endTime: newEvent.endTime, location: eventField
              });
            } catch (e) { /* linked scheduler game may have been deleted independently -- not fatal */ }
          }
          if (!canManage && !editingEvent && datesToSave.length > 0) {
            // One grouped notification for the whole request (single date or many),
            // not one per date -- event_request_group replaces the old single-date
            // event_request type for all new writes going forward.
            await createNotification('event_request_group', '📅 ' + user.name + ' requested ' + datesToSave.length + ' event' + (datesToSave.length > 1 ? 's' : '') + ': "' + newEvent.title.trim() + '" on ' + eventField, 'admin', {
              requestGroupId, title: newEvent.title.trim(), field: eventField, startTime: newEvent.startTime, endTime: newEvent.endTime, dates: datesToSave, conflictDates: (conflictDates || []).filter(d => datesToSave.includes(d)), requestedBy: user.name,
            });
            const adminEmails = [league.adminEmail, ...((league.coAdmins || []).map(ca => ca.email))].filter(Boolean);
            const fmtRequestDate = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
            sendEmail('event_request', adminEmails, { leagueName: league.leagueName, eventTitle: newEvent.title.trim(), dates: datesToSave.map(fmtRequestDate), startTime: newEvent.startTime || '', endTime: newEvent.endTime || '', field: eventField, requestedBy: user.name });
            const adminSmsPhones = getAdminSmsPhones('eventRequests');
            if (adminSmsPhones.length > 0) {
              sendSMS('event_request_pending', adminSmsPhones, { leagueName: league.leagueName, eventTitle: newEvent.title.trim(), dateCount: datesToSave.length });
            }
          }
          setShowEventForm(false); setEditingEvent(null); if (newEventSlots.length > 0) { setSlotsNotification({ count: newEventSlots.length, title: newEvent.title.trim() }); setTimeout(() => setSlotsNotification(null), 4000); } setNewEvent({ title:'', field:'', dates:[''], startTime:'09:00', endTime:'10:00', notes:'', eventType:'game' }); setCalImageData(''); setNewEventSlots([]); setNewEventRecurrence(EMPTY_RECURRENCE); setDatesConflictInfo({}); setExcludedPreviewDates([]);
        };

        const resolveConflictsAndSave = async () => {
          if (!pendingSaveConflicts || savingEvent) return;
          setSavingEvent(true);
          try {
            const conflicts = pendingSaveConflictItems;
            const overridden = conflicts.filter(c => pendingSaveOverrideIds.includes(c.id));
            const skipped = conflicts.filter(c => !pendingSaveOverrideIds.includes(c.id));
            const blockedDates = [...new Set(skipped.map(c => c._date))];

            for (const c of overridden) {
              if (c._source === 'event') {
                const creator = members.find(m => m.name === c.createdBy);
                await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', c.id));
                triggerNotify('event', { action: 'deleted', evData: { ...c, creatorEmail: creator ? creator.email : null } });
              } else if (c._source === 'game') {
                if (c.calendarEventId) { try { await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', c.calendarEventId)); } catch (e) {} }
                await deleteDoc(doc(db, 'leagues', lid, 'games', c.id));
              }
            }

            const { validDates, eventField, status, requestGroupId } = pendingSaveConflicts;
            setPendingSaveConflicts(null);
            setPendingSaveOverrideIds([]);
            setPendingSaveConflictItems([]);
            await finalizeSaveEvent(validDates, eventField, status, blockedDates, requestGroupId, []);
          } finally {
            setSavingEvent(false);
          }
        };
        const deleteEvent = async (ev) => {
          const canDelete = canManage || (ev.createdBy === (user && user.name));
          if (!canDelete) return;
          showConfirm('Delete Event', 'This event will be permanently deleted.', async () => {
            await deleteDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id));
            const creator = members.find(m => m.name === ev.createdBy);
            triggerNotify('event', { action: 'deleted', evData: { ...ev, creatorEmail: creator ? creator.email : null } });
          }, { danger: true, confirmLabel: 'Delete' });
        };
        const sendSignupEmail = (ev, slot, comment) => {
          const emailjsServiceId = league && league.emailjsServiceId;
          const emailjsTemplateId = league && league.emailjsTemplateId;
          const emailjsPublicKey = league && league.emailjsPublicKey;
          if (!emailjsServiceId || !emailjsTemplateId || !emailjsPublicKey || !user.email) return;
          const params = {
            to_email: user.email, to_name: user.name,
            event_title: ev.title, event_date: ev.date,
            slot_task: slot.task, slot_location: slot.location || '',
            slot_start: fmtTime(slot.startTime), slot_end: fmtTime(slot.endTime),
            league_name: league.name || 'Your League',
            comment: comment || 'None',
          };
          if (window.emailjs) window.emailjs.send(emailjsServiceId, emailjsTemplateId, params, emailjsPublicKey).catch(() => {});
        };
        const signupForSlot = async (ev, slotId, comment) => {
          const slot = (ev.slots || []).find(s => s.id === slotId);
          if (!slot) return;
          if ((slot.signups || []).some(sg => sg.name === user.name)) return;
          if ((slot.signups || []).length >= slot.spotsNeeded) { alert('Sorry, this slot is full.'); return; }
          const updatedSlots = (ev.slots || []).map(s => {
            if (s.id !== slotId) return s;
            return Object.assign({}, s, { signups: [...(s.signups || []), { name: user.name, signedUpAt: new Date().toISOString(), comment: comment || '' }] });
          });
          await updateDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id), { slots: updatedSlots });
          setViewingEvent(Object.assign({}, ev, { slots: updatedSlots }));
          setSignupNotification({ eventTitle: ev.title, task: slot.task, location: slot.location || '', startTime: slot.startTime, endTime: slot.endTime, date: ev.date });
          sendSignupEmail(ev, slot, comment);
          const me = findMyMemberDoc();
          if (me && me.smsConsent && me.phone) {
            sendSMS('volunteer_signup', [me.phone], { leagueName: (league && league.leagueName) || 'Your League', eventDate: ev.date, slotTime: fmtTime(slot.startTime) });
          }
          setTimeout(() => setSignupNotification(null), 6000);
        };
        const cancelSlotSignup = async (ev, slotId) => {
          const updatedSlots = (ev.slots || []).map(s => {
            if (s.id !== slotId) return s;
            return Object.assign({}, s, { signups: (s.signups || []).filter(sg => sg.name !== user.name) });
          });
          await updateDoc(doc(db, 'leagues', lid, 'fieldEvents', ev.id), { slots: updatedSlots });
          setViewingEvent(Object.assign({}, ev, { slots: updatedSlots }));
        };
        // approveEvent/rejectEvent (and the group-aware variants) now live at
        // FieldOpsApp's top level -- see near the recurrence useEffect -- so the
        // header's NotificationsPanel can also drive them; referenced here via
        // closure.
        const weekLabel = fmt(days[0]) + ' – ' + fmt(days[6]) + ', ' + days[0].getFullYear();
        return (
          <div style={{ padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={prevWeek} style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.control, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                <button onClick={nextWeek} style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.control, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                <button onClick={goToday} style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.control, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Today</button>
                <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{weekLabel}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {canManage && pendingEvents.length > 0 && <span style={{ background: C.amber, color: C.white, borderRadius: R.control, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{pendingEvents.length} pending</span>}
                <Btn onClick={() => { setEditingEvent(null); setCalImageData(''); setNewEvent({ title:'', field: fields[0]||'', dates:[new Date().toISOString().split('T')[0]], startTime:'09:00', endTime:'10:00', notes:'', eventType:'game' }); setNewEventRecurrence(EMPTY_RECURRENCE); setDatesConflictInfo({}); setExcludedPreviewDates([]); setShowEventForm(true); }} size="sm">+ {canManage ? 'New Event' : 'Request Event'}</Btn>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
              {['game', 'practice', 'other'].map(k => {
                const es = getEventStyle(k);
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: R.control, background: es.chipBg, border: es.chipBorder !== 'none' ? es.chipBorder : 'none', flexShrink: 0, boxSizing: 'border-box' }} />
                    <span style={{ fontSize: 12, color: C.muted }}>{es.label}</span>
                  </div>
                );
              })}
            </div>
            {canManage && (() => {
              const today = getLocalDateStr();
              const todayClosures = fieldClosures.filter(cl => cl.date === today);
              const closedFields = todayClosures.map(cl => cl.field);
              const closeField = async (field, reason, message) => {
                const id = 'cl' + Date.now();
                await setDoc(doc(db, 'leagues', lid, 'fieldClosures', id), {
                  id, field, reason, message: message || '', date: today,
                  closedBy: user.name, closedAt: new Date().toISOString()
                });
                triggerNotify('field', { fieldName: field, action: 'close', reason, message: message || '' });
              };
              const reopenField = async (field) => {
                const closure = todayClosures.find(cl => cl.field === field);
                if (closure) {
                  await deleteDoc(doc(db, 'leagues', lid, 'fieldClosures', closure.id));
                  triggerNotify('field', { fieldName: field, action: 'open', reason: '', message: '' });
                }
              };
              return (
                <div style={{ marginBottom: 14 }}>
                  <div onClick={() => setFieldStatusOpen(v => !v)} style={{ fontWeight: 700, fontSize: 13, color: closedFields.length > 0 ? '#501313' : C.text, marginBottom: fieldStatusOpen ? 8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', padding: '8px 12px', background: closedFields.length > 0 ? '#FCEBEB' : C.white, border: '1.5px solid ' + (closedFields.length > 0 ? '#E24B4A' : '#444441'), borderRadius: R.control }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      📍 Location status · {closedFields.length > 0 ? closedFields.length + ' closed' : 'all clear'}
                      <span onClick={e => { e.stopPropagation(); setShowFieldStatusInfo(v => !v); }}
                        style={{ width: 16, height: 16, borderRadius: '50%', background: C.white, border: '1px solid ' + C.border, color: C.muted, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                        i
                      </span>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={e => { e.stopPropagation(); setEditingExtBatchId(null); setExtClosureFields([]); setExtClosureStart(''); setExtClosureEnd(''); setExtClosureReason('Weather'); setExtClosureMessage(''); setShowExtendedClosure(true); setExtClosureStep('form'); }} style={{ background: 'transparent', color: closedFields.length > 0 ? '#501313' : C.text, border: '1.5px solid ' + (closedFields.length > 0 ? '#E24B4A' : '#444441'), borderRadius: R.control, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+ Extended closure</button>
                      <span style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.control, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.muted }}>{fieldStatusOpen ? '▲' : '▼'}</span>
                    </span>
                  </div>
                  {showFieldStatusInfo && (
                    <div onClick={e => e.stopPropagation()} style={{ background: '#1C1C1E', color: '#fff', fontSize: 12, lineHeight: 1.6, padding: '10px 12px', borderRadius: R.control, marginTop: 6 }}>
                      Mark a location closed to let volunteers and coaches know it's not usable — closed locations also get blocked from new games in Scheduler automatically, so nothing gets scheduled somewhere that's shut down. Use <b>+ Extended closure</b> for closures spanning more than one day, like a rained-out weekend or a holiday break.
                    </div>
                  )}

                  {/* Extended Closure Modal */}
                  {showExtendedClosure && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                      <div style={{ background: '#fff', borderRadius: R.card, padding: '24px 20px', maxWidth: 420, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
                        {extClosureStep === 'form' ? (
                          <>
                            <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', marginBottom: 4 }}>🚫 {editingExtBatchId ? 'Edit' : ''} Extended Location Closure</div>
                            <div style={{ fontSize: 13, color: '#8E8E93', marginBottom: 16 }}>{editingExtBatchId ? 'Update the fields, dates, or reason for this closure.' : 'Close one or more locations for a date range.'}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 6 }}>LOCATIONS</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                              {leagueLocations.filter(l => l && l.trim()).map(loc => (
                                <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: R.control, border: '1px solid ' + (extClosureFields.includes(loc) ? '#E24B4A' : '#E5E5EA'), background: extClosureFields.includes(loc) ? '#FCEBEB' : '#fff', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={extClosureFields.includes(loc)} onChange={e => setExtClosureFields(prev => e.target.checked ? [...prev, loc] : prev.filter(f => f !== loc))} style={{ accentColor: '#E24B4A' }} />
                                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1C1C1E' }}>{loc}</span>
                                </label>
                              ))}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 4 }}>START DATE</div>
                                <input type="date" value={extClosureStart} onChange={e => setExtClosureStart(e.target.value)} style={{ width: '100%', padding: '9px 10px', borderRadius: R.control, border: '1px solid #E5E5EA', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 4 }}>END DATE</div>
                                <input type="date" value={extClosureEnd} min={extClosureStart} onChange={e => setExtClosureEnd(e.target.value)} style={{ width: '100%', padding: '9px 10px', borderRadius: R.control, border: '1px solid #E5E5EA', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
                              </div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 6 }}>REASON</div>
                            <select value={extClosureReason} onChange={e => setExtClosureReason(e.target.value)} style={{ width: '100%', padding: '9px 10px', borderRadius: R.control, border: '1px solid #E5E5EA', fontSize: 13, fontFamily: 'inherit', marginBottom: 10, background: '#fff' }}>
                              <option>Weather</option><option>Location Conditions</option><option>Other</option>
                            </select>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.08em', marginBottom: 6 }}>NOTE (optional)</div>
                            <textarea value={extClosureMessage} onChange={e => setExtClosureMessage(e.target.value)} placeholder="e.g. Turf renovation in progress" rows={2} style={{ width: '100%', padding: '9px 10px', borderRadius: R.control, border: '1px solid #E5E5EA', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: 16 }} />
                            <div style={{ display: 'flex', gap: 10 }}>
                              <button onClick={() => { setShowExtendedClosure(false); setExtClosureFields([]); setExtClosureStart(''); setExtClosureEnd(''); setExtClosureMessage(''); setExtClosureStep('form'); setEditingExtBatchId(null); }} style={{ flex: 1, padding: '11px', borderRadius: R.control, border: '1px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                              <button onClick={checkExtClosureConflicts} disabled={!extClosureFields.length || !extClosureStart || !extClosureEnd} style={{ flex: 2, padding: '11px', borderRadius: R.control, border: 'none', background: extClosureFields.length && extClosureStart && extClosureEnd ? C.redFill : '#E5E5EA', color: extClosureFields.length && extClosureStart && extClosureEnd ? '#fff' : '#8E8E93', fontWeight: 700, fontSize: 14, cursor: extClosureFields.length && extClosureStart && extClosureEnd ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Continue →</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', marginBottom: 4 }}>⚠️ Conflicting Events</div>
                            <div style={{ fontSize: 13, color: '#8E8E93', marginBottom: 16 }}>The following approved events overlap with this closure. Select which ones to cancel.</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                              {extClosureConflicts.map(ev => (
                                <label key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: R.control, border: '1px solid ' + (extClosureCancelIds.includes(ev.id) ? '#E24B4A' : '#E5E5EA'), background: extClosureCancelIds.includes(ev.id) ? '#FCEBEB' : '#fff', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={extClosureCancelIds.includes(ev.id)} onChange={e => setExtClosureCancelIds(prev => e.target.checked ? [...prev, ev.id] : prev.filter(id => id !== ev.id))} style={{ accentColor: '#E24B4A', marginTop: 2 }} />
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E' }}>{ev.title}</div>
                                    <div style={{ fontSize: 11, color: '#8E8E93' }}>{ev.date} · {ev.field}</div>
                                    {ev.slots && ev.slots.some(s => (s.signups||[]).length > 0) && (<div style={{ fontSize: 11, color: '#501313', marginTop: 2 }}>⚠️ Has volunteer signups — they will be notified</div>)}
                                  </div>
                                </label>
                              ))}
                            </div>
                            <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 16, fontStyle: 'italic' }}>Event creators and signed-up volunteers will receive a cancellation email.</div>
                            <div style={{ display: 'flex', gap: 10 }}>
                              <button onClick={() => setExtClosureStep('form')} style={{ flex: 1, padding: '11px', borderRadius: R.control, border: '1px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
                              <button onClick={() => submitExtClosure(extClosureCancelIds)} style={{ flex: 2, padding: '11px', borderRadius: R.control, border: 'none', background: C.redFill, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Confirm Closure</button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Event/Game conflict resolution modal (admin-only) */}
                  {pendingSaveConflicts && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                      <div style={{ background: '#fff', borderRadius: R.card, padding: '24px 20px', maxWidth: 460, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#1C1C1E', marginBottom: 4 }}>⚠️ Scheduling Conflict</div>
                        <div style={{ fontSize: 13, color: '#8E8E93', marginBottom: 12 }}>
                          The event you're creating below overlaps with the existing item(s) listed under it. For each one, check the box to <strong>cancel and delete the existing item</strong> so your new event can take that date — or leave it unchecked to <strong>keep the existing item</strong>, in which case your new event will simply not be created for that date.
                        </div>
                        <div style={{ padding: '10px 12px', borderRadius: R.control, border: '1.5px solid #75C83C', background: '#F1FAEA', marginBottom: 14 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#2F5A16', letterSpacing: '0.05em', marginBottom: 2 }}>YOUR NEW EVENT</div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E' }}>{newEvent.title || '(untitled)'}</div>
                          <div style={{ fontSize: 11, color: '#444441' }}>{newEvent.startTime}–{newEvent.endTime} · {pendingSaveConflicts.eventField}</div>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', letterSpacing: '0.05em', marginBottom: 6 }}>CONFLICTS WITH (EXISTING)</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                          {pendingSaveConflictItems.map(c => (
                            <label key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: R.control, border: '1px solid ' + (pendingSaveOverrideIds.includes(c.id) ? '#E24B4A' : '#E5E5EA'), background: pendingSaveOverrideIds.includes(c.id) ? '#FCEBEB' : '#fff', cursor: 'pointer' }}>
                              <input type="checkbox" checked={pendingSaveOverrideIds.includes(c.id)} onChange={e => setPendingSaveOverrideIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} style={{ accentColor: '#E24B4A', marginTop: 2 }} />
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1C1E' }}>
                                  {c._source === 'game' ? (c.title || 'Scheduled game') : c.title}
                                  <span style={{ fontWeight: 400, color: '#8E8E93', marginLeft: 6, fontSize: 11 }}>{c._source === 'game' ? 'Scheduler game' : 'Event'}</span>
                                </div>
                                <div style={{ fontSize: 11, color: '#8E8E93' }}>{c._date} · {c.startTime}–{c.endTime} · {c._source === 'game' ? c.location : c.field}</div>
                                {c._source === 'game' && !c.calendarEventId && (<div style={{ fontSize: 11, color: '#8E8E93', marginTop: 2, fontStyle: 'italic' }}>Not published to the Events calendar yet — still holds this time slot in Scheduler.</div>)}
                                {c._source === 'event' && c.slots && c.slots.some(s => (s.signups || []).length > 0) && (<div style={{ fontSize: 11, color: '#501313', marginTop: 2 }}>⚠️ Has volunteer signups</div>)}
                                {pendingSaveOverrideIds.includes(c.id) ? <div style={{ fontSize: 11, color: '#501313', marginTop: 2, fontWeight: 700 }}>Will be cancelled and deleted — your new event wins this slot</div> : <div style={{ fontSize: 11, color: '#8E8E93', marginTop: 2 }}>Will be kept — your new event skips this date</div>}
                              </div>
                            </label>
                          ))}
                        </div>
                        <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 16, fontStyle: 'italic' }}>If a cancelled event has a known creator, you'll be asked whether to notify them.</div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button onClick={() => { setPendingSaveConflicts(null); setPendingSaveConflictItems([]); setPendingSaveOverrideIds([]); }} disabled={savingEvent} style={{ flex: 1, padding: '11px', borderRadius: R.control, border: '1px solid #E5E5EA', background: '#fff', fontWeight: 700, fontSize: 14, color: '#8E8E93', cursor: savingEvent ? 'default' : 'pointer', opacity: savingEvent ? 0.6 : 1, fontFamily: 'inherit' }}>Cancel</button>
                          <button onClick={resolveConflictsAndSave} disabled={savingEvent} style={{ flex: 2, padding: '11px', borderRadius: R.control, border: 'none', background: C.redFill, color: '#fff', fontWeight: 700, fontSize: 14, cursor: savingEvent ? 'default' : 'pointer', opacity: savingEvent ? 0.6 : 1, fontFamily: 'inherit' }}>{savingEvent ? 'Saving…' : 'Continue'}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {fieldStatusOpen && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {leagueLocations.filter(l => l && l.trim()).map(field => {
                      const isClosed = closedFields.includes(field);
                      const closure = todayClosures.find(cl => cl.field === field);
                      return (
                        <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: R.card, background: C.white, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isClosed ? 3 : 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{field}</div>
                              <span style={{ background: isClosed ? '#FCEBEB' : C.greenLt, color: isClosed ? '#501313' : C.green, borderRadius: R.control, padding: '2px 9px', fontSize: 10, fontWeight: 700 }}>{isClosed ? '🚫 Closed' : '✅ Open'}</span>
                            </div>
                            {isClosed && <div style={{ fontSize: 11, color: '#501313' }}>{closure.reason}{closure.message ? ' — ' + closure.message : ''}</div>}
                          </div>
                          {isClosed ? (
                            <Btn onClick={() => reopenField(field)} size="sm" variant="white">Reopen</Btn>
                          ) : (
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 5 }}>CLOSE DUE TO:</div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {['Weather', 'Location Conditions', 'Other'].map(reason => (
                                <Btn key={reason} onClick={() => { const msg = reason === 'Other' ? window.prompt('Add a note (optional):') || '' : ''; closeField(field, reason, msg); }} size="sm" variant="white">{reason}</Btn>
                              ))}
                            </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>}
                </div>
              );
            })()}

            {/* Extended Closures — manage batches created via the "+ Extended closure" flow */}
            {(() => {
              const todayStr = getLocalDateStr();
              const batches = {};
              fieldClosures.filter(cl => cl.extended && cl.extendedBatchId && cl.date >= todayStr).forEach(cl => {
                if (!batches[cl.extendedBatchId]) batches[cl.extendedBatchId] = { fields: new Set(), dates: [], reason: cl.reason, message: cl.message };
                batches[cl.extendedBatchId].fields.add(cl.field);
                batches[cl.extendedBatchId].dates.push(cl.date);
              });
              const batchList = Object.entries(batches).map(([id, b]) => ({ id, fields: [...b.fields], dates: b.dates.sort(), reason: b.reason, message: b.message }));

              // Older extended closures created before this feature shipped have no
              // extendedBatchId to group by — fall back to grouping by reason + note
              // so they're still visible and removable, not permanently stuck.
              const legacyGroups = {};
              fieldClosures.filter(cl => cl.extended && !cl.extendedBatchId && cl.date >= todayStr).forEach(cl => {
                const key = (cl.reason || '') + '||' + (cl.message || '');
                if (!legacyGroups[key]) legacyGroups[key] = { fields: new Set(), dates: [], reason: cl.reason, message: cl.message, ids: [] };
                legacyGroups[key].fields.add(cl.field);
                legacyGroups[key].dates.push(cl.date);
                legacyGroups[key].ids.push(cl.id);
              });
              const legacyList = Object.entries(legacyGroups).map(([key, b]) => ({ id: key, fields: [...b.fields], dates: b.dates.sort(), reason: b.reason, message: b.message, ids: b.ids }));

              if (batchList.length === 0 && legacyList.length === 0) return null;
              return (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>EXTENDED CLOSURES</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {batchList.map(b => (
                      <div key={b.id} style={{ background: C.white, borderRadius: R.card, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{b.fields.join(', ')}</div>
                            <div style={{ fontSize: 12, color: '#501313', marginTop: 2 }}>
                              {new Date(b.dates[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {b.dates[0] !== b.dates[b.dates.length - 1] ? ' – ' + new Date(b.dates[b.dates.length - 1] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                              {' · ' + b.reason}
                            </div>
                            {b.message && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{b.message}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Btn onClick={() => startEditExtClosure(b.id)} size="sm" variant="white">Edit</Btn>
                            <Btn onClick={() => showConfirm('Remove Extended Closure', 'This will reopen ' + b.fields.join(', ') + ' for every date in this closure (' + b.dates[0] + ' to ' + b.dates[b.dates.length - 1] + ').', () => removeExtClosureBatch(b.id), { danger: true, confirmLabel: 'Remove' })} size="sm" variant="white">Remove</Btn>
                          </div>
                        </div>
                      </div>
                    ))}
                    {legacyList.map(b => (
                      <div key={b.id} style={{ background: C.white, borderRadius: R.card, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{b.fields.join(', ')}</div>
                              <span style={{ fontSize: 9, fontWeight: 700, color: C.muted, background: C.grayLt, borderRadius: R.control, padding: '2px 7px' }}>LEGACY</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#501313', marginTop: 2 }}>
                              {new Date(b.dates[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {b.dates[0] !== b.dates[b.dates.length - 1] ? ' – ' + new Date(b.dates[b.dates.length - 1] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                              {' · ' + b.reason}
                            </div>
                            {b.message && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{b.message}</div>}
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 3, fontStyle: 'italic' }}>Created before editing was supported — remove and recreate via "+ Extended" to make changes.</div>
                          </div>
                          <div>
                            <Btn onClick={() => showConfirm('Remove Extended Closure', 'This will reopen ' + b.fields.join(', ') + ' for every date in this closure (' + b.dates[0] + ' to ' + b.dates[b.dates.length - 1] + ').', async () => { for (const id of b.ids) { await deleteDoc(doc(db, 'leagues', lid, 'fieldClosures', id)); } triggerNotify('field', { fields: b.fields, action: 'open', reason: '', message: '' }); }, { danger: true, confirmLabel: 'Remove' })} size="sm" variant="white">Remove</Btn>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Consolidated Volunteer Signups View */}
        {canManage && fieldEvents.filter(ev => ev.slots && ev.slots.length > 0).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 10 }}>VOLUNTEER SIGNUPS THIS WEEK</div>
            {fieldEvents
              .filter(ev => ev.slots && ev.slots.length > 0 && ev.date >= calWeekStart.toISOString().split('T')[0] && ev.date <= new Date(calWeekStart.getTime() + 6*86400000).toISOString().split('T')[0])
              .sort((a, b) => a.date > b.date ? 1 : -1)
              .map(ev => (
                <div key={ev.id} style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.card, marginBottom: 10, overflow: 'hidden' }}>
                  <div onClick={() => setViewingEvent(ev)} style={{ background: C.grayLt, padding: '10px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{ev.title}</span>
                      <span style={{ fontSize: 12, color: C.muted, marginLeft: 8 }}>{ev.date} · {ev.field}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{ev.slots.reduce((acc, s) => acc + (s.signups||[]).length, 0)} / {ev.slots.reduce((acc, s) => acc + s.spotsNeeded, 0)} filled</span>
                      <button onClick={e => { e.stopPropagation(); setViewingEvent(ev); }}
                        style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.control, padding: '4px 10px', fontSize: 11, fontWeight: 700, color: C.text, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Edit →
                      </button>
                    </div>
                  </div>
                  {ev.slots.map(slot => (
                    <div key={slot.id} style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 180 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{slot.task}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{fmtTime(slot.startTime)} – {fmtTime(slot.endTime)}{slot.location ? ' · 📍 ' + slot.location : ''}</div>
                        <div style={{ marginTop: 4 }}>
                          <span style={{ background: (slot.signups||[]).length >= slot.spotsNeeded ? C.redLt : C.greenLt, color: (slot.signups||[]).length >= slot.spotsNeeded ? C.redDark : C.green, borderRadius: R.control, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                            {(slot.signups||[]).length}/{slot.spotsNeeded} filled
                          </span>
                        </div>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(slot.signups||[]).length === 0 ? (
                          <span style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>No signups yet</span>
                        ) : (slot.signups||[]).map((sg, i) => (
                          <div key={i} style={{ background: C.greenLt, border: '1px solid ' + C.greenBd, borderRadius: R.control, padding: '3px 10px', fontSize: 12 }}>
                            <span style={{ fontWeight: 700, color: C.green }}>✓ {sg.name}</span>
                            {sg.comment && <span style={{ color: C.muted, fontStyle: 'italic', marginLeft: 4 }}>"{sg.comment}"</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {canManage && pendingEvents.length > 0 && (
              <div style={{ background: C.amberLt, border: '1px solid ' + C.amberBd, borderRadius: R.control, padding: 14, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.amber, marginBottom: 10 }}>Pending Approval ({pendingEvents.length})</div>
                {pendingEvents.map(ev => {
                  const d = new Date(ev.date + 'T00:00:00');
                  const dayNum = d.getDate();
                  const monthAbbr = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                  const es = getEventStyle(ev.eventType);
                  const isOutline = es.chipBg === 'transparent';
                  const approvedConflicts = findApprovedConflictsFor(ev);
                  const hasConflict = approvedConflicts.length > 0 || ev.requesterFlaggedConflict;
                  return (
                    <div key={ev.id} style={{ background: C.white, borderRadius: R.card, padding: 10, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', flexWrap: 'wrap', marginBottom: 8, border: hasConflict ? '1px solid ' + C.redBd : 'none' }}>
                      <div style={{ width: 44, height: 44, borderRadius: R.control, background: isOutline ? C.white : es.chipBg, border: isOutline ? es.chipBorder : 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box' }}>
                        <div style={{ fontSize: 9, color: es.textMain, opacity: 0.75, fontWeight: 700, lineHeight: 1 }}>{monthAbbr}</div>
                        <div style={{ fontSize: 16, color: es.textMain, fontWeight: 700, lineHeight: 1.3 }}>{dayNum}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{ev.title}</div>
                        <div style={{ fontSize: 12, color: C.muted }}>{ev.field} · {fmtTime(ev.startTime)}{ev.endTime ? ' – ' + fmtTime(ev.endTime) : ''}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>Requested by {ev.createdBy}</div>
                        {ev.notes && <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>{ev.notes}</div>}
                        {hasConflict && (
                          <div style={{ fontSize: 11, color: C.redDark, fontWeight: 700, marginTop: 4 }}>
                            ⚠️ Conflicts with {approvedConflicts.length > 0 ? approvedConflicts.map(c => c.title + ' (' + fmtTime(c.startTime) + (c.endTime ? '–' + fmtTime(c.endTime) : '') + ')').join(', ') : 'an already-approved event'}
                          </div>
                        )}
                      </div>
                      <span style={{ background: isOutline ? '#F2F2F1' : es.legendBg, color: es.legendText, border: isOutline ? es.chipBorder : 'none', borderRadius: R.control, padding: '3px 9px', fontSize: 10, fontWeight: 700, boxSizing: 'border-box' }}>{es.label}</span>
                      <div style={{ display: 'flex', gap: 6, width: '100%', justifyContent: 'flex-end' }}><Btn onClick={() => approveEvent(ev.id)} size="sm">✓ Approve</Btn><Btn onClick={() => rejectEvent(ev.id)} variant="danger" size="sm">✕ Reject</Btn></div>
                    </div>
                  );
                })}
              </div>
            )}
            {showEventForm && (
              <div style={{ background: C.white, border: '1px solid ' + C.greenBd, borderRadius: R.card, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>{editingEvent ? 'Edit Event' : canManage ? 'New Event' : 'Request Event'}</div>
                {!canManage && <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Your request will be reviewed by an admin before appearing on the calendar.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div><label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>EVENT TYPE *</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['game', 'practice', 'other'].map(k => {
                        const es = getEventStyle(k);
                        const isSel = newEvent.eventType === k;
                        const isOutline = es.chipBg === 'transparent';
                        const selBg = isOutline ? '#EDEDEB' : es.chipBg;
                        const selBorder = isOutline ? '#888780' : es.chipBg;
                        const selText = isOutline ? '#444441' : '#fff';
                        return (
                          <div key={k} onClick={() => setNewEvent(Object.assign({}, newEvent, { eventType: k }))} style={{ flex: 1, textAlign: 'center', padding: '8px 4px', borderRadius: R.control, border: '2px solid ' + (isSel ? selBorder : C.border), background: isSel ? selBg : C.white, color: isSel ? selText : C.text, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{es.label}</div>
                        );
                      })}
                    </div>
                  </div>
                  <input placeholder="Event title *" value={newEvent.title} onChange={e => setNewEvent(Object.assign({}, newEvent, { title: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, overflow: 'hidden', width: '100%', alignItems: 'start' }}>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>DATE(S) *</label>
                      {!newEventRecurrence.enabled && (newEvent.dates||['']).map((d, idx) => (
                        <div key={idx} style={{ marginBottom: 4 }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="date" value={d} onChange={e => { const nd = [...(newEvent.dates||[''])]; nd[idx]=e.target.value; setNewEvent(Object.assign({}, newEvent, { dates: nd })); }} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + (d && datesConflictInfo[d] ? C.amberBd : C.border), fontSize: 14, fontFamily: 'inherit', flex: 1, boxSizing: 'border-box', outline: 'none' }} />
                            {(newEvent.dates||['']).length > 1 && <button onClick={() => { const nd=[...(newEvent.dates||[''])]; nd.splice(idx,1); setNewEvent(Object.assign({}, newEvent, {dates:nd})); }} style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, width: 28, height: 28, cursor: 'pointer', fontSize: 14, color: C.redDark }}>✕</button>}
                          </div>
                          {d && datesConflictInfo[d] && (
                            <div style={{ fontSize: 11, color: C.amber, marginTop: 2 }}>⚠️ Conflicts with an existing {datesConflictInfo[d].source === 'game' ? 'scheduled game' : 'event'}</div>
                          )}
                        </div>
                      ))}
                      {newEventRecurrence.enabled && (
                        <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', padding: '9px 0' }}>Set by the recurrence pattern below.</div>
                      )}
                      {!editingEvent && !newEventRecurrence.enabled && <button onClick={() => setNewEvent(Object.assign({}, newEvent, { dates: [...(newEvent.dates||['']), ''] }))} style={{ fontSize: 12, color: C.green, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'inherit' }}>+ Add another date</button>}
                      {!editingEvent && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, fontWeight: 700, color: '#111111', cursor: 'pointer' }}>
                          <input type="checkbox" checked={newEventRecurrence.enabled} onChange={e => {
                            const enabled = e.target.checked;
                            setExcludedPreviewDates([]);
                            if (!enabled) { setNewEventRecurrence(Object.assign({}, newEventRecurrence, { enabled: false })); return; }
                            const refDate = (newEvent.dates && newEvent.dates[0]) || new Date().toISOString().split('T')[0];
                            const refDow = new Date(refDate + 'T00:00:00').getDay();
                            setNewEventRecurrence(Object.assign({}, newEventRecurrence, { enabled: true, daysOfWeek: newEventRecurrence.daysOfWeek.length ? newEventRecurrence.daysOfWeek : [refDow], startDate: newEventRecurrence.startDate || refDate }));
                          }} />
                          <RepeatIcon size={14} strokeWidth={2.25} style={{ flexShrink: 0 }} />
                          Repeats
                        </label>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>FIELD *</label>
                      <select value={newEvent.field || (fields[0]||'')} onChange={e => setNewEvent(Object.assign({}, newEvent, { field: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 14, background: C.white, fontFamily: 'inherit', width: '100%' }}>
                        {fields.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                  {!editingEvent && newEventRecurrence.enabled && (
                    <div style={{ background: '#F5F5F4', border: '1px solid ' + C.border, borderRadius: R.control, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>PATTERN</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {[{ k: 'weekly', l: 'Weekly' }, { k: 'biweekly', l: 'Bi-weekly' }, { k: 'monthly', l: 'Monthly' }, { k: 'custom', l: 'Custom' }].map(p => {
                            const isSel = newEventRecurrence.pattern === p.k;
                            return (
                              <div key={p.k} onClick={() => setNewEventRecurrence(Object.assign({}, newEventRecurrence, { pattern: p.k }))} style={{ padding: '6px 12px', borderRadius: R.control, border: '2px solid ' + (isSel ? C.green : C.border), background: isSel ? C.greenLt : C.white, color: isSel ? C.green : C.text, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{p.l}</div>
                            );
                          })}
                        </div>
                      </div>
                      {(newEventRecurrence.pattern === 'weekly' || newEventRecurrence.pattern === 'biweekly') && (
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>DAY OF WEEK</label>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((lbl, dow) => {
                              const isSel = newEventRecurrence.daysOfWeek.includes(dow);
                              return (
                                <div key={dow} onClick={() => setNewEventRecurrence(Object.assign({}, newEventRecurrence, { daysOfWeek: [dow] }))} style={{ width: 40, textAlign: 'center', padding: '6px 0', borderRadius: R.control, border: '2px solid ' + (isSel ? C.green : C.border), background: isSel ? C.greenLt : C.white, color: isSel ? C.green : C.text, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{lbl}</div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {newEventRecurrence.pattern === 'custom' && (
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>DAYS OF WEEK</label>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((lbl, dow) => {
                              const isSel = newEventRecurrence.daysOfWeek.includes(dow);
                              return (
                                <div key={dow} onClick={() => setNewEventRecurrence(Object.assign({}, newEventRecurrence, { daysOfWeek: isSel ? newEventRecurrence.daysOfWeek.filter(x => x !== dow) : [...newEventRecurrence.daysOfWeek, dow].sort() }))} style={{ width: 40, textAlign: 'center', padding: '6px 0', borderRadius: R.control, border: '2px solid ' + (isSel ? C.green : C.border), background: isSel ? C.greenLt : C.white, color: isSel ? C.green : C.text, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{lbl}</div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {newEventRecurrence.pattern === 'monthly' && (
                        <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Repeats monthly on the same day of the month as the start date below.</div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>START DATE</label>
                          <input type="date" value={newEventRecurrence.startDate} onChange={e => setNewEventRecurrence(Object.assign({}, newEventRecurrence, { startDate: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>END DATE</label>
                          <input type="date" value={newEventRecurrence.endDate} onChange={e => setNewEventRecurrence(Object.assign({}, newEventRecurrence, { endDate: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' }} />
                        </div>
                      </div>
                      {previewDates.length >= MAX_RECURRING_OCCURRENCES && (
                        <div style={{ fontSize: 11, color: C.amber }}>⚠️ Capped at {MAX_RECURRING_OCCURRENCES} occurrences — narrow the date range to generate fewer.</div>
                      )}
                    </div>
                  )}
                  {!editingEvent && (newEventRecurrence.enabled ? previewDates.length > 0 : previewDates.length > 1) && (
                    <div style={{ border: '1px solid ' + C.border, borderRadius: R.control, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                        This will create {finalDates.length} event{finalDates.length === 1 ? '' : 's'}
                        {previewDates.length !== finalDates.length ? ' (' + (previewDates.length - finalDates.length) + ' removed)' : ''}:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                        {previewDates.map(d => {
                          const excluded = excludedPreviewDates.includes(d);
                          const conflict = datesConflictInfo[d];
                          return (
                            <div key={d} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '5px 8px', borderRadius: R.control, background: excluded ? '#F5F5F4' : (conflict ? C.amberLt : C.greenLt), opacity: excluded ? 0.6 : 1 }}>
                              <div style={{ fontSize: 12, color: C.text, textDecoration: excluded ? 'line-through' : 'none' }}>
                                {d}{conflict && !excluded ? ' — ⚠️ conflicts with ' + (conflict.source === 'game' ? 'a scheduled game' : 'an existing event') : ''}
                              </div>
                              <button onClick={() => setExcludedPreviewDates(prev => excluded ? prev.filter(x => x !== d) : [...prev, d])} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: excluded ? C.green : C.redDark, fontFamily: 'inherit' }}>{excluded ? 'Restore' : 'Remove'}</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, overflow: 'hidden', width: '100%' }}>
                    <div style={{ minWidth: 0 }}><label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>START TIME</label><input type="time" value={newEvent.startTime} onChange={e => setNewEvent(Object.assign({}, newEvent, { startTime: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, WebkitAppearance: 'none', border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' }} /></div>
                    <div style={{ minWidth: 0 }}><label style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>END TIME</label><input type="time" value={newEvent.endTime} onChange={e => setNewEvent(Object.assign({}, newEvent, { endTime: e.target.value }))} style={{ padding: '9px 12px', borderRadius: R.control, WebkitAppearance: 'none', border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' }} /></div>
                  </div>
                  <textarea placeholder="Notes (optional)" value={newEvent.notes} onChange={e => setNewEvent(Object.assign({}, newEvent, { notes: e.target.value }))} rows={2} style={{ padding: '9px 12px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 14, fontFamily: 'inherit', resize: 'none', outline: 'none' }} />
                  {canManage && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.text, letterSpacing: '0.08em', marginBottom: 8 }}>VOLUNTEER SLOTS</div>
                      {newEventSlots.map((slot, si) => (
                        <div key={si} style={{ background: C.greenLt, borderRadius: R.control, border: '1px solid ' + C.greenBd, padding: '10px 12px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: C.green }}>Slot {si + 1}</span>
                            <button onClick={() => setNewEventSlots(prev => prev.filter((_, i) => i !== si))} style={{ background: C.redLt, border: 'none', borderRadius: R.control, width: 24, height: 24, cursor: 'pointer', fontSize: 14, color: C.redDark, fontFamily: 'inherit' }}>✕</button>
                          </div>
                          <input placeholder="Task name (e.g. Gate Duty, Concessions...)" value={slot.task} onChange={e => { const u = [...newEventSlots]; u[si] = Object.assign({}, u[si], { task: e.target.value }); setNewEventSlots(u); }} style={{ padding: '7px 10px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                          <input placeholder="Location (e.g. Main Gate, Concession Stand, Parking Lot...)" value={slot.location || ''} onChange={e => { const u = [...newEventSlots]; u[si] = Object.assign({}, u[si], { location: e.target.value }); setNewEventSlots(u); }} style={{ padding: '7px 10px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr 1fr 1fr', gap: 8, width: '100%', overflow: 'hidden' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 3 }}>START</div>
                              <input type="time" value={slot.startTime} onChange={e => { const u = [...newEventSlots]; u[si] = Object.assign({}, u[si], { startTime: e.target.value }); setNewEventSlots(u); }} style={{ padding: '7px 8px', borderRadius: R.control, WebkitAppearance: 'none', border: '1px solid ' + C.greenBd, background: C.white, fontSize: 12, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 3 }}>END</div>
                              <input type="time" value={slot.endTime} onChange={e => { const u = [...newEventSlots]; u[si] = Object.assign({}, u[si], { endTime: e.target.value }); setNewEventSlots(u); }} style={{ padding: '7px 8px', borderRadius: R.control, WebkitAppearance: 'none', border: '1px solid ' + C.greenBd, background: C.white, fontSize: 12, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', marginBottom: 3 }}>SPOTS NEEDED</div>
                              <input type="number" min="1" max="50" value={slot.spotsNeeded} onChange={e => { const u = [...newEventSlots]; u[si] = Object.assign({}, u[si], { spotsNeeded: parseInt(e.target.value) || 1 }); setNewEventSlots(u); }} style={{ padding: '7px 8px', borderRadius: R.control, border: '1px solid ' + C.greenBd, background: C.white, fontSize: 12, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => setNewEventSlots(prev => [...prev, { id: 'slot_' + Date.now(), task: '', location: '', startTime: newEvent.startTime || '09:00', endTime: newEvent.endTime || '10:00', spotsNeeded: 1, signups: [] }])} style={{ background: C.greenMid, border: 'none', borderRadius: R.control, padding: '8px', width: '100%', color: '#0D0D0D', fontWeight: 800, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Volunteer Slot</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn onClick={saveEvent} disabled={!newEvent.title.trim() || finalDates.length === 0 || savingEvent} full>{savingEvent ? 'Saving…' : (canManage ? 'Save Event' : 'Submit Request')}</Btn>
                    <Btn onClick={() => { setShowEventForm(false); setEditingEvent(null); setCalImageData(''); }} variant="ghost">Cancel</Btn>
                  </div>
                </div>
              </div>
            )}
            {/* Event Detail Panel */}
            {viewingEvent && (
              <div style={{ background: C.white, border: '1px solid ' + C.border, borderRadius: R.card, padding: 16, marginBottom: 14, position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 12, height: 12, borderRadius: R.control, background: getEventStyle(viewingEvent.eventType).chipBg, border: getEventStyle(viewingEvent.eventType).chipBorder !== 'none' ? getEventStyle(viewingEvent.eventType).chipBorder : 'none', flexShrink: 0, boxSizing: 'border-box' }} />
                      <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{viewingEvent.title}</div>
                    </div>
                    <div style={{ fontSize: 13, color: C.muted }}>📍 {viewingEvent.field} · {viewingEvent.date} · {fmtTime(viewingEvent.startTime)}{viewingEvent.endTime ? ' – ' + fmtTime(viewingEvent.endTime) : ''}</div>
                    {viewingEvent.notes && <div style={{ fontSize: 13, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>{viewingEvent.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {canManage && <button onClick={() => { setEditingEvent(viewingEvent); setCalImageData(viewingEvent.image||''); setNewEvent({ title: viewingEvent.title, field: viewingEvent.field, dates: [viewingEvent.date], startTime: viewingEvent.startTime||'09:00', endTime: viewingEvent.endTime||'10:00', notes: viewingEvent.notes||'', eventType: viewingEvent.eventType||'game' }); setNewEventSlots(viewingEvent.slots || []); setNewEventRecurrence(EMPTY_RECURRENCE); setDatesConflictInfo({}); setExcludedPreviewDates([]); setShowEventForm(true); setViewingEvent(null); }} style={{ background: C.blueLt, border: '1px solid ' + C.blueBd, borderRadius: R.control, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.blue, fontFamily: 'inherit' }}>✏️ Edit</button>}
                    <button onClick={() => setViewingEvent(null)} style={{ background: C.grayLt, border: 'none', borderRadius: R.control, width: 28, height: 28, cursor: 'pointer', fontSize: 16, color: C.muted, fontFamily: 'inherit' }}>✕</button>
                  </div>
                </div>
                {(!viewingEvent.slots || viewingEvent.slots.length === 0) ? (
                  <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', padding: '8px 0' }}>
                    {canManage ? 'No volunteer slots added. Edit this event to add slots.' : 'No volunteer slots for this event.'}
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: C.text, letterSpacing: '0.08em', marginBottom: 8 }}>VOLUNTEER SLOTS</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {viewingEvent.slots.map(slot => {
                        const signups = slot.signups || [];
                        const isFull = signups.length >= slot.spotsNeeded;
                        const iSignedUp = signups.some(sg => sg.name === user.name);
                        const spotsLeft = slot.spotsNeeded - signups.length;
                        return (
                          <div key={slot.id} style={{ background: C.white, borderRadius: R.card, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', opacity: isFull && !iSignedUp ? 0.85 : 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{slot.task}</div>
                                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{fmtTime(slot.startTime)} – {fmtTime(slot.endTime)}{slot.location ? ' · 📍 ' + slot.location : ''}</div>
                                <div style={{ fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ background: isFull ? C.redLt : C.greenLt, color: isFull ? C.redDark : C.green, borderRadius: R.control, padding: '2px 8px', fontWeight: 700, fontSize: 11 }}>{isFull ? 'Full' : spotsLeft + ' spot' + (spotsLeft !== 1 ? 's' : '') + ' left'}</span>
                                  {iSignedUp && <span style={{ background: C.greenLt, color: C.green, borderRadius: R.control, padding: '2px 8px', fontWeight: 700, fontSize: 11 }}>✓ You're in</span>}
                                </div>
                                {/* Literal 3, not an R token: this is half of height 5, i.e. a capsule cap on a progress
                                    indicator, not a control corner. Squaring it also squares the inner fill via overflow: hidden. */}
                                <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden', marginTop: 6, width: '100%', maxWidth: 200 }}>
                                  <div style={{ width: Math.min(100, (signups.length / slot.spotsNeeded) * 100) + '%', height: '100%', background: isFull ? C.muted : C.greenMid }} />
                                </div>
                                {signups.length > 0 && (
                                  <div style={{ display: 'flex', marginTop: 8 }}>
                                    {signups.slice(0, 4).map((sg, avi) => {
                                      const initials = sg.name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                                      const avPalette = [C.blue, C.amber, C.green, C.red, C.purple];
                                      return (
                                        <div key={sg.name + avi} title={sg.name} style={{ width: 22, height: 22, borderRadius: '50%', background: avPalette[avi % avPalette.length], color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid ' + C.white, marginLeft: avi === 0 ? 0 : -6 }}>{initials}</div>
                                      );
                                    })}
                                    {signups.length > 4 && (
                                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.muted, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid ' + C.white, marginLeft: -6 }}>+{signups.length - 4}</div>
                                    )}
                                  </div>
                                )}
                                {signups.length > 0 && (
                                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {signups.map((sg, sgi) => (
                                      <div key={sg.name + sgi} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ background: C.greenLt, border: '1px solid ' + C.greenBd, borderRadius: R.control, padding: '2px 10px', fontSize: 11, color: C.green, fontWeight: 700, flex: 1 }}>✓ {sg.name}</span>
                                        {canManage && (
                                          <>
                                            <button onClick={() => {
                                              const newName = window.prompt('Edit volunteer name:', sg.name);
                                              if (!newName || !newName.trim()) return;
                                              const updatedSlots = (viewingEvent.slots || []).map(s => {
                                                if (s.id !== slot.id) return s;
                                                const updatedSignups = s.signups.map((x, xi) => xi === sgi ? Object.assign({}, x, { name: newName.trim() }) : x);
                                                return Object.assign({}, s, { signups: updatedSignups });
                                              });
                                              updateDoc(doc(db, 'leagues', lid, 'fieldEvents', viewingEvent.id), { slots: updatedSlots });
                                              setViewingEvent(Object.assign({}, viewingEvent, { slots: updatedSlots }));
                                            }} style={{ background: C.blueLt, border: '1px solid ' + C.blueBd, borderRadius: R.control, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: C.blue, fontWeight: 700, fontFamily: 'inherit' }}>Edit</button>
                                            <button onClick={() => {
                                              showConfirm('Remove Volunteer', 'Remove ' + sg.name + ' from this slot?', async () => {
                                              const updatedSlots = (viewingEvent.slots || []).map(s => {
                                                if (s.id !== slot.id) return s;
                                                return Object.assign({}, s, { signups: s.signups.filter((_, xi) => xi !== sgi) });
                                              });
                                              updateDoc(doc(db, 'leagues', lid, 'fieldEvents', viewingEvent.id), { slots: updatedSlots });
                                              setViewingEvent(Object.assign({}, viewingEvent, { slots: updatedSlots }));
                                              }, { danger: true, confirmLabel: 'Remove' });
                                            }} style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: C.redDark, fontWeight: 700, fontFamily: 'inherit' }}>✕</button>
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {canManage && (slot.signups || []).length < slot.spotsNeeded && (
                                  <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                                    <input
                                      id={'add-vol-' + slot.id}
                                      placeholder="Add volunteer by name..."
                                      style={{ flex: 1, padding: '5px 10px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
                                      onKeyDown={e => {
                                        if (e.key !== 'Enter') return;
                                        const name = e.target.value.trim();
                                        if (!name) return;
                                        if ((slot.signups || []).some(sg => sg.name === name)) { alert(name + ' is already signed up.'); return; }
                                        if ((slot.signups || []).length >= slot.spotsNeeded) { alert('This slot is already full (' + slot.spotsNeeded + ' spot' + (slot.spotsNeeded !== 1 ? 's' : '') + ').'); return; }
                                        const updatedSlots = (viewingEvent.slots || []).map(s => {
                                          if (s.id !== slot.id) return s;
                                          return Object.assign({}, s, { signups: [...(s.signups || []), { name, signedUpAt: new Date().toISOString(), addedByAdmin: true }] });
                                        });
                                        updateDoc(doc(db, 'leagues', lid, 'fieldEvents', viewingEvent.id), { slots: updatedSlots });
                                        setViewingEvent(Object.assign({}, viewingEvent, { slots: updatedSlots }));
                                        e.target.value = '';
                                      }}
                                    />
                                    <button onClick={() => {
                                      const input = document.getElementById('add-vol-' + slot.id);
                                      const name = input.value.trim();
                                      if (!name) return;
                                      if ((slot.signups || []).some(sg => sg.name === name)) { alert(name + ' is already signed up.'); return; }
                                      if ((slot.signups || []).length >= slot.spotsNeeded) { alert('This slot is already full (' + slot.spotsNeeded + ' spot' + (slot.spotsNeeded !== 1 ? 's' : '') + ').'); return; }
                                      const updatedSlots = (viewingEvent.slots || []).map(s => {
                                        if (s.id !== slot.id) return s;
                                        return Object.assign({}, s, { signups: [...(s.signups || []), { name, signedUpAt: new Date().toISOString(), addedByAdmin: true }] });
                                      });
                                      updateDoc(doc(db, 'leagues', lid, 'fieldEvents', viewingEvent.id), { slots: updatedSlots });
                                      setViewingEvent(Object.assign({}, viewingEvent, { slots: updatedSlots }));
                                      input.value = '';
                                    }} style={{ background: C.greenMid, border: 'none', borderRadius: R.control, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.text, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>+ Add</button>
                                  </div>
                                )}
                                {canManage && (slot.signups || []).length >= slot.spotsNeeded && (
                                  <div style={{ marginTop: 8, fontSize: 12, color: C.muted, fontStyle: 'italic' }}>This slot is full.</div>
                                )}
                              </div>
                              <div>
                                {iSignedUp ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                                    {signups.find(sg => sg.name === user.name)?.comment && <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', maxWidth: 180, textAlign: 'right' }}>"{signups.find(sg => sg.name === user.name).comment}"</div>}
                                    <button onClick={() => cancelSlotSignup(viewingEvent, slot.id)} style={{ background: C.redLt, border: '1px solid ' + C.redBd, borderRadius: R.control, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.redDark, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Cancel Signup</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                                    <input id={'comment-' + slot.id} placeholder="Add a note (optional)" style={{ padding: '5px 8px', borderRadius: R.control, border: '1px solid ' + C.border, fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 160 }} />
                                    <button onClick={() => { if (!isFull) { const comment = document.getElementById('comment-' + slot.id)?.value || ''; signupForSlot(viewingEvent, slot.id, comment); } }} disabled={isFull} style={{ background: isFull ? C.grayLt : C.greenMid, border: 'none', borderRadius: R.control, padding: '6px 14px', cursor: isFull ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, color: isFull ? C.muted : C.text, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{isFull ? 'Full' : '+ Sign Up'}</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {fields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: C.muted, fontSize: 14 }}>No field locations set up yet. Add them in ⚙️ Settings.</div>
            ) : isMobile ? (
              <div>
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 14, WebkitOverflowScrolling: 'touch' }}>
                  {days.map((d, i) => {
                    const dSel = d.toDateString() === selectedMobileDay.toDateString();
                    const dToday = isToday(d);
                    const dayTypesPresent = [...new Set(fields.flatMap(field => getEventsForFieldDay(field, d).map(ev => ev.eventType || 'game')))];
                    return (
                      <button key={i} onClick={() => setSelectedMobileDay(d)}
                        style={{ flexShrink: 0, minWidth: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '8px 4px', borderRadius: R.control, border: dSel && !dToday ? '1.5px solid #75C83C' : '1.5px solid transparent', background: dToday ? '#75C83C' : dSel ? '#EAF7DF' : C.white, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: dToday ? '#111111' : C.muted }}>{fmtDay(d).toUpperCase()}</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: dToday ? '#111111' : C.text }}>{d.getDate()}</span>
                        <span style={{ display: 'flex', gap: 2, height: 5 }}>
                          {dayTypesPresent.slice(0, 3).map(t => {
                            const es = getEventStyle(t);
                            return <span key={t} style={{ width: 5, height: 5, borderRadius: '50%', background: es.chipBg === 'transparent' ? '#888780' : es.chipBg }} />;
                          })}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const dateStr = selectedMobileDay.toISOString().split('T')[0];
                  const closuresForDay = fields.filter(field => fieldClosures.some(cl => cl.field === field && cl.date === dateStr));
                  const dayEvents = fields.flatMap(field => getEventsForFieldDay(field, selectedMobileDay).map(ev => Object.assign({}, ev, { field })));
                  const addEventForDay = (field) => { setEditingEvent(null); setCalImageData(''); setNewEvent({ title: '', field: field || fields[0] || '', dates: [dateStr], startTime: '09:00', endTime: '10:00', notes: '', eventType: 'game' }); setNewEventRecurrence(EMPTY_RECURRENCE); setDatesConflictInfo({}); setExcludedPreviewDates([]); setShowEventForm(true); };
                  if (closuresForDay.length === 0 && dayEvents.length === 0) {
                    return (
                      <button onClick={() => addEventForDay()} style={{ width: '100%', textAlign: 'center', padding: '2rem 1rem', color: C.muted, fontSize: 13, background: C.grayLt, border: 'none', borderRadius: R.control, cursor: 'pointer', fontFamily: 'inherit' }}>
                        No events on {fmtDay(selectedMobileDay)}, {fmt(selectedMobileDay)}. Tap to add one.
                      </button>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {closuresForDay.map(field => {
                        const cl = fieldClosures.find(c => c.field === field && c.date === dateStr);
                        return (
                          <div key={'closed-' + field} style={{ background: '#FCEBEB', border: '1px solid #E24B4A', borderRadius: R.control, padding: '10px 12px', fontSize: 12, color: '#501313' }}>
                            <span style={{ fontWeight: 800 }}>🚫 {field} closed</span>{cl && cl.reason ? ' — ' + cl.reason : ''}
                          </div>
                        );
                      })}
                      {dayEvents.map(ev => {
                        const isPending = ev.status === 'pending';
                        const canDelete = canManage || (ev.createdBy === (user && user.name));
                        const es = getEventStyle(ev.eventType);
                        return (
                          <div key={ev.id} onClick={() => setViewingEvent(ev)}
                            style={{ background: isPending ? C.grayBd : (es.chipBg === 'transparent' ? C.white : es.chipBg), border: es.chipBorder !== 'none' ? es.chipBorder : 'none', borderRadius: R.control, padding: '10px 12px', cursor: 'pointer', boxSizing: 'border-box' }}>
                            {isPending && <div style={{ fontSize: 9, fontWeight: 800, color: C.amber, background: C.amberLt, borderRadius: R.control, padding: '1px 4px', display: 'inline-block', marginBottom: 4 }}>PENDING</div>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: isPending ? C.text : es.textMain }}>{ev.title}</div>
                              {canDelete && <div onClick={e => { e.stopPropagation(); deleteEvent(ev); }} style={{ fontSize: 11, color: isPending ? C.muted : es.textFaint, cursor: 'pointer', flexShrink: 0 }}>✕</div>}
                            </div>
                            <div style={{ fontSize: 11, color: isPending ? C.muted : es.textSub, marginTop: 2 }}>{ev.field}{ev.startTime ? ' · ' + fmtTime(ev.startTime) + (ev.endTime ? ' – ' + fmtTime(ev.endTime) : '') : ''}</div>
                            <div style={{ fontSize: 11, color: isPending ? C.muted : es.textFaint, marginTop: 2 }}>by {ev.createdBy}</div>
                          </div>
                        );
                      })}
                      <button onClick={() => addEventForDay()} style={{ width: '100%', textAlign: 'center', padding: 10, background: C.grayLt, border: 'none', borderRadius: R.control, color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>+ Add event for this day</button>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px repeat(7, minmax(110px, 1fr))', marginBottom: 6, minWidth: 860 }}>
                  <div />
                  {days.map((d, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '6px 4px 4px', margin: '0 2px', borderBottom: '3px solid ' + (isToday(d) ? '#75C83C' : 'transparent') }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '0.06em' }}>{fmtDay(d).toUpperCase()}</div>
                      <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, lineHeight: 1.1, color: C.text }}>{d.getDate()}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{d.toLocaleDateString('en-US',{month:'short'})}</div>
                    </div>
                  ))}
                </div>
                {fields.map(field => (
                  <div key={field} style={{ display: 'grid', gridTemplateColumns: '90px repeat(7, minmax(110px, 1fr))', marginBottom: 4, alignItems: 'stretch', minWidth: 860, borderTop: '1px solid ' + C.border, paddingTop: 8 }}>
                    <div style={{ padding: '8px 6px', fontSize: 12, fontWeight: 700, borderRadius: R.control, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginRight: 4, background: fieldClosures.some(cl => cl.field === field && cl.date === getLocalDateStr()) ? '#FCEBEB' : C.grayLt, color: fieldClosures.some(cl => cl.field === field && cl.date === getLocalDateStr()) ? '#501313' : C.text }}>
                      {fieldClosures.some(cl => cl.field === field && cl.date === getLocalDateStr()) && <span style={{ fontSize: 9, fontWeight: 800, marginBottom: 2 }}>CLOSED</span>}
                      {field}
                    </div>
                    {days.map((d, i) => {
                      const evs = getEventsForFieldDay(field, d);
                      const dateStr = d.toISOString().split('T')[0];
                      const isClosed = fieldClosures.some(cl => cl.field === field && cl.date === dateStr);
                      return (
                        <div key={i} onClick={() => { if (evs.length === 0 && !isClosed) { setEditingEvent(null); setCalImageData(''); setNewEvent({ title:'', field, dates:[dateStr], startTime:'09:00', endTime:'10:00', notes:'', eventType:'game' }); setNewEventRecurrence(EMPTY_RECURRENCE); setDatesConflictInfo({}); setExcludedPreviewDates([]); setShowEventForm(true); } }}
                          style={{ minHeight: 52, background: isClosed ? '#FCEBEB' : isToday(d) ? '#E8F9ED' : C.grayLt, borderRadius: R.control, padding: 4, margin: '0 2px', cursor: evs.length===0 && !isClosed ? 'pointer' : 'default', border: '1px solid ' + (isClosed ? '#E24B4A' : isToday(d) ? C.greenBd : 'transparent'), boxSizing: 'border-box' }}>
                          {isClosed && (<div style={{ background: C.redFill, borderRadius: R.control, padding: '4px 6px', marginBottom: 2 }}><div style={{ fontSize: 10, fontWeight: 800, color: '#fff' }}>🚫 CLOSED</div>{fieldClosures.find(cl => cl.field === field && cl.date === dateStr) && (<div style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)' }}>{fieldClosures.find(cl => cl.field === field && cl.date === dateStr).reason}</div>)}</div>)}
                          {!isClosed && evs.length === 0 && <div style={{ height: '100%', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 20, opacity: 0.4 }}>+</div>}
                          {evs.map(ev => {
                            const isPending = ev.status === 'pending';
                            const canDelete = canManage || (ev.createdBy === (user && user.name));
                            const es = getEventStyle(ev.eventType);
                            return (
                              <div key={ev.id} onClick={e => { e.stopPropagation(); setViewingEvent(ev); }}
                                style={{ background: isPending ? C.grayBd : (es.chipBg === 'transparent' ? C.white : es.chipBg), border: !isPending && es.chipBorder !== 'none' ? es.chipBorder : 'none', borderRadius: R.control, padding: '5px 7px', marginBottom: 3, cursor: canManage ? 'pointer' : 'default', opacity: isPending ? 0.8 : 1, boxSizing: 'border-box' }}>
                                {isPending && <div style={{ fontSize: 9, fontWeight: 800, color: C.amber, background: C.amberLt, borderRadius: R.control, padding: '1px 4px', display: 'inline-block', marginBottom: 2 }}>PENDING</div>}
                                <div style={{ fontSize: 11, fontWeight: 700, color: isPending ? C.text : es.textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
                                {ev.notes && <div style={{ fontSize: 10, fontWeight: 600, color: isPending ? C.muted : es.textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.notes}</div>}
                                {ev.startTime && <div style={{ fontSize: 10, color: isPending ? C.muted : es.textSub }}>{fmtTime(ev.startTime)}{ev.endTime ? ' – ' + fmtTime(ev.endTime) : ''}</div>}
                                <div style={{ fontSize: 10, color: isPending ? C.muted : es.textFaint, marginTop: 2 }}>by {ev.createdBy}</div>
                                {canDelete && <div onClick={e => { e.stopPropagation(); deleteEvent(ev); }} style={{ fontSize: 10, color: isPending ? C.muted : es.textFaint, cursor: 'pointer', marginTop: 2 }}>✕ remove</div>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div style={{ borderTop: '1px solid ' + C.border, minWidth: 860 }} />
              </div>
            )}

            {myEvents.length > 0 && (
              <div style={{ marginTop: 16, background: C.white, border: '0.5px solid ' + C.border, borderRadius: R.card, overflow: 'hidden' }}>
                <div onClick={() => setShowMyEvents(!showMyEvents)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: C.text, letterSpacing: '0.02em' }}>MY EVENTS</span>
                    {myEventsPendingCount > 0 && (
                      <span style={{ background: C.amberLt, color: C.amber, borderRadius: R.control, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{myEventsPendingCount} pending</span>
                    )}
                  </div>
                  {showMyEvents ? <ChevronUpIcon size={18} strokeWidth={2} color={C.muted} /> : <ChevronDownIcon size={18} strokeWidth={2} color={C.muted} />}
                </div>
                {showMyEvents && (
                  <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                    {myEvents.map(ev => (
                      <div key={ev.id} onClick={() => setViewingEvent(ev)}
                        style={{ background: C.white, border: '0.5px solid ' + C.border, borderRadius: R.card, padding: '8px 10px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{ev.title}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                              {new Date(ev.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              {ev.startTime ? ' · ' + fmtTime(ev.startTime) + (ev.endTime ? ' – ' + fmtTime(ev.endTime) : '') : ''}
                              {' · ' + ev.field}
                            </div>
                          </div>
                          <span style={{ flexShrink: 0, background: ev.status === 'pending' ? C.amberLt : C.greenLt, color: ev.status === 'pending' ? C.amber : C.green, borderRadius: R.control, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
                            {ev.status === 'pending' ? 'Pending' : 'Approved'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "crewstats" && (() => {
        const completedTasks = archive.filter(t => t.status === "complete" || t.status === "completed");
        // Build full member list including admin and co-admins
        const adminEntry = league ? [{ name: league.adminName, role: 'admin' }] : [];
        const coAdminEntries = (league && league.coAdmins) ? league.coAdmins.map(ca => ({ name: ca.name, role: 'coadmin' })) : [];
        const allCrew = [...adminEntry, ...coAdminEntries, ...members.map(m => ({ name: m.name, role: m.role }))];
        // Deduplicate by name
        const seenNames = new Set();
        const uniqueCrew = allCrew.filter(m => { if (seenNames.has(m.name)) return false; seenNames.add(m.name); return true; });
        // Include one-off/guest volunteers who were entered directly into an event slot
        // without ever creating a GroundsPal account. They only ever accrue event hours
        // (never task completions) and won't receive notifications, since there's no
        // contact info on file for them -- but they still deserve credit for their time.
        const guestNames = new Set();
        (fieldEvents || []).forEach(ev => {
          (ev.slots || []).forEach(slot => {
            (slot.signups || []).forEach(sg => {
              if (sg.name && !seenNames.has(sg.name) && !guestNames.has(sg.name)) guestNames.add(sg.name);
            });
          });
        });
        guestNames.forEach(name => uniqueCrew.push({ name, role: 'guest' }));
        const getCompletedForUser = (name) => completedTasks.filter(t => {
          // Credit always goes to assignedTo - if task was assigned to someone, they get the credit
          if (t.assignedTo && t.assignedTo === name) return true;
          // Only count completedBy if there was no assigned person (self-completed tasks)
          if (!t.assignedTo && t.completedBy === name) return true;
          // For old tasks with no assignedTo or completedBy, check updates
          if (!t.assignedTo && !t.completedBy && t.updates) {
            return t.updates.some(u => u.author === name && (u.message||'').toLowerCase().includes('complet'));
          }
          return false;
        });
        const timeToHours = (start, end) => {
          if (!start || !end) return 0;
          const [sh, sm] = start.split(':').map(Number);
          const [eh, em] = end.split(':').map(Number);
          const mins = (eh * 60 + em) - (sh * 60 + sm);
          if (mins <= 0) return 0;
          const rawHours = mins / 60;
          return Math.ceil(rawHours * 2) / 2; // always round UP to the nearest half hour, never down
        };
        const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
        const getEventHoursForUser = (name) => {
          let total = 0;
          (fieldEvents || []).forEach(ev => {
            if (ev.status !== 'approved') return; // skip cancelled/pending events
            const evDate = new Date(ev.date + 'T00:00:00');
            if (evDate >= todayMidnight) return; // only credit hours once the event date has passed
            (ev.slots || []).forEach(slot => {
              if ((slot.signups || []).some(sg => sg.name === name)) {
                total += timeToHours(slot.startTime, slot.endTime); // each slot already rounded up individually
              }
            });
          });
          return total;
        };
        const memberStats = uniqueCrew.map(m => ({
          name: m.name,
          role: m.role,
          completed: getCompletedForUser(m.name).length,
          eventHours: getEventHoursForUser(m.name),
          lastCompleted: getCompletedForUser(m.name).sort((a,b) => new Date(b.completedAt||b.createdAt) - new Date(a.completedAt||a.createdAt))[0]
        })).sort((a, b) => b.completed - a.completed);
        const sorted = crewSortAsc ? [...memberStats].sort((a,b) => a.completed - b.completed) : memberStats;
        const maxCount = memberStats[0] ? memberStats[0].completed : 1;
        const MEDALS = ['🥇','🥈','🥉'];
        if (!hasPro) {
          return (
            <div style={{ padding: '14px' }}>
              <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem 1.5rem", textAlign: "center" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🏅</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 6 }}>Crew Stats is a Pro feature</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
                  See a leaderboard of your most active volunteers, ranked by completed tasks. Upgrade to Pro to unlock this and more.
                </div>
                <Btn onClick={() => setScreen('show-code')} variant="primary">View Upgrade Options</Btn>
              </div>
            </div>
          );
        }
        return (
          <div style={{ padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>🏅 Crew Stats</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Tasks completed by each crew member</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Sort:</span>
                <button onClick={() => setCrewSortAsc(false)} style={{ padding: '5px 12px', borderRadius: R.control, border: '1px solid ' + (!crewSortAsc ? C.green : C.border), background: !crewSortAsc ? C.green : C.white, color: !crewSortAsc ? C.white : C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Most First</button>
                <button onClick={() => setCrewSortAsc(true)} style={{ padding: '5px 12px', borderRadius: R.control, border: '1px solid ' + (crewSortAsc ? C.green : C.border), background: crewSortAsc ? C.green : C.white, color: crewSortAsc ? C.white : C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Least First</button>
                <button onClick={() => exportCrewStatsCSV(memberStats)} title={hasLeague ? "Export as CSV" : "Upgrade to League to export"}
                  style={{ padding: '5px 12px', borderRadius: R.control, border: '1px solid ' + (hasLeague ? C.blueBd : C.border), background: hasLeague ? C.blueLt : C.grayLt, color: hasLeague ? C.blue : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                  ⬇️ Export CSV {!hasLeague && '🔒'}
                </button>
              </div>
            </div>
            {sorted.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: C.muted, fontSize: 14 }}>No crew members yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sorted.map((m, idx) => {
                  const rank = memberStats.findIndex(x => x.name === m.name);
                  const pct = maxCount > 0 ? (m.completed / maxCount) * 100 : 0;
                  return (
                    <div key={m.name} style={{ background: C.white, borderRadius: R.card, border: '1px solid ' + C.border, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: rank < 3 ? [C.amber, C.grayBd, '#cd7f32'][rank] : C.greenLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: rank < 3 ? 20 : 14, fontWeight: 800, color: rank < 3 ? C.white : C.green, flexShrink: 0 }}>
                          {rank < 3 ? MEDALS[rank] : rank + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{m.name}</div>
                          <div style={{ fontSize: 11, color: C.muted, textTransform: 'capitalize' }}>{m.role}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: T.display, fontSize: 24, fontWeight: 700, color: m.completed > 0 ? C.green : C.muted, lineHeight: 1 }}>{m.completed}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>task{m.completed !== 1 ? 's' : ''} done</div>
                          {m.eventHours > 0 && <div style={{ fontSize: 11, color: C.blue, marginTop: 2, fontWeight: 700 }}>{m.eventHours} event hr{m.eventHours !== 1 ? 's' : ''}</div>}
                        </div>
                      </div>

                      {m.lastCompleted && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Last: {m.lastCompleted.title} · {new Date(m.lastCompleted.completedAt||m.lastCompleted.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {tab === "waivers" && user && !hasPro && (
        <div style={{ padding: '14px' }}>
          <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem 1.5rem", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 6 }}>Waivers is a Pro feature</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Create waivers for volunteers and admins to review and sign, with a full record of who signed what and when. Upgrade to Pro to unlock this and more.
            </div>
            <Btn onClick={() => setScreen('show-code')} variant="primary">View Upgrade Options</Btn>
          </div>
        </div>
      )}
      {tab === "waivers" && user && hasPro && (
        <WaiversManagement lid={lid} user={user} />
      )}

      {tab === "team" && user && user.role === "admin" && (
        <div style={{ padding: "14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Volunteer Team</div>
              <div style={{ fontSize: 12, color: C.muted }}>Everyone who has joined your league</div>
            </div>
            <div style={{ background: C.greenLt, border: "1px solid " + C.greenBd, borderRadius: R.control, padding: "4px 12px", fontSize: 12, fontWeight: 700, color: C.green }}>
              {members.length} member{members.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Co-Admins Section */}
          {league && league.coAdmins && league.coAdmins.length > 0 && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>CO-ADMINS ({league.coAdmins.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {league.coAdmins.map((ca, i) => (
                  <div key={i} style={{ background: C.white, border: "1px solid " + C.purpleBd, borderLeft: "4px solid " + C.purple, borderRadius: R.card, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.purpleLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: C.purple, flexShrink: 0 }}>
                      {ca.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      {editingMemberId === 'co-' + ca.uid ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input value={editingMemberName} onChange={e => setEditingMemberName(e.target.value)} autoFocus
                            style={{ fontSize: 14, fontWeight: 700, padding: '4px 8px', borderRadius: R.control, border: '1px solid ' + C.border, fontFamily: 'inherit', flex: 1 }}
                            onKeyDown={e => { if (e.key === 'Enter') saveCoAdminName(ca.uid); if (e.key === 'Escape') setEditingMemberId(null); }} />
                          <button onClick={() => saveCoAdminName(ca.uid)} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: R.control, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
                          <button onClick={() => setEditingMemberId(null)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {ca.name}
                          <button onClick={() => { setEditingMemberId('co-' + ca.uid); setEditingMemberName(ca.name); }} title="Edit name" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.muted, padding: 0 }}>✏️</button>
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: C.muted }}>{ca.email}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: R.control, background: C.purpleLt, color: C.purple }}>Co-Admin</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Volunteers Section */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", marginBottom: 8 }}>VOLUNTEERS ({members.length})</div>
            {members.length === 0 ? (
              <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🙋</div>
                <div style={{ fontSize: 14, color: C.muted, marginBottom: 4 }}>No volunteers yet</div>
                <div style={{ fontSize: 12, color: C.muted }}>Share your league code from ⚙️ Settings to invite volunteers</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {members.map(m => (
                  <div key={m.id} style={{ background: C.white, border: "1px solid " + C.border, borderLeft: "4px solid " + C.greenMid, borderRadius: R.card, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.greenBd}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.greenLt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: C.green, flexShrink: 0 }}>
                      {m.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      {editingMemberId === m.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                          <input value={editingMemberName} onChange={e => setEditingMemberName(e.target.value)} autoFocus
                            style={{ fontSize: 14, fontWeight: 700, padding: '4px 8px', borderRadius: R.control, border: '1px solid ' + C.border, fontFamily: 'inherit', flex: 1 }}
                            onKeyDown={e => { if (e.key === 'Enter') saveMemberName(m.id); if (e.key === 'Escape') setEditingMemberId(null); }} />
                          <button onClick={() => saveMemberName(m.id)} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: R.control, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
                          <button onClick={() => setEditingMemberId(null)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {m.name}
                          <button onClick={() => { setEditingMemberId(m.id); setEditingMemberName(m.name); }} title="Edit name" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.muted, padding: 0 }}>✏️</button>
                        </div>
                      )}
                      {m.email && <div style={{ fontSize: 12, color: C.muted }}>{m.email}</div>}
                      <div style={{ fontSize: 12, color: C.muted }}>
                        Joined {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "recently"}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {tasks.filter(t => t.assignedTo === m.name).length > 0 &&
                          <span style={{ background: C.amberLt, color: C.amber, borderRadius: R.control, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                            {tasks.filter(t => t.assignedTo === m.name).length} active
                          </span>
                        }
                        {archive.filter(t => t.assignedTo === m.name).length > 0 &&
                          <span style={{ background: C.greenLt, color: C.green, borderRadius: R.control, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                            {archive.filter(t => t.assignedTo === m.name).length} done
                          </span>
                        }
                      </div>
                      <button onClick={() => {
                        showConfirm("Remove Volunteer", "Remove " + m.name + " from the league? They will need to rejoin with the invite code.", () => removeVolunteer(m.id), { danger: true, confirmLabel: "Remove" });
                      }} style={{ background: C.redLt, border: "1px solid " + C.redBd, borderRadius: R.control, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: C.redDark, cursor: "pointer", fontFamily: "inherit" }}>
                        Remove
                      </button>
                    </div>
                    </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── COACHES TAB ─────────────────────────────────────────── */}
      {/* Open to any signed-in user (volunteer, coadmin, or admin) -- unlike
          Teams/Scheduler/Tryouts, this section isn't restricted to admins.
          "Coaches" describes the toolset (tactics board, practice plans,
          etc.), not who's allowed in -- any team's head/assistant coach may
          only have a volunteer account, so gating this to admin would lock
          out the actual target user. */}
      {tab === "coaches" && user && (() => {
        // Hub of coach tools -- a simple array of { label, icon, description, url,
        // external } objects rendered as cards, per the Coaches build spec. Some
        // tools are native to GroundsPal, some (like Whiteboards) are outbound
        // links to purpose-built external apps -- same pattern already used for
        // Tryouts. Keeping this as a plain array (rather than a plugin system)
        // makes adding the next coach tool trivial.
        const COACH_TOOLS = [
          {
            label: "Whiteboards",
            Icon: PresentationIcon,
            description: "Draw up plays and formations for any sport",
            url: "https://whiteboards.risegame.com",
            external: true
          }
        ];
        return (
          <div style={{ padding: "1.5rem" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 4 }}>Coaches</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Tools to help you plan, run, and manage your team.</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
              {COACH_TOOLS.map(toolCard => (
                <a key={toolCard.label} href={toolCard.url}
                  target={toolCard.external ? "_blank" : undefined}
                  rel={toolCard.external ? "noopener noreferrer" : undefined}
                  style={{ display: "flex", flexDirection: "column", gap: 8, background: C.white, border: "1px solid " + C.border, borderRadius: R.control, padding: "1.25rem", textDecoration: "none", color: "inherit" }}>
                  <div style={{ width: 44, height: 44, borderRadius: R.control, background: C.greenLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <toolCard.Icon size={22} strokeWidth={2} color={C.green} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{toolCard.label}</div>
                  <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{toolCard.description}</div>
                </a>
              ))}
            </div>
            <div style={{ marginTop: 20, textAlign: "center", color: C.muted, fontSize: 13 }}>
              More coach tools are coming soon.
            </div>
          </div>
        );
      })()}

      {/* ─── TEAMS TAB ───────────────────────────────────────────── */}
      {tab === "teams" && user && user.role === "admin" && (
        <div>
          <SeasonManagerCard
            seasons={seasons} currentSeasonId={currentSeasonId} setCurrentSeasonId={setCurrentSeasonId}
            showSeasonMenu={showSeasonMenu} setShowSeasonMenu={setShowSeasonMenu}
            editingSeasonId={editingSeasonId} setEditingSeasonId={setEditingSeasonId}
            editSeasonLabel={editSeasonLabel} setEditSeasonLabel={setEditSeasonLabel}
            editSeasonCustomLabel={editSeasonCustomLabel} setEditSeasonCustomLabel={setEditSeasonCustomLabel}
            editSeasonYear={editSeasonYear} setEditSeasonYear={setEditSeasonYear}
            startEditSeason={startEditSeason} saveSeasonEdit={saveSeasonEdit}
            duplicateSeason={duplicateSeason} archiveSeason={archiveSeason} unarchiveSeason={unarchiveSeason}
            showArchivedSeasons={showArchivedSeasons} setShowArchivedSeasons={setShowArchivedSeasons}
            creatingSeason={creatingSeason} setCreatingSeason={setCreatingSeason}
            newSeasonLabel={newSeasonLabel} setNewSeasonLabel={setNewSeasonLabel}
            newSeasonCustomLabel={newSeasonCustomLabel} setNewSeasonCustomLabel={setNewSeasonCustomLabel}
            newSeasonYear={newSeasonYear} setNewSeasonYear={setNewSeasonYear}
            createSeason={createSeason}
          />
          <div style={{ fontSize: 11, color: C.muted, margin: "0 1rem 1rem" }}>
            Divisions and teams below are the same ones used in Scheduler — create or edit them from either tab.
          </div>
          {currentSeasonId ? (
            <div>
              <SchedulerSetup lid={lid} leagueLocations={leagueLocations} seasonId={currentSeasonId} hideBlackoutDates={true} hideScheduleConfig={true} />
              <PlayersManagement lid={lid} seasonId={currentSeasonId} isMobile={isMobile} />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "2rem", color: C.muted, fontSize: 13 }}>
              Create a season above to start setting up divisions and teams.
            </div>
          )}
        </div>
      )}

      {/* ─── SCHEDULER TAB ───────────────────────────────────────── */}
      {tab === "scheduler" && user && user.role === "admin" && !hasLeague && (
        <div style={{ padding: '14px' }}>
          <div style={{ background: C.white, borderRadius: R.card, border: "1px solid " + C.border, padding: "2rem 1.5rem", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 6 }}>Scheduler is a League-tier feature</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Auto-generate a full season schedule, manage games and standings, publish to the shared calendar, and export to GameChanger. Upgrade to League to unlock this and more.
            </div>
            <Btn onClick={() => setScreen('show-code')} variant="primary">View Upgrade Options</Btn>
          </div>
        </div>
      )}
      {tab === "scheduler" && user && user.role === "admin" && hasLeague && (
        <div>
          <SeasonManagerCard
            seasons={seasons} currentSeasonId={currentSeasonId} setCurrentSeasonId={setCurrentSeasonId}
            showSeasonMenu={showSeasonMenu} setShowSeasonMenu={setShowSeasonMenu}
            editingSeasonId={editingSeasonId} setEditingSeasonId={setEditingSeasonId}
            editSeasonLabel={editSeasonLabel} setEditSeasonLabel={setEditSeasonLabel}
            editSeasonCustomLabel={editSeasonCustomLabel} setEditSeasonCustomLabel={setEditSeasonCustomLabel}
            editSeasonYear={editSeasonYear} setEditSeasonYear={setEditSeasonYear}
            startEditSeason={startEditSeason} saveSeasonEdit={saveSeasonEdit}
            duplicateSeason={duplicateSeason} archiveSeason={archiveSeason} unarchiveSeason={unarchiveSeason}
            showArchivedSeasons={showArchivedSeasons} setShowArchivedSeasons={setShowArchivedSeasons}
            creatingSeason={creatingSeason} setCreatingSeason={setCreatingSeason}
            newSeasonLabel={newSeasonLabel} setNewSeasonLabel={setNewSeasonLabel}
            newSeasonCustomLabel={newSeasonCustomLabel} setNewSeasonCustomLabel={setNewSeasonCustomLabel}
            newSeasonYear={newSeasonYear} setNewSeasonYear={setNewSeasonYear}
            createSeason={createSeason}
          />

          {currentSeasonId ? (
            <div>
              <SchedulerSetup lid={lid} leagueLocations={leagueLocations} seasonId={currentSeasonId} />
              <GameScheduler lid={lid} user={user} seasonId={currentSeasonId} leagueName={league && league.leagueName} seasonName={seasons.find(s => s.id === currentSeasonId) ? seasons.find(s => s.id === currentSeasonId).name : ""} fieldEvents={fieldEvents} members={members} triggerNotify={triggerNotify} sendEmail={sendEmail} />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "2rem", color: C.muted, fontSize: 13 }}>
              Create a season above to start setting up divisions and teams.
            </div>
          )}
        </div>
      )}

      {/* ─── REGISTRATION TAB ───────────────────────────────────────── */}
      {tab === "registration" && user && user.role === "admin" && (
        <div>
          <SeasonManagerCard
            seasons={seasons} currentSeasonId={currentSeasonId} setCurrentSeasonId={setCurrentSeasonId}
            showSeasonMenu={showSeasonMenu} setShowSeasonMenu={setShowSeasonMenu}
            editingSeasonId={editingSeasonId} setEditingSeasonId={setEditingSeasonId}
            editSeasonLabel={editSeasonLabel} setEditSeasonLabel={setEditSeasonLabel}
            editSeasonCustomLabel={editSeasonCustomLabel} setEditSeasonCustomLabel={setEditSeasonCustomLabel}
            editSeasonYear={editSeasonYear} setEditSeasonYear={setEditSeasonYear}
            startEditSeason={startEditSeason} saveSeasonEdit={saveSeasonEdit}
            duplicateSeason={duplicateSeason} archiveSeason={archiveSeason} unarchiveSeason={unarchiveSeason}
            showArchivedSeasons={showArchivedSeasons} setShowArchivedSeasons={setShowArchivedSeasons}
            creatingSeason={creatingSeason} setCreatingSeason={setCreatingSeason}
            newSeasonLabel={newSeasonLabel} setNewSeasonLabel={setNewSeasonLabel}
            newSeasonCustomLabel={newSeasonCustomLabel} setNewSeasonCustomLabel={setNewSeasonCustomLabel}
            newSeasonYear={newSeasonYear} setNewSeasonYear={setNewSeasonYear}
            createSeason={createSeason}
          />
          <div style={{ fontSize: 11, color: C.muted, margin: "0 1rem 1rem" }}>
            Registration prices the divisions already set up in Teams/Scheduler for this season — create divisions there first if you don't see them below.
          </div>
          <RegistrationManager lid={lid} seasonId={currentSeasonId} seasonName={seasons.find(s => s.id === currentSeasonId) ? seasons.find(s => s.id === currentSeasonId).name : ""} league={league} leagueLocations={leagueLocations} connectSyncLoading={connectSyncLoading} connectSyncError={connectSyncError} />
        </div>
      )}

      {/* ─── TRYOUTS TAB ─────────────────────────────────────────── */}
      {tab === "tryouts" && user && user.role === "admin" && (
        <div style={{ padding: isMobile ? "10px" : "14px" }}>
          {/* Trophy, at the sidebar's own size=17 — same rule as the Board header. */}
<div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 4, display: "flex", alignItems: "center", gap: 7 }}>
  <TrophyIcon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
  Tryouts
</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Manage your league tryouts on the RiseGame Tryout Scheduler</div>
          <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: R.card, padding: "2rem", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
            <img src={RISE_LOGO_URL} alt="RiseGame" style={{ width: 64, height: 64, objectFit: "contain", borderRadius: R.control }} />
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>RiseGame Tryout Scheduler</div>
            <div style={{ fontSize: 13, color: C.muted, maxWidth: 360, lineHeight: 1.6 }}>
              Create and manage your division tryouts, set up time slots, share signup links with parents, and view registrations — all on the RiseGame Tryout Scheduler.
            </div>
            <a href="https://signups.risegame.com" target="_blank" rel="noopener noreferrer"
              style={{ background: C.greenMid, color: C.text, border: "none", borderRadius: R.control, padding: "12px 28px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4, width: "100%", maxWidth: 340, boxSizing: "border-box", lineHeight: "normal" }}>
              Go to RiseGame Tryout Scheduler →
            </a>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Opens in a new tab at signups.risegame.com</div>

            <button onClick={() => { setAppDownloadTarget('tryouts'); setShowAppDownloadModal(true); }}
              style={{ background: "#0D0D0D", color: C.white, border: "none", borderRadius: R.control, padding: "12px 28px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8, width: "100%", maxWidth: 340, boxSizing: "border-box", lineHeight: "normal" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <SmartphoneIcon size={17} strokeWidth={2} />
                Get the Tryouts by RiseGame App
              </span>
            </button>
            <div style={{ fontSize: 11, color: C.muted, marginTop: -6 }}>An easier way for leagues and coaches to score tryouts</div>

            <button onClick={() => { setAppDownloadTarget('elevate'); setShowAppDownloadModal(true); }}
              style={{ background: "#0D0D0D", color: C.white, border: "none", borderRadius: R.control, padding: "12px 28px", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8, width: "100%", maxWidth: 340, boxSizing: "border-box", lineHeight: "normal" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <SmartphoneIcon size={17} strokeWidth={2} />
                Free Coaches Tool - Elevate App
              </span>
            </button>
            <div style={{ fontSize: 11, color: C.muted, marginTop: -6 }}>Coaches can assign their players sports homework</div>
          </div>

          {showAppDownloadModal && (() => {
            const APP_LINKS = {
              tryouts: { name: "Tryouts by RiseGame", apple: "https://apps.apple.com/us/app/tryouts-by-risegame/id6740584601", google: "https://play.google.com/store/apps/details?id=com.risegame.tryouts" },
              elevate: { name: "RiseGame: Elevate Your Athlete", apple: "https://apps.apple.com/us/app/risegame-elevate-your-athlete/id6477887846", google: "https://play.google.com/store/apps/details?id=com.risegame.rg1z" }
            };
            const appInfo = APP_LINKS[appDownloadTarget];
            return (
            <div onClick={() => setShowAppDownloadModal(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <div onClick={e => e.stopPropagation()}
                style={{ background: C.white, borderRadius: R.card, padding: "1.5rem", maxWidth: 420, width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 4 }}>Get {appInfo.name}</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>
                  {isMobile ? "Tap your device's app store below." : "Scan with your phone's camera to download."}
                </div>

                {isMobile ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <a href={appInfo.apple} target="_blank" rel="noopener noreferrer"
                      style={{ background: "#000", color: "#fff", borderRadius: R.control, padding: "12px", fontWeight: 700, fontSize: 14, textDecoration: "none", fontFamily: "inherit" }}>
                       Download on the App Store
                    </a>
                    <a href={appInfo.google} target="_blank" rel="noopener noreferrer"
                      style={{ background: "#000", color: "#fff", borderRadius: R.control, padding: "12px", fontWeight: 700, fontSize: 14, textDecoration: "none", fontFamily: "inherit" }}>
                      ▶ Get it on Google Play
                    </a>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                    <div>
                      <img
                        src={"https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=" + encodeURIComponent(appInfo.apple)}
                        alt={"QR code to download " + appInfo.name + " on the App Store"} style={{ width: 150, height: 150, borderRadius: R.control, border: "1px solid " + C.border }} />
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 6 }}>iPhone</div>
                    </div>
                    <div>
                      <img
                        src={"https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=" + encodeURIComponent(appInfo.google)}
                        alt={"QR code to download " + appInfo.name + " on Google Play"} style={{ width: 150, height: 150, borderRadius: R.control, border: "1px solid " + C.border }} />
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 6 }}>Android</div>
                    </div>
                  </div>
                )}

                <button onClick={() => setShowAppDownloadModal(false)}
                  style={{ background: C.grayLt, border: "1px solid " + C.border, borderRadius: R.control, padding: "9px 18px", fontSize: 13, fontWeight: 600, color: C.muted, cursor: "pointer", fontFamily: "inherit", marginTop: 20 }}>
                  Close
                </button>
              </div>
            </div>
            );
          })()}
        </div>
      )}
        </div>
      </div>

      {/* ─── BOTTOM TAB BAR (mobile only) ───────────────────────── */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#111111", borderTop: "0.5px solid #2A2A2A", padding: "6px 10px 8px", display: "flex", gap: 4, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", zIndex: 100 }}>
          {[
          { k: "tasks", l: "Tasks", Icon: CheckSquareIcon },
          { k: "calendar", l: "Events", Icon: CalendarClockIcon },
          { k: "coaches", l: "Coaches", Icon: ClipboardListIcon },
          { k: "board", l: "board_tab", Icon: MegaphoneIcon },
          { k: "projects", l: "Projects", Icon: FolderOpenIcon },
          ...(user && user.role === "admin" ? [{ k: "teams", l: "Teams", Icon: UsersIcon }] : []),
          ...(user && user.role === "admin" ? [{ k: "scheduler", l: "Scheduler", Icon: CalendarRangeIcon }] : []),
          ...(user && user.role === "admin" ? [{ k: "registration", l: "Registration", Icon: CreditCardIcon }] : []),
          ...(user && user.role === "admin" ? [{ k: "tryouts", l: "Tryouts", Icon: TrophyIcon }] : [])
        ].map(t => {
            const isActive = tab === t.k;
            const pillColor = isActive ? "#111111" : "#fff";
            return (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{ padding: "5px 10px", border: "none", borderRadius: R.control, background: isActive ? "#75C83C" : "transparent", color: pillColor, fontWeight: isActive ? 800 : 600, fontSize: isMobile ? 11 : 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0, position: "relative", margin: "4px 2px", display: "flex", alignItems: "center", gap: 5 }}>
            <t.Icon size={15} strokeWidth={2} color={pillColor} style={{ flexShrink: 0 }} />
            {t.l === "board_tab" ? (
              <span style={{ position: "relative" }}>
                Board
                {boardPosts.some(p => !readBoardPosts.has(p.id)) && (
                  <span style={{ position: "absolute", top: -4, right: -8, width: 8, height: 8, background: C.red, borderRadius: "50%", display: "inline-block" }} />
                )}
              </span>
            ) : t.k === "calendar" && user && (user.role === "admin" || user.role === "coadmin") && fieldEvents.filter(e => e.status === "pending").length > 0 ? (
              <span style={{ position: "relative" }}>
                Events
                <span style={{ position: "absolute", top: -4, right: -8, width: 8, height: 8, background: C.red, borderRadius: "50%", display: "inline-block" }} />
              </span>
            ) : t.l}
            </button>
            );
          })}
          <div style={{ position: "relative", margin: "4px 2px" }}>
            <button ref={otherBtnRef} onClick={e => {
                e.stopPropagation();
                if (!showOtherMenu && otherBtnRef.current) {
                  const rect = otherBtnRef.current.getBoundingClientRect();
                  const rightPos = Math.max(8, window.innerWidth - rect.right);
                  setOtherMenuPos({ bottom: window.innerHeight - rect.top + 4, right: rightPos });
                }
                setShowOtherMenu(v => !v);
              }}
              style={{ padding: "5px 10px", border: "none", borderRadius: R.control, background: ["supplies", "crewstats", "archive", "waivers"].includes(tab) ? "#75C83C" : "transparent", color: ["supplies", "crewstats", "archive", "waivers"].includes(tab) ? "#111111" : "#fff", fontWeight: ["supplies", "crewstats", "archive", "waivers"].includes(tab) ? 800 : 600, fontSize: 11, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 3 }}>
              Other {showOtherMenu ? <ChevronUpIcon size={13} strokeWidth={2.5} /> : <ChevronDownIcon size={13} strokeWidth={2.5} />}
            </button>
            {showOtherMenu && createPortal(
              <div onClick={e => e.stopPropagation()}
                style={{ position: "fixed", bottom: otherMenuPos.bottom, right: otherMenuPos.right, background: "#111111", border: "1px solid #2A2A2A", borderRadius: R.control, boxShadow: "0 -4px 16px rgba(0,0,0,0.35)", minWidth: 160, zIndex: 200, overflow: "hidden" }}>
                {[
                  { k: "supplies", l: "Supplies" + (stats.sup > 0 ? " · " + stats.sup : ""), Icon: PackageIcon },
                  { k: "archive", l: "Archive", Icon: ArchiveIcon },
                  { k: "crewstats", l: "Stats", Icon: AwardIcon },
                  { k: "waivers", l: "Waivers", Icon: FileTextIcon }
                ].map(o => {
                  const isActive = tab === o.k;
                  return (
                  <button key={o.k} onClick={() => { setTab(o.k); setShowOtherMenu(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "10px 14px", background: isActive ? "#75C83C" : "#111111", border: "none", borderBottom: "1px solid #2A2A2A", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: isActive ? 800 : 500, color: isActive ? "#111111" : "#fff" }}>
                    <o.Icon size={16} strokeWidth={2} color={isActive ? "#111111" : "#fff"} style={{ flexShrink: 0 }} />
                    {o.l}
                  </button>
                  );
                })}
              </div>,
              document.body
            )}
          </div>
        </div>
      )}
      {isMobile && <div style={{ height: 56 }} />}

    </div>
  );
}

// Pathname-gate fork: /register/{slug} renders the public, parent-facing
// registration page instead of the authenticated admin/volunteer app.
// Deliberately a synchronous, render-time check (not an effect) -- the fork
// happens before FieldOpsAppInner (and its onAuthStateChanged league-lookup
// effect, which would otherwise redirect into choose-league/enterLeague on
// a stale admin/volunteer session) ever mounts. See
// registration-parent-facing-v1-spec.md, section 1.
export default function FieldOpsApp() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  // /register/{slug}/account -- the read-only "My Account" page (past
  // registrations per athlete, volunteer history, editable contact info).
  // Checked before the plain /register/{slug} match since it's the more
  // specific pattern; matched separately (not as an optional group on the
  // same regex) so the two views stay simple to reason about independently.
  const accountMatch = pathname.match(/^\/register\/([a-zA-Z0-9-]+)\/account\/?$/);
  if (accountMatch) {
    return <PublicRegistrationPage slug={accountMatch[1]} view="account" />;
  }
  const registerMatch = pathname.match(/^\/register\/([a-zA-Z0-9-]+)\/?$/);
  if (registerMatch) {
    return <PublicRegistrationPage slug={registerMatch[1]} view="register" />;
  }
  return <FieldOpsAppInner />;
}
